/* ============================================================
   ============= STRAT DE INTEGRARE SUPABASE ==================
   Acest bloc rescrie stratul de date al aplicației pentru a
   folosi Supabase ca sursă unică de adevăr. Obiectul `state`
   este folosit în continuare drept cache local pentru funcțiile
   de randare (UI). Definițiile de mai jos SUPRASCRIU funcțiile
   cu același nume declarate mai sus (hoisting: ultima declarație
   din același scope câștigă).
   ============================================================ */

/* ---------- Configurare & client ---------- */
const SUPABASE_URL = "https://howzltbnowpmdgjnfnhi.supabase.co";

/* Coloanele publice ale lui `profiles`, FĂRĂ `email`. (V8) Emailul e revocat la
   SELECT pentru anon/authenticated; `select('*')` ar da "permission denied for table",
   așa că enumerăm explicit coloanele non-email peste tot unde citim profiles.
   Emailul propriu vine din sesiune (authUser); emailurile cercului din RPC-ul emails_for. */
/* Coloanele cu care citim `profiles` (§24.1 — niciodată select('*')).
   ⚠ LECȚIE (iul. 2026): NU adăuga aici coloane noi fără o migrare deja live.
   Dacă o coloană din listă nu e expusă de API (migrare neaplicată SAU cache de
   schemă PostgREST neîmprospătat), `select` eșuează, iar codul care nu distinge
   „eroare de interogare" de „rând inexistent" ateriza într-un CONT GOL (fără poză,
   fără user_tag, fără consimțământ) — care putea fi salvat peste rândul real.
   De aceea „Jocurile deținute" NU stau într-o coloană nouă, ci în `profile_extra`
   (jsonb, deja existent și deja citit peste tot). Vezi ownedTitlesOf/_ownedApplyDelta.
   v9 FC (13 oct. 2026): `favorite_color` scos — câmpul dispăruse demult din profil
   (19/19 valori goale), dar ajungea în CSS-ul altor ecrane. Coloana se șterge din bază
   abia după ce nicio versiune veche nu o mai cere (§37). / Removed: unused artefact. */
window.PROFILE_COLS = 'id, name, role, points, avatar, created_at, bio, skill_level, preferred_games, bgg_username, hide_email, is_discoverable, deactivated_at, profile_extra, display_name, user_tag, notif_cleared_at, notif_dismissed, user_tag_locked, consent_terms_at, consent_age16_at';

/* Cheia ANON publică este concepută să fie expusă în client.
   Securitatea reală se asigură prin politicile RLS din Supabase. */
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imhvd3psdGJub3dwbWRnam5mbmhpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODAzMTQzODEsImV4cCI6MjA5NTg5MDM4MX0.qQYAwgYnijP2CGZp-oYnNUTA3utth2vbH5-HxK5gYOY";

const sb = (window.supabase && window.supabase.createClient)
    ? window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
        auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true }
      })
    : null;
let messagesChannel = null;
let presenceChannel = null;

/* ============================================================
   TurnOrder multi-club (Faza 1)
   - currentClubId: clubul activ pentru toate citirile/scrierile de date
   - state.myClubs: cluburile din care face parte utilizatorul curent
   În Faza 1 există încă un singur club (cel implicit), deci comportamentul
   rămâne identic; codul devine doar pregătit pentru mai multe cluburi.
   ============================================================ */
const DEFAULT_CLUB_ID = '11111111-1111-1111-1111-111111111111';
let currentClubId = DEFAULT_CLUB_ID;

function clubStorageKey(userId) { return 'turnorder_club_' + (userId || 'anon'); }

/* Task 4: persistă ultimul club vizitat în profil (DB) — sursă de adevăr, imună la
   blocarea localStorage (ex. Tracking Prevention) și valabilă cross-device. */
function _persistLastClub(clubId) {
    try {
        if (!clubId || !state.currentUser || !state.currentUser.id) return;
        if (typeof isDemoMode === 'function' && isDemoMode()) return;
        state.currentUser.lastClubId = clubId;
        sb.from('profiles').update({ last_club_id: clubId }).eq('id', state.currentUser.id)
            .then(function () {}, function (e) { logError('_persistLastClub', e); });
    } catch (e) { logError('_persistLastClub', e); }
}
function isPlatformOwner() { return !!(state.currentUser && (state.currentUser.isDemo || state.isPlatformOwner)); }
function canCreateClub() { return !!(state.currentUser); }

/* ---- Abonament pe USER (sursa de adevar pt. limite) ---- */
async function loadUserSubscription() {
    state.userSub = null;
    if (!sb || !state.currentUser) return;
    try {
        const { data } = await sb.from('user_subscriptions')
            .select('plan, subscription_status, billing_period, current_period_end, cancel_at_period_end, over_limit_choice_locked, stripe_subscription_id')
            .eq('user_id', state.currentUser.id).maybeSingle();
        if (data) state.userSub = data;
    } catch (e) { console.warn('loadUserSubscription:', e && e.message); }
}

/* ============================================================================
   FREE TRIAL — 30 de zile, fara card (★ NOU)
   RO: trialul e un rand REAL in `user_subscriptions` (subscription_status =
       'trialing', current_period_end = data de expirare a trialului). Deci
       userActivePlan()/planAllows/limitele functioneaza NESCHIMBATE.
       Anti-abuz (server-side, RPC SECURITY DEFINER): 1 trial / user
       (tabel `user_trials`) SI 1 trial / club (`clubs.trial_consumed`,
       permanent). Clientul DOAR reflecta starea — blocarea reala e in DB.
   EN: card-less 30-day trial; one per user AND one per club, enforced in DB.
   ============================================================================ */
function isTrialing() { const u = state.userSub; return !!(u && u.subscription_status === 'trialing'); }
function trialDaysLeft() {
    const u = state.userSub;
    if (!isTrialing() || !u.current_period_end) return null;
    return Math.max(0, Math.ceil((new Date(u.current_period_end).getTime() - Date.now()) / 86400000));
}
let _trialStatus = null;
/* Starea de eligibilitate vine de la server (`my_trial_status`), nu din client. */
async function loadTrialStatus(force) {
    if (_trialStatus && !force) return _trialStatus;
    if (!sb || !state.currentUser) { _trialStatus = null; return null; }
    try {
        const r = await sb.rpc('my_trial_status');
        _trialStatus = (r && !r.error && r.data && r.data[0]) ? r.data[0] : null;
    } catch (e) { logError('loadTrialStatus', e); _trialStatus = null; }
    return _trialStatus;
}
function trialEligible() { return !!(_trialStatus && _trialStatus.eligible); }

/* Porneste trialul. Ocoleste checkout-ul Stripe, dar scrie in ACEEASI structura
   (`user_subscriptions`) — fara sistem paralel. */
async function startFreeTrial(plan) {
    if (!sb || !state.currentUser) return;
    const label = (PLAN_INFO[plan] || {}).label || plan;
    if (!await confirmModal(t('trial.confirm').replace('{plan}', label))) return;
    try {
        showNotification(t('trial.starting'), 'info');
        const res = await sb.rpc('start_free_trial', { p_plan: plan });
        if (res && res.error) { showNotification(t('trial.failed') + ' (' + res.error.message + ')', 'error'); return; }
        const code = res && res.data;
        if (code !== 'OK') {
            const map = {
                TRIAL_ALREADY_USED: t('trial.errUsed'),
                ALREADY_SUBSCRIBED: t('trial.errHasSub'),
                PAID_BEFORE:        t('trial.errPaidBefore'),
                INVALID_PLAN:       t('trial.errPlan')
            };
            showNotification(map[code] || (t('trial.failed') + ' (' + code + ')'), 'error');
            return;
        }
        await loadUserSubscription();
        await loadTrialStatus(true);
        try { document.querySelectorAll('.modal.active').forEach(m => m.remove()); } catch (e) { logError('startFreeTrial', e); }
        try { if (typeof applyClubRole === 'function') applyClubRole(); } catch (e) { logError('startFreeTrial', e); }
        try { if (typeof renderClubSwitcher === 'function') renderClubSwitcher(); } catch (e) { logError('startFreeTrial', e); }
        try { const hub = document.getElementById('clubHub'); if (hub && !hub.classList.contains('hidden') && typeof renderClubHub === 'function') renderClubHub(); } catch (e) { logError('startFreeTrial', e); }
        showNotification(t('trial.started').replace('{plan}', label), 'success');
        /* Trialul e util doar cu un club: daca nu are niciunul, il ducem la creare. */
        if ((typeof countMyOwnedClubs === 'function' ? countMyOwnedClubs() : 0) === 0
            && typeof createClubPrompt === 'function') setTimeout(createClubPrompt, 600);
    } catch (e) { logError('startFreeTrial', e); showNotification(t('trial.failed'), 'error'); }
}
window.isTrialing = isTrialing;
window.trialDaysLeft = trialDaysLeft;
window.trialEligible = trialEligible;
window.loadTrialStatus = loadTrialStatus;
window.startFreeTrial = startFreeTrial;

function userActivePlan() {
    const u = state.userSub;
    if (!u || !u.plan) return null;
    const ok = (u.subscription_status === 'active' || u.subscription_status === 'trialing');
    return ok ? String(u.plan).toLowerCase() : null;
}
function planClubLimit(plan) {
    const v = (PLAN_INFO[plan] || {}).clubs;
    return (v == null || v === Infinity) ? Infinity : (Number(v) || Infinity);
}
function planMembersLimit(plan) {
    const v = (PLAN_INFO[plan] || {}).members;
    return (v == null || v === Infinity) ? Infinity : (Number(v) || Infinity);
}
function countMyOwnedClubs() {
    return (state.myClubs || []).filter(c => c.myRole === 'principal_admin').length;
}
async function loadPlatformRole(authUser) {
    state.canCreateClub = false; state.isPlatformOwner = false; state.platformRole = null;
    try {
        const email = ((authUser && authUser.email) || (state.currentUser && state.currentUser.email) || '').toLowerCase();
        if (!email || !sb) return;
        const res = await sb.from('app_admins').select('role').eq('email', email);
        const roles = (res && res.data) ? res.data.map(r => r.role) : [];
        if (roles.indexOf('owner') !== -1) { state.isPlatformOwner = true; state.canCreateClub = true; state.platformRole = 'owner'; }
        else if (roles.indexOf('creator') !== -1) { state.canCreateClub = true; state.platformRole = 'creator'; }
    } catch (e) { console.warn('loadPlatformRole:', e && e.message); }
}
/* ── Panou owner platformă ───────────────────────────────────────────────
   Apelat din butonul „Panou owner" (hub + meniul More din Profil), gated pe isPlatformOwner().
   Secțiuni: A) inbox feedback (best-effort, doar owner real, degradare grațioasă
   la RLS), B) cluburile mele (din state, zero risc), C) info platformă. RO/EN. */
function _ownerEsc(s) {
    return window.escHtml(s);
}

function openOwnerPanel() {
    /* Demo: `isPlatformOwner()` e adevărat intenționat (toate drepturile), dar panoul
       proprietarului rămâne închis acolo. EN: the owner panel never opens in demo. */
    if (typeof isPlatformOwner !== 'function' || !isPlatformOwner() || (typeof isDemoMode === 'function' && isDemoMode())) {
        showNotification(t('rbac.noPermission'), 'error');
        return;
    }
    const clubs = (state.myClubs || []);
    const clubsHtml = clubs.length
        ? clubs.map(function (c) {
            const plan = _ownerEsc(c.planType || c.plan || 'starter');
            const status = (typeof clubStatusLabel === 'function') ? _ownerEsc(clubStatusLabel(c.status)) : _ownerEsc(c.status || 'active');
            const role = _ownerEsc(c.myRole || '-');
            const lim = (c.memberLimit == null) ? '∞' : c.memberLimit;
            return `<div style="border-bottom:1px solid var(--border,#333); padding:8px 0;">
                <strong>${_ownerEsc(c.name || '-')}</strong>
                <div style="font-size:0.82em; opacity:0.78; margin-top:2px;">
                    ${t('owner.plan')}: ${plan} · ${t('owner.status')}: ${status} · ${t('owner.role')}: ${role} · ${t('owner.limit')}: ${lim}
                </div>
            </div>`;
        }).join('')
        : `<div style="opacity:0.7;">${t('owner.noClubs')}</div>`;

    const email = _ownerEsc((state.currentUser && state.currentUser.email) || '-');
    let pwa = '-';
    try { pwa = (navigator.serviceWorker && navigator.serviceWorker.controller) ? t('owner.pwaOn') : t('owner.pwaOff'); } catch (e) { logError('openOwnerPanel', e); }

    const modal = document.createElement('div');
    modal.className = 'modal active';
    modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
    modal.innerHTML = `<div class="modal-content" style="max-width:720px;">
        <h2 class="card-title"><i class="fas fa-user-shield"></i> ${t('owner.title')}</h2>

        <h3 style="margin-top:14px;"><i class="fas fa-comment-dots"></i> ${t('owner.feedbackTitle')}</h3>
        <div id="ownerFeedbackBox" style="max-height:260px; overflow:auto; margin-top:6px;">
            <div style="opacity:0.7;">${t('owner.feedbackLoading')}</div>
        </div>

        <h3 style="margin-top:18px;"><i class="fas fa-bug"></i> ${t('owner.errorsTitle')}
            <button class="btn btn-sm btn-secondary" style="float:right; padding:2px 10px; font-size:0.7em;" onclick="loadOwnerErrors()" title="${window.attrJs(t('common.refresh'))}"><i class="fas fa-rotate"></i></button>
        </h3>
        <input id="ownerErrFilter" class="form-control" placeholder="${window.attrJs(t('owner.errorsFilter'))}" oninput="renderOwnerErrors()" style="margin-top:6px; margin-bottom:6px;">
        <div id="ownerErrorsBox" style="max-height:300px; overflow:auto; margin-top:4px;">
            <div style="opacity:0.7;">${t('owner.feedbackLoading')}</div>
        </div>

        <h3 style="margin-top:18px;"><i class="fas fa-flag"></i> ${t('mod.title')} <small style="opacity:.6; font-weight:400; font-size:0.62em;">(${t('mod.ownerScope')})</small></h3>
        <div id="ownerModerationBox" style="max-height:340px; overflow:auto; margin-top:6px;">
            <div style="opacity:0.7;">…</div>
        </div>

        <h3 style="margin-top:18px;"><i class="fas fa-people-group"></i> ${t('owner.clubsTitle')}</h3>
        <div style="margin-top:6px;">${clubsHtml}</div>

        <h3 style="margin-top:18px;"><i class="fas fa-circle-info"></i> ${t('owner.infoTitle')}</h3>
        <div style="font-size:0.88em; opacity:0.85; margin-top:6px; line-height:1.7;">
            ${t('owner.email')}: <strong>${window.escHtml(email)}</strong><br>
            ${t('owner.clubCount')}: <strong>${clubs.length}</strong><br>
            ${t('owner.pwa')}: <strong>${pwa}</strong>
        </div>

        <div class="btn-group" style="margin-top:18px;">
            <button class="btn btn-secondary" onclick="this.closest('.modal').remove()">${t('common.close')}</button>
        </div>
    </div>`;
    document.body.appendChild(modal);
    loadOwnerFeedback();
    loadOwnerErrors();
    try { if (typeof renderOwnerModeration === 'function') renderOwnerModeration(); } catch (e) { logError('openOwnerPanel', e); }
}

/* Încarcă feedback-ul în panoul owner. Doar owner REAL (nu demo). Degradare
   grațioasă: dacă RLS blochează sau apare orice eroare, afișează mesaj, nu crapă. */
async function loadOwnerFeedback() {
    const box = document.getElementById('ownerFeedbackBox');
    if (!box) return;
    if (!state.isPlatformOwner) { box.innerHTML = `<div style="opacity:0.7;">${t('owner.demoNote')}</div>`; return; }
    if (!sb) { box.innerHTML = `<div style="opacity:0.7;">${t('owner.feedbackNoAccess')}</div>`; return; }
    try {
        const res = await sb.from('feedback')
            .select('message, user_name, user_email, lang, created_at')
            .order('created_at', { ascending: false })
            .limit(50);
        if (res.error) { box.innerHTML = `<div style="opacity:0.7;">${t('owner.feedbackNoAccess')}</div>`; return; }
        const rows = res.data || [];
        if (!rows.length) { box.innerHTML = `<div style="opacity:0.7;">${t('owner.feedbackEmpty')}</div>`; return; }
        box.innerHTML = rows.map(function (r) {
            let when = '';
            try { when = new Date(r.created_at).toLocaleString(); } catch (e) { logError('loadOwnerFeedback', e); }
            /* Nume ȘI e-mail, nu unul SAU altul: fără e-mail nu poți răspunde,
               fără nume nu știi cine e. */
            const _wn = r.user_name ? _ownerEsc(r.user_name) : '';
            const _we = r.user_email ? _ownerEsc(r.user_email) : '';
            const who = (_wn && _we) ? (_wn + ' &lt;' + _we + '&gt;') : (_wn || _we || '—');
            return `<div style="border-bottom:1px solid var(--border,#333); padding:8px 0;">
                <div style="font-size:0.78em; opacity:0.65;">${who} · ${_ownerEsc(when)}</div>
                <div style="margin-top:2px;">${_ownerEsc(r.message || '')}</div>
            </div>`;
        }).join('');
    } catch (e) {
        box.innerHTML = `<div style="opacity:0.7;">${t('owner.feedbackNoAccess')}</div>`;
    }
}
/* Erori client în panoul owner. Doar owner REAL. Filtru live + scroll.
   Degradare grațioasă la RLS/offline (mesaj, nu crapă). §9: doar context/mesaj/url. */
let _ownerErrRows = [];
async function loadOwnerErrors() {
    const box = document.getElementById('ownerErrorsBox');
    if (!box) return;
    if (!state.isPlatformOwner) { box.innerHTML = `<div style="opacity:0.7;">${t('owner.demoNote')}</div>`; return; }
    if (!sb) { box.innerHTML = `<div style="opacity:0.7;">${t('owner.feedbackNoAccess')}</div>`; return; }
    try {
        const res = await sb.from('client_errors')
            .select('ctx, message, url, created_at')
            .order('created_at', { ascending: false })
            .limit(200);
        if (res.error) { box.innerHTML = `<div style="opacity:0.7;">${t('owner.feedbackNoAccess')}</div>`; return; }
        _ownerErrRows = res.data || [];
        renderOwnerErrors();
    } catch (e) {
        box.innerHTML = `<div style="opacity:0.7;">${t('owner.feedbackNoAccess')}</div>`;
    }
}
function renderOwnerErrors() {
    const box = document.getElementById('ownerErrorsBox');
    if (!box) return;
    const fEl = document.getElementById('ownerErrFilter');
    const f = ((fEl && fEl.value) || '').trim().toLowerCase();
    let rows = _ownerErrRows || [];
    if (f) rows = rows.filter(function (r) {
        return (((r.ctx || '') + ' ' + (r.message || '') + ' ' + (r.url || '')).toLowerCase().indexOf(f) >= 0);
    });
    if (!rows.length) { box.innerHTML = `<div style="opacity:0.7;">${t('owner.errorsEmpty')}</div>`; return; }
    box.innerHTML = rows.map(function (r) {
        let when = '';
        try { when = new Date(r.created_at).toLocaleString(); } catch (e) { logError('renderOwnerErrors', e); }
        const ctx = _ownerEsc(r.ctx || '—');
        const msg = _ownerEsc(r.message || '');
        const url = _ownerEsc(r.url || '');
        return `<div style="border-bottom:1px solid var(--border,#333); padding:8px 0;">
            <div style="font-size:0.78em; opacity:0.65;"><strong style="color:var(--accent-primary,#e07a5f);">${ctx}</strong> · ${_ownerEsc(when)}</div>
            <div style="margin-top:2px; font-size:0.9em;">${msg}</div>
            ${url ? `<div style="font-size:0.74em; opacity:0.5; margin-top:1px;">${url}</div>` : ''}
        </div>`;
    }).join('');
}
window.loadOwnerErrors = loadOwnerErrors;
window.renderOwnerErrors = renderOwnerErrors;

function lastViewKey(userId) { return 'turnorder_view_' + (userId || 'anon'); }
function setLastView(v) { try { if (state.currentUser && state.currentUser.id) localStorage.setItem(lastViewKey(state.currentUser.id), v); } catch (e) { logError('setLastView', e); } }

/* Încarcă cluburile utilizatorului și alege clubul activ.
   Dacă nu e membru nicăieri (cont nou creat după Faza 0), îl adaugă
   automat în clubul implicit, ca aplicația să funcționeze ca până acum. */
async function loadMyClubsAndSelect(authUser) {
    state.myClubs = [];
    currentClubId = DEFAULT_CLUB_ID;
    if (!sb || !authUser) return;
    try {
        let res = await sb.from('club_members')
            .select('role, status, clubs(*)')
            .eq('profile_id', authUser.id)
            .eq('status', 'active');
        let rows = (res && res.data) || [];

        /* NU mai adăugăm automat la clubul implicit. Conturile noi rămân fără club
           și ajung în hub (Descoperă cluburi / jucători / Creează club). */

        state.myClubs = rows
            .filter(r => r.clubs)
            .map(r => ({
                id: r.clubs.id, name: r.clubs.name, slug: r.clubs.slug,
                status: r.clubs.status, plan: r.clubs.plan,
                planType: r.clubs.plan_type || null, billingPeriod: r.clubs.billing_period || null,
                subscriptionStatus: r.clubs.subscription_status || null,
                currentPeriodEnd: r.clubs.current_period_end || null,
                isPublic: !!r.clubs.is_public, joinPolicy: r.clubs.join_policy || 'request',
                imageUrl: r.clubs.image_url || null, memberLimit: r.clubs.member_limit,
                overLimit: !!r.clubs.over_limit,
                ownerId: r.clubs.owner_id, myRole: r.role
            }));

        /* Alege clubul activ: ultimul ales (din localStorage) dacă e încă
           în listă, altfel primul. Dacă nu există cluburi -> null (hub). */
        /* Task 4: ultimul club vizitat. Prioritate DB — citit SEPARAT și TOLERANT — apoi
           localStorage (fallback). Imun la blocarea localStorage (Tracking Prevention).

           RO: citirea se face prin RPC `my_last_club()`, NU prin SELECT direct pe
               `profiles.last_club_id`. Motivul: rolul `authenticated` are pe coloana asta
               doar INSERT/UPDATE, nu și SELECT — SELECT-ul direct returna 403 la FIECARE
               login, tăcut (eroarea era înghițită), deci „ultimul club" mergea exclusiv
               din localStorage: fix pe dos față de scopul funcției.
               Grantul de SELECT pe coloană NU e o opțiune: politica `profiles_select`
               permite citirea rândurilor altor utilizatori (is_discoverable / can_see_profile),
               iar privilegiile pe coloană sunt per-rol, nu per-rând => ar fi expus tuturor
               în ce club a fost ultima dată oricine. RPC-ul e `security definer` și
               returnează strict valoarea lui auth.uid().
           EN: read via the self-scoped `my_last_club()` RPC; a column-level SELECT grant
               would leak every user's last club to anyone who can see their profile row. */
        let saved = null;
        try { saved = (state.currentUser && state.currentUser.lastClubId) || null; } catch (e) {}
        if (!saved && !isDemoMode()) {
            try {
                const _lc = await sb.rpc('my_last_club');
                if (_lc && !_lc.error && _lc.data) {
                    saved = _lc.data;
                    if (state.currentUser) state.currentUser.lastClubId = saved;
                }
            } catch (e) { /* RPC-ul lipsește (SQL nerulat) -> ignorăm, folosim localStorage */ }
        }
        if (!saved) { try { saved = localStorage.getItem(clubStorageKey(authUser.id)); } catch (e) { logError('loadMyClubsAndSelect', e); } }
        /* Preferă cluburile deschideabile (active/grace); cele arhivate/închise nu se deschid. */
        const openable = state.myClubs.filter(c => c.status !== 'archived' && c.status !== 'closed');
        const ids = openable.map(c => c.id);
        if (saved && ids.includes(saved)) currentClubId = saved;
        else if (ids.length) currentClubId = ids[0];
        else currentClubId = null;

        if (currentClubId) {
            try { localStorage.setItem(clubStorageKey(authUser.id), currentClubId); } catch (e) { logError('loadMyClubsAndSelect', e); }
            _persistLastClub(currentClubId);
        }
    } catch (e) {
        console.warn('loadMyClubsAndSelect:', e && e.message);
        currentClubId = null;
    }
}

/* ---------- Selector de club (UI header) ---------- */
function clubEsc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, m => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[m]));
}
function clubRoleLabel(r) {
    return ({
        principal_admin: t('role.principal'), secondary_admin: t('role.secondary'),
        moderator: t('role.moderator'), member: t('role.member')
    })[r] || t('role.member');
}

function renderClubSwitcher() {
    const el = document.getElementById('clubSwitcher');
    const nameEl = document.getElementById('clubSwitcherName');
    if (!el || !nameEl) return;
    if (!state.currentUser || state.currentUser.isDemo) { el.classList.add('hidden'); return; }
    const clubs = state.myClubs || [];
    if (!clubs.length) { el.classList.add('hidden'); return; }
    const cur = clubs.find(c => c.id === currentClubId);
    const _numeClub = cur ? cur.name : 'Club';
    nameEl.textContent = _numeClub;
    /* Numele complet la hover (desktop). ⚠ `title` NU funcționează la atingere:
       pe mobil, numele întreg se vede la deschiderea listei de cluburi — de acolo
       vine soluția reală, nu de aici. Antetul are înălțime fixă, deci două
       rânduri nu sunt o opțiune. */
    try { nameEl.setAttribute('title', _numeClub); } catch (e) { logError('renderClubSwitcher', e); }
    el.classList.remove('hidden');
    if (typeof renderClubReadonlyBanner === 'function') renderClubReadonlyBanner();
}

/* Ancorează un popup sub elementul declanșator (desktop) sau centrat (mobil). */
function anchorPopup(panel, trigger) {
    if (!panel) return;
    panel.style.position = 'fixed';
    const isMobile = window.innerWidth <= 768;
    if (isMobile || !trigger) {
        panel.style.left = '50%';
        panel.style.right = 'auto';
        panel.style.transform = 'translateX(-50%)';
        panel.style.top = '64px';
    } else {
        const r = trigger.getBoundingClientRect();
        panel.style.transform = 'none';
        panel.style.right = 'auto';
        const w = panel.offsetWidth || 320;
        let left = r.right - w;                     /* aliniat la dreapta trigger-ului */
        const maxLeft = window.innerWidth - w - 8;
        if (left > maxLeft) left = maxLeft;
        if (left < 8) left = 8;
        panel.style.left = left + 'px';
        panel.style.top = (r.bottom + 8) + 'px';
    }
}

function toggleClubMenu() {
    const menu = document.getElementById('clubMenu');
    if (!menu) return;
    if (menu.classList.contains('hidden')) {
        renderClubMenu();
        menu.classList.remove('hidden');
        anchorPopup(menu, document.getElementById('clubSwitcher'));
    }
    else menu.classList.add('hidden');
}

function toggleClubSwitchList() {
    const el = document.getElementById('clubSwitchList');
    const ch = document.getElementById('clubSwitchChevron');
    if (el) el.classList.toggle('hidden');
    if (ch) { ch.classList.toggle('fa-chevron-down'); ch.classList.toggle('fa-chevron-up'); }
}
function renderClubMenu() {
    const menu = document.getElementById('clubMenu');
    if (!menu) return;
    const clubs = state.myClubs || [];
    const rowHtml = (c) => {
        const active = c.id === currentClubId;
        const statusTag = (c.status && c.status !== 'active')
            ? ` <span style="font-size:0.7em; background:#b23b3b; color:#fff; border-radius:6px; padding:1px 6px;">${clubStatusLabel(c.status)}</span>`
            : '';
        return `<a href="javascript:void(0)" onclick="switchClub('${c.id}')" style="display:flex; align-items:center; justify-content:space-between; gap:8px; padding:10px 12px; text-decoration:none; color:var(--text-primary,#eee); ${active ? 'background:rgba(255,107,53,0.12);' : ''}">
            <span style="display:flex; flex-direction:column; min-width:0;">
                <span style="font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${clubEsc(c.name)}${statusTag}</span>
                <span style="font-size:0.72em; opacity:0.65;">${clubRoleLabel(c.myRole)}${planTagHtml(c)}</span>
            </span>
            ${active ? '<i class="fas fa-check" style="color:var(--accent-primary,#ff6b35);"></i>' : ''}
        </a>`;
    };
    let clubsBlock;
    if (clubs.length <= 1) {
        clubsBlock = clubs.map(rowHtml).join('') || ('<p style="padding:14px; opacity:0.7;">' + t('club.noClubs') + '</p>');
    } else {
        const activeC = clubs.find(c => c.id === currentClubId) || clubs[0];
        const others = clubs.filter(c => c.id !== activeC.id);
        clubsBlock = rowHtml(activeC)
            + `<a href="javascript:void(0)" onclick="toggleClubSwitchList()" style="display:flex; align-items:center; justify-content:center; gap:8px; padding:11px 12px; border-top:1px dashed var(--border,#333); color:var(--accent-primary,#ff6b35); font-weight:600; text-decoration:none;"><i class="fas fa-right-left"></i> ${t('menu.switchClub')} (${clubs.length}) <i class="fas fa-chevron-down" id="clubSwitchChevron"></i></a>`
            + `<div id="clubSwitchList" class="hidden" style="max-height:38vh; overflow:auto; border-top:1px solid var(--border,#333);">${others.map(rowHtml).join('')}</div>`;
    }
    menu.innerHTML = `
        <div style="padding:10px 12px; font-weight:700; border-bottom:1px solid var(--border,#333); display:flex; justify-content:space-between; align-items:center;">
            <span><i class="fas fa-users"></i> ${t('menu.myClubs')}</span>
            <i class="fas fa-times" style="cursor:pointer;" onclick="document.getElementById('clubMenu').classList.add('hidden')"></i>
        </div>
        <div style="max-height:60vh; overflow:auto;">${clubsBlock}</div>
        <a href="javascript:void(0)" onclick="openDiscoverClubs()" style="display:block; padding:12px; text-align:center; font-weight:600; border-top:1px solid var(--border,#333); color:var(--text-primary,#eee); text-decoration:none;"><i class="fas fa-compass"></i> ${t('discover.clubsTitle')}</a>
        <a href="javascript:void(0)" onclick="openDiscoverPlayers()" style="display:block; padding:12px; text-align:center; font-weight:600; border-top:1px solid var(--border,#333); color:var(--text-primary,#eee); text-decoration:none;"><i class="fas fa-user-group"></i> ${t('discover.playersTitle')}</a>
        ${isAdminOfActiveClub() ? `<a href="javascript:void(0)" onclick="openClubSettings()" style="display:block; padding:12px; text-align:center; font-weight:600; border-top:1px solid var(--border,#333); color:var(--text-primary,#eee); text-decoration:none;"><i class="fas fa-gear"></i> ${t('clubset.title')}</a><a href="javascript:void(0)" onclick="openInviteLink()" style="display:block; padding:12px; text-align:center; font-weight:600; border-top:1px solid var(--border,#333); color:var(--text-primary,#eee); text-decoration:none;"><i class="fas fa-link"></i> ${t('menu.inviteLink')}</a><a href="javascript:void(0)" onclick="openJoinRequests()" style="display:block; padding:12px; text-align:center; font-weight:600; border-top:1px solid var(--border,#333); color:var(--text-primary,#eee); text-decoration:none;"><i class="fas fa-envelope-open-text"></i> ${t('join.title')}${(state.pendingByClub && state.pendingByClub[currentClubId]) ? ' (' + state.pendingByClub[currentClubId] + ')' : ''}</a>` : ''}
        ${(currentClubMyRole() === 'principal_admin' && activeClub() && activeClub().status && activeClub().status !== 'active')
            ? `<a href="javascript:void(0)" onclick="reactivateActiveClub()" style="display:block; padding:12px; text-align:center; font-weight:600; border-top:1px solid var(--border,#333); color:var(--success,#27ae60); text-decoration:none;"><i class="fas fa-box-open"></i> ${t('menu.reactivate')}</a>`
            : ''}
        ${canCreateClub() ? `<a href="javascript:void(0)" onclick="createClubPrompt()" style="display:block; padding:12px; text-align:center; font-weight:600; border-top:1px solid var(--border,#333); color:var(--accent-primary,#ff6b35); text-decoration:none;">
            <i class="fas fa-plus"></i> ${window.t('hub.createClubNew')}
        </a>` : ''}
        ${activeClub() ? `<a href="javascript:void(0)" onclick="leaveClub()" style="display:block; padding:12px; text-align:center; font-weight:600; border-top:1px solid var(--border,#333); color:var(--danger,#e74c3c); text-decoration:none;"><i class="fas fa-right-from-bracket"></i> ${t('leave.menu')}</a>` : ''}`;
}

/* F1 (3 oct. 2026): `opts.force` reîncarcă și clubul DEJA selectat. Necesar după acceptarea unei
   invitații: `loadMyClubsAndSelect` alege deja noul club (la un cont nou e singurul), deci
   `currentClubId === clubId`, iar fără `force` ieșeam aici fără să încărcăm datele lui —
   clubul apărea gol până la un refresh. EN: `force` reloads the already-selected club. */
async function switchClub(clubId, opts) {
    const menu = document.getElementById('clubMenu');
    if (menu) menu.classList.add('hidden');
    if (!clubId || (clubId === currentClubId && !(opts && opts.force))) return;
    /* Cluburile ARHIVATE / ÎNCHISE nu se pot deschide — datele sunt inaccesibile.
       Oferim în schimb preluarea (Claim Club). Grace (read_only) se poate deschide (doar citire). */
    const target = (state.myClubs || []).find(c => c.id === clubId);
    if (target) {
        const ph = clubLifecyclePhase(target);
        if (ph === 'archived' || ph === 'closed') { openClaimPrompt(target); return; }
    }
    currentClubId = clubId;
    /* F4: la schimbarea clubului, datele noului club încă se încarcă => realizările lui existente
       se înregistrează fără notificare (fereastra ACH_SILENT_MS). */
    try { achStartSilentWindow(); } catch (e) { logError('switchClub', e); }
    try { if (state.currentUser) localStorage.setItem(clubStorageKey(state.currentUser.id), clubId); } catch (e) { logError('switchClub', e); }
    _persistLastClub(clubId);   /* Task 4: sursă de adevăr în DB (imun la blocarea localStorage) */
    try { showNotification(t('sb.switchingClub'), 'info'); } catch (e) { logError('switchClub', e); }
    applyClubRole();
    /* Club nou = generație nouă: un răspuns despre clubul precedent nu are voie
       să deschidă fereastra peste clubul în care tocmai am intrat. */
    _rulesAckSeq++;
    _rulesAckState = null;
    try { const _m = document.getElementById('rulesAckModal'); if (_m) _m.remove(); } catch (e) {}

    await hydrateFromSupabase();
    try { subscribeRealtimeChat(); subscribeRealtimeData(); subscribeRealtimePresence(); subscribeRealtimeTimer(); subscribeRealtimeLive(); } catch (e) { logError('switchClub', e); }
    try { loadLiveSessions().then(function () { if (typeof renderLiveSessionsBar === 'function') renderLiveSessionsBar(); }); } catch (e) { logError('switchClub', e); }
    try { if (typeof loadDirectMessages === 'function') loadDirectMessages().then(function () { renderNotifBadge(); }); if (typeof subscribeRealtimeDM === 'function') subscribeRealtimeDM(); if (typeof subscribeRealtimeSocial === 'function') subscribeRealtimeSocial(); } catch (e) { logError('switchClub', e); }
    try { if (typeof loadFriendData === 'function') loadFriendData(); } catch (e) { logError('switchClub', e); }
    /* Starea de cont e per CLUB. Fără golire, un club nou ar citi harta celui
       vechi (`hasAccount` compară `clubId`, dar golim explicit ca să nu ținem
       date ale unui club în care poate nu mai suntem). */
    try { _acctStatus = { clubId: null, map: {}, loading: false }; } catch (e) { logError('switchClub', e); }
    try { if (typeof notifPollStart === 'function') notifPollStart(); } catch (e) { logError('switchClub', e); }
    try { resetLiveTimer(); loadTimerState(); } catch (e) { logError('switchClub', e); }
    renderClubSwitcher();
    try { refreshPendingBadge(); } catch (e) { logError('switchClub', e); }
    try { if (typeof loadChat === 'function') loadChat(); } catch (e) { logError('switchClub', e); }
    try { if (typeof render === 'function') render(); } catch (e) { logError('switchClub', e); }
    try { switchTab('dashboard'); } catch (e) { logError('switchClub', e); }
    try { if (typeof loadOnlineUsers === 'function') loadOnlineUsers(); } catch (e) { logError('switchClub', e); }
    try { loadAllNotifications().then(function () { renderNotifBadge(); }); } catch (e) { logError('switchClub', e); }
    const cur = (state.myClubs || []).find(c => c.id === clubId);
    try { showNotification(t('sb.activeClubColon') + (cur ? cur.name : ''), 'success'); } catch (e) { logError('switchClub', e); }
}

async function createClubPrompt() {
    const menu = document.getElementById('clubMenu');
    if (menu) menu.classList.add('hidden');
    if (!canCreateClub()) {
        showNotification(t('rbac.noPermission'), 'info');
        return;
    }
    const plan = (typeof userActivePlan === 'function') ? userActivePlan() : null;
    if (!plan) {
        showNotification(t('club.needSubscription'), 'info');
        try { localStorage.setItem('to_after_buy_create', '1'); } catch (e) { logError('createClubPrompt', e); }
        if (typeof openPlansModal === 'function') openPlansModal();
        return;
    }
    const maxClubs = (typeof planClubLimit === 'function') ? planClubLimit(plan) : Infinity;
    if ((typeof countMyOwnedClubs === 'function' ? countMyOwnedClubs() : 0) >= maxClubs) {
        showUpgradeModal({ clubLimit: true, max: maxClubs });
        return;
    }
    const name = await promptModal(t('hub.createClubName') || 'Numele noului club:');
    if (!name || !name.trim()) return;
    await createClub(name.trim());
}

async function createClub(name) {
    if (!sb || !state.currentUser) return;
    const plan = (typeof userActivePlan === 'function') ? userActivePlan() : null;
    if (!plan) { showNotification(t('club.needSubscription'), 'info'); if (typeof openPlansModal === 'function') openPlansModal(); return; }
    const ml = (typeof planMembersLimit === 'function') ? planMembersLimit(plan) : Infinity;
    try {
        showNotification(t('sb.creatingClub'), 'info');
        const insertObj = { name: name, owner_id: state.currentUser.id, status: 'active', plan: plan, plan_type: plan };
        if (ml !== Infinity) insertObj.member_limit = ml;
        const ins = await sb.from('clubs').insert(insertObj).select().maybeSingle();
        if (ins.error || !ins.data) {
            const m = (ins.error && ins.error.message) || '';
            if (m.indexOf('CLUB_LIMIT_REACHED') !== -1) { showUpgradeModal({ clubLimit: true, max: planClubLimit(plan) }); return; }
            if (m.indexOf('NO_SUBSCRIPTION') !== -1) { showNotification(t('club.needSubscription'), 'info'); if (typeof openPlansModal === 'function') openPlansModal(); return; }
            showNotification(t('sb.createClubErrorColon') + (m || t('sb.unknown')), 'error');
            return;
        }
        const club = ins.data;
        const mem = await sb.from('club_members')
            .upsert({ club_id: club.id, profile_id: state.currentUser.id, role: 'principal_admin' },
                    { onConflict: 'club_id,profile_id' });
        if (mem.error) showNotification(t('sb.clubCreatedNotAdminColon') + mem.error.message, 'error');
        state.myClubs = state.myClubs || [];
        state.myClubs.push({
            id: club.id, name: club.name, slug: club.slug, status: club.status,
            plan: club.plan, planType: club.plan_type || null, isPublic: !!club.is_public, joinPolicy: club.join_policy || 'request',
            imageUrl: club.image_url || null, memberLimit: club.member_limit,
            overLimit: false,
            ownerId: club.owner_id, myRole: 'principal_admin'
        });
        showNotification(t('sb.clubCreatedColon') + club.name, 'success');
        await enterClub(club.id);
    } catch (e) {
        showNotification(t('sb.errorColon') + (e && e.message), 'error');
    }
}

/* Aplică rolul utilizatorului din clubul ACTIV asupra permisiunilor.
   admin = principal_admin sau secondary_admin (aplicația e binară azi;
   moderatorul rămâne deocamdată non-admin, se rafinează ulterior). */
function currentClubMyRole() {
    const c = (state.myClubs || []).find(x => x.id === currentClubId);
    return c ? c.myRole : null;
}
function applyClubRole() {
    if (!state.currentUser) return;
    const r = currentClubMyRole();
    const admin = (r === 'principal_admin' || r === 'secondary_admin');
    state.currentUser.role = admin ? 'admin' : 'member';
    state.currentUser.isAdmin = admin;
    state.currentUser.clubRole = r || 'member';
    state.currentUser.clubRoleRank = roleRank(r || 'member');
    isAdmin = admin;
    if (typeof updateUserDisplay === 'function') { try { updateUserDisplay(); } catch (e) { logError('applyClubRole', e); } }
}

/* ============================================================
   RBAC — Ierarhie de roluri pe 4 niveluri (model extensibil)
   RO: Sursa unică de adevăr pentru permisiuni. NU înlocuiește `isAdmin`
       (acela rămâne principal||secondary, ca să nu altereze logica globală);
       capabilitățile sunt un strat DEASUPRA, folosit pentru gating fin,
       ecranul de gestiune comunitate și funcții viitoare.
   EN: Single source of truth for permissions. Does NOT replace `isAdmin`
       (kept as principal||secondary to avoid altering global logic);
       capabilities are a layer ON TOP for fine-grained gating, the
       community-management screen and future features.

   Niveluri: principal_admin(4) > secondary_admin(3) > moderator(2) > member(1)
   Extensibilitate: orice funcție nouă (catering, achievements, discover, …)
   se distribuie pe niveluri adăugând o intrare în CAP_MIN_RANK. */
const ROLE_RANK = { member: 1, moderator: 2, secondary_admin: 3, principal_admin: 4 };
const ROLE_ORDER = ['member', 'moderator', 'secondary_admin', 'principal_admin'];

/* capability -> rangul minim necesar */
const CAP_MIN_RANK = {
    /* Nivel 4 — Membru: vizualizare + participare */
    'club.viewStats': 1, 'catalog.view': 1, 'poll.vote': 1, 'chat.send': 1, 'event.rsvp': 1,
    /* v9 8.4: reacțiile la mesaje = membru activ (react_chat_message pe server; în DM fără prag: doar cei doi) */
    'chat.react': 1,
    /* Nivel 3 — Moderator: operare conținut + moderare */
    'session.create': 2, 'score.edit': 2, 'session.logOnBehalf': 2, 'chat.moderate': 2, 'event.manage': 2, 'campaign.manage': 2,
    /* v9 8.3: înainte le înregistra tournaments.js la încărcare; acum se încarcă la cerere,
       iar butonul „Turneu nou" (data-cap) trebuie gardat corect de la pornire. Aceleași praguri. */
    'tournament.manage': 2, 'tournament.join': 1,
    /* `catalog.manage` = 2: aliniat cu RLS-ul din DB (games insert/update/delete cer rang>=2).
       Înainte clientul cerea 3 => moderatorii erau blocați în UI deși DB le permitea. */
    'catalog.manage': 2,
    /* `catalog.import` = 3: ALINIAT cu pragul din RPC-ul `import_games_bulk`
       (`rbac_role_rank(p_club_id) < 3 -> 'admin required'`). NU coborî la 2 fără să
       cobori și RPC-ul, altfel moderatorul apasă butonul și primește eroarea brută
       din baza de date. Importul în masă e o clasă diferită de acțiune față de
       `catalog.manage`: rescrie zeci/sute de rânduri dintr-o apăsare și completează
       câmpuri la jocuri editate manual de alții. Adăugarea unui joc pe rând (inclusiv
       din căutarea BGG) rămâne la rang 2. */
    'catalog.import': 3,
    /* `backup.restore` = 3: aliniat cu CEL MAI STRICT prag de pe traseul restaurării —
       `import_games_bulk` (rang>=3), `import_matches_bulk` (rang>=3) și RLS-ul de pe
       `club_settings` (insert/update rang>=3; excepția pentru moderatori acoperă doar
       cheile de vot, nu `regulation`/`season_archives`). Dacă ar fi mai permisiv,
       restaurarea ar începe și s-ar opri la jumătate cu o eroare brută din DB. */
    'backup.restore': 3,
    /* `backup.export` = 3: exportul produce un fișier cu TOT clubul (catalog, membri,
       istoric, setări). Chiar dacă e doar citire din memorie, agregarea într-un fișier
       descărcabil e o acțiune de administrare, nu de consultare. */
    'backup.export': 3,
    /* `catalog.delete` = 3: ȘTERGEREA de jocuri (individuală ȘI în masă), aliniată cu
       politica RLS `games_delete_min_admin` (rang>=3). O SINGURĂ capabilitate pentru
       ambele trasee — două nume pentru aceeași întrebare e exact tiparul care produce
       divergențe. `catalog.manage` (2) rămâne pentru ADĂUGARE și EDITARE, unde RLS-ul
       de pe `games` chiar permite rang 2. */
    'catalog.delete': 3,
    /* `game.rate` = 1: orice membru poate da rating (prin RPC `game_rate`, care scrie
       DOAR votul propriu — scrierea directă în `games` ar permite editarea oricărui câmp). */
    'game.rate': 1,
    /* §2: o sesiune live e modificată/închisă DOAR de host sau de un admin (rang>=3).
       Un moderator care nu e host o vede read-only (poate doar propriul cronometru).
       Deschiderea rămâne la rang>=2 (`session.create`). */
    'live.manage': 3,
    /* Nivel 2 — Admin secundar: management operațional */
    'member.manage': 3, 'member.ban': 3, 'member.mute': 3, 'settings.edit': 3, 'rules.edit': 3, 'chat.clear': 3,
    'chat.deleteAny': 3,   /* v9 B2a: ștergerea mesajului altcuiva (= `delete_chat_message`, rang ≥ 3); până la 8.13-3 o adăuga chat-core.js la rulare */
    'reports.view': 3, 'moderation.view': 3, 'achievement.grant': 3, 'discover.manage': 3, 'catering.manage': 3,
    'match.edit': 3, 'match.delete': 3, 'announcements.post': 3,
    /* Nivel 1 — Admin principal: proprietate juridică/financiară */
    'finance.manage': 4, 'plan.manage': 4, 'club.delete': 4, 'ownership.transfer': 4, 'role.assignAdmin': 4
};

function roleRank(r) { return ROLE_RANK[r] || 0; }

/* Permisiunea curentă în clubul activ. Owner-ul platformei trece peste tot. */
function can(cap) {
    try { if (typeof isPlatformOwner === 'function' && isPlatformOwner()) return true; } catch (e) { logError('can', e); }
    const need = CAP_MIN_RANK[cap];
    if (need == null) return false;
    return roleRank(currentClubMyRole()) >= need;
}

/* Rolurile pe care le pot ATRIBUI, în funcție de rolul meu (restricțiile din tabel):
   - Admin principal poate seta: secundar / moderator / membru (NU principal — acela e transfer de proprietate)
   - Admin secundar poate seta: moderator / membru (NU poate promova la principal sau secundar)
   - restul: niciunul */
function assignableRolesFor(myRole) {
    if (myRole === 'principal_admin') return ['secondary_admin', 'moderator', 'member'];
    if (myRole === 'secondary_admin') return ['moderator', 'member'];
    return [];
}

/* Pot gestiona un membru cu rolul `targetRole`?
   - trebuie să am member.manage (rang >= 3)
   - nu pot atinge un Admin Principal (se schimbă doar prin transfer de proprietate)
   - pot gestiona doar roluri cu rang STRICT mai mic decât al meu */
function canManageMember(targetRole) {
    if (!can('member.manage')) return false;
    if (targetRole === 'principal_admin') return false;
    return roleRank(currentClubMyRole()) > roleRank(targetRole);
}

/* Regulă generală de UX: orice element marcat cu data-cap="capability" (sau mai
   multe, separate prin spațiu/virgulă) este ASCUNS complet dacă utilizatorul nu
   are cel puțin una dintre capabilități. Apelat din render() și după switchClub.
   Extensibil: pentru a gata orice element nou, adaugi doar atributul data-cap.
   NU atinge logica globală `isAdmin`. */
function applyRoleUIGating() {
    try {
        document.querySelectorAll('[data-cap]').forEach(el => {
            const caps = (el.getAttribute('data-cap') || '').split(/[\s,]+/).filter(Boolean);
            const allowed = caps.length === 0 || (typeof can === 'function' && caps.some(c => can(c)));
            el.style.display = allowed ? '' : 'none';
        });
    } catch (e) { logError('applyRoleUIGating', e); }
}

/* Are utilizatorul curent dreptul de a accesa un tab (după data-cap din nav)? */
function canAccessTab(tabName) {
    try {
        const navEl = document.querySelector('[data-tab="' + tabName + '"]');
        const caps = navEl ? (navEl.getAttribute('data-cap') || '').split(/[\s,]+/).filter(Boolean) : [];
        if (!caps.length) return true;
        return (typeof can === 'function') && caps.some(c => can(c));
    } catch (e) { return true; }
}

/* ---------- Stare club (activ / blocat / arhivat / închis) ---------- */
/* ============== Planuri & funcții stratificate pe abonament ==============
   Maparea din business plan (cumulativ):
   Starter(8 membri,1 club) < Basic(20,2 +export+regulament) <
   Pro(60,5 +ELO+scoresheets) < Elite(150,nelimitat +turnee+achievements club+API+cross-club)
   < Enterprise(nelimitat +branding+HR+SLA). */
const PLAN_ORDER = ['starter', 'basic', 'pro', 'elite', 'enterprise'];
const PLAN_INFO = {
    starter:    { label: 'Starter',    members: 8,        clubs: 1 },
    basic:      { label: 'Basic',      members: 20,       clubs: 2 },
    pro:        { label: 'Pro',        members: 60,       clubs: 5 },
    elite:      { label: 'Elite',      members: 150,      clubs: Infinity },
    enterprise: { label: 'Enterprise', members: Infinity, clubs: Infinity }
};

/* ============================================================
   Prețuri abonament (RON). Configurație centrală, pregătită pentru Stripe.
   Anual = 10× lunar => 2 luni gratis (≈ 16.7% reducere). Enterprise = „de la".
   ============================================================ */
/* ============================================================================
   PREȚURI — O SINGURĂ SURSĂ: TABELUL `plan_prices` DIN BAZA DE DATE
   ----------------------------------------------------------------------------
   RO: Înainte, cifrele afișate erau scrise în TREI locuri din cod — în lei, în
       dolari/euro, și ca text de rezervă în `PLAN_TIERS.price` — niciunul legat
       de Stripe. Al treilea ajunsese să spună că Starter e „Gratuit".
       Acum vin toate din baza de date, prin `plan_display_prices()`, care le
       ține lângă identificatorul prețului Stripe pe care îl reprezintă.
       ⚠ Fără listă de rezervă în cod: o rezervă ar reintroduce exact duplicarea
       pe care o eliminăm, iar o cifră veche afișată e mai rea decât una absentă.
   EN: Plan prices come from the database (single source), never hardcoded.
   ============================================================================ */
let _planPrices = null;            /* { 'pro_monthly': { RON, USD, EUR } } */
let _planPricesLoading = false;

async function loadPlanPrices(force) {
    if (_planPrices && !force) return _planPrices;
    if (_planPricesLoading) return null;
    _planPricesLoading = true;
    try {
        const res = await sb.rpc('plan_display_prices');
        _planPricesLoading = false;
        if (!res || res.error || !res.data) {
            logError('loadPlanPrices', (res && res.error) || 'fara date');
            return null;
        }
        const m = {};
        res.data.forEach(function (r) {
            if (!r || !r.plan_type || !r.billing_period) return;
            m[r.plan_type + '_' + r.billing_period] = {
                RON: (r.amount_ron != null) ? Number(r.amount_ron) : null,
                USD: (r.amount_usd != null) ? Number(r.amount_usd) : null,
                EUR: (r.amount_eur != null) ? Number(r.amount_eur) : null
            };
        });
        _planPrices = m;
        return m;
    } catch (e) {
        _planPricesLoading = false;
        logError('loadPlanPrices', e);
        return null;
    }
}

/* Următorul plan superior (pentru sugestii de upgrade) */
function nextPlanUp(plan) {
    const i = PLAN_ORDER.indexOf(plan || currentPlan());
    return (i >= 0 && i < PLAN_ORDER.length - 1) ? PLAN_ORDER[i + 1] : null;
}

/* ============================================================
   Valute pentru AFIȘARE (USD implicit). Facturarea reală rămâne în RON
   (SmartBill / e-Factura România). Cursurile sunt raportate la RON și se
   actualizează live din frankfurter.app (cursuri BCE); avem fallback dacă
   API-ul nu răspunde, ca să nu se blocheze niciodată afișarea.
   ============================================================ */
const CURRENCY_META = {
    USD: { symbol: '$',   after: false, dec: 2 },
    EUR: { symbol: '€',   after: false, dec: 2 },
    RON: { symbol: 'RON', after: true,  dec: 0 }
};
let _plansCurrency = 'USD';
/* cât valorează 1 RON în valuta țintă (fallback rezonabil) */
let _fxRatesFromRON = { RON: 1, USD: 0.22, EUR: 0.20 };
let _fxLoaded = false;

async function loadFxRates() {
    if (_fxLoaded) return;
    try {
        const r = await fetch('https://api.frankfurter.app/latest?from=RON&to=USD,EUR');
        if (r.ok) {
            const d = await r.json();
            if (d && d.rates) {
                if (d.rates.USD) _fxRatesFromRON.USD = d.rates.USD;
                if (d.rates.EUR) _fxRatesFromRON.EUR = d.rates.EUR;
                _fxLoaded = true;
            }
        }
    } catch (e) { /* păstrăm fallback-ul */ }
    const host = document.getElementById('plansCards'); if (host) host.innerHTML = plansCardsHtml();
}
function convertFromRON(amountRON, currency) {
    const rate = (_fxRatesFromRON[currency] != null) ? _fxRatesFromRON[currency] : 1;
    return amountRON * rate;
}
function formatMoney(amountRON, currency) {
    const m = CURRENCY_META[currency] || CURRENCY_META.RON;
    const val = convertFromRON(amountRON, currency);
    const num = m.dec ? val.toFixed(m.dec) : Math.round(val).toString();
    return m.after ? (num + ' ' + m.symbol) : (m.symbol + num);
}
/* Prețul de afișare pentru plan+perioadă în valuta cerută. `null` = încă nu s-au
   încărcat, sau planul nu are preț (Enterprise) — apelantul afișează un marcaj
   neutru, niciodată o cifră veche. */
function planDisplayPrice(plan, period, currency) {
    if (!_planPrices) return null;
    const row = _planPrices[plan + '_' + (period === 'yearly' ? 'yearly' : 'monthly')];
    if (!row) return null;
    const v = (row[currency] != null) ? row[currency] : row.RON;
    return (v != null) ? v : null;
}
function formatDisplayMoney(value, currency) {
    const m = CURRENCY_META[currency] || CURRENCY_META.RON;
    const num = m.dec ? value.toFixed(m.dec) : Math.round(value).toString();
    return m.after ? (num + ' ' + m.symbol) : (m.symbol + num);
}
/* Economia anuală (%) calculată pe prețurile AFIȘATE în valuta curentă. */
function planDisplaySavingsPct(plan, currency) {
    const mVal = planDisplayPrice(plan, 'monthly', currency);
    const yVal = planDisplayPrice(plan, 'yearly', currency);
    if (!mVal || !yVal) return 0;
    return Math.round((1 - yVal / (mVal * 12)) * 1000) / 10;
}
/* Cost estimat / jucător / lună, derivat din prețul AFIȘAT (fără neconcordanțe). */
function planDisplayPerPlayer(plan, currency) {
    const mVal = planDisplayPrice(plan, 'monthly', currency);
    const mem = (PLAN_INFO[plan] || {}).members;
    if (mVal == null || !mem || mem === Infinity) return null;
    return Math.round((mVal / mem) * 100) / 100;
}

function setPlansCurrency(cur) {
    _plansCurrency = CURRENCY_META[cur] ? cur : 'USD';
    const host = document.getElementById('plansCards'); if (host) host.innerHTML = plansCardsHtml();
    const tg = document.getElementById('plansCurrencyToggle'); if (tg) tg.innerHTML = plansCurrencyToggleHtml();
}
function plansCurrencyToggleHtml() {
    const mk = (cur) => `<button type="button" onclick="setPlansCurrency('${cur}')" class="pf-scope-btn${_plansCurrency === cur ? ' active' : ''}">${cur}</button>`;
    return mk('USD') + mk('EUR') + mk('RON');
}
const FEATURE_MIN_PLAN = {
    /* ⚠ `liveSessions` e SEPARAT de `timer` (iulie 2026). Erau legate: sesiunile
       live foloseau feature-ul `timer`, deci pe Starter modalul de upgrade vorbea
       despre CRONOMETRU când tu voiai o SESIUNE LIVE. Două funcționalități
       distincte, care se pot vinde și pot evolua independent. Ambele pornesc de
       la Basic, dar de acum fiecare pe cheia ei.
       EN: live sessions were gated behind the `timer` feature, so the upgrade
       modal talked about timers. Separate concerns, separate keys. */
    export: 'basic', regulament: 'basic', photos: 'basic', timer: 'basic', liveSessions: 'basic',
    elo: 'pro', scoresheets: 'pro', advancedStats: 'pro', coop: 'pro', campaigns: 'pro',
    tournaments: 'elite', clubAchievements: 'elite', crossClubStats: 'elite'
};
const FEATURE_LABEL = {
    export: 'Export rapoarte', regulament: 'Regulament', photos: 'Fotografii sesiune',
    elo: 'Mod competitiv (ELO)',
    scoresheets: 'Scoresheets custom', advancedStats: 'Statistici avansate (H-Index, win rate per rol)',
    timer: 'Cronometru sesiune & per jucător',
    liveSessions: 'Sesiuni live',
    coop: 'Cooperativ',
    campaigns: 'Campanii / Legacy',
    tournaments: 'Turnee', clubAchievements: 'Realizări de club',
    crossClubStats: 'Statistici inter-club'
};
/* Etichetă mică de plan (design uniform cu .sess-pro; doar culoarea diferă pe plan).
   inline=false pentru tile-uri poziționate (badge absolut, colț dreapta-sus). */
function planBadgeHtml(feature, inline) {
    const need = FEATURE_MIN_PLAN[feature];
    if (!need || need === 'starter') return '';
    const label = ((PLAN_INFO[need] || {}).label || need).toUpperCase();
    return '<b class="sess-pro plan-' + need + (inline === false ? '' : ' plan-inline') + '">' + label + '</b>';
}

function currentPlan() {
    const c = (state.myClubs || []).find(x => x.id === currentClubId);
    const raw = c ? (c.planType || c.plan) : null;
    const p = raw ? String(raw).toLowerCase() : 'starter';
    return PLAN_ORDER.indexOf(p) !== -1 ? p : 'starter';
}
/* Tier-uri de abonament (stil Kickstarter). Momentan toate funcțiile sunt
   deschise (beta gratuit). Prețurile sunt orientative, pregătite pentru Stripe. */
const PLAN_TIERS = [
    { key: 'starter', label: 'Starter', tagline: 'Pentru un grup mic de prieteni', members: 8, clubs: 1,
      perks: ['Sesiuni, scoruri & clasament', 'Chat de club', 'Calendar & evenimente (RSVP)', 'Poll-uri', 'Randomizere & vot de joc', 'Catalog cu integrare BoardGameGeek', 'Import istoric (BG Stats / ScorePal / CSV)', 'Sincronizare în cloud', 'PWA instalabilă'] },
    { key: 'basic', label: 'Basic', tagline: 'Pentru un club care crește', members: 20, clubs: 2,
      perks: ['Tot din Starter', 'Sesiuni live', 'Export rapoarte', 'Regulament de club', 'Fotografii sesiune', 'Cronometru sesiune & per jucător'] },
    { key: 'pro', label: 'Pro', tagline: 'Pentru cluburi active', members: 60, clubs: 5,
      perks: ['Tot din Basic', 'Mod competitiv ELO', 'Scoresheets custom', 'Statistici avansate (H-Index, win-rate/rol)', 'Cooperativ', 'Campanii / Legacy'] },
    { key: 'elite', label: 'Elite', tagline: 'Pentru rețele de cluburi', members: 150, clubs: '∞',
      perks: ['Tot din Pro', 'Turnee', 'Realizări de club', 'Statistici inter-club'] },
    { key: 'enterprise', label: 'Enterprise', tagline: 'Pentru organizații & federații', members: '∞', clubs: '∞',
      perks: ['Tot din Elite', 'SLA & suport dedicat', 'Onboarding asistat', 'Facturare/contract la cerere'] }
];

let _plansPeriod = 'monthly';
/* Carduri echilibrate: primele 5 perks vizibile, restul sub „Vezi toate funcțiile". */
let _plansPerksOpen = {};
function togglePlanPerks(key) {
    _plansPerksOpen[key] = !_plansPerksOpen[key];
    const host = document.getElementById('plansCards'); if (host) host.innerHTML = plansCardsHtml();
}
function setPlansPeriod(period) {
    _plansPeriod = (period === 'yearly') ? 'yearly' : 'monthly';
    const host = document.getElementById('plansCards'); if (host) host.innerHTML = plansCardsHtml();
    const tg = document.getElementById('plansToggle'); if (tg) tg.innerHTML = plansToggleHtml();
}
function plansToggleHtml() {
    const mk = (per, label) => `<button type="button" onclick="setPlansPeriod('${per}')" class="pf-scope-btn${_plansPeriod === per ? ' active' : ''}">${label}</button>`;
    return mk('monthly', t('plans.monthly')) + mk('yearly', t('plans.yearly'));
}
/* ============================================================================
   STAREA ABONAMENTULUI PE CARDUL PLANULUI ACTIV
   ----------------------------------------------------------------------------
   RO: Cardul planului curent nu are buton de plată (nu ai ce cumpăra din nou),
       dar rămânea complet gol — ceea ce ridică exact întrebarea la care omul
       vrea răspuns: „ce se întâmplă cu abonamentul meu?".
       Afișăm STAREA, nu un buton: se reînnoiește / se încheie / probă.
       ⚠ Deliberat FĂRĂ butoane care ating plăți. Gestionarea abonamentului
       (card, facturi, anulare, schimbare de plan) se face prin portalul Stripe,
       care e pasul următor și cere o funcție pe server.
   EN: Renders a subscription status line on the active plan card. No payment
       buttons here by design — billing actions belong in the Stripe portal.
   ============================================================================ */
function _planStatusDate(iso) {
    if (!iso) return '';
    try {
        /* Folosim helperul proiectului (`appDateLocale`), nu o detectare proprie. */
        const loc = (typeof appDateLocale === 'function') ? appDateLocale() : undefined;
        return new Date(iso).toLocaleDateString(loc, { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) { logError('_planStatusDate', e); return ''; }
}

function planStatusLineHtml(tierKey) {
    try {
        const u = state.userSub;
        if (!u) return '';
        if (tierKey === 'enterprise') return '';

        const când = _planStatusDate(u.current_period_end);
        /* ★ Ocupa exact locul butonului de plata: `margin-top:auto` il impinge la
           baza cardului, iar chenarul discret il face sa se citeasca drept
           „starea abonamentului", nu drept o nota pierduta in text. */
        const stil = 'font-size:.78em; margin-top:auto; padding:9px 10px; border-radius:10px;'
                   + ' display:flex; align-items:center; justify-content:center; gap:7px;'
                   + ' text-align:center; line-height:1.35; border:1px dashed;';

        /* Perioadă de probă: countdown-ul există deja separat; aici adăugăm data. */
        if (u.subscription_status === 'trialing') {
            return când
                ? `<div style="${stil} color:var(--accent-gold,#f9a826); border-color:rgba(249,168,38,0.45); background:rgba(249,168,38,0.07);"><i class="fas fa-hourglass-half"></i> ${t('plans.trialEndsOn')} ${când}</div>`
                : '';
        }

        /* Anulare programată: accesul continuă până la finalul perioadei plătite. */
        if (u.cancel_at_period_end) {
            return `<div style="${stil} color:var(--warning,#f5b301); border-color:rgba(245,179,1,0.45); background:rgba(245,179,1,0.07);"><i class="fas fa-circle-exclamation"></i> ${
                când ? t('plans.endsOn') + ' ' + când : t('plans.willEnd')}</div>`;
        }

        if (u.subscription_status === 'active') {
            const perioadă = (u.billing_period === 'yearly') ? t('plans.billedYearly')
                : (u.billing_period === 'monthly') ? t('plans.billedMonthly') : '';
            return `<div style="${stil} color:var(--success,#27ae60); border-color:rgba(39,174,96,0.45); background:rgba(39,174,96,0.07);"><i class="fas fa-rotate"></i> ${
                când ? t('plans.renewsOn') + ' ' + când : t('plans.autoRenews')}${perioadă ? ' · ' + perioadă : ''}</div>`;
        }

        /* Orice altă stare (neplătit, în așteptare): spunem că e o problemă,
           fără să pretindem că știm care — detaliile vin de la Stripe. */
        if (u.subscription_status) {
            return `<div style="${stil} color:var(--danger,#e74c3c); border-color:rgba(231,76,60,0.5); background:rgba(231,76,60,0.08);"><i class="fas fa-triangle-exclamation"></i> ${t('plans.needsAttention')}</div>`;
        }
        return '';
    } catch (e) { logError('planStatusLineHtml', e); return ''; }
}

/* Butonul „Gestioneaza abonamentul" -> Portalul de Facturare Stripe.
   Apare DOAR daca exista un abonament real la Stripe. Perioada de proba nu are
   client Stripe (e fara card), deci acolo butonul n-ar avea ce deschide. */
/* Are un abonament Stripe VIU? Daca da, un al doilea nu se mai cumpara: schimbarea
   de plan se face din portal, care il INLOCUIESTE pe cel curent (cu proportionalizare)
   in loc sa adauge unul peste. Proba locala (fara card, fara abonament Stripe) NU
   intra aici — de acolo trebuie sa se poata cumpara normal. */
function hasLiveStripeSub() {
    const u = state.userSub;
    if (!u || !u.stripe_subscription_id) return false;
    return ['active', 'trialing', 'past_due', 'unpaid'].indexOf(String(u.subscription_status || '')) !== -1;
}

function planPortalBtnHtml(tierKey) {
    try {
        if (tierKey === 'enterprise') return '';
        const u = state.userSub;
        if (!u || !u.subscription_status || u.subscription_status === 'trialing') return '';
        return `<button class="btn btn-sm btn-secondary" style="margin-top:8px;" onclick="openBillingPortal(this)">`
             + `<i class="fas fa-gear"></i> ${t('plans.manageBilling')}</button>`;
    } catch (e) { logError('planPortalBtnHtml', e); return ''; }
}

/* Cere serverului un link temporar catre portal si redirectioneaza.
   Toate deciziile sunt ale serverului: clientul NU trimite niciun identificator
   de client Stripe, ca sa nu poata cere portalul altcuiva. */
async function openBillingPortal(btn) {
    if (!state.currentUser) { showNotification(t('rbac.noPermission'), 'error'); return; }
    /* Dublu-clic pe un buton care duce la plati = doua sesiuni deschise. */
    if (btn) { try { btn.disabled = true; } catch (e) { logError('openBillingPortal', e); } }
    showNotification(t('plans.portalLoading'), 'info');
    try {
        /* ⚠ Adresa de retur TREBUIE sa poarte un parametru: `handleCheckoutReturn`
           se declanseaza doar daca il gaseste. Fara el, te intorci din portal si
           aplicatia nu afla ca s-a schimbat ceva (cardul ramanea verde dupa anulare).
           Folosim `billing=return`, NU `checkout=success`, ca sa nu afisam „plata
           reusita" cuiva care tocmai si-a anulat abonamentul. */
        const { data, error } = await sb.functions.invoke('create-portal-session', {
            body: { return_url: window.location.origin + '/?billing=return' }
        });
        const code = (data && data.error) || '';
        if (code === 'no_customer') { showNotification(t('plans.portalNoSub'), 'error'); return; }
        if (error || !data || !data.url) {
            showNotification(t('plans.portalError') + (code ? ' (' + code + ')' : ''), 'error');
            return;
        }
        /* La intoarcerea din portal, planul se poate fi schimbat: refolosim
           acelasi mecanism de reimprospatare ca dupa plata. */
        try { localStorage.setItem('gbgsc_checkout_return', '1'); } catch (e) { logError('openBillingPortal', e); }
        window.location.href = data.url;
    } catch (e) {
        logError('openBillingPortal', e);
        showNotification(t('plans.portalError'), 'error');
    } finally {
        if (btn) { try { btn.disabled = false; } catch (e) { logError('openBillingPortal', e); } }
    }
}

/* Cere preturile o singura data si re-randeaza fereastra cand sosesc. */
function ensurePlanPricesThenRender() {
    if (_planPrices) return;
    loadPlanPrices(false).then(function (m) {
        if (!m) return;
        try {
            const host = document.getElementById('plansCards');
            if (host) host.innerHTML = plansCardsHtml();
        } catch (e) { logError('ensurePlanPricesThenRender', e); }
    });
}

function plansCardsHtml() {
    const cur = (typeof userActivePlan === 'function') ? userActivePlan() : null;
    const yearly = _plansPeriod === 'yearly';
    return PLAN_TIERS.map(tier => {
        const isCur = tier.key === cur;
        const price = planDisplayPrice(tier.key, _plansPeriod, _plansCurrency);
        const perPlayer = planDisplayPerPlayer(tier.key, _plansCurrency);
        const sav = planDisplaySavingsPct(tier.key, _plansCurrency);
        const isEnterprise = tier.key === 'enterprise';
        /* ★ T3: ancorare decizionala — Pro e planul recomandat („Cel mai popular"). */
        const isPop = tier.key === 'pro';
        const priceBig = isEnterprise
            ? `<span style="font-size:0.62em;">${t('plans.enterprisePrice')}</span>`
            : (price != null)
                ? `${formatDisplayMoney(price, _plansCurrency)}<span style="font-size:.5em; opacity:.7;"> ${yearly ? t('plans.perYear') : t('plans.perMonth')}</span>`
                /* ⚠ NU `tier.price`: e text vechi, scris de mana (spunea ca Starter
                   e „Gratuit"). Cat timp preturile nu au sosit, aratam un marcaj
                   neutru — mai bine nicio cifra decat una gresita. */
                : `<span style="opacity:.45;">···</span>`;
        const savBadge = (yearly && sav > 0) ? `<div style="font-size:.78em; color:var(--success,#27ae60);"><i class="fas fa-gift"></i> −${sav}% · ${t('plans.save2months')}</div>` : '';
        const perPlayerLine = (perPlayer != null) ? `<div style="font-size:.78em; opacity:.7;"><i class="fas fa-user"></i> ${t('plans.estCost')}: ≈ ${formatDisplayMoney(perPlayer, _plansCurrency)} ${t('plans.perPlayer')}</div>` : '';
        const _curIdx = cur ? PLAN_ORDER.indexOf(cur) : -1;
        const _tierIdx = PLAN_ORDER.indexOf(tier.key);
        const _isDown = (_curIdx !== -1 && _tierIdx !== -1 && _tierIdx < _curIdx);
        /* ★ Cu abonament viu, butonul NU mai porneste o plata noua (ar fi creat un
           al doilea abonament, platit in paralel) — deschide portalul, unde
           schimbarea inlocuieste abonamentul curent. */
        const _areAbonament = (typeof hasLiveStripeSub === 'function') && hasLiveStripeSub();
        const _actiune = _areAbonament
            ? 'openBillingPortal(this)'
            : `startCheckout('${tier.key}','${_plansPeriod}')`;
        /* `!from` era de fapt „nu e Enterprise" (singurul care avea steagul `from`).
           Odata cu eliminarea preturilor din cod a disparut si steagul; exprimam
           conditia direct, cum ar fi trebuit sa fie de la inceput. */
        const checkoutBtn = (!isCur && !isEnterprise && !!state.currentUser)
            ? `<button class="btn btn-sm" style="margin-top:auto;" onclick="${_actiune}"><i class="fas fa-${_areAbonament ? 'right-left' : 'credit-card'}"></i> ${_isDown ? t('plans.downgrade') : t('plans.choose')}</button>`
            : '';
        /* ★ TRIAL: butonul apare DOAR daca serverul (my_trial_status) spune ca e eligibil.
           Enterprise e exclus (plan la cerere / contract). */
        const canTrial = (typeof trialEligible === 'function') && trialEligible() && !isEnterprise && !!state.currentUser;
        const trialBtn = canTrial
            ? `<button class="btn btn-sm btn-success" style="margin-top:6px;" onclick="startFreeTrial('${tier.key}')"><i class="fas fa-gift"></i> ${t('trial.cta')}</button>`
            : '';
        /* Countdown pe planul aflat in trial. */
        const _inTrial = (typeof isTrialing === 'function') && isTrialing();
        const trialBadge = (isCur && _inTrial)
            ? `<div style="font-size:.78em; color:var(--accent-gold,#f9a826);"><i class="fas fa-hourglass-half"></i> ${t('trial.expiresIn').replace('{n}', String(trialDaysLeft()))}</div>`
            : '';
        const _MAXP = 5;
        const _open = !!_plansPerksOpen[tier.key];
        const _shownPerks = _open ? tier.perks : tier.perks.slice(0, _MAXP);
        const _moreN = tier.perks.length - _MAXP;
        const perksMoreBtn = (_moreN > 0)
            ? `<li><button type="button" class="plans-more-btn" onclick="togglePlanPerks('${tier.key}')"><i class="fas fa-chevron-${_open ? 'up' : 'down'}"></i> ${_open ? t('plans.seeLess') : (t('plans.seeAll') + ' (+' + _moreN + ')')}</button></li>`
            : '';
        return `<div style="position:relative; flex:1; min-width:230px; max-width:300px; background:var(--bg-card,#1e1e2e); border:2px solid ${isCur ? 'var(--accent-primary,#ff6b35)' : (isPop ? 'var(--accent-gold,#f9a826)' : 'var(--border,#333)')}; border-radius:16px; padding:18px; display:flex; flex-direction:column; gap:6px; ${isCur ? 'box-shadow:0 0 0 3px rgba(255,107,53,0.25);' : (isPop ? 'box-shadow:0 0 0 3px rgba(249,168,38,0.18);' : '')}">${isPop ? `<span style="position:absolute; top:0; left:50%; transform:translate(-50%,-55%); background:var(--accent-gold,#f9a826); color:#1a1a2e; font-size:0.68em; font-weight:800; padding:3px 12px; border-radius:999px; white-space:nowrap; letter-spacing:0.3px; box-shadow:0 2px 8px rgba(0,0,0,0.35);"><i class="fas fa-star"></i> ${t('plans.mostPopular')}</span>` : ''}
            <div style="display:flex; justify-content:space-between; align-items:center;">
                <span style="font-size:1.25em; font-weight:800; color:var(--accent-gold,#f9a826);">${tier.label}</span>
                ${isCur ? '<span style="font-size:0.7em; background:var(--accent-primary,#ff6b35); color:#fff; border-radius:8px; padding:2px 8px;">' + t('plans.yourPlan') + '</span>' : ''}
            </div>
            <div style="font-size:1.5em; font-weight:800;">${priceBig}</div>
            ${trialBadge}${savBadge}${perPlayerLine}
            <div style="opacity:0.75; font-size:0.85em; min-height:30px; margin-top:4px;">${window.ptr(tier.tagline)}</div>
            <div style="font-size:0.85em; opacity:0.9;"><i class="fas fa-users"></i> ${tier.members} ${t('plans.membersWord')} · <i class="fas fa-layer-group"></i> ${tier.clubs} ${tier.clubs === 1 ? t('plans.clubWord') : t('plans.clubsWord')}</div>
            <ul style="list-style:none; padding:0; margin:8px 0 8px; display:flex; flex-direction:column; gap:6px;">
                ${_shownPerks.map(p => `<li style="font-size:0.86em;"><i class="fas fa-check" style="color:var(--success,#27ae60); margin-right:8px;"></i>${window.ptr(p)}</li>`).join('')}
                ${perksMoreBtn}
            </ul>
            ${isCur ? planStatusLineHtml(tier.key) : ''}${isCur ? planPortalBtnHtml(tier.key) : ''}${checkoutBtn}${trialBtn}
        </div>`;
    }).join('');
}
/* Info planuri (un singur (i), click-friendly pe mobil). */
function showPlansInfo() {
    const m = document.createElement('div');
    m.className = 'modal active';
    m.onclick = (e) => { if (e.target === m) m.remove(); };
    m.innerHTML = `<div class="modal-content" style="max-width:440px;">
        <h3 style="margin-top:0;"><i class="fas fa-circle-info"></i> ${t('plans.title')}</h3>
        <p style="opacity:0.85; line-height:1.6;">${t('plans.subtitle')}</p>
        <p style="opacity:0.85; line-height:1.6;">${t('plans.currencyNote')}</p>
        <div class="btn-group" style="margin-top:14px;"><button class="btn btn-secondary" onclick="this.closest('.modal').remove()">${t('common.close')}</button></div>
    </div>`;
    document.body.appendChild(m);
}
function openPlansModal() {
    /* Preturile vin din baza de date; cand sosesc, cardurile se re-randeaza. */
    try { ensurePlanPricesThenRender(); } catch (e) { logError('openPlansModal', e); }
    const modal = document.createElement('div');
    modal.className = 'modal active';
    modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
    modal.innerHTML = `<div class="modal-content" style="max-width:1050px;">
        <h2 class="card-title"><i class="fas fa-rocket"></i> ${t('plans.title')}
            <i class="fas fa-circle-info" onclick="showPlansInfo()" style="font-size:0.6em; opacity:0.6; cursor:pointer; margin-left:6px;"></i>
        </h2>
        <div style="text-align:center; margin-bottom:6px; display:flex; gap:10px; justify-content:center; align-items:center; flex-wrap:wrap;">
            <span id="plansToggle" class="pf-scope-toggle">${plansToggleHtml()}</span>
            <span id="plansCurrencyToggle" class="pf-scope-toggle">${plansCurrencyToggleHtml()}</span>
        </div>
        <div id="plansCards" style="display:flex; flex-wrap:wrap; gap:14px; justify-content:center; margin-top:10px;">${plansCardsHtml()}</div>
        <div class="btn-group" style="margin-top:18px;">
            <button class="btn btn-secondary" onclick="this.closest('.modal').remove()">${t('common.close')}</button>
        </div>
    </div>`;
    document.body.appendChild(modal);
    /* ★ T2: prețurile afișate sunt fixe per valută — nu mai e nevoie de cursuri live. */
    /* ★ TRIAL: intrebam serverul daca userul mai e eligibil, apoi repictam cardurile. */
    if (typeof loadTrialStatus === 'function') {
        loadTrialStatus(true).then(function () {
            const host = document.getElementById('plansCards');
            if (host) host.innerHTML = plansCardsHtml();
        });
    }
}

/* Modal „Upgrade necesar" — afișat DOAR Adminului Principal (cei care nu pot
   gestiona planul nu văd prompturi financiare; lor li se ascunde funcția). */
function showUpgradeModal(opts) {
    opts = opts || {};
    let bodyHtml;
    if (opts.clubLimit) {
        const np = (typeof nextPlanUp === 'function') ? nextPlanUp(userActivePlan()) : null;
        bodyHtml = `${t('upgrade.clubLimit')}` + ((opts.max != null && isFinite(opts.max)) ? ` (${opts.max}).` : '.') + (np ? ` ${t('upgrade.suggestPlan')} <strong>${(PLAN_INFO[np] || {}).label}</strong>.` : '');
    } else if (opts.memberLimit) {
        const lim = planMemberLimit();
        const np = opts.neededPlan || nextPlanUp();
        bodyHtml = `${t('upgrade.memberLimit')} (${lim}).` + (np ? ` ${t('upgrade.suggestPlan')} <strong>${(PLAN_INFO[np] || {}).label}</strong>.` : '');
    } else {
        const feat = opts.feature;
        const need = opts.neededPlan || (feat && FEATURE_MIN_PLAN[feat]);
        const label = feat ? (window.ptr(FEATURE_LABEL[feat] || '') || t('feat.thisFeature')) : t('feat.thisFeature');
        bodyHtml = `<strong>${label}</strong> ${t('upgrade.featureFrom')} <strong>${(PLAN_INFO[need] || {}).label || ''}</strong>${t('feat.planSuffix')}`;
    }
    const modal = document.createElement('div');
    modal.className = 'modal active';
    modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
    modal.innerHTML = `<div class="modal-content" style="max-width:460px; text-align:center;">
        <div style="font-size:2.4em;">🚀</div>
        <h2 style="margin:6px 0;">${t('upgrade.title')}</h2>
        <p style="opacity:0.85; margin-bottom:16px;">${bodyHtml}</p>
        <div class="btn-group" style="justify-content:center;">
            <button class="btn" onclick="this.closest('.modal').remove(); openPlansModal();"><i class="fas fa-rocket"></i> ${t('upgrade.cta')}</button>
            <button class="btn btn-secondary" onclick="this.closest('.modal').remove()">${t('upgrade.later')}</button>
        </div>
    </div>`;
    document.body.appendChild(modal);
}

/* Gating declarativ pe plan: ascunde elementele [data-plan="<plan>"] dacă planul
   clubului e inferior. Principalul (plan.manage) le vede ca să poată face upgrade;
   ceilalți doar nu le văd (fără prompturi financiare). Apelat din render(). */
function applyPlanGating() {
    try {
        const isPrincipal = (typeof can === 'function') && can('plan.manage');
        document.querySelectorAll('[data-plan]').forEach(el => {
            const need = el.getAttribute('data-plan');
            const ok = (typeof planRank === 'function') && planRank(currentPlan()) >= planRank(need);
            el.style.display = (ok || isPrincipal) ? '' : 'none';
        });
    } catch (e) { logError('applyPlanGating', e); }
}
function planRank(p) { return PLAN_ORDER.indexOf(p); }
function planAllows(feature) {
    const need = FEATURE_MIN_PLAN[feature];
    if (!need) return true;
    if (state.currentUser && state.currentUser.isDemo) return true;
    return planRank(currentPlan()) >= planRank(need);
}
function planMemberLimit() {
    const c = (state.myClubs || []).find(x => x.id === currentClubId);
    if (c && c.memberLimit != null && c.memberLimit !== '') return Number(c.memberLimit) || Infinity;
    return (PLAN_INFO[currentPlan()] || {}).members || Infinity;
}
function currentMemberCount() { return Object.keys(state.members || {}).length; }
function requireFeature(feature) {
    if (planAllows(feature)) return true;
    const need = FEATURE_MIN_PLAN[feature];
    if (typeof can === 'function' && can('plan.manage')) {
        showUpgradeModal({ feature: feature, neededPlan: need });
    } else {
        showNotification((window.ptr(FEATURE_LABEL[feature] || '') || t('feat.thisFeature')) + ' ' + t('feat.availFrom') + ' ' + ((PLAN_INFO[need] || {}).label || '') + t('feat.planSuffix'), 'info');
    }
    return false;
}
function requireMemberSlot() {
    const lim = planMemberLimit();
    if (currentMemberCount() >= lim) {
        if (typeof can === 'function' && can('plan.manage')) {
            showUpgradeModal({ memberLimit: true, neededPlan: nextPlanUp() });
        } else {
            showNotification(t('upgrade.memberLimit') + ' (' + lim + ').', 'error');
        }
        return false;
    }
    return true;
}
function featureLockNote(feature) {
    if (planAllows(feature)) return '';
    const need = FEATURE_MIN_PLAN[feature];
    const isAdm = (currentClubMyRole() === 'principal_admin' || currentClubMyRole() === 'secondary_admin');
    return `<div style="padding:14px; border:1px dashed var(--border,#444); border-radius:10px; text-align:center;">
        <div style="font-size:1.4em;">🔒</div>
        <div style="font-weight:600; margin:4px 0;">${window.ptr(FEATURE_LABEL[feature] || '') || t('feat.advancedFeature')}</div>
        <div style="font-size:0.85em; opacity:0.8;">${t('feat.availableFromPlan')} <strong>${(PLAN_INFO[need] || {}).label}</strong>${t('feat.planSuffix')}
        ${isAdm ? t('feat.changePlan') : t('feat.askAdmin')}</div>
    </div>`;
}

function activeClub() { return (state.myClubs || []).find(c => c.id === currentClubId) || null; }
function clubDisplayName() { const c = activeClub(); return (c && c.name) || 'TurnOrder'; }
/* Reluare plată pentru un club rămas „pending" (blocat). Setează clubul activ
   (ușor, fără hydrate complet) și deschide planurile pentru checkout. */
async function resumeClubPayment(clubId) {
    try { const menu = document.getElementById('clubMenu'); if (menu) menu.classList.add('hidden'); } catch (e) { logError('resumeClubPayment', e); }
    currentClubId = clubId;
    try { if (state.currentUser) localStorage.setItem(clubStorageKey(state.currentUser.id), clubId); } catch (e) { logError('resumeClubPayment', e); }
    try { applyClubRole(); } catch (e) { logError('resumeClubPayment', e); }
    if (typeof openPlansModal === 'function') openPlansModal();
}

/* Tag colorat cu nivelul de abonament (Starter/Basic/Pro/Elite/Enterprise). */
function planTagHtml(c) {
    if (c && c.status === 'pending') return '';
    let p;
    const owned = !!(c && state.currentUser && (c.ownerId === state.currentUser.id || c.myRole === 'principal_admin'));
    if (owned && typeof userActivePlan === 'function' && userActivePlan()) p = userActivePlan();
    else p = String((c && (c.planType || c.plan)) || 'starter').toLowerCase();
    const map = { starter: ['Starter', '#6b7280'], basic: ['Basic', '#3b82f6'], pro: ['Pro', '#8b5cf6'], elite: ['Elite', '#f59e0b'], enterprise: ['Enterprise', '#10b981'] };
    const e = map[p] || map.starter;
    return ' <span style="font-size:0.68em; background:' + e[1] + '; color:#fff; border-radius:6px; padding:1px 6px; font-weight:600; white-space:nowrap;">' + e[0] + '</span>';
}

function clubStatusLabel(s) {
    return window.ptr(({ active: 'activ', blocked: 'blocat', read_only: 'doar-citire', archived: 'arhivat', closed: 'închis', pending: 'în așteptare plată' })[s] || s || '');
}
function isClubReadOnly() {
    const c = activeClub();
    return !!(c && ((c.status && c.status !== 'active') || c.overLimit));
}
function requireWritableClub() {
    /* Regulamentul neacceptat blochează SCRIERILE, nu citirea. Poarta e aceeași
       pentru toate acțiunile care modifică ceva, deci n-a trebuit atinsă niciuna
       dintre ele individual. */
    if (typeof rulesAckPending === 'function' && rulesAckPending()) {
        showNotification(t('rulesack.blocked'), 'warning');
        if (typeof showRulesAckModal === 'function') showRulesAckModal();
        return false;
    }
    if (isClubReadOnly()) {
        const c = activeClub();
        showNotification(t('sb.clubStatusNoChanges').replace('{name}', (c ? c.name : '')).replace('{status}', clubStatusLabel(c && c.status)), 'error');
        return false;
    }
    if (isMuted) {
        showNotification(t('sb.viewOnlyMuted'), 'info');
        return false;
    }
    return true;
}

/* ===== ACCEPTAREA REGULAMENTULUI DE CLUB ====================================
   RO: Când adminul activează comutatorul, membrii trebuie să citească și să
       accepte regulamentul înainte de a putea SCRIE ceva în club. Citirea rămâne
       liberă — altfel n-ar avea ce accepta.

       Verificarea se face la HIDRATARE, nu pe un anumit ecran. Așa acoperă
       automat toate căile de intrare: alăturare directă, cerere aprobată,
       invitație prin link, și membrii vechi când adminul publică o versiune nouă.
       Un membru venit prin invitație e membru activ din clipa în care ajunge în
       club, deci `my_club_rules_state` îl prinde la prima încărcare, fără cod
       special pe traseul de invitație.

       Se cere din nou la FIECARE versiune nouă de regulament: altfel comutatorul
       ar deveni un formular bifat o dată și uitat.
   EN: acceptance is checked at hydration, so every join path — direct, approved
       request, invite link — is covered without special-casing any of them.
   ============================================================================ */
let _rulesAckState = null;      /* ultimul răspuns de la my_club_rules_state */
/* RO: Contor de „generație". La intrarea într-un club, hidratarea se declanșează
       de mai multe ori (alăturare, `switchClub`, apoi evenimentul realtime de pe
       `club_members`), deci verificarea pleca de trei ori. Fiecare răspuns
       deschidea fereastra — utilizatorul o accepta, iar un răspuns pornit ÎNAINTE
       de acceptare sosea după și o redeschidea. De trei ori la rând.
       Contorul crește la fiecare acceptare și la fiecare schimbare de club: un
       răspuns întors cu o generație veche e aruncat, fiindcă descrie o stare care
       nu mai există.
   EN: hydration fires several times when joining; in-flight checks started before
       the acceptance came back afterwards and reopened the dialog. */
let _rulesAckSeq = 0;
let _rulesAckInFlight = false;

function rulesAckPending() {
    return !!(_rulesAckState && _rulesAckState.must_accept);
}
window.rulesAckPending = rulesAckPending;

async function refreshRulesAckState() {
    if (isDemoMode() || !sb || !currentClubId) { _rulesAckState = null; return; }
    /* O singură verificare în zbor. Trei hidratări apropiate nu au ce afla diferit
       una de alta, iar trei cereri produceau trei ferestre. */
    if (_rulesAckInFlight) return;
    _rulesAckInFlight = true;
    const gen = _rulesAckSeq;
    const clubLaPornire = currentClubId;
    try {
        const r = await sb.rpc('my_club_rules_state', { p_club_id: clubLaPornire });
        /* Răspuns învechit: între timp s-a acceptat sau s-a schimbat clubul. */
        if (gen !== _rulesAckSeq || clubLaPornire !== currentClubId) return;
        _rulesAckState = (!r.error && r.data) ? r.data : null;
    } catch (e) {
        logError('refreshRulesAckState', e);
    } finally {
        _rulesAckInFlight = false;
    }
    /* Nu redeschidem peste o fereastră deja afișată. */
    if (rulesAckPending() && !document.getElementById('rulesAckModal')) showRulesAckModal();
}
window.refreshRulesAckState = refreshRulesAckState;

function showRulesAckModal(fortat) {
    /* RO: Amânare cu un tic. Modalul e cerut din `requireWritableClub()`, adică
           din INTERIORUL unui `onclick`. Creat sincron, apărea și era închis
           imediat: fluxurile care eșuează cheamă `closeAnyModal()`, iar acela ia
           primul `.modal.active` din pagină — care tocmai devenise al nostru.
           De asta „apărea doar la refresh": la refresh venea din hidratare, în
           afara oricărui click. Cu `setTimeout(0)` se adaugă după ce evenimentul
           curent și curățenia lui s-au încheiat.
       EN: deferred by a tick — created synchronously inside a click handler it was
           immediately swept away by the failing flow's own closeAnyModal(). */
    setTimeout(function () { _buildRulesAckModal(fortat); }, 0);
}
window.showRulesAckModal = showRulesAckModal;

function _buildRulesAckModal(fortat) {
    try {
        /* RO: Ștergem un eventual exemplar rămas, în loc să ieșim devreme. Vechiul
               `if (getElementById(...)) return;` transforma orice exemplar rămas
               invizibil în „butonul nu face nimic" — fără eroare, fără urmă.
               O reconstrucție e ieftină; o tăcere e imposibil de diagnosticat.
           EN: rebuild instead of bailing out when a leftover node exists; the early
               return turned any stale node into a silently dead button. */
        const vechi = document.getElementById('rulesAckModal');
        if (vechi) vechi.remove();
        /* `fortat` = apel explicit din bannerul tabului de regulament. Acolo
           utilizatorul a cerut fereastra; n-o refuzăm dacă starea nu s-a
           reîmprospătat încă. */
        if (!fortat && typeof rulesAckPending === 'function' && !rulesAckPending()) return;
    const arts = ((state.regulation && state.regulation.articles) || []);
    const esc = window.escHtml;
    const c = (typeof activeClub === 'function') ? activeClub() : null;

    const corp = arts.length
        ? arts.map(a => `
            <div class="reg-article" style="margin-bottom:12px;">
                <div class="reg-article-title">${esc(a.title || '')}</div>
                <div class="reg-article-body">${(a.content || '').split('\n').map(esc).join('<br>')}</div>
            </div>`).join('')
        : `<p style="color:var(--text-secondary);">${esc(t('reg.empty'))}</p>`;

    const m = document.createElement('div');
    m.className = 'modal active';
    m.id = 'rulesAckModal';
    /* Fără buton „×" și fără închidere la click în fundal: e o poartă, nu o
       fereastră informativă. Ieșirea din club rămâne posibilă prin comutatorul
       de club din antet. */
    m.innerHTML = `
        <div class="modal-content" style="max-width:720px;">
            <div class="modal-header">
                <h2><i class="fas fa-gavel"></i> ${esc(t('rulesack.title'))}</h2>
            </div>
            <p style="color:var(--text-secondary); margin-bottom:12px;">
                ${esc(t('rulesack.intro').replace('{club}', (c && c.name) || ''))}
            </p>
            <div id="rulesAckBody" style="max-height:48vh; overflow-y:auto; border:1px solid var(--border); border-radius:10px; padding:14px;">
                ${corp}
            </div>
            <label style="display:flex; gap:10px; align-items:flex-start; margin-top:14px; font-weight:normal;">
                <input type="checkbox" id="rulesAckCheck" onchange="_rulesAckToggle()">
                <span>${esc(t('rulesack.checkbox'))}</span>
            </label>
            <div class="btn-group" style="margin-top:16px;">
                <button type="button" class="btn btn-primary" id="rulesAckBtn" disabled onclick="acceptClubRules()">
                    <i class="fas fa-check"></i> ${esc(t('rulesack.accept'))}
                </button>
            </div>
            <p style="color:var(--text-secondary); font-size:.82em; margin-top:10px;">
                <i class="fas fa-circle-info"></i> ${esc(t('rulesack.readOnlyHint'))}
            </p>
        </div>`;
        document.body.appendChild(m);
    } catch (e) {
        /* Fără catch, o excepție într-un `setTimeout` dispărea în consolă și
           butonul părea pur și simplu mort. */
        logError('rulesAckModal', e);
        showNotification(t('rulesack.error').replace('{msg}', (e && e.message) || ''), 'error');
    }
}

/* Butonul se deblochează doar după bifă ȘI după ce textul a fost derulat până
   la capăt: o casetă bifată fără să fi ajuns jos nu înseamnă „am citit". */
function _rulesAckToggle() {
    const cb = document.getElementById('rulesAckCheck');
    const btn = document.getElementById('rulesAckBtn');
    if (btn) btn.disabled = !(cb && cb.checked);
}
window._rulesAckToggle = _rulesAckToggle;

async function acceptClubRules() {
    const btn = document.getElementById('rulesAckBtn');
    if (btn) btn.disabled = true;
    try {
        const r = await sb.rpc('accept_club_rules', { p_club_id: currentClubId });
        if (r.error) {
            showNotification(t('rulesack.error').replace('{msg}', r.error.message || ''), 'error');
            if (btn) btn.disabled = false;
            return;
        }
        /* Generație nouă: orice verificare pornită înainte de acest moment devine
           irelevantă și nu mai are voie să redeschidă fereastra. */
        _rulesAckSeq++;
        _rulesAckState = Object.assign({}, _rulesAckState, {
            must_accept: false,
            accepted_version: (r.data && r.data.version) || null
        });
        const m = document.getElementById('rulesAckModal');
        if (m) m.remove();
        showNotification(t('rulesack.done'), 'success');
        if (typeof renderClubReadonlyBanner === 'function') renderClubReadonlyBanner();
        /* Bannerul din tabul de regulament trebuie să dispară pe loc. */
        if (window.TO_CHAT && window.TO_CHAT.readyModule && window.TO_CHAT.readyModule('TO_REG')) { try { renderRegulation(); } catch (e) { logError('acceptClubRules.render', e); } }   /* R2: regulation.js la cerere */
    } catch (e) {
        logError('acceptClubRules', e);
        if (btn) btn.disabled = false;
    }
}
window.acceptClubRules = acceptClubRules;

function renderClubReadonlyBanner() {
    let b = document.getElementById('clubReadonlyBanner');
    const muted = !!isMuted;
    if (isClubReadOnly() || muted) {
        if (!b) {
            b = document.createElement('div');
            b.id = 'clubReadonlyBanner';
            /* Istoric (3 încercări, ca să nu se mai repete):
                 • `sticky`  -> trecea PESTE header;
                 • `relative` în #mainContainer -> era DECUPAT de container;
                 • `fixed`   -> se vedea, dar acoperea conținutul la SCROLL.
               Soluția: flux NORMAL, direct în <body> (nu în mainContainer, care taie).
               Ocupă spațiu real => împinge tot conținutul în jos, nu acoperă nimic,
               nu poate fi decupat, iar la derulare pur și simplu iese din ecran. */
            b.style.cssText = 'position:static; display:block; width:100%;'
                + ' color:#fff; text-align:center; padding:10px 14px; font-weight:600;'
                + ' font-size:0.9em; line-height:1.35; box-sizing:border-box;';
            document.body.insertBefore(b, document.body.firstChild);
        }
        if (muted && !isClubReadOnly()) {
            b.textContent = window.t('banner.muted');
            b.style.background = '#9a6b00';
        } else {
            const c = activeClub();
            if (c && c.overLimit) {
                /* Club înghețat la downgrade (peste limita planului) — distinct (amber) de read-only-ul de neplată. */
                const iAmOwner = !!(state.currentUser && (c.ownerId === state.currentUser.id || c.myRole === 'principal_admin'));
                if (iAmOwner) {
                    b.innerHTML = '<span style="display:inline-flex; align-items:center; gap:10px; flex-wrap:wrap; justify-content:center;">'
                        + '<span>⚠️ ' + window.t('overlimit.bannerOwner') + '</span>'
                        + '<button onclick="openChooseActiveClubsModal()" style="background:#fff; color:#9a4b00; border:none; border-radius:8px; padding:5px 12px; font-weight:700; cursor:pointer; font-size:0.92em;">' + window.t('overlimit.chooseBtn') + '</button>'
                        + '</span>';
                } else {
                    b.innerHTML = '<span>⏸️ ' + window.t('overlimit.bannerMember') + '</span>';
                }
                b.style.background = '#c47a1e';
            } else {
                const claimBtn = (typeof claimClubButtonHtml === 'function') ? claimClubButtonHtml(c, { small: true }) : '';
                b.innerHTML = '<span style="display:inline-flex; align-items:center; gap:10px; flex-wrap:wrap; justify-content:center;">'
                    + '<span>🔒 ' + window.t('banner.readonlyPre') + ' „' + (c ? (typeof clubEsc === 'function' ? clubEsc(c.name) : c.name) : '') + '" — ' + clubStatusLabel(c && c.status) + '.</span>'
                    + claimBtn + '</span>';
                b.style.background = '#b23b3b';
            }
        }
        b.style.display = 'block';
    } else if (b) {
        /* Îl ELIMINĂM din DOM, nu doar îl ascundem: altfel rămânea agățat după
           logout (nodul exista în continuare, doar cu display:none — și reapărea). */
        b.remove();
    }
}

/* Curăță bannerul necondiționat. Se apelează la logout / resetare de stare,
   când nu mai există niciun club activ de raportat. */
function clearClubReadonlyBanner() {
    const b = document.getElementById('clubReadonlyBanner');
    if (b) b.remove();
    document.body.style.paddingTop = '';   /* curăță și reziduul variantei „fixed" */
}
window.clearClubReadonlyBanner = clearClubReadonlyBanner;

/* ---------- Over-limit: owner alege ce cluburi rămân active (o singură dată) ---------- */
async function openChooseActiveClubsModal() {
    const existing = document.getElementById('chooseClubsModal'); if (existing) existing.remove();
    let st = null;
    try { const r = await sb.rpc('my_over_limit_state'); if (r && !r.error && r.data && r.data[0]) st = r.data[0]; } catch (e) { logError('openChooseActiveClubsModal', e); }
    const locked = !!(st && st.locked);
    const limFromRpc = (st && st.club_limit != null) ? Number(st.club_limit) : null;
    const plan = (typeof userActivePlan === 'function') ? userActivePlan() : null;
    const limit = (limFromRpc != null) ? limFromRpc : ((typeof planClubLimit === 'function') ? planClubLimit(plan) : Infinity);
    const lim = (limit === Infinity || !isFinite(limit)) ? 999 : limit;
    const clubs = (state.myClubs || []).filter(c => c.myRole === 'principal_admin' && c.status !== 'archived' && c.status !== 'closed');
    const rows = clubs.map(function (c) {
        const checked = !c.overLimit ? ' checked' : '';
        const dis = locked ? ' disabled' : '';
        const nm = (typeof clubEsc === 'function') ? clubEsc(c.name) : c.name;
        return '<label style="display:flex; align-items:center; gap:10px; padding:9px 4px; border-bottom:1px solid var(--border,#2a2a2a); cursor:' + (locked ? 'default' : 'pointer') + ';">'
            + '<input type="checkbox" class="choose-club-cb" value="' + c.id + '"' + checked + dis + ' onchange="_chooseClubsUpdateCount()">'
            + '<span style="flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">' + nm + '</span>'
            + '</label>';
    }).join('');
    const footer = locked
        ? '<p style="color:#c47a1e; font-size:0.86em; margin:12px 0 2px;"><i class="fas fa-lock"></i> ' + window.t('overlimit.locked') + '</p>'
        : '<div id="chooseClubsCount" style="font-size:0.85em; color:var(--text-secondary); margin:10px 0 4px;"></div>'
          + '<button class="btn" style="width:100%;" onclick="saveChosenActiveClubs(this)"><i class="fas fa-check"></i> ' + window.t('common.save') + '</button>';
    const modal = document.createElement('div');
    modal.className = 'modal active'; modal.id = 'chooseClubsModal';
    modal.onclick = function (e) { if (e.target === modal) modal.remove(); };
    modal.innerHTML =
        '<div class="modal-content" style="max-width:460px;">'
        + '<div class="modal-header">'
        +   '<h2><i class="fas fa-layer-group"></i> ' + window.t('overlimit.modalTitle') + '</h2>'
        +   '<button class="modal-close" onclick="this.closest(\'.modal\').remove()">×</button>'
        + '</div>'
        + '<p style="color:var(--text-secondary); font-size:0.9em; margin:0 0 10px;">' + window.t('overlimit.modalIntro').replace('{n}', String(lim)) + '</p>'
        + '<div id="chooseClubsList">' + rows + '</div>'
        + footer
        + '</div>';
    document.body.appendChild(modal);
    modal._limit = lim;
    if (!locked) _chooseClubsUpdateCount();
}
function _chooseClubsUpdateCount() {
    const modal = document.getElementById('chooseClubsModal'); if (!modal) return;
    const lim = modal._limit || 1;
    const n = modal.querySelectorAll('.choose-club-cb:checked').length;
    const el = document.getElementById('chooseClubsCount');
    if (el) el.textContent = n + '/' + lim + ' ' + window.t('overlimit.selected');
}
async function saveChosenActiveClubs(btn) {
    const modal = document.getElementById('chooseClubsModal'); if (!modal) return;
    const lim = modal._limit || 1;
    const ids = Array.prototype.slice.call(modal.querySelectorAll('.choose-club-cb:checked')).map(function (cb) { return cb.value; });
    if (ids.length > lim) { showNotification(t('overlimit.tooMany').replace('{n}', String(lim)), 'error'); return; }
    if (btn) { btn.disabled = true; btn.style.opacity = '0.6'; }
    try {
        const r = await sb.rpc('set_active_clubs', { p_keep_ids: ids });
        if (r.error) throw r.error;
        try { await sb.rpc('refresh_my_over_limit'); } catch (e) { logError('saveChosenActiveClubs', e); }
        await loadMyClubsAndSelect({ id: state.currentUser.id });
        try { applyClubRole(); } catch (e) { logError('saveChosenActiveClubs', e); }
        try { renderClubReadonlyBanner(); } catch (e) { logError('saveChosenActiveClubs', e); }
        try { if (typeof renderClubSwitcher === 'function') renderClubSwitcher(); } catch (e) { logError('saveChosenActiveClubs', e); }
        modal.remove();
        showNotification(t('overlimit.saved'), 'success');
    } catch (e) {
        if (btn) { btn.disabled = false; btn.style.opacity = ''; }
        const msg = (e && (e.message || e)) || '';
        if (String(msg).indexOf('CHOICE_LOCKED') >= 0) {
            showNotification(t('overlimit.lockedToast'), 'error');
            try { modal.remove(); } catch (_) { logError('saveChosenActiveClubs', _); }
            return;
        }
        showNotification(t('sb.errorColon') + msg, 'error');
    }
}
window.openChooseActiveClubsModal = openChooseActiveClubsModal;
window._chooseClubsUpdateCount = _chooseClubsUpdateCount;
window.saveChosenActiveClubs = saveChosenActiveClubs;

/* ---------- Moderare: ban (excludere) & mute (mod vizualizare) ---------- */
async function loadModeration() {
    state.memberMuted = {}; state.bannedList = []; isMuted = false;
    state.memberRoleById = {}; state.memberRoleByName = {};
    if (isDemoMode() || !sb || !currentClubId) return;
    let data = null;
    try {
        const r = await sb.from('club_members').select('profile_id, status, muted, role, profiles(name)').eq('club_id', currentClubId);
        if (r.error) throw r.error;
        data = r.data;
    } catch (e) {
        try {
            const r2 = await sb.from('club_members').select('profile_id, status, role, profiles(name)').eq('club_id', currentClubId);
            data = (r2.data || []).map(x => Object.assign({}, x, { muted: false }));
        } catch (e2) { data = []; }
    }
    (data || []).forEach(m => {
        const nm = (m.profiles && m.profiles.name) || null;
        const role = m.role || 'member';
        if (m.status === 'active') {
            state.memberMuted[m.profile_id] = !!m.muted;
            state.memberRoleById[m.profile_id] = role;
            if (nm) state.memberRoleByName[nm] = role;
        }
        if (state.currentUser && m.profile_id === state.currentUser.id) isMuted = (m.status === 'active' && !!m.muted);
    });
    if (state.currentUser) state.currentUser.muted = isMuted;

    /* Lista de excluși (ban) — sursa de adevăr e club_bans (persistă peste leave/rejoin). */
    try {
        const rb = await sb.from('club_bans').select('profile_id, profiles(name)').eq('club_id', currentClubId);
        if (!rb.error && Array.isArray(rb.data)) {
            state.bannedList = rb.data.map(b => ({ id: b.profile_id, name: (b.profiles && b.profiles.name) || '—' }));
        }
    } catch (e) { /* tabelul poate lipsi înainte de aplicarea 38_club_bans.sql */ }
}

function _modGuard(profileId) {
    if (!isAdmin) { showNotification(t('sb.onlyAdminsThis'), 'error'); return false; }
    if (state.currentUser && profileId === state.currentUser.id) { showNotification(t('sb.cantApplySelf'), 'error'); return false; }
    return true;
}

async function muteMember(profileId, playerName) {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    /* DOAR admini (secundar/principal) — capabilitate, nu flag-ul legacy `isAdmin`.
       Rangul e impus ȘI server-side, în RPC (clientul e doar UX). */
    if (typeof can === 'function' && !can('member.mute')) { showNotification(t('rbac.noPermission'), 'error'); return; }
    if (!_modGuard(profileId)) return;
    /* Paritate cu ban: confirmare explicită. FĂRĂ `danger` (mute e reversibil) și
       FĂRĂ reauth (impact mic, spre deosebire de excludere). */
    if (!await confirmModal(t('sb.confirmMute').replace('{name}', (playerName || t('sb.thisMember'))))) return;

    /* ATOMIC (§23.c): rangul, protecțiile (nu te auto-muți, nu muți un admin) și
       scrierea se fac SERVER-SIDE, într-un singur pas. */
    const up = await sb.rpc('mute_member', { p_club_id: currentClubId, p_profile_id: profileId });
    if (up.error) { showNotification(t('sb.muteErrorColon') + _muteErrText(up.error), 'error'); return; }
    state.memberMuted[profileId] = true;
    addAuditLog('audit.memberMuted', state.currentUser.name, playerName || profileId);
    renderPlayers();
    showNotification(t('sb.mutedMsg').replace('{name}', playerName || t('sb.memberFallback')), 'success');
}

/* Traduce codurile de eroare ale RPC-urilor de mute în mesaje utile. */
function _muteErrText(err) {
    const m = (err && (err.message || err.msg)) || '';
    if (/insufficient_role/i.test(m)) return t('rbac.noPermission');
    if (/cannot_mute_self/i.test(m)) return t('sb.cantApplySelf');
    if (/cannot_mute_admin/i.test(m)) return t('sb.cantMuteAdmin');
    if (/not_a_member/i.test(m)) return t('sb.notAMember');
    return m;
}

async function unmuteMember(profileId, playerName) {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    if (typeof can === 'function' && !can('member.mute')) { showNotification(t('rbac.noPermission'), 'error'); return; }
    if (!_modGuard(profileId)) return;
    const up = await sb.rpc('unmute_member', { p_club_id: currentClubId, p_profile_id: profileId });
    if (up.error) { showNotification(t('sb.unmuteErrorColon') + _muteErrText(up.error), 'error'); return; }
    state.memberMuted[profileId] = false;
    addAuditLog('audit.memberUnmuted', state.currentUser.name, playerName || profileId);
    renderPlayers();
    showNotification(t('sb.unmutedMsg').replace('{name}', playerName || t('sb.memberFallback')), 'success');
}

async function banMember(profileId, playerName) {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    if (!_modGuard(profileId)) return;
    if (!await confirmModal(t('sb.confirmBan').replace('{name}', (playerName || t('sb.thisMember'))), { danger: true })) return;
    if (!await requireReauth('member.ban')) return;
    const up = await sb.rpc('ban_member_atomic', { p_club_id: currentClubId, p_profile_id: profileId });
    if (up.error) { showNotification(t('sb.banErrorColon') + up.error.message, 'error'); return; }
    addAuditLog('audit.memberBanned', state.currentUser.name, playerName || profileId);
    await hydrateFromSupabase();
    render();
    showNotification(t('sb.memberExcluded').replace('{name}', playerName || t('sb.thisMember')), 'success');
}

async function unbanMember(profileId, playerName) {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    if (!isAdmin) return;
    if (!await requireReauth('member.unban')) return;
    const up = await sb.rpc('unban_member', { p_club_id: currentClubId, p_profile_id: profileId });
    if (up.error) { showNotification(t('sb.unbanErrorColon') + up.error.message, 'error'); return; }
    addAuditLog('audit.memberUnbanned', state.currentUser.name, playerName || profileId);
    await hydrateFromSupabase();
    render();
    showNotification(t('sb.unbannedCanRejoin').replace('{name}', playerName || t('sb.thisMember')), 'success');
}

async function closeActiveClub() {
    const menu = document.getElementById('clubMenu');
    if (menu) menu.classList.add('hidden');
    const c = activeClub();
    if (!c) return;
    if (currentClubMyRole() !== 'principal_admin') { showNotification(t('sb.onlyPrincipalClose'), 'error'); return; }
    if ((state.myClubs || []).length <= 1) { showNotification(t('sb.cantCloseOnlyClub'), 'error'); return; }
    if (!await confirmModal(t('sb.confirmCloseClub').replace('{name}', c.name), { danger: true })) return;
    if (!await requireReauth('club.close')) return;
    const up = await sb.from('clubs').update({ status: 'closed' }).eq('id', c.id);
    if (up.error) { showNotification(t('sb.closeErrorColon') + up.error.message, 'error'); return; }
    c.status = 'closed';
    const other = (state.myClubs || []).find(x => x.id !== c.id && x.status === 'active')
        || (state.myClubs || []).find(x => x.id !== c.id);
    showNotification(t('sb.clubClosed').replace('{name}', c.name), 'success');
    if (other) await switchClub(other.id);
    else { renderClubSwitcher(); }
}

async function reactivateActiveClub() {
    const menu = document.getElementById('clubMenu');
    if (menu) menu.classList.add('hidden');
    const c = activeClub();
    if (!c) return;
    if (currentClubMyRole() !== 'principal_admin') { showNotification(t('sb.onlyPrincipalReopen'), 'error'); return; }
    if (c.status === 'active') { showNotification(t('sb.clubAlreadyActive'), 'info'); return; }
    const up = await sb.from('clubs').update({ status: 'active' }).eq('id', c.id);
    if (up.error) { showNotification(t('sb.reopenErrorColon') + up.error.message, 'error'); return; }
    c.status = 'active';
    showNotification(t('sb.clubReopened').replace('{name}', c.name), 'success');
    renderClubSwitcher();
    if (typeof renderClubMenu === 'function') renderClubMenu();
    render();
}

/* ============================================================
   ===== PĂRĂSEȘTE CLUBUL (auto-excluziune) — Batch 4 =========
   Orice membru NON-owner poate părăsi clubul. Admin Principal NU
   poate părăsi fără transfer de proprietate prealabil -> îl trimitem
   spre openTransferOwnership. La părăsire: confirmModal danger ->
   DELETE rândul propriu din club_members -> notificare admini ->
   scoatere din state + comutare pe alt club / hub. RBAC + RO+EN.
   ============================================================ */
async function leaveClub() {
    const menu = document.getElementById('clubMenu');
    if (menu) menu.classList.add('hidden');
    const c = activeClub();
    if (!c) return;
    if (typeof isDemoMode === 'function' && isDemoMode()) { showNotification(t('sb.demoNoSaveChanges'), 'info'); return; }
    if (!state.currentUser || !state.currentUser.id) return;

    /* Admin Principal (owner) — trebuie să transfere proprietatea înainte. */
    if (currentClubMyRole() === 'principal_admin') {
        showNotification(t('leave.ownerMustTransfer'), 'info');
        setTimeout(function () { try { if (typeof openTransferOwnership === 'function') openTransferOwnership(); } catch (e) { logError('leaveClub', e); } }, 500);
        return;
    }

    if (!await confirmModal(t('leave.confirm').replace('{name}', c.name), { danger: true })) return;
    if (!await requireReauth('club.leave')) return;

    const me = state.currentUser.id;
    const myName = state.currentUser.name || t('dm.someone');
    const clubId = c.id;

    /* Colectează adminii ÎNAINTE de a pleca (ca să-i pot notifica). */
    let adminIds = [];
    try {
        const r = await sb.from('club_members')
            .select('profile_id, role')
            .eq('club_id', clubId).eq('status', 'active')
            .in('role', ['principal_admin', 'secondary_admin']);
        adminIds = ((r && r.data) || []).map(m => m.profile_id).filter(id => id && id !== me);
    } catch (e) { logError('leaveClub', e); }

    /* Notificăm adminii ÎNAINTE de a ne șterge calitatea de membru — altfel
       RLS-ul (notif_insert_scoped) ar respinge inserția (nu mai suntem membri). */
    try {
        if (adminIds.length && typeof pushNotification === 'function') {
            await pushNotification(adminIds, {
                clubId: clubId, type: 'member_left', icon: 'fa-user-minus', tab: 'players',
                text: t('notif.memberLeft').replace('{name}', myName)
            });
        }
    } catch (e) { logError('leaveClub', e); }

    const del = await sb.from('club_members').delete().eq('club_id', clubId).eq('profile_id', me);
    if (del.error) { showNotification(t('leave.errorColon') + del.error.message, 'error'); return; }

    /* Scoate clubul din state-ul local și comută. */
    state.myClubs = (state.myClubs || []).filter(x => x.id !== clubId);
    showNotification(t('leave.done').replace('{name}', c.name), 'success');

    const other = (state.myClubs || []).find(x => x.status === 'active') || (state.myClubs || [])[0];
    if (other) {
        await switchClub(other.id);
    } else {
        /* Niciun club rămas -> înapoi la hub. */
        currentClubId = null;
        try { localStorage.removeItem(clubStorageKey(me)); } catch (e) { logError('leaveClub', e); }
        if (typeof renderClubSwitcher === 'function') renderClubSwitcher();
        if (typeof showClubHub === 'function') showClubHub();
    }
    if (typeof renderClubMenu === 'function') renderClubMenu();
}
window.leaveClub = leaveClub;

/* ============================================================================
   DATE CROSS-CLUB FĂRĂ CLUB ACTIV
   ----------------------------------------------------------------------------
   RO: Notificările, mesajele private și cererile de prietenie NU depind de un
       club. Dar `loadAllNotifications()`, `loadDirectMessages()`,
       `subscribeRealtimeDM()` și `subscribeRealtimeSocial()` erau apelate DOAR
       din `switchClub()`. Cine nu are (încă) niciun club — de exemplu cineva
       care tocmai a trimis o cerere de aderare — rămânea cu clopoțelul gol și
       fără mesaje private, deși comentariul din `showClubHub` promitea exact
       invers: „Clopoțelul rămâne (notificări cross-club)".
       Efect concret: îi scrii unui solicitant, el nu primește nimic — și nici nu
       are cum să găsească conversația, fiindcă singura cale spre ea e clopoțelul.
       Le încărcăm și la intrarea în hub-ul de cluburi. Nu sunt abonamente NOI:
       sunt exact aceleași pe care le-ar primi în clipa în care intră într-un
       club, iar ambele funcții de abonare sunt idempotente (închid canalul vechi).
   EN: cross-club data (notifications, DMs, friend requests) was only loaded from
       `switchClub()`, leaving club-less users with an empty bell. Same loaders,
       now also on the club hub. Both subscribe helpers are idempotent.
   ============================================================================ */
let _crossClubLoadedFor = null;

async function loadCrossClubData() {
    if (!sb || !state.currentUser) return;
    const me = state.currentUser.id;

    /* ⚠ CAUZA REALĂ, confirmată cu diagnostic pe producție (iulie 2026):
       `loadDirectMessages()`, `subscribeRealtimeDM()` și `subscribeRealtimeSocial()`
       sunt apelate dintr-un SINGUR loc — `switchClub()`, linia ~530.
       La încărcarea normală a paginii aplicația îți restaurează ultimul club FĂRĂ
       să treacă prin `switchClub` (aceea e pentru COMUTARE, cu tot cu toast).
       Rezultat: după orice reîncărcare rămâi fără mesaje private în memorie și
       fără cele două canale realtime. Diagnosticul a arătat exact asta —
       `_dmAll: 0`, „canal realtime DM: NICIUNUL", deși clubul activ era setat.
       Părea că merge doar pentru că, în timpul testelor, se comuta între cluburi.

       Prima mea corecție a agățat funcția de `showClubHub` și apoi am limitat-o
       la utilizatorii FĂRĂ club — adică exact cazul care NU era stricat. Greșeală.

       Acum: rulează la intrarea în aplicație, indiferent de club, și completează
       DOAR ce lipsește. Ca să nu producem „churn" de canale (închidere +
       redeschidere cu același nume, ceea ce chiar poate omorî abonamentul), ne
       uităm la starea REALĂ a canalelor, nu la un flag.
       EN: these three ran only from `switchClub()`, so a normal page load left the
       user without DMs and without the two realtime channels. Now they run on app
       show as well, filling only what is actually missing. */

    let hasDm = false, hasSocial = false;
    try {
        const chans = (sb.getChannels && sb.getChannels()) || [];
        hasDm = chans.some(c => String((c && c.topic) || '').indexOf('dm-') > -1);
        hasSocial = chans.some(c => String((c && c.topic) || '').indexOf('social-') > -1);
    } catch (e) { logError('loadCrossClubData.channels', e); }

    /* Mesajele se încarcă o singură dată per utilizator (resetat la logout). */
    if (_crossClubLoadedFor !== me) {
        _crossClubLoadedFor = me;
        try { if (typeof loadDirectMessages === 'function') await loadDirectMessages(); }
        catch (e) { logError('loadCrossClubData.dm', e); }
    }

    if (!hasDm) {
        try { if (typeof subscribeRealtimeDM === 'function') subscribeRealtimeDM(); }
        catch (e) { logError('loadCrossClubData.subDm', e); }
    }
    if (!hasSocial) {
        try { if (typeof subscribeRealtimeSocial === 'function') subscribeRealtimeSocial(); }
        catch (e) { logError('loadCrossClubData.subSocial', e); }
    }

    if (!state.notifLoaded) {
        try { if (typeof loadAllNotifications === 'function') await loadAllNotifications(); }
        catch (e) { logError('loadCrossClubData.notif', e); }
    }

    try { if (typeof renderNotifBadge === 'function') renderNotifBadge(); }
    catch (e) { logError('loadCrossClubData.badge', e); }
}
window.loadCrossClubData = loadCrossClubData;
/* La schimbarea utilizatorului (logout/login) garda trebuie resetată. */
window.resetCrossClubGuard = function () { _crossClubLoadedFor = null; };

/* Cârlig aditiv pe `showClubHub` (app-core.js). Nu rescriem funcția — o apelăm
   și adăugăm încărcarea după ea. */
/* Cârlige ADITIVE pe AMBELE intrări în aplicație (app-core.js):
     · `showMainApp`  — ai un club activ (cazul care era stricat)
     · `showClubHub`  — n-ai niciun club
   Nu rescriem funcțiile, le apelăm și completăm după ele. */
(function () {
    if (window.__toCrossClubHooked) return;
    ['showMainApp', 'showClubHub'].forEach(function (fn) {
        if (typeof window[fn] !== 'function') return;
        const _orig = window[fn];
        window[fn] = function () {
            const r = _orig.apply(this, arguments);
            try { loadCrossClubData(); } catch (e) { logError(fn + '.after', e); }
            return r;
        };
    });
    window.__toCrossClubHooked = true;
})();

/* Randează secțiunea de notificări push în panoul de Setări.
   Cârlig ADITIV pe `renderSettingsContent` (app-core.js) — nu rescriem funcția,
   o apelăm și completăm după ea. `refreshPushUI()` actualizează apoi starea
   butonului; funcționează pentru că acum secțiunea există într-un SINGUR loc,
   deci nu există id-uri duplicate (`#pushToggleBtn`, `#pushStatusLine`). */
(function () {
    if (window.__toPushInSettings) return;
    if (typeof window.renderSettingsContent !== 'function') return;
    const _origRenderSettings = window.renderSettingsContent;
    window.renderSettingsContent = function () {
        const r = _origRenderSettings.apply(this, arguments);
        try {
            const box = document.getElementById('settingsPushSection');
            if (box) {
                box.innerHTML = (typeof renderPushSettingsHTML === 'function') ? renderPushSettingsHTML() : '';
                if (typeof refreshPushUI === 'function') setTimeout(function () {
                    try { refreshPushUI(); } catch (e) { logError('renderSettingsContent.push', e); }
                }, 0);
            }
        } catch (e) { logError('renderSettingsContent.after', e); }
        return r;
    };
    window.__toPushInSettings = true;
})();

/* ===================== SETĂRI ▸ APLICAȚIE (instalare + actualizare) =========
   RO: Punct de intrare PERMANENT pentru instalare, cerut fiindcă butonul din hub
       (`#pwaInstallBtn`, app-core.js) e ascuns implicit și se aprinde DOAR din
       handlerul `beforeinstallprompt` — un eveniment al browserului cu moment de
       sosire necontrolabil, care la prima vizită poate să nu vină deloc (Chrome
       evaluează instalabilitatea înainte ca Service Worker-ul, înregistrat în
       handlerul `load`, să fie gata). În plus, cine intră prin invitație direct
       într-un club nu vede niciodată hub-ul, deci nu vede nici butonul.
       `installPwa()` are deja cale de rezervă (instrucțiuni manuale), deci
       butonul e util CHIAR DACĂ promptul nativ lipsește.
   EN: a permanent install entry point, because the hub button only appears from
       the `beforeinstallprompt` handler, which may never fire on a first visit,
       and invitation users may never see the hub at all. */

function renderAppSettingsHTML() {
    if (!state.currentUser) return '';
    /* Ascundem instalarea DOAR când rulăm deja ca aplicație — acolo chiar n-are
       sens. În orice alt caz o arătăm: „deja instalat, dar deschis în tab" nu e
       detectabil, iar un buton în plus care afișează instrucțiuni e mai bun decât
       un buton absent pentru cine chiar vrea să instaleze. */
    const standalone = (typeof _toIsStandalone === 'function') ? _toIsStandalone() : false;
    return `
    <div class="admin-acc profile-acc" style="margin-top:6px;">
        <button type="button" class="admin-acc-head" data-action="appsec-toggle">
            <span><i class="fas fa-mobile-screen"></i> ${t('appsec.title')}</span>
            <i class="fas fa-chevron-down admin-acc-chev"></i>
        </button>
        <div id="pfSec-app" class="admin-acc-body hidden">
            <p style="color:var(--text-secondary); font-size:.9em; margin:0 0 10px;">${t('appsec.desc')}</p>
            ${standalone ? '' : `<button type="button" class="btn btn-primary" data-action="appsec-install" style="width:100%; margin-bottom:8px;"><i class="fas fa-download"></i> ${t('hub.install')}</button>`}
            <button type="button" id="appUpdateBtn" class="btn btn-secondary" data-action="appsec-update" style="width:100%;"><i class="fas fa-rotate"></i> ${t('appsec.checkUpdate')}</button>
        </div>
    </div>`;
}

/* Delegare pe containerul STATIC din index.html (golden standard: zero `onclick=`).
   Se leagă o singură dată; supraviețuiește oricărei re-randări a secțiunii. */
let _appSecBound = false;
function _bindAppSettingsDelegation() {
    if (_appSecBound) return;
    const host = document.getElementById('settingsAppSection');
    if (!host) return;
    _appSecBound = true;
    host.addEventListener('click', function (ev) {
        const el = ev.target && ev.target.closest ? ev.target.closest('[data-action]') : null;
        if (!el || !host.contains(el)) return;
        const act = el.dataset.action;
        if (act === 'appsec-toggle') { try { toggleTilePanel('pfSec-app', el); } catch (e) { logError('appsec-toggle', e); } }
        else if (act === 'appsec-install') { try { installPwa(); } catch (e) { logError('appsec-install', e); } }
        else if (act === 'appsec-update') { checkForAppUpdate(); }
    });
}

/* Așteaptă ca un worker „installing" să ajungă într-o stare finală.
   ⚠ Plasa de 8 s: pe rețea moartă `statechange` poate să nu vină niciodată, iar
   fără ea butonul ar rămâne blocat pe „Se verifică…". */
function _waitWorkerSettled(worker) {
    return new Promise(function (res) {
        let done = false;
        const fin = function () { if (!done) { done = true; res(); } };
        try {
            worker.addEventListener('statechange', function () {
                if (worker.state === 'installed' || worker.state === 'activated' || worker.state === 'redundant') fin();
            });
        } catch (e) { /* noop */ }
        setTimeout(fin, 8000);
    });
}

let _appUpdateBusy = false;

/* Verificare MANUALĂ a actualizărilor. Bannerul automat (`_toShowUpdateBanner`,
   app-core.js) rămâne neatins — asta e o cale în plus, la cerere.
   ⚠ `sw.js` cheamă `skipWaiting()` la instalare, deci un SW nou poate trece
     direct în „activated", fără să treacă prin `waiting`. De aceea nu ne uităm
     doar la `reg.waiting`: ascultăm și `updatefound` pe durata verificării.
     Chiar și activat, pagina rulează cu resursele vechi ⇒ reîncărcarea e necesară. */
async function checkForAppUpdate() {
    if (_appUpdateBusy) return;
    const btn = document.getElementById('appUpdateBtn');
    const restore = function () {
        if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fas fa-rotate"></i> ' + window.escHtml(t('appsec.checkUpdate')); }
        _appUpdateBusy = false;
    };
    if (!('serviceWorker' in navigator)) { showNotification(t('appsec.updateUnsupported'), 'info'); return; }
    _appUpdateBusy = true;
    if (btn) { btn.disabled = true; btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> ' + window.escHtml(t('appsec.checking')); }
    let reloading = false;
    try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) { showNotification(t('appsec.updateUnsupported'), 'info'); return; }
        let found = false;
        const onFound = function () { found = true; };
        try { reg.addEventListener('updatefound', onFound); } catch (e) { /* noop */ }
        try {
            await reg.update(); /* cere sw.js de la rețea; offline ⇒ aruncă, prins mai jos */
            if (reg.installing) await _waitWorkerSettled(reg.installing);
            else await new Promise(function (r) { setTimeout(r, 600); }); /* răgaz pentru `updatefound` */
        } finally {
            try { reg.removeEventListener('updatefound', onFound); } catch (e) { /* noop */ }
        }
        /* T-1 (29 oct. 2026): SW-ul poate fi DEJA nou (actualizat în fundal, bannerul închis cu ✕),
           iar pagina tot veche ⇒ `reg.update()` nu mai găsește nimic și butonul spunea „Ai deja cea
           mai recentă versiune". SW-ul nou a șters cache-ul versiunii vechi: dacă `TO_RELEASE` (versiunea
           paginii) nu mai are cache, dar există cache-ul altei versiuni, pagina e veche ⇒ reîncărcăm.
           EN: the SW may already be newer than the page; a missing cache for TO_RELEASE means reload. */
        if (!found && !reg.waiting && window.caches && /^turnorder-[a-z0-9]+$/.test(String(window.TO_RELEASE || ''))) {
            try {
                const ks = await caches.keys();
                found = ks.indexOf(window.TO_RELEASE) < 0 && ks.some(function (k) { return /^turnorder-[a-z0-9]+$/.test(k); });
            } catch (e) { logError('checkForAppUpdate', e); }
        }
        if (found || reg.waiting) {
            showNotification(t('appsec.updating'), 'success');
            try { if (reg.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' }); } catch (e) { logError('checkForAppUpdate', e); }
            reloading = true;
            setTimeout(function () { window.location.reload(); }, 700);
            return;
        }
        showNotification(t('appsec.upToDate'), 'success');
    } catch (e) {
        logError('checkForAppUpdate', e);
        showNotification(t('appsec.updateFail'), 'error');
    } finally {
        /* Dacă reîncărcăm, NU deblocăm butonul: ar licări „Verifică actualizările"
           o fracțiune de secundă înainte ca pagina să dispară. */
        if (!reloading) restore();
    }
}

/* Cârlig ADITIV pe `renderSettingsContent`, ca cel de mai sus pentru push —
   nu rescriem funcția din app-core.js, o completăm după ea. */
(function () {
    if (window.__toAppSecInSettings) return;
    if (typeof window.renderSettingsContent !== 'function') return;
    const _origRS = window.renderSettingsContent;
    window.renderSettingsContent = function () {
        const r = _origRS.apply(this, arguments);
        try {
            const box = document.getElementById('settingsAppSection');
            if (box) {
                box.innerHTML = renderAppSettingsHTML();
                _bindAppSettingsDelegation();
            }
        } catch (e) { logError('renderSettingsContent.appsec', e); }
        return r;
    };
    window.__toAppSecInSettings = true;
})();

function isAdminOfActiveClub() {
    const r = currentClubMyRole();
    return r === 'principal_admin' || r === 'secondary_admin';
}

/* ===================== CLAIM CLUB (Survival Mode) ===================== */
/* Faza din ciclul de viață a clubului (status setat de DB: recompute_club_lifecycle). */
function clubLifecyclePhase(club) {
    const s = club && club.status;
    if (s === 'read_only') return 'grace';
    if (s === 'archived') return 'archived';
    if (s === 'closed') return 'closed';
    return 'active';
}
/* Un club poate fi „preluat" doar dacă e abandonat din cauza neplății
   (grace/arhivat). Cluburile ÎNCHISE manual de owner NU sunt claimable. */
/* Ce acțiune i se oferă utilizatorului curent pentru un club inactiv:
   - 'reactivate' : sunt owner (admin principal) ȘI am abonament activ → doar reactivez.
   - 'claim'      : club abandonat (neplată) și eu am cu ce-l prelua.
   - null         : nimic (ex. club închis al cărui owner încă plătește). */
function clubActionForMe(club) {
    if (!club || !state.currentUser) return null;
    const ph = clubLifecyclePhase(club);
    if (ph === 'active') return null;
    const iAmOwner = club.myRole === 'principal_admin' || club.ownerId === state.currentUser.id;
    const iHaveSub = (typeof userActivePlan === 'function') && !!userActivePlan();
    /* Owner cu abonament activ → reactivare simplă (nu re-plătește cât e abonat). */
    if (iAmOwner && iHaveSub) return 'reactivate';
    /* Toți ceilalți (membru/moderator/admin secundar) + owner fără abonament → preluare.
       claim_club refuză oricum preluarea unui club al cărui owner încă plătește. */
    return 'claim';
}
/* Reactivează un club (status → active) — pentru owner-ul cu abonament activ. */
async function reactivateClub(clubId) {
    try { const menu = document.getElementById('clubMenu'); if (menu) menu.classList.add('hidden'); } catch (e) { logError('reactivateClub', e); }
    const c = (state.myClubs || []).find(x => x.id === clubId); if (!c) return;
    const iAmOwner = c.myRole === 'principal_admin' || c.ownerId === (state.currentUser && state.currentUser.id);
    if (!iAmOwner) { showNotification(window.t('claim.onlyOwnerReactivate'), 'error'); return; }
    const up = await sb.from('clubs').update({ status: 'active' }).eq('id', clubId);
    if (up.error) { showNotification(window.t('claim.reactivateFailed') + ' ' + up.error.message, 'error'); return; }
    c.status = 'active';
    showNotification(window.t('claim.reactivated'), 'success');
    try { const m = document.querySelector('.modal.active'); if (m && m.dataset && m.dataset.dynamic) m.remove(); } catch (e) { logError('reactivateClub', e); }
    try { if (typeof renderClubHub === 'function') renderClubHub(); } catch (e) { logError('reactivateClub', e); }
    try { renderClubSwitcher(); } catch (e) { logError('reactivateClub', e); }
    await switchClub(clubId);
}
function clubClaimablePhaseLabel(ph) {
    return window.t(ph === 'archived' ? 'claim.phaseArchived' : ph === 'closed' ? 'claim.phaseClosed' : 'claim.phaseGrace');
}
/* HTML buton pentru un club inactiv: „Reactivează" (owner cu abonament) sau
   „Preia Clubul / Claim Club" (membru, club abandonat). */
function claimClubButtonHtml(club, opts) {
    const action = clubActionForMe(club);
    if (!action || !state.currentUser) return '';
    opts = opts || {};
    const style = opts.small
        ? 'width:auto; min-width:auto; padding:6px 12px; font-size:0.85em;'
        : 'width:auto; min-width:auto; padding:10px 16px;';
    if (action === 'reactivate') {
        return '<button class="btn btn-success" style="' + style + '" onclick="event.stopPropagation(); reactivateClub(\'' + club.id + '\')">'
            + '<i class="fas fa-rotate-left"></i> ' + window.t('claim.reactivate') + '</button>';
    }
    return '<button class="btn btn-success" style="' + style + '" onclick="event.stopPropagation(); startClaimClub(\'' + club.id + '\')">'
        + '<i class="fas fa-hand-holding-heart"></i> ' + window.t('claim.button') + '</button>';
}
/* Pornește preluarea: reține clubul țintă + deschide planurile pt. checkout
   (tier ≥ tier-ul curent al clubului). La revenirea din Stripe se finalizează. */
async function startClaimClub(clubId) {
    try { const menu = document.getElementById('clubMenu'); if (menu) menu.classList.add('hidden'); } catch (e) { logError('startClaimClub', e); }
    const club = (state.myClubs || []).find(c => c.id === clubId);
    if (!club) { showNotification(window.t('claim.notMember'), 'error'); return; }

    /* ★ TRIAL (anti-abuz, nivel 2): preluarea unui club cere INTOTDEAUNA plata reala.
       Serverul refuza oricum (claim_club -> TRIAL_CANNOT_CLAIM); aici doar explicam
       si trimitem la checkout, pastrand clubul tinta. */
    if (typeof isTrialing === 'function' && isTrialing()) {
        try { localStorage.setItem('to_claim_club_id', clubId); } catch (e) { logError('startClaimClub', e); }
        showNotification(window.t('trial.claimBlocked'), 'info');
        try { const m0 = document.querySelector('.modal.active'); if (m0 && m0.dataset && m0.dataset.dynamic) m0.remove(); } catch (e) { logError('startClaimClub', e); }
        if (typeof openPlansModal === 'function') openPlansModal();
        return;
    }

    /* Dacă userul are DEJA un abonament activ ȘI un slot liber de club → preluare GRATUITĂ,
       fără să mai plătească un abonament nou. */
    const myPlan = (typeof userActivePlan === 'function') ? userActivePlan() : null;
    if (myPlan) {
        const limit = (typeof planClubLimit === 'function') ? planClubLimit(myPlan) : Infinity;
        const owned = (typeof countMyOwnedClubs === 'function') ? countMyOwnedClubs() : 0;
        const hasFreeSlot = (limit === Infinity) || (owned < limit);
        if (hasFreeSlot) {
            try { localStorage.setItem('to_claim_club_id', clubId); } catch (e) { logError('startClaimClub', e); }
            showNotification(window.t('claim.claiming'), 'info');
            try { const m = document.querySelector('.modal.active'); if (m && m.dataset && m.dataset.dynamic) m.remove(); } catch (e) { logError('startClaimClub', e); }
            await finalizeClaimIfPending();
            return;
        }
    }

    /* Fără abonament activ SAU fără slot liber → checkout (abonament/upgrade), apoi preluare. */
    try { localStorage.setItem('to_claim_club_id', clubId); } catch (e) { logError('startClaimClub', e); }
    try { window._claimTargetPlan = String(club.plan || club.planType || 'starter').toLowerCase(); } catch (e) { logError('startClaimClub', e); }
    showNotification(window.t('claim.redirecting'), 'info');
    if (typeof openPlansModal === 'function') openPlansModal();
}
/* Modal când încerci să deschizi un club arhivat/închis: explică + oferă Claim. */
function openClaimPrompt(club) {
    if (!club) return;
    const esc = s => window.escHtml(s);
    const ph = clubLifecyclePhase(club);
    const action = clubActionForMe(club);
    const actBtn = claimClubButtonHtml(club);
    const explain = (action === 'reactivate')
        ? window.t('claim.closedExplainOwner')
        : (window.t('claim.archivedExplain') + ' <strong>' + clubClaimablePhaseLabel(ph) + '</strong>.');
    const cta = (action === 'reactivate') ? '' : ('<p style="margin:0 0 18px;">' + window.t('claim.archivedCta') + '</p>');
    const modal = document.createElement('div');
    modal.className = 'modal active'; if (modal.dataset) modal.dataset.dynamic = '1';
    modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
    modal.innerHTML = `<div class="modal-content" style="max-width:460px; text-align:center;">
        <div style="font-size:2.4em; margin-bottom:6px;">🗄️</div>
        <h2 style="margin:0 0 6px;">${esc(club.name)}</h2>
        <p style="color:var(--text-secondary); margin:0 0 14px;">${explain}</p>
        ${cta}
        <div class="btn-group" style="justify-content:center;">${actBtn}
            <button class="btn btn-secondary" onclick="this.closest('.modal').remove()">${window.t('common.close')}</button>
        </div></div>`;
    document.body.appendChild(modal);
}
/* Finalizează preluarea după ce abonamentul noului plătitor e activ. */
async function finalizeClaimIfPending() {
    let clubId = null;
    try { clubId = localStorage.getItem('to_claim_club_id'); } catch (e) { logError('finalizeClaimIfPending', e); }
    if (!clubId) return false;
    if (typeof userActivePlan === 'function' && !userActivePlan()) return false; // încă nu e plata confirmată
    try {
        const res = await sb.rpc('claim_club', { p_club_id: clubId });
        const code = res && res.data;
        if (res && res.error) { showNotification(window.t('claim.failed') + ' (' + res.error.message + ')', 'error'); return false; }
        if (code !== 'OK') {
            /* ★ TRIAL: coduri dedicate -> mesaje clare, nu eroare seaca. */
            const cmap = {
                TRIAL_CANNOT_CLAIM: window.t('trial.claimBlocked'),
                TRIAL_EXHAUSTED:    window.t('trial.clubExhausted')
            };
            if (cmap[code]) {
                showNotification(cmap[code], 'info');
                if (typeof openPlansModal === 'function') openPlansModal();
                return false;
            }
            showNotification(window.t('claim.failed') + ' (' + code + ')', 'error');
            return false;
        }
        try { localStorage.removeItem('to_claim_club_id'); } catch (e) { logError('finalizeClaimIfPending', e); }
        await loadMyClubsAndSelect({ id: state.currentUser.id });
        try { applyClubRole(); } catch (e) { logError('finalizeClaimIfPending', e); }
        showNotification(window.t('claim.success'), 'success');
        await switchClub(clubId);
        return true;
    } catch (e) { showNotification(window.t('claim.failed'), 'error'); return false; }
}

function closeAnyModal() {
    const m = document.querySelector('.modal.active'); if (m) m.remove();
}

/* ---------- Descoperă cluburi publice ---------- */
let _discoverClubs = [];
window._discClubFilters = { vis: '', country: '', city: '' };

/* Helpere comune pentru panourile de filtre (cluburi + jucători). */
function _discCountryOptions(sel) {
    const names = window.PROFILE_COUNTRY_NAMES || {};
    const listc = window.PROFILE_COUNTRIES || [];
    return '<option value="">' + t('discover.any') + '</option>' + listc.map(c => {
        const nm = c === 'Other' ? t('country.other') : (names[c] || c);
        return '<option value="' + c + '"' + (sel === c ? ' selected' : '') + '>' + escHtml(nm) + '</option>';
    }).join('');
}
function _discCityDatalist(id) {
    let all = [];
    Object.keys(window.PROFILE_CITIES || {}).forEach(k => { all = all.concat(window.PROFILE_CITIES[k]); });
    return '<datalist id="' + id + '">' + all.map(c => '<option value="' + escHtml(c) + '"></option>').join('') + '</datalist>';
}
function _discSetFilterBtn(btnId, active) { const b = document.getElementById(btnId); if (b) b.classList.toggle('is-on', !!active); }
function _discTogglePanel(panelId) { const p = document.getElementById(panelId); if (p) p.classList.toggle('hidden'); }

function toggleDiscClubFilters() { _discTogglePanel('discClubFilters'); }
function applyDiscClubFilters() {
    window._discClubFilters = {
        vis: ((document.getElementById('dfClubVis') || {}).value) || '',
        country: ((document.getElementById('dfClubCountry') || {}).value) || '',
        city: (((document.getElementById('dfClubCity') || {}).value) || '').trim()
    };
    const f = window._discClubFilters;
    _discSetFilterBtn('discClubFilterBtn', !!(f.vis || f.country || f.city));
    renderDiscoverList();
}
function resetDiscClubFilters() {
    window._discClubFilters = { vis: '', country: '', city: '' };
    ['dfClubVis', 'dfClubCountry', 'dfClubCity'].forEach(id => { const e = document.getElementById(id); if (e) e.value = ''; });
    _discSetFilterBtn('discClubFilterBtn', false);
    renderDiscoverList();
}
window.toggleDiscClubFilters = toggleDiscClubFilters;
window.applyDiscClubFilters = applyDiscClubFilters;
window.resetDiscClubFilters = resetDiscClubFilters;

function renderDiscoverList() {
    const list = document.getElementById('discoverList');
    if (!list) return;
    let clubs = _discoverClubs || [];
    const q = (document.getElementById('discoverClubSearch') ? document.getElementById('discoverClubSearch').value : '').trim().toLowerCase();
    if (q) {
        clubs = clubs.filter(c => (c.name || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q));
    }
    /* Filtre avansate: vizibilitate (join_policy) + locație (țară/oraș din clubs.location). */
    const cf = window._discClubFilters || {};
    if (cf.vis) clubs = clubs.filter(c => (c.join_policy || 'request') === cf.vis);
    if (cf.country) clubs = clubs.filter(c => (c.location && c.location.country) === cf.country);
    if (cf.city) { const cc = cf.city.toLowerCase(); clubs = clubs.filter(c => ((c.location && c.location.city) || '').toLowerCase().includes(cc)); }
    if (!clubs.length) { list.innerHTML = `<p style="opacity:0.7;">${t('discover.noClubs')}${q ? ' ' + t('discover.for') + ' „' + clubEsc(q) + '"' : ''}</p>`; return; }
    state.requestedClubs = state.requestedClubs || {};
    list.innerHTML = clubs.map(c => {
        const open = (c.join_policy || 'request') === 'open';
        let btn;
        if (open) {
            btn = `<button class="btn btn-sm btn-primary disc-act-btn" onclick="joinClubOpen('${c.id}')"><i class="fas fa-right-to-bracket"></i> ${t('discover.join')}</button>`;
        } else if (state.requestedClubs[c.id]) {
            /* stare reținută: cererea a fost deja trimisă (rezistă la schimbarea limbii) */
            btn = `<button class="btn btn-sm btn-secondary disc-act-btn" disabled><i class="fas fa-check"></i> ${t('discover.requestSent')}</button>`;
        } else {
            btn = `<button class="btn btn-sm btn-secondary disc-act-btn" onclick="requestJoinClub('${c.id}', this)"><i class="fas fa-paper-plane"></i> ${t('discover.requestAccess')}</button>`;
        }
        const cimg = c.image_url || c.imageUrl || '';
        const cimgHtml = cimg
            ? `<img src="${clubEsc(cimg)}" style="width:46px;height:46px;border-radius:10px;object-fit:cover;flex:none;">`
            : `<div style="width:46px;height:46px;border-radius:10px;background:var(--gradient-primary,#ff6b35);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;flex:none;">${clubEsc((c.name || '?').trim().charAt(0).toUpperCase())}</div>`;
        return `<div style="display:flex; align-items:center; gap:12px; padding:10px 0; border-bottom:1px solid var(--border,#333);">
            ${cimgHtml}
            <div class="disc-club-main">
                <div class="disc-club-title"><a href="javascript:void(0)" onclick="openClubPublicInfo('${c.id}')" class="disc-club-name" style="color:inherit; cursor:pointer;" title="${clubEsc(c.name)}">${clubEsc(c.name)}</a></div>
                <div style="font-size:0.78em; opacity:0.7;">${clubEsc(c.description || (open ? window.t('hub.freeJoin') : window.t('hub.needsApproval')))}</div>
            </div>${btn}</div>`;
    }).join('');
}
/* Re-randează lista de descoperire dacă e deschisă (folosit la schimbarea limbii). */
function refreshOpenDiscover() { if (document.getElementById('discoverList')) renderDiscoverList(); }

/* ---------- Info public club (din cardul de Descoperă cluburi) ---------- */
async function openClubPublicInfo(clubId) {
    if (!sb || !clubId) return;
    const ex = document.getElementById('clubInfoModal'); if (ex) ex.remove();
    const modal = document.createElement('div');
    modal.className = 'modal active'; modal.id = 'clubInfoModal';
    modal.onclick = e => { if (e.target === modal) modal.remove(); };
    modal.innerHTML = `<div class="modal-content" style="max-width:520px;">
        <div class="modal-header"><h2><i class="fas fa-layer-group"></i> ${t('clubinfo.title')}</h2>
        <button class="modal-close" onclick="this.closest('.modal').remove()">×</button></div>
        <div id="clubInfoBody"><p style="opacity:.7;">${t('common.loading')}</p></div></div>`;
    document.body.appendChild(modal);
    try {
        const r = await sb.rpc('club_public_info', { p_club_id: clubId });
        if (r.error) throw r.error;
        const body = document.getElementById('clubInfoBody'); if (!body) return;
        if (!r.data) { body.innerHTML = `<p style="opacity:.7;">${t('clubinfo.unavailable')}</p>`; return; }
        body.innerHTML = renderClubPublicInfo(r.data);
    } catch (e) {
        const body = document.getElementById('clubInfoBody');
        if (body) body.innerHTML = `<p style="color:#e07a5f;">${t('clubinfo.needsSql')}<br><span style="opacity:.6; font-size:.85em;">${clubEsc((e && e.message) || '')}</span></p>`;
    }
}
function renderClubPublicInfo(d) {
    const img = d.image_url
        ? `<img src="${clubEsc(d.image_url)}" style="width:60px;height:60px;border-radius:14px;object-fit:cover;flex:none;">`
        : `<div style="width:60px;height:60px;border-radius:14px;background:var(--gradient-primary,#ff6b35);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:1.5em;flex:none;">${clubEsc((d.name || '?').trim().charAt(0).toUpperCase())}</div>`;
    const loc = (typeof window.clubLocationText === 'function') ? window.clubLocationText(d.location) : '';
    let created = ''; try { created = d.created_at ? new Date(d.created_at).toLocaleDateString('en-GB') : ''; } catch (e) { logError('renderClubPublicInfo', e); }
    return `
        <div style="display:flex; align-items:center; gap:14px; margin-bottom:14px;">
            ${img}
            <div style="min-width:0;">
                <div style="font-size:1.25em; font-weight:700;">${escHtml(d.name)}</div>
                ${loc ? `<div style="font-size:0.85em; opacity:0.8;"><i class="fas fa-location-dot" style="opacity:.7;"></i> ${escHtml(loc)}</div>` : ''}
                ${created ? `<small style="opacity:0.6;">${t('clubinfo.since')} ${escHtml(created)}</small>` : ''}
            </div>
        </div>
        ${d.description ? `<p style="margin-bottom:12px; opacity:0.9;">${escHtml(d.description)}</p>` : ''}
        <div class="stats-grid">
            <div class="stat-card"><div class="stat-label">${t('clubinfo.members')}</div><div class="stat-value">${Number(d.member_count) || 0}</div></div>
            <div class="stat-card"><div class="stat-label">${t('clubinfo.sessions')}</div><div class="stat-value">${Number(d.session_count) || 0}</div></div>
            <div class="stat-card"><div class="stat-label">${t('clubinfo.games')}</div><div class="stat-value">${Number(d.game_count) || 0}</div></div>
        </div>`;
}
window.openClubPublicInfo = openClubPublicInfo;

async function openDiscoverClubs() {
    const menu = document.getElementById('clubMenu'); if (menu) menu.classList.add('hidden');
    if (!sb || !state.currentUser) return;
    /* Aceeași imunizare ca la Discover Players: purjare + id + scopare. Bug-ul e
       identic ca natură; aici doar fereastra de cursă e mai scurtă (1 request, nu 5). */
    const prevC = document.getElementById('discoverClubsModal'); if (prevC) prevC.remove();
    window._discClubFilters = { vis: '', country: '', city: '' };
    const modal = document.createElement('div');
    modal.className = 'modal active';
    modal.id = 'discoverClubsModal';
    modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
    modal.innerHTML = `<div class="modal-content"><div class="modal-header">
        <h2><i class="fas fa-compass"></i> ${t('discover.clubsTitle')}</h2>
        <button class="modal-close" onclick="closeModal('discoverClubsModal')">×</button>
        </div>
        <div class="disc-search-row">
            <div class="disc-search-input-wrap">
                <i class="fas fa-search disc-search-icon"></i>
                <input type="text" id="discoverClubSearch" class="form-control" placeholder="${t('discover.clubSearchPh')}" oninput="renderDiscoverList()">
            </div>
            <button type="button" class="tab-head-btn" id="discClubFilterBtn" onclick="toggleDiscClubFilters()" data-i18n-title="discover.filters" title="${t('discover.filters')}"><i class="fas fa-sliders"></i></button>
        </div>
        <div id="discClubFilters" class="sess-panel hidden">
            <div class="disc-filter-grid">
                <label class="disc-filter-field"><span>${t('discover.visibility')}</span>
                    <select id="dfClubVis" class="form-control" onchange="applyDiscClubFilters()">
                        <option value="">${t('discover.visAll')}</option>
                        <option value="open">${t('discover.visOpen')}</option>
                        <option value="request">${t('discover.visRequest')}</option>
                    </select></label>
                <label class="disc-filter-field"><span>${t('pf.country')}</span>
                    <select id="dfClubCountry" class="form-control" onchange="applyDiscClubFilters()">${_discCountryOptions('')}</select></label>
                <label class="disc-filter-field"><span>${t('pf.city')}</span>
                    <div class="geo-city-wrap"><input type="text" id="dfClubCity" class="form-control" autocomplete="off" placeholder="${t('pf.cityPh')}" oninput="geoCitySearch('dfClubCity','dfClubCityResults','dfClubCountry','applyDiscClubFilters')" onblur="geoCityHide('dfClubCityResults')"><div id="dfClubCityResults" class="geo-city-results" style="display:none;"></div></div></label>
            </div>
            <button type="button" class="btn btn-sm btn-secondary" onclick="resetDiscClubFilters()" style="margin-top:10px; width:auto; min-width:auto;"><i class="fas fa-rotate-left"></i> ${t('discover.reset')}</button>
        </div>
        <div id="discoverList"><p style="opacity:0.7;">${t('common.loading')}</p></div></div>`;
    document.body.appendChild(modal);

    /* Încarcă cererile deja trimise de mine (ca să afișez corect butonul „Cerere trimisă") */
    state.requestedClubs = state.requestedClubs || {};
    try {
        const reqs = await sb.from('club_join_requests').select('club_id')
            .eq('profile_id', state.currentUser.id).eq('status', 'pending');
        (reqs.data || []).forEach(r => { state.requestedClubs[r.club_id] = true; });
    } catch (e) { /* graceful */ }

    const res = await sb.from('clubs').select('*').eq('is_public', true).eq('status', 'active').order('name');
    const list = modal.querySelector('#discoverList');
    if (!list || !modal.isConnected) return;
    if (res.error) { list.innerHTML = `<p style="color:#e07a5f;">${t('common.errorPrefix')}${clubEsc(res.error.message)}</p>`; return; }
    const mine = new Set((state.myClubs || []).map(c => c.id));
    _discoverClubs = (res.data || []).filter(c => !mine.has(c.id));
    renderDiscoverList();
}

async function joinClubOpen(clubId) {
    if (!sb || !state.currentUser) return;
    const ins = await sb.from('club_members')
        .upsert({ club_id: clubId, profile_id: state.currentUser.id, role: 'member' }, { onConflict: 'club_id,profile_id' });
    if (ins.error) { showNotification(t('sb.joinErrorColon') + ins.error.message, 'error'); return; }
    try { await notifyClubNewMember(clubId, state.currentUser.id, state.currentUser.name); } catch (e) { logError('joinClubOpen', e); }
    const cres = await sb.from('clubs').select('*').eq('id', clubId).maybeSingle();
    const club = cres && cres.data;
    if (club) {
        state.myClubs = state.myClubs || [];
        if (!state.myClubs.some(c => c.id === club.id)) {
            state.myClubs.push({
                id: club.id, name: club.name, slug: club.slug, status: club.status, plan: club.plan,
                isPublic: !!club.is_public, joinPolicy: club.join_policy || 'request', ownerId: club.owner_id, myRole: 'member'
            });
        }
    }
    closeAnyModal();
    showNotification(t('sb.joinedClub'), 'success');
    await enterClub(clubId);
}

async function requestJoinClub(clubId, btn) {
    if (!sb || !state.currentUser) return;
    /* Userii excluși (ban) nu pot cere intrarea — mesaj clar (RLS oricum blochează insertul). */
    try {
        const bn = await sb.from('club_bans').select('profile_id').eq('club_id', clubId).eq('profile_id', state.currentUser.id).maybeSingle();
        if (bn && bn.data) { showNotification(t('sb.bannedFromClub'), 'error'); return; }
    } catch (e) { /* ignor; insertul e oricum blocat de RLS */ }
    const ins = await sb.from('club_join_requests')
        .upsert({ club_id: clubId, profile_id: state.currentUser.id, status: 'pending' }, { onConflict: 'club_id,profile_id' });
    if (ins.error) { showNotification(t('sb.requestErrorColon') + ins.error.message, 'error'); return; }
    state.requestedClubs = state.requestedClubs || {};
    state.requestedClubs[clubId] = true;
    renderDiscoverList(); /* re-randează din stare -> butonul rămâne „Cerere trimisă" și la schimbarea limbii */
    showNotification(t('discover.requestSentToast'), 'success');
}

/* ---------- Setări club (public + politică de aderare) ---------- */
/* Card „Remindere implicite" (Settings) — adminul setează default-urile de club
   care pre-populează formularele de creare (event/poll/game vote). Fiecare tip
   are propriul picker; folosim un scop id ('event'/'poll'/'gamevote') ca să
   citim corect la salvare. Reutilizează REMINDER_OFFSETS din app-modules-2. */
function _reminderDefaultRow(kind, offset) {
    const OFF = (typeof REMINDER_OFFSETS !== 'undefined') ? REMINDER_OFFSETS : [];
    const opts = OFF.map(o => `<option value="${o.v}" ${o.v === offset ? 'selected' : ''}>${t(o.k)}</option>`).join('');
    return `<div class="rem-row">
        <i class="fas fa-bell rem-bell"></i>
        <select class="form-control rem-select rdef-${kind}-offset">${opts}</select>
        <button type="button" class="btn btn-secondary rem-del" onclick="this.parentElement.remove()"><i class="fas fa-times"></i></button>
    </div>`;
}

function _reminderDefaultBlock(kind, labelKey) {
    let cfg = { enabled: true, rules: [{ offset: 1440 }] };
    try {
        const d = (state.settings && state.settings.reminderDefaults) || {};
        if (d[kind] && typeof d[kind] === 'object') cfg = d[kind];
    } catch (e) { logError('_reminderDefaultBlock', e); }
    const enabled = cfg.enabled !== false && Array.isArray(cfg.rules) && cfg.rules.length > 0;
    const rules = (Array.isArray(cfg.rules) && cfg.rules.length) ? cfg.rules : [{ offset: 1440 }];
    const rows = rules.map(r => _reminderDefaultRow(kind, Number(r.offset) || 1440)).join('');
    return `<div style="margin-bottom:12px;">
        <label style="display:flex; align-items:center; gap:8px; font-size:.9em;">
            <input type="checkbox" class="rdef-${kind}-enabled" ${enabled ? 'checked' : ''}
                onchange="var b=this.closest('.rdef-block').querySelector('.rdef-${kind}-rules'); if(b) b.style.display=this.checked?'block':'none';">
            <strong>${t(labelKey)}</strong>
        </label>
        <div class="rdef-${kind}-rules" style="display:${enabled ? 'block' : 'none'}; margin-top:6px;">
            <div class="rdef-${kind}-list">${rows}</div>
            <button type="button" class="btn btn-secondary rem-add" onclick="addReminderDefaultRule('${kind}')"><i class="fas fa-plus"></i> ${t('rem.addRule')}</button>
        </div>
    </div>`;
}

function addReminderDefaultRule(kind) {
    const list = document.querySelector('.rdef-' + kind + '-list');
    if (!list) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = _reminderDefaultRow(kind, 60);
    list.appendChild(tmp.firstElementChild);
}
window.addReminderDefaultRule = addReminderDefaultRule;

function reminderDefaultsCardHTML() {
    if (typeof REMINDER_OFFSETS === 'undefined') return ''; /* component-ul de remindere nu e încărcat */
    return `<div class="form-group rdef-block" style="background:rgba(255,107,53,0.05); padding:12px; border-radius:8px;">
        <span class="form-section-label" style="margin-bottom:8px;"><i class="fas fa-bell"></i> ${t('rem.defaultsTitle')}</span>
        <p style="color:var(--text-secondary); font-size:.8em; margin:0 0 10px;">${t('rem.defaultsHint')}</p>
        ${_reminderDefaultBlock('event', 'rem.defEvent')}
        ${_reminderDefaultBlock('poll', 'rem.defPoll')}
        ${_reminderDefaultBlock('gamevote', 'rem.defGameVote')}
    </div>`;
}
window.reminderDefaultsCardHTML = reminderDefaultsCardHTML;

/* Citește default-urile din card -> { event:{...}, poll:{...}, gamevote:{...} } */
function readReminderDefaults() {
    const out = {};
    ['event', 'poll', 'gamevote'].forEach(kind => {
        const en = document.querySelector('.rdef-' + kind + '-enabled');
        if (!en || !en.checked) { out[kind] = { enabled: false, rules: [] }; return; }
        const seen = {};
        const rules = Array.prototype.slice.call(document.querySelectorAll('.rdef-' + kind + '-offset'))
            .map(sel => Number(sel.value) || 0)
            .filter(v => v > 0 && !seen[v] && (seen[v] = 1))
            .sort((a, b) => b - a)
            .map(offset => ({ offset }));
        out[kind] = { enabled: rules.length > 0, rules };
    });
    return out;
}
window.readReminderDefaults = readReminderDefaults;

function openClubSettings() {
    const menu = document.getElementById('clubMenu'); if (menu) menu.classList.add('hidden');
    if (!isAdminOfActiveClub()) { showNotification(t('sb.onlyAdminsClubSettings'), 'error'); return; }
    const c = activeClub(); if (!c) return;
    const lim = planMemberLimit();
    const used = currentMemberCount();
    const planOpts = PLAN_ORDER.map(p => `<option value="${p}" ${currentPlan() === p ? 'selected' : ''}>${PLAN_INFO[p].label} — ${PLAN_INFO[p].members === Infinity ? window.t('plan.unlimitedMembers') : window.t('plan.upTo')+' '+PLAN_INFO[p].members+' '+window.t('plans.membersWord')}</option>`).join('');
    const featList = Object.keys(FEATURE_MIN_PLAN).map(f => {
        const ok = planAllows(f);
        return `<div style="display:flex; align-items:center; gap:8px; padding:3px 0; font-size:0.85em;">
            <span style="color:${ok ? 'var(--success,#27ae60)' : 'var(--text-secondary,#888)'};">${ok ? '✓' : '🔒'}</span>
            <span style="${ok ? '' : 'opacity:0.65;'}">${window.ptr(FEATURE_LABEL[f])}${ok ? '' : ' <span style="opacity:0.7;">(' + t('feat.fromShort') + ' ' + (PLAN_INFO[FEATURE_MIN_PLAN[f]] || {}).label + ')</span>'}</span>
        </div>`;
    }).join('');
    const canPlan = (typeof currentClubMyRole === 'function' && currentClubMyRole() === 'principal_admin') || isPlatformOwner();
    /* Elimină orice modal de setări rămas în DOM (evită stivuirea care făcea ca
       getElementById să citească inputul VECHI/al altui club). */
    const _existing = document.getElementById('clubSettingsModal'); if (_existing) _existing.remove();
    /* ★ T1: pre-calcule pentru acordeoanele „Default Reminders" + „Danger Zone".
       Guard-urile rămân IDENTICE cu cele de dinainte de reorganizare:
       - Transfer: can('ownership.transfer');
       - Arhivare: doar Admin Principal, doar pe club activ (mutat din sertarul „My clubs"). */
    const _remindersCard = (typeof reminderDefaultsCardHTML === 'function') ? reminderDefaultsCardHTML() : '';
    const _canTransfer = (typeof can === 'function' && can('ownership.transfer'));
    const _canArchive = (typeof currentClubMyRole === 'function' && currentClubMyRole() === 'principal_admin') && (!c.status || c.status === 'active');
    const modal = document.createElement('div');
    modal.className = 'modal active';
    modal.id = 'clubSettingsModal';
    /* Starea comutatorului de acceptare a regulamentului. Trăiește în
       `club_settings` (cheia `rules_ack_required`, RLS rang>=3), nu pe rândul
       clubului: e o setare de club, nu o proprietate a lui, iar `clubs` are deja
       coloane pe care nu vrem să le atingem de aici. */
    const _rulesAckOn = !!(state.settings && state.settings.rulesAckRequired);
    /* Legăm modalul de clubul pentru care a fost deschis → salvarea va ținti EXACT
       acest club, nu pe cel „activ" de la momentul salvării. */
    if (modal.dataset) { modal.dataset.dynamic = '1'; modal.dataset.clubId = c.id; }
    modal.innerHTML = `<div class="modal-content"><div class="modal-header">
        <h2><i class="fas fa-gear"></i> ${t('clubset.title')} — ${clubEsc(c.name)}</h2>
        <button type="button" class="modal-close" data-action="clubset-close">×</button>
        </div>
        <div class="form-group">
            <label for="clubNameInput"><i class="fas fa-signature"></i> ${t('clubset.name')}</label>
            <input type="text" id="clubNameInput" class="form-control" value="${clubEsc(c.name || '')}" maxlength="60">
        </div>
        <div class="form-group">
            <label for="clubImgFile"><i class="fas fa-image"></i> ${t('clubset.avatar')}</label>
            <div style="display:flex; align-items:center; gap:12px; flex-wrap:wrap;">
                <div id="clubImgPreview">${c.imageUrl ? `<img src="${clubEsc(c.imageUrl)}" style="width:56px;height:56px;border-radius:12px;object-fit:cover;">` : `<div style="width:56px;height:56px;border-radius:12px;background:var(--gradient-primary,#ff6b35);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:1.3em;">${clubEsc((c.name || '?').trim().charAt(0).toUpperCase())}</div>`}</div>
                <input type="file" id="clubImgFile" accept="image/*" style="display:none;" onchange="clubAvatarPick(this)">
                <button type="button" class="btn btn-sm" onclick="document.getElementById('clubImgFile').click()"><i class="fas fa-camera"></i> ${t('clubset.avatarUpload')}</button>
                <button type="button" class="btn btn-sm btn-secondary" onclick="clubAvatarRemove()"><i class="fas fa-times"></i> ${t('clubset.avatarRemove')}</button>
            </div>
            <input type="hidden" id="clubImageData" value="${c.imageUrl ? clubEsc(c.imageUrl) : ''}">
        </div>
        <div class="form-group">
            <span class="form-section-label"><i class="fas fa-crown"></i> ${t('clubset.plan')}</span>
            <div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap;">
                <span style="font-weight:700;">${(PLAN_INFO[currentPlan()] || {}).label || currentPlan()}</span>
                ${canPlan ? `<button type="button" class="btn btn-sm btn-secondary" onclick="openPlansModal()"><i class="fas fa-rocket"></i> ${t('profile.manageSub')}</button>` : ''}
            </div>
            <small style="color:var(--text-secondary);">${t('clubset.membersUsed')} <strong>${used}</strong> / ${lim === Infinity ? '∞' : lim}. ${t('clubset.planFromSub')}</small>
        </div>
        <div class="form-group" style="background:rgba(255,107,53,0.05); padding:10px; border-radius:8px;">
            <label for="clubIsPublic" style="margin-bottom:6px;"><i class="fas fa-list-check"></i> ${t('clubset.feats')}</label>
            ${featList}
        </div>
        <div class="form-group">
            <label><input type="checkbox" id="clubIsPublic" ${c.isPublic ? 'checked' : ''}> ${t('clubset.public')}</label>
        </div>
        <div class="form-group">
            <label><input type="checkbox" id="clubRulesAck" ${_rulesAckOn ? 'checked' : ''}> ${t('clubset.rulesAck')}</label>
            <small style="color:var(--text-secondary); display:block; margin-top:4px;">${t('clubset.rulesAckHint')}</small>
        </div>
        <div class="form-group">
            <label for="clubJoinPolicy"><i class="fas fa-door-open"></i> ${t('clubset.joinPolicy')}</label>
            <select id="clubJoinPolicy" class="form-control">
                <option value="request" ${c.joinPolicy !== 'open' ? 'selected' : ''}>${t('clubset.joinRequest')}</option>
                <option value="open" ${c.joinPolicy === 'open' ? 'selected' : ''}>${t('clubset.joinOpen')}</option>
            </select>
        </div>
        ${typeof window.clubLocationEditorHTML === 'function' ? window.clubLocationEditorHTML(c) : ''}

        ${_remindersCard ? `
        <button type="button" class="admin-acc-head" style="margin-top:6px;" onclick="toggleTilePanel('clubsetAcc-reminders', this)">
            <span><i class="fas fa-bell"></i> ${t('rem.defaultsTitle')}</span>
            <i class="fas fa-chevron-down admin-acc-chev"></i>
        </button>
        <div id="clubsetAcc-reminders" class="admin-acc-body hidden">${_remindersCard}</div>` : ''}

        ${(_canTransfer || _canArchive) ? `
        <button type="button" class="admin-acc-head admin-danger" style="margin-top:6px;" onclick="toggleTilePanel('clubsetAcc-danger', this)">
            <span><i class="fas fa-triangle-exclamation"></i> ${t('admin.dangerZone')}</span>
            <i class="fas fa-chevron-down admin-acc-chev"></i>
        </button>
        <div id="clubsetAcc-danger" class="admin-acc-body hidden">
            ${_canTransfer ? `<div class="form-group" style="border:1px solid var(--accent-primary,#ff6b35); background:rgba(255,107,53,0.06); padding:12px; border-radius:10px; margin-top:6px;">
                <span class="form-section-label" style="margin-bottom:4px;"><i class="fas fa-crown"></i> ${t('transfer.title')}</span>
                <small style="color:var(--text-secondary); display:block; margin-bottom:10px;">${t('transfer.settingsHint')}</small>
                <button type="button" class="btn btn-sm btn-secondary" onclick="openTransferOwnership()"><i class="fas fa-people-arrows"></i> ${t('transfer.button')}</button>
            </div>` : ''}
            ${_canArchive ? `<div class="form-group" style="border:1px solid var(--danger,#e74c3c); background:rgba(231,76,60,0.06); padding:12px; border-radius:10px; margin-top:6px;">
                <span class="form-section-label" style="margin-bottom:4px;"><i class="fas fa-box-archive"></i> ${t('menu.closeClub')}</span>
                <small style="color:var(--text-secondary); display:block; margin-bottom:10px;">${t('clubset.archiveHint')}</small>
                <button type="button" class="btn btn-sm btn-danger" onclick="closeAnyModal(); closeActiveClub()"><i class="fas fa-box-archive"></i> ${t('menu.closeClub')}</button>
            </div>` : ''}
        </div>` : ''}

        <button type="button" id="clubSettingsSaveBtn" class="btn btn-primary" style="margin-top:16px;" data-action="clubset-save"><i class="fas fa-save"></i> ${t('common.save')}</button>
        </div>`;
    document.body.appendChild(modal);

    /* RO: Delegare pe modal (golden standard §5.1) — inlocuieste `onclick=`.
       Modalul e construit DINAMIC si recreat la fiecare deschidere (linia de mai sus il
       sterge pe cel vechi), deci ascultatorul se leaga PE MODAL, la deschidere, cu garda
       pe element — nu o singura data la pornire, cand modalul nici nu exista.
       EN: click delegation bound on the dynamically built modal at open time, guarded by a
       per-element flag; the modal is recreated on every open, so nothing leaks. */
    /* ⚠ Acelasi carlig ca la profilul-modal: `_disposeModal` cheama `__dispose` daca exista,
       deci tasta `Escape` si clicul pe fundal trec si ele prin functia noastra de inchidere,
       in loc sa stearga elementul pe la spate. Cu el, TOATE iesirile ajung intr-un loc.
       ⚠ `closeAnyModal()` sterge direct, FARA sa treaca pe aici — si e bine asa: e chemat
       de salvarea reusita (deja s-a salvat) si de „Inchide clubul" (unde o intrebare despre
       setari nesalvate ar fi absurda). EN: __dispose funnels the close paths into one place;
       closeAnyModal deliberately bypasses it. */
    modal.__dispose = function () { closeClubSettings(); };

    if (!modal.__csActionsBound) {
        modal.__csActionsBound = true;
        modal.addEventListener('click', function (ev) {
            const el = ev.target && ev.target.closest ? ev.target.closest('[data-action]') : null;
            if (!el || !modal.contains(el)) return;
            if (el.dataset.action === 'clubset-save') saveClubSettings();
            else if (el.dataset.action === 'clubset-close') closeClubSettings();
        });
    }

    /* Reperul pentru „ai modificari nesalvate?" (§8.2, pasul 5).
       ⚠ Aici, spre deosebire de profil, e nevoie de o SINGURA fixare: tot continutul
       modalului — inclusiv editorul de locatie (`clubLocationEditorHTML`) si cardul de
       remindere — e construit SINCRON, in acelasi sablon de mai sus. Nu exista nicio
       incarcare care sa rescrie campuri dupa randare, deci nici cursa de la profil.
       Daca se adauga vreodata una, reperul va trebui refixat la sosirea ei.
       EN: a single baseline suffices here — the whole modal is built synchronously. */
    try { clubSettingsBaseline(); } catch (e) { logError('openClubSettings.baseline', e); }
}

/* ============================================================
   DETECTOR DE MODIFICARI NESALVATE — SETARILE CLUBULUI   (§8.2, pasul 5)
   ------------------------------------------------------------
   RO: raspunde la „modalul de setari arata acum altfel decat cand a fost deschis?".
   Nu e conectat la nimic in acest pas — se cheama doar manual, din consola.

   ⚠ REGULA: acopera EXACT ce scrie `saveClubSettings`, in cele trei tabele:
     `clubs`         — nume, public, politica de aderare, imagine
     `club_settings` — acceptarea regulamentului, locatia clubului, reminderele

   ⚠ SI OGLINDESTE O ASIMETRIE, INTENTIONAT: `saveClubSettings` isi citeste propriile
   campuri SCOPAT PE MODAL (`q`, cu un comentariu care explica de ce), dar
   `clubLocationCollect` si `readReminderDefaults` citesc la nivel de DOCUMENT
   (`getElementById` / `querySelectorAll`). Detectorul face la fel. Daca l-as „corecta"
   sa citeasca totul scopat, cele doua ar ajunge in dezacord si ai primi fie dialoguri
   false, fie pierderi tacute. Inconsecventa se repara — daca se repara — in ambele
   locuri odata, nu pe furis, aici.
   EN: mirrors exactly what saveClubSettings writes, including its mixed scoping.
   ============================================================ */
function _csStableStr(v) {
    if (v === null || v === undefined) return 'null';
    if (Array.isArray(v)) return '[' + v.map(_csStableStr).join(',') + ']';
    if (typeof v === 'object') {
        return '{' + Object.keys(v).sort().map(function (k) {
            return JSON.stringify(k) + ':' + _csStableStr(v[k]);
        }).join(',') + '}';
    }
    return JSON.stringify(v);
}

function clubSettingsSnapshot() {
    try {
        const modal = document.getElementById('clubSettingsModal');
        if (!modal) return null;                       /* modalul nu e deschis */
        const q = sel => modal.querySelector(sel);
        const el = sel => q(sel);

        const nameEl = el('#clubNameInput');
        const snap = {
            /* `clubs` — citite SCOPAT pe modal, ca in salvare. */
            name: nameEl ? nameEl.value.trim() : null,
            isPublic: !!(el('#clubIsPublic') && el('#clubIsPublic').checked),
            joinPolicy: el('#clubJoinPolicy') ? el('#clubJoinPolicy').value : null
        };
        /* Imaginea: salvarea o rescrie DOAR daca elementul exista („ '' => sters"). */
        const imgEl = el('#clubImageData');
        if (imgEl) snap.image = (imgEl.value || '').trim() || null;

        /* Regulamentul: salvarea il atinge doar daca elementul exista. */
        const ackEl = el('#clubRulesAck');
        if (ackEl) snap.rulesAck = !!ackEl.checked;

        /* ⚠ Locatia si reminderele: citite GLOBAL, exact ca in salvare. */
        if (typeof window.clubLocationCollect === 'function') {
            snap.location = window.clubLocationCollect();
        }
        if (typeof readReminderDefaults === 'function') {
            snap.reminders = readReminderDefaults();
        }
        return _csStableStr(snap);
    } catch (e) {
        logError('clubSettingsSnapshot', e);
        return null;      /* la orice indoiala, NU raportam modificari */
    }
}

function clubSettingsBaseline() {
    window._csBaseline = clubSettingsSnapshot();
    return window._csBaseline;
}

/* `false` la orice incertitudine (modal inchis, reper nefixat, eroare): un dialog
   aparut degeaba e mai daunator decat unul lipsa. */
function clubSettingsIsDirty() {
    const acum = clubSettingsSnapshot();
    if (acum === null) return false;
    if (typeof window._csBaseline !== 'string') return false;
    return acum !== window._csBaseline;
}

window.clubSettingsSnapshot = clubSettingsSnapshot;
window.clubSettingsBaseline = clubSettingsBaseline;
window.clubSettingsIsDirty = clubSettingsIsDirty;

/* Inchiderea modalului de setari, intr-un singur loc. Pana acum ✕ facea chirurgie pe DOM
   (`this.parentElement.parentElement.parentElement.remove()`), `Escape` mergea prin
   `closeAllModals`, iar clicul pe fundal prin handlerul global — trei cai, niciuna o
   functie pe care sa se poata pune ceva. Deocamdata doar inchide; garda pentru modificari
   nesalvate se conecteaza la pasul urmator.
   ⚠ `querySelectorAll`, nu `getElementById`: acesta din urma sterge un SINGUR element.
   EN: one close path for the X button, Escape and backdrop; the guard comes next. */
function closeClubSettings() {
    /* Garda „modificari nesalvate" (§8.2, pasul 6). Refolosim EXACT garda din nav.js,
       expusa ca `_navGuardUnsaved`, ca sa nu existe o a doua implementare a aceleiasi
       reguli. Intoarce `true` daca a interceptat: atunci nu inchidem nimic, iar inchiderea
       se face din dialog, prin functia data ca al treilea argument.
       ⚠ La „Salveaza" chemam `saveClubSettings()`. Pe calea de SUCCES aceasta inchide deja
       modalul prin `closeAnyModal()`, deci `_closeClubSettingsNow()` de dupa nu mai are ce
       inchide — inofensiv. Pe calea de EROARE modalul ramane, `clubSettingsIsDirty()` e in
       continuare `true`, deci ramanem pe loc: exact varianta A.
       EN: reuses the nav.js guard; on save success the modal is already gone, on failure we
       stay put. */
    try {
        if (typeof window._navGuardUnsaved === 'function'
            && window._navGuardUnsaved(
                function () { return clubSettingsIsDirty(); },
                function () { return saveClubSettings(); },
                _closeClubSettingsNow)) return;
    } catch (e) { logError('closeClubSettings.guard', e); }
    _closeClubSettingsNow();
}

/* Inchiderea propriu-zisa, fara nicio intrebare. Separata ca garda de mai sus sa o poata
   chema dupa raspunsul utilizatorului. EN: the actual close, no questions asked. */
function _closeClubSettingsNow() {
    document.querySelectorAll('#clubSettingsModal').forEach(function (el) { el.remove(); });
}
window.closeClubSettings = closeClubSettings;

async function saveClubSettings() {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    /* Citim DIN modalul de setări (nu prin getElementById global, care prindea primul
       input din pagină) și salvăm pe clubul cu care a fost deschis modalul. */
    const modal = document.getElementById('clubSettingsModal');
    const clubId = (modal && modal.dataset && modal.dataset.clubId) || (activeClub() && activeClub().id);
    const c = (clubId && (state.myClubs || []).find(x => x.id === clubId)) || activeClub();
    if (!c) return;
    const q = sel => (modal ? modal.querySelector(sel) : document.querySelector(sel));
    const nameEl = q('#clubNameInput');
    const newName = nameEl ? nameEl.value.trim() : c.name;
    if (!newName) { showNotification(t('sb.clubNameEmpty'), 'error'); return; }
    const pubEl = q('#clubIsPublic');
    const polEl = q('#clubJoinPolicy');
    const isPublic = !!(pubEl && pubEl.checked);
    const joinPolicy = polEl ? polEl.value : c.joinPolicy;
    const payload = { name: newName, is_public: isPublic, join_policy: joinPolicy };
    /* Avatar club (data URL base64 în image_url; '' => șters) */
    const imgEl = q('#clubImageData');
    if (imgEl) { const v = (imgEl.value || '').trim(); payload.image_url = v || null; }

    /* RO: Rotita pe buton (varianta A): pornita DUPA validarile sincrone (club scris,
       club gasit, nume nevid) si INAINTE de prima scriere. Tot ce urmeaza e inchis intr-un
       `try/finally`, deci butonul se reseteaza pe ORICE iesire — inclusiv pe cele patru
       `return`-uri de eroare. Cautam butonul PRIN MODAL (`q`), nu prin `getElementById`
       global, din acelasi motiv pentru care functia citeste inputurile asa.
       Pe calea de succes `closeAnyModal()` sterge modalul, deci resetarea atinge un element
       detasat — inofensiv.
       EN: spinner starts after the sync validations, before the first write; the try/finally
       below is the single reset point, covering every error return. */
    const _csBtn = q('#clubSettingsSaveBtn');
    const _csSaveEsc = window.escHtml || function (s) { return s == null ? '' : String(s); };
    if (_csBtn) { _csBtn.disabled = true; _csBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> ' + _csSaveEsc(t('common.saving')); }
    const _csResetBtn = function () {
        if (!_csBtn) return;
        _csBtn.disabled = false;
        _csBtn.innerHTML = '<i class="fas fa-save"></i> ' + _csSaveEsc(t('common.save'));
    };

    try {
    const up = await sb.from('clubs').update(payload).eq('id', c.id).select('id, name, is_public, join_policy, image_url');
    if (up.error) { showNotification(t('m1.saveErrorColon') + up.error.message, 'error'); return; }
    if (!up.data || up.data.length === 0) {
        /* 0 rânduri actualizate = update-ul a fost respins/filtrat de RLS (fără eroare).
           Nu mai raportăm fals „salvat"; numele rămâne neschimbat în DB. */
        showNotification(t('sb.rlsRejected'), 'error');
        return;
    }
    /* Comutatorul de acceptare a regulamentului merge în `club_settings`, nu în
       `clubs`. Se salvează separat, dar în același gest de UI. */
    const ackEl = q('#clubRulesAck');
    if (ackEl) {
        const vrea = !!ackEl.checked;
        if (vrea !== !!(state.settings && state.settings.rulesAckRequired)) {
            const r = await sb.from('club_settings').upsert(
                { club_id: c.id, key: 'rules_ack_required', value: vrea, updated_at: new Date().toISOString() },
                { onConflict: 'club_id,key' });
            if (r.error) { showNotification(t('m1.saveErrorColon') + r.error.message, 'error'); return; }
            if (!state.settings) state.settings = {};
            state.settings.rulesAckRequired = vrea;
            addAuditLog(vrea ? 'audit.rulesAckOn' : 'audit.rulesAckOff', state.currentUser.name);
        }
    }

    const saved = up.data[0];
    c.name = saved.name; c.isPublic = !!saved.is_public; c.joinPolicy = saved.join_policy;
    if (imgEl) c.imageUrl = saved.image_url || null;
    /* Locația clubului — în club_settings (fără coloană nouă). */
    try {
        const loc = (typeof window.clubLocationCollect === 'function') ? window.clubLocationCollect() : null;
        await sb.from('club_settings').upsert({ club_id: c.id, key: 'club_location', value: loc, updated_at: new Date().toISOString() }, { onConflict: 'club_id,key' });
        c.location = loc;
    } catch (e) { console.warn('club_location:', e); }
    /* Default-uri de remindere (card Settings) — în state.settings + club_settings. */
    try {
        if (typeof readReminderDefaults === 'function') {
            const rd = readReminderDefaults();
            if (!state.settings) state.settings = {};
            state.settings.reminderDefaults = rd;
            if (!isDemoMode()) {
                await sb.from('club_settings').upsert({ club_id: c.id, key: 'reminder_defaults', value: rd, updated_at: new Date().toISOString() }, { onConflict: 'club_id,key' });
            }
            if (typeof saveState === 'function') saveState();
        }
    } catch (e) { logError('saveClubSettings reminders', e); }
    closeAnyModal();
    showNotification(t('sb.clubSettingsSaved'), 'success');
    try { if (typeof render === 'function') render(); } catch (e) { logError('saveClubSettings', e); }
    renderClubSwitcher();

    } finally {
        /* RO: singurul punct de resetare a butonului — acopera si cele patru iesiri de
           eroare de mai sus. EN: single reset point, covers every error return. */
        _csResetBtn();
    }
}

/* ===================== TRANSFER PROPRIETATE CLUB ===================== */
let _transferMembers = [];
async function openTransferOwnership() {
    if (!(typeof can === 'function' && can('ownership.transfer'))) { showNotification(t('rbac.noPermission'), 'error'); return; }
    const c = activeClub(); if (!c) return;
    _transferMembers = [];
    /* Bug fix: construim lista din membrii deja hidratați (state.members) — robust
       față de RLS-ul restrâns pe `profiles` care făcea ca embed-ul
       club_members→profiles să întoarcă null (lista rămânea goală → „search-ul nu mergea"). */
    try {
        Object.keys(state.members || {}).forEach(name => {
            const m = state.members[name];
            if (!m || !m.dbId) return;                                   // sare invitații fără cont
            if (state.currentUser && m.dbId === state.currentUser.id) return; // mă exclud pe mine
            _transferMembers.push({
                id: m.dbId,
                name: name || '—',
                userTag: m.userTag || '',
                avatar: m.avatar || '',
                role: m.role || 'member'
            });
        });
        _transferMembers.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } catch (e) { console.warn('transfer members (state):', e && e.message); }
    /* Fallback DB doar dacă state.members e gol din vreun motiv. */
    if (!_transferMembers.length) {
        try {
            const r = await sb.from('club_members')
                .select('profile_id, role, profiles(name, display_name, user_tag, avatar)')
                .eq('club_id', currentClubId).eq('status', 'active');
            _transferMembers = ((r && r.data) || [])
                .filter(m => m.profile_id && state.currentUser && m.profile_id !== state.currentUser.id)
                .map(m => ({
                    id: m.profile_id,
                    name: (m.profiles && (m.profiles.display_name || m.profiles.name)) || '—',
                    userTag: (m.profiles && m.profiles.user_tag) || '',
                    avatar: (m.profiles && m.profiles.avatar) || '',
                    role: m.role || 'member'
                }));
        } catch (e) { console.warn('transfer members (db):', e && e.message); }
    }

    const modal = document.createElement('div');
    modal.className = 'modal active'; if (modal.dataset) modal.dataset.dynamic = '1';
    modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
    modal.innerHTML = `<div class="modal-content" style="max-width:480px;">
        <div class="modal-header">
            <h2><i class="fas fa-people-arrows"></i> ${t('transfer.title')}</h2>
            <button class="modal-close" onclick="this.closest('.modal').remove()">×</button>
        </div>
        <div style="background:rgba(255,107,53,0.08); border:1px solid var(--accent-primary,#ff6b35); border-radius:10px; padding:10px 12px; margin-bottom:12px; font-size:0.88em;">
            <i class="fas fa-triangle-exclamation" style="color:var(--accent-primary,#ff6b35);"></i> ${t('transfer.warning')}
        </div>
        <input type="text" id="transferSearch" class="form-control" placeholder="${t('transfer.searchPh')}" oninput="renderTransferList()" autocomplete="off">
        <div id="transferList" style="max-height:280px; overflow:auto; border:1px solid var(--border,#333); border-radius:10px; margin-top:10px;"></div>
    </div>`;
    document.body.appendChild(modal);
    renderTransferList();
}
function renderTransferList() {
    const list = document.getElementById('transferList'); if (!list) return;
    const q = ((document.getElementById('transferSearch') || {}).value || '').trim().toLowerCase().replace(/^@/, '');
    let rows = _transferMembers;
    if (q) rows = rows.filter(m => (m.name || '').toLowerCase().indexOf(q) !== -1 || (m.userTag || '').toLowerCase().indexOf(q) !== -1);
    if (!rows.length) { list.innerHTML = '<div style="padding:12px; color:var(--text-secondary); font-size:0.85em;">' + t('transfer.noMembers') + '</div>'; return; }
    list.innerHTML = rows.map(m => {
        const tag = m.userTag ? ' <span style="color:var(--accent-primary); font-size:0.82em;">@' + clubEsc(m.userTag) + '</span>' : '';
        const av = (typeof avatarHtml === 'function') ? avatarHtml(m.avatar, 30) : '';
        return `<div onclick="confirmTransferOwnership('${attrJs(m.id)}','${attrJs(m.name)}')" style="display:flex; align-items:center; justify-content:space-between; gap:10px; padding:10px 12px; cursor:pointer; border-bottom:1px solid var(--border,#2a2a3a);">
            <span style="display:flex; align-items:center; gap:8px;">${av}<span>${clubEsc(m.name)}${tag}</span></span>
            <i class="fas fa-arrow-right" style="opacity:0.5;"></i>
        </div>`;
    }).join('');
}
async function confirmTransferOwnership(profileId, name) {
    if (!await confirmModal(t('transfer.confirm').replace('{name}', name), { danger: true })) return;
    if (!await requireReauth('ownership.transfer')) return;
    doTransferOwnership(profileId, name);
}
async function doTransferOwnership(profileId, name) {
    const c = activeClub(); if (!c) return;
    try {
        showNotification(t('transfer.working'), 'info');
        const res = await sb.rpc('transfer_club_ownership', { p_club_id: c.id, p_new_owner: profileId });
        if (res && res.error) { showNotification(t('transfer.failed') + ' (' + res.error.message + ')', 'error'); return; }
        const code = res && res.data;
        if (code !== 'OK') {
            const map = {
                TARGET_NO_SUBSCRIPTION: t('transfer.errNoSub'),
                TARGET_TRIAL: t('trial.transferBlocked'),
                TARGET_NO_FREE_SLOT: t('transfer.errNoSlot'),
                TARGET_NOT_MEMBER: t('transfer.errNotMember'),
                NOT_PRINCIPAL: t('rbac.noPermission')
            };
            showNotification(map[code] || (t('transfer.failed') + ' (' + code + ')'), 'error');
            return;
        }
        try { document.querySelectorAll('.modal.active').forEach(m => { if (m.dataset && m.dataset.dynamic) m.remove(); }); } catch (e) { logError('doTransferOwnership', e); }
        /* Notificare țintită către noul owner (DM privat → apare ca notificare la el). */
        try {
            await sb.from('direct_messages').insert({
                sender_id: state.currentUser.id,
                recipient_id: profileId,
                body: t('transfer.dmBody').replace('{club}', c.name)
            });
        } catch (e) { console.warn('transfer dm:', e && e.message); }
        /* Notifică ceilalți membri despre noul admin principal (clopoțel + push). */
        try {
            if (typeof pushNotification === 'function' && typeof clubMemberIds === 'function') {
                const ids = clubMemberIds().filter(id => id && id !== state.currentUser.id && id !== profileId);
                if (ids.length) pushNotification(ids, { clubId: c.id, type: 'ownership', icon: 'fa-crown', tab: 'players', text: t('notif.newPrincipal').replace('{name}', name) });
            }
        } catch (e) { logError('doTransferOwnership', e); }
        await loadMyClubsAndSelect({ id: state.currentUser.id });
        try { applyClubRole(); } catch (e) { logError('doTransferOwnership', e); }
        try { if (typeof loadModeration === 'function') await loadModeration(); } catch (e) { logError('doTransferOwnership', e); }
        showNotification(t('transfer.success').replace('{name}', name), 'success');
        try { if (typeof render === 'function') render(); } catch (e) { logError('doTransferOwnership', e); }
        try { renderClubSwitcher(); } catch (e) { logError('doTransferOwnership', e); }
    } catch (e) {
        showNotification(t('transfer.failed'), 'error');
    }
}

/* ---------- Avatar club (comprimat la ~256px, salvat ca data URL în image_url) ---------- */
function clubAvatarPick(input) {
    const f = input.files && input.files[0]; if (!f) return;
    if (!/^image\//.test(f.type)) { showNotification(t('clubset.avatarNotImage'), 'error'); return; }
    const reader = new FileReader();
    reader.onload = e => {
        const img = new Image();
        img.onload = () => {
            const max = 256; let w = img.width, h = img.height;
            if (w > h) { if (w > max) { h = Math.round(h * max / w); w = max; } }
            else { if (h > max) { w = Math.round(w * max / h); h = max; } }
            const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
            cv.getContext('2d').drawImage(img, 0, 0, w, h);
            const data = cv.toDataURL('image/jpeg', 0.82);
            const hidden = document.getElementById('clubImageData'); if (hidden) hidden.value = data;
            const prev = document.getElementById('clubImgPreview');
            if (prev) prev.innerHTML = `<img src="${data}" style="width:56px;height:56px;border-radius:12px;object-fit:cover;">`;
            showNotification(t('clubset.avatarReady'), 'info');
        };
        img.src = e.target.result;
    };
    reader.readAsDataURL(f);
}
function clubAvatarRemove() {
    const hidden = document.getElementById('clubImageData'); if (hidden) hidden.value = '';
    const prev = document.getElementById('clubImgPreview');
    const c = activeClub();
    if (prev) prev.innerHTML = `<div style="width:56px;height:56px;border-radius:12px;background:var(--gradient-primary,#ff6b35);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:1.3em;">${clubEsc(((c && c.name) || '?').trim().charAt(0).toUpperCase())}</div>`;
}

/* Pornește Stripe Checkout (doar Principal): validează fiscalul, cheamă
   Edge Function-ul create-checkout-session și redirecționează către Stripe. */
let _pendingCheckout = null;
async function startCheckout(plan, period) {
    if (!state.currentUser) { showNotification(t('rbac.noPermission'), 'error'); return; }
    if (plan === 'enterprise') { showNotification(t('plans.enterpriseContact'), 'info'); return; }
    showNotification(t('checkout.loading'), 'info');
    try {
        const { data, error } = await sb.functions.invoke('create-checkout-session', {
            /* ★ Trimitem si valuta aleasa din comutator: pana acum el era doar
               vizual, iar Stripe alegea singur — deci puteai vedea un pret in
               dolari si sa ti se ceara plata in lei. */
            body: {
                plan_type: plan,
                billing_period: (period === 'yearly' ? 'yearly' : 'monthly'),
                currency: _plansCurrency,
                return_url: window.location.origin
            }
        });
        if (error || !data || !data.url) {
            const code = (data && data.error) || (error && error.message) || '';
            /* Serverul refuza a doua cumparare peste un abonament viu. Nu e o
               eroare pentru om — il ducem exact unde trebuie. */
            if (/already_subscribed/i.test(String(code))) {
                showNotification(t('plans.alreadySubscribed'), 'info');
                if (typeof openBillingPortal === 'function') openBillingPortal(null);
                return;
            }
            showNotification(t('checkout.netError') + (code ? ' (' + code + ')' : ''), 'error');
            return;
        }
        showNotification(t('checkout.redirect'), 'info');
        try { localStorage.setItem('gbgsc_checkout_return', '1'); } catch (e) { logError('startCheckout', e); }
        window.location.href = data.url;
    } catch (e) {
        showNotification(t('checkout.netError'), 'error');
    }
}
/* După plată, webhook-ul activează planul în DB cu mică întârziere. Reîncarcă
   cluburile de câteva ori (polling) și re-randează când planul s-a schimbat. */
async function refreshAfterCheckout(attempt) {
    attempt = attempt || 0;
    try {
        if (!state.currentUser || !state.currentUser.id) return;
        const before = (typeof userActivePlan === 'function') ? userActivePlan() : null;
        await loadUserSubscription();
        await loadMyClubsAndSelect({ id: state.currentUser.id });
        try { if (typeof applyClubRole === 'function') applyClubRole(); } catch (e) { logError('refreshAfterCheckout', e); }
        const after = (typeof userActivePlan === 'function') ? userActivePlan() : null;
        try { const hub = document.getElementById('clubHub'); if (hub && !hub.classList.contains('hidden') && typeof renderClubHub === 'function') renderClubHub(); } catch (e) { logError('refreshAfterCheckout', e); }

        /* NOU: după downgrade, forțează recompute over-limit; dacă owner-ul a rămas
           peste limită și încă n-a ales, deschide automat alegerea cluburilor. */
        try {
            await sb.rpc('refresh_my_over_limit');
            await loadMyClubsAndSelect({ id: state.currentUser.id });
            try { renderClubReadonlyBanner(); } catch (e) { logError('refreshAfterCheckout', e); }
            let _claimPending = null; try { _claimPending = localStorage.getItem('to_claim_club_id'); } catch (e) { logError('refreshAfterCheckout', e); }
            if (!_claimPending && (state.myClubs || []).some(function (c) { return c.overLimit; })) {
                const rs = await sb.rpc('my_over_limit_state');
                const stt = (rs && rs.data && rs.data[0]) || null;
                if (stt && stt.is_over && !stt.locked) { setTimeout(function () { openChooseActiveClubsModal(); }, 600); return; }
            }
        } catch (e) { logError('refreshAfterCheckout', e); }

        /* PRELUARE CLUB în curs — independent de „schimbarea" planului (userul putea avea deja unul). */
        let _claim = null; try { _claim = localStorage.getItem('to_claim_club_id'); } catch (e) { logError('refreshAfterCheckout', e); }
        if (_claim) {
            if (after) { await finalizeClaimIfPending(); return; }
            /* abonamentul încă nu e activ (webhook întârziat) → mai încearcă */
            if (attempt < 10) { setTimeout(function () { refreshAfterCheckout(attempt + 1); }, 2500); }
            return;
        }

        if (after && after !== before) {
            showNotification(t('checkout.activated'), 'success');
            let _pc = null; try { _pc = localStorage.getItem('to_after_buy_create'); } catch (e) { logError('refreshAfterCheckout', e); }
            if (_pc) { try { localStorage.removeItem('to_after_buy_create'); } catch (e) { logError('refreshAfterCheckout', e); } setTimeout(function () { if (typeof createClubPrompt === 'function') createClubPrompt(); }, 700); }
            return;
        }
    } catch (e) { logError('refreshAfterCheckout', e); }
    if (attempt < 8) {
        setTimeout(function () { refreshAfterCheckout(attempt + 1); }, 2500);
    } else {
        /* Z5-D: webhook lent (>~20s). Plata e OK (DB = sursa de adevăr); planul apare la
           următoarea încărcare. NU lăsăm userul „în aer" -> mesaj clar de reîncărcare. */
        showNotification(t('checkout.pendingReload'), 'info');
    }
}

/* La întoarcerea de la Stripe (?checkout=success|cancel) afișează mesajul potrivit. */
function handleCheckoutReturn() {
    let co = null, billing = null;
    try {
        const params = new URLSearchParams(window.location.search);
        co = params.get('checkout');
        billing = params.get('billing');
        if (!co && !billing) return;
        const clean = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, clean);
    } catch (e) { return; }
    if (co === 'success') {
        showNotification(t('checkout.success'), 'success');
        setTimeout(function () { if (typeof refreshAfterCheckout === 'function') refreshAfterCheckout(0); }, 1500);
    } else if (co === 'cancel') {
        showNotification(t('checkout.canceled'), 'info');
    } else if (billing === 'return') {
        if (typeof refreshAfterBillingPortal === 'function') refreshAfterBillingPortal();
    }
}

/* Intoarcere din portalul de facturare.
   ⚠ NU refolosim `refreshAfterCheckout`: aceea e construita in jurul intrebarii
   „s-a schimbat PLANUL?". La o anulare planul NU se schimba (ramai pe el pana la
   finalul perioadei platite), deci ar fi reincercat ~20 de secunde si ar fi
   incheiat cu un mesaj de „reincarca pagina" complet nepotrivit.
   Aici nu stim CE s-a schimbat si nici nu ne intereseaza: reimprospatam starea
   de cateva ori, ca sa acoperim intarzierea webhook-ului, si lasam datele sa
   vorbeasca. Fara notificari — o anulare nu e un „succes", iar daca omul n-a
   schimbat nimic, orice mesaj ar fi zgomot. */
async function refreshAfterBillingPortal() {
    const _amprenta = function () {
        const u = state.userSub;
        if (!u) return '';
        return [u.plan, u.subscription_status, u.billing_period,
                u.current_period_end, !!u.cancel_at_period_end].join('|');
    };
    const _reimprospateaza = async function () {
        try {
            await loadUserSubscription();
            await loadMyClubsAndSelect({ id: state.currentUser.id });
            try { if (typeof applyClubRole === 'function') applyClubRole(); } catch (e) { logError('refreshAfterBillingPortal', e); }
            try { if (typeof render === 'function') render(); } catch (e) { logError('refreshAfterBillingPortal', e); }
        } catch (e) { logError('refreshAfterBillingPortal', e); }
    };
    try {
        if (!state.currentUser || !state.currentUser.id) return;
        await _reimprospateaza();
        let anterior = _amprenta();
        /* Webhook-ul Stripe soseste de obicei in 1-3 secunde, dar nu e garantat.
           Trei incercari rare acopera cazul obisnuit fara sa incarcam serverul. */
        for (const asteptare of [3000, 6000]) {
            await new Promise(function (r) { setTimeout(r, asteptare); });
            await _reimprospateaza();
            const acum = _amprenta();
            if (acum !== anterior) return;   /* s-a vazut schimbarea; ne oprim */
            anterior = acum;
        }
    } catch (e) { logError('refreshAfterBillingPortal', e); }
}



let _joinReqGen = 0;

async function openJoinRequests() {
    const menu = document.getElementById('clubMenu'); if (menu) menu.classList.add('hidden');
    if (!isAdminOfActiveClub()) { showNotification(t('sb.onlyAdminsRequests'), 'error'); return; }

    /* Idempotent (§1/§20.5) — EXACT tiparul din `openDiscoverPlayers`, care avea
       aceeași problemă și a fost reparat acolo.
       Modalul nu avea `id`, deci la a doua deschidere rămâneau DOUĂ noduri cu
       `id="joinReqList"`. `getElementById` îl prinde pe PRIMUL — cel vechi,
       invizibil — deci randam în fantomă, iar modalul nou rămânea pe „Se
       încarcă…" la nesfârșit. Doar un refresh de pagină îl repara.
       EN: same ghost-node bug already fixed in `openDiscoverPlayers`. */
    const prev = document.getElementById('joinReqModal'); if (prev) prev.remove();
    const gen = ++_joinReqGen;

    const modal = document.createElement('div');
    modal.className = 'modal active';
    modal.id = 'joinReqModal';
    modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
    modal.innerHTML = `<div class="modal-content"><div class="modal-header">
        <h2><i class="fas fa-envelope-open-text"></i> ${t('join.title')}</h2>
        <button class="modal-close" onclick="closeModal('joinReqModal')">×</button>
        </div><div id="joinReqList"><p style="opacity:0.7;">${t('common.loading')}</p></div></div>`;
    document.body.appendChild(modal);
    await refreshJoinRequests(gen);
}

async function refreshJoinRequests(gen) {
    /* Căutăm lista DOAR în modalul curent, nu global — a doua plasă de siguranță
       împotriva nodurilor-fantomă. */
    const host = document.getElementById('joinReqModal');
    const list = host ? host.querySelector('#joinReqList') : document.getElementById('joinReqList');
    if (!list) return;
    const alive = () => (gen === undefined || (gen === _joinReqGen && (!host || host.isConnected)));
    /* ★ FIX: NU mai folosim embed-ul PostgREST `profiles!profile_id(...)`.
       Solicitantul NU e inca membru al clubului, iar RLS filtreaza resursele
       incorporate -> embed-ul venea `null` si afisam "Necunoscut".
       Citim profilurile separat (acelasi tipar folosit deja la notificari),
       cu poza, display_name si @tag, si facem numele clickabil spre profilul public. */
    const res = await sb.from('club_join_requests')
        .select('id, profile_id, message, created_at')
        .eq('club_id', currentClubId).eq('status', 'pending').order('created_at', { ascending: true });
    if (!alive()) return;
    if (res.error) { list.innerHTML = `<p style="color:#e07a5f;">${t('common.errorPrefix')}${clubEsc(res.error.message)}</p>`; return; }
    const reqs = res.data || [];
    if (!reqs.length) { list.innerHTML = `<p style="opacity:0.7;">${t('join.none')}</p>`; return; }

    const ids = reqs.map(r => r.profile_id).filter(Boolean);
    const profs = {};
    if (ids.length) {
        try {
            const pr = await sb.from('profiles').select('id, name, display_name, avatar, user_tag').in('id', ids);
            if (pr && pr.error) logError('refreshJoinRequests.profiles', pr.error);
            (pr && pr.data || []).forEach(p => { profs[p.id] = p; });
        } catch (e) { logError('refreshJoinRequests', e); }

        /* ⚠ Solicitantul NU e încă membru al clubului. Politica `profiles_select`
           îl lasă vizibil doar dacă e `is_discoverable` sau dacă `can_see_profile`
           întoarce adevărat — altfel citirea de mai sus întoarce ZERO rânduri și
           afișam „Utilizator necunoscut", exact când aveai cea mai mare nevoie să
           știi cine cere să intre.
           Rezervă: RPC-ul EXISTENT `names_for_ids` (SECURITY DEFINER, deci
           ocolește RLS; deja folosit în alte două locuri din fișier). Nu aduce
           poză și @tag, dar aduce NUMELE — suficient ca să poți decide.
           EN: the requester is not a member yet, so RLS can hide their profile.
           Fall back to the existing `names_for_ids` RPC for at least the name. */
        const missing = ids.filter(id => !profs[id]);
        if (missing.length) {
            try {
                const nres = await sb.rpc('names_for_ids', { p_ids: missing });
                if (!nres.error && Array.isArray(nres.data)) {
                    nres.data.forEach(r => { if (r && r.id) profs[r.id] = { id: r.id, name: r.name || '' }; });
                }
            } catch (e) { logError('refreshJoinRequests.names', e); }
        }
    }
    if (!alive()) return;

    list.innerHTML = reqs.map(r => {
        const p = profs[r.profile_id] || {};
        const nm = p.display_name || p.name || t('common.unknownUser');
        const av = (typeof avatarHtml === 'function') ? avatarHtml(p.avatar, 40) : '';
        const tag = p.user_tag ? ` <span style="font-size:0.72em; color:var(--accent-primary); font-weight:600;">@${clubEsc(p.user_tag)}</span>` : '';
        const openProf = `openPublicProfileById('${window.attrJs(String(r.profile_id))}','${window.attrJs(String(nm))}')`;
        return `<div style="display:flex; align-items:center; justify-content:space-between; gap:10px; padding:10px 0; border-bottom:1px solid var(--border,#333);">
            <div style="display:flex; align-items:center; gap:10px; min-width:0; flex:1; cursor:pointer;" onclick="${openProf}">
                <div style="width:40px; height:40px; flex:none; display:flex; align-items:center; justify-content:center;">${av}</div>
                <div style="min-width:0;">
                    <div style="font-weight:600; color:var(--accent-primary);">${clubEsc(nm)}${tag}</div>
                    ${r.message ? `<div style="font-size:0.78em; opacity:0.7;">${clubEsc(r.message)}</div>` : `<div style="font-size:0.72em; opacity:0.55;">${t('join.viewProfile')}</div>`}
                </div>
            </div>
            <div style="display:flex; gap:6px; flex:none;">
                <button class="btn btn-sm btn-primary" style="width:auto; min-width:44px; padding:8px 12px;" onclick="event.stopPropagation(); approveJoinRequest('${r.id}','${r.profile_id}')"><i class="fas fa-check"></i></button>
                <button class="btn btn-sm btn-danger" style="width:auto; min-width:44px; padding:8px 12px;" onclick="event.stopPropagation(); rejectJoinRequest('${r.id}')"><i class="fas fa-times"></i></button>
            </div></div>`;
    }).join('');
}

async function approveJoinRequest(reqId, profileId) {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    if (!requireMemberSlot()) return;
    const ins = await sb.from('club_members')
        .upsert({ club_id: currentClubId, profile_id: profileId, role: 'member' }, { onConflict: 'club_id,profile_id' });
    if (ins.error) { showNotification(t('sb.approveErrorColon') + ins.error.message, 'error'); return; }
    try { await notifyClubNewMember(currentClubId, profileId); } catch (e) { logError('approveJoinRequest', e); }
    /* Notifică userul aprobat că a fost acceptat (clopoțel + push). */
    try { if (typeof pushNotification === 'function') pushNotification([profileId], { clubId: currentClubId, type: 'join_approved', icon: 'fa-circle-check', tab: 'dashboard', text: t('notif.joinApproved') }); } catch (e) { logError('approveJoinRequest', e); }
    await sb.from('club_join_requests').update({
        status: 'approved', decided_by: state.currentUser.id, decided_at: new Date().toISOString()
    }).eq('id', reqId);
    showNotification(t('sb.requestApproved'), 'success');
    await refreshJoinRequests();
    await refreshPendingBadge();
    await hydrateFromSupabase();
    /* Aceeasi reimprospatare fortata ca la `addPlayer`: o cerere de aderare aprobata aduce
       un membru NOU, dar clubul nu s-a schimbat, deci hartile s-ar sari.
       EN: same forced refresh as in addPlayer — a new member, unchanged club. */
    try { if (typeof paRefreshActivationStatus === 'function') await paRefreshActivationStatus(true); } catch (e) { logError('approveJoinRequest.paStatus', e); }
    try { if (typeof refreshAccountStatus === 'function') await refreshAccountStatus(true); } catch (e) { logError('approveJoinRequest.acctStatus', e); }
    if (typeof renderPlayers === 'function') renderPlayers();
}

async function rejectJoinRequest(reqId) {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    const up = await sb.from('club_join_requests').update({
        status: 'rejected', decided_by: state.currentUser.id, decided_at: new Date().toISOString()
    }).eq('id', reqId);
    if (up.error) { showNotification(t('sb.errorColon') + up.error.message, 'error'); return; }
    showNotification(t('sb.requestRejected'), 'info');
    await refreshJoinRequests();
    await refreshPendingBadge();
}

/* ============================================================================
   ETAPA 0 — TOKENII DE INTRARE DIN URL SUPRAVIEȚUIESC ÎNREGISTRĂRII
   ----------------------------------------------------------------------------
   RO: Un om FĂRĂ cont care primește un link cu token (`?invite=` azi, `?pa=` la
       activarea profilului) pierdea parametrul în timpul autentificării:
         · retur Google  -> `_oauthReloadWhenPersisted` face
                            `location.replace(window.location.origin)` = query șters;
         · confirmare pe email -> `emailRedirectTo` era `window.location.origin`,
                            deci linkul din email nu purta tokenul.
       Reparăm pe DOUĂ căi, ca să acoperim ambele situații:
         (1) `sessionStorage` — supraviețuiește redirect-ului ÎN ACELAȘI TAB (Google);
         (2) tokenul atașat la URL-ul de retur — singurul mod în care supraviețuiește
             CONFIRMĂRII PE EMAIL, care se deschide într-un TAB NOU (unde
             sessionStorage e gol). Același model ca linkul de resetare de parolă.
       ⚠ Fără token, `paAuthRedirectUrl()` întoarce EXACT `window.location.origin`,
       bit-identic cu comportamentul de dinainte (zero risc pe fluxul normal).
       ⚠ NU folosim `localStorage` pentru tokeni de identitate.
   EN: Entry tokens from the URL now survive sign-up (session storage for the
       same-tab OAuth return, redirect URL for the new-tab email confirmation).
   Globale noi (prefix `pa`, ZERO overrides): `PA_ENTRY_PARAMS`, `PA_ENTRY_SS_PREFIX`,
   `paStashEntryTokens`, `paPeekEntryToken`, `paClearEntryToken`, `paAuthRedirectUrl`.
   ============================================================================ */
const PA_ENTRY_PARAMS = ['invite', 'pa'];
const PA_ENTRY_SS_PREFIX = 'to_entry_';

/* Salvează tokenii din URL la încărcarea paginii, ÎNAINTE ca vreun redirect sau
   `history.replaceState` să-i șteargă. */
function paStashEntryTokens() {
    try {
        const sp = new URL(window.location.href).searchParams;
        for (const k of PA_ENTRY_PARAMS) {
            const v = sp.get(k);
            if (v) { try { sessionStorage.setItem(PA_ENTRY_SS_PREFIX + k, v); } catch (e) { logError('paStashEntryTokens:set', e); } }
        }
    } catch (e) { logError('paStashEntryTokens', e); }
}

/* Citește tokenul fără să-l consume: întâi din URL (sursa proaspătă), apoi din stash. */
function paPeekEntryToken(kind) {
    try {
        const fromUrl = new URL(window.location.href).searchParams.get(kind);
        if (fromUrl) return fromUrl;
    } catch (e) { logError('paPeekEntryToken:url', e); }
    try { return sessionStorage.getItem(PA_ENTRY_SS_PREFIX + kind) || null; }
    catch (e) { logError('paPeekEntryToken:ss', e); return null; }
}

/* Consumă tokenul: îl scoate ȘI din stash ȘI din URL (ca să nu rămână vizibil în
   bara de adrese sau în `Referer`-ul cererilor următoare). */
function paClearEntryToken(kind) {
    try { sessionStorage.removeItem(PA_ENTRY_SS_PREFIX + kind); } catch (e) { logError('paClearEntryToken:ss', e); }
    try {
        const u = new URL(window.location.href);
        if (u.searchParams.get(kind) != null) {
            u.searchParams.delete(kind);
            window.history.replaceState({}, '', u.toString());
        }
    } catch (e) { logError('paClearEntryToken:url', e); }
}

/* URL-ul de retur pentru `signUp` / `resend` / `signInWithOAuth`. */
function paAuthRedirectUrl() {
    try {
        const pairs = [];
        for (const k of PA_ENTRY_PARAMS) {
            const v = paPeekEntryToken(k);
            if (v) pairs.push([k, v]);
        }
        /* Fără token -> exact valoarea folosită înainte (fără slash adăugat), ca să nu
           depindem de forma exactă din allowlist-ul de Redirect URLs din Supabase. */
        if (!pairs.length) return window.location.origin;
        const u = new URL(window.location.origin);
        for (const [k, v] of pairs) u.searchParams.set(k, v);
        return u.toString();
    } catch (e) { logError('paAuthRedirectUrl', e); return window.location.origin; }
}

/* Rulează la încărcarea scriptului: cel mai devreme moment în care putem citi URL-ul. */
paStashEntryTokens();

/* ---------- Invitații prin link (token) ---------- */
/* ============================================================================
   ETAPA 3 — INTERFAȚA DE ADMINISTRARE A LINKURILOR DE ACTIVARE
   ----------------------------------------------------------------------------
   RO: Adminul vede pe cardul fiecărui jucător dacă acesta are cont, iar pentru
       cei fără cont poate genera, copia sau anula un link personal de activare.
       Cine are voie decide SERVERUL (rang >= 3 în `profile_activation_*`).
       Verificările de aici sunt doar ca să nu arătăm butoane inutile.
   EN: Admin-side UI for profile-activation links. Authorization is server-side.
   ============================================================================ */
/* ===== Cine are cont: disponibil pentru TOȚI membrii clubului ================
   RO: `_paStatus` (mai jos) vine din `profile_activation_status_for_club`, care
       cere rang >= 3 — deci NUMAI administratorii știu cine are cont. Un Membru
       simplu vedea „Adaugă prieten" și pe profiluri fără cont.
       ⚠ Cererea nu eșua: `friendships.addressee_id` are cheie străină spre
         `profiles`, NU spre `auth.users`, deci inserarea REUȘEA. Nimeni nu se
         putea autentifica pe acel profil ca s-o accepte ⇒ rămânea `pending` la
         infinit, iar `UNIQUE (requester_id, addressee_id)` bloca orice
         reîncercare, chiar și după activarea ulterioară a contului.
       Serverul are acum `club_members_account_status(p_club_id)` — SECURITY
       DEFINER, `search_path = ''`, autorizare prin `is_club_member` (NU prin RLS,
       pe care SECURITY DEFINER o ocolește), fără `EXECUTE` pentru `anon`.
       Returnează strict `(profile_id, has_account)` — niciun email, nimic din
       `auth.users`. În plus, un trigger BEFORE INSERT pe `friendships` respinge
       cererile către profiluri fără cont: interfața nu mai e singura apărare.
   EN: account status is now readable by every club member via a hardened RPC;
       a BEFORE INSERT trigger also rejects requests to account-less profiles. */
var _acctStatus = { clubId: null, map: {}, loading: false };

async function refreshAccountStatus(force) {
    try {
        if (!sb || !currentClubId || !state.currentUser) return;
        /* ★ Demo = zero trafic către baza reală, fără excepții. */
        if (typeof isDemoMode === 'function' && isDemoMode()) {
            _acctStatus = { clubId: null, map: {}, loading: false };
            return;
        }
        if (_acctStatus.loading) return;
        if (!force && _acctStatus.clubId === currentClubId) return;
        _acctStatus.loading = true;
        const cerutPentru = currentClubId;
        const res = await sb.rpc('club_members_account_status', { p_club_id: cerutPentru });
        _acctStatus.loading = false;
        /* Funcția poate lipsi (migrare neaplicată) sau poate refuza. Nu e o
           eroare de tratat zgomotos: rămânem pe `null` = „nu știm", iar butonul
           se comportă ca înainte. */
        if (res && res.error) { _acctStatus = { clubId: null, map: {}, loading: false }; return; }
        /* Clubul s-a schimbat cât timp așteptam ⇒ aruncăm răspunsul vechi. */
        if (cerutPentru !== currentClubId) return;
        const map = {};
        (res && res.data ? res.data : []).forEach(function (r) {
            if (r && r.profile_id) map[r.profile_id] = (r.has_account === true);
        });
        _acctStatus = { clubId: cerutPentru, map: map, loading: false };
    } catch (e) {
        _acctStatus.loading = false;
        logError('refreshAccountStatus', e);
    }
}

/* `true` / `false` / `null` dacă nu știm (alt club, demo, RPC indisponibil). */
function hasAccount(profileId) {
    if (!profileId || _acctStatus.clubId !== currentClubId) return null;
    const v = _acctStatus.map[profileId];
    return (v === undefined) ? null : v;
}

/* Se cheamă o singură dată pe club, la nevoie — ca `ensureFriendData`. */
function ensureAccountStatus() {
    if (_acctStatus.clubId === currentClubId) return Promise.resolve();
    return refreshAccountStatus(false).catch(function (e) { logError('ensureAccountStatus', e); });
}

var _paStatus = { clubId: null, map: {}, loading: false };

/* Încarcă starea de activare a membrilor clubului curent. Rezultatul e ținut în
   memorie și reîmprospătat doar când se schimbă clubul sau după o acțiune. */
async function paRefreshActivationStatus(force) {
    try {
        if (!sb || !currentClubId || !state.currentUser) return;
        /* ★ În modul demo NU atingem baza de date reală, pe nicio cale. Datele
           demo sunt fictive, deci starea de activare n-ar însemna nimic — iar
           regula „demo = zero trafic către baza reală" nu are excepții. */
        if (typeof isDemoMode === 'function' && isDemoMode()) {
            _paStatus = { clubId: null, map: {}, loading: false };
            return;
        }
        if (typeof can !== 'function' || !can('member.manage')) {
            _paStatus = { clubId: null, map: {}, loading: false };
            return;
        }
        if (_paStatus.loading) return;
        if (!force && _paStatus.clubId === currentClubId) return;
        _paStatus.loading = true;
        const cerutPentru = currentClubId;
        const res = await sb.rpc('profile_activation_status_for_club', { p_club_id: cerutPentru });
        _paStatus.loading = false;
        if (res && res.error) { logError('paRefreshActivationStatus', res.error); return; }
        /* Clubul s-a schimbat cât timp așteptam: aruncăm răspunsul vechi. */
        if (cerutPentru !== currentClubId) return;
        const map = {};
        (res && res.data ? res.data : []).forEach(function (r) {
            if (r && r.profile_id) map[r.profile_id] = r;
        });
        _paStatus = { clubId: cerutPentru, map: map, loading: false };
        if (typeof renderPlayersGrid === 'function') renderPlayersGrid();
    } catch (e) {
        _paStatus.loading = false;
        logError('paRefreshActivationStatus', e);
    }
}

/* Starea unui membru, sau `null` dacă nu o știm (alt club, sau nu suntem admin). */
function paStatusOf(profileId) {
    if (!profileId || _paStatus.clubId !== currentClubId) return null;
    return _paStatus.map[profileId] || null;
}

/* Traduce codurile de refuz ale serverului în mesaje omenești. */
function _paMesajEroare(msg) {
    const m = String(msg || '');
    if (/TARGET_ALREADY_ACTIVATED/i.test(m)) return t('pact.admin.alreadyActivated');
    if (/TARGET_NOT_MEMBER/i.test(m))        return t('pact.admin.notMember');
    if (/RATE_LIMIT/i.test(m))               return t('pact.admin.rateLimit');
    if (/FORBIDDEN|permission denied/i.test(m)) return t('rbac.noPermission');
    return t('pact.admin.error');
}

/* Adresa introdusă aparține unui om care are deja cont. Nu-l putem lega de club
   fără acordul lui, deci îi oferim adminului calea corectă: linkul de invitație,
   pe care persoana îl acceptă singură. */
function paOfferInviteInstead(playerName) {
    const nume = playerName || '';
    showNotification(t('pact.admin.hasAccount').replace('{name}', nume), 'error');
    try {
        if (typeof can === 'function' && can('member.manage') && typeof openInviteLink === 'function') {
            setTimeout(function () { openInviteLink(); }, 900);
        }
    } catch (e) { logError('paOfferInviteInstead', e); }
}

async function paIssueLinkFor(profileId, playerName) {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    if (typeof isDemoMode === 'function' && isDemoMode()) return;   /* demo: zero trafic real */
    if (typeof can !== 'function' || !can('member.manage')) {
        showNotification(t('rbac.noPermission'), 'error'); return;
    }
    try {
        const res = await sb.rpc('profile_activation_issue', {
            p_club_id: currentClubId, p_profile_id: profileId
        });
        if (!res || res.error || !res.data) {
            showNotification(_paMesajEroare(res && res.error && res.error.message), 'error');
            return;
        }
        const link = window.location.origin + '/profile-activation.html?pa=' + encodeURIComponent(res.data);
        const modal = document.createElement('div');
        modal.className = 'modal active';
        modal.dataset.back = '1';   /* „înapoi" de pe telefon o închide (chat-core.js, v9 zona 2) */
        modal.innerHTML = '<div class="modal-content"><div class="modal-header">'
            + '<h2><i class="fas fa-user-check"></i> ' + t('pact.admin.modalTitle') + ' ' + clubEsc(playerName || '') + '</h2>'
            + '<button class="modal-close" data-pl-act="close" aria-label="' + t('common.close') + '">×</button>'
            + '</div>'
            + '<p style="opacity:0.85; font-size:0.9em;">' + t('pact.admin.modalNote') + '</p>'
            + '<div class="form-group">'
            + '<input type="text" id="inviteLinkInput" class="form-control" readonly value="' + clubEsc(link) + '" data-pl-act="select">'
            + '</div>'
            + '<button class="btn btn-primary" data-pl-act="copy-link"><i class="fas fa-copy"></i> ' + t('invite.copy') + '</button>'
            + '</div>';
        document.body.appendChild(modal);
        await paRefreshActivationStatus(true);
    } catch (e) {
        logError('paIssueLinkFor', e);
        showNotification(t('pact.admin.error'), 'error');
    }
}

async function paRevokeLinkFor(profileId, playerName) {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    if (typeof isDemoMode === 'function' && isDemoMode()) return;   /* demo: zero trafic real */
    if (typeof can !== 'function' || !can('member.manage')) {
        showNotification(t('rbac.noPermission'), 'error'); return;
    }
    const intrebare = t('pact.admin.confirmRevoke').replace('{name}', playerName || t('sb.thisMember'));
    if (!await confirmModal(intrebare, { danger: true })) return;
    try {
        const res = await sb.rpc('profile_activation_revoke', {
            p_club_id: currentClubId, p_profile_id: profileId
        });
        if (!res || res.error) {
            showNotification(_paMesajEroare(res && res.error && res.error.message), 'error');
            return;
        }
        showNotification(t('pact.admin.revoked'), 'success');
        await paRefreshActivationStatus(true);
    } catch (e) {
        logError('paRevokeLinkFor', e);
        showNotification(t('pact.admin.error'), 'error');
    }
}

async function openInviteLink() {
    const menu = document.getElementById('clubMenu'); if (menu) menu.classList.add('hidden');
    if (!isAdminOfActiveClub()) { showNotification(t('sb.onlyAdminsInvites'), 'error'); return; }
    const token = (window.crypto && crypto.randomUUID)
        ? crypto.randomUUID().replace(/-/g, '')
        : (Date.now().toString(36) + Math.random().toString(36).slice(2, 12));
    const ins = await sb.from('club_invitations')
        .insert({ club_id: currentClubId, token: token, role: 'member', created_by: state.currentUser.id })
        .select().maybeSingle();
    if (ins.error || !ins.data) { showNotification(t('sb.inviteCreateErrorColon') + (ins.error ? ins.error.message : ''), 'error'); return; }
    let base = window.location.origin + window.location.pathname;
    const link = base + '?invite=' + token;
    const c = activeClub();
    const modal = document.createElement('div');
    modal.className = 'modal active';
    modal.innerHTML = `<div class="modal-content"><div class="modal-header">
        <h2><i class="fas fa-link"></i> ${t('invite.titlePrefix')} ${clubEsc(c ? c.name : '')}</h2>
        <button class="modal-close" onclick="this.parentElement.parentElement.parentElement.remove()">×</button>
        </div>
        <p style="opacity:0.8; font-size:0.9em;">${t('invite.note')}</p>
        <div class="form-group">
            <input type="text" id="inviteLinkInput" class="form-control" readonly value="${clubEsc(link)}" onclick="this.select()">
        </div>
        <button class="btn btn-primary" onclick="copyInviteLink()"><i class="fas fa-copy"></i> ${t('invite.copy')}</button>
        </div>`;
    document.body.appendChild(modal);
}

function copyInviteLink() {
    const inp = document.getElementById('inviteLinkInput');
    if (!inp) return;
    inp.select();
    const done = () => showNotification(t('sb.linkCopied'), 'success');
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(inp.value).then(done).catch(() => { try { document.execCommand('copy'); done(); } catch (e) { logError('copyInviteLink', e); } });
    } else { try { document.execCommand('copy'); done(); } catch (e) { logError('copyInviteLink', e); } }
}

/* Procesează ?invite=TOKEN din URL după autentificare */
/* ============================================================================
   ETAPA 2 — CONSUMAREA LINKULUI DE ACTIVARE A PROFILULUI
   ----------------------------------------------------------------------------
   RO: Omul a venit din `profile-activation.html`, tocmai și-a făcut cont, iar
       tokenul l-a urmat prin înregistrare (vezi Etapa 0). Aici se cere serverului
       legarea profilului vechi de contul nou.
       TOATE deciziile sunt ale serverului. Funcția asta nu verifică nimic pe cont
       propriu — doar traduce răspunsul într-un mesaj omenesc.
   EN: Redeems a profile-activation token right after sign-up. All authorization
       happens server-side in `profile_activation_redeem`.
   ============================================================================ */
async function processPendingActivation() {
    let token = null;
    try { token = paPeekEntryToken('pa'); } catch (e) { logError('processPendingActivation', e); }
    if (!token || !sb || !state.currentUser) return;
    try {
        const res = await sb.rpc('profile_activation_redeem', { p_token: token });
        /* Consumat într-un singur sens: reușit sau nu, nu-l reluăm automat.
           Singura excepție ar fi o pană de rețea, tratată de `catch`-ul de jos,
           care păstrează tokenul pentru o încercare ulterioară. */
        paClearEntryToken('pa');

        if (res && res.error) {
            showNotification(t('pact.error'), 'error');
            logError('processPendingActivation', res.error);
            return;
        }
        const d = (res && res.data) || null;
        if (!d || d.ok !== true) {
            const reason = (d && d.reason) || 'invalid';
            const cheie = 'pact.fail.' + reason;
            const mesaj = t(cheie);
            showNotification(mesaj === cheie ? t('pact.fail.invalid') : mesaj, 'error');
            return;
        }

        /* ── Reușit ─────────────────────────────────────────────────────────
           `relink_profile` a mutat toate referințele de pe profilul vechi pe
           contul curent și a șters profilul vechi. Tot ce e deja încărcat în
           memoria paginii (membri, sesiuni, clasamente) se referă la un
           identificator care NU MAI EXISTĂ. Nu e o simplă întârziere de
           reîmprospătare — e o stare inconsistentă, din care se ajunge la liste
           goale și la date care apar abia după un refresh manual.
           Reîncărcăm pagina. E singura garanție de stare curată după o fuziune
           de profiluri, și se întâmplă o singură dată în viața unui cont. */
        try {
            if (d.club_id) {
                await sb.from('profiles').update({ last_club_id: d.club_id })
                        .eq('id', state.currentUser.id);
            }
        } catch (e) { logError('processPendingActivation', e); }

        showNotification(
            (d.already_linked ? t('pact.alreadyLinked') : t('pact.success')) + (d.club_name || ''),
            'success'
        );

        /* Răgaz cât să se citească mesajul, apoi repornire curată. */
        window.__paReloading = true;   /* F1d: onAuthenticated nu mai desenează nimic între timp */
        _authTrace('pa:reload');
        setTimeout(function () {
            try { window.location.replace(window.location.origin); }
            catch (e) { try { window.location.reload(); } catch (e2) { logError('processPendingActivation', e2); } }
        }, 2200);
    } catch (e) {
        showNotification(t('pact.error'), 'error');
        logError('processPendingActivation', e);
    }
}

/* F1b: amprenta (SHA-256, primele 12 octeți) a unui token de invitație - cheia marcajului
   „invitație acceptată" din localStorage. Tokenul în sine NU ajunge în localStorage (KB §47). */
async function _inviteDoneKey(token) {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(String(token)));
    return 'to_inv_done_' + Array.from(new Uint8Array(buf)).slice(0, 12).map(function (b) { return b.toString(16).padStart(2, '0'); }).join('');
}

async function processPendingInvite() {
    /* ★ Etapa 0: tokenul poate veni din URL SAU din stash-ul salvat înainte de
       redirect-ul de autentificare (vezi `paStashEntryTokens`). */
    let token = null;
    try { token = paPeekEntryToken('invite'); } catch (e) { logError('processPendingInvite', e); }
    _authTrace('invite:start', { areToken: !!token });
    if (!token || !sb || !state.currentUser) return;
    /* F1b: dacă altă filă a acceptat deja ACEEAȘI invitație (marcaj cu amprenta tokenului, nu
       tokenul însuși), nu o mai trimitem o dată: doar intrăm în club. */
    let _mk = null;
    try {
        _mk = await _inviteDoneKey(token);
        const _done = JSON.parse(localStorage.getItem(_mk) || 'null');
        if (_done && _done.clubId && (Date.now() - Number(_done.ts || 0)) < 86400000) {
            paClearEntryToken('invite');
            await loadMyClubsAndSelect({ id: state.currentUser.id });
            applyClubRole();
            await enterClub(_done.clubId, { force: true });
            return;
        }
    } catch (e) { logError('processPendingInvite', e); }
    try {
        const res = await sb.rpc('accept_club_invite', { p_token: token });
        /* ★ Etapa 1b: „clubul e plin" NU e „invitație invalidă". Mai mult, în cazul
           ăsta NU consumăm tokenul — omul trebuie să poată reîncerca după ce
           adminul face upgrade, fără să-i cerem un link nou. */
        const _em = (res && res.error && res.error.message) || '';
        if (/MEMBER_LIMIT_REACHED/i.test(_em)) {
            showNotification(t('sb.clubFull'), 'error');
            return;
        }
        paClearEntryToken('invite');
        const clubId = res && res.data;
        if (res.error || !clubId) { _authTrace('invite:eroare', { mesaj: (res && res.error && res.error.message) || 'fara club' }); showNotification(t('sb.inviteInvalid'), 'error'); return; }
        _authTrace('invite:acceptata');
        try { if (_mk) localStorage.setItem(_mk, JSON.stringify({ clubId: clubId, ts: Date.now() })); } catch (e) { logError('processPendingInvite', e); }
        await loadMyClubsAndSelect({ id: state.currentUser.id });
        applyClubRole();
        await refreshPendingBadge();
        showNotification(t('sb.joinedViaInvite'), 'success');
        try { await notifyClubNewMember(clubId, state.currentUser.id, state.currentUser.name); } catch (e) { logError('processPendingInvite', e); }
        /* F1: `force` — clubul e deja selectat de loadMyClubsAndSelect, dar datele lui nu sunt încă încărcate. */
        await enterClub(clubId, { force: true });
    } catch (e) { showNotification(t('sb.inviteErrorColon') + (e && e.message), 'error'); }
}

/* ---------- Badge cereri în așteptare (pentru admini) ---------- */
async function refreshPendingBadge() {
    const badge = document.getElementById('clubReqBadge');
    state.pendingByClub = {};
    if (!sb || !state.currentUser || state.currentUser.isDemo) { if (badge) badge.classList.add('hidden'); return; }
    const adminClubIds = (state.myClubs || [])
        .filter(c => c.myRole === 'principal_admin' || c.myRole === 'secondary_admin')
        .map(c => c.id);
    if (!adminClubIds.length) { if (badge) badge.classList.add('hidden'); return; }
    try {
        const res = await sb.from('club_join_requests').select('club_id').in('club_id', adminClubIds).eq('status', 'pending');
        (res.data || []).forEach(r => { state.pendingByClub[r.club_id] = (state.pendingByClub[r.club_id] || 0) + 1; });
        const total = Object.values(state.pendingByClub).reduce((a, b) => a + b, 0);
        if (badge) {
            if (total > 0) { badge.textContent = total; badge.classList.remove('hidden'); }
            else badge.classList.add('hidden');
        }
        try { const hub = document.getElementById('clubHub'); if (hub && !hub.classList.contains('hidden')) renderClubHub(); } catch (e) { logError('refreshPendingBadge', e); }
    } catch (e) { if (badge) badge.classList.add('hidden'); }
}

/* ---------- Descoperă jucători (platformă) ---------- */
let _discoverPlayers = [];
/* Statistici globale (XP + tag) per jucător, calculate de RPC-ul player_public_stats,
   strict din cluburile în care e membru userul curent. Cheie: profile_id.
   SURSĂ UNICĂ DE ADEVĂR pentru XP/nivel (§7): cache-ul se ACUMULEAZĂ, nu se resetează.
   Înainte era golit (`= {}`) la fiecare încărcare, deci lista Discover și membrii
   clubului se evictau RECIPROC -> profilul deschis „al doilea" nu mai găsea statistici
   globale, cădea pe fallback-ul „doar clubul curent" și arăta un nivel prea mic. */
let _playerStatsById = {};
let _playerStatsAsked = {};   /* id -> true: deja interogat (chiar dacă RPC n-a întors rând) — anti-refetch */
let _playerStatsOwner = null; /* userul pt. care e valid cache-ul (RPC agregă cluburile LUI) */
let _discSpaceById = {}; /* id -> { has_space, capacity, address } gated pe vizibilitate (RPC) */
let _friendsById = {};          // otherId -> { status:'accepted'|'pending', dir:'out'|'in', rowId }
let _clubsByProfile = {};       // profileId -> [club names publice]
let _friendNames = {};          // id -> name (pentru lista de prieteni)
let _friendAvatars = {};        // id -> avatar (emoji sau data URL)
let _friendTags = {};           // id -> user_tag

let _friendDataLoaded = false;  /* „harta e goală" ≠ „nu am prieteni" — vezi ensureFriendData */

async function loadFriendData() {
    _friendsById = {}; _friendNames = {}; _friendAvatars = {}; _friendTags = {};
    if (isDemoMode() || !sb || !state.currentUser) return;
    try {
        const me = state.currentUser.id;
        const r = await sb.from('friendships').select('id, requester_id, addressee_id, status, created_at').or('requester_id.eq.' + me + ',addressee_id.eq.' + me);
        if (r.error) throw r.error;
        const otherIds = [];
        (r.data || []).forEach(f => {
            const other = f.requester_id === me ? f.addressee_id : f.requester_id;
            const dir = f.requester_id === me ? 'out' : 'in';
            _friendsById[other] = { status: f.status, dir, rowId: f.id, ts: f.created_at ? new Date(f.created_at).getTime() : Date.now() };
            otherIds.push(other);
        });
        if (otherIds.length) {
            let pr = await sb.from('profiles').select('id, name, avatar, user_tag').in('id', otherIds);
            if (pr.error) pr = await sb.from('profiles').select('id, name').in('id', otherIds);
            (pr.data || []).forEach(p => {
                _friendNames[p.id] = p.name;
                if (p.avatar) _friendAvatars[p.id] = p.avatar;
                if (p.user_tag) _friendTags[p.id] = p.user_tag;
            });
        }
    } catch (e) { /* tabela friendships lipsește -> ignoră */ }
    /* Marcat și pe drumul de eroare: dacă tabela lipsește, nu are rost să reîncercăm
       la fiecare deschidere de profil. */
    _friendDataLoaded = true;
    /* Blocările se încarcă din același punct: `loadFriendData` rulează la fiecare
       autentificare, schimbare de club și ciclu de poll, deci e singurul cârlig
       real de după sesiune. `block.js` NU are pornire proprie — un eveniment
       inventat n-ar fi fost emis de nimeni. */
    try { if (typeof window.blockLoad === 'function') window.blockLoad(true); } catch (e) { logError('loadFriendData', e); }
}

/* Amprentă a stării de prietenie, pentru consumatorii care vor să redeseneze DOAR
   când s-a schimbat ceva.
   ⚠ MOTIVUL: `refreshFriendsUi` e chemată din realtime ȘI din poll-ul de 20 de
     secunde. Fără amprentă, redesenarea butoanelor și a listei Descoperă ar rula
     la fiecare ciclu, la momente pe care utilizatorul nu le controlează — exact
     genul de reîmprospătare care fură cursorul sau resetează derularea.
   ⚠ Include `status` ȘI `dir`: o cerere primită și una trimisă către același om
     produc butoane diferite, deci sunt stări diferite.
   EN: cheap fingerprint of the friendship map so pollers can skip redundant
       re-renders instead of redrawing every 20 seconds. */
function _friendSignature() {
    try {
        const ids = Object.keys(_friendsById || {}).sort();
        let s = '';
        for (let i = 0; i < ids.length; i++) {
            const f = _friendsById[ids[i]] || {};
            s += ids[i] + ':' + (f.status || '') + ':' + (f.dir || '') + '|';
        }
        return s;
    } catch (e) { logError('_friendSignature', e); return String(Math.random()); }
}
window._friendSignature = _friendSignature;

/* RO: `_friendsById` gol înseamnă DOUĂ lucruri diferite — „încă nu s-au încărcat"
       și „nu am niciun prieten". Fără să le distingem, `_friendBtnHtml` afișa
       „Adaugă prieten" pentru cineva care ÎȚI ERA DEJA prieten, dacă modalul se
       deschidea înainte ca `loadFriendData()` (pornit fără `await` din
       `switchClub`) să fi terminat. Aici încărcăm o singură dată, la nevoie.
   EN: an empty map meant both "not loaded yet" and "no friends", so the button
       could show "Add friend" for an existing friend. Loaded once, on demand. */
let _friendDataPromise = null;
function ensureFriendData() {
    if (_friendDataLoaded) return Promise.resolve();
    if (!_friendDataPromise) {
        _friendDataPromise = Promise.resolve()
            .then(function () { return loadFriendData(); })
            .catch(function (e) { logError('ensureFriendData', e); })
            .then(function () { _friendDataPromise = null; });
    }
    return _friendDataPromise;
}

/* Secțiunea „Prietenii mei" din profil (lipsea definiția -> lista apărea goală). */

async function loadPublicClubsFor(ids) {
    _clubsByProfile = {};
    if (isDemoMode() || !sb || !ids || !ids.length) return;
    try {
        const r = await sb.rpc('public_clubs_for_profiles', { ids });
        if (r.error) throw r.error;
        (r.data || []).forEach(row => {
            (_clubsByProfile[row.profile_id] = _clubsByProfile[row.profile_id] || []).push(row.club_name);
        });
    } catch (e) { /* functia lipsește -> ignoră */ }
}

async function sendFriendRequest(otherId) {
    if (isDemoMode()) { showNotification(t('sb.unavailableDemo'), 'info'); return; }
    try {
        const r = await sb.from('friendships').insert({ requester_id: state.currentUser.id, addressee_id: otherId, status: 'pending' });
        if (r.error) throw r.error;
        _friendsById[otherId] = { status: 'pending', dir: 'out' };
        renderDiscoverPlayers();
        _refreshFriendBtns(otherId); /* modalul rămânea pe „Adaugă prieten" ⇒ a doua apăsare = inserare duplicat */
        showNotification(t('sb.friendRequestSent'), 'success');
    } catch (e) {
        /* Garda din baza de date (trigger BEFORE INSERT pe `friendships`) e ultima
           apărare, dacă interfața a lăsat totuși butonul vizibil. Fără traducerea
           asta, utilizatorul ar primi mesajul tehnic „rulează turnorder_friends.sql". */
        const _msg = String((e && e.message) || '');
        if (_msg.indexOf('ADDRESSEE_HAS_NO_ACCOUNT') !== -1) {
            showNotification(t('sb.friendNoAccount'), 'error');
            /* Marcăm local, ca butonul să dispară imediat, fără reîncărcare. */
            try { if (_acctStatus.clubId === currentClubId) { _acctStatus.map[otherId] = false; _refreshFriendBtns(otherId); } } catch (e2) { logError('sendFriendRequest', e2); }
            return;
        }
        showNotification(t('sb.friendsNeedSql').replace('{msg}', _msg), 'error');
    }
}
async function acceptFriendRequest(otherId, btnEl) {
    const f = _friendsById[otherId];
    /* Idempotent: deja acceptat sau în curs -> al doilea click nu face nimic. */
    if (f && (f.status === 'accepted' || f._accepting)) return;
    if (f) f._accepting = true;
    /* Dezactivează imediat butonul apăsat (anti double-click vizual). */
    if (btnEl) { try { btnEl.disabled = true; btnEl.style.opacity = '0.6'; btnEl.style.pointerEvents = 'none'; } catch (e) { logError('acceptFriendRequest', e); } }
    try {
        const q = f && f.rowId ? sb.from('friendships').update({ status: 'accepted' }).eq('id', f.rowId)
                               : sb.from('friendships').update({ status: 'accepted' }).eq('requester_id', otherId).eq('addressee_id', state.currentUser.id);
        const r = await q; if (r.error) throw r.error;
        if (_friendsById[otherId]) { _friendsById[otherId].status = 'accepted'; delete _friendsById[otherId]._accepting; }
        /* Refresh DOAR după ce starea s-a actualizat (altfel rândul re-apare cu Accept). */
        try { if (typeof renderDiscoverPlayers === 'function') renderDiscoverPlayers(); } catch (e) { logError('acceptFriendRequest', e); }
        try { if (typeof renderMyProfile === 'function') renderMyProfile(); } catch (e) { logError('acceptFriendRequest', e); }
        try { if (typeof renderNotifBadge === 'function') renderNotifBadge(); } catch (e) { logError('acceptFriendRequest', e); }
        try { if (typeof renderNotifPanel === 'function') renderNotifPanel(); } catch (e) { logError('acceptFriendRequest', e); }
        _refreshFriendBtns(otherId);
        showNotification(t('sb.nowFriends'), 'success');
    } catch (e) {
        if (_friendsById[otherId]) delete _friendsById[otherId]._accepting;
        if (btnEl) { try { btnEl.disabled = false; btnEl.style.opacity = ''; btnEl.style.pointerEvents = ''; } catch (e) { logError('acceptFriendRequest', e); } }
        showNotification(t('sb.errorColon') + (e && e.message), 'error');
    }
}
async function removeFriend(otherId) {
    try {
        const me = state.currentUser.id;
        const r = await sb.from('friendships').delete().or('and(requester_id.eq.' + me + ',addressee_id.eq.' + otherId + '),and(requester_id.eq.' + otherId + ',addressee_id.eq.' + me + ')');
        if (r.error) throw r.error;
        delete _friendsById[otherId];
        try { if (typeof renderDiscoverPlayers === 'function') renderDiscoverPlayers(); } catch (e) { logError('removeFriend', e); }
        try { if (typeof renderMyProfile === 'function') renderMyProfile(); } catch (e) { logError('removeFriend', e); }
        try { if (typeof renderNotifBadge === 'function') renderNotifBadge(); } catch (e) { logError('removeFriend', e); }
        try { if (typeof renderNotifPanel === 'function') renderNotifPanel(); } catch (e) { logError('removeFriend', e); }
        _refreshFriendBtns(otherId);
        showNotification(t('sb.friendRemoved'), 'info');
    } catch (e) { showNotification(t('sb.errorColon') + (e && e.message), 'error'); }
}
/* RO: Învelișul `data-friend-btn` face butonul REÎMPROSPĂTABIL în loc. Fără el,
       după „Adaugă prieten" dintr-un modal, butonul rămânea neschimbat (singura
       reîmprospătare era `renderDiscoverPlayers()`, utilă doar în Descoperă), iar
       o a doua apăsare încerca să insereze un rând duplicat în `friendships` —
       adică o eroare afișată utilizatorului pentru o acțiune deja reușită.
   ⚠ Auto-corectare: dacă datele de prietenie nu sunt încă încărcate, randăm ce
     știm ACUM (fără să blocăm afișarea modalului) și cerem încărcarea; când
     sosește, `_refreshFriendBtns()` rescrie butonul cu starea reală. Așa se
     repară și modalul din `social.js`, FĂRĂ să-l modificăm.
   EN: the wrapper makes the button refreshable in place, so it updates after a
       request instead of inviting a duplicate insert, and it self-corrects once
       friend data arrives — which also fixes the modal in social.js untouched. */
function _friendBtnHtml(p) {
    const id = p && p.id;
    if (!_friendDataLoaded) {
        try { ensureFriendData().then(function () { _refreshFriendBtns(id); }); } catch (e) { logError('_friendBtnHtml', e); }
    }
    return '<span data-friend-btn="' + window.escHtml(String(id == null ? '' : id)) + '" style="display:inline-flex; gap:8px; align-items:center;">' + _friendBtnInner(p) + '</span>';
}

function _friendBtnInner(p) {
    const f = _friendsById[p.id];
    if (!f) return `<button class="btn btn-sm" style="width:auto; min-width:auto; padding:6px 12px;" onclick="sendFriendRequest('${p.id}')"><i class="fas fa-user-plus"></i> ${window.t('friends.add')}</button>`;
    /* ⚠ Când există meniul „⋮" (block.js încărcat), butonul mic „Șterge prietenia"
         NU se mai randează aici: a fost mutat în meniu, fiindcă patru butoane late
         pe același rând înghesuiau profilul. Fără block.js, rămâne exact ca înainte. */
    if (f.status === 'accepted') {
        const _hasMenu = (typeof window.userMenuBtn === 'function');
        return `<span style="font-size:0.78em; color:var(--success,#27ae60);"><i class="fas fa-user-check"></i> ${window.t('friends.friends')}</span>`
            + (_hasMenu ? '' : ` <button class="btn btn-sm btn-secondary friend-remove-mini" onclick="confirmRemoveFriend('${p.id}')"><i class="fas fa-user-xmark"></i> ${window.t('friends.remove')}</button>`);
    }
    /* 26 oct. 2026: + „Refuză" lângă „Acceptă" (social.js, delegare pe `data-action`). */
    if (f.dir === 'in') return `<button class="btn btn-sm btn-success" style="width:auto; min-width:auto; padding:6px 12px;" onclick="acceptFriendRequest('${p.id}', this)"><i class="fas fa-check"></i> ${t('friend.accept')}</button>`
        + `<button type="button" class="btn btn-sm btn-secondary" style="width:auto; min-width:auto; padding:6px 12px;" data-action="friend-decline" data-id="${window.escHtml(String(p.id))}"><i class="fas fa-xmark" aria-hidden="true"></i> ${t('friend.refuse')}</button>`;
    /* 26 oct. 2026: + „Anulează cererea" (apăsată din greșeală / te-ai răzgândit; social.js). */
    return `<span style="font-size:0.78em; opacity:0.7;"><i class="fas fa-hourglass-half"></i> ${window.t('friends.requestSent')}</span>`
        + `<button type="button" class="btn btn-sm btn-secondary" style="width:auto; min-width:auto; padding:6px 12px;" data-action="friend-cancel" data-id="${window.escHtml(String(p.id))}"><i class="fas fa-rotate-left" aria-hidden="true"></i> ${t('friend.cancelReq')}</button>`;
}

/* Rescrie TOATE butoanele de prietenie prezente în pagină (modal + liste).
   Fără `otherId` le reîmprospătează pe toate. */
function _refreshFriendBtns(otherId) {
    try {
        const nodes = document.querySelectorAll('[data-friend-btn]');
        for (let i = 0; i < nodes.length; i++) {
            const el = nodes[i];
            const id = el.getAttribute('data-friend-btn');
            if (!id) continue;
            if (otherId && id !== String(otherId)) continue;
            el.innerHTML = _friendBtnInner({ id: id });
        }
    } catch (e) { logError('_refreshFriendBtns', e); }
}

window._discPlayerFilters = { country: '', city: '', types: [] };
function toggleDiscPlayerFilters() { _discTogglePanel('discPlayerFilters'); }
function toggleDiscPlayerType(gt, el) {
    const f = window._discPlayerFilters || (window._discPlayerFilters = { country: '', city: '', types: [] });
    const i = f.types.indexOf(gt);
    if (i === -1) f.types.push(gt); else f.types.splice(i, 1);
    if (el) el.classList.toggle('active', i === -1);
    applyDiscPlayerFilters();
}
function applyDiscPlayerFilters() {
    const f = window._discPlayerFilters || { country: '', city: '', types: [] };
    f.country = ((document.getElementById('dfPlayerCountry') || {}).value) || '';
    f.city = (((document.getElementById('dfPlayerCity') || {}).value) || '').trim();
    window._discPlayerFilters = f;
    _discSetFilterBtn('discPlayerFilterBtn', !!(f.country || f.city || (f.types && f.types.length)));
    renderDiscoverPlayers();
}
function resetDiscPlayerFilters() {
    window._discPlayerFilters = { country: '', city: '', types: [] };
    ['dfPlayerCountry', 'dfPlayerCity'].forEach(id => { const e = document.getElementById(id); if (e) e.value = ''; });
    document.querySelectorAll('#discPlayerFilters .disc-chip.active').forEach(c => c.classList.remove('active'));
    _discSetFilterBtn('discPlayerFilterBtn', false);
    renderDiscoverPlayers();
}
window.toggleDiscPlayerFilters = toggleDiscPlayerFilters;
window.toggleDiscPlayerType = toggleDiscPlayerType;
window.applyDiscPlayerFilters = applyDiscPlayerFilters;
window.resetDiscPlayerFilters = resetDiscPlayerFilters;

/* Token de generație: dacă între timp s-a deschis din nou Discover (sau s-a închis),
   lanțul vechi de await-uri NU mai are voie să scrie în modalul curent. */
let _discPlayersGen = 0;

async function openDiscoverPlayers() {
    const menu = document.getElementById('clubMenu'); if (menu) menu.classList.add('hidden');
    if (!sb || !state.currentUser) return;

    /* Idempotent (§1/§20.5): purjăm orice nod rămas dintr-o deschidere anterioară.
       Altfel `getElementById('discoverPlayersList')` prindea nodul-FANTOMĂ vechi și
       randa în el, iar modalul nou rămânea pe „Loading…" la nesfârșit (doar refresh
       îl repara). Discover Clubs păreau OK doar pentru că face 1 singur request,
       nu 5 secvențiale -> fereastra de cursă e mult mai scurtă. */
    const prev = document.getElementById('discoverPlayersModal'); if (prev) prev.remove();
    const gen = ++_discPlayersGen;

    window._discPlayerFilters = { country: '', city: '', types: [] };
    const typeChips = (window.PROFILE_GAME_TYPES || []).map(gt =>
        `<button type="button" class="disc-chip" onclick="toggleDiscPlayerType('${attrJs(gt)}', this)">${escHtml(gt)}</button>`).join('');
    const modal = document.createElement('div');
    modal.className = 'modal active';
    modal.id = 'discoverPlayersModal';
    modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
    modal.innerHTML = `<div class="modal-content"><div class="modal-header">
        <h2><i class="fas fa-user-group"></i> ${t('discover.playersTitle')}</h2>
        <button class="modal-close" onclick="closeModal('discoverPlayersModal')">×</button>
        </div>
        <div class="disc-search-row">
            <div class="disc-search-input-wrap">
                <i class="fas fa-search disc-search-icon"></i>
                <input type="text" id="discoverPlayerSearch" class="form-control" placeholder="${t('discover.playerSearchPh')}" oninput="renderDiscoverPlayers()">
            </div>
            <button type="button" class="tab-head-btn" id="discPlayerFilterBtn" onclick="toggleDiscPlayerFilters()" data-i18n-title="discover.filters" title="${t('discover.filters')}"><i class="fas fa-sliders"></i></button>
        </div>
        <div id="discPlayerFilters" class="sess-panel hidden">
            <div class="disc-filter-grid">
                <label class="disc-filter-field"><span>${t('pf.country')}</span>
                    <select id="dfPlayerCountry" class="form-control" onchange="applyDiscPlayerFilters()">${_discCountryOptions('')}</select></label>
                <label class="disc-filter-field"><span>${t('pf.city')}</span>
                    <div class="geo-city-wrap"><input type="text" id="dfPlayerCity" class="form-control" autocomplete="off" placeholder="${t('pf.cityPh')}" oninput="geoCitySearch('dfPlayerCity','dfPlayerCityResults','dfPlayerCountry','applyDiscPlayerFilters')" onblur="geoCityHide('dfPlayerCityResults')"><div id="dfPlayerCityResults" class="geo-city-results" style="display:none;"></div></div></label>
                <div class="disc-filter-field disc-filter-full"><span>${t('discover.interests')}</span>
                    <div class="disc-chips">${typeChips}</div></div>
            </div>
            <button type="button" class="btn btn-sm btn-secondary" onclick="resetDiscPlayerFilters()" style="margin-top:10px; width:auto; min-width:auto;"><i class="fas fa-rotate-left"></i> ${t('discover.reset')}</button>
        </div>
        <div id="discoverPlayersList"><p style="opacity:0.7;">${t('common.loading')}</p></div></div>`;
    document.body.appendChild(modal);

    /* Căutări SCOPATE la modal (nu getElementById global) + gardă anti-cursă. */
    const listEl = () => modal.querySelector('#discoverPlayersList');
    const alive = () => (gen === _discPlayersGen && modal.isConnected);
    const fail = (msg) => { const l = listEl(); if (l) l.innerHTML = `<p style="color:#e07a5f;">${t('common.errorPrefix')}${clubEsc(msg || '')}</p>`; };

    /* Orice eroare devine mesaj vizibil: „Loading…" NU mai poate rămâne înghețat. */
    try {
        const res = await sb.from('profiles')
            .select(window.PROFILE_COLS)
            .eq('is_discoverable', true).order('name');
        if (!alive()) return;
        if (res.error) { fail(res.error.message); return; }
        _discoverPlayers = (res.data || []).filter(p => p.id !== state.currentUser.id);
        try { if (typeof window.blockStripAvatars === 'function') window.blockStripAvatars(); } catch (e) { logError('loadDiscoverPlayers', e); }
        const ids = _discoverPlayers.map(p => p.id);
        await loadFriendData();
        await loadPublicClubsFor(ids);
        await loadDiscoverPlayerStats(ids);
        await loadDiscoverSpaces(ids);
        if (!alive()) return;
        renderDiscoverPlayers();
    } catch (e) {
        logError('openDiscoverPlayers', e);
        if (alive()) fail(e && e.message);
    }
}

/* Spații fizice pentru jucătorii din Discover — un singur RPC, gated server-side pe
   vizibilitate (public / cluburile mele / prieteni). Întoarce DOAR ce am voie să văd. */
async function loadDiscoverSpaces(ids) {
    _discSpaceById = {};
    try {
        if (!sb || !ids || !ids.length) return;
        const res = await sb.rpc('profile_spaces_for', { p_ids: ids });
        if (res.error) { console.warn('profile_spaces_for:', res.error.message); return; }
        (res.data || []).forEach(r => { if (r && r.user_id) _discSpaceById[r.user_id] = r; });
    } catch (e) { console.warn('loadDiscoverSpaces:', e && e.message); }
}

/* Injectează async blocul de spațiu fizic într-un container, ORIUNDE se afișează un profil
   (profil din club, etc.). Respectă vizibilitatea (RPC gated). Sigur dacă lipsește containerul. */
async function injectProfileSpace(containerId, profileId) {
    try {
        if (!sb || !profileId || !containerId) return;
        if (typeof isDemoMode === 'function' && isDemoMode()) return;
        const res = await sb.rpc('profile_spaces_for', { p_ids: [profileId] });
        if (res.error || !res.data || !res.data.length) return;
        const el = document.getElementById(containerId);
        if (el && typeof window.profileSpacePublicHtml === 'function') {
            el.innerHTML = window.profileSpacePublicHtml(res.data[0]);
        }
    } catch (e) { console.warn('injectProfileSpace:', e && e.message); }
}
window.injectProfileSpace = injectProfileSpace;

/* ============================================================
   ===== CATALOG: „cine deține jocul" + „Îl am și eu" (Batch 5)
   Proprietatea e title-keyed în `profiles.profile_extra.ownedGames` (game_owners = legacy).
   club, INSERT/DELETE doar pentru sine. Nume clickabile + avatar (§11.6).
   Secțiunea se completează async în showGameDetails (placeholder DOM).
   ============================================================ */
async function renderGameOwners(box) {
    /* `box` = elementul secțiunii din modalul CURENT. Fallback: secțiunea din modalul
       activ (evită nodurile vechi ascunse care păstrează același id). */
    if (!box) box = document.querySelector('.modal.active #gameOwnersSection') || document.getElementById('gameOwnersSection');
    if (!box) return;
    const gameDbId = box.dataset.gameid || '';
    const gameName = box.dataset.gamename || '';
    const addedBy = box.dataset.addedby || '';
    const esc = window.escHtml || function (s) { return s == null ? '' : String(s); };
    const aj = window.attrJs || function (s) { return s == null ? '' : String(s); };
    const me = (state.currentUser && state.currentUser.id) || '';
    const demo = (typeof isDemoMode === 'function' && isDemoMode());
    const key = gameDbId || gameName;

    let owners = [];
    if (demo) {
        owners = (window._demoGameOwners && window._demoGameOwners[key]) || [];
    } else {
        /* MODEL NOU (title-keyed): deținătorii = membrii clubului al căror
           lista lor de jocuri deținute conține TITLUL jocului. Datele sunt deja hidratate
           (PROFILE_COLS) -> ZERO request suplimentar.
           Aici se rezolvă automat cerința „adaug jocul în profil înainte ca
           clubul să-l aibă": în clipa în care jocul apare în catalog cu acest
           titlu, deținătorii sunt recunoscuți și „Îl am și eu" apare bifat. */
        const want = _ogKey(gameName);
        if (want) {
            const byId = state.membersById || {};
            Object.keys(byId).forEach(function (pid) {
                const m = byId[pid];
                const list = ownedTitlesOf(pid);
                if (list.some(function (tt) { return _ogKey(tt) === want; })) owners.push(pid);
            });
        }
    }

    const iOwn = owners.indexOf(me) !== -1;
    const addedHtml = addedBy
        ? '<div class="game-owner-added"><i class="fas fa-circle-plus" style="opacity:.6;"></i> ' + esc(t('owners.addedBy').replace('{name}', addedBy)) + '</div>'
        : '';
    const ownersHtml = owners.length
        ? owners.map(function (id) {
            const m = (typeof memberById === 'function') ? memberById(id) : null;
            const nm = (typeof displayNameById === 'function' && displayNameById(id)) || t('dm.someone');
            const av = (typeof avatarHtml === 'function') ? avatarHtml(m && m.avatar, 24) : '';
            /* v9 8.16-4e: fără onclick — fișa jocului deleagă (`data-gd-act`, app-modules-1.js); id-ul e dată. */
            return '<span class="game-owner-chip" data-gd-act="owner" data-uid="' + esc(String(id)) + '"><span class="game-owner-av">' + av + '</span>' + esc(nm) + '</span>';
        }).join('')
        : '<span style="color:var(--text-secondary); font-size:.88em;">' + esc(t('owners.none')) + '</span>';

    const canToggle = demo || !!gameDbId;
    const btn = canToggle
        ? '<div style="margin-top:12px;"><button class="btn btn-sm ' + (iOwn ? 'btn-secondary' : 'btn-primary') + '" data-gd-act="own">'
            + '<i class="fas ' + (iOwn ? 'fa-check' : 'fa-plus') + '"></i> ' + esc(iOwn ? t('owners.iHaveIt') : t('owners.iHaveItToo')) + '</button></div>'
        : '';

    box.innerHTML = '<h3 style="margin-top:24px;"><i class="fas fa-box-open"></i> ' + esc(t('owners.title')) + '</h3>'
        + addedHtml
        + '<div class="game-owners-list">' + ownersHtml + '</div>'
        + btn;
}
window.renderGameOwners = renderGameOwners;

async function toggleGameOwn(srcEl) {
    /* `srcEl` = butonul apăsat -> găsim secțiunea părinte (modalul corect). */
    const box = (srcEl && srcEl.closest) ? srcEl.closest('.game-owners-section')
        : (document.querySelector('.modal.active #gameOwnersSection') || document.getElementById('gameOwnersSection'));
    if (!box) return;
    const gameDbId = box.dataset.gameid || '';
    const gameName = box.dataset.gamename || '';
    const me = state.currentUser && state.currentUser.id;
    if (!me) return;
    const demo = (typeof isDemoMode === 'function' && isDemoMode());
    const key = gameDbId || gameName;

    if (demo) {
        window._demoGameOwners = window._demoGameOwners || {};
        const arr = window._demoGameOwners[key] = window._demoGameOwners[key] || [];
        const i = arr.indexOf(me);
        if (i === -1) arr.push(me); else arr.splice(i, 1);
        renderGameOwners(box); return;
    }
    if (!gameName) return;

    /* MODEL NOU: „Îl am și eu" scrie pe PROFILUL MEU, pe TITLU (nu în game_owners,
       nu legat de club) -> proprietatea rămâne a mea chiar dacă părăsesc clubul.
       Scrierea e un DELTA peste lista citită din DB (vezi _ownedApplyDelta), deci
       nu poate șterge restul jocurilor nici dacă memoria locală e goală.
       NOTĂ: NU cerem `requireWritableClub` — profilul e al meu, nu al clubului. */
    const cur = ownedTitlesOf(me);
    const want = _ogKey(gameName);
    const has = cur.some(tt => _ogKey(tt) === want);

    let img = '';
    if (!has) {
        const g = (state.catalog && state.catalog[gameName]) || null;
        if (g && g.imageUrl) img = g.imageUrl;
    }

    const ok = has ? await removeOwnedGame(gameName) : await addOwnedGame(gameName, img);
    if (!ok) return;

    await renderGameOwners(box);
    /* Cerința: apare LIVE în „Jocuri deținute" din profil, fără refresh. */
    try { await refreshOwnedGamesUI(); } catch (e) { logError('toggleGameOwn', e); }
}
window.toggleGameOwn = toggleGameOwn;

/* Invalidează cache-ul de statistici DOAR la schimbarea identității (login/logout/alt
   user): RPC-ul agregă strict cluburile userului curent, deci rezultatele nu sunt
   transferabile între conturi. */
function _playerStatsResetIfOtherUser() {
    const me = (state.currentUser && state.currentUser.id) || null;
    if (_playerStatsOwner !== me) { _playerStatsById = {}; _playerStatsAsked = {}; _playerStatsOwner = me; }
}

/* ============================================================
   §3 — „Jocuri deținute" = COMPONENTĂ A PROFILULUI PERSONAL (title-keyed).
   Sursa unică de adevăr: `profiles.profile_extra.ownedGames` (array de TITLURI),
   exact ca `preferred_games`. Imaginile: `profile_extra.ownedGameImages`.

   De ce pe titlu, nu pe `game_id`: `game_id` e un rând din catalogul UNUI CLUB,
   deci proprietatea murea la ieșirea din club și nu putea fi recunoscută într-un
   club care încă n-are jocul. Cheiat pe titlu:
     • persistă indiferent de apartenența la cluburi;
     • când ORICINE adaugă jocul în catalogul clubului, deținătorii sunt
       recunoscuți AUTOMAT (potrivire pe titlu) și „Îl am și eu" apare bifat.
   `game_owners` a devenit legacy — nu-l mai citim/scriem.
   ============================================================ */

function _ogKey(t) { return String(t == null ? '' : t).trim().toLowerCase(); }

/* profile_extra al unui profil, din datele deja hidratate. */
function _ogExtraOf(profileId) {
    const me = state.currentUser && state.currentUser.id;
    if (me && profileId === me) {
        const u = (state.currentUser.email && state.users && state.users[state.currentUser.email]) || null;
        if (u && u.profileExtra && typeof u.profileExtra === 'object') return u.profileExtra;
    }
    const m = (typeof memberById === 'function') ? memberById(profileId) : null;
    if (m && m.profileExtra && typeof m.profileExtra === 'object') return m.profileExtra;
    return null;
}

/* Titlurile deținute — din `profile_extra.ownedGames`. ZERO request: profile_extra
   e deja în PROFILE_COLS, deci vine hidratat pentru membri, Discover și profil public. */
function ownedTitlesOf(profileId) {
    if (!profileId) return [];
    const me = state.currentUser && state.currentUser.id;
    /* Pentru MINE, `state.currentUser.ownedGames` e cea mai proaspătă (o actualizează
       _ownedApplyDelta imediat după scrierea confirmată în DB). */
    if (me && profileId === me && Array.isArray(state.currentUser.ownedGames)) {
        return state.currentUser.ownedGames.slice();
    }
    const ex = _ogExtraOf(profileId);
    return (ex && Array.isArray(ex.ownedGames)) ? ex.ownedGames.slice() : [];
}
window.ownedTitlesOf = ownedTitlesOf;

/* Harta de coperți pentru jocurile deținute (tot din profile_extra). */
function ownedImagesOf(profileId) {
    const ex = _ogExtraOf(profileId);
    return (ex && ex.ownedGameImages && typeof ex.ownedGameImages === 'object') ? ex.ownedGameImages : {};
}

/* Cache pentru profilurile care NU sunt membri ai clubului curent (ex. Discover):
   acolo nu avem datele în memorie, deci le cerem punctual. */
let _ownedGamesById = {};
let _ownedGamesOwner = null;

async function loadOwnedGames(profileId, preloaded) {
    if (!profileId) return [];
    const me = (state.currentUser && state.currentUser.id) || null;
    if (_ownedGamesOwner !== me) { _ownedGamesById = {}; _ownedGamesOwner = me; }

    /* 0) Profil deja încărcat (ex. rândul din Discover) -> îl folosim direct. */
    if (preloaded) {
        const pex = preloaded.profile_extra || preloaded.profileExtra || null;
        if (pex && typeof pex === 'object') {
            const imgs = (pex.ownedGameImages && typeof pex.ownedGameImages === 'object') ? pex.ownedGameImages : {};
            return _ogBuild(Array.isArray(pex.ownedGames) ? pex.ownedGames : [], imgs);
        }
    }
    /* 1) Date deja hidratate (eu sau un membru al clubului) — profile_extra vine
          din PROFILE_COLS, deci de regulă nu mai e nevoie de niciun request. */
    const local = ownedTitlesOf(profileId);
    if (local.length) return _ogBuild(local, ownedImagesOf(profileId));

    /* 2) Cache. */
    if (_ownedGamesById[profileId]) return _ownedGamesById[profileId];

    /* 3) Fallback: cerem DOAR profile_extra (coloană veche, sigur expusă de API). */
    if (!sb || isDemoMode()) return [];
    try {
        const r = await sb.from('profiles').select('profile_extra').eq('id', profileId).maybeSingle();
        if (r.error || !r.data) return [];
        const ex = (r.data.profile_extra && typeof r.data.profile_extra === 'object') ? r.data.profile_extra : {};
        const imgs = (ex.ownedGameImages && typeof ex.ownedGameImages === 'object') ? ex.ownedGameImages : {};
        const out = _ogBuild(Array.isArray(ex.ownedGames) ? ex.ownedGames : [], imgs);
        _ownedGamesById[profileId] = out;
        return out;
    } catch (e) {
        console.warn('loadOwnedGames:', e && e.message);
        return [];
    }
}
window.loadOwnedGames = loadOwnedGames;

/* Construiește lista afișabilă: DEDUP pe titlu + imagine (din profil sau catalog). */
function _ogBuild(titles, imgs) {
    const seen = {};
    const out = [];
    (Array.isArray(titles) ? titles : []).forEach(tt => {
        const k = _ogKey(tt);
        if (!k || seen[k]) return;      /* dedup: același joc din mai multe cluburi -> o dată */
        seen[k] = true;
        let img = (imgs && imgs[k]) || '';
        if (!img) {
            const cat = state.catalog || {};
            for (const n in cat) { if (n.toLowerCase() === k && cat[n] && cat[n].imageUrl) { img = cat[n].imageUrl; break; } }
        }
        out.push({ title: tt, imageUrl: img });
    });
    out.sort((a, b) => String(a.title).localeCompare(String(b.title)));
    return out;
}

/* Citirea AUTORITARĂ a jocurilor mele, direct din DB. Cere DOAR `profile_extra`
   (coloană veche, sigur expusă de API) — nicio dependență de coloane noi. */
async function loadMyOwnedFromDb() {
    const me = state.currentUser && state.currentUser.id;
    if (!me || !sb || isDemoMode()) return null;
    try {
        const r = await sb.from('profiles').select('profile_extra').eq('id', me).maybeSingle();
        if (r && r.error) throw r.error;
        if (!r || !r.data) return null;
        const ex = (r.data.profile_extra && typeof r.data.profile_extra === 'object' && !Array.isArray(r.data.profile_extra))
            ? r.data.profile_extra : {};
        return {
            titles: Array.isArray(ex.ownedGames) ? ex.ownedGames : [],
            extra: ex,
            images: (ex.ownedGameImages && typeof ex.ownedGameImages === 'object') ? ex.ownedGameImages : {}
        };
    } catch (e) {
        console.warn('loadMyOwnedFromDb:', e && e.message);
        return null;
    }
}
window.loadMyOwnedFromDb = loadMyOwnedFromDb;

/* ============================================================
   9.6 — O SINGURĂ sursă pentru „ce adresă am voie să arăt".
   RO: aplicația avea DOUĂ ferestre de profil care decideau fiecare pe cont propriu, din
       date diferite, și ajungeau la răspunsuri diferite pentru același om. Una citea
       datele hidratate (venite prin `emails_for`, care respectă `hide_email` și dreptul
       de a vedea profilul) și afișa corect adresa. Cealaltă o cerea din coloana
       `profiles.email`, care NU mai e livrată deloc către client — deci scria „Email
       ascuns" pentru TOATĂ lumea, inclusiv pentru cei care nu ascunseseră nimic.
   ⚠ Regula, de acum într-un singur loc: adresa se arată dacă serverul ne-a dat-o, și se
       ascunde dacă nu. Nicio decizie locală despre ascundere — serverul a luat-o deja.
       Blocarea rămâne o interdicție suplimentară, verificată de apelant.
   EN: two profile modals decided independently from different data and disagreed; the
       visibility decision now lives in one place and simply trusts what the server gave.
   ============================================================ */
function visibleEmailOf(profileId) {
    try {
        if (!profileId) return null;
        const me = state.currentUser && state.currentUser.id;
        if (me && String(profileId) === String(me)) return state.currentUser.email || null;
        const m = (typeof memberById === 'function') ? memberById(profileId) : null;
        if (m) {
            if (m.hideEmail) return null;
            if (m.email) return m.email;
        }
        if (state.users) {
            const k = Object.keys(state.users);
            for (let i = 0; i < k.length; i++) {
                const u = state.users[k[i]];
                if (u && String(u.id) === String(profileId)) return (u.hideEmail ? null : (u.email || null));
            }
        }
    } catch (e) { logError('visibleEmailOf', e); }
    return null;
}
window.visibleEmailOf = visibleEmailOf;

/* Pentru cineva din AFARA cluburilor mele (Discover), datele hidratate n-au adresa.
   Întrebăm serverul pentru un singur id — el decide dacă am voie s-o văd.
   ⚠ Doar la deschiderea unui profil, niciodată la pornire. */
async function fetchVisibleEmail(profileId) {
    try {
        if (!profileId || !sb || (typeof isDemoMode === 'function' && isDemoMode())) return null;
        const local = visibleEmailOf(profileId);
        if (local) return local;
        const r = await sb.rpc('emails_for', { p_ids: [profileId] });
        if (r && !r.error && Array.isArray(r.data) && r.data.length) return r.data[0].email || null;
    } catch (e) { logError('fetchVisibleEmail', e); }
    return null;
}
window.fetchVisibleEmail = fetchVisibleEmail;

/* ⚠ SINGURA cale de scriere a jocurilor deținute. Aplică un DELTA (adaugă/șterge UN
   titlu) peste `profile_extra` CITIT DIN DB — niciodată peste o listă reconstruită
   din memorie. Dacă citirea eșuează, ANULĂM scrierea (nu scriem pe orb).
   Astfel o stare locală goală nu poate șterge nimic. */
async function _ownedApplyDelta(addTitle, removeTitle, img) {
    const me = state.currentUser && state.currentUser.id;
    if (!me) return false;

    if (isDemoMode() || !sb) {
        const cur = ownedTitlesOf(me);
        let next = cur.slice();
        if (removeTitle) next = next.filter(tt => _ogKey(tt) !== _ogKey(removeTitle));
        if (addTitle && !next.some(tt => _ogKey(tt) === _ogKey(addTitle))) next.push(addTitle);
        state.currentUser.ownedGames = next;
        return true;
    }

    const base = await loadMyOwnedFromDb();
    if (!base) {
        showNotification(t('sb.ownedSaveAborted'), 'error');
        return false;
    }

    let next = base.titles.slice();
    const imgs = Object.assign({}, base.images);

    if (removeTitle) {
        next = next.filter(tt => _ogKey(tt) !== _ogKey(removeTitle));
        delete imgs[_ogKey(removeTitle)];
    }
    if (addTitle && !next.some(tt => _ogKey(tt) === _ogKey(addTitle))) {
        next.push(String(addTitle).trim());
        if (img) imgs[_ogKey(addTitle)] = img;
    }

    /* Pornim de la profile_extra REAL din DB și atingem DOAR cheile noastre. */
    const ex = Object.assign({}, base.extra);
    ex.ownedGames = next;
    ex.ownedGameImages = imgs;

    /* ⚠ Aceeași gardă ca la preferința de catalog: fără ea, adăugarea unui joc deținut
       își vedea propriul ecou și refăcea toată pagina. Defect vechi, aceeași cauză. */
    const up = await _withSelfWriteGuard(function () {
        return sb.from('profiles').update({ profile_extra: ex }).eq('id', me);
    });
    if (up.error) { showNotification(t('owners.errColon') + up.error.message, 'error'); return false; }

    /* DB-first: abia acum sincronizăm memoria, în toate locurile unde e ținută. */
    state.currentUser.ownedGames = next.slice();
    const u = (state.currentUser.email && state.users && state.users[state.currentUser.email]) || null;
    if (u) u.profileExtra = ex;
    const m = (typeof memberById === 'function') ? memberById(me) : null;
    if (m) m.profileExtra = ex;
    _ownedGamesById = {};
    return true;
}

/* ⚠ 9.2 — REÎMPROSPĂTAREA BRUSCĂ A PAGINII. Orice scriere pe `profiles` declanșează un
   eveniment realtime, iar acela reface TOT: statistici, clasamente, profilul propriu.
   `updateMyProfile` se apără de ecoul propriilor scrieri prin `_selfWriteInFlight`, dar
   celelalte căi care scriu în profil (jocuri deținute, preferința de catalog) NU o făceau
   — de aceea, după o astfel de acțiune, acordeonul și pagina „săreau".
   Helperul de mai jos face exact ce face `updateMyProfile`: ridică steagul, scrie, apoi
   îl coboară în `finally` și rulează o SINGURĂ reîmprospătare dacă între timp a venit o
   schimbare de la ALTCINEVA. Zero evenimente pierdute, zero zgomot.
   ⚠ `finally` e obligatoriu: un steag rămas sus ar face aplicația surdă la realtime până
   la refresh — mult mai rău decât problema reparată.
   EN: writes to `profiles` outside updateMyProfile echoed back through realtime and
   re-rendered the whole page; this wrapper suppresses the echo the same way. */
let _selfWriteToken = 0;
const _SELF_WRITE_GRACE_MS = 1500;

async function _withSelfWriteGuard(fn, graceMs) {
    /* ⚠ Steagul NU se coboară imediat ce scrierea s-a terminat. Ecoul realtime al
       propriei scrieri sosește ceva mai TÂRZIU decât răspunsul la `update` — o gardă
       care se ridică și cade instantaneu nu prinde nimic. De aceea îl ținem sus încă
       ~1,5 s, timp în care ecoul e absorbit.
       ⚠ Un eveniment venit de la ALTCINEVA în acea fereastră NU se pierde: e reținut în
       `_missedRefresh` și rulat o singură dată la coborâre.
       ⚠ Jetonul apără de suprapuneri: dacă între timp pornește altă scriere, eliberarea
       programată de prima nu mai are voie să coboare steagul sub ea. */
    const _mine = ++_selfWriteToken;
    window._selfWriteInFlight = true;
    try {
        return await fn();
    } finally {
        const _release = function () {
            if (_selfWriteToken !== _mine) return;   /* altcineva scrie acum: nu atingem */
            window._selfWriteInFlight = false;
            if (window._missedRefresh) {
                window._missedRefresh = false;
                try { scheduleRealtimeRefresh(); } catch (e) { logError('_withSelfWriteGuard', e); }
            }
        };
        const _g = (graceMs == null) ? _SELF_WRITE_GRACE_MS : graceMs;
        if (_g > 0) setTimeout(_release, _g); else _release();
    }
}
window._withSelfWriteGuard = _withSelfWriteGuard;

/* 9.2 — modul de vizualizare al catalogului, pe CONT.
   ⚠ Același tipar ca `_ownedApplyDelta`: citim `profile_extra` REAL din baza de date și
   atingem DOAR cheia noastră. O scriere „pe orb", dintr-o copie din memorie, ar putea
   șterge jocurile deținute ale utilizatorului. Dacă citirea eșuează, NU scriem.
   EN: read-modify-write on the real profile_extra; never blind-write the whole object. */
async function persistCatalogView(view) {
    try {
        const me = state.currentUser && state.currentUser.id;
        if (!me) return false;
        if (typeof isDemoMode === 'function' && isDemoMode()) return true;   /* demo: zero scrieri */
        if (!sb) return false;
        const base = await loadMyOwnedFromDb();
        if (!base) return false;          /* citire eșuată -> nu scriem nimic */
        const ex = Object.assign({}, base.extra);
        ex.catalogView = view;
        const up = await _withSelfWriteGuard(function () {
            return sb.from('profiles').update({ profile_extra: ex }).eq('id', me);
        });
        if (up.error) { logError('persistCatalogView', up.error); return false; }
        state.currentUser.profileExtra = ex;
        const m = (typeof memberById === 'function') ? memberById(me) : null;
        if (m) m.profileExtra = ex;
        return true;
    } catch (e) { logError('persistCatalogView', e); return false; }
}
window.persistCatalogView = persistCatalogView;

async function addOwnedGame(title, img) { return _ownedApplyDelta(title, null, img); }
async function removeOwnedGame(title) { return _ownedApplyDelta(null, title, null); }
window.addOwnedGame = addOwnedGame;
window.removeOwnedGame = removeOwnedGame;

/* Reîmprospătează LIVE secțiunile „Jocuri deținute" deschise (cerința: bifez
   „Îl am și eu" în catalog -> apare imediat în profil, fără refresh). */
async function refreshOwnedGamesUI() {
    const me = state.currentUser && state.currentUser.id;
    if (!me) return;
    /* Editorul din profilul propriu (dacă acordeonul „Public profile" e deschis). */
    try {
        if (document.getElementById('pfOwnChips')) await ownEditorInit();
    } catch (e) { logError('refreshOwnedGamesUI', e); }
    /* Secțiunile read-only din profilurile publice deschise. */
    document.querySelectorAll('#playerOwnedGames, #ppOwnedGames').forEach(function (el) {
        const pid = el.dataset.profileId;
        if (!pid) return;
        try { injectOwnedGames(el, pid); } catch (e) { logError('refreshOwnedGamesUI', e); }
    });
}
window.refreshOwnedGamesUI = refreshOwnedGamesUI;

/* HTML-ul secțiunii (refolosește cardurile `favCardsHtml`, §13c/§11).
   DEDUP (cerință): dacă deții același joc în MAI MULTE cluburi, apare O SINGURĂ
   dată — deduplicarea pe titlu se face în `loadOwnedGames`. */
function ownedGamesHtml(list, profileId) {
    const arr = Array.isArray(list) ? list : [];
    if (!arr.length) return '';
    const titles = arr.map(x => x.title);
    const imgs = {};
    arr.forEach(x => { if (x.imageUrl) imgs[String(x.title).toLowerCase()] = x.imageUrl; });
    const heading = t('pf.ownedGames');
    return `<div style="margin-bottom:10px;">
        <strong><i class="fas fa-dice"></i> ${heading} (${arr.length}):</strong>
        <div style="margin-top:6px;">${favCardsHtml(titles, imgs, { key: 'owned-' + (profileId || 'me'), heading: heading })}</div>
    </div>`;
}
window.ownedGamesHtml = ownedGamesHtml;

/* Injectează secțiunea într-un container-placeholder, după ce datele sosesc
   (fetch/paint separate, §11 — fără flash). Scopat la element, nu getElementById. */
async function injectOwnedGames(boxEl, profileId, preloaded) {
    if (!boxEl || !profileId) return;
    boxEl.dataset.profileId = profileId;   /* pentru refreshOwnedGamesUI (live) */
    const list = await loadOwnedGames(profileId, preloaded);
    if (!boxEl.isConnected) return;   /* modalul s-a închis între timp */
    boxEl.innerHTML = ownedGamesHtml(list, profileId);
}
window.injectOwnedGames = injectOwnedGames;

/* Apelează RPC-ul player_public_stats și ACUMULEAZĂ în _playerStatsById (un singur
   request). Implicit cere DOAR id-urile lipsă (zero request dacă avem deja tot);
   cu { force: true } reinterogează tot (ex. după import, când XP-ul s-a schimbat). */
async function loadDiscoverPlayerStats(ids, opts) {
    _playerStatsResetIfOtherUser();
    try {
        if (!sb || !ids || !ids.length) return;
        const force = !!(opts && opts.force);
        const want = force
            ? ids.filter(Boolean)
            : ids.filter(id => id && !_playerStatsById[id] && !_playerStatsAsked[id]);
        if (!want.length) return;
        const res = await sb.rpc('player_public_stats', { p_profile_ids: want });
        if (res.error) { console.warn('player_public_stats:', res.error.message); return; }
        /* Marcăm ca „interogate" chiar și id-urile fără rând (jucători fără meciuri):
           altfel am relansa RPC-ul la fiecare deschidere de profil. */
        want.forEach(id => { _playerStatsAsked[id] = true; });
        (res.data || []).forEach(r => { if (r && r.profile_id) _playerStatsById[r.profile_id] = r; });
    } catch (e) {
        console.warn('loadDiscoverPlayerStats:', e && e.message);
    }
}

/* Garantează că statisticile GLOBALE ale id-urilor cerute sunt în cache ÎNAINTE de a
   randa un profil (§7). Fetch doar pentru ce lipsește -> de regulă instant, fără request.
   Se așteaptă înainte de paint (anti-flash: mai bine o clipă de întârziere decât să
   afișăm „intermediate" și apoi să sară pe „advanced"). */
async function ensurePlayerStats(ids) {
    const list = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);
    if (!list.length) return;
    await loadDiscoverPlayerStats(list);
}
window.ensurePlayerStats = ensurePlayerStats;

function renderDiscoverPlayers() {
    /* Scopat la modal: chiar dacă ar exista un nod vechi în DOM, nu mai putem rada în el. */
    const dpModal = document.getElementById('discoverPlayersModal');
    const list = dpModal ? dpModal.querySelector('#discoverPlayersList') : document.getElementById('discoverPlayersList');
    if (!list) return;
    const searchEl = dpModal ? dpModal.querySelector('#discoverPlayerSearch') : document.getElementById('discoverPlayerSearch');
    const q = (searchEl ? searchEl.value : '').trim().toLowerCase();
    let arr = _discoverPlayers;
    /* ⚠ Utilizatorii blocati DISPAR complet din Descopera, in ambele sensuri.
         Descopera e un instrument de a gasi oameni NOI; cineva cu care relatia e
         blocata nu e o sugestie utila. Tiparul obisnuit (Instagram) e la fel.
       ⚠ Filtrarea se face LA RANDARE, nu la incarcare: la deblocare persoana
         reapare imediat, fara sa fie nevoie de o noua cerere catre server.
       ⚠ NU atinge lista de membri ai clubului, istoricul sau selectoarele de
         jucatori — acolo numele ramane, altfel s-ar rupe integritatea datelor. */
    if (typeof window.isBlockedPair === 'function') {
        arr = arr.filter(p => !window.isBlockedPair(p.id));
    }
    if (q) {
        arr = arr.filter(p => {
            const games = (Array.isArray(p.preferred_games) ? p.preferred_games : []).join(' ').toLowerCase();
            return (p.name || '').toLowerCase().includes(q) || (p.user_tag || '').toLowerCase().includes(q) || games.includes(q);
        });
    }
    /* Filtre avansate: locație (țară/oraș) + interese (tipuri de joc) din profile_extra. */
    const pf = window._discPlayerFilters || {};
    if (pf.country) arr = arr.filter(p => (p.profile_extra && p.profile_extra.country) === pf.country);
    if (pf.city) { const cc = pf.city.toLowerCase(); arr = arr.filter(p => ((p.profile_extra && p.profile_extra.city) || '').toLowerCase().includes(cc)); }
    if (pf.types && pf.types.length) {
        arr = arr.filter(p => {
            const gt = (p.profile_extra && Array.isArray(p.profile_extra.gameTypes)) ? p.profile_extra.gameTypes : [];
            return pf.types.some(tp => gt.indexOf(tp) !== -1);
        });
    }
    if (!arr.length) { list.innerHTML = `<p style="opacity:0.7;">${t('discover.noPlayers')}${q ? ' ' + t('discover.for') + ' „' + clubEsc(q) + '"' : ''}.</p>`; return; }

    list.innerHTML = arr.map(p => {
        const initial = (p.name || '?').trim().charAt(0).toUpperCase();
        const av = isPhotoAvatar(p.avatar)
            ? `<img src="${clubEsc(p.avatar)}" style="width:54px; height:54px; border-radius:50%; object-fit:cover; flex:none;">`
            : (p.avatar
                ? `<div style="width:54px; height:54px; border-radius:50%; background:rgba(255,255,255,0.06); display:flex; align-items:center; justify-content:center; font-size:30px; flex:none;">${clubEsc(p.avatar)}</div>`
                : `<div style="width:54px; height:54px; border-radius:50%; background:var(--accent-primary,#ff6b35); color:#fff; display:flex; align-items:center; justify-content:center; font-weight:700; font-size:1.35em; flex:none;">${clubEsc(initial)}</div>`);
        const st = _playerStatsById[p.id];
        const spaceBadge = (typeof window.profileSpaceBadgeHtml === 'function') ? window.profileSpaceBadgeHtml(_discSpaceById[p.id]) : '';
        const xpBadge = (st && Number(st.matches) > 0)
            ? `<div style="display:flex; flex-wrap:wrap; gap:8px; align-items:center; margin-top:5px; font-size:0.74em;">
                    <span class="badge badge-primary" style="font-size:1em;"><i class="fas fa-medal"></i> ${t(st.level_key)}</span>
                    <span style="opacity:0.85;"><i class="fas fa-bolt" style="opacity:0.7;"></i> ${st.xp} XP</span>
                    <span style="opacity:0.85;"><i class="fas fa-dice" style="opacity:0.7;"></i> ${st.matches}</span>
                    <span style="opacity:0.85;"><i class="fas fa-trophy" style="opacity:0.7;"></i> ${st.win_rate}%</span>
                </div>`
            : '';
        return `<div class="disc-player-card" onclick="openPublicProfileById('${p.id}', '${window.attrJs(p.name)}')">
            ${av}
            <div style="min-width:0; flex:1;">
                <div style="font-weight:700; font-size:1.02em;" class="disc-club-name">${clubEsc(p.name || 'Necunoscut')}${p.user_tag ? ` <span style="font-size:0.72em; color:var(--accent-primary); font-weight:600;">@${clubEsc(p.user_tag)}</span>` : ''}${spaceBadge ? ` ${spaceBadge}` : ''}</div>
                ${xpBadge}
            </div>
            <i class="fas fa-chevron-right" style="opacity:0.4; flex:none;"></i>
        </div>`;
    }).join('');
}

/* ============================================================
   §2 — SESIUNI LIVE (batch B: strat de date + realtime)

   MODEL (tabelul `live_sessions`):
     • `sheet`  = foaia (joc, jucători pe id, scoruri, roluri, tournamentId).
                  UN singur scriitor logic: host + Moderator+.
     • `timers` = { profile_id: {...} } — N scriitori. FIECARE jucător își scrie
                  DOAR sub-cheia lui, prin `ls_set_my_timer` (jsonb_set server-side).
                  Nu poate atinge timerul altuia și nu poate atinge foaia — nici
                  măcar modificând cererea din consolă.

   DE CE tabel nou și nu `live_timers`: acela are UN rând per club
   (`onConflict: 'club_id'`) => o singură sesiune, un singur scriitor.
   `live_timers` rămâne NEATINS (plasă de siguranță); îl absorbim în batch-ul C.
   ============================================================ */

/* Deschide o sesiune live. Rang >= 2 — impus ȘI server-side, în RPC. */
async function lsOpen(sheet) {
    if (isDemoMode() || !sb || !currentClubId) return null;
    if (typeof can === 'function' && !can('session.create')) {
        showNotification(t('rbac.noPermission'), 'error'); return null;
    }
    const r = await sb.rpc('ls_open', { p_club_id: currentClubId, p_sheet: sheet || {} });
    if (r.error) { showNotification(t('live.errOpen') + r.error.message, 'error'); return null; }
    await loadLiveSessions();
    return r.data || null;
}
window.lsOpen = lsOpen;

/* Actualizează FOAIA (host + Moderator+). NU atinge timerele jucătorilor. */
async function lsSetSheet(sessionId, sheet) {
    if (isDemoMode() || !sb || !sessionId) return null;
    const r = await sb.rpc('ls_set_sheet', { p_id: sessionId, p_sheet: sheet || {} });
    if (r.error) { showNotification(t('live.errSave') + r.error.message, 'error'); return null; }
    return r.data || null;
}
window.lsSetSheet = lsSetSheet;

/* Scrie DOAR timer-ul MEU. Orice membru. Atomic, pe sub-cheia proprie. */
async function lsSetMyTimer(sessionId, timer) {
    if (isDemoMode() || !sb || !sessionId) return null;
    const r = await sb.rpc('ls_set_my_timer', { p_id: sessionId, p_timer: timer || {} });
    if (r.error) { showNotification(t('live.errTimer') + r.error.message, 'error'); return null; }
    return r.data || null;   /* harta AUTORITARĂ a tuturor timerelor, din server */
}
window.lsSetMyTimer = lsSetMyTimer;

/* Semn de viață al host-ului (altfel sesiunea ar rămâne „live" la nesfârșit). */
async function lsHeartbeat(sessionId) {
    if (isDemoMode() || !sb || !sessionId) return;
    try { await sb.rpc('ls_heartbeat', { p_id: sessionId }); } catch (e) { logError('lsHeartbeat', e); }
}
window.lsHeartbeat = lsHeartbeat;

/* Închide sesiunea. Idempotent server-side: a doua apăsare NU creează alt meci. */
async function lsClose(sessionId, status, matchId) {
    if (isDemoMode() || !sb || !sessionId) return null;
    const r = await sb.rpc('ls_close', {
        p_id: sessionId,
        p_status: status || 'abandoned',
        p_match_id: (matchId != null ? matchId : null)
    });
    if (r.error) { showNotification(t('live.errClose') + r.error.message, 'error'); return null; }
    await loadLiveSessions();
    return r.data || null;
}
window.lsClose = lsClose;

/* Sesiunile live DESCHISE ale clubului curent. */
/* Sincronizează ceasul acestui client cu al serverului.
   OBLIGATORIU și pentru SPECTATORI: fără el, un client cu ceasul în urma serverului
   calcula un timp scurs NEGATIV -> îl rotunjea la 0 -> ceasurile apăreau ÎNGHEȚATE
   pentru toți cei care doar priveau. */
async function lsSyncClock() {
    if (!sb || isDemoMode()) return;
    try {
        const r = await sb.rpc('ls_now');
        if (!r.error && typeof r.data !== 'undefined' && r.data !== null) {
            if (typeof _lsNoteServerNow === 'function') _lsNoteServerNow(Number(r.data));
        }
    } catch (e) { logError('lsSyncClock', e); }
}
window.lsSyncClock = lsSyncClock;

async function loadLiveSessions() {
    if (!sb || !currentClubId || isDemoMode()) { state.liveSessions = []; return []; }
    try {
        const r = await sb.from('live_sessions')
            .select('id, club_id, host_id, status, sheet, timers, match_id, created_at, updated_at')
            .eq('club_id', currentClubId)
            .eq('status', 'open')
            .order('created_at', { ascending: false });
        if (r.error) { console.warn('loadLiveSessions:', r.error.message); return state.liveSessions || []; }
        state.liveSessions = r.data || [];
        /* Dacă găzduiesc măcar o sesiune deschisă, pulsul trebuie să meargă — inclusiv
           după o re-logare, când `_liveSessionId` s-a pierdut. Fără asta, sesiunile
           supraviețuiau delogării, dar mureau tăcut o oră mai târziu. */
        if (state.liveSessions.some(lsIAmHost)) startLiveHeartbeat(); else stopLiveHeartbeat();
        return state.liveSessions;
    } catch (e) {
        console.warn('loadLiveSessions:', e && e.message);
        return state.liveSessions || [];
    }
}
window.loadLiveSessions = loadLiveSessions;

/* ---- REALTIME ----------------------------------------------------------
   UN canal per club, filtrat pe club_id (nu canal per user).
   Debounce: o rafală de update-uri (host-ul tastează un scor) produce O singură
   reîncărcare, nu zece. Același pattern ca `scheduleRealtimeRefresh`. */
let liveChannel = null;
let _liveRefreshTimer = null;

function scheduleLiveRefresh() {
    clearTimeout(_liveRefreshTimer);
    _liveRefreshTimer = setTimeout(async function () {
        await loadLiveSessions();

        /* Sesiunea de care e legat formularul a DISPĂRUT (host-ul a salvat-o sau a
           închis-o). Fără asta, rămâneai pe pagina de sesiune cu un formular mort:
           cronometrele cădeau pe zero, butoanele nu făceau nimic, și nimeni nu-ți
           spunea de ce. Dezlegăm curat și te anunțăm. */
        if (window._liveSessionId) {
            const _still = (state.liveSessions || []).some(function (x) {
                return String(x.id) === String(window._liveSessionId);
            });
            if (!_still) {
                const _wasRead = !!window._liveReadOnly;
                /* ⚠ 9.1: sesiunea a dispărut din listă — dar DE CE? `loadLiveSessions` cere
                   doar rândurile `open`, deci „salvată de altcineva" și „abandonată" arată
                   identic. Diferența contează: dacă a fost SALVATĂ, formularul din fața mea
                   conține o partidă care există deja în Istoric, iar o apăsare pe „Salvează"
                   ar dubla-o (și ar dubla și invitații). Întrebăm rândul o singură dată, pe
                   calea asta rară, și reținem verdictul pentru garda din `saveSession`.
                   EN: the row vanished from the open list; ask why, so a later Save can be
                   blocked when it was already saved elsewhere. */
                const _goneId = window._liveSessionId;
                window._liveSessionId = null;
                window._liveReadOnly = false;
                if (typeof _liveLastForget === 'function') _liveLastForget();   /* 9.2: masa nu mai e deschisă */
                let _wasSavedElsewhere = false;
                try {
                    const _row = await sb.from('live_sessions')
                        .select('status, match_id').eq('id', _goneId).maybeSingle();
                    if (!_row.error && _row.data && _row.data.status === 'saved') {
                        _wasSavedElsewhere = true;
                        window._liveSavedElsewhereId = _goneId;
                        window._liveSavedElsewhereMatch = _row.data.match_id || null;
                    }
                } catch (e) { logError('scheduleLiveRefresh.why', e); }
                try { if (typeof applyLiveReadOnly === 'function') applyLiveReadOnly(); } catch (e) { logError('scheduleLiveRefresh', e); }
                try { if (typeof resetLiveTimer === 'function') resetLiveTimer(); } catch (e) { logError('scheduleLiveRefresh', e); }
                showNotification(_wasSavedElsewhere ? t('live.savedElsewhere') : t('live.ended'), 'info');
                /* Cine era în doar-vizualizare n-are ce căuta pe tab-ul Sesiune (nu are
                   dreptul să creeze una) -> îl ducem înapoi pe dashboard. */
                if (_wasRead && typeof currentTab !== 'undefined' && currentTab === 'session'
                    && typeof switchTab === 'function') switchTab('dashboard');
            }
        }
        /* §2 batch C: dacă lucrăm într-o sesiune live, proiectăm timerele venite de
           la ceilalți jucători în `liveTimer` -> butoanele „Turn" din FORMULAR se
           actualizează live, identic cu cele din foaia live. Aceeași sursă de adevăr. */
        try {
            const ls = (typeof activeLiveSession === 'function') ? activeLiveSession() : null;
            if (ls && typeof applyLiveTimersToLocal === 'function') {
                applyLiveTimersToLocal(ls);
                /* Sincronizăm PAGINA de sesiune cu foaia venită de la ceilalți: joc, jucători,
                   scoruri, roluri, ceasuri. Re-randăm doar la schimbări STRUCTURALE (joc /
                   jucători); restul se actualizează in-place, fără să atingem câmpul de sub
                   cursorul utilizatorului. */
                if (typeof syncSessionTabFromLive === 'function') syncSessionTabFromLive(ls);
            }
        } catch (e) { logError('scheduleLiveRefresh', e); }
        try { if (typeof renderLiveSessionsBar === 'function') renderLiveSessionsBar(); } catch (e) { logError('scheduleLiveRefresh', e); }
        try { if (typeof renderLiveSheetIfOpen === 'function') renderLiveSheetIfOpen(); } catch (e) { logError('scheduleLiveRefresh', e); }
    }, 250);
}

function subscribeRealtimeLive() {
    if (!sb || !currentClubId) return;
    lsSyncClock();   /* aflăm ora serverului ÎNAINTE de a afișa vreun ceas */
    try { if (liveChannel) sb.removeChannel(liveChannel); } catch (e) { logError('subscribeRealtimeLive', e); }
    liveChannel = sb.channel('live-sessions-' + currentClubId)
        .on('postgres_changes', {
            event: '*', schema: 'public', table: 'live_sessions',
            filter: 'club_id=eq.' + currentClubId
        }, scheduleLiveRefresh)
        .subscribe();
}
window.subscribeRealtimeLive = subscribeRealtimeLive;

/* Bătaie de inimă pentru TOATE sesiunile pe care le găzduiesc (5 min; TTL server = 60 min).
   ⚠ Varianta inițială ținea UN singur interval, pentru O singură sesiune. Două probleme:
     • găzduiești mai multe sesiuni -> doar ultima primea puls, restul erau marcate
       `abandoned` de cron după 60 min, deși erau vii;
     • după re-login, pulsul nu repornea deloc (se pierdea `_liveSessionId`) -> sesiunile
       tale ar fi murit tăcut într-o oră.
   Acum e un puls global care le acoperă pe toate, recitite din starea curentă. */
let _lsHeartbeatTimer = null;

function _lsHeartbeatTick() {
    (state.liveSessions || []).forEach(function (s) {
        if (lsIAmHost(s)) lsHeartbeat(s.id);
    });
}

function startLiveHeartbeat() {
    if (_lsHeartbeatTimer) return;   /* idempotent: un singur puls global */
    _lsHeartbeatTick();              /* ⚠ 9.4: și ACUM, nu abia peste 5 minute (vezi mai jos) */
    _lsHeartbeatTimer = setInterval(_lsHeartbeatTick, 5 * 60 * 1000);
}

/* ⚠ 9.4 — DE CE MUREAU SESIUNILE LUNGI.
   RO: pulsul pornea abia după 5 minute și rula doar cât timp pagina e VIE. Pe telefon,
       browserul oprește temporizatoarele când blochezi ecranul sau treci în altă
       aplicație — exact ce face un host care pune telefonul pe masă și joacă. După 60 de
       minute fără puls, curățarea de pe server marca masa drept abandonată.
       Reparăm partea de client, care nu depinde de nicio modificare pe server:
         (a) puls imediat la pornire, nu după 5 minute;
         (b) puls imediat la REVENIREA în aplicație (deblocarea ecranului, revenirea din
             altă aplicație) — momentul în care recuperăm intervalul pierdut.
   EN: the heartbeat started only after 5 minutes and stopped while the phone was locked.
       Now it fires immediately on start and whenever the app becomes visible again.
   ⚠ Asta REDUCE riscul, nu-l elimină: dacă telefonul stă blocat mai bine de o oră și
     nimeni nu atinge aplicația, masa tot expiră. Pragul de pe server e partea care
     lipsește (vezi pasul cu SQL). */
document.addEventListener('visibilitychange', function () {
    if (document.hidden) return;
    try { if ((state.liveSessions || []).some(lsIAmHost)) _lsHeartbeatTick(); }
    catch (e) { logError('liveHeartbeat.visibility', e); }
});
function stopLiveHeartbeat() {
    if (_lsHeartbeatTimer) { clearInterval(_lsHeartbeatTimer); _lsHeartbeatTimer = null; }
}
window.startLiveHeartbeat = startLiveHeartbeat;
window.stopLiveHeartbeat = stopLiveHeartbeat;

function lsIAmHost(s) {
    return !!(s && state.currentUser && s.host_id === state.currentUser.id);
}
window.lsIAmHost = lsIAmHost;

function subscribeRealtimePresence() {
    if (presenceChannel) { try { sb.removeChannel(presenceChannel); } catch (e) { logError('subscribeRealtimePresence', e); } presenceChannel = null; }
    if (!state.currentUser) return;
    /* v9 8.14: canalul meu personal (presence.js) + setarea „Cine vede când sunt online".
       Cu „Nimeni" nu intrăm deloc pe canalul clubului: nu apari și nu-i vezi pe alții
       (reciprocitatea, impusă și pe server — V9_814_PREZENTA_SETARE.sql).
       EN: own presence channel + the visibility setting (reciprocal). */
    if (window.TO_PRES) {
        try { window.TO_PRES.ownStart(); } catch (e) { logError('subscribeRealtimePresence', e); }
        if (!window.TO_PRES.shares()) { renderOnlinePresence([]); return; }
    }
    const me = state.currentUser;
    /* v9 P2a: cheia era EMAILUL, iar canalul nu e privat — verificarea din 14 oct. a arătat
       că un cont din afara clubului citea emailurile celor online. Id-ul de profil nu e secret,
       iar afișarea folosește meta.id / meta.name, nu cheia. Canalul privat vine la 8.14.
       EN: the key was the email on a non-private channel (a non-member could read it, verified
       14 Oct); the profile id is not secret and the UI reads meta.id / meta.name, not the key. */
    const key = me.id || me.name;
    /* v9 P2b: canal PRIVAT, cu nume nou. Serverul (V9_P2B_PREZENTA_PRIVATA.sql, reguli pe
       realtime.messages) lasă doar membrii activi ai clubului să vadă cine e online și să apară
       online. Vechiul canal public ('gbgsc-presence-') era citit și din afara clubului
       (verificat 14 oct.). Nume nou ca să nu depindem de felul nedocumentat în care un canal
       public și unul privat cu același nume s-ar putea amesteca. Fără întoarcere la canalul
       public: ar redeschide scurgerea; dacă serverul refuză, doar nu apar bulinele.
       EN: private presence channel, new name; members only, enforced by RLS on realtime.messages. */
    presenceChannel = sb.channel('club-presence:' + currentClubId, { config: { private: true, presence: { key } } });
    presenceChannel.on('presence', { event: 'sync' }, () => {
        const ps = presenceChannel.presenceState();
        const seen = {};
        Object.keys(ps).forEach(k => {
            (ps[k] || []).forEach(meta => {
                const name = meta.name || k;
                if (name && !seen[name]) seen[name] = { id: meta.id || null, name, isAdmin: !!meta.isAdmin };
            });
        });
        renderOnlinePresence(Object.values(seen));
    });
    const pch = presenceChannel;
    presenceChannel.subscribe(async (status) => {
        /* 29 oct. 2026 (presence.js, „Fără internet"): conexiunea căzută / canalul închis ⇒ lista
           veche nu mai e adevărată ⇒ nimeni online până la sincronizarea următoare (la revenire).
           Doar pentru canalul CURENT: unul vechi, închis la schimbarea clubului, nu golește lista nouă.
           EN: a dropped / closed current channel clears the stale online list. */
        if ((status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') && presenceChannel === pch) renderOnlinePresence([]);
        if (status === 'SUBSCRIBED') {
            try { await presenceChannel.track({ id: me.id, name: me.name, isAdmin: !!me.isAdmin, at: Date.now() }); } catch (e) { logError('subscribeRealtimePresence', e); }
        } else if ((status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') && !subscribeRealtimePresence._logged) {
            /* v9 P2b: o singură urmă în consolă dacă serverul refuză canalul privat (diagnostic). */
            subscribeRealtimePresence._logged = true;
            logError('subscribeRealtimePresence', new Error('presence ' + status));
        }
    });
}

function renderOnlinePresence(users) {
    // Cache ÎNTÂI (T7): punctele verzi + contorul depind de _lastOnlinePresence,
    // iar popup-ul din header a fost eliminat — deci #onlinePresence poate lipsi.
    /* v9 8.14-b: lista BRUTĂ de pe canalul clubului se păstrează (_presenceRaw), iar cei
       cu care există o blocare se scot la FIECARE desenare, nu doar la sosirea datelor.
       Defect văzut de Alex: blocai pe cineva și îl vedeai în continuare online în
       Players (lista fusese filtrată înainte de blocare). block.js cheamă funcția fără
       argumente după orice schimbare de blocare ⇒ se refiltrează din lista brută
       (și la deblocare persoana revine pe loc). EN: block filter applied on every render. */
    if (typeof users === 'undefined') users = window._presenceRaw || window._lastOnlinePresence || [];
    else window._presenceRaw = users;
    users = (users || []).filter(u => !(u && u.id && typeof window.isBlockedPair === 'function' && window.isBlockedPair(u.id)));
    window._lastOnlinePresence = users;
    try { refreshPresenceDots(); } catch (e) { logError('renderOnlinePresence', e); }
    const el = document.getElementById('onlinePresence');
    if (!el) return;
    const esc2 = s => window.escHtml(s);
    users = (users || []).filter(u => u && u.name);
    const myName = state.currentUser && state.currentUser.name;
    users.sort((a, b) => (a.name === myName ? -1 : b.name === myName ? 1 : a.name.localeCompare(b.name)));
    if (!users.length) { el.classList.add('hidden'); el.innerHTML = ''; return; }
    el.classList.remove('hidden');
    el.innerHTML =
        `<span><span class="op-dot"></span>${users.length} ${users.length === 1 ? t('dash.memberOne') : t('dash.memberMany')} ${t('dash.online')}</span>` +
        users.map(u => {
            const m = ((u.id && typeof memberById === 'function' && memberById(u.id)) || (state.members && state.members[u.name])) || {};
            const dbId = u.id || m.dbId || null;
            const isMe = (u.id && state.currentUser && u.id === state.currentUser.id) || u.name === myName;
            const nameHtml = dbId
                ? `<span class="op-name op-name-link" onclick="openLeaderboardProfile('${window.attrJs(dbId)}')">${esc2(u.name)}</span>`
                : `<span class="op-name">${esc2(u.name)}</span>`;
            let sendHtml = '';
            if (!isMe && dbId && typeof window.dmCanMessage === 'function') {
                const chk = window.dmCanMessage(dbId, { profileExtra: m.profileExtra });
                if (chk && chk.ok) {
                    sendHtml = `<button class="op-dm" title="${t('dm.send')}" onclick="event.stopPropagation(); openDM('${dbId}','${window.attrJs(u.name)}')"><i class="fas fa-paper-plane"></i></button>`;
                }
            }
            return `<span class="op-user">${nameHtml}${u.isAdmin ? ' 👑' : ''}${isMe ? ' ' + t('dash.you') : ''}${sendHtml}</span>`;
        }).join('');
}


/* ===== T7: prezența ca indicator pasiv, în context =====
   Punct verde pe avatarele membrilor + contor discret „N online" în tab-ul Players.
   Sursa de date: presenceChannel existent -> window._lastOnlinePresence. */
function isUserOnline(ref) {
    if (!ref) return false;
    const list = window._lastOnlinePresence || [];
    for (let i = 0; i < list.length; i++) {
        const u = list[i]; if (!u) continue;
        if ((u.id && u.id === ref) || u.name === ref) return true;
    }
    return false;
}
window.isUserOnline = isUserOnline;

function refreshPresenceDots() {
    /* Punctele verzi (randate cu data-presence-*) — actualizate in-place, fără re-randare. */
    document.querySelectorAll('.presence-dot[data-presence-name]').forEach(dot => {
        const on = isUserOnline(dot.getAttribute('data-presence-id') || '') || isUserOnline(dot.getAttribute('data-presence-name') || '');
        dot.classList.toggle('on', on);
    });
    /* Butonul „N online" din pagina Players (+ panoul lui, dacă e deschis). */
    const btn = document.getElementById('playersOnlineBtn');
    if (btn) {
        const n = (window._lastOnlinePresence || []).filter(u => u && u.name).length;
        if (n > 0) {
            btn.innerHTML = `<span class="op-dot"></span>${n} ${t('dash.online')} <i class="fas fa-chevron-down" style="font-size:.75em; opacity:.6;"></i>`;
            btn.classList.remove('hidden');
        } else {
            btn.classList.add('hidden'); btn.innerHTML = '';
            const pn = document.getElementById('playersOnlinePanel');
            if (pn) { pn.classList.add('hidden'); pn.innerHTML = ''; }
        }
        const panel = document.getElementById('playersOnlinePanel');
        if (panel && !panel.classList.contains('hidden')) renderPlayersOnlineList();
    }
    /* v9 8.14: punctele din chat / DM / profil (presence.js), tot pe loc. */
    try { if (window.TO_PRES) window.TO_PRES.paint(); } catch (e) { logError('refreshPresenceDots', e); }
}
window.refreshPresenceDots = refreshPresenceDots;

/* Panoul cu membrii online din pagina Players: avatar + nume clickabil (profil). */
function renderPlayersOnlineList() {
    const panel = document.getElementById('playersOnlinePanel');
    if (!panel) return;
    const esc2 = s => window.escHtml(s);
    const myName = state.currentUser && state.currentUser.name;
    const users = (window._lastOnlinePresence || []).filter(u => u && u.name).slice();
    users.sort((a, b) => (a.name === myName ? -1 : b.name === myName ? 1 : a.name.localeCompare(b.name)));
    if (!users.length) { panel.innerHTML = `<div class="popup-empty">${t('online.empty')}</div>`; return; }
    panel.innerHTML = users.map(u => {
        const m = ((u.id && typeof memberById === 'function' && memberById(u.id)) || (state.members && state.members[u.name])) || {};
        const dbId = u.id || m.dbId || null;
        const isMe = (u.id && state.currentUser && u.id === state.currentUser.id) || u.name === myName;
        const av = (typeof avatarHtml === 'function') ? avatarHtml(m.avatar, 30) : '';
        const inner = `<span class="pol-avatar">${av}</span><span class="pol-name">${esc2(u.name)}</span>${u.isAdmin ? ' 👑' : ''}${isMe ? ` <span class="pol-you">${t('dash.you')}</span>` : ''}`;
        return dbId
            ? `<div class="pol-row pol-link" data-pl-act="online-profile" data-id="${window.escHtml(dbId)}">${inner}</div>`
            : `<div class="pol-row">${inner}</div>`;
    }).join('');
}
window.renderPlayersOnlineList = renderPlayersOnlineList;

function togglePlayersOnlinePanel() {
    const panel = document.getElementById('playersOnlinePanel');
    if (!panel) return;
    const willOpen = panel.classList.contains('hidden');
    if (willOpen) { renderPlayersOnlineList(); panel.classList.remove('hidden'); }
    else panel.classList.add('hidden');
}
window.togglePlayersOnlinePanel = togglePlayersOnlinePanel;

/* ---------- Utilitare ---------- */
function isDemoMode() {
    return !!(state.currentUser && state.currentUser.isDemo);
}
function sbArray(v) {
    if (Array.isArray(v)) return v;
    if (v == null) return [];
    if (typeof v === 'string') { try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch (e) { return []; } }
    return [];
}
function sbObject(v) {
    if (v && typeof v === 'object' && !Array.isArray(v)) return v;
    if (typeof v === 'string') { try { const p = JSON.parse(v); return (p && typeof p === 'object' && !Array.isArray(p)) ? p : {}; } catch (e) { return {}; } }
    return {};
}

/* ---------- RSVP: normalizare + utilitare (Vin / Poate / Nu vin) ----------
   `participants` la evenimente este acum o HARTĂ nume->status ('yes'|'maybe'|'no').
   Compatibilitate înapoi: evenimentele vechi (listă simplă de nume) sunt citite
   ca „toți = Vin", ca să nu se piardă nimic. */
const RSVP_STATUSES = ['yes', 'maybe', 'no'];
function rsvpMap(v) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
        const out = {};
        Object.keys(v).forEach(k => { if (RSVP_STATUSES.includes(v[k])) out[k] = v[k]; });
        return out;
    }
    if (Array.isArray(v)) { const out = {}; v.forEach(n => { if (n) out[n] = 'yes'; }); return out; }
    if (typeof v === 'string') { try { return rsvpMap(JSON.parse(v)); } catch (e) { return {}; } }
    return {};
}
function rsvpList(map, status) { return Object.keys(map || {}).filter(n => map[n] === status); }
function rsvpOf(map, name) { return (map && name && map[name]) || null; }
function canDeleteEvent(event) {
    if (!state.currentUser || !event) return false;
    return isAdmin || (event.organizer && event.organizer === state.currentUser.id);
}
/* Editarea unui eveniment: admin (rang>=3) SAU organizatorul lui.
   Gardă de UX; autorizarea REALĂ e în RLS (`events_update_admin_or_organizer`, §23.e).
   Moderatorii (rang 2) pot edita DOAR evenimentele pe care le-au creat ei. */
function canEditEvent(event) {
    if (!state.currentUser || !event) return false;
    return isAdmin || (event.organizer && event.organizer === state.currentUser.id);
}
window.canEditEvent = canEditEvent;

/* Salvează modificările unui eveniment (toate câmpurile). DB-first (§23.a):
   starea locală se atinge DOAR după confirmarea scrierii. */
async function updateEvent(eventId) {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    const idx = (state.events || []).findIndex(e => String(e.id) === String(eventId));
    if (idx === -1) { showNotification(t('sb.eventNotFoundDot'), 'error'); return; }
    const event = state.events[idx];
    if (!canEditEvent(event)) { showNotification(t('sb.onlyEditOwnEvents'), 'error'); return; }

    const gv = id => (document.getElementById(id) || {}).value;
    const title = (gv('eventTitle') || '').trim();
    const date = gv('eventDate');
    const time = gv('eventTime');
    const location = (gv('eventLocation') || '').trim();
    const type = gv('eventType') || 'session';
    const description = (gv('eventDescription') || '').trim();
    if (!title || !date || !time) { showNotification(t('m2.fillTitleDateTime'), 'error'); return; }

    const iso = new Date(`${date}T${time}`).toISOString();
    const status = type === 'tournament' ? 'tournament' : 'confirmed';
    const reminders = (typeof readReminderConfig === 'function') ? readReminderConfig() : (event.reminders || { enabled: false, rules: [] });

    /* v8 — limita de locuri. `undefined` = câmpul nu a fost randat pe calea asta
       ⇒ NU atingem coloana, ca să nu ștergem tăcut o limită pe care nici n-am
       arătat-o utilizatorului. */
    const _capEdit = _readEventCapacity();
    let _capWritten = false;

    if (!isDemoMode() && !String(event.id).startsWith('tmp-')) {
        const patch = { title, date: iso, location: location || null, status, type, description: description || null, reminders };
        let up = { error: { skipped: true } };
        if (_capEdit !== undefined) {
            /* Treaptă NOUĂ în capul lanțului (vezi nota din `saveEvent`): un eșec
               aici nu trebuie să arunce odată cu limita și description/type. */
            up = await sb.from('events').update(Object.assign({}, patch, { max_participants: _capEdit })).eq('id', event.id);
        }
        /* Dacă treapta cu limită a picat, limita NU a ajuns în baza de date —
           marcăm asta ca starea locală să nu mintă mai jos. */
        _capWritten = !up.error;
        if (up.error) up = await sb.from('events').update(patch).eq('id', event.id);
        if (up.error) {
            /* coloanele description/type pot lipsi (migrare neaplicată) -> reîncearcă fără ele */
            up = await sb.from('events').update({ title, date: iso, location: location || null, status, reminders }).eq('id', event.id);
        }
        if (up.error) { showNotification(t('sb.updateErrorColon') + up.error.message, 'error'); return; }
    }

    /* DB-first: abia acum atingem starea locală. */
    event.title = title; event.date = iso; event.location = location || null;
    event.type = type; event.status = status; event.description = description || null;
    /* Starea locală urmează ce a acceptat baza, nu ce am cerut (§23 DB-first).
       În demo nu există scriere, deci acolo aplicăm direct valoarea din formular. */
    if (_capEdit !== undefined && (_capWritten || isDemoMode() || String(event.id).startsWith('tmp-'))) {
        event.maxParticipants = _capEdit;
    } else if (_capEdit !== undefined && !_capWritten) {
        showNotification(t('ev.seatLimitHint'), 'warning');
    }
    event.reminders = reminders;

    closeModal('editEventModal');
    renderCalendar();
    if (typeof renderNotifBadge === 'function') renderNotifBadge();
    addAuditLog('audit.eventUpdated', state.currentUser.name, title);
    showNotification(t('sb.eventUpdated'), 'success');

    /* Dacă pop-up-ul de detalii era deschis, îl reîmprospătăm cu datele noi. */
    const openEv = document.getElementById('eventDetailModal');
    if (openEv && typeof openEvent === 'function') { openEv.remove(); openEvent(event.id); }
}
window.updateEvent = updateEvent;
function eventDeleteBtnHtml(event) {
    if (!canDeleteEvent(event)) return '';
    return `<button class="btn btn-sm btn-danger" style="margin-top:10px;" onclick="deleteEvent('${String(event.id).replace(/'/g, "\\'")}')"><i class="fas fa-trash"></i> ${t('cal.deleteEvent')}</button>`;
}
async function deleteEvent(eventId) {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    const event = (state.events || []).find(e => String(e.id) === String(eventId));
    if (!event) { showNotification(t('sb.eventNotFoundDot'), 'error'); return; }
    if (!canDeleteEvent(event)) { showNotification(t('sb.onlyDeleteOwnEvents'), 'error'); return; }
    if (!await confirmModal(t('sb.confirmDeleteEvent').replace('{title}', event.title), { danger: true })) return;

    const isRealId = !isDemoMode() && event.id != null && String(event.id).indexOf('tmp-') !== 0;
    if (isRealId) {
        const del = await sb.from('events').delete().eq('id', event.id);
        if (del.error) { showNotification(t('m2.deleteErrorColon') + del.error.message, 'error'); return; }
    }
    state.events = (state.events || []).filter(e => String(e.id) !== String(eventId));
    const modal = document.querySelector('.modal.active'); if (modal) modal.remove();
    renderCalendar();
    if (typeof renderUpcomingEvents === 'function') renderUpcomingEvents();
    if (typeof renderNotifBadge === 'function') renderNotifBadge();
    addAuditLog('audit.eventDeleted', state.currentUser.name, event.title);
    /* Notifică membrii despre anularea evenimentului (clopoțel + push). */
    try {
        if (typeof pushNotification === 'function' && typeof clubMemberIds === 'function') {
            const others = clubMemberIds(state.currentUser.id);
            if (others.length) pushNotification(others, { clubId: currentClubId, type: 'event_cancelled', icon: 'fa-calendar-xmark', tab: 'calendar', text: t('notif.eventCancelled').replace('{title}', event.title || '') });
        }
    } catch (e) { logError('deleteEvent', e); }
    showNotification(t('sb.eventDeleted'), 'success');
}

/* Controale RSVP. `opts.scope` = prefix pentru id-urile interne, ca sumarul cu numele
   votanților să poată exista SIMULTAN în listă și în pop-up fără id-uri duplicate
   (id duplicat = toggle rupt). Sumarul (câți vin / poate / nu) apare acum PESTE TOT. */
/* v8 — helperi de capacitate. Fiecare se apără SINGUR (§55.3): sunt chemați din
   calendar, din lista zilei, din pop-up ȘI de pe Dashboard.
   `null` întors de `evSeatLists` = coloanele lipsesc (SQL vechi) => afișare ca înainte. */
function evCapacityOf(ev) {
    const n = parseInt(ev && ev.maxParticipants, 10);
    return (!isNaN(n) && n > 0) ? n : null;
}
function evSeatLists(ev) {
    const cap = evCapacityOf(ev);
    if (cap == null) return null;                                   /* fără limită = ca înainte de v8 */
    if (!Array.isArray(ev.rsvpConfirmed) || !Array.isArray(ev.rsvpWaitlist)) return null;  /* SQL vechi */
    return { cap: cap, conf: ev.rsvpConfirmed.map(String), wait: ev.rsvpWaitlist.map(String) };
}

/* Controale RSVP. `opts.scope` = prefix pentru id-urile interne, ca sumarul cu numele
   votanților să poată exista SIMULTAN în listă și în pop-up fără id-uri duplicate
   (id duplicat = toggle rupt). Sumarul (câți vin / poate / nu) apare acum PESTE TOT. */
function rsvpControlsHtml(event, opts) {
    const o = opts || {};
    const scope = o.scope || 'm';
    const isDash = scope === 'dash';
    const namesId = 'rsvpNames_' + scope + '_' + event.id;
    const me = state.currentUser ? String(state.currentUser.id) : null;
    const esc = s => window.escHtml(s);
    const nm = id => esc((typeof displayNameById === 'function' && displayNameById(id)) || id);
    const map = (event.participants && typeof event.participants === 'object' && !Array.isArray(event.participants))
        ? event.participants : rsvpMap(event.participants);
    const mine = me ? rsvpOf(map, me) : null;
    const yesList = rsvpList(map, 'yes'), maybeList = rsvpList(map, 'maybe'), noList = rsvpList(map, 'no');
    const yes = yesList.length, maybe = maybeList.length, no = noList.length;

    /* v8 — capacitate. `seats === null` => tot ce urmează se comportă EXACT ca înainte. */
    const seats = evSeatLists(event);
    const full = !!(seats && seats.conf.length >= seats.cap);
    const myWaitPos = (seats && me) ? (seats.wait.indexOf(me) + 1) : 0;

    const btn = (st, icon, label, activeCls) => {
        const active = mine === st;
        /* v8 — DELEGARE în loc de `onclick=`: id-ul evenimentului era interpolat
           direct în cod executabil. Ca `data-*` e DATĂ, nu cod (§5.1, §35 Z9-A). */
        return `<button class="btn btn-sm rsvp-btn ${active ? activeCls : 'btn-secondary'}" style="${active ? '' : 'opacity:.65;'}" title="${label}" data-action="rsvp" data-event-id="${esc(String(event.id))}" data-status="${st}"><i class="fas ${icon}"></i> <span class="rsvp-btn-label">${label}</span></button>`;
    };

    /* Butonul „Vin" spune adevărul: dacă e plin și nu ești deja înscris, ce urmează
       e o intrare pe listă, nu o confirmare. Interfața nu promite ce serverul refuză (§6.5). */
    const yesLabel = (full && mine !== 'yes') ? t('ev.joinWaitlist') : t('cal.coming');

    const seatsLine = seats
        ? `<div style="color:var(--text-secondary); font-size:.82em; margin-bottom:6px;"><i class="fas fa-chair"></i> ${t('ev.seatsOf').replace('{n}', seats.conf.length).replace('{max}', seats.cap)}</div>`
        : '';

    /* Cine e pe listă TREBUIE să-și vadă poziția scrisă, nu doar numele mai jos.
       Pe Dashboard cardul e compact => forma scurtă „Listă · #2" (§7.4). */
    const mineLine = (myWaitPos > 0)
        ? `<div style="margin-top:8px; font-size:.84em; color:var(--warning, #f0ad4e);"><i class="fas fa-hourglass-half"></i> ${isDash ? t('ev.waitlistShort').replace('{n}', myWaitPos) : t('ev.waitlistMine').replace('{n}', myWaitPos)}</div>`
        : '';

    /* Numele: cu limită, „vin" se desface în confirmați (numerotați) și listă
       (numerotată). Numerele apar DOAR aici, sub toggle — nu pe cardul compact. */
    const numbered = ids => ids.map((id, i) => (i + 1) + '. ' + nm(id)).join(', ');
    const yesNamesHtml = seats
        ? `${seats.conf.length ? `<div>✅ <strong>${t('ev.confirmedTitle')}</strong>: ${numbered(seats.conf)}</div>` : ''}
           ${seats.wait.length ? `<div style="margin-top:4px;">⏳ <strong>${t('ev.waitlistTitle')}</strong>: ${numbered(seats.wait)}</div>` : ''}`
        : (yesList.length ? `<div>✅ ${yesList.map(nm).join(', ')}</div>` : '');

    /* Cele 3 butoane: UN rând, fără wrap (pe ecrane înguste rămân doar iconițele — §18.7,
       `title` păstrat pentru accesibilitate). */
    /* v9 8.16-4g (2 nov. 2026): evenimentul a ÎNCEPUT ⇒ răspunsurile sunt închise (cererea lui Alex: „după
       ce se încheie, nu mai votează nimeni" — evenimentul n-are oră de sfârșit, deci se închide la ora de
       început). Rămân cifrele și numele; butoanele dispar. Serverul refuză și el (V9_816_4G_RSVP.sql).
       EN: RSVP closes at the event's start time; the server enforces it too. */
    const _tStart = Date.parse(event.date), _started = isFinite(_tStart) && _tStart < Date.now();
    return `${seatsLine}${_started ? `<div class="ev-rsvp-closed" style="font-size:.86em; color:var(--text-secondary);"><i class="fas fa-lock"></i> ${t('ev.rsvpClosed')}</div>` : `<div class="cal-rsvp-btns">
            ${btn('yes', 'fa-check', yesLabel, 'btn-success')}
            ${btn('maybe', 'fa-question', t('cal.maybe'), 'btn-warning')}
            ${btn('no', 'fa-times', t('cal.notComing'), 'btn-danger')}
        </div>
        ${mineLine}`}
        <small style="color:var(--text-secondary); display:block; margin-top:8px; cursor:pointer;" data-action="rsvp-names" data-names-id="${namesId}" title="${t('ev.participants')}">
            <i class="fas fa-users"></i> ${yes} ${t('cal.rsvpYes')} &middot; ${maybe} ${t('cal.rsvpMaybe')} &middot; ${no} ${t('cal.rsvpNo')} <i class="fas fa-chevron-down" style="font-size:.85em;"></i>
        </small>
        <div id="${namesId}" class="hidden" style="margin-top:6px; font-size:.82em; color:var(--text-secondary); line-height:1.7;">
            ${yesNamesHtml}
            ${maybeList.length ? `<div>❓ ${maybeList.map(nm).join(', ')}</div>` : ''}
            ${noList.length ? `<div>❌ ${noList.map(nm).join(', ')}</div>` : ''}
            ${(!yesList.length && !maybeList.length && !noList.length) ? window.t('ev.noVotesYet') : ''}
        </div>`;
}

/* v8 — delegare unică pentru controalele RSVP.
   RO: ⚠ ascultătorul e pe FAZA DE CAPTURĂ, intenționat. Containerele care
       înconjoară controalele au `onclick="event.stopPropagation()"` (calendar,
       lista zilei, cardul de Dashboard) ca un clic pe buton să nu deschidă
       evenimentul. În faza de bubbling acel `stopPropagation` ar opri evenimentul
       ÎNAINTE să ajungă la `document` și butoanele ar muri tăcut. Captura merge
       de sus în jos, deci ne executăm înaintea lui.
   EN: capture phase on purpose — the wrapper divs call stopPropagation on bubble,
       which would otherwise silently kill every RSVP button.
   Legat O SINGURĂ DATĂ, cu gardă: modalele se recreează la fiecare deschidere. */
if (!window._rsvpDelegBound) {
    window._rsvpDelegBound = true;
    document.addEventListener('click', function (e) {
        if (!e.target || !e.target.closest) return;
        const el = e.target.closest('[data-action="rsvp"],[data-action="rsvp-names"]');
        if (el) {
            if (el.dataset.action === 'rsvp') {
                e.preventDefault();
                if (typeof setRSVP === 'function') setRSVP(el.dataset.eventId, el.dataset.status);
            } else if (typeof toggleRsvpNames === 'function') {
                toggleRsvpNames(el.dataset.namesId);
            }
            return;
        }
        /* Deschiderea evenimentului de pe cardul de Dashboard.
           ⚠ `return` de mai sus ține locul vechiului `stopPropagation`: un clic
           pornit dintr-un control RSVP nu ajunge NICIODATĂ aici, deci butoanele
           „Vin / Poate / Nu vin" nu mai deschid pop-up-ul. */
        const op = e.target.closest('[data-action="dash-open-event"]');
        if (op && typeof openEvent === 'function') openEvent(op.dataset.eventId);
    }, true);
}

function toggleRsvpNames(domId) {
    const el = document.getElementById(domId);
    if (el) el.classList.toggle('hidden');
}

/* ---------- Faza 2 (refactor name->id): hartă pe id + helperi ----------
   `state.membersById` (id -> member) devine sursa de adevăr; `state.members`
   (pe nume) rămâne TRANZITORIU până la migrarea tuturor subsistemelor (Faza 3),
   apoi se elimină (Faza 5). Membrii din ambele hărți sunt ACELEAȘI obiecte. */
function memberById(id) {
    if (id == null) return null;
    return (state.membersById && state.membersById[id]) || null;
}
/* Un UUID care nu se rezolvă în niciun nume. Definit o singură dată, lângă
   consumatorul lui — celelalte două apariții ale regexului sunt locale altor funcții. */
const _looksLikeUuid = s => typeof s === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);

function displayNameById(id) {
    const m = memberById(id);
    if (m && (m.displayName || m.name)) return m.displayName || m.name;
    if (state.profileNamesById && state.profileNamesById[id]) return state.profileNamesById[id];
    /* RO: Un UUID complet nerezolvabil înseamnă un cont ȘTERS: hidratarea completează
           `profileNamesById` pentru orice id întâlnit în meciuri/turnee, iar un profil
           inexistent nu întoarce nimic din `profiles`, deci rămâne lipsă pentru totdeauna.
           Înainte se returna '' și cele ~20 de locuri care fac `displayNameById(id) || id`
           afișau UUID-ul brut utilizatorului — după ștergerea unui cont, istoricul
           clubului arăta `4fbdf811-dc84-…` în loc de un nume.

           NU scriem textul în date: UUID-ul rămâne cheia, ca să nu confundăm doi
           utilizatori șterși, să nu rupem clasamentele care numără pe id și să putem
           relega contul dacă e vreodată restaurat. Se schimbă doar ce vede omul.

           Garda pe harta goală: în primele momente de la pornire `profileNamesById` e
           încă necompletată, iar un id nerezolvat înseamnă „nu s-a încărcat încă", nu
           „șters". Acolo păstrăm comportamentul vechi.
       EN: an unresolvable UUID means a deleted account; show a label instead of the raw
           id. The id itself stays as the key — only the rendering changes. */
    if (_looksLikeUuid(id)
        && state.profileNamesById && Object.keys(state.profileNamesById).length > 0) {
        return (typeof t === 'function') ? t('common.deletedProfile') : 'Deleted profile';
    }
    return ''; // necunoscut total -> caller cade pe id
}
function memberByName(name) {
    if (name == null) return null;
    return (state.members && state.members[name]) || null;
}
function membersList() {
    return state.membersById ? Object.values(state.membersById)
         : Object.values(state.members || {});
}

/* ---------- Hidratare: tabele Supabase -> obiectul `state` ---------- */
/* ─────────────────────────────────────────────────────────────────────────
   P4 (Z8-B/C): maparea unui rând `matches` (DB) -> obiect sesiune (state.games).
   Extrasă ca funcție reutilizabilă, folosită atât de hidratarea completă cât și
   de actualizarea INCREMENTALĂ din realtime (aplicăm doar rândul schimbat, în loc
   să reîncărcăm tot istoricul la fiecare eveniment). Aceeași logică, o singură sursă.
   ───────────────────────────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────────────────────────
   P4-Faza 2: paginarea încărcării istoricului. La login încărcăm doar ultimele
   HISTORY_PAGE meciuri (afișare + UI instant); restul se completează în fundal sau
   la cerere prin ensureFullHistory(). Sub prag, totul se încarcă deodată (identic cu
   înainte). ELO/stats/backup rămân pe CLIENT, pe istoricul COMPLET (după completare)
   -> zero reimplementare, zero risc de divergență a clasamentelor.
   ───────────────────────────────────────────────────────────────────────── */
const HISTORY_PAGE = 500;

/* Completează state.games cu ÎNTREGUL istoric al clubului, dacă e încă parțial.
   Idempotentă: dacă nu e parțial (sau nu e club), iese imediat. Apelată în fundal
   după login și forțat (await) de operațiile care au nevoie de tot (backup, export,
   „vezi tot” în istoric). O singură cerere completă; apoi invalidează ELO + re-randează. */
let _fullHistoryInFlight = null;
async function ensureFullHistory() {
    if (!state._gamesPartial || !currentClubId || isDemoMode()) return;
    if (_fullHistoryInFlight) return _fullHistoryInFlight;   /* nu porni două deodată */
    _fullHistoryInFlight = (async () => {
        try {
            const res = await sb.from('matches').select('*').eq('club_id', currentClubId).order('date', { ascending: false });
            if (!res.error && Array.isArray(res.data)) {
                state.games = res.data.map(mapMatchRowToSession);
                state._gamesPartial = false;
                /* istoric complet -> ELO/stats corecte; re-randează view-urile dependente */
                if (typeof _runDataRefresh === 'function') _runDataRefresh();
            }
        } catch (e) { logError('ensureFullHistory', e); }
        finally { _fullHistoryInFlight = null; }
    })();
    return _fullHistoryInFlight;
}
window.ensureFullHistory = ensureFullHistory;

function mapMatchRowToSession(m) {
    return {
        id: m.id, timestamp: m.date, gameName: m.game_title,
        scores: sbObject(m.scores), participants: sbArray(m.players),
        winners: sbArray(m.winners),
        clubPoints: sbObject(m.club_points),
        scoreMode: (m.meta && m.meta.scoreMode === 'uniform') ? 'uniform' : 'raw',
        scoreDir: (m.meta && m.meta.scoreDir === 'low') ? 'low' : 'high',
        scoreKind: (m.meta && m.meta.scoreKind) || null,
        campaignId: (m.meta && m.meta.campaignId) || null,
        scenarioName: (m.meta && m.meta.scenarioName) || null,
        tieBreaks: (m.meta && m.meta.tieBreaks && typeof m.meta.tieBreaks === 'object') ? m.meta.tieBreaks : null,
        guests: (m.meta && Array.isArray(m.meta.guests)) ? m.meta.guests : [],
        duration: (m.meta && m.meta.duration) || 0,
        location: (m.meta && m.meta.location) || null,
        notes: (m.meta && m.meta.notes) || null,
        type: (m.meta && m.meta.type) || 'standard',
        isTournament: !!(m.meta && m.meta.type === 'tournament'),
        tournamentName: (m.meta && m.meta.tournamentName) || '',
        prize: (m.meta && m.meta.prize) || '',
        format: (m.meta && m.meta.format) || '',
        rounds: (m.meta && m.meta.rounds) || null,
        fee: (m.meta && m.meta.fee) || '',
        scoresheet: (m.meta && m.meta.scoresheet) || null,
        roles: (m.meta && m.meta.roles) || null,
        playerTimes: (m.meta && m.meta.playerTimes) || null,
        coop: (m.meta && m.meta.coop) || null,
        friendly: !!(m.meta && m.meta.friendly),
        photos: (m.meta && Array.isArray(m.meta.photos)) ? m.meta.photos : [],
        /* ⚠ `timestamp` (mapat din `matches.date`) e data PLANIFICATĂ a sesiunii, nu
           ora la care a fost înregistrată — de aceea o sesiune notată azi pentru
           diseară avea o dată în VIITOR și apărea etichetată „chiar acum" zile în
           șir, în capul clopoțelului. `created_at` e ora reală de scriere.
           ⚠ Se citește TOLERANT: hidratarea folosește `select('*')`, deci coloana
             apare singură după migrare și lipsește curat înainte — aplicația merge
             identic în ambele stări, indiferent de ordinea deploy-ului (§38f).
           EN: `date` is the PLANNED date; `created_at` is when the row was written.
               Read tolerantly so the app works with or without the migration. */
        createdAt: m.created_at ? new Date(m.created_at).getTime() : null,
        recordedBy: m.recorded_by || '—'
    };
}

/* Rezolvă (prin RPC `names_for_ids`) numele care lipsesc din profileNamesById pentru
   o listă de sesiuni — folosită la actualizarea incrementală (doar meciul nou). */
async function resolveMissingNamesForGames(games) {
    try {
        const _isUuid = s => typeof s === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
        const need = new Set();
        const want = id => { if (_isUuid(id) && !(state.profileNamesById && state.profileNamesById[id])) need.add(id); };
        (games || []).forEach(g => {
            (Array.isArray(g.participants) ? g.participants : []).forEach(want);
            if (g.scores) Object.keys(g.scores).forEach(want);
            if (Array.isArray(g.winners)) g.winners.forEach(want);
            want(g.recordedBy);
        });
        if (need.size) {
            const nres = await sb.rpc('names_for_ids', { p_ids: Array.from(need) });
            if (!nres.error && Array.isArray(nres.data)) {
                if (!state.profileNamesById) state.profileNamesById = {};
                nres.data.forEach(r => { if (r && r.id) state.profileNamesById[r.id] = r.name || ''; });
            }
        }
    } catch (e) { logError('resolveMissingNamesForGames', e); }
}

async function hydrateFromSupabase(opts) {
    /* P4-Faza 1b: `skipHeavy` sare peste matches+messages (tabelele nelimitate),
       păstrând istoricul deja din memorie. Folosit la evenimente realtime pe tabele
       MICI (profiles/games/events/settings/members) — nu mai reîncărcăm tot istoricul
       de meciuri+mesaje pentru o schimbare de profil sau un joc nou. */
    const _skipHeavy = !!(opts && opts.skipHeavy);
    /* Gardă: fără club activ, interogările club-scoped ar trimite `club_id=eq.null`
       -> PostgREST respinge "null" ca uuid (400). Sărim peste ele (profiles nu depinde
       de club); fallback-urile de mai jos lucrează cu liste goale. */
    const _noClub = !currentClubId;
    const _emptyRes = { data: [], error: null };
    const _skipRes = { data: null, error: null };   /* data null => nu remapăm (păstrăm ce e) */
    const [profilesRes, gamesRes, eventsRes, matchesRes, messagesRes, settingsRes, membersRes] = await Promise.all([
        sb.from('profiles').select(window.PROFILE_COLS),
        _noClub ? Promise.resolve(_emptyRes) : sb.from('games').select('*').eq('club_id', currentClubId),
        _noClub ? Promise.resolve(_emptyRes) : sb.from('events').select('*').eq('club_id', currentClubId),
        (_noClub || _skipHeavy) ? Promise.resolve(_skipRes) : sb.from('matches').select('*').eq('club_id', currentClubId).order('date', { ascending: false }).limit(HISTORY_PAGE),
        /* v9 8.1-c3a: doar ultimele 200 de mesaje (înainte: TOT istoricul); cele mai vechi vin la derulare (chat-core.js). */
        (_noClub || _skipHeavy) ? Promise.resolve(_skipRes) : sb.from('messages').select('*').eq('club_id', currentClubId).order('timestamp', { ascending: false }).limit(200),
        _noClub ? Promise.resolve(_emptyRes) : sb.from('club_settings').select('*').eq('club_id', currentClubId),
        _noClub ? Promise.resolve(_emptyRes) : sb.from('club_members').select('profile_id, role, status, points, joined_at').eq('club_id', currentClubId).eq('status', 'active')
    ]);

    [profilesRes, gamesRes, eventsRes, matchesRes, messagesRes].forEach(r => {
        if (r && r.error) console.warn('Supabase hydrate:', r.error.message);
    });

    const profiles = profilesRes.data || [];
    /* La skipHeavy nu re-fetchuim matches; derivările de mai jos (gamesPlayed, statistici
       de catalog) au nevoie totuși de istoric -> îl reconstituim din state.games păstrat. */
    const matches = matchesRes.data || (state.games || []).map(g => ({
        game_title: g.gameName, date: g.timestamp, players: g.participants,
        meta: { friendly: g.friendly, duration: g.duration }
    }));

    /* Apartenența la clubul activ: profile_id -> rol în club.
       Dacă interogarea eșuează, revenim la comportamentul vechi (toți), ca să
       nu rămână aplicația goală din cauza unei erori temporare. */
    const memberRoleById = {};
    const memberPointsById = {};
    /* Data intrării în club, din `club_members.joined_at`. Ordinea de afișare din
       administrare se baza pe ordinea cheilor din `state.users`, adică pe ordinea
       în care le returna baza de date — care se schimbă la orice actualizare a unui
       rând. De aici senzația că lista „se amestecă" la fiecare schimbare de rol. */
    const memberJoinedById = {};
    let scopeMembers = true;
    if (membersRes && membersRes.error) { scopeMembers = false; console.warn('Supabase hydrate (members):', membersRes.error.message); }
    else (membersRes && membersRes.data || []).forEach(m => { memberRoleById[m.profile_id] = m.role; memberPointsById[m.profile_id] = Number(m.points) || 0; memberJoinedById[m.profile_id] = m.joined_at || null; });
    const clubRoleToLegacy = r => (r === 'principal_admin' || r === 'secondary_admin') ? 'admin' : 'member';

    /* Câte jocuri a jucat fiecare (derivat din matches — players sunt ACUM id-uri).
       Sesiunile amicale NU contează (§12.d) — aliniat cu countWins ȘI cu calea
       optimistă din saveSession, ca să nu „sară" gamesPlayed la primul refresh. */
    const gamesPlayedById = {};
    matches.forEach(m => {
        if (m.meta && m.meta.friendly) return;
        sbArray(m.players).forEach(pid => {
            gamesPlayedById[pid] = (gamesPlayedById[pid] || 0) + 1;
        });
    });

    /* profiles -> state.users (conturi) + state.members (jucători)
       Doar profilurile care sunt membre ale clubului activ.

       ⚠ HIDRATARE ATOMICĂ (bug real, intermitent):
       Înainte goleam `state.users = {}` AICI, apoi repopulam după await-uri către DB.
       În fereastra aia (zeci-sute de ms) starea era GOALĂ. Iar `updateMyProfile` face
       4 scrieri pe `profiles`, fiecare declanșând un eveniment realtime -> încă un
       `hydrateFromSupabase()` + o cascadă de randări. Două hidratări suprapuse: una
       golea starea, cealaltă randa peste gol => avatarul și favoritele DISPĂREAU din UI
       (deși în DB erau intacte — de aceea reveneau la refresh).

       ACUM construim în obiecte LOCALE și le schimbăm DINTR-O SINGURĂ MIȘCARE, după ce
       sunt complet populate. Un cititor vede fie versiunea veche, fie cea nouă —
       NICIODATĂ una pe jumătate. Elimină categoria de bug, nu doar instanța.

       Swap-ul se face imediat după bucla de populare, ca tot codul de mai jos (care
       CITEȘTE state.users — ex. chat, chatReads) să funcționeze neschimbat. */
    const _users = {};
    const _members = {};
    const _membersById = {};
    /* Hartă globală id->nume din TOATE profilurile (chiar ne-membri ai clubului:
       invitați, jucători care au plecat) — rezervă pt. displayNameById, ca să nu
       apară UUID-uri brute în istoric/statistici. */
    const _profileNamesById = {};
    profiles.forEach(p => { if (p && p.id) _profileNamesById[p.id] = p.display_name || p.name || ''; });

    /* FAZA B (V8): coloana `email` nu mai e livrata prin select (bucket privat de PII).
       Aducem emailurile VIZIBILE (self + cerc, respectand hide_email) prin RPC-ul
       `emails_for`. Cheia state.users devine email (cand e vizibil) sau 'id:<uuid>'
       (cand hide_email il ascunde), ca sa nu pierdem membrii ascunsi din listele admin. */
    const emailById = {};
    try {
        const _mids = profiles
            .filter(p => p && p.id && (!scopeMembers || Object.prototype.hasOwnProperty.call(memberRoleById, p.id)))
            .map(p => p.id);
        if (_mids.length) {
            const er = await sb.rpc('emails_for', { p_ids: _mids });
            if (er && !er.error) (er.data || []).forEach(row => { if (row && row.id) emailById[row.id] = row.email || null; });
        }
    } catch (e) { logError('hydrate emails_for', e); }
    /* Emailul propriu: sursa de adevar e sesiunea (authUser), nu profiles. */
    if (state.currentUser && state.currentUser.id && state.currentUser.email) {
        emailById[state.currentUser.id] = state.currentUser.email;
    }

    profiles.forEach(p => {
        const inClub = !scopeMembers || Object.prototype.hasOwnProperty.call(memberRoleById, p.id);
        if (!inClub) return;
        const legacyRole = scopeMembers ? clubRoleToLegacy(memberRoleById[p.id]) : (p.role || 'member');
        const isAdminRole = legacyRole === 'admin';
        const uEmail = emailById[p.id] || null;             /* email vizibil (sau null daca ascuns) */
        const uKey = uEmail || ('id:' + p.id);              /* cheie stabila indiferent de vizibilitate */
        {
            _users[uKey] = {
                id: p.id, name: p.name, email: uEmail, role: legacyRole,
                userTag: p.user_tag || '', displayName: p.display_name || p.name,
                isAdmin: isAdminRole, points: Number(memberPointsById[p.id]) || 0, avatar: p.avatar || null,
                bio: p.bio || '', skillLevel: p.skill_level || 3,
                preferredGames: sbArray(p.preferred_games),
                bggUsername: p.bgg_username || '', hideEmail: !!p.hide_email,
                isDiscoverable: !!p.is_discoverable,
                profileExtra: (p.profile_extra && typeof p.profile_extra === 'object' && !Array.isArray(p.profile_extra)) ? p.profile_extra : null,
                createdAt: p.created_at || new Date().toISOString(),
                joinedAt: memberJoinedById[p.id] || null,   /* intrarea în CLUB, nu crearea contului */
                lastLogin: new Date().toISOString(), verified: true
            };
        }
        if (p.name) {
            const memberObj = {
                dbId: p.id, name: p.name, role: legacyRole,
                userTag: p.user_tag || '', displayName: p.display_name || p.name,
                score: Number(memberPointsById[p.id]) || 0,
                gamesPlayed: gamesPlayedById[p.id] || 0,
                hidden: false, email: uEmail,
                /* `joinDate` = intrarea în CLUB. Înainte lua `p.created_at`, adică data
                   creării CONTULUI — două lucruri diferite pentru cineva care s-a
                   înscris acum trei ani și a intrat în club luna trecută. */
                joinDate: ((memberJoinedById[p.id] || p.created_at || '')).split('T')[0] || '',
                joinedAt: memberJoinedById[p.id] || null,
                preferredGames: sbArray(p.preferred_games), skillLevel: p.skill_level || 3,
                avatar: p.avatar || null, bio: p.bio || '',
                bggUsername: p.bgg_username || '', hideEmail: !!p.hide_email,
                profileExtra: (p.profile_extra && typeof p.profile_extra === 'object' && !Array.isArray(p.profile_extra)) ? p.profile_extra : null
            };
            _members[p.name] = memberObj;     // legacy (nume) — tranzitoriu (Faza 3->5)
            _membersById[p.id] = memberObj;   // ★ Faza 2: sursa de adevăr pe id
        }
    });

    /* ★ SWAP ATOMIC. Structurile sunt complet populate -> le publicăm dintr-o singură
       mișcare. Starea partajată nu a fost niciodată goală: un render concurent (de la
       realtime) vedea versiunea VECHE, nu una pe jumătate construită.
       De aici încolo, codul de mai jos citește `state.users` ca înainte. */
    state.users = _users;
    state.members = _members;
    state.membersById = _membersById;
    /* ⚠ Hidratarea reconstruiește obiectele de membru cu avatarurile PROASPETE din
       baza de date, deci ascunderea aplicată anterior se pierde. Se reaplică aici,
       imediat după swap-ul atomic. Fără linia asta, poza unui utilizator blocat
       reapărea la fiecare re-hidratare (schimbare de club, refresh de date). */
    try { if (typeof window.blockStripAvatars === 'function') window.blockStripAvatars(); } catch (e) { logError('hydrate', e); }
    state.profileNamesById = _profileNamesById;

    /* currentUser.points = punctele din CLUBUL ACTIV (per-club). currentUser se
       setează o dată la login; aici îl re-sincronizăm la fiecare hidratare /
       schimbare de club activ, ca să nu rămână o valoare veche. */
    if (state.currentUser && state.currentUser.id && Object.prototype.hasOwnProperty.call(memberPointsById, state.currentUser.id)) {
        state.currentUser.points = Number(memberPointsById[state.currentUser.id]) || 0;
    }

    /* games -> state.catalog (cheie = title)

       ⚠ HIDRATARE ATOMICĂ (aceeași clasă de bug ca la `state.users`, §31.7):
       goleam `state.catalog = {}` AICI, apoi îl repopulam. Între timp, o randare
       concurentă (declanșată de realtime) putea prinde catalogul GOL -> jocurile
       „dispăreau" din UI până la următoarea hidratare.
       Construim într-un obiect LOCAL și îl publicăm DINTR-O SINGURĂ MIȘCARE, după ce
       e complet — inclusiv statisticile derivate din meciuri și mediile.
       Un cititor vede fie versiunea veche, fie cea nouă. Niciodată una pe jumătate. */
    const _catalog = {};
    (gamesRes.data || []).forEach(g => {
        const min = g.min_players, max = g.max_players;
        const playersStr = (min && max) ? (min === max ? `${min}` : `${min}-${max}`)
            : (min ? `${min}+` : (max ? `${max}` : '?'));
        _catalog[g.title] = {
            dbId: g.id,
            categories: g.category ? String(g.category).split(',').map(s => s.trim()).filter(Boolean) : ['Altele'],
            players: playersStr, minPlayers: min || null, maxPlayers: max || null,
            duration: g.duration || 60, difficulty: g.complexity || 3,
            baseDifficulty: (typeof g.complexity === 'number' ? g.complexity : null),
            baseRating: (typeof g.base_rating === 'number' ? g.base_rating : null),
            ratingVotes: (g.rating_votes && typeof g.rating_votes === 'object' && !Array.isArray(g.rating_votes)) ? g.rating_votes : {},
            rating: 0, description: g.description || '', imageUrl: g.image_url || null,
            minAge: (typeof g.min_age === 'number' ? g.min_age : null), designer: g.designer || null, artist: g.artist || null, publisher: g.publisher || null, edition: g.edition || null,
            scoreDir: (String(g.score_dir || 'high').toLowerCase() === 'low' ? 'low' : 'high'),
            createdAt: g.created_at || null,
            owner: g.owner || null, bggId: g.bgg_id || null,
            timesPlayed: 0, actualDurations: [], avgDuration: null, lastPlayed: null,
            addedBy: g.owner || ''
        };
    });
    /* timesPlayed / lastPlayed / durate reale derivate din matches */
    matches.forEach(m => {
        const c = _catalog[m.game_title];
        if (c) {
            c.timesPlayed = (c.timesPlayed || 0) + 1;
            if (m.date && (!c.lastPlayed || new Date(m.date) > new Date(c.lastPlayed))) c.lastPlayed = m.date;
            const md = (m.meta && typeof m.meta === 'object') ? Number(m.meta.duration) : 0;
            if (md > 0) { if (!Array.isArray(c.actualDurations)) c.actualDurations = []; c.actualDurations.push(md); }
        }
    });
    Object.keys(_catalog).forEach(function (k) {
        const c = _catalog[k];
        if (c && Array.isArray(c.actualDurations) && c.actualDurations.length) {
            c.avgDuration = Math.round(c.actualDurations.reduce(function (a, b) { return a + b; }, 0) / c.actualDurations.length);
        }
        if (typeof gameRatingAvg === 'function') { const ra = gameRatingAvg(c); if (ra != null) c.rating = Math.round(ra * 10) / 10; }
        if (typeof gameWeightAvg === 'function') { const wa = gameWeightAvg(c); if (wa != null) c.difficulty = Math.round(wa * 100) / 100; }
    });

    /* ★ SWAP ATOMIC — catalogul e complet (jocuri + statistici + medii). */
    state.catalog = _catalog;

    /* events -> state.events
       FIX (§bug „descrierea nu apare"): `description` era HARDCODAT null aici, iar
       `reminders` nu era citit deloc, iar `type` era dedus din `status` (deci
       „meeting"/„other" deveneau „session"). Acum le citim pe toate din DB.
       `type` are fallback pe status pentru rândurile vechi (înainte de backfill). */
    state.events = (eventsRes.data || []).map(e => ({
        id: e.id, title: e.title, date: e.date, location: e.location || null,
        maxParticipants: e.max_participants || null, gameId: e.game_id || null,
        /* v8 — liste ORDONATE după momentul răspunsului, scrise de RPC. Harta
           `participants` nu poate purta ordine (cheile jsonb se ordonează după
           lungime/octeți), iar o interogare separată la pornire e interzisă (§5bis.2).
           ⚠ `null` (nu `[]`) când coloanele lipsesc: distinge „SQL vechi, comportă-te
           ca înainte" de „listă goală". EN: null = columns absent, [] = empty list. */
        rsvpConfirmed: Array.isArray(e.rsvp_confirmed) ? e.rsvp_confirmed.map(String) : null,
        rsvpWaitlist: Array.isArray(e.rsvp_waitlist) ? e.rsvp_waitlist.map(String) : null,
        participants: rsvpMap(e.participants), status: e.status || 'confirmed',
        type: e.type || (e.status === 'tournament' ? 'tournament' : 'session'),
        description: e.description || null,
        reminders: (e.reminders && typeof e.reminders === 'object') ? e.reminders : null,
        organizer: e.organizer || '—', createdAt: e.created_at || null
    }));
    /* v9 8.16-4g: răspunsurile RSVP „în zbor" (setRSVP) se reaplică peste ce a adus serverul, până la
       răspunsul lui — altfel o reîncărcare concurentă arăta o clipă starea veche. Doar răspunsul MEU și
       doar cât e în zbor (cel mult 15 s). EN: overlay my in-flight RSVP on the freshly loaded events. */
    try {
        const _pend = window._rsvpPending || {}, _meId = state.currentUser ? String(state.currentUser.id) : null;
        Object.keys(_pend).forEach(function (id) {
            const p = _pend[id];
            if (!p || Date.now() - p.at > 15000) { delete _pend[id]; return; }
            const ev = state.events.find(x => String(x.id) === String(id));
            if (!ev || !_meId) return;
            if (p.desired === null) delete ev.participants[_meId]; else ev.participants[_meId] = p.desired;
            if (p.conf && Array.isArray(ev.rsvpConfirmed)) ev.rsvpConfirmed = p.conf.slice();
            if (p.wait && Array.isArray(ev.rsvpWaitlist)) ev.rsvpWaitlist = p.wait.slice();
        });
    } catch (e) { logError('hydrate.rsvpPending', e); }

    /* matches -> state.games (istoric sesiuni) */
    if (matchesRes.data) {
        state.games = matchesRes.data.map(mapMatchRowToSession);
        /* Faza 2: dacă am primit exact HISTORY_PAGE, POSIBIL mai există istoric vechi ->
           marcăm parțial și-l completăm în fundal (ensureFullHistory). Sub prag =
           tot istoricul e deja aici -> comportament IDENTIC cu înainte, zero risc. */
        state._gamesPartial = (matchesRes.data.length >= HISTORY_PAGE);
    }

    /* Rezolvă numele participanților din meciuri care NU sunt în profileNamesById
       (ex. invitați care nu-s membri ai clubului -> excluși de RLS la profiles).
       RPC SECURITY DEFINER întoarce DOAR nume (low-sensitivity), ocolind RLS. */
    try {
        const _isUuid = s => typeof s === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
        const need = new Set();
        const want = id => { if (_isUuid(id) && !(state.profileNamesById && state.profileNamesById[id])) need.add(id); };
        (state.games || []).forEach(g => {
            (Array.isArray(g.participants) ? g.participants : []).forEach(want);
            if (g.scores) Object.keys(g.scores).forEach(want);
            if (Array.isArray(g.winners)) g.winners.forEach(want);
            want(g.recordedBy);
        });
        if (need.size) {
            const nres = await sb.rpc('names_for_ids', { p_ids: Array.from(need) });
            if (!nres.error && Array.isArray(nres.data)) {
                nres.data.forEach(r => { if (r && r.id) state.profileNamesById[r.id] = r.name || ''; });
            } else if (nres.error) {
                console.warn('names_for_ids:', nres.error.message);
            }
        }
    } catch (e) { console.warn('names_for_ids:', e && e.message); }

    /* messages -> state.chatMessages */
    if (messagesRes.data) state.chatMessages = (messagesRes.data || []).slice().reverse().map(msg => ({   /* 8.1-c3a: cele mai noi au venit primele */
        id: msg.id, sender: msg.sender_name, senderEmail: msg.sender_email, senderId: msg.sender_id || null,
        text: msg.text, timestamp: msg.timestamp,
        /* Căile din bucket-ul privat `chat-photos`, nesemnate. Semnarea o face
           observatorul global (`data-sp`) la randare, ca la pozele de sesiune.
           ⚠ `|| []` acoperă mesajele de dinaintea coloanei ȘI cazul în care
             migrarea nu a fost rulată: `msg.photos` e atunci `undefined`, iar
             restul codului trebuie să primească MEREU un tablou, niciodată null. */
        photos: Array.isArray(msg.photos) ? msg.photos : [],
        edited: !!msg.edited, replyTo: msg.reply_to || null, replySender: msg.reply_sender || null, replySnippet: msg.reply_snippet || null,
        deletedAt: msg.deleted_at || null, deletedByAdmin: !!msg.deleted_by_admin,   /* v9 B2a: urma ștergerii (chat-core.js) / deleted trace */
        mentions: Array.isArray(msg.mentions) ? msg.mentions : null,                 /* v9 8.1-f: @mențiuni / mentions */
        linkPreview: msg.link_preview || null,                                       /* v9 8.6: cardul linkului (chat-core.js) / link card */
        liveRef: msg.live_ref || null,                                               /* v9 8.16-4b: atașamentul viu (chat-cards.js) / live attachment */
        isAdmin: ((state.users[msg.sender_email] || (msg.sender_id && state.users['id:' + msg.sender_id])) || {}).isAdmin || false
    }));

    /* club_settings -> setări de club. Mod competitiv ELO: dezactivat implicit.
       Dacă tabela încă nu există (scriptul SQL nerulat), rămâne pe false. */
    /* eloEnabled este o setare PER CLUB — sursa de adevăr e DB (club_settings).
       O resetăm la fiecare hidratare și o setăm doar din citirea DB de mai jos,
       ca să nu „curgă" valoarea unui club în altul (bug la comutarea clubului).
       Fără cache localStorage global (era per-browser, deci greșit pentru o
       setare per-club). */
    state.eloEnabled = false;
    state.gameVote = null;
    if (typeof state.seasonMonth !== 'number') state.seasonMonth = new Date().getMonth() + 1;
    if (typeof state.seasonYear !== 'number') state.seasonYear = new Date().getFullYear();
    if (settingsRes && !settingsRes.error && Array.isArray(settingsRes.data)) {
        const findVal = k => { const r = settingsRes.data.find(x => x.key === k); return r ? r.value : undefined; };
        const row = settingsRes.data.find(r => r.key === 'elo_enabled');
        if (row) {
            const v = row.value;
            state.eloEnabled = (v === true || v === 'true' || v === 1);
        }
        const gv = settingsRes.data.find(r => r.key === 'game_night_vote');
        state.gameVote = (gv && gv.value && typeof gv.value === 'object' && !Array.isArray(gv.value)) ? gv.value : null;

        const pollsVal = findVal('polls');
        if (Array.isArray(pollsVal)) state.polls = pollsVal;
        const gvHist = findVal('game_vote_history');
        if (Array.isArray(gvHist)) state.gameVoteHistory = gvHist; else if (!Array.isArray(state.gameVoteHistory)) state.gameVoteHistory = [];

        const cm = findVal('current_month');
        if (cm != null && !isNaN(parseInt(cm))) state.seasonMonth = Math.min(12, Math.max(1, parseInt(cm)));
        const cy = findVal('current_year');
        if (cy != null && !isNaN(parseInt(cy))) state.seasonYear = parseInt(cy);
        const cs = findVal('current_season');
        if (cs != null && !isNaN(parseInt(cs))) state.currentSeason = parseInt(cs);
        const slbl = findVal('season_label');
        state.seasonLabel = (typeof slbl === 'string') ? slbl : '';
        const arch = findVal('season_archives');
        if (Array.isArray(arch)) state.seasons = arch;

        const ss = findVal('scoresheets');
        state.scoresheets = (ss && typeof ss === 'object' && !Array.isArray(ss)) ? ss : {};

        /* Șabloane de uniformizare a scorurilor (A/B/C) + modul implicit al clubului. */
        const sct = findVal('scoring_templates');
        state.scoringTemplates = (sct && typeof sct === 'object' && !Array.isArray(sct)) ? sct : null;

        const gr = findVal('gameroles');
        state.gameRoles = (gr && typeof gr === 'object' && !Array.isArray(gr)) ? gr : {};

        /* Comutatorul „acceptarea regulamentului" — citit odată cu restul setărilor. */
        try {
            if (!state.settings) state.settings = {};
            state.settings.rulesAckRequired = !!findVal('rules_ack_required');
        } catch (e) { logError('hydrate rules_ack_required', e); }

        const reg = findVal('regulation');
        /* Default-uri de remindere (card Settings) -> state.settings.reminderDefaults. */
        try {
            const rd = findVal('reminder_defaults');
            if (rd && typeof rd === 'object') {
                if (!state.settings) state.settings = {};
                state.settings.reminderDefaults = rd;
            }
        } catch (e) { logError('hydrate reminder_defaults', e); }        if (reg && typeof reg === 'object' && Array.isArray(reg.articles)) {
            state.regulation = reg;
        } else {
            /* Niciun regulament salvat pentru acest club -> articolele standard */
            state.regulation = { version: '1.0', lastModified: new Date().toISOString(), articles: [] };
            if (typeof initRegulation === 'function') initRegulation();
        }

        /* Verificarea acceptării, DUPĂ ce regulamentul e în memorie: modalul îl
           afișează, deci n-are sens să întrebăm înainte să existe ce citi.
           Nu așteptăm rezultatul — hidratarea nu trebuie să se blocheze pe el. */
        try { if (typeof refreshRulesAckState === 'function') refreshRulesAckState(); } catch (e) { logError('hydrate rulesAck', e); }

        const camp = findVal('campaigns');
        state.campaigns = Array.isArray(camp) ? camp : [];

        /* Preferințe de profil (ex. ascunderea email-ului) — stocate aici ca să nu
           depindă de o coloană dedicată în `profiles`. Cheie = id-ul (uuid) al userului. */
        const prefs = findVal('profile_prefs');
        if (prefs && typeof prefs === 'object' && !Array.isArray(prefs)) {            Object.values((state.membersById && Object.keys(state.membersById).length) ? state.membersById : state.members).forEach(m => {
                if (m && m.dbId && prefs[m.dbId]) { m.hideEmail = !!prefs[m.dbId].hideEmail; m.profileExtra = m.profileExtra || prefs[m.dbId]; }
            });
            Object.keys(state.users).forEach(email => {
                const u = state.users[email];
                if (u && u.id && prefs[u.id]) { u.hideEmail = !!prefs[u.id].hideEmail; u.profileExtra = u.profileExtra || prefs[u.id]; }
            });
        }

        /* Locația clubului (stocată în club_settings, fără coloană dedicată). */
        const clubLoc = findVal('club_location');
        try { const _ac = (typeof activeClub === 'function') ? activeClub() : null; if (_ac) _ac.location = (clubLoc && typeof clubLoc === 'object' && !Array.isArray(clubLoc)) ? clubLoc : null; } catch (e) { logError('hydrateFromSupabase', e); }

        const reads = findVal('chat_reads');
        state.chatReads = (reads && typeof reads === 'object' && !Array.isArray(reads)) ? reads : {};
    }

    try { await loadModeration(); } catch (e) { console.warn('loadModeration:', e && e.message); }

    /* Faza 2: dacă istoricul a venit parțial (club mare, >= HISTORY_PAGE), îl completăm
       în FUNDAL — afișarea/UI sunt deja active cu ultimele meciuri; restul curge fără
       să blocheze. Doar la hydrate complet (nu skipHeavy, unde matches nici nu s-a atins). */
    if (!_skipHeavy && state._gamesPartial) {
        setTimeout(() => { try { ensureFullHistory(); } catch (e) { logError('hydrate.bgHistory', e); } }, 150);
    }
}

/* ---------- Realtime: chat ---------- */
/* ============================================================
   ===================== CENTRU NOTIFICĂRI ====================
   Derivat din datele comune (sesiuni în care joci + mesaje chat).
   „Necitit" = mai nou decât ultima deschidere a clopoțelului
   (reținută local, per dispozitiv). */
function notifSeenKey() {
    /* Cheie STABILĂ pe user (id-ul nu lipsește niciodată ca email-ul). Dacă userul
       nu e încă pregătit, întoarcem null și NU citim/scriem (altfel notificările
       citite reapăreau, fiindcă se scria sub o cheie „_x" temporară). */
    const uid = state.currentUser && (state.currentUser.id || state.currentUser.email);
    return uid ? ('gbgsc_notif_seen_ids_' + uid) : null;
}
/* ─── PRAG „CITIT PÂNĂ LA" — persistent pe CONT, nu pe browser (aug. 2026) ──────
   RO: DEFECT RAPORTAT: „Marchează tot ca citit" nu supraviețuia unei ferestre private
       sau unui browser fără cache — bulina cu numărul reapărea, deși utilizatorul
       apăsase butonul într-o sesiune anterioară. Cauza: starea „citit" trăia EXCLUSIV
       în `localStorage` (`getSeenIds`/`saveSeenIds`), care e per-dispozitiv.
       ⚠ Asimetrie care a scos defectul la iveală: butonul VECIN, „Șterge tot"
       (`clearAllNotifications`), persista deja server-side prin `notif_cleared_at` +
       `notif_dismissed`. Deci una dintre cele două acțiuni supraviețuia și cealaltă nu.
       Soluția NU cere coloană nouă (deci nici SQL rulat înainte de deploy, §52): pragul
       stă în `profile_extra.notifReadAt`, o coloană jsonb care e DEJA în `PROFILE_COLS`
       și deja se citește la login. Orice notificare mai veche decât pragul e „citită".
       Lista de id-uri din `localStorage` rămâne — e instantanee și acoperă și cazurile
       fără marcaj de timp; pragul din DB e doar plasa care traversează dispozitivele.
   EN: "mark all as read" only lived in localStorage, so it did not survive a private
       window — while the neighbouring "clear all" button already persisted server-side.
       Fixed with a `profile_extra.notifReadAt` threshold: no new column, no SQL ordering
       constraint, and anything older than the threshold counts as read on any device. */
function notifReadAtKey() {
    const uid = state.currentUser && (state.currentUser.id || state.currentUser.email);
    return uid ? ('gbgsc_notif_read_at_' + uid) : null;
}
function getNotifReadAt() {
    const key = notifReadAtKey(); if (!key) return 0;
    try { const raw = localStorage.getItem(key); const n = raw ? parseInt(raw, 10) : 0; return isNaN(n) ? 0 : n; } catch (e) { return 0; }
}
function setNotifReadAt(ts) {
    const key = notifReadAtKey(); if (!key) return;
    try { if (ts > getNotifReadAt()) localStorage.setItem(key, String(ts)); } catch (e) { logError('setNotifReadAt', e); }
}
/* O notificare e citită dacă i-am reținut id-ul SAU dacă e mai veche decât pragul.
   EN: read = id remembered locally, or older than the account-wide threshold. */
function isNotifRead(item, seen, readAt) {
    if (!item) return true;
    if (seen && seen.has(item.id)) return true;
    return !!(readAt && item.ts && item.ts <= readAt);
}
window.getNotifReadAt = getNotifReadAt;

function getSeenIds() {
    const key = notifSeenKey(); if (!key) return null;
    try { const raw = localStorage.getItem(key); return raw ? new Set(JSON.parse(raw)) : null; } catch (e) { return null; }
}
function saveSeenIds(set) {
    const key = notifSeenKey(); if (!key) return;
    try {
        let arr = Array.from(set);
        if (arr.length > 2000) arr = arr.slice(arr.length - 2000);
        localStorage.setItem(key, JSON.stringify(arr));
    } catch (e) { logError('saveSeenIds', e); }
}
/* Pe un browser fără listă locală, NU mai marcăm nimic „văzut" din oficiu.
   RO: 🔴 DEFECT RAPORTAT (aug. 2026): după logout + login, notificările NOI apăreau ca
       citite — indicatorul numeric dispărea, deși ele erau acolo, nedeschise.
       CAUZA: varianta veche a funcției ăsteia, când nu găsea o listă locală de id-uri,
       marca „văzut" TOT ce era încărcat în acel moment — inclusiv notificările sosite
       chiar atunci. Era o euristică anti-inundare, necesară pe vremea când starea
       „citit" exista DOAR în `localStorage`: fără ea, orice browser nou afișa deodată
       tot istoricul ca necitit. Problema ei e că depinde de un moment de cursă — se
       declanșa ori de câte ori cheia locală lipsea sau se schimba (fereastră privată,
       dispozitiv nou, un interval scurt la re-login în care `state.currentUser` are
       emailul dar încă nu id-ul, deci cheia diferă). De fiecare dată înghițea exact
       notificările pe care utilizatorul nu le văzuse.
       DE CE SE POATE ELIMINA ACUM: pragul de cont `profile_extra.notifReadAt` face
       treaba corect și determinist — „tot ce e mai vechi decât momentul în care am
       apăsat «Marchează tot ca citit» e citit", pe orice dispozitiv. Euristica nu mai
       apără nimic; doar ascunde adevărul.
       ⚠ CONSECINȚĂ ASUMATĂ: un cont care N-A apăsat NICIODATĂ „Marchează tot ca citit"
       va vedea, pe un browser nou, numărul REAL de notificări necitite (posibil mare)
       în loc de zero. E comportamentul corect, iar o singură apăsare pe buton îl rezolvă
       definitiv — de data asta pe cont, nu pe browser.
   EN: the old bulk-marking heuristic swallowed genuinely new notifications whenever the
       local key was missing or changed (private window, new device, a brief window at
       re-login where the storage key differs). It existed only because "read" state used
       to live solely in localStorage; the account-wide `notifReadAt` threshold now covers
       that correctly and deterministically, so the heuristic is removed. Trade-off: an
       account that never pressed "mark all as read" now sees the true unread count on a
       fresh browser — which is the honest answer, and one press fixes it for good. */
function ensureSeenInit(items) {
    const seen = getSeenIds();
    return (seen === null) ? new Set() : seen;
}
/* ⚠ DEFECT REPARAT (aug. 2026): cele patru forme erau scrise DIRECT în română
   („acum", „acum 5 min", „acum 2 h", „acum 3 zile"), deci un utilizator cu limba
   pe engleză vedea româna în panoul de notificări. `clubTimeAgoShort`
   (app-core.js) făcea deja corect același lucru, prin familia de chei `time.*` —
   aici doar aliniem. `time.daysAgo` se REFOLOSEȘTE, nu se dublează.
   EN: the four relative-time strings were hardcoded Romanian; they now use the
       same `time.*` keys that `clubTimeAgoShort` already relies on. */
function notifTimeAgo(ts) {
    if (!ts) return '';
    const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (s < 60) return t('time.justNow');
    const m = Math.floor(s / 60); if (m < 60) return t('time.minAgo').replace('{n}', m);
    const h = Math.floor(m / 60); if (h < 24) return t('time.hoursAgo').replace('{n}', h);
    const d = Math.floor(h / 24); if (d < 30) return t('time.daysAgo').replace('{n}', d);
    return new Date(ts).toLocaleDateString('en-GB');
}
/* Momentul la care o sesiune devine „noutate" pentru clopoțel.
   RO: `matches.date` e data PLANIFICATĂ, `matches.created_at` e ora scrierii. Nici
       una singură nu e suficientă:
         · doar `date`  → o sesiune notată azi pentru diseară primește o dată în
           VIITOR; `notifTimeAgo` calculează negativ, `Math.max(0,…)` scrie „chiar
           acum" zile în șir, iar sortarea o ține în capul listei. Defectul raportat.
         · doar `created_at` → la un IMPORT de istoric (backup/restore, BGG, BGStats)
           toate rândurile primesc ora importului și clopoțelul s-ar umple dintr-o
           dată cu sute de sesiuni vechi, care azi sunt filtrate corect de pragul de
           creare a contului. Regresie mai gravă decât defectul.
       Luăm cea mai TIMPURIE dintre ele: o sesiune înregistrată acum pentru diseară
       dă ora de acum (corect — chiar s-a întâmplat ceva), iar una importată din 2023
       rămâne în 2023 și cade sub prag, ca până acum.
   ⚠ Fără `created_at` (migrarea încă nerulată) se păstrează comportamentul de azi,
     dar plafonat la momentul curent, ca eticheta să nu mai fie „chiar acum" la
     nesfârșit. Degradare curată, fără dependență de ordinea deploy-ului.
   EN: earliest of write time and planned date — fixes future-dated sessions without
       letting historical imports flood the bell. */
function _sessionNotifTs(createdAtMs, plannedMs) {
    const planned = Number(plannedMs) || 0;
    const created = Number(createdAtMs) || 0;
    if (created) return planned ? Math.min(created, planned) : created;
    return planned ? Math.min(planned, Date.now()) : 0;
}
window._sessionNotifTs = _sessionNotifTs;

function buildNotifications() {
    if (!state.currentUser) return [];
    const me = state.currentUser.name;
    const floorTs = state.currentUser.createdAt ? new Date(state.currentUser.createdAt).getTime() : 0;
    const items = [];
    /* Sesiuni înregistrate în club (marcăm dacă ai jucat tu). */
    const _meId = state.currentUser.id;
    (state.games || []).forEach(g => {
        const ts = _sessionNotifTs(g.createdAt, new Date(g.timestamp).getTime());
        if (!ts || ts < floorTs || g.id == null) return;
        const iPlayed = Array.isArray(g.participants) && g.participants.includes(_meId);
        const by = (g.recordedBy && g.recordedBy !== '—') ? g.recordedBy : null;
        const byName = by ? ((typeof displayNameById === 'function' && displayNameById(by)) || by) : null;
        let text;
        if (iPlayed && by && by !== _meId) text = window.t('notif.recByOther').replace('{by}', byName).replace('{game}', g.gameName);
        else if (by === _meId) text = window.t('notif.recByMe').replace('{game}', g.gameName);
        else if (iPlayed) text = window.t('notif.recIPlayed').replace('{game}', g.gameName);
        else text = `${byName ? byName + ' ' + window.t('notif.recordedVerb') : window.t('notif.recordedImpersonal')} ${window.t('notif.sessionOf')} ${g.gameName}`;
        items.push({ id: 's' + g.id, ts, icon: 'fa-dice', text });
    });
    /* Evenimente de club */
    (state.events || []).forEach(e => {
        const ts = e.createdAt ? new Date(e.createdAt).getTime() : (e.date ? new Date(e.date).getTime() : Date.now());
        if (!ts || ts < floorTs) return;
        const when = e.date ? (' · ' + new Date(e.date).toLocaleDateString('en-GB')) : '';
        items.push({ id: 'e' + e.id, ts, icon: 'fa-calendar', text: `${window.t('notif.eventPrefix')}: ${e.title}${when}` });
    });
    /* Mesaje de chat de la alții */
    (state.chatMessages || []).forEach(msg => {
        if ((msg.senderId && state.currentUser && msg.senderId === state.currentUser.id) || msg.sender === me) return;
        if (msg.deletedAt) return;   /* v9 B2a: mesaj șters ⇒ fără rândul gol „Nume: " / no empty bell row */
        const ts = new Date(msg.timestamp).getTime();
        if (!ts || ts < floorTs) return;
        const tx = (msg.text || '');
        items.push({ id: 'm' + msg.id, ts, icon: 'fa-comment', text: `${msg.sender}: ${tx.slice(0, 60)}${tx.length > 60 ? '…' : ''}` });
    });
    return items.filter(x => x.id != null).sort((a, b) => b.ts - a.ts);
}

/* Notificări pe USER (din TOATE cluburile), nu pe club. */
/* Trimite notificări push cross-device către unul/mai mulți destinatari (id-uri de profil).
   Apare pe orice dispozitiv: la deschidere (loadAllNotifications) + în timp real (subscribeRealtimeData). */
async function pushNotification(recipientIds, opts) {
    try {
        if ((typeof isDemoMode === 'function' && isDemoMode()) || !sb) return;
        opts = opts || {};
        const seen = {};
        const ids = (Array.isArray(recipientIds) ? recipientIds : [recipientIds])
            .filter(id => id && !seen[id] && (seen[id] = 1));
        if (!ids.length) return;
        const now = new Date().toISOString();
        const rows = ids.map(rid => ({
            /* ⚠ `opts.clubId || currentClubId` NU permitea trimiterea unei
               notificări FĂRĂ club: `null` e o valoare falsă, deci ar fi căzut tot
               pe clubul curent. Verificăm dacă proprietatea a fost dată explicit —
               altfel o notificare care nu aparține niciunui club (rezolvarea unui
               raport, de exemplu) moștenește clubul expeditorului și îl aruncă pe
               destinatar într-un club străin la apăsare.
               EN: `||` could not express "no club"; check for explicit presence. */
            club_id: (opts && Object.prototype.hasOwnProperty.call(opts, 'clubId')) ? (opts.clubId || null) : (currentClubId || null),
            recipient_id: rid,
            type: opts.type || 'info',
            text: opts.text || '',
            icon: opts.icon || 'fa-bell',
            tab: opts.tab || null,
            data: opts.data || null,
            created_at: now
        }));
        const r = await sb.from('notifications').insert(rows);
        if (!r.error) return;

        /* ⚠ FRAGILITATE REALĂ, nu ipoteză: politica `notif_insert_scoped` cere
             `club_id IS NOT NULL` ȘI ca DESTINATARUL să fie membru ACTIV al acelui
             club. Toate rândurile plecau într-un SINGUR `insert`, deci era de ajuns
             ca UN destinatar să fie inactiv, exclus sau din alt club, ca inserarea
             ÎNTREAGĂ să fie respinsă — și nimeni să nu primească notificarea.
             Un membru care tocmai a plecat din club anula anunțul pentru toți.
           ⚠ Reîncercăm rând cu rând: cei eligibili primesc notificarea, restul cad
             individual. Ineficient, dar rulează DOAR pe calea de eroare.
           ⚠ Raportăm în Sentry `club_id`/`recipient_id`/`type`, NU și `text`:
             textul poate conține nume sau conținut de mesaj. Fără datele astea,
             defectul rămâne invizibil — exact situația în care am fost azi, cu o
             eroare care nu s-a mai putut reproduce.
           EN: a single ineligible recipient rejected the whole batch; retry per row
               so eligible recipients still get notified, and report enough context
               to diagnose it next time (never the message text). */
        const esuate = [];
        for (const row of rows) {
            const one = await sb.from('notifications').insert([row]);
            if (one.error) esuate.push({ club_id: row.club_id, recipient_id: row.recipient_id, type: row.type, err: one.error.message });
        }
        if (esuate.length) {
            const detaliu = 'notificări respinse: ' + esuate.length + '/' + rows.length
                + ' | club_id=' + (esuate[0].club_id === null ? 'null' : 'prezent')
                + ' | type=' + esuate[0].type
                + ' | motiv=' + esuate[0].err;
            logError('pushNotification', new Error(detaliu), { report: true });
        }
    } catch (e) { logError('pushNotification', e); }
}
window.pushNotification = pushNotification;

/* ===== RAPORTARE CONȚINUT / UTILIZATORI (DSA UE + takedown copyright) =====
   Modal reutilizabil apelat din: profil public (social.js), meniu mesaj chat
   (app-modules-2.js), meniu mesaj DM (social.js), lightbox poze (app-core.js).
   Scrie în tabela `reports` (RLS strict: INSERT doar reporter pentru sine).
   target_type: 'user' | 'chat_msg' | 'dm' | 'photo'. target_id = text (id mesaj
   bigint -> String, id profil uuid, id meci -> String). club_id poate fi null (DM). */
const REPORT_REASONS = ['spam', 'harassment', 'sexual', 'hate', 'copyright', 'misinfo', 'other'];

window.openReportModal = function (targetType, targetId, opts) {
    opts = opts || {};
    if (typeof isDemoMode === 'function' && isDemoMode()) { showNotification(t('report.demo'), 'info'); return; }
    if (!state.currentUser || !state.currentUser.id) { showNotification(t('report.loginRequired'), 'error'); return; }
    const esc = (typeof window.escHtml === 'function') ? window.escHtml : function (s) { return s == null ? '' : String(s); };
    const aj = (typeof window.attrJs === 'function') ? window.attrJs : function (s) { return s == null ? '' : String(s); };
    /* ⚠ VARIANTA A (iulie 2026): un raport despre o PERSOANĂ e întotdeauna de
       PLATFORMĂ, niciodată de club.
       Clubul răspunde de CONȚINUTUL din club (mesaje, poze); platforma răspunde
       de OAMENI. Din perspectiva DSA, furnizorul serviciului e platforma, nu
       clubul.
       Forțăm aici, la SURSĂ, nu în fiecare apelant: cardul de profil public e
       același obiect folosit în Discover Players, prieteni și lista de membri.
       Dacă aș fi pasat `{clubId:null}` doar din locurile știute azi, fiecare loc
       NOU care randează cardul ar fi devenit o ocazie de a greși din nou —
       exact tiparul „listă scrisă de mână" care ne-a costat de patru ori.
       Bug-ul reparat: `reportIconBtn('user', id)` din social.js nu trimitea
       `opts`, deci se stampila `currentClubId`. Rezultat: TOȚI administratorii
       clubului tău puteau citi (și închide) un raport despre doi străini —
       `reports_select_scoped` permite `club_id IS NOT NULL AND rank >= 3`.
       Cu `club_id = NULL` îl văd doar raportorul și owner-ul de platformă, iar
       `notify_new_report` notifică doar owner-ii (are deja
       `where new.club_id is not null` pe ramura de club).
       ⚠ Clientul e DOAR UX — regula e impusă și în politica de INSERT.
       EN: reports about PEOPLE are always platform-level. Forced at the single
       choke point so no future call site can get it wrong. */
    const clubId = (targetType === 'user')
        ? null
        : ((opts.clubId !== undefined) ? opts.clubId : (currentClubId || null));
    const existing = document.getElementById('reportModal'); if (existing) existing.remove();
    const modal = document.createElement('div');
    modal.className = 'modal active'; modal.id = 'reportModal';
    modal.style.zIndex = '100010';
    modal.onclick = function (e) { if (e.target === modal) modal.remove(); };
    const reasonOpts = REPORT_REASONS.map(function (r) {
        return '<label class="report-reason"><input type="radio" name="reportReason" value="' + r + '"><span>' + esc(t('report.reason.' + r)) + '</span></label>';
    }).join('');
    const ctx = opts.contextLabel ? '<div class="report-ctx">' + esc(opts.contextLabel) + '</div>' : '';
    const clubArg = clubId ? "'" + aj(String(clubId)) + "'" : 'null';
    modal.innerHTML =
        '<div class="modal-content" style="max-width:480px;">'
        + '<div class="modal-header"><h2><i class="fas fa-flag"></i> ' + esc(t('report.title')) + '</h2>'
        + '<button class="modal-close" onclick="this.closest(\'.modal\').remove()">×</button></div>'
        + ctx
        + '<p style="color:var(--text-secondary); font-size:0.9em; margin-bottom:12px;">' + esc(t('report.intro')) + '</p>'
        + '<div class="report-reasons">' + reasonOpts + '</div>'
        + '<textarea id="reportCustom" class="form-control" rows="3" maxlength="1000" placeholder="' + esc(t('report.customPh')) + '" style="margin-top:12px;"></textarea>'
        + '<p style="color:var(--text-secondary); font-size:0.78em; margin-top:10px;">' + esc(t('report.dsaNote').replace('{email}', 'contact@turnorder.org')) + '</p>'
        + '<div style="display:flex; gap:10px; margin-top:16px;">'
        +   '<button class="btn btn-secondary" onclick="this.closest(\'.modal\').remove()">' + esc(t('cm.cancel')) + '</button>'
        +   '<button class="btn btn-danger" onclick="submitReport(\'' + aj(targetType) + '\',\'' + aj(String(targetId)) + '\',' + clubArg + ')"><i class="fas fa-flag"></i> ' + esc(t('report.submit')) + '</button>'
        + '</div></div>';
    document.body.appendChild(modal);
};

window.submitReport = async function (targetType, targetId, clubId) {
    /* A doua plasă: `clubId` ajunge aici prin `onclick`, deci ar putea fi
       modificat din DOM. Regula se reaplică. */
    if (targetType === 'user') clubId = null;
    const sel = document.querySelector('#reportModal input[name="reportReason"]:checked');
    if (!sel) { showNotification(t('report.pickReason'), 'error'); return; }
    const custom = (document.getElementById('reportCustom') && document.getElementById('reportCustom').value) || '';
    try {
        const r = await sb.from('reports').insert({
            reporter_id: state.currentUser.id,
            target_type: targetType,
            target_id: String(targetId),
            club_id: clubId || null,
            reason_key: sel.value,
            custom_text: custom.trim() ? custom.trim().slice(0, 1000) : null,
            status: 'open'
        });
        if (r.error) { showNotification(t('report.failed') + (r.error.message || ''), 'error'); return; }
        const m = document.getElementById('reportModal'); if (m) m.remove();
        showNotification(t('report.sent'), 'success');
        /* Oferta de blocare, imediat dupa raportare. Momentul de intentie maxima.
           ⚠ DOAR pentru `targetType === 'user'`: un raport pe un mesaj, o poza sau
             un anunt nu implica nimic despre relatia cu autorul.
           ⚠ Dupa `m.remove()`, ca modalul de confirmare sa nu se suprapuna peste
             cel de raportare. */
        if (targetType === 'user' && typeof window.blockOfferAfterReport === 'function') {
            try { window.blockOfferAfterReport(targetId); } catch (e2) { logError('submitReport', e2); }
        }
    } catch (e) { showNotification(t('report.failed') + (e && e.message ? e.message : ''), 'error'); }
};

/* Buton-iconiță reutilizabil „Raportează" — mic, subtil, doar iconiță (ca la poze),
   împins în lateral (margin-left:auto din CSS) ca să nu fie apăsat din greșeală
   lângă DM/Prieten. opts.clubId: undefined = clubul curent, null = fără club (DM). */
window.reportIconBtn = function (targetType, targetId, opts) {
    if (typeof window.openReportModal !== 'function') return '';
    opts = opts || {};
    const aj = (typeof window.attrJs === 'function') ? window.attrJs : function (s) { return s == null ? '' : String(s); };
    let call = "openReportModal('" + aj(targetType) + "','" + aj(String(targetId)) + "'";
    if (opts.clubId === null) call += ',{clubId:null}';
    else if (opts.clubId) call += ",{clubId:'" + aj(String(opts.clubId)) + "'}";
    call += ')';
    const title = (typeof window.t === 'function') ? window.t('report.action') : 'Report';
    return '<button class="report-icon-btn" title="' + title + '" aria-label="' + title + '" onclick="' + call + '"><i class="fas fa-flag"></i></button>';
};

/* ===== PANOU DE MODERARE (DSA) — listă rapoarte + workflow status + Mute/Ban =====
   Gated `can('moderation.view')` = Admin secundar+ (rang 3). Owner platformă vede tot
   (toate cluburile + DM-urile) prin panoul owner. Ștergerea efectivă de conținut
   (mesaj/DM/poză) vine în Batch 1b-ii printr-un RPC privilegiat. */
const MOD_STATUS_FILTERS = ['open', 'reviewing', 'resolved', 'dismissed', 'all'];

function _modTime(iso) {
    try { return new Date(iso).toLocaleString(); } catch (e) { return ''; }
}

/* ============================================================================
   NUMĂRUL DE RAPOARTE DESCHISE — pentru indicatorul din meniu
   ----------------------------------------------------------------------------
   RO: Rapoartele NU sunt hidratate în `state` (se citesc la deschiderea
       panoului), deci nu puteam pune un badge fără nicio interogare. Aici e o
       numărătoare cu `head: true, count: 'exact'` — serverul întoarce DOAR
       numărul, zero rânduri transferate.
       Se apelează dintr-o ACȚIUNE a utilizatorului (deschiderea unui hub),
       strangulată în `nav.js` la 30s. Nu e polling și nu e abonament nou (§36).
       Gardat pe `moderation.view`, deci un membru nu poate afla câte rapoarte
       are clubul.
   EN: reports are not hydrated; this is a head-only exact count, user-triggered
       and throttled, gated on `moderation.view`.
   ============================================================================ */
async function refreshOpenReportsBadge() {
    try {
        if (!sb || !currentClubId) return;
        if (typeof can === 'function' && !can('moderation.view')) return;
        state.openReportsByClub = state.openReportsByClub || {};
        const r = await sb.from('reports')
            .select('id', { count: 'exact', head: true })
            .eq('club_id', currentClubId)
            .eq('status', 'open');
        if (r && r.error) { logError('refreshOpenReportsBadge', r.error); return; }
        state.openReportsByClub[currentClubId] = (r && typeof r.count === 'number') ? r.count : 0;
    } catch (e) { logError('refreshOpenReportsBadge', e); }
}
window.refreshOpenReportsBadge = refreshOpenReportsBadge;

async function _modFetchReports(scope) {
    if (!sb) return { error: 'no-sb', rows: [] };
    try {
        let q = sb.from('reports').select('*').order('created_at', { ascending: false }).limit(200);
        if (scope === 'club') q = q.eq('club_id', currentClubId);
        const r = await q;
        if (r.error) return { error: r.error.message, rows: [] };
        return { error: null, rows: r.data || [] };
    } catch (e) { return { error: (e && e.message) || 'error', rows: [] }; }
}

async function _modResolveNames(rows) {
    const need = new Set();
    rows.forEach(r => {
        if (r.reporter_id) need.add(r.reporter_id);
        if (r.target_type === 'user' && r.target_id) need.add(r.target_id);
    });
    const map = {};
    if (need.size) {
        try {
            const nres = await sb.rpc('names_for_ids', { p_ids: Array.from(need) });
            if (nres && !nres.error && Array.isArray(nres.data)) nres.data.forEach(x => { if (x && x.id) map[x.id] = x.name || ''; });
        } catch (e) { logError('_modResolveNames', e); }
    }
    return map;
}

function _modFilterBar(scope, active) {
    const aj = (typeof window.attrJs === 'function') ? window.attrJs : function (s) { return s; };
    return '<div class="mod-filterbar">' + MOD_STATUS_FILTERS.map(function (s) {
        const label = t('mod.filter' + s.charAt(0).toUpperCase() + s.slice(1));
        return '<button class="mod-chip ' + (s === active ? 'is-on' : '') + '" onclick="moderationFilterSet(\'' + aj(scope) + '\',\'' + s + '\')">' + label + '</button>';
    }).join('') + '</div>';
}

function _modRowHtml(r, names, scope) {
    const esc = (typeof window.escHtml === 'function') ? window.escHtml : function (s) { return s == null ? '' : String(s); };
    const aj = (typeof window.attrJs === 'function') ? window.attrJs : function (s) { return s == null ? '' : String(s); };
    const reporter = esc(names[r.reporter_id] || (r.reporter_id ? String(r.reporter_id).slice(0, 8) : '—'));
    const reason = esc(t('report.reason.' + r.reason_key) || r.reason_key);
    const tgtLabel = { user: t('mod.targetUser'), chat_msg: t('mod.targetChat'), dm: t('mod.targetDm'), photo: t('mod.targetPhoto'), ann: t('mod.targetAnn') }[r.target_type] || esc(r.target_type);
    const tgtName = (r.target_type === 'user') ? esc(names[r.target_id] || '') : '';
    const custom = r.custom_text ? '<div class="mod-custom">' + esc(r.custom_text) + '</div>' : '';
    const statusKey = 'mod.status' + r.status.charAt(0).toUpperCase() + r.status.slice(1);
    const badge = '<span class="mod-badge mod-' + r.status + '">' + t(statusKey) + '</span>';
    const rid = aj(String(r.id));
    const reporterArg = r.reporter_id ? "'" + aj(String(r.reporter_id)) + "'" : 'null';

    /* Vezi ținta */
    let viewBtn = '';
    if (r.target_type === 'user') viewBtn = '<button class="btn btn-sm btn-secondary" onclick="openPublicProfileById(\'' + aj(String(r.target_id)) + '\')"><i class="fas fa-eye"></i> ' + t('mod.view') + '</button>';
    else if (r.target_type === 'chat_msg') viewBtn = '<button class="btn btn-sm btn-secondary" onclick="moderationViewChat(\'' + aj(String(r.target_id)) + '\')"><i class="fas fa-eye"></i> ' + t('mod.view') + '</button>';
    else if (r.target_type === 'photo') viewBtn = '<button class="btn btn-sm btn-secondary" onclick="moderationViewPhoto(\'' + aj(String(r.target_id)) + '\')"><i class="fas fa-eye"></i> ' + t('mod.view') + '</button>';
    /* ⚠ Fără acest buton, moderatorul primea DOAR „Șterge anunțul" și un id —
       adică ștergea în orb. Toate celelalte tipuri au vizualizare; `ann` nu avea,
       fiindcă am adăugat inițial doar acțiunea de ștergere. */
    else if (r.target_type === 'ann') viewBtn = '<button class="btn btn-sm btn-secondary" onclick="moderationViewAnn(\'' + aj(String(r.target_id)) + '\',' + (r.club_id ? "'" + aj(String(r.club_id)) + "'" : 'null') + ')"><i class="fas fa-eye"></i> ' + t('mod.view') + '</button>';

    /* Mute/Ban pe ofensator — doar dacă raportul e din clubul curent (mute/ban operează pe currentClubId) */
    let offenderId = null, offenderName = '';
    if (r.target_type === 'user') { offenderId = r.target_id; offenderName = names[r.target_id] || ''; }
    else if (r.target_type === 'chat_msg') {
        const cm = (state.chatMessages || []).find(m => String(m.id) === String(r.target_id));
        if (cm && cm.senderId) { offenderId = cm.senderId; offenderName = cm.sender || ''; }
    }
    let muteBan = '';
    if (offenderId && r.club_id && String(r.club_id) === String(currentClubId)
        && offenderId !== (state.currentUser && state.currentUser.id)
        && typeof can === 'function' && can('member.ban')) {
        muteBan = '<button class="btn btn-sm" onclick="moderationMute(\'' + aj(String(offenderId)) + '\',\'' + aj(offenderName) + '\')"><i class="fas fa-volume-xmark"></i> ' + t('mod.mute') + '</button>'
            + '<button class="btn btn-sm btn-danger" onclick="moderationBan(\'' + aj(String(offenderId)) + '\',\'' + aj(offenderName) + '\')"><i class="fas fa-ban"></i> ' + t('mod.ban') + '</button>';
    }

    const statusBtns =
        '<button class="btn btn-sm btn-secondary" onclick="moderationSetStatus(\'' + rid + '\',\'reviewing\',\'' + aj(r.reason_key) + '\',' + reporterArg + ')">' + t('mod.markReviewing') + '</button>'
        + '<button class="btn btn-sm" style="background:var(--success,#27ae60); color:#fff;" onclick="moderationSetStatus(\'' + rid + '\',\'resolved\',\'' + aj(r.reason_key) + '\',' + reporterArg + ')">' + t('mod.markResolved') + '</button>'
        + '<button class="btn btn-sm btn-secondary" onclick="moderationSetStatus(\'' + rid + '\',\'dismissed\',\'' + aj(r.reason_key) + '\',' + reporterArg + ')">' + t('mod.markDismissed') + '</button>';

    /* Takedown — ștergere efectivă de conținut (RPC privilegiat, rang>=3 sau owner). */
    const clubArg = r.club_id ? "'" + aj(String(r.club_id)) + "'" : 'null';
    let takedown = '';
    if (r.target_type === 'chat_msg') {
        takedown = '<button class="btn btn-sm btn-danger" onclick="moderationDeleteMessage(\'' + aj(String(r.target_id)) + '\',' + clubArg + ')"><i class="fas fa-trash"></i> ' + t('mod.delMsg') + '</button>';
    } else if (r.target_type === 'photo') {
        takedown = '<button class="btn btn-sm btn-danger" onclick="moderationPhotoManage(\'' + aj(String(r.target_id)) + '\',' + clubArg + ')"><i class="fas fa-images"></i> ' + t('mod.managePhotos') + '</button>';
    } else if (r.target_type === 'ann') {
        /* ⚠ NU `deleteAnnouncement`: aceea e funcția obișnuită de utilizator,
           supusă RLS (`announcements_delete_admin` cere rang >= 3 ÎN CLUBUL
           respectiv). Owner-ul platformei nu e membru acolo, deci ștergerea
           returna ZERO rânduri FĂRĂ eroare — eșec tăcut, exact ce a raportat
           testerul. RPC-ul dedicat urmează modelul lui `moderator_delete_message`.
           EN: the user-facing delete is RLS-bound and silently affected no rows
               for the platform owner; this uses the moderation RPC instead. */
        takedown = '<button class="btn btn-sm btn-danger" onclick="moderationDeleteAnn(\'' + aj(String(r.target_id)) + '\',' + clubArg + ')"><i class="fas fa-trash"></i> ' + t('mod.delAnn') + '</button>';
    } else if (r.target_type === 'dm' && typeof isPlatformOwner === 'function' && isPlatformOwner()) {
        takedown = '<button class="btn btn-sm btn-danger" onclick="moderationDeleteDm(\'' + aj(String(r.target_id)) + '\')"><i class="fas fa-trash"></i> ' + t('mod.delDm') + '</button>';
    }

    const dmNote = (r.target_type === 'dm' && !(typeof isPlatformOwner === 'function' && isPlatformOwner())) ? '<div class="mod-custom" style="opacity:.7;"><i class="fas fa-circle-info"></i> ' + t('mod.dmNote') + '</div>' : '';

    return '<div class="mod-report">'
        + '<div class="mod-report-top"><span class="mod-reason"><i class="fas fa-flag"></i> ' + reason + '</span>' + badge + '</div>'
        + '<div class="mod-meta">' + tgtLabel + (tgtName ? ': <strong>' + tgtName + '</strong>' : '') + ' · ' + t('mod.from').replace('{name}', reporter) + ' · ' + esc(_modTime(r.created_at)) + '</div>'
        + custom + dmNote
        + '<div class="mod-actions">' + viewBtn + takedown + muteBan + statusBtns + '</div>'
        + '</div>';
}

async function _modRender(scope) {
    const containerId = scope === 'owner' ? 'ownerModerationBox' : 'moderationPanel';
    const el = document.getElementById(containerId);
    if (!el) return;
    if (scope === 'club' && typeof can === 'function' && !can('moderation.view')) { el.innerHTML = ''; return; }
    const filter = scope === 'owner' ? (window._modFilterOwner || 'open') : (window._modFilter || 'open');
    const listId = scope === 'owner' ? 'modListOwner' : 'modListClub';
    el.innerHTML = _modFilterBar(scope, filter) + '<div id="' + listId + '" style="margin-top:10px;"><div style="opacity:.6;">…</div></div>';
    const res = await _modFetchReports(scope === 'owner' ? 'all' : 'club');
    const list = document.getElementById(listId);
    if (!list) return;
    if (res.error) { list.innerHTML = '<div style="opacity:.7;">' + (typeof window.escHtml === 'function' ? window.escHtml(t('mod.loadError') + res.error) : t('mod.loadError')) + '</div>'; return; }
    const filtered = (filter === 'all') ? res.rows : res.rows.filter(r => r.status === filter);
    if (!filtered.length) { list.innerHTML = '<div style="opacity:.7;">' + t('mod.none') + '</div>'; return; }
    const names = await _modResolveNames(filtered);
    list.innerHTML = filtered.map(r => _modRowHtml(r, names, scope)).join('');
}

function renderModerationPanel() { _modRender('club'); }
function renderOwnerModeration() { _modRender('owner'); }

window.moderationFilterSet = function (scope, status) {
    if (scope === 'owner') { window._modFilterOwner = status; renderOwnerModeration(); }
    else { window._modFilter = status; renderModerationPanel(); }
};

window.moderationSetStatus = async function (reportId, status, reasonKey, reporterId) {
    try {
        const patch = { status: status };
        if (status === 'resolved' || status === 'dismissed') {
            patch.resolved_by = (state.currentUser && state.currentUser.id) || null;
            patch.resolved_at = new Date().toISOString();
        }
        const r = await sb.from('reports').update(patch).eq('id', reportId);
        if (r.error) { showNotification(t('mod.loadError') + r.error.message, 'error'); return; }
        /* DSA: notifică reporter-ul despre soluționare/respingere. Flux distinct pt. copyright. */
        if ((status === 'resolved' || status === 'dismissed') && reporterId && reporterId !== 'null') {
            let key = status === 'resolved' ? 'modnotif.resolved' : 'modnotif.dismissed';
            if (status === 'resolved' && reasonKey === 'copyright') key = 'modnotif.copyright';
            /* ⚠ `clubId: null` EXPLICIT. `pushNotification` cade altfel pe
               `currentClubId` — adică pe clubul în care se afla OWNER-UL când a
               rezolvat raportul, care n-are nicio legătură cu raportul și în care
               destinatarul poate să nu fie membru. La apăsare, `openNotif` încerca
               `switchClub` acolo și utilizatorul ajungea într-un club străin,
               afișat pe jumătate, cu numele implicit „Club".
               Un raport nu aparține niciunui club: raportul de mesaj privat chiar
               n-are club, iar cel de club îl vede owner-ul, nu raportorul.
               EN: report notifications belong to no club; without an explicit null
                   they inherited the owner's current club and hijacked the user. */
            try { if (typeof pushNotification === 'function') pushNotification([reporterId], { type: 'report_status', icon: 'fa-flag', text: t(key), clubId: null }); } catch (e) { logError('renderOwnerModeration', e); }
        }
        showNotification(t('mod.statusUpdated'), 'success');
        /* Numărul din meniu se actualizează IMEDIAT după ce tu închizi un raport —
           fără să aștepți strangularea de 30s sau o reîncărcare de pagină. */
        try {
            if (typeof refreshOpenReportsBadge === 'function') {
                refreshOpenReportsBadge().then(function () {
                    try { if (window.TO_NAV && TO_NAV.refresh) TO_NAV.refresh(); } catch (e) { logError('modStatus.nav', e); }
                });
            }
        } catch (e) { logError('modStatus.badge', e); }
        if (document.getElementById('moderationPanel')) renderModerationPanel();
        if (document.getElementById('ownerModerationBox')) renderOwnerModeration();
    } catch (e) { showNotification(t('mod.loadError') + (e && e.message || ''), 'error'); }
};

window.moderationViewChat = function (id) {
    try { if (typeof switchTab === 'function') switchTab('chat'); setTimeout(function () { if (typeof scrollToMessage === 'function') scrollToMessage(id); }, 300); } catch (e) { logError('renderOwnerModeration', e); }
};
window.moderationViewPhoto = function (targetId) {
    /* target_id poate fi "sesiuneId#path" (poza specifică) sau doar "sesiuneId" (reporturi vechi). */
    var s = String(targetId || '');
    var h = s.indexOf('#');
    var matchId = h === -1 ? s : s.slice(0, h);
    var photo = h === -1 ? null : s.slice(h + 1);
    if (photo && typeof openLightbox === 'function') { openLightbox(photo); return; }   // poza exact raportată
    const g = (state.games || []).find(x => String(x.id) === String(matchId));
    if (g && g.photos && g.photos.length && typeof openLightbox === 'function') openLightbox(g.photos[0]);
    else showNotification(t('mod.none'), 'info');
};
window.moderationMute = function (pid, name) { if (typeof muteMember === 'function') muteMember(pid, name); };
window.moderationBan = function (pid, name) { if (typeof banMember === 'function') banMember(pid, name); };

/* Takedown conținut — prin RPC privilegiate (SECURITY DEFINER, rang>=3 sau owner). */
function _modRefreshPanels() {
    if (document.getElementById('moderationPanel')) renderModerationPanel();
    if (document.getElementById('ownerModerationBox')) renderOwnerModeration();
}
window.moderationDeleteMessage = async function (msgId, clubId) {
    if (!await confirmModal(t('mod.confirmDelMsg'), { danger: true })) return;
    try {
        const r = await sb.rpc('moderator_delete_message', { p_id: msgId, p_club: clubId || null });
        if (r.error) { showNotification(t('mod.takedownFail') + r.error.message, 'error'); return; }
        state.chatMessages = (state.chatMessages || []).filter(m => String(m.id) !== String(msgId));
        showNotification(t('mod.takedownOk'), 'success');
        _modRefreshPanels();
    } catch (e) { showNotification(t('mod.takedownFail') + (e && e.message || ''), 'error'); }
};
/* ===== Moderare anunțuri (§8.9 extensii) ==================================
   Ambele trec prin RPC-uri `SECURITY DEFINER`, ca surorile lor pentru mesaje și
   DM. Motivul e concret: RLS-ul de pe `announcements` cere apartenență la club
   (`announcements_select`) și rang >= 3 pentru ștergere. Owner-ul platformei nu e
   membru al clubului raportat, deci pe calea obișnuită NU putea nici să citească
   anunțul, nici să-l șteargă — ștergerea returna zero rânduri fără eroare.
   EN: both go through moderation RPCs; the RLS path silently did nothing for the
       platform owner, who is not a member of the reported club. */
window.moderationViewAnn = async function (annId, clubId) {
    const esc = (typeof window.escHtml === 'function') ? window.escHtml : function (s) { return s == null ? '' : String(s); };
    try {
        const r = await sb.rpc('moderator_view_announcement', { p_id: Number(annId), p_club: clubId || null });
        if (r.error) { showNotification(t('mod.viewFail') + r.error.message, 'error'); return; }
        const a = Array.isArray(r.data) ? r.data[0] : r.data;
        if (!a) { showNotification(t('mod.annGone'), 'info'); return; }

        /* Numele autorului prin RPC-ul folosit deja de panoul de moderare —
           funcția de vizualizare întoarce doar `author_id`, ca să nu citească
           din `profiles` cu drepturi ridicate. */
        let autor = '';
        try {
            const nres = await sb.rpc('names_for_ids', { p_ids: [a.author_id] });
            if (nres && !nres.error && Array.isArray(nres.data) && nres.data[0]) autor = nres.data[0].name || '';
        } catch (e) { logError('moderationViewAnn', e); }

        const imgs = Array.isArray(a.images) ? a.images : [];
        /* Pozele primesc `data-sp` (semnare automată) și `data-gal` (galerie),
           ca peste tot altundeva. */
        const grid = imgs.length
            ? '<div class="ann-photos" style="margin-top:10px;">' + imgs.map(function (u) {
                return '<img data-sp="' + esc(String(u)) + '" data-spt="1" data-gal="' + esc(String(u)) + '" src="' + (window._SP_PH || '') + '" loading="lazy" class="ann-photo" onclick="if(typeof openLightbox===\'function\') openLightbox(\'' + window.attrJs(String(u)) + '\', null, this)">';
              }).join('') + '</div>'
            : '';

        const existing = document.getElementById('modAnnModal'); if (existing) existing.remove();
        const modal = document.createElement('div');
        modal.className = 'modal active'; modal.id = 'modAnnModal';
        modal.style.zIndex = '100010';
        if (modal.dataset) modal.dataset.dynamic = '1';
        modal.onclick = function (e) { if (e.target === modal) modal.remove(); };
        modal.innerHTML = '<div class="modal-content" style="max-width:560px;">'
            + '<div class="modal-header"><h2><i class="fas fa-bullhorn"></i> ' + esc(t('mod.targetAnn')) + '</h2>'
            + '<button class="modal-close" data-action="mod-ann-close">×</button></div>'
            + '<div class="mod-meta" style="margin-bottom:8px;">' + esc(autor || '—') + ' · ' + esc(_modTime(a.created_at)) + '</div>'
            + (a.title ? '<div class="ann-item-title">' + esc(a.title) + '</div>' : '')
            + '<div class="ann-body" style="white-space:pre-wrap;">' + window.linkifyEscaped(esc(a.body || '')) + '</div>'
            + grid
            + '</div>';
        modal.addEventListener('click', function (ev) {
            const el = ev.target && ev.target.closest ? ev.target.closest('[data-action="mod-ann-close"]') : null;
            if (el) { ev.preventDefault(); modal.remove(); }
        });
        document.body.appendChild(modal);
    } catch (e) { showNotification(t('mod.viewFail') + (e && e.message || ''), 'error'); }
};

window.moderationDeleteAnn = async function (annId, clubId) {
    if (!await confirmModal(t('mod.confirmDelAnn'), { danger: true })) return;
    try {
        const r = await sb.rpc('moderator_delete_announcement', { p_id: Number(annId), p_club: clubId || null });
        if (r.error) { showNotification(t('mod.takedownFail') + r.error.message, 'error'); return; }
        state.announcements = (state.announcements || []).filter(a => String(a.id) !== String(annId));
        try { renderAnnouncements(); } catch (e) { logError('moderationDeleteAnn', e); }
        showNotification(t('mod.takedownOk'), 'success');
        _modRefreshPanels();
    } catch (e) { showNotification(t('mod.takedownFail') + (e && e.message || ''), 'error'); }
};

window.moderationDeleteDm = async function (dmId) {
    if (!await confirmModal(t('mod.confirmDelDm'), { danger: true })) return;
    try {
        const r = await sb.rpc('moderator_delete_dm', { p_id: dmId });
        if (r.error) { showNotification(t('mod.takedownFail') + r.error.message, 'error'); return; }
        showNotification(t('mod.takedownOk'), 'success');
        _modRefreshPanels();
    } catch (e) { showNotification(t('mod.takedownFail') + (e && e.message || ''), 'error'); }
};
window.moderationPhotoManage = async function (matchId, clubId) {
    const esc = (typeof window.escHtml === 'function') ? window.escHtml : function (s) { return s == null ? '' : String(s); };
    const aj = (typeof window.attrJs === 'function') ? window.attrJs : function (s) { return s == null ? '' : String(s); };
    /* matchId poate fi "sesiuneId#path" — extrag sesiunea + path-ul poza raportate (evidențiere). */
    const _mid = String(matchId || '');
    const _h = _mid.indexOf('#');
    const sessId = _h === -1 ? _mid : _mid.slice(0, _h);
    const repPath = _h === -1 ? '' : ((typeof window._spPath === 'function') ? window._spPath(_mid.slice(_h + 1)) : _mid.slice(_h + 1));
    matchId = sessId;
    let photos = [];
    const g = (state.games || []).find(x => String(x.id) === String(matchId));
    if (g && Array.isArray(g.photos)) photos = g.photos.slice();
    else { try { const r = await sb.from('matches').select('meta').eq('id', matchId).maybeSingle(); if (r && r.data && r.data.meta && Array.isArray(r.data.meta.photos)) photos = r.data.meta.photos.slice(); } catch (e) { logError('_modRefreshPanels', e); } }
    const existing = document.getElementById('modPhotoModal'); if (existing) existing.remove();
    const modal = document.createElement('div');
    modal.className = 'modal active'; modal.id = 'modPhotoModal'; modal.style.zIndex = '100010';
    modal.onclick = function (e) { if (e.target === modal) modal.remove(); };
    const clubArg = clubId ? "'" + aj(String(clubId)) + "'" : 'null';
    const grid = photos.length ? photos.map(function (u) {
        const isRep = repPath && (typeof window._spPath === 'function') && window._spPath(u) === repPath;
        const flag = isRep ? 'box-shadow:0 0 0 3px var(--danger,#e74c3c); border-radius:8px;' : '';
        return '<div style="position:relative;' + flag + '">'
            + '<img data-sp="' + esc(u) + '" data-spt="1" src="' + _SP_PH + '" style="width:96px; height:96px; object-fit:cover; border-radius:8px;">'
            + '<button class="btn btn-sm btn-danger" style="position:absolute; top:3px; right:3px; padding:2px 7px;" onclick="moderationDeletePhoto(\'' + aj(String(matchId)) + '\',\'' + aj(String(u)) + '\',' + clubArg + ')"><i class="fas fa-trash"></i></button>'
            + '</div>';
    }).join('') : '<div style="opacity:.7;">' + t('mod.none') + '</div>';
    modal.innerHTML = '<div class="modal-content" style="max-width:480px;"><div class="modal-header"><h2><i class="fas fa-images"></i> ' + t('mod.targetPhoto') + '</h2><button class="modal-close" onclick="this.closest(\'.modal\').remove()">×</button></div><div style="display:flex; flex-wrap:wrap; gap:8px;">' + grid + '</div></div>';
    document.body.appendChild(modal);
};
window.moderationDeletePhoto = async function (matchId, url, clubId) {
    if (!await confirmModal(t('mod.confirmDelPhoto'), { danger: true })) return;
    try {
        const r = await sb.rpc('moderator_remove_session_photo', { p_match_id: matchId, p_url: url, p_club: clubId || null });
        if (r.error) { showNotification(t('mod.takedownFail') + r.error.message, 'error'); return; }
        const g = (state.games || []).find(x => String(x.id) === String(matchId));
        if (g && Array.isArray(g.photos)) g.photos = g.photos.filter(p => String(p) !== String(url));
        showNotification(t('mod.takedownOk'), 'success');
        const m = document.getElementById('modPhotoModal'); if (m) m.remove();
        _modRefreshPanels();
    } catch (e) { showNotification(t('mod.takedownFail') + (e && e.message || ''), 'error'); }
};
window.renderModerationPanel = renderModerationPanel;
window.renderOwnerModeration = renderOwnerModeration;

/* Id-urile de profil ale membrilor clubului (opțional excluzând pe cineva, ex. autorul). */
function clubMemberIds(excludeId) {
    const out = [];
    const src = (state.membersById && Object.keys(state.membersById).length) ? state.membersById : (state.members || {});
    Object.values(src).forEach(m => {
        if (m && m.dbId && m.dbId !== excludeId) out.push(m.dbId);
    });
    return out;
}
window.clubMemberIds = clubMemberIds;

/* Notifică membrii activi ai unui club că un membru nou s-a alăturat.
   Folosește aceeași infrastructură ca notificările game-vote (pushNotification),
   deci RLS-ul pe `notifications` o permite. Prefixul [Nume club] e adăugat automat
   de loadAllNotifications. */
async function notifyClubNewMember(clubId, newMemberId, newMemberName) {
    try {
        if (!sb || !clubId || !newMemberId) return;
        if (typeof isDemoMode === 'function' && isDemoMode()) return;
        const r = await sb.from('club_members')
            .select('profile_id')
            .eq('club_id', clubId).eq('status', 'active');
        const ids = ((r && r.data) || [])
            .map(m => m.profile_id)
            .filter(id => id && id !== newMemberId);
        if (!ids.length) return;
        let nm = (newMemberName || '').trim();
        if (!nm) {
            try {
                const p = await sb.from('profiles').select('name, display_name').eq('id', newMemberId).maybeSingle();
                nm = (p && p.data && (p.data.display_name || p.data.name)) || window.t('dm.someone');
            } catch (e) { nm = window.t('dm.someone'); }
        }
        const text = window.t('notif.newMember').replace('{name}', nm);
        await pushNotification(ids, { clubId: clubId, type: 'new_member', text: text, icon: 'fa-user-plus', tab: 'players' });
    } catch (e) { console.warn('notifyClubNewMember:', e && e.message); }
}
window.notifyClubNewMember = notifyClubNewMember;

/* ============================================================
   ===================== ANUNȚURI (stil WhatsApp) ============
   Container sus în Dashboard. Postare gated `can('announcements.post')`
   (rang >= secundar). Orice membru al clubului poate RĂSPUNDE.
   Push la anunț nou prin pushNotification -> trigger DB existent
   (`trg_notifications_push`). Realtime opțional (vezi subscribeRealtimeData).
   RO+EN, escapare la randare (§9), RBAC (§5), pattern UI tab-head (§11).
   ============================================================ */
async function loadAnnouncements() {
    if (typeof isDemoMode === 'function' && isDemoMode()) return; // demo: doar local în state.announcements
    if (!sb || !currentClubId) { state.announcements = []; return; }
    try {
        const aRes = await sb.from('announcements').select('*')
            .eq('club_id', currentClubId).order('created_at', { ascending: false }).limit(50);
        const anns = (aRes && aRes.data) || [];
        const annIds = anns.map(a => a.id);
        let replies = [];
        if (annIds.length) {
            const rRes = await sb.from('announcement_replies').select('*')
                .in('announcement_id', annIds).order('created_at', { ascending: true });
            replies = (rRes && rRes.data) || [];
        }
        const byAnn = {};
        replies.forEach(r => {
            (byAnn[r.announcement_id] = byAnn[r.announcement_id] || [])
                .push({ id: r.id, authorId: r.author_id, body: r.body, createdAt: r.created_at });
        });
        state.announcements = anns.map(a => ({
            id: a.id, authorId: a.author_id, title: a.title || '', body: a.body,
            images: Array.isArray(a.images) ? a.images : (a.images || []),
            createdAt: a.created_at, replies: byAnn[a.id] || []
        }));
    } catch (e) { console.warn('loadAnnouncements:', e && e.message); }
}
window.loadAnnouncements = loadAnnouncements;

function renderAnnouncements() {
    const box = document.getElementById('announcementsList');
    if (!box) return;
    const esc = window.escHtml || function (s) { return s == null ? '' : String(s); };
    const aj = window.attrJs || function (s) { return s == null ? '' : String(s); };
    const anns = state.announcements || [];
    const me = (state.currentUser && state.currentUser.id) || '';
    const canModerate = (typeof can === 'function') && can('chat.moderate');
    const canManageAnn = (typeof can === 'function') && can('announcements.post');
    if (!anns.length) { box.innerHTML = '<div class="ann-empty">' + esc(t('ann.empty')) + '</div>'; return; }
    const locale = (typeof appDateLocale === 'function') ? appDateLocale() : 'en-GB';
    const fmt = ts => { try { return new Date(ts).toLocaleString(locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; } };
    const authorHtml = (id, cls) => {
        const nm = (typeof displayNameById === 'function' && displayNameById(id)) || t('dm.someone');
        const m = (typeof memberById === 'function') ? memberById(id) : null;
        const av = (typeof avatarHtml === 'function') ? avatarHtml(m && m.avatar, 28) : '';
        return '<span class="ann-avatar">' + av + '</span>'
            + '<span class="' + cls + '" onclick="openLeaderboardProfile(\'' + aj(String(id)) + '\')">' + esc(nm) + '</span>';
    };
    const renderAnn = a => {
        const replies = a.replies || [];
        const repliesInner = replies.map(r => {
            const canDelR = (r.authorId === me) || canModerate;
            return '<div class="ann-reply">'
                + '<div class="ann-reply-head">' + authorHtml(r.authorId, 'ann-reply-author')
                +   '<span class="ann-reply-time">' + fmt(r.createdAt) + '</span>'
                +   (canDelR ? '<button class="ann-tool" title="' + esc(t('common.delete')) + '" onclick="deleteAnnouncementReply(\'' + aj(String(a.id)) + '\',\'' + aj(String(r.id)) + '\')"><i class="fas fa-trash"></i></button>' : '')
                + '</div>'
                + '<div class="ann-reply-body">' + esc(r.body) + '</div>'
                + '</div>';
        }).join('');
        const repliesBlock = replies.length
            ? '<button type="button" class="ann-replies-toggle" onclick="toggleAnnReplies(\'' + aj(String(a.id)) + '\')">'
                + '<i class="fas fa-comment-dots"></i> <span data-lbl="show">' + esc(t('ann.viewReplies')) + ' (' + replies.length + ')</span>'
                + '<span data-lbl="hide" style="display:none">' + esc(t('ann.hideReplies')) + '</span></button>'
              + '<div class="ann-replies" id="annReplies-' + esc(String(a.id)) + '" style="display:none">' + repliesInner + '</div>'
            : '';
        return '<div class="ann-item" id="ann-' + esc(String(a.id)) + '">'
            + '<div class="ann-head">' + authorHtml(a.authorId, 'ann-author')
            +   '<span class="ann-time">' + fmt(a.createdAt) + '</span>'
            +   (canManageAnn ? '<button class="ann-tool" title="' + esc(t('history.edit')) + '" onclick="editAnnouncement(\'' + aj(String(a.id)) + '\')"><i class="fas fa-pen"></i></button>' : '')
            +   (canManageAnn ? '<button class="ann-tool" title="' + esc(t('common.delete')) + '" onclick="deleteAnnouncement(\'' + aj(String(a.id)) + '\')"><i class="fas fa-trash"></i></button>' : '')
            + '</div>'
            + (a.title ? '<div class="ann-item-title">' + esc(a.title) + '</div>' : '')
            + '<div class="ann-body">' + window.linkifyEscaped(esc(a.body)) + '</div>'
            + ((a.images && a.images.length) ? '<div class="ann-photos">' + a.images.map(u =>
                /* ⚠ `data-gal` PE LÂNGĂ `data-sp`: observatorul de semnare îl ȘTERGE
                   pe al doilea imediat ce a pus adresa semnată, deci galeria din
                   lightbox n-ar mai găsi niciun frate. Aceeași pereche ca în chat
                   și în istoricul de sesiuni.
                   ⚠ Al treilea parametru (`this`) e ELEMENTUL apăsat, din care se
                     deduce galeria. Contextul de raportare lipsea complet aici: o
                     poză nepotrivită dintr-un anunț nu se putea raporta deloc.
                   EN: adds gallery navigation and the missing report context. */
                '<img data-sp="' + esc(String(u)) + '" data-spt="1" data-gal="' + esc(String(u)) + '" src="' + _SP_PH + '" loading="lazy" class="ann-photo" onclick="if(typeof openLightbox===\'function\') openLightbox(\'' + aj(String(u)) + '\', {type:\'ann\', id:\'' + aj(String(a.id)) + '\', clubId:\'' + aj(String(currentClubId || '')) + '\'}, this)">'
              ).join('') + '</div>' : '')
            + repliesBlock
            + '<div class="ann-reply-box">'
            +   '<input type="text" class="form-control" id="annReplyInput-' + esc(String(a.id)) + '" maxlength="2000" placeholder="' + esc(t('ann.replyPh')) + '" onkeydown="if(event.key===\'Enter\'){event.preventDefault();replyToAnnouncement(\'' + aj(String(a.id)) + '\');}">'
            +   '<button class="btn btn-sm btn-secondary ann-reply-send" onclick="replyToAnnouncement(\'' + aj(String(a.id)) + '\')">' + esc(t('ann.replyBtn')) + '</button>'
            + '</div>'
            + '</div>';
    };
    const latest = anns[0];
    const rest = anns.slice(1);
    box.innerHTML = renderAnn(latest)
        + (rest.length
            ? '<button type="button" class="ann-showall-toggle" onclick="toggleAllAnnouncements()">'
                + '<span data-lbl="show"><i class="fas fa-chevron-down"></i> ' + esc(t('ann.viewAll')) + ' (' + rest.length + ')</span>'
                + '<span data-lbl="hide" style="display:none"><i class="fas fa-chevron-up"></i> ' + esc(t('ann.hideAll')) + '</span></button>'
              + '<div class="ann-rest" id="annRestList" style="display:none">' + rest.map(renderAnn).join('') + '</div>'
            : '');
}
window.renderAnnouncements = renderAnnouncements;

function toggleAllAnnouncements() {
    const list = document.getElementById('annRestList');
    const btn = document.querySelector('.ann-showall-toggle');
    if (!list || !btn) return;
    const open = list.style.display !== 'none';
    list.style.display = open ? 'none' : 'block';
    const sh = btn.querySelector('[data-lbl="show"]'), hd = btn.querySelector('[data-lbl="hide"]');
    if (sh) sh.style.display = open ? '' : 'none';
    if (hd) hd.style.display = open ? 'none' : '';
}
window.toggleAllAnnouncements = toggleAllAnnouncements;

function toggleAnnReplies(id) {
    const box = document.getElementById('annReplies-' + id);
    const btn = box && box.previousElementSibling;
    if (!box || !btn) return;
    const open = box.style.display !== 'none';
    box.style.display = open ? 'none' : 'block';
    const sh = btn.querySelector('[data-lbl="show"]'), hd = btn.querySelector('[data-lbl="hide"]');
    if (sh) sh.style.display = open ? '' : 'none';
    if (hd) hd.style.display = open ? 'none' : '';
}
window.toggleAnnReplies = toggleAnnReplies;

/* Task 3: cardul „Următorul eveniment" din dashboard — cel mai apropiat eveniment viitor
   (dacă nu există, cel mai recent), cu controale de vot (RSVP) și buton de deschidere.
   Refolosește rsvpControlsHtml + openEvent (fără a duplica logica). */
function renderDashboardEvent() {
    const card = document.getElementById('dashboardEventCard');
    const body = document.getElementById('dashboardEventBody');
    if (!card || !body) return;
    const esc = window.escHtml || function (s) { return s == null ? '' : String(s); };
    const evs = (state.events || []).filter(e => e && e.date);
    if (!evs.length) { card.style.display = 'none'; var _s0 = document.getElementById('dashEventOpenSlot'); if (_s0) _s0.innerHTML = ''; return; }
    const now = Date.now();
    const future = evs.filter(e => new Date(e.date).getTime() >= now).sort((a, b) => new Date(a.date) - new Date(b.date));
    const past = evs.filter(e => new Date(e.date).getTime() < now).sort((a, b) => new Date(b.date) - new Date(a.date));
    const ev = future[0] || past[0];
    if (!ev) { card.style.display = 'none'; var _s1 = document.getElementById('dashEventOpenSlot'); if (_s1) _s1.innerHTML = ''; return; }
    card.style.display = '';
    const locale = (typeof appDateLocale === 'function') ? appDateLocale() : 'en-GB';
    let whenStr = '';
    try { whenStr = new Date(ev.date).toLocaleString(locale, { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (e) {}
    const isTour = !!(ev.type === 'tournament' || ev.isTournament);
    const locHtml = ev.location ? '<div class="dash-ev-loc"><i class="fas fa-location-dot"></i> ' + esc(String(ev.location)) + '</div>' : '';
    /* v8 — cele TREI `onclick=` inline de aici au fost migrate la delegare (§5.1).
       RO: titlul evenimentului venea de la utilizator și ajungea într-un atribut
           care se EXECUTĂ; ca `data-*` e simplă dată. A dispărut și
           `onclick="event.stopPropagation()"` de pe zona RSVP: nu mai are ce opri,
           fiindcă deschiderea evenimentului e acum ea însăși delegată și ignoră
           din construcție clicurile venite dintr-un control RSVP.
       EN: three inline handlers replaced by delegation; the stopPropagation wrapper
           is gone because the open action now ignores clicks originating in RSVP. */
    const idAttr = esc(String(ev.id));
    body.innerHTML =
        '<div class="dash-ev">'
        + '<div class="dash-ev-main" data-action="dash-open-event" data-event-id="' + idAttr + '">'
        +   '<div class="dash-ev-title">' + (isTour ? '🏆 ' : '🎮 ') + esc(ev.title || '') + '</div>'
        +   '<div class="dash-ev-when"><i class="fas fa-clock"></i> ' + esc(whenStr) + '</div>'
        +   locHtml
        + '</div>'
        + '<div class="dash-ev-rsvp">' + (typeof rsvpControlsHtml === 'function' ? rsvpControlsHtml(ev, { scope: 'dash' }) : '') + '</div>'
        + '</div>';
    /* Butonul „Deschide” stă sus-dreapta, lângă calendar (mai compact). */
    const slot = document.getElementById('dashEventOpenSlot');
    if (slot) {
        slot.innerHTML = '<button class="tab-head-btn" title="' + esc(t('dash.openEvent')) + '" data-action="dash-open-event" data-event-id="' + idAttr + '"><i class="fas fa-arrow-up-right-from-square"></i></button>';
    }
}
window.renderDashboardEvent = renderDashboardEvent;


/* Composer bogat pentru admini: titlu + text + poze. Modal tematizat (NU prompt nativ). */
function postAnnouncement(editId) {
    if (typeof can === 'function' && !can('announcements.post')) { showNotification(t('ann.noRight'), 'error'); return; }
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    window._annComposeFiles = [];
    /* Mod editare: precompletăm din anunțul existent. `_annEditId` = id (sau null la creare),
       `_annEditKeep` = pozele EXISTENTE păstrate (path-uri bucket, semnate la randare via data-sp). */
    var ed = null;
    if (editId != null && editId !== '') {
        ed = (state.announcements || []).find(function (x) { return String(x.id) === String(editId); }) || null;
        if (!ed) { showNotification(t('ann.saveErr'), 'error'); return; }
    }
    window._annEditId = ed ? ed.id : null;
    window._annEditKeep = ed ? (Array.isArray(ed.images) ? ed.images.slice() : []) : [];
    const esc = window.escHtml || function (s) { return s == null ? '' : String(s); };
    const old = document.getElementById('annComposeModal'); if (old) old.remove();
    const modal = document.createElement('div');
    modal.className = 'modal active'; modal.id = 'annComposeModal';
    modal.style.zIndex = '100010';
    modal.onclick = function (e) { if (e.target === modal) modal.remove(); };
    modal.innerHTML =
        '<div class="modal-content" style="max-width:520px;">'
        + '<div class="modal-header"><h2><i class="fas fa-bullhorn"></i> ' + esc(ed ? t('ann.editTitle') : t('ann.new')) + '</h2>'
        +   '<button class="modal-close" onclick="this.closest(\'.modal\').remove()">×</button></div>'
        + '<div class="form-group"><label for="annComposeTitle" class="form-label">' + esc(t('ann.titleLabel')) + '</label>'
        +   '<input type="text" id="annComposeTitle" class="form-control" maxlength="120" placeholder="' + esc(t('ann.titlePh')) + '" value="' + esc(ed ? (ed.title || '') : '') + '"></div>'
        + '<div class="form-group"><label for="annComposeBody" class="form-label">' + esc(t('ann.bodyLabel')) + '</label>'
        +   '<textarea id="annComposeBody" class="form-control" rows="5" maxlength="4000" placeholder="' + esc(t('ann.bodyPh')) + '">' + esc(ed ? (ed.body || '') : '') + '</textarea></div>'
        + '<div class="form-group"><span class="form-section-label">' + esc(t('ann.photosLabel')) + '</span>'
        +   '<div class="ann-compose-photos" id="annComposePreview"></div>'
        +   '<label class="btn btn-sm btn-secondary ann-compose-add"><i class="fas fa-image"></i> ' + esc(t('ann.addPhotos'))
        +     '<input type="file" accept="image/jpeg,image/png,image/webp" multiple style="display:none" onchange="annComposePick(this)"></label></div>'
        + '<div style="display:flex; gap:10px; margin-top:16px;">'
        +   '<button class="btn btn-secondary" onclick="this.closest(\'.modal\').remove()">' + esc(t('cm.cancel')) + '</button>'
        +   '<button class="btn" id="annComposeSubmit" onclick="submitAnnouncement()"><i class="fas fa-paper-plane"></i> ' + esc(ed ? t('common.save') : t('ann.publish')) + '</button>'
        + '</div>'
        + '</div>';
    document.body.appendChild(modal);
    annComposeRenderPreview();  /* randează pozele existente (edit) + eventuale fișiere noi */
    setTimeout(function () { const b = document.getElementById('annComposeBody'); if (b) b.focus(); }, 50);
}
window.postAnnouncement = postAnnouncement;

/* Deschide composer-ul în mod editare pentru anunțul dat (autor sau moderator+). */
function editAnnouncement(id) { postAnnouncement(id); }
window.editAnnouncement = editAnnouncement;

/* Aceeași comprimare ca la pozele de sesiune (§8.8): până la 8 poze brute de
   telefon însemnau zeci de MB per anunț. Secvențial, cu previzualizarea ca
   indicator de progres. Vezi nota de la `onSessionPhotosPicked`. */
async function annComposePick(input) {
    const files = input && input.files ? Array.from(input.files) : [];
    if (!Array.isArray(window._annComposeFiles)) window._annComposeFiles = [];
    try { input.value = ''; } catch (e) { logError('annComposePick', e); }
    const picked = files.filter(function (f) { return /^image\//.test(f.type); });
    if (!picked.length) return;

    window._photoPrepBusy = true;
    if (picked.length > 1) showNotification(t('photo.preparing').replace('{n}', picked.length), 'info');
    try {
        for (const f of picked) {
            if (window._annComposeFiles.length >= 8) break;
            let out = f;
            try {
                out = (typeof window.compressImage === 'function') ? await window.compressImage(f) : f;
            } catch (e) { logError('annComposePick', e); out = f; }
            window._annComposeFiles.push(out);
            annComposeRenderPreview();
        }
    } finally {
        window._photoPrepBusy = false;
    }
    annComposeRenderPreview();
}
window.annComposePick = annComposePick;

function annComposeRenderPreview() {
    const box = document.getElementById('annComposePreview'); if (!box) return;
    const esc = window.escHtml || function (s) { return s == null ? '' : String(s); };
    const aj = window.attrJs || function (s) { return s == null ? '' : String(s); };
    /* Pozele EXISTENTE păstrate (doar în mod editare) — path-uri bucket, semnate
       automat de observerul global data-sp. Eliminarea le scoate din _annEditKeep. */
    const keep = Array.isArray(window._annEditKeep) ? window._annEditKeep : [];
    const existing = keep.map(function (u, i) {
        return '<div class="ann-compose-thumb"><img data-sp="' + esc(String(u)) + '" data-spt="1" src="' + (window._SP_PH || '') + '">'
            + '<button type="button" title="' + esc(t('common.remove')) + '" onclick="annEditRemoveExisting(' + i + ')"><i class="fas fa-times"></i></button></div>';
    }).join('');
    const fresh = (window._annComposeFiles || []).map(function (f, i) {
        const url = URL.createObjectURL(f);
        return '<div class="ann-compose-thumb"><img src="' + url + '">'
            + '<button type="button" title="' + esc(t('common.remove')) + '" onclick="annComposeRemove(' + i + ')"><i class="fas fa-times"></i></button></div>';
    }).join('');
    box.innerHTML = existing + fresh;
}
window.annComposeRenderPreview = annComposeRenderPreview;

/* Scoate o poză EXISTENTĂ din setul păstrat la editare. */
function annEditRemoveExisting(i) {
    if (Array.isArray(window._annEditKeep) && i >= 0 && i < window._annEditKeep.length) window._annEditKeep.splice(i, 1);
    annComposeRenderPreview();
}
window.annEditRemoveExisting = annEditRemoveExisting;

function annComposeRemove(i) {
    if (Array.isArray(window._annComposeFiles) && i >= 0 && i < window._annComposeFiles.length) window._annComposeFiles.splice(i, 1);
    annComposeRenderPreview();
}
window.annComposeRemove = annComposeRemove;

/* Încarcă pozele anunțului în bucket-ul session-photos (public read / auth write) și
   întoarce URL-urile publice. Reutilizează pattern-ul uploadSessionPhotos (§15.3: doar
   URL-uri http, NU base64 — esențial pentru ca push-ul să nu depășească limita de 4KB).
   `folderToken` = subfolder random (urcăm ÎNAINTE de insert → nu avem încă id-ul). */
async function uploadAnnouncementPhotos(folderToken, files) {
    const urls = [];
    let rejected = 0;
    for (const f of files) {
        try {
            const ext = ((f.name && f.name.split('.').pop()) || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
            const path = (currentClubId || 'club') + '/announcements/' + folderToken + '/' + Date.now() + '-' + Math.random().toString(36).slice(2, 8) + '.' + ext;
            const up = await sb.storage.from('session-photos').upload(path, f, { cacheControl: '3600', upsert: false, contentType: f.type || 'image/jpeg' });
            if (up.error) { console.warn('ann photo:', up.error.message); rejected++; continue; }
            urls.push(path);   /* bucket privat: stocăm PATH-ul, semnarea se face la randare */
            /* v9 8.5-C: miniatura (320 px) lângă poză, ÎNAINTE de publicare; eșecul nu oprește nimic */
            if (window.TO_CHAT && TO_CHAT.thumbUp) await TO_CHAT.thumbUp('session-photos', f, path);
        } catch (e) { console.warn('ann photo:', e && e.message); rejected++; }
    }
    /* ⚠ Doar când au trecut UNELE: dacă a picat TOT, `submitAnnouncement` afișează
       deja `ann.photoUploadErr` și două mesaje suprapuse ar deruta. */
    if (rejected && urls.length) _warnPhotosRejected(rejected);
    return urls;
}
window.uploadAnnouncementPhotos = uploadAnnouncementPhotos;

async function submitAnnouncement() {
    /* Aceeași grijă ca la `saveSession`: pozele încă în comprimare s-ar pierde tăcut. */
    if (window._photoPrepBusy) { showNotification(t('photo.stillPreparing'), 'info'); return; }
    const titleEl = document.getElementById('annComposeTitle');
    const bodyEl = document.getElementById('annComposeBody');
    const title = ((titleEl && titleEl.value) || '').trim();
    const body = ((bodyEl && bodyEl.value) || '').trim();
    if (!body) { showNotification(t('ann.empty2'), 'error'); return; }
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    const me = state.currentUser && state.currentUser.id;
    const files = (window._annComposeFiles || []).slice();
    const editId = window._annEditId || null;
    const keep = Array.isArray(window._annEditKeep) ? window._annEditKeep.slice() : [];
    const esc = window.escHtml || function (s) { return s == null ? '' : String(s); };
    const _closeReset = function () {
        const m = document.getElementById('annComposeModal'); if (m) m.remove();
        window._annComposeFiles = []; window._annEditId = null; window._annEditKeep = [];
    };

    /* ---------- DEMO: totul local ---------- */
    if (typeof isDemoMode === 'function' && isDemoMode()) {
        if (!Array.isArray(state.announcements)) state.announcements = [];
        const freshImgs = files.map(function (f) { return URL.createObjectURL(f); });
        if (editId) {
            const a = state.announcements.find(function (x) { return String(x.id) === String(editId); });
            if (a) { a.title = title; a.body = body; a.images = keep.concat(freshImgs); }
        } else {
            state.announcements.unshift({ id: 'demo-' + Date.now(), authorId: me, title: title, body: body, images: freshImgs, createdAt: new Date().toISOString(), replies: [] });
        }
        _closeReset(); renderAnnouncements(); return;
    }
    if (!sb || !currentClubId) return;

    const btn = document.getElementById('annComposeSubmit');
    const busyLabel = editId ? t('ann.publishing') : t('ann.publishing');
    if (btn) { btn.disabled = true; btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> ' + esc(busyLabel); }
    const _resetBtn = function () {
        if (!btn) return; btn.disabled = false;
        btn.innerHTML = editId ? ('<i class="fas fa-paper-plane"></i> ' + esc(t('common.save')))
                               : ('<i class="fas fa-paper-plane"></i> ' + esc(t('ann.publish')));
    };
    try {
        /* Urcăm pozele NOI (dacă există) → path-uri. */
        let newImages = [];
        if (files.length) {
            const token = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
            newImages = await uploadAnnouncementPhotos(token, files);
            if (!newImages.length) { showNotification(t('ann.photoUploadErr'), 'error'); }
        }

        if (editId) {
            /* ---------- EDITARE: UPDATE prin RPC (autor SAU rang≥2, server-side) ---------- */
            const finalImages = keep.concat(newImages);
            const upd = await sb.rpc('announcement_update', {
                p_id: Number(editId), p_title: title || null, p_body: body, p_images: finalImages
            });
            if (upd.error) { showNotification(t('ann.saveErr') + (upd.error.message || ''), 'error'); _resetBtn(); return; }
            _closeReset();
            await loadAnnouncements(); renderAnnouncements();
            showNotification(t('ann.updated'), 'success');
            return;
        }

        /* ---------- CREARE: INSERT (un singur insert, cu poze incluse) ---------- */
        const ins = await sb.from('announcements')
            .insert({ club_id: currentClubId, author_id: me, title: title || null, body: body, images: newImages })
            .select('id').maybeSingle();
        if (ins.error || !ins.data) { showNotification(t('ann.saveErr') + ((ins.error && ins.error.message) || ''), 'error'); _resetBtn(); return; }
        try { await notifyClubAnnouncement(currentClubId, me, title ? (title + ' — ' + body) : body); } catch (e) { logError('submitAnnouncement', e); }
        _closeReset();
        await loadAnnouncements(); renderAnnouncements();
        showNotification(t('ann.posted'), 'success');
    } catch (e) {
        console.warn('submitAnnouncement:', e && e.message);
        _resetBtn();
    }
}
window.submitAnnouncement = submitAnnouncement;

async function replyToAnnouncement(annId) {
    const inp = document.getElementById('annReplyInput-' + annId);
    if (!inp) return;
    const txt = (inp.value || '').trim();
    if (!txt) return;
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    const me = state.currentUser && state.currentUser.id;
    if (typeof isDemoMode === 'function' && isDemoMode()) {
        const a = (state.announcements || []).find(x => String(x.id) === String(annId));
        if (a) { a.replies = a.replies || []; a.replies.push({ id: 'demo-' + Date.now(), authorId: me, body: txt, createdAt: new Date().toISOString() }); renderAnnouncements(); }
        return;
    }
    if (!sb) return;
    try {
        const r = await sb.from('announcement_replies').insert({ announcement_id: Number(annId), author_id: me, body: txt });
        if (r.error) { showNotification(t('ann.saveErr') + (r.error.message || ''), 'error'); return; }
        inp.value = '';
        await loadAnnouncements(); renderAnnouncements();
    } catch (e) { console.warn('replyToAnnouncement:', e && e.message); }
}
window.replyToAnnouncement = replyToAnnouncement;

async function deleteAnnouncement(id) {
    const ok = await confirmModal(t('ann.delConfirm'), { danger: true });
    if (!ok) return;
    if (typeof isDemoMode === 'function' && isDemoMode()) {
        state.announcements = (state.announcements || []).filter(a => String(a.id) !== String(id));
        renderAnnouncements(); return;
    }
    if (!sb) return;
    try {
        const r = await sb.from('announcements').delete().eq('id', Number(id));
        if (r.error) { showNotification(t('ann.delErr') + (r.error.message || ''), 'error'); return; }
        await loadAnnouncements(); renderAnnouncements();
    } catch (e) { console.warn('deleteAnnouncement:', e && e.message); }
}
window.deleteAnnouncement = deleteAnnouncement;

async function deleteAnnouncementReply(annId, id) {
    const ok = await confirmModal(t('ann.delReplyConfirm'), { danger: true });
    if (!ok) return;
    if (typeof isDemoMode === 'function' && isDemoMode()) {
        const a = (state.announcements || []).find(x => String(x.id) === String(annId));
        if (a && Array.isArray(a.replies)) a.replies = a.replies.filter(r => String(r.id) !== String(id));
        renderAnnouncements(); return;
    }
    if (!sb) return;
    try {
        const r = await sb.from('announcement_replies').delete().eq('id', Number(id));
        if (r.error) { showNotification(t('ann.delErr') + (r.error.message || ''), 'error'); return; }
        await loadAnnouncements(); renderAnnouncements();
    } catch (e) { console.warn('deleteAnnouncementReply:', e && e.message); }
}
window.deleteAnnouncementReply = deleteAnnouncementReply;

async function notifyClubAnnouncement(clubId, authorId, body) {
    try {
        if (!sb || !clubId) return;
        if (typeof isDemoMode === 'function' && isDemoMode()) return;
        const r = await sb.from('club_members').select('profile_id').eq('club_id', clubId).eq('status', 'active');
        const ids = ((r && r.data) || []).map(m => m.profile_id).filter(id => id && id !== authorId);
        if (!ids.length) return;
        const snippet = (body || '').slice(0, 80);
        const text = window.t('notif.announcement').replace('{text}', snippet);
        await pushNotification(ids, { clubId: clubId, type: 'announcement', text: text, icon: 'fa-bullhorn', tab: 'dashboard' });
    } catch (e) { console.warn('notifyClubAnnouncement:', e && e.message); }
}
window.notifyClubAnnouncement = notifyClubAnnouncement;

/* ─── COALESCENȚĂ + FEREASTRĂ DE PROSPEȚIME (Batch 4a, perf · §54) ─────────────
   RO: `loadAllNotifications` NU e ieftină: trei interogări în paralel (80 de sesiuni,
       40 de evenimente, 60 de mesaje) + un RPC de nume + cererile de aderare + profiluri
       + 60 de notificări. Adică ~7 dus-întorsuri de fiecare dată.
       PROBLEMA MĂSURATĂ (22.08.2026): era chemată de 3-4 ori la FIECARE pornire, din
       locuri care nu știau unele de altele — `onAuthenticated` (în fundal),
       `loadCrossClubData`, reîmprospătarea realtime și DOUĂ cronometre independente
       (`social.js` la 20 s, `app-modules-3.js` la 45 s). Garda existentă
       (`if (!state.notifLoaded)` din `loadCrossClubData`) nu ajuta: apelurile porneau
       aproape simultan, deci toate vedeau steagul încă pe `false`.
       SOLUȚIA, în două straturi, aplicate AICI ca să acopere TOȚI apelanții — inclusiv
       pe cei care se vor adăuga în viitor, fără să trebuiască să-și amintească cineva:
       (1) COALESCENȚĂ: dacă o încărcare e deja în curs, apelantul primește ACEEAȘI
           promisiune în loc să pornească una nouă. Zero risc de date învechite —
           așteaptă exact aceleași date, doar că nu le mai cere a doua oară.
       (2) FEREASTRĂ DE PROSPEȚIME de 5 s: un apel care vine imediat după o încărcare
           încheiată reia rezultatul din memorie. Cinci secunde e sub pragul la care un
           om observă diferența, dar acoperă exact rafala de la pornire.
       ⚠ `{ force: true }` ocolește fereastra (NU și coalescența — acolo n-are ce ocoli).
       Îl folosește deschiderea clopoțelului: acolo utilizatorul CERE explicit datele.
   EN: this function costs ~7 round trips and was being called 3-4× per startup by
       callers unaware of each other; the existing `!state.notifLoaded` guard lost the
       race because they all started at once. Fixed at the source with in-flight
       coalescing plus a 5 s freshness window, so every present and future caller is
       covered. `{ force: true }` skips the window for user-initiated refreshes. */
/* ⚠ `var`, nu `let`/`const`: `loadAllNotifications` e o declarație de funcție, deci e
   RIDICATĂ (hoisted) și poate fi apelată dinainte de linia asta. Cu `let`/`const` ar fi
   crăpat cu „Cannot access before initialization"; cu `var` valorile sunt pur și simplu
   `undefined`, iar gărzile de mai jos tratează asta corect (§46.4/C1).
   EN: `var` because the function is hoisted and could be called before this line. */
var _notifLoadInFlight = null;   /* promisiunea încărcării curente, dacă există */
var _notifLoadedAt = 0;          /* momentul ultimei încărcări ÎNCHEIATE cu succes */
var NOTIF_FRESH_MS = 5000;

/* Interogarea de sesiuni pentru clopoțelul cross-club.
   ⚠ AICI, spre deosebire de hidratare, NU se folosește `select('*')`, deci cererea
     unei coloane care încă nu există face interogarea să EȘUEZE ÎN ÎNTREGIME — exact
     tiparul care a doborât toate notificările de sesiune în producție (§60.3). De
     aceea coloana se cere optimist, iar la refuz se cade pe varianta veche.
   ⚠ Steagul se stinge DOAR când mesajul de eroare vorbește despre `created_at`: o
     pană de rețea nu trebuie să dezactiveze permanent sortarea corectă.
   ⚠ `null` = încă nu știm; `true`/`false` = aflat. O singură interogare irosită per
     sesiune de navigare, în cel mai rău caz.
   EN: optimistically request `created_at`, fall back to the legacy query if the
       column is not there yet; never disable it because of a transient error. */
let _matchesHasCreatedAt = null;
async function _notifMatchesQuery(ids) {
    const COLS = 'id, club_id, game_title, players, recorded_by, date';
    if (_matchesHasCreatedAt !== false) {
        try {
            const r = await sb.from('matches').select(COLS + ', created_at')
                .in('club_id', ids).order('created_at', { ascending: false }).limit(80);
            if (!r.error) { _matchesHasCreatedAt = true; return r; }
            if (/created_at/i.test(String(r.error.message || ''))) _matchesHasCreatedAt = false;
        } catch (e) { console.warn('_notifMatchesQuery:', e && e.message); }
    }
    return sb.from('matches').select(COLS).in('club_id', ids).order('date', { ascending: false }).limit(80);
}

async function loadAllNotifications(opts) {
    const force = !!(opts && opts.force);
    /* (1) O încărcare e deja în zbor → ne alipim de ea. */
    if (_notifLoadInFlight) return _notifLoadInFlight;
    /* (2) Datele sunt proaspete → nu mai cerem nimic. */
    if (!force && _notifLoadedAt && (Date.now() - _notifLoadedAt) < NOTIF_FRESH_MS) return;
    _notifLoadInFlight = (async function () {
        try { return await _loadAllNotificationsCore(); }
        finally {
            /* ⚠ `finally`: steagul se eliberează chiar dacă încărcarea a eșuat, altfel
               clopoțelul ar rămâne blocat pentru tot restul sesiunii.
               Marcăm momentul DOAR la final, ca fereastra să se numere de la încheiere. */
            _notifLoadedAt = Date.now();
            _notifLoadInFlight = null;
        }
    })();
    return _notifLoadInFlight;
}
window.loadAllNotifications = loadAllNotifications;

async function _loadAllNotificationsCore() {
    if (!state.currentUser) { state.allNotifications = []; state.notifLoaded = true; return; }
    const me = state.currentUser.id;
    if (isDemoMode() || !sb) {
        state.allNotifications = buildNotifications().map(n => Object.assign({}, n, { clubId: currentClubId, clubName: clubDisplayName(), tab: n.id && n.id[0] === 's' ? 'history' : n.id && n.id[0] === 'e' ? 'calendar' : 'chat' }));
        state.notifLoaded = true; return;
    }
    const clubs = state.myClubs || [];
    const ids = clubs.map(c => c.id);
    if (!ids.length) { state.allNotifications = []; state.notifLoaded = true; return; }
    const nameById = {}; clubs.forEach(c => nameById[c.id] = c.name);
    /* PRAG: un cont NOU pornește cu clopoțelul GOL. Afișăm doar activitatea apărută
       DUPĂ crearea contului (nu inundăm userul cu tot istoricul vechi al clubului). */
    const floorTs = state.currentUser.createdAt ? new Date(state.currentUser.createdAt).getTime() : 0;
    const items = [];
    try {
        /* IZOLARE: doar cluburile din care FAC PARTE (ids). Activitatea acelor cluburi
           (sesiuni, evenimente, chat de la alții) e legitimă pentru toți membrii.
           Niciodată notificările altor cluburi/useri. */
        const [mres, eres, msgres] = await Promise.all([
            _notifMatchesQuery(ids),
            sb.from('events').select('id, club_id, title, date, created_at').in('club_id', ids).order('created_at', { ascending: false }).limit(40),
            sb.from('messages').select('id, club_id, sender_name, sender_id, text, timestamp, deleted_at').in('club_id', ids).order('timestamp', { ascending: false }).limit(60)
        ]);
        /* Rezolv numele celor care au înregistrat (pot fi din alte cluburi -> ocolesc RLS prin RPC). */
        const _recIds = Array.from(new Set((mres.data || []).map(g => g.recorded_by)
            .filter(x => typeof x === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(x))));
        const _recNames = {};
        if (_recIds.length) {
            try {
                const nr = await sb.rpc('names_for_ids', { p_ids: _recIds });
                if (!nr.error && Array.isArray(nr.data)) nr.data.forEach(r => { if (r && r.id) _recNames[r.id] = r.name || ''; });
            } catch (e) { console.warn('names_for_ids notif:', e && e.message); }
        }
        const _nmBy = id => _recNames[id] || (typeof displayNameById === 'function' && displayNameById(id)) || id;
        (mres.data || []).forEach(g => {
            /* ⚠ `matches` are DOAR coloana `date` — nu există `timestamp` în tabel.
               Obiectul local `g.timestamp` din `buildNotifications` e mapat din
               `m.date` (linia ~5061), deci cele două funcții folosesc de fapt
               ACELAȘI câmp; nu diverjeau, cum am presupus greșit.
               ⛔ NU cere `timestamp` de la `matches`: interogarea eșuează și cad
                  TOATE notificările de sesiune, nu doar cele vechi.
               ★ Cauza ordinii greșite era `date` = data PLANIFICATĂ. Se folosește
                 acum `created_at` (ora scrierii) prin același helper ca în
                 `buildNotifications`, ca cele două liste să nu poată diverge.
               EN: `matches` has no `timestamp` column; ordering now derives from
                   `created_at` through the same shared helper. */
            const ts = _sessionNotifTs(g.created_at ? new Date(g.created_at).getTime() : 0, new Date(g.date).getTime());
            if (!ts || ts < floorTs) return;
            const players = (typeof sbArray === 'function' ? sbArray(g.players) : g.players) || [];
            const iPlayed = Array.isArray(players) && players.indexOf(me) !== -1;
            const by = (g.recorded_by && g.recorded_by !== '—') ? g.recorded_by : null;
            let text;
            if (iPlayed && by && by !== me) text = window.t('notif.recByOther').replace('{by}', _nmBy(by)).replace('{game}', g.game_title);
            else if (by === me) text = window.t('notif.recByMe').replace('{game}', g.game_title);
            else if (iPlayed) text = window.t('notif.recIPlayed2').replace('{game}', g.game_title);
            else text = `${by ? _nmBy(by) + ' ' + window.t('notif.recordedVerb') : window.t('notif.recordedImpersonal')} ${window.t('notif.sessionOf')} ${g.game_title}`;
            items.push({ id: 's' + g.id, ts, icon: 'fa-dice', text, clubId: g.club_id, clubName: nameById[g.club_id], tab: 'history' });
        });
        (eres.data || []).forEach(e => {
            const ts = e.created_at ? new Date(e.created_at).getTime() : (e.date ? new Date(e.date).getTime() : Date.now());
            if (!ts || ts < floorTs) return;
            const when = e.date ? (' · ' + new Date(e.date).toLocaleDateString('en-GB')) : '';
            items.push({ id: 'e' + e.id, ts, icon: 'fa-calendar', text: `${window.t('notif.eventPrefix')}: ${e.title}${when}`, clubId: e.club_id, clubName: nameById[e.club_id], tab: 'calendar' });
        });
        (msgres.data || []).forEach(msg => {
            if ((msg.sender_id && msg.sender_id === me) || msg.sender_name === (state.currentUser && state.currentUser.name)) return; // nu mă notific pe mine
            if (msg.deleted_at) return;   /* v9 8.1-c3a: mesaj șters ⇒ fără rândul gol „Nume: ” (scăpat în B2a) */
            const ts = new Date(msg.timestamp).getTime(); if (!ts || ts < floorTs) return;
            const tx = msg.text || '';
            items.push({ id: 'm' + msg.id, ts, icon: 'fa-comment', text: `${msg.sender_name}: ${tx.slice(0, 60)}${tx.length > 60 ? '…' : ''}`, clubId: msg.club_id, clubName: nameById[msg.club_id], tab: 'chat' });
        });
    } catch (e) { console.warn('loadAllNotifications:', e && e.message); }
    /* Cereri de aderare la club (doar pentru cluburile unde ești admin) */
    try {
        const adminIds = clubs.filter(c => c.myRole === 'principal_admin' || c.myRole === 'secondary_admin').map(c => c.id);
        if (adminIds.length) {
            const jr = await sb.from('club_join_requests').select('id, club_id, profile_id, created_at, status').in('club_id', adminIds).eq('status', 'pending');
            const reqs = (jr.data || []);
            const pids = reqs.map(r => r.profile_id).filter(Boolean);
            const names = {};
            if (pids.length) { const pr = await sb.from('profiles').select('id, name').in('id', pids); (pr.data || []).forEach(p => names[p.id] = p.name); }
            reqs.forEach(r => {
                const ts = r.created_at ? new Date(r.created_at).getTime() : Date.now();
                const nm = names[r.profile_id] || window.t('dm.someone');
                items.push({ id: 'jr' + r.id, ts, icon: 'fa-user-clock', text: nm + ' ' + window.t('notif.joinReq') + ' ' + (nameById[r.club_id] || ''), joinClub: r.club_id, clubName: nameById[r.club_id] });
            });
        }
    } catch (e) { console.warn('join req notif:', e && e.message); }
    /* ⚡ PERF §54 (Batch 2a): se ridică în buclă, se consumă O SINGURĂ DATĂ după ea.
       EN: raised inside the loop, acted upon exactly once afterwards. */
    let _needReportsBadge = false;
    /* Notificări push directe către mine (tabela notifications) — cross-device. */
    try {
        const nres = await sb.from('notifications')
            .select('id, club_id, type, text, icon, tab, data, created_at')
            .eq('recipient_id', state.currentUser.id)
            .order('created_at', { ascending: false }).limit(60);
        (nres.data || []).forEach(n => {
            const ts = n.created_at ? new Date(n.created_at).getTime() : Date.now();
            if (!ts || ts < floorTs) return;
            /* Notificările generate de server (ex. expirare abonament) se localizează după tip. */
            let txt = n.text || '';
            if (n.type === 'sub_expiring') {
                const d = (n.data && n.data.date) || '';
                txt = window.t('notif.subExpiring').replace('{date}', d);
            } else if (n.type === 'new_report') {
                /* ⚡ PERF §54 (Batch 2a) — DOAR ridicăm steagul; reîmprospătarea se face
                   O SINGURĂ DATĂ, după bucla asta.
                   RO: aici se parcurg pana la 60 de notificari DEJA EXISTENTE in clopotel.
                       Varianta veche chema `refreshOpenReportsBadge()` pentru FIECARE
                       notificare de tip `new_report`, deci un cont cu 8 rapoarte in istoric
                       trimitea 8 cereri IDENTICE, in rafala, la fiecare incarcare a
                       clopotelului. Iar `loadAllNotifications` e chemata din PATRU locuri
                       (pornire, `loadCrossClubData`, reimprospatare realtime si un
                       `setInterval` din `startPeriodicUpdates`), deci se inmultea de fiecare
                       data. Masurat 22.08.2026: ~8 cereri `reports` per pornire, in loc de una.
                       Numarul afisat e acelasi — o numaratoare nu devine mai exacta daca o
                       ceri de opt ori la rand.
                   EN: this loop walks up to 60 EXISTING bell notifications and used to
                       refresh the badge once per `new_report` entry — 8 identical requests
                       for an account with 8 such notifications, multiplied by the four
                       callers of `loadAllNotifications`. One refresh after the loop gives
                       the exact same number. */
                _needReportsBadge = true;
                const rk = (n.data && n.data.reason) ? window.t('report.reason.' + n.data.reason) : '';
                txt = window.t('notif.newReport') + (rk ? ' — ' + rk : '');
            }
            items.push({ id: 'n' + n.id, ts, icon: n.icon || 'fa-bell', text: txt, clubId: n.club_id, clubName: nameById[n.club_id], tab: n.tab || '' });
        });
    } catch (e) { console.warn('push notif load:', e && e.message); }
    /* ⚡ PERF §54 (Batch 2a): O SINGURĂ reîmprospătare a numărului de rapoarte, oricâte
       notificări `new_report` ar fi fost în listă. Comportamentul vizibil e identic.
       EN: exactly one badge refresh regardless of how many `new_report` entries existed. */
    if (_needReportsBadge) {
        try {
            if (typeof refreshOpenReportsBadge === 'function') {
                refreshOpenReportsBadge().then(function () {
                    try { if (window.TO_NAV && TO_NAV.refresh) TO_NAV.refresh(); } catch (e) { logError('newReport.nav', e); }
                });
            }
        } catch (e) { logError('newReport.badge', e); }
    }
    /* Etichetează MEREU cu numele clubului din care vine notificarea. */
    items.forEach(it => { if (it.clubName && !it.joinClub) it.text = '[' + it.clubName + '] ' + it.text; });
    /* Z8-D: dedup pe id (plasă de siguranță) ca o sursă care revine să nu producă
       intrări duplicate în clopoțel — id-urile sunt deterministe (s/e/m/jr/n + id). */
    const _seenNotif = Object.create(null);
    state.allNotifications = items
        .filter(x => { if (x.id == null || _seenNotif[x.id]) return false; _seenNotif[x.id] = 1; return true; })
        .sort((a, b) => b.ts - a.ts).slice(0, 60);
    state.notifLoaded = true;
}

/* ===================== Share (text + imagine) ===================== */
function _se(s) { return window.escHtml(s); }

function buildShareData(kind, id) {
    /* Turnee: logica trăiește în tournaments.js (modul autonom). Dispatch vizibil. */
    if (kind === 'tournament' && typeof window.buildTournamentShareData === 'function')
        return window.buildTournamentShareData(id);
    if (kind === 'session') {
        const g = (state.games || []).find(x => String(x.id) === String(id)); if (!g) return null;
        const _nmShare = pid => (typeof displayNameById === 'function' && displayNameById(pid)) || pid;
        const lines = [];

        /* — Când & unde & cât — */
        const dt = g.timestamp ? new Date(g.timestamp) : null;
        if (dt) lines.push({ text: '📅 ' + dt.toLocaleDateString('en-GB') + ' ' + dt.toLocaleTimeString(appTimeLocale(), { hour: '2-digit', minute: '2-digit' }), muted: true });
        if (g.location) lines.push({ text: '📍 ' + g.location, muted: true });
        if (g.duration) lines.push({ text: '⏱ ' + g.duration + ' min', muted: true });

        /* — Tip sesiune (fix: pe câmpurile reale din state.games, nu g.meta) — */
        const isCoop = g.type === 'coop' || !!g.coop;
        const isCampaign = g.scoreKind === 'campaign';
        const isUniform = g.scoreMode === 'uniform';
        if (g.isTournament) {
            const tmeta = [];
            if (g.tournamentName) tmeta.push(g.tournamentName);
            if (g.format) tmeta.push(g.format);
            if (g.rounds) tmeta.push(g.rounds + ' ' + window.t('ra.rounds'));
            if (g.prize) tmeta.push(window.t('ra.prize') + ': ' + g.prize);
            if (g.fee) tmeta.push(window.t('ra.fee') + ': ' + g.fee);
            lines.push({ text: '🏆 ' + window.t('session.tournament') + (tmeta.length ? ' — ' + tmeta.join(' · ') : ''), strong: true });
        }
        if (g.friendly) lines.push({ text: '🤝 ' + window.t('hist.friendly'), muted: true });
        if (isCampaign) lines.push({ text: '🐉 ' + window.t('hist.campaign'), muted: true });
        if (isUniform) lines.push({ text: '⚖️ ' + window.t('hist.uniform'), muted: true });

        const scores = g.scores || {};
        const clubPts = g.clubPoints || {};
        const tbWon = pid => !!(g.tieBreaks && g.tieBreaks[pid]);
        const tbMark = pid => tbWon(pid) ? ' ⚔ (tie-break)' : '';
        const cpMark = pid => (isUniform && !isCampaign && clubPts[pid] != null)
            ? ' (' + (Number(clubPts[pid]) || 0) + ' ' + window.t('hist.clubPts') + ')' : '';

        if (isCoop) {
            const o = (g.coop && g.coop.outcome) || '';
            lines.push({ text: '🤝 ' + window.t('hist.coop') + ' — ' + (o === 'win' ? window.t('camp.win') : o === 'loss' ? window.t('camp.loss') : o), strong: true });
            const _dtxt = (typeof coopDifficultyText === 'function') ? coopDifficultyText(g.coop) : ((g.coop && g.coop.difficulty) || '');
            if (_dtxt) lines.push({ text: '🏔 ' + window.t('coop.difficultyShort') + ': ' + _dtxt });
            if (g.coop && g.coop.teamScore != null) lines.push({ text: '⭐ ' + window.t('hist.teamScore') + ': ' + g.coop.teamScore });
            if (isUniform && clubPts && Object.keys(clubPts).length) {
                const _pe = (g.participants || []).map(p => clubPts[p]).find(x => x != null);
                if (_pe != null) lines.push({ text: '⚖️ ' + (Number(_pe) || 0) + ' ' + window.t('hist.clubPts') + ' ' + window.t('coop.ptsEachSuffix') });
            }
            if (g.coop && g.coop.detail) lines.push({ text: '🎯 ' + window.t('coop.detailShort') + ': ' + g.coop.detail });
            if (g.coop && g.coop.scenario) lines.push({ text: '📖 ' + g.coop.scenario, muted: true });
            if (g.coop && (g.coop.mvp || g.coop.mvpCustom)) lines.push({ text: '🏅 ' + window.t('coop.mvpShort') + ': ' + (g.coop.mvpCustom || _nmShare(g.coop.mvp)) });
            const parts = (g.participants || []).map(_nmShare);
            if (parts.length) lines.push({ text: '👥 ' + parts.join(', ') });
        } else {
            /* Câștigătorul = din g.winners (respectă tie-break-ul, §18.f);
               fallback pe scorul maxim DOAR dacă winners lipsește. */
            let wIds = isCampaign ? [] : (Array.isArray(g.winners) ? g.winners.filter(Boolean) : []);
            const entries = Object.entries(scores);
            if (!wIds.length && !isCampaign && entries.length) {
                const max = Math.max(...entries.map(([, s]) => Number(s) || 0));
                wIds = entries.filter(([, s]) => (Number(s) || 0) === max).map(([n]) => n);
            }
            if (wIds.length) lines.push({ text: '🏆 ' + wIds.map(w => _nmShare(w) + tbMark(w)).join(', '), strong: true });
            /* Toate scorurile (la campania uniformă afișăm Punctele de Club, ca în istoric). */
            const dispObj = (isUniform && isCampaign && Object.keys(clubPts).length) ? clubPts : scores;
            Object.entries(dispObj)
                .sort((a, b) => (Number(b[1]) || 0) - (Number(a[1]) || 0))
                .forEach(([n, s]) => lines.push({ text: _nmShare(n) + ': ' + (Number(s) || 0) + cpMark(n) + tbMark(n) }));
            /* Participanți fără scor înregistrat — să nu lipsească nimeni. */
            (g.participants || []).filter(p => dispObj[p] === undefined)
                .forEach(p => lines.push({ text: _nmShare(p) + ': —', muted: true }));
        }

        /* — Invitați, roluri, notițe, înregistrat de — */
        if (Array.isArray(g.guests) && g.guests.length) {
            lines.push({ text: '➕ ' + window.t('hist.guests') + ': ' + g.guests.map(x => x && x.name ? x.name : x).join(', '), muted: true });
        }
        if (g.roles && typeof g.roles === 'object' && Object.keys(g.roles).length) {
            lines.push({ text: '🎭 ' + window.t('roles.heading') + ': ' + Object.keys(g.roles).map(p => _nmShare(p) + ' — ' + g.roles[p]).join(', '), muted: true });
        }
        if (g.notes) lines.push({ text: '📝 ' + g.notes, muted: true });
        if (g.recordedBy && g.recordedBy !== '—') lines.push({ text: '👤 ' + window.t('history.recordedBy') + ' ' + _nmShare(g.recordedBy), muted: true });

        return { title: '🎲 ' + (g.gameName || window.t('ra.fallbackName')), lines };
    }
    if (kind === 'poll') {
        const p = (state.polls || []).find(x => String(x.id) === String(id)); if (!p) return null;
        const lines = (p.options || []).map(o => ({ text: '• ' + o.text + ': ' + ((o.votes && o.votes.length) || 0) + ' ' + (((o.votes && o.votes.length) || 0) === 1 ? window.t('notif.voteSing') : window.t('notif.votePlur')) }));
        const totalV = (p.options || []).reduce((s, o) => s + ((o.votes && o.votes.length) || 0), 0);
        lines.push({ text: '🗳 ' + window.t('share.totalVotes') + ': ' + totalV, muted: true });
        return { title: '📊 ' + (p.question || window.t('notif.pollWord')), lines };
    }
    if (kind === 'event') {
        const e = (state.events || []).find(x => String(x.id) === String(id)); if (!e) return null;
        const lines = [];
        if (e.date) lines.push({ text: '🕒 ' + new Date(e.date).toLocaleString('en-GB'), muted: true });
        if (e.location) lines.push({ text: '📍 ' + e.location, muted: true });
        if (e.type === 'tournament') lines.push({ text: '🏆 ' + window.t('session.tournament'), strong: true });
        if (e.gameId) {
            const gm = Object.values(state.catalog || {}).find(c => String(c.dbId) === String(e.gameId));
            const gTitle = gm ? Object.keys(state.catalog).find(k => state.catalog[k] === gm) : null;
            if (gTitle) lines.push({ text: '🎲 ' + gTitle });
        }
        const pCount = e.participants ? Object.keys(e.participants).length : 0;
        lines.push({ text: '👥 ' + pCount + (e.maxParticipants ? ' / ' + e.maxParticipants : '') + ' ' + window.t('hist.participants'), muted: true });
        if (e.organizer && e.organizer !== '—') lines.push({ text: '👤 ' + ((typeof displayNameById === 'function' && displayNameById(e.organizer)) || e.organizer), muted: true });
        if (e.description) lines.push({ text: e.description });
        return { title: '📅 ' + (e.title || window.t('notif.eventWord')), lines };
    }
    if (kind === 'stats') {
        const players = getSortedPlayers(); const lines = [];
        lines.push({ text: '🎮 ' + window.t('notif.gamesPlayed') + ': ' + (state.games ? state.games.length : 0) });
        lines.push({ text: '👥 ' + window.t('notif.members') + ': ' + players.length });
        const top = players.slice(0, 3).map((p, i) => ({ text: (i + 1) + '. ' + p.name + ' — ' + (p.score || 0) + ' ' + window.t('common.ptsShort') }));
        if (top.length) { lines.push({ text: '🏆 ' + window.t('notif.topPlayers') + ':', strong: true }); top.forEach(t => lines.push(t)); }
        const counts = {}; (state.games || []).forEach(g => { counts[g.gameName] = (counts[g.gameName] || 0) + 1; });
        const most = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
        if (most) lines.push({ text: '🎯 ' + window.t('notif.mostPlayed') + ': ' + most[0] + ' (' + most[1] + 'x)' });
        lines.push({ text: '🌈 ' + window.t('stats.distinctGames') + ': ' + Object.keys(counts).length });
        return { title: '📈 ' + window.t('notif.statsWord') + ' ' + clubDisplayName(), lines };
    }
    if (kind === 'gamevote') {
        const v = state.gameVote; if (!v || !v.active) return null;
        const totals = (typeof gvTally === 'function') ? gvTally() : {};
        const cands = (v.candidates || []).slice()
            .sort((a, b) => (((totals[b] || {}).score) || 0) - (((totals[a] || {}).score) || 0));
        const lines = [];
        lines.push({ text: window.t('gv.shareCta'), strong: true });
        cands.forEach(c => { const sc = ((totals[c] || {}).score) || 0; lines.push({ text: '🎲 ' + ((window.TO_POLLS && window.TO_POLLS.gvTitle) ? window.TO_POLLS.gvTitle(c) : c) /* 8.16-3e: id -> titlu */ + (sc ? ' — ' + sc + ' ' + window.t('common.ptsShort') : '') }); });
        if (v.expiresAt) lines.push({ text: '⏳ ' + (typeof gvFmtDateTime === 'function' ? gvFmtDateTime(v.expiresAt) : new Date(v.expiresAt).toLocaleString('en-GB')), muted: true });
        const base = (typeof location !== 'undefined') ? (location.origin + location.pathname) : '';
        const link = base ? (base + '#polls') : '';
        if (link) lines.push({ text: '👉 ' + window.t('gv.shareOpen') + ' ' + link });
        return { title: '🎲 ' + window.t('gv.shareTitle'), lines, link };
    }
    return null;
}

/* Indicator „se generează…" care NU dispare până nu îl ștergem NOI.
   ⚠ GREȘEALA MEA, CORECTATĂ (25 aug. 2026): am refolosit clasa `.notification` ca
     să evit CSS nou, fără să citesc ce face. `style.css:1988` are:
        animation: slideInRight 0.3s ease, slideOutRight 0.3s ease 2.7s forwards;
     Adică indicatorul „persistent" se AUTO-ASCUNDEA după 3 s, exact ca un toast —
     defectul original („apare mesajul, apoi nimic") nu fusese reparat, ci doar
     mutat: înainte nodul se ștergea, apoi rămânea în DOM dar invizibil.
     Acum suprascriem animația: DOAR intrarea, fără `slideOutRight`.
   ⚠ A DOUA cauză, care explica de ce pe desktop nu se vedea iar pe telefon da:
     `slideInRight` pornește de la `translateX(400px); opacity:0` și ține 300 ms.
     Pe desktop, cu librăria deja în memorie, generarea ține ~200–400 ms ⇒ chip-ul
     era șters în timp ce încă „zbura" spre ecran, aproape transparent. Pe telefon
     generarea e mult mai lentă ⇒ animația se termina ⇒ se vedea.
     De aceea impunem o durată MINIMĂ vizibilă — întârzie doar ȘTERGEREA lui,
     imaginea fiind deja livrată.
   EN: the reused `.notification` class auto-hides after 3s and takes 300ms to
       slide in, so the "persistent" chip vanished on its own and was invisible on
       fast desktop runs. The outro animation is overridden and a minimum visible
       duration is enforced. */
const SHARE_BUSY_MIN_MS = 700;
let _shareBusyNode = null;
let _shareBusyShownAt = 0;

function _shareBusyShow(msg) {
    try {
        _shareBusyHide();
        const c = document.getElementById('notificationContainer');
        if (!c) return;
        const n = document.createElement('div');
        n.className = 'notification info';
        n.setAttribute('role', 'status');
        n.setAttribute('aria-live', 'polite');
        /* Suprascrie regula din style.css: păstrăm intrarea, tăiem auto-ascunderea. */
        n.style.animation = 'slideInRight 0.3s ease';
        n.innerHTML = '<div class="notification-icon"><i class="fas fa-spinner fa-spin"></i></div>' +
            '<div class="notification-content"><div class="notification-message">' + window.escHtml(msg) + '</div></div>';
        c.appendChild(n);
        _shareBusyNode = n;
        _shareBusyShownAt = Date.now();
    } catch (e) { logError('_shareBusyShow', e); }
}

function _shareBusyHide() {
    const n = _shareBusyNode;
    _shareBusyNode = null;
    if (!n) return;
    const kill = function () { try { if (n.parentNode) n.parentNode.removeChild(n); } catch (e) { /* noop */ } };
    const left = SHARE_BUSY_MIN_MS - (Date.now() - _shareBusyShownAt);
    if (left > 0) setTimeout(kill, left); else kill();
}

/* RO: `_shareCard` avea PROPRIUL încărcător de html2canvas, anterior lui
       `ensureLib` (§54.2) și fără memoizare: fiecare apăsare crea un `<script>`
       nou, deci a treia apăsare descărca a treia oară aceiași ~200 kB de la CDN —
       apăsatul repetat ÎNCETINEA generarea, nu o grăbea. `ensureLib` memorează
       promisiunea în `_cache` (app-core.js:143), deci N apăsări = O SINGURĂ
       descărcare, iar a doua generare e instantanee.
   EN: `_shareCard` had its own, pre-`ensureLib` loader with no memoisation, so
       every click started another ~200 kB download of the same library. */
async function _shareCard(title, lines) {
    await window.ensureLib('html2canvas'); /* respinge → prins de apelant (sb.imageGenFail) */
    const card = document.createElement('div');
    card.style.cssText = 'position:fixed; left:-9999px; top:0; width:600px; padding:28px; background:#1a1a2e; color:#fff; font-family:system-ui,-apple-system,sans-serif; border-radius:16px;';
    card.innerHTML = `<div style="display:flex;align-items:center;gap:10px;margin-bottom:14px;">
                <span style="font-size:22px;font-weight:800;color:#ff6b35;">${_se(title)}</span>
            </div>
            <div style="height:3px;background:linear-gradient(90deg,#ff6b35,#f9a826);border-radius:3px;margin-bottom:16px;"></div>
            ${lines.map(l => `<div style="font-size:16px;line-height:1.7;${l.strong ? 'font-weight:700;' : ''}${l.muted ? 'opacity:0.7;' : ''}">${_se(l.text != null ? l.text : l)}</div>`).join('')}
            <div style="margin-top:18px;font-size:13px;opacity:0.6;">via TurnOrder${currentClubId ? (' · ' + _se(clubDisplayName())) : ''}</div>`;
    document.body.appendChild(card);
    /* `finally`: cardul temporar se scoate din DOM pe ORICE ieșire (reușită,
       eroare sau excepție din html2canvas) — ca în versiunea veche. */
    try { return await html2canvas(card, { backgroundColor: '#1a1a2e', scale: 2 }); }
    finally { try { card.remove(); } catch (e) { /* noop */ } }
}

/* ⚠ DEFECT REPARAT (25 aug. 2026) — „uneori nu apare mesajul de generare".
   RO: `_shareBusyShow` punea chip-ul în DOM, dar imediat după porneam generarea.
       Când html2canvas e DEJA încărcat, `ensureLib` se rezolvă într-un microtask,
       deci browserul NU apucă să deseneze; iar html2canvas blochează apoi firul
       principal cu randarea. Rezultat: indicatorul era în DOM, dar invizibil —
       exact „lag-ul" perceput. La PRIMA generare mesajul apărea, fiindcă pauza de
       rețea de la CDN lăsa browserul să deseneze: de aici impresia de aleatoriu.
       Două cadre `requestAnimationFrame` garantează o desenare înainte de blocaj.
   ⚠ Plasa de `setTimeout`: în filă de fundal `rAF` nu rulează, iar fără ea
     generarea nu ar mai porni deloc. Costul e ~16–32 ms, plătiți conștient —
     fără ei, indicatorul nu are niciun rost.
   EN: the busy chip was appended and immediately followed by a main-thread block,
       so it never painted; two rAFs force a paint first, with a timeout fallback
       for background tabs where rAF does not fire. */
function _nextPaint() {
    return new Promise(function (res) {
        let done = false;
        const fin = function () { if (!done) { done = true; res(); } };
        try { requestAnimationFrame(function () { requestAnimationFrame(fin); }); } catch (e) { /* noop */ }
        setTimeout(fin, 200);
    });
}

/* Gardă anti-apăsare-dublă (tiparul din `login()`, §55.1: `_loginCaptchaWaiting`).
   RO: fără ea, fiecare apăsare construia un card, îl adăuga în DOM și pornea o
       randare html2canvas în paralel. Acum a doua apăsare doar reamintește că
       operațiunea e în curs.
   EN: guards against concurrent image generations from impatient double clicks. */
let _shareImageBusy = false;

async function shareItemImage(kind, id) {
    if (_shareImageBusy) { showNotification(t('sb.imageBusy'), 'info'); return; }
    const d = buildShareData(kind, id);
    if (!d) { showNotification(t('sb.nothingToShare'), 'error'); return; }
    _shareImageBusy = true;
    _shareBusyShow(t('sb.generatingImage'));
    await _nextPaint(); /* fără asta, indicatorul nu se vede niciodată — vezi nota de la _nextPaint */
    try {
        const canvas = await _shareCard(d.title, d.lines);
        const blob = await new Promise(function (res) { canvas.toBlob(res, 'image/png'); });
        if (!blob) { showNotification(t('sb.imageGenError'), 'error'); return; }
        const file = new File([blob], 'turnorder.png', { type: 'image/png' });
        /* ⚠ DEFECT REPARAT (25 aug. 2026) — imaginea se pierdea în tăcere.
           RO: `navigator.share()` cere ACTIVARE TRANZITORIE — permisiunea care vine
               din gestul utilizatorului și EXPIRĂ. Între apăsare și acest apel trec
               `_nextPaint`, randarea html2canvas și `toBlob`; pe generările lente
               permisiunea e deja expirată și apelul aruncă `NotAllowedError`
               („Must be handling a user gesture") — confirmat în consolă de tester.
               Ramura de descărcare era în `else`, deci NU se executa: utilizatorul
               nu primea nici partajare, nici fișier, nici mesaj. Acum orice eșec
               al partajării (în afară de anularea VOITĂ) cade pe descărcare.
           ⚠ Nu e o vindecare, ci o cădere elegantă: activarea tranzitorie nu poate
             fi păstrată peste o operațiune lungă. Pe generările lente vei primi
             descărcare în loc de foaia nativă de partajare.
           EN: `navigator.share()` needs transient user activation, which expires
               during image generation; the failure left the user with nothing.
               Any share failure other than a deliberate cancel now falls back to
               downloading the file. */
        let handled = false;
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
            try { await navigator.share({ files: [file], title: d.title }); handled = true; }
            catch (e) {
                /* Anularea din foaia nativă e o decizie a utilizatorului, nu un eșec:
                   nu descărcăm nimic peste ea și nu o raportăm ca eroare. */
                if (e && e.name === 'AbortError') handled = true;
                /* `NotAllowedError` e cazul de mai sus — tratat, deci nu-l mai
                   trimitem în Sentry ca zgomot. Orice altceva e neașteptat. */
                else if (!(e && e.name === 'NotAllowedError')) logError('shareItemImage', e);
            }
        }
        if (!handled) {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url;
            a.download = (d.title || 'turnorder').replace(/[^\w]+/g, '_') + '.png'; a.click();
            setTimeout(function () { URL.revokeObjectURL(url); }, 2000);
            showNotification(t('sb.imageDownloaded'), 'success');
        }
    } catch (e) {
        /* §46 #28c: înainte eșecul era înghițit tăcut (doar toast), deci nu ajungea
           niciodată în Sentry / `client_errors` — nu se putea diagnostica. */
        logError('shareItemImage', e);
        showNotification(t('sb.imageGenFail'), 'error');
    } finally {
        /* Pe ORICE ieșire: garda se eliberează și indicatorul dispare. Fără asta,
           un singur eșec ar bloca definitiv butonul „Ca imagine". */
        _shareImageBusy = false;
        _shareBusyHide();
    }
}

async function shareItemText(kind, id) {
    const d = buildShareData(kind, id); if (!d) { showNotification(t('sb.nothingToShare'), 'error'); return; }
    const text = d.title + '\n' + d.lines.map(l => (l.text != null ? l.text : l)).join('\n') + '\n— via TurnOrder' + (currentClubId ? (' · ' + clubDisplayName()) : '');
    if (navigator.share) {
        try { await navigator.share({ title: d.title, text }); return; }
        catch (e) { if (e && e.name === 'AbortError') return; }
    }
    try { await navigator.clipboard.writeText(text); showNotification(t('sb.copiedPasteWhatsApp'), 'success'); }
    catch (e) { await promptModal(window.t('copy.prompt'), text); }
}

/* RO: MIGRAT la delegare `data-action` (golden standard §5.1), la cererea
       explicită a interlocutorului. Înainte: `onclick="shareItemText('${kind}','${id}')"`
       — `kind` și `id` erau INTERPOLATE ÎN COD EXECUTABIL, deci corectitudinea
       depindea de escaparea manuală a fiecărei linii. Acum stau în `dataset` pe
       meniu, ca DATE: chiar dacă escaparea ar scăpa, nu se execută nimic.
       Textele „Ca text"/„Ca imagine" erau scrise direct în română. Cheile
       `share.asText` / `share.asImage` EXISTAU DEJA în i18n.js și erau folosite
       în celălalt meniu de share (linia ~6682) — doar aici nu.
   EN: migrated to `data-action` delegation; kind/id are now data, not code. The
       two labels are wired to the pre-existing `share.asText`/`share.asImage`. */
function openShareMenu(kind, id, btn) {
    closeShareMenu();
    const menu = document.createElement('div'); menu.id = 'shareMenu';
    menu.style.cssText = 'position:fixed; z-index:10000; background:var(--bg-card,#1e1e2e); border:1px solid var(--border,#333); border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,0.4); padding:6px; min-width:160px;';
    menu.dataset.kind = String(kind == null ? '' : kind);
    menu.dataset.shareId = String(id == null ? '' : id);
    menu.innerHTML = `
        <button type="button" class="btn btn-sm btn-secondary" style="width:100%; margin-bottom:4px;" data-action="share-text"><i class="fas fa-font"></i> ${t('share.asText')}</button>
        <button type="button" class="btn btn-sm btn-secondary" style="width:100%;" data-action="share-image"><i class="fas fa-image"></i> ${t('share.asImage')}</button>`;
    /* Ascultătorul stă pe PĂRINTE (meniul), pus o dată la construire. Meniul e
       distrus la închidere, deci nu rămâne niciun ascultător orfan. */
    menu.addEventListener('click', function (ev) {
        const el = ev.target && ev.target.closest ? ev.target.closest('[data-action]') : null;
        if (!el || !menu.contains(el)) return;
        const act = el.dataset.action;
        const k = menu.dataset.kind, i = menu.dataset.shareId;
        closeShareMenu(); /* k/i sunt deja citite, deci închiderea e sigură */
        if (act === 'share-text') shareItemText(k, i);
        else if (act === 'share-image') shareItemImage(k, i);
    });
    document.body.appendChild(menu);
    /* ⚠ DEFECT REPARAT (30 aug. 2026) — meniul „pleca" de lângă buton la derulare.
       RO: era `position:absolute` poziționat cu `window.scrollY/X`, deci ancorat de
           PAGINĂ, nu de buton. Într-un modal cu scroll propriu, `window.scrollY` nu
           se schimbă: butonul urca, meniul rămânea pe loc și plutea peste conținut.
           Se mai deschidea și mereu în JOS, deci lângă marginea de jos ieșea din
           ecran. Acum e `position:fixed` cu coordonate din viewport, reașezat la
           fiecare derulare, cu deschidere în SUS când nu încape jos.
       EN: was absolute + page scroll offsets, so it detached from the button inside
           any inner scroller, and always opened downwards. Now fixed + viewport
           coords, repositioned on scroll, flipping upwards when there's no room. */
    _shareMenuAnchor = btn;
    _positionShareMenu(menu, btn);
    _shareMenuReposition = function () {
        /* O singură reașezare pe cadru: fără asta am citi layout-ul la fiecare
           eveniment de scroll, adică exact „forced reflow" în buclă (§54.4). */
        if (_shareMenuRaf) return;
        _shareMenuRaf = requestAnimationFrame(function () {
            _shareMenuRaf = 0;
            const m = document.getElementById('shareMenu');
            if (m && _shareMenuAnchor) _positionShareMenu(m, _shareMenuAnchor);
        });
    };
    /* `true` = fază de CAPTURĂ: scroll-ul din interiorul unui modal NU produce
       evenimente pe `window` în faza de bubbling, deci fără asta reașezarea nu
       s-ar declanșa tocmai în cazul care a generat defectul. */
    window.addEventListener('scroll', _shareMenuReposition, true);
    window.addEventListener('resize', _shareMenuReposition);
    /* ⚠ DEFECT VECHI REPARAT (25 aug. 2026) — „trebuie apăsat de 2-3 ori".
       RO: ascultătorul de închidere se ștergea singur DOAR dacă chiar închidea
           meniul. Când utilizatorul apăsa „Ca text"/„Ca imagine",
           `menu.contains(e.target)` era adevărat ⇒ `h` nu făcea nimic ȘI NU se
           dezînregistra: rămânea pe `document` pentru totdeauna, cu o referință
           la un meniu deja șters. La următoarea apăsare pe un buton de share,
           `openShareMenu` construia meniul nou, click-ul urca la `document`,
           `h`-ul vechi se trezea, vedea că vechiul lui meniu nu conține ținta și
           chema `closeShareMenu()` — care ștergea MENIUL NOU. Abia a doua apăsare
           funcționa. Fiecare partajare lăsa încă un `h` orfan, deci după două
           partajări era nevoie de trei apăsări. Explică și telefonul: prima
           apăsare deschidea meniul, care era șters instantaneu.
       ACUM: referința se ține într-o variabilă unică, iar `closeShareMenu()` o
           dezînregistrează ORICUM s-ar închide meniul ⇒ zero ascultători orfani.
       EN: the outside-click closer only unregistered itself when it actually
           closed the menu, so choosing an item left it on `document` forever; the
           stale listener then killed the NEXT menu right after it opened. The
           reference is now held centrally and always removed on close. */
    setTimeout(function () {
        if (document.getElementById('shareMenu') !== menu) return; /* deja închis între timp */
        _shareMenuOutsideHandler = function (e) {
            if (!menu.contains(e.target) && e.target !== btn) closeShareMenu();
        };
        document.addEventListener('click', _shareMenuOutsideHandler);
    }, 50);
}

let _shareMenuOutsideHandler = null;
let _shareMenuAnchor = null;
let _shareMenuReposition = null;
let _shareMenuRaf = 0;

/* Așază meniul de partajare lângă butonul care l-a deschis, în coordonate de
   VIEWPORT (`position:fixed`). Chemat la deschidere și la fiecare derulare.
   RO: dacă butonul a ieșit COMPLET din ecran, meniul se închide — unul ancorat
       de ceva invizibil n-are ce ancora. Nu se închide la simpla derulare.
   EN: closes only when the anchor is fully off-screen, not on any scroll. */
function _positionShareMenu(menu, btn) {
    const r = btn.getBoundingClientRect();
    if (r.bottom < 0 || r.top > window.innerHeight || r.right < 0 || r.left > window.innerWidth) {
        closeShareMenu();
        return;
    }
    const M = 8;                                   /* margine minimă față de marginile ecranului */
    const h = menu.offsetHeight || 96;             /* înălțimea REALĂ, nu ghicită */
    const w = menu.offsetWidth || 176;
    let top = r.bottom + 6;
    /* Nu încape dedesubt, dar încape deasupra => se deschide în SUS. */
    if (top + h + M > window.innerHeight && (r.top - h - 6) >= M) top = r.top - h - 6;
    top = Math.max(M, Math.min(top, window.innerHeight - h - M));
    const left = Math.max(M, Math.min(r.left, window.innerWidth - w - M));
    menu.style.top = top + 'px';
    menu.style.left = left + 'px';
}

function closeShareMenu() {
    if (_shareMenuOutsideHandler) {
        try { document.removeEventListener('click', _shareMenuOutsideHandler); } catch (e) { /* noop */ }
        _shareMenuOutsideHandler = null;
    }
    /* Aceeași disciplină ca la ascultătorul de click (vezi nota din `openShareMenu`):
       se dezînregistrează ORICUM s-ar închide meniul, altfel rămân orfani pe `window`. */
    if (_shareMenuReposition) {
        try {
            window.removeEventListener('scroll', _shareMenuReposition, true);
            window.removeEventListener('resize', _shareMenuReposition);
        } catch (e) { /* noop */ }
        _shareMenuReposition = null;
    }
    if (_shareMenuRaf) { try { cancelAnimationFrame(_shareMenuRaf); } catch (e) { /* noop */ } _shareMenuRaf = 0; }
    _shareMenuAnchor = null;
    const m = document.getElementById('shareMenu');
    if (m) m.remove();
}

/* 8.16-3c (31 oct. 2026): partajarea votului „Ce jucam" (gvLocale, gvFmtDateTime, WhatsApp, foaia nativa,
   fereastra de partajare) a fost MUTATA identic in polls.js. EN: game-vote sharing moved to polls.js. */

/* ----- Avatar: poză (data URL/http) sau emoji ----- */
function isPhotoAvatar(a) { return typeof a === 'string' && (a.indexOf('data:') === 0 || a.indexOf('http') === 0); }
/* v9 S1 — SECURITATE: `profiles.avatar` e scris de utilizator, iar serverul nu-i
   restrânge formatul (V9_AVATAR_DIAGNOSTIC.sql, 13 oct. 2026). Înainte, emoji-ul intra
   NEESCAPAT în HTML, iar adresa pozei era escapată pentru `onclick` în ordinea greșită
   (§66.3) ⇒ un avatar construit rula cod la afișare / la clic (dovedit în jsdom).
   Acum: poza doar ca `data:image/…;base64,…` (formatul scris de `pfPhotoPick`) sau
   `https://`, fără ghilimele / spații / `<>` / backslash, cu `escHtml` în `src` și
   `attrJs` în `onclick`; emoji-ul escapat, max. 16 caractere (lista are 1–2); altfel
   silueta. Pentru valorile reale ieșirea e identică (verificat). Verificarea caută
   caracterele interzise în loc să valideze tot șirul cu o clasă: pe o poză de 16 kB,
   0,007 ms per avatar față de 0,09 ms (măsurat).
   EN: strict photo formats, escHtml for src, attrJs for onclick, escaped short emoji,
   otherwise the default icon; output for legitimate values is byte-identical. */
function avatarHtml(a, size) {
    size = size || 42;
    const icon = `<i class="fas fa-user-circle" style="color:var(--text-secondary);font-size:${size}px;"></i>`;
    if (isPhotoAvatar(a)) {
        if (!/^(?:data:image\/(?:png|jpe?g|webp|gif);base64,|https:\/\/)/.test(a) || /[\s"'<>\\`]/.test(a)) return icon;
        return `<img src="${window.escHtml(a)}" onclick="event.stopPropagation(); if(typeof openLightbox==='function') openLightbox('${window.attrJs(a)}')" style="width:${size}px;height:${size}px;border-radius:50%;object-fit:cover;cursor:zoom-in;">`;
    }
    if (a) {
        const txt = String(a);
        if (Array.from(txt).length > 16) return icon;
        return `<span style="font-size:${Math.round(size * 0.62)}px;line-height:1;">${window.escHtml(txt)}</span>`;
    }
    return icon;
}
function pfPhotoPick(input) {
    const f = input.files && input.files[0]; if (!f) return;
    if (!/^image\//.test(f.type)) { showNotification(t('sb.chooseImageFile'), 'error'); return; }
    const reader = new FileReader();
    reader.onload = e => {
        const img = new Image();
        img.onload = () => {
            const max = 256; let w = img.width, h = img.height;
            if (w > h) { if (w > max) { h = Math.round(h * max / w); w = max; } }
            else { if (h > max) { w = Math.round(w * max / h); h = max; } }
            const c = document.createElement('canvas'); c.width = w; c.height = h;
            c.getContext('2d').drawImage(img, 0, 0, w, h);
            const data = c.toDataURL('image/jpeg', 0.82);
            const hidden = document.getElementById('pfPhoto'); if (hidden) hidden.value = data;
            const sel = document.getElementById('pfAvatar'); if (sel) sel.value = '';
            const mp = document.getElementById('pfAvatarModalPreview'); if (mp) mp.innerHTML = `<img src="${data}" style="width:84px;height:84px;border-radius:50%;object-fit:cover;">`;
            const prev = document.getElementById('pfPhotoPreview'); if (prev) prev.innerHTML = `<img src="${data}" style="width:56px;height:56px;border-radius:50%;object-fit:cover;">`;
            document.querySelectorAll('#pfAvatarPicker .pf-emoji-btn').forEach(b => b.classList.remove('is-on'));
            showNotification(t('profile.photoReady'), 'info');
        };
        img.src = e.target.result;
    };
    reader.readAsDataURL(f);
}
function pfPhotoRemove() {
    const h = document.getElementById('pfPhoto'); if (h) h.value = '';
    const mp = document.getElementById('pfAvatarModalPreview'); if (mp) mp.innerHTML = '<i class="fas fa-user-circle" style="color:var(--text-secondary);font-size:84px;"></i>';
    const prev = document.getElementById('pfPhotoPreview'); if (prev) prev.innerHTML = '<i class="fas fa-user-circle" style="color:var(--text-secondary);font-size:56px;"></i>';
}

/* Popup unic pentru editarea pozei/avatarului (creionul de pe poza din header). */
function openAvatarEditor() {
    if (document.getElementById('avatarEditorModal')) return;
    const photo = (document.getElementById('pfPhoto') && document.getElementById('pfPhoto').value) || '';
    const emoji = (document.getElementById('pfAvatar') && document.getElementById('pfAvatar').value) || '';
    const cur = photo || emoji || '';
    const previewHtml = cur ? avatarHtml(cur, 84) : '<i class="fas fa-user-circle" style="color:var(--text-secondary);font-size:84px;"></i>';
    const grid = PROFILE_AVATARS.map(a => `<button type="button" class="pf-emoji-btn${(a === emoji && !photo) ? ' is-on' : ''}" data-av="${a}" onclick="pfPickAvatar('${a}')">${a}</button>`).join('');
    const modal = document.createElement('div');
    modal.className = 'modal active'; modal.id = 'avatarEditorModal';
    modal.onclick = (e) => { if (e.target === modal) closeAvatarEditor(); };
    modal.innerHTML = `<div class="modal-content" style="max-width:420px;">
        <div class="modal-header"><h2><i class="fas fa-user-pen"></i> ${t('profile.editPhoto')}</h2>
        <button class="modal-close" onclick="closeAvatarEditor()">×</button></div>
        <div style="text-align:center; margin-bottom:14px;"><div id="pfAvatarModalPreview" style="display:inline-block; line-height:1;">${previewHtml}</div></div>
        <div class="form-group">
            <label for="pfPhotoFile">${t('profile.photo')}</label>
            <div style="display:flex; gap:8px; flex-wrap:wrap;">
                <input type="file" id="pfPhotoFile" accept="image/*" style="display:none;" onchange="pfPhotoPick(this)">
                <button type="button" class="btn btn-sm" onclick="document.getElementById('pfPhotoFile').click()"><i class="fas fa-camera"></i> ${t('profile.attachPhoto')}</button>
                <button type="button" class="btn btn-sm btn-secondary" onclick="pfPhotoRemove()"><i class="fas fa-times"></i> ${t('profile.removePhoto')}</button>
            </div>
            <small style="color:var(--text-secondary);">${t('profile.photoNote')}</small>
        </div>
        <div class="form-group">
            <span class="form-section-label">${t('profile.avatar')}</span>
            <div id="pfAvatarPicker" class="pf-emoji-grid">${grid}</div>
        </div>
        <div class="btn-group" style="margin-top:6px;">
            <button class="btn btn-success" onclick="closeAvatarEditor()"><i class="fas fa-check"></i> ${t('common.done')}</button>
        </div>
    </div>`;
    document.body.appendChild(modal);
}
function closeAvatarEditor() {
    const m = document.getElementById('avatarEditorModal'); if (m) m.remove();
    const photo = (document.getElementById('pfPhoto') && document.getElementById('pfPhoto').value) || '';
    const emoji = (document.getElementById('pfAvatar') && document.getElementById('pfAvatar').value) || '';
    const cur = photo || emoji || '';
    const hdr = document.getElementById('pfHeaderAvatar');
    if (hdr) hdr.innerHTML = cur ? avatarHtml(cur, 56) : '<i class="fas fa-user-circle" style="color:var(--text-secondary);font-size:56px;"></i>';
}

/* ----- Ștergere cont (dezactivare 30 zile -> ștergere definitivă) ----- */
/* ===== RE-AUTENTIFICARE pentru acțiuni sensibile (componenta 3, folosit de GDPR) =====
   Verifică parola contului prin Supabase Auth (signInWithPassword pe emailul curent).
   NU stochează parola. Întoarce Promise<boolean>. În demo: trece direct (true).
   Conturi pur-OAuth (fără parolă): verificarea eșuează -> hint să-și seteze o parolă. */
window.reauthModal = function (opts) {
    opts = opts || {};
    return new Promise(function (resolve) {
        if (typeof isDemoMode === 'function' && isDemoMode()) { resolve(true); return; }
        const email = state.currentUser && state.currentUser.email;
        if (!email || !sb) { resolve(false); return; }
        const esc = (typeof window.escHtml === 'function') ? window.escHtml : function (s) { return s == null ? '' : String(s); };
        const existing = document.getElementById('reauthModal'); if (existing) existing.remove();
        const ov = document.createElement('div');
        ov.className = 'modal active'; ov.id = 'reauthModal'; ov.style.zIndex = '2147483600';
        ov.setAttribute('role', 'dialog');
        let done = false;
        const finish = function (val) { if (done) return; done = true; try { ov.remove(); } catch (e) { logError('closeAvatarEditor', e); } document.removeEventListener('keydown', onKey); resolve(val); };
        ov.onclick = function (e) { if (e.target === ov) finish(false); };
        const title = esc(opts.title || t('reauth.title'));
        const msg = esc(opts.message || t('reauth.message'));
        ov.innerHTML = '<div class="modal-content" style="max-width:420px;">'
            + '<div class="modal-header"><h2><i class="fas fa-lock"></i> ' + title + '</h2></div>'
            + '<p style="color:var(--text-secondary); margin-bottom:12px;">' + msg + '</p>'
            + '<input type="password" id="reauthPass" class="form-control" placeholder="' + esc(t('reauth.placeholder')) + '" autocomplete="current-password">'
            + '<div id="reauthErr" style="color:var(--danger,#e74c3c); font-size:0.85em; margin-top:8px; display:none;"></div>'
            + '<div style="color:var(--text-secondary); font-size:0.78em; margin-top:10px;"><i class="fas fa-circle-info"></i> ' + esc(t('reauth.oauthHint')) + '</div>'
            + '<div style="display:flex; gap:10px; margin-top:16px;">'
            +   '<button class="btn btn-secondary" id="reauthCancel">' + esc(t('cm.cancel')) + '</button>'
            +   '<button class="btn" id="reauthOk" style="background:var(--gradient-primary,linear-gradient(135deg,#ff6b35,#ff8c42)); color:#fff;">' + esc(t('reauth.confirm')) + '</button>'
            + '</div></div>';
        document.body.appendChild(ov);
        const input = ov.querySelector('#reauthPass');
        const err = ov.querySelector('#reauthErr');
        const okBtn = ov.querySelector('#reauthOk');
        setTimeout(function () { try { input.focus(); } catch (e) { logError('closeAvatarEditor', e); } }, 50);
        const submit = async function () {
            const pass = input.value || '';
            if (!pass) { err.textContent = t('reauth.empty'); err.style.display = 'block'; return; }
            okBtn.disabled = true; err.style.display = 'none';
            try {
                const r = await sb.auth.signInWithPassword({ email: email, password: pass });
                if (r.error) { err.textContent = t('reauth.wrong'); err.style.display = 'block'; okBtn.disabled = false; try { input.select(); } catch (e) { logError('closeAvatarEditor', e); } return; }
                finish(true);
            } catch (e) { err.textContent = t('reauth.wrong'); err.style.display = 'block'; okBtn.disabled = false; }
        };
        okBtn.onclick = submit;
        ov.querySelector('#reauthCancel').onclick = function () { finish(false); };
        const onKey = function (e) { if (e.key === 'Enter') { e.preventDefault(); submit(); } else if (e.key === 'Escape') finish(false); };
        document.addEventListener('keydown', onKey);
    });
};

/* True dacă utilizatorul are o parolă REALĂ setată.
   SURSĂ DE ADEVĂR: RPC `has_real_password()` (SECURITY DEFINER) care citește
   auth.users.encrypted_password pe auth.uid() — NEFORJABIL de client.
   NU ne mai bazăm pe user_metadata.has_password (era client-writable → forjabil,
   V6). Fallback (doar dacă RPC eșuează): semnale server-side din sesiune
   (identități / app_metadata.providers), niciodată user_metadata. La dubiu -> true. */
async function _hasPasswordIdentity() {
    try {
        try {
            const { data, error } = await sb.rpc('has_real_password');
            if (!error && typeof data === 'boolean') return data;
        } catch (e) { logError('_hasPasswordIdentity.rpc', e); }
        /* Fallback server-side (fără user_metadata): */
        const { data } = await sb.auth.getUser();
        const u = data && data.user;
        if (!u) return true;
        const ids = u.identities || [];
        if (ids.some(function (i) { return i && i.provider === 'email'; })) return true;
        const provs = (u.app_metadata && u.app_metadata.providers) || [];
        if (Array.isArray(provs) && provs.indexOf('email') !== -1) return true;
        return false;
    } catch (e) { return true; }
}

/* ===== Stratul de re-autentificare pentru acțiuni sensibile (componenta 3) =====
   Sursă unică: ce acțiune necesită parola actuală + ce mesaj afișează.
   Conturile pur-OAuth (Google, fără parolă) trec fără reauth (sesiune deja validă). */
const REAUTH_ACTIONS = {
    'ownership.transfer': 'reauth.actTransfer',
    'club.close': 'reauth.actClubClose',
    'club.leave': 'reauth.actClubLeave',
    'player.delete': 'reauth.actPlayerDelete',
    'member.ban': 'reauth.actBan',
    'member.unban': 'reauth.actUnban',
    /* Restaurarea unui backup și resetarea datelor rescriu/șterg tot clubul —
       aceeași clasă cu ban-ul și transferul de proprietate, deci aceeași
       confirmare prin parolă. Uniformitatea contează: un utilizator care a
       învățat că „acțiunile grave cer parola" nu trebuie să întâlnească aici
       o excepție cu alt ritual. */
    'backup.restore': 'reauth.actBackupRestore',
    'club.reset': 'reauth.actClubReset',
    'gdpr.export': 'gdpr.reauthExport',
    'gdpr.delete': 'gdpr.reauthDelete',
    'gdpr.cancelDelete': 'gdpr.reauthCancelDelete'
};
async function requireReauth(action) {
    try { if (typeof isDemoMode === 'function' && isDemoMode()) return true; } catch (e) { logError('requireReauth', e); }
    const hasPw = await _hasPasswordIdentity();
    if (!hasPw) { showNotification(t('reauth.needPassword'), 'error'); return false; } // blochează până își setează o parolă
    return await reauthModal({ message: t(REAUTH_ACTIONS[action] || 'reauth.message') });
}
window.requireReauth = requireReauth;

/* Re-autentificare prin cod pe email (OTP Supabase) — folosit la setarea PRIMEI
   parole pe un cont pur-OAuth, ca să nu poată cineva cu sesiunea deschisă să-și
   pună o parolă și să preia contul. Întoarce codul (nonce) sau null la anulare. */
async function _emailOtpReauth() {
    try {
        const r = await sb.auth.reauthenticate();
        if (r && r.error) { showNotification(t('reauth.otpSendFail') + r.error.message, 'error'); return null; }
    } catch (e) { showNotification(t('reauth.otpSendFail') + (e && e.message || ''), 'error'); return null; }
    showNotification(t('reauth.otpSent'), 'info');
    const code = await promptModal(t('reauth.otpPrompt'), '', { placeholder: t('reauth.otpPh') });
    if (code == null) return null;
    const c = String(code).trim();
    return c || null;
}

async function deleteMyAccount() {
    if (isDemoMode()) { showNotification(t('sb.unavailableDemoMode'), 'info'); return; }
    if (!state.currentUser) return;
    if (!await confirmModal(t('gdpr.confirmDelete'), { danger: true })) return;
    if (!await requireReauth('gdpr.delete')) return;
    try {
        const now = new Date().toISOString();
        const up = await sb.from('profiles').update({ deactivated_at: now }).eq('id', state.currentUser.id);
        if (up.error) throw up.error;
        state.currentUser.deletionPending = true;
        state.currentUser.deactivatedAt = now;
        showNotification(t('gdpr.deleteScheduled'), 'success');
        if (typeof refreshDeletionState === 'function') refreshDeletionState();
        _gdprMail('deletion_scheduled');
    } catch (e) {
        showNotification(t('sb.deleteNeedSql'), 'info');
    }
}

/* Anulează ștergerea programată (fereastra de grație de 30 zile). */
async function cancelDataDeletion() {
    if (isDemoMode() || !state.currentUser) return;
    /* Paritate cu `deleteMyAccount`: anularea unei ștergeri programate e o acțiune
       GDPR sensibilă. Fără reauth, cineva cu acces temporar la sesiunea ta deschisă
       putea anula TĂCUT o ștergere pe care ai cerut-o. Acum trece prin aceeași poartă.
       (Fără `danger` — anularea e o acțiune de PĂSTRARE, nu de distrugere.) */
    if (!await confirmModal(t('gdpr.confirmCancelDelete'))) return;
    if (!await requireReauth('gdpr.cancelDelete')) return;
    try {
        const up = await sb.from('profiles').update({ deactivated_at: null }).eq('id', state.currentUser.id);
        if (up.error) throw up.error;
        state.currentUser.deletionPending = false;
        state.currentUser.deactivatedAt = null;
        showNotification(t('gdpr.deleteCanceled'), 'success');
        if (typeof refreshDeletionState === 'function') refreshDeletionState();
        _gdprMail('deletion_canceled');
    } catch (e) { showNotification(t('gdpr.exportFail') + (e && e.message || ''), 'error'); }
}
window.cancelDataDeletion = cancelDataDeletion;

/* Randează în #pfDeletionState fie linkul de ștergere, fie starea „programat
   pentru ștergere" + buton de anulare (citește deactivated_at din DB la nevoie). */
async function refreshDeletionState() {
    let el = document.getElementById('pfDeletionState'); if (!el) return;
    const esc = (typeof window.escHtml === 'function') ? window.escHtml : function (s) { return s == null ? '' : String(s); };
    let pending = !!(state.currentUser && state.currentUser.deletionPending);
    let at = state.currentUser && state.currentUser.deactivatedAt;
    if (!pending && sb && state.currentUser && !isDemoMode()) {
        try {
            const r = await sb.from('profiles').select('deactivated_at').eq('id', state.currentUser.id).maybeSingle();
            if (r && r.data && r.data.deactivated_at) { pending = true; at = r.data.deactivated_at; state.currentUser.deletionPending = true; state.currentUser.deactivatedAt = at; }
        } catch (e) { logError('refreshDeletionState', e); }
    }
    el = document.getElementById('pfDeletionState'); if (!el) return;
    if (pending) {
        let dateTxt = '';
        try { dateTxt = new Date(new Date(at).getTime() + 30 * 86400000).toLocaleDateString(); } catch (e) { logError('refreshDeletionState', e); }
        el.innerHTML = '<div class="pf-deletion-pending">'
            + '<div><i class="fas fa-hourglass-half"></i> ' + esc(t('gdpr.deletePending').replace('{date}', dateTxt)) + '</div>'
            + '<button class="btn btn-secondary" style="margin-top:10px;" onclick="cancelDataDeletion()"><i class="fas fa-rotate-left"></i> ' + esc(t('gdpr.cancelDelete')) + '</button>'
            + '</div>';
    } else {
        el.innerHTML = '<div class="pf-danger-row"><a href="javascript:void(0)" onclick="deleteMyAccount()" class="pf-delete-link"><i class="fas fa-user-slash"></i> ' + esc(t('profile.deleteAccount')) + '</a></div>';
    }
}
window.refreshDeletionState = refreshDeletionState;
async function checkAccountDeactivation(authUser) {
    try {
        const r = await sb.from('profiles').select('deactivated_at').eq('id', authUser.id).single();
        if (r.error || !r.data || !r.data.deactivated_at) return true;
        const days = (Date.now() - new Date(r.data.deactivated_at).getTime()) / 86400000;
        if (days >= 30) { showNotification(t('sb.accountScheduledDelete'), 'error'); return true; }
        const ok = await confirmModal(t('sb.confirmReactivate').replace('{days}', Math.max(0, Math.ceil(30 - days))));
        if (ok) { await sb.from('profiles').update({ deactivated_at: null }).eq('id', authUser.id); showNotification(t('sb.accountReactivated'), 'success'); }
        return true;
    } catch (e) { return true; }
}
/* ===== GDPR — PORTARE DATE PERSONALE (export PDF + Excel, arhivă criptată) =====
   Necesită re-autentificare. Strânge DOAR datele proprii (RLS le limitează la
   utilizatorul curent). Pune PDF + XLSX într-un ZIP criptat AES (zip.js), cu o
   parolă GENERATĂ afișată o singură dată (NU parola contului). */
/* Trimite un email tranzacțional GDPR prin Edge Function `gdpr-mail` (Resend).
   Best-effort: dacă funcția nu e încă deployată, acțiunea decurge normal fără email.
   Funcția trimite DOAR pe emailul utilizatorului autentificat (derivat din JWT). */
async function _gdprMail(template) {
    try {
        if ((typeof isDemoMode === 'function' && isDemoMode()) || !sb || !state.currentUser) return;
        const lang = (document.documentElement.lang || 'en');
        await sb.functions.invoke('gdpr-mail', { body: { template: template, lang: lang } });
    } catch (e) { /* best-effort, nu blocăm acțiunea */ }
}

async function _gdprGatherData() {
    const me = state.currentUser.id;
    const out = { profile: null, subscription: null, memberships: [], friends: [], dms: [], sessions: [] };
    try { const r = await sb.from('profiles').select(window.PROFILE_COLS).eq('id', me).maybeSingle(); if (r && r.data) out.profile = r.data; } catch (e) { logError('_gdprGatherData', e); }
    /* Coloana `email` nu mai e citibilă via select (V8); pentru propriul export o luăm din sesiune. */
    if (out.profile && !out.profile.email && state.currentUser && state.currentUser.email) out.profile.email = state.currentUser.email;
    try { const r = await sb.from('user_subscriptions').select('*').eq('user_id', me).maybeSingle(); if (r && r.data) out.subscription = r.data; } catch (e) { logError('_gdprGatherData', e); }
    try { const r = await sb.from('club_members').select('club_id, role, status, muted').eq('profile_id', me); if (r && r.data) out.memberships = r.data; } catch (e) { logError('_gdprGatherData', e); }
    /* RLS limitează deja friendships/DM la rândurile mele -> select * fără filtru pe coloane necunoscute. */
    try { const r = await sb.from('friendships').select('*'); if (r && r.data) out.friends = r.data; } catch (e) { logError('_gdprGatherData', e); }
    try { const r = await sb.from('direct_messages').select('*').order('created_at', { ascending: true }).limit(5000); if (r && r.data) out.dms = r.data; } catch (e) { logError('_gdprGatherData', e); }
    /* v9 B2b: preferințele conversațiilor private (conversații șterse doar pentru tine, notificări oprite). */
    try { const r = await sb.from('dm_hidden').select('other_id, hidden_before').eq('user_id', me); if (r && r.data) out.dmHidden = r.data; } catch (e) { logError('_gdprGatherData', e); }
    try { const r = await sb.from('dm_mutes').select('other_id, created_at').eq('user_id', me); if (r && r.data) out.dmMutes = r.data; } catch (e) { logError('_gdprGatherData', e); }
    /* v9 B2c: și persoanele blocate de tine (doar blocările TALE: filtrul pe `blocker_id` contează, vezi `blockLoad`). */
    try { const r = await sb.from('user_blocks').select('blocked_id, created_at').eq('blocker_id', me); if (r && r.data) out.blocks = r.data; } catch (e) { logError('_gdprGatherData', e); }
    /* v9 8.7-D3: notificările pe club (doar cluburile cu altă alegere decât „Toate"; doar ale tale). */
    try { const r = await sb.from('club_notif_prefs').select('club_id, level, updated_at').eq('user_id', me).limit(1000); if (r && r.data) out.clubNotif = r.data; } catch (e) { logError('_gdprGatherData', e); }
    /* v9 8.8: preferințele de aspect ale chatului (doar ale tale, prin funcția de pe server). */
    try { const r = await sb.rpc('my_chat_prefs'); if (r && r.data) out.chatPrefs = r.data; } catch (e) { logError('_gdprGatherData', e); }
    /* v9 8.3: emoji-urile folosite recent + nuanțele alese (doar ale tale, prin funcția de pe server). */
    try { const r = await sb.rpc('my_emoji_recents'); if (r && r.data) out.emojiRecents = r.data; } catch (e) { logError('_gdprGatherData', e); }
    /* v9 8.4: reacțiile mele la mesaje (club + DM; doar ale tale, prin funcția de pe server). */
    try { const r = await sb.rpc('my_reactions'); if (r && r.data) out.reactions = [].concat((r.data.club || []).map(x => Object.assign({ kind: 'club' }, x)), (r.data.dm || []).map(x => Object.assign({ kind: 'dm' }, x))); } catch (e) { logError('_gdprGatherData', e); }
    try {
        const clubs = (state.myClubs || []).map(c => c.id);
        if (clubs.length) {
            const r = await sb.from('matches').select('id, club_id, game_title, players, winners, scores, date').in('club_id', clubs).order('date', { ascending: false }).limit(2000);
            if (r && r.data) out.sessions = r.data.filter(m => {
                const pl = (typeof sbArray === 'function' ? sbArray(m.players) : m.players) || [];
                return Array.isArray(pl) && pl.map(String).indexOf(String(me)) !== -1;
            });
        }
    } catch (e) { logError('_gdprGatherData', e); }
    return out;
}

function _gdprFlat(o) {
    const out = {};
    Object.keys(o || {}).forEach(k => {
        const v = o[k];
        if (k === 'avatar') { out[k] = (typeof v === 'string' && v.length > 80) ? '[omis]' : v; return; }
        out[k] = (v && typeof v === 'object') ? JSON.stringify(v) : v;
    });
    return out;
}

function _gdprBuildPdf(data) {
    const jsPDF = window.jspdf.jsPDF;
    const doc = new jsPDF();
    let y = 16;
    doc.setFontSize(16); doc.text('TurnOrder — ' + t('gdpr.pdfTitle'), 14, y); y += 8;
    doc.setFontSize(9); doc.setTextColor(120);
    doc.text(t('gdpr.generatedOn') + ' ' + new Date().toLocaleString(), 14, y); y += 6;
    if (data.profile && data.profile.email) { doc.text(String(data.profile.email), 14, y); y += 4; }
    doc.setTextColor(0);
    const head = (txt) => [[txt, '']];
    const p = data.profile || {};
    const profRows = [
        ['Name', p.name || ''], ['Display name', p.display_name || ''], ['User tag', p.user_tag || ''],
        ['Email', p.email || ''], ['Bio', p.bio || ''], ['Skill level', p.skill_level || ''],
        ['BGG', p.bgg_username || ''],
        ['Discoverable', String(p.is_discoverable)], ['Points', String(p.points != null ? p.points : '')],
        ['Created', p.created_at || ''], ['Preferred games', (Array.isArray(p.preferred_games) ? p.preferred_games.join(', ') : '')]
    ];
    doc.autoTable({ startY: y + 2, head: head(t('gdpr.secProfile')), body: profRows, styles: { fontSize: 8 }, headStyles: { fillColor: [255, 107, 53] } });
    if (data.subscription) {
        const s = data.subscription;
        doc.autoTable({ startY: doc.lastAutoTable.finalY + 6, head: head(t('gdpr.secSub')), body: [['Plan', s.plan || ''], ['Status', s.subscription_status || ''], ['Period', s.billing_period || ''], ['Renews', s.current_period_end || ''], ['Auto-renew', String(!s.cancel_at_period_end)]], styles: { fontSize: 8 }, headStyles: { fillColor: [255, 107, 53] } });
    }
    if (data.memberships && data.memberships.length) {
        doc.autoTable({ startY: doc.lastAutoTable.finalY + 6, head: [['Club ID', 'Role', 'Status']], body: data.memberships.map(m => [m.club_id, m.role, m.status]), styles: { fontSize: 8 }, headStyles: { fillColor: [255, 107, 53] } });
    }
    if (data.sessions && data.sessions.length) {
        doc.autoTable({ startY: doc.lastAutoTable.finalY + 6, head: [[t('gdpr.secSessions') + ' (' + data.sessions.length + ')', 'Date']], body: data.sessions.slice(0, 200).map(m => [m.game_title || '', (m.date ? new Date(m.date).toLocaleDateString() : '')]), styles: { fontSize: 8 }, headStyles: { fillColor: [255, 107, 53] } });
    }
    doc.autoTable({ startY: doc.lastAutoTable.finalY + 6, head: head(t('gdpr.secDms')), body: [[t('gdpr.dmCount'), String((data.dms || []).length)], [t('gdpr.dmHidden'), String((data.dmHidden || []).length)], [t('gdpr.dmMuted'), String((data.dmMutes || []).length)], [t('gdpr.blocked'), String((data.blocks || []).length)], [t('gdpr.clubNotif'), String((data.clubNotif || []).length)], [t('gdpr.chatPrefs'), String((data.chatPrefs || []).length)], [t('gdpr.emojiRecents'), String(((data.emojiRecents && data.emojiRecents[0] && data.emojiRecents[0].recents) || []).length)], [t('gdpr.reactions'), String((data.reactions || []).length)]], styles: { fontSize: 8 }, headStyles: { fillColor: [255, 107, 53] } });
    return doc.output('blob');
}

function _gdprSheet(rows) {
    return window.XLSX.utils.json_to_sheet((rows && rows.length) ? rows.map(_gdprFlat) : [{ note: 'none' }]);
}
function _gdprBuildXlsx(data) {
    const XLSX = window.XLSX;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, _gdprSheet(data.profile ? [data.profile] : []), 'Profile');
    XLSX.utils.book_append_sheet(wb, _gdprSheet(data.subscription ? [data.subscription] : []), 'Subscription');
    XLSX.utils.book_append_sheet(wb, _gdprSheet(data.memberships), 'Memberships');
    XLSX.utils.book_append_sheet(wb, _gdprSheet(data.friends), 'Friends');
    XLSX.utils.book_append_sheet(wb, _gdprSheet(data.dms), 'DirectMessages');
    XLSX.utils.book_append_sheet(wb, _gdprSheet(data.dmHidden), 'DMHidden');   /* v9 B2b */
    XLSX.utils.book_append_sheet(wb, _gdprSheet(data.dmMutes), 'DMMuted');
    XLSX.utils.book_append_sheet(wb, _gdprSheet(data.blocks), 'BlockedUsers');   /* v9 B2c */
    XLSX.utils.book_append_sheet(wb, _gdprSheet(data.clubNotif), 'ClubNotif');   /* v9 8.7-D3 */
    XLSX.utils.book_append_sheet(wb, _gdprSheet(data.chatPrefs), 'ChatPrefs');   /* v9 8.8 */
    XLSX.utils.book_append_sheet(wb, _gdprSheet(data.emojiRecents), 'EmojiRecents');   /* v9 8.3 */
    XLSX.utils.book_append_sheet(wb, _gdprSheet(data.reactions), 'Reactions');   /* v9 8.4 */
    XLSX.utils.book_append_sheet(wb, _gdprSheet((data.sessions || []).map(s => ({ id: s.id, club_id: s.club_id, game: s.game_title, date: s.date }))), 'Sessions');
    const arr = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    return new Blob([arr], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

function _loadZipJs() {
    return new Promise(function (res, rej) {
        if (window.zip && window.zip.ZipWriter) return res();
        const s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/@zip.js/zip.js@2.7.52/dist/zip-full.min.js';
        s.onload = function () { res(); };
        s.onerror = function () { rej(new Error('zip.js')); };
        document.head.appendChild(s);
    });
}
async function _gdprZip(pdfBlob, xlsxBlob, pwd, stamp) {
    await _loadZipJs();
    const z = window.zip;
    try { z.configure({ useWebWorkers: false }); } catch (e) { logError('_gdprZip', e); }
    const blobWriter = new z.BlobWriter('application/zip');
    const writer = new z.ZipWriter(blobWriter, { password: pwd, encryptionStrength: 3 });
    await writer.add('TurnOrder_data_' + stamp + '.pdf', new z.BlobReader(pdfBlob));
    await writer.add('TurnOrder_data_' + stamp + '.xlsx', new z.BlobReader(xlsxBlob));
    await writer.close();
    return await blobWriter.getData();
}

function _gdprPassword() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let s = '';
    try {
        const a = new Uint32Array(20); window.crypto.getRandomValues(a);
        for (let i = 0; i < 20; i++) s += chars[a[i] % chars.length];
    } catch (e) {
        for (let i = 0; i < 20; i++) s += chars[Math.floor(Math.random() * chars.length)];
    }
    return s.slice(0, 6) + '-' + s.slice(6, 12) + '-' + s.slice(12, 18);
}

function _gdprShowPassword(pwd) {
    const esc = (typeof window.escHtml === 'function') ? window.escHtml : function (s) { return s == null ? '' : String(s); };
    const aj = (typeof window.attrJs === 'function') ? window.attrJs : function (s) { return s == null ? '' : String(s); };
    const ov = document.createElement('div');
    ov.className = 'modal active'; ov.style.zIndex = '2147483600';
    ov.onclick = function (e) { if (e.target === ov) ov.remove(); };
    ov.innerHTML = '<div class="modal-content" style="max-width:440px;">'
        + '<div class="modal-header"><h2><i class="fas fa-key"></i> ' + esc(t('gdpr.pwTitle')) + '</h2><button class="modal-close" onclick="this.closest(\'.modal\').remove()">×</button></div>'
        + '<p style="color:var(--text-secondary); margin-bottom:10px;">' + esc(t('gdpr.pwIntro')) + '</p>'
        + '<div style="display:flex; gap:8px; align-items:center;"><code style="flex:1; font-size:1.15em; letter-spacing:1px; background:rgba(255,255,255,0.06); padding:10px 12px; border-radius:8px; user-select:all;">' + esc(pwd) + '</code>'
        + '<button class="btn btn-secondary" onclick="if(navigator.clipboard) navigator.clipboard.writeText(\'' + aj(pwd) + '\'); showNotification(t(\'gdpr.pwCopied\'),\'success\')"><i class="fas fa-copy"></i></button></div>'
        + '<p style="color:var(--accent-gold,#ffd700); font-size:0.82em; margin-top:12px;"><i class="fas fa-triangle-exclamation"></i> ' + esc(t('gdpr.pwWarn')) + '</p>'
        + '<div style="margin-top:14px;"><button class="btn" style="background:var(--gradient-primary,linear-gradient(135deg,#ff6b35,#ff8c42)); color:#fff;" onclick="this.closest(\'.modal\').remove()">' + esc(t('common.close')) + '</button></div>'
        + '</div>';
    document.body.appendChild(ov);
}

window.exportMyData = async function () {
    if (isDemoMode()) { showNotification(t('sb.unavailableDemoMode'), 'info'); return; }
    if (!state.currentUser || !state.currentUser.id) return;
    if (!await requireReauth('gdpr.export')) return;
    /* F2 (2 oct. 2026): din aug. 2026 librariile de export se incarca LA CERERE (ensureLib,
       app-core.js). Verificarea veche `window.jspdf && window.XLSX` nu le incarca, deci exportul
       GDPR esua pe orice cont. ensureLibOrWarn le incarca si, la esec real, afiseaza singura
       `gdpr.libMissing` (RO + EN). EN: load the export libraries on demand, like every other export. */
    if (!await window.ensureLibOrWarn('jspdf') || !await window.ensureLibOrWarn('xlsx')) return;
    showNotification(t('gdpr.preparing'), 'info');
    try {
        const data = await _gdprGatherData();
        const stamp = new Date().toISOString().slice(0, 10);
        const pdfBlob = _gdprBuildPdf(data);
        const xlsxBlob = _gdprBuildXlsx(data);
        const pwd = _gdprPassword();
        const zipBlob = await _gdprZip(pdfBlob, xlsxBlob, pwd, stamp);
        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement('a'); a.href = url; a.download = 'TurnOrder_personal_data_' + stamp + '.zip'; a.click();
        setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
        _gdprShowPassword(pwd);
        _gdprMail('export_done');
    } catch (e) { showNotification(t('gdpr.exportFail') + (e && e.message || ''), 'error'); }
};

/* ===== GDPR — FORMULAR DE SOLICITARE (rectificare/acces/restricționare/opoziție) =====
   Scrie în `feedback` (inbox owner platformă), tag-uit „[GDPR: categorie]". */
const GDPR_REQ_CATS = ['rectification', 'access', 'restriction', 'objection', 'other'];
window.openGdprRequest = function () {
    if (isDemoMode()) { showNotification(t('sb.unavailableDemoMode'), 'info'); return; }
    if (!state.currentUser) return;
    const esc = (typeof window.escHtml === 'function') ? window.escHtml : function (s) { return s == null ? '' : String(s); };
    const existing = document.getElementById('gdprReqModal'); if (existing) existing.remove();
    const modal = document.createElement('div');
    modal.className = 'modal active'; modal.id = 'gdprReqModal'; modal.style.zIndex = '100010';
    modal.onclick = function (e) { if (e.target === modal) modal.remove(); };
    const opts = GDPR_REQ_CATS.map(function (c) { return '<option value="' + c + '">' + esc(t('gdpr.reqCat.' + c)) + '</option>'; }).join('');
    const email = esc((state.currentUser && state.currentUser.email) || '');
    modal.innerHTML = '<div class="modal-content" style="max-width:480px;">'
        + '<div class="modal-header"><h2><i class="fas fa-envelope-open-text"></i> ' + esc(t('gdpr.reqTitle')) + '</h2><button class="modal-close" onclick="this.closest(\'.modal\').remove()">×</button></div>'
        + '<p style="color:var(--text-secondary); font-size:0.9em; margin-bottom:10px;">' + esc(t('gdpr.reqIntro')) + '</p>'
        + '<label for="gdprReqCat" style="font-size:0.85em;">' + esc(t('gdpr.reqCategory')) + '</label>'
        + '<select id="gdprReqCat" class="form-control" style="margin-bottom:10px;">' + opts + '</select>'
        + '<textarea id="gdprReqText" class="form-control" rows="4" maxlength="2000" placeholder="' + esc(t('gdpr.reqPh')) + '"></textarea>'
        + '<small style="color:var(--text-secondary); display:block; margin-top:8px;">' + esc(t('gdpr.reqReply').replace('{email}', email)) + '</small>'
        + '<div style="display:flex; gap:10px; margin-top:16px;">'
        +   '<button class="btn btn-secondary" onclick="this.closest(\'.modal\').remove()">' + esc(t('cm.cancel')) + '</button>'
        +   '<button class="btn" style="background:var(--gradient-primary,linear-gradient(135deg,#ff6b35,#ff8c42)); color:#fff;" onclick="submitGdprRequest()"><i class="fas fa-paper-plane"></i> ' + esc(t('gdpr.reqSend')) + '</button>'
        + '</div></div>';
    document.body.appendChild(modal);
};
window.submitGdprRequest = async function () {
    const cat = (document.getElementById('gdprReqCat') && document.getElementById('gdprReqCat').value) || 'other';
    const text = ((document.getElementById('gdprReqText') && document.getElementById('gdprReqText').value) || '').trim();
    if (!text) { showNotification(t('gdpr.reqEmpty'), 'error'); return; }
    try {
        const r = await sb.from('feedback').insert({
            message: '[GDPR: ' + cat + '] ' + text.slice(0, 2000),
            user_email: (state.currentUser && state.currentUser.email) || null,
            user_name: (state.currentUser && state.currentUser.name) || null,
            club_id: (typeof currentClubId !== 'undefined' && currentClubId) ? currentClubId : null,
            lang: (document.documentElement.lang || 'en'),
            url: (typeof location !== 'undefined' ? location.href : null)
        });
        if (r && r.error) { showNotification(t('gdpr.reqFail') + r.error.message, 'error'); return; }
        const m = document.getElementById('gdprReqModal'); if (m) m.remove();
        showNotification(t('gdpr.reqSent'), 'success');
    } catch (e) { showNotification(t('gdpr.reqFail') + (e && e.message || ''), 'error'); }
};

/* RO: MIGRAT la delegare `data-action`. Înainte:
       `onclick="event.stopPropagation(); openShareMenu('${kind}','${id}', this)"`,
       cu escapare manuală prin `.replace(/'/g, "\\'")` — corectă, dar fragilă:
       orice linie nouă trebuia să și-o amintească. Acum `kind`/`id` sunt `data-*`.
       ⚠ În PLUS: `title="Distribuie"` era HARDCODAT în română (deci un utilizator
       pe EN vedea „Distribuie" la hover). Cheia `common.share` exista deja.
   ⚠ `shareBtnHtml` are UN SINGUR apelant — `renderPollCard` (app-modules-2.js:399),
     chemat exclusiv din `renderPolls`, care randează în `#pollsList`. De aceea
     ascultătorul poate sta pe acel container, nu pe `document`.
   EN: migrated to data-action delegation on `#pollsList` (its only host) and the
       hardcoded Romanian tooltip is now translated. */
function shareBtnHtml(kind, id) {
    _bindShareOpenDelegation();
    const esc = window.escHtml;
    return `<button type="button" class="btn-share" title="${esc(t('common.share'))}" data-action="share-open" data-kind="${esc(kind)}" data-share-id="${esc(id)}" style="background:none; border:none; color:var(--text-secondary); cursor:pointer; padding:4px 8px; font-size:0.95em;"><i class="fas fa-share-nodes"></i></button>`;
}

/* Delegare pe `#pollsList` — element STATIC din index.html, deci ascultătorul
   supraviețuiește oricărui `innerHTML = ...` din `renderPolls`.
   ⚠ Faza de CAPTURE, intenționat: `onclick`-ul vechi chema `event.stopPropagation()`
   ÎNAINTE ca evenimentul să urce spre `.poll-card`. Un ascultător pe părinte, în
   faza de bubbling, ar rula DUPĂ un eventual handler de pe card — deci ar pierde
   acel comportament. În capture rulăm înaintea lui, ca înainte.
   (`.poll-card` nu are azi niciun `onclick` — verificat — dar păstrăm semantica.) */
let _shareOpenBound = false;
function _bindShareOpenDelegation() {
    if (_shareOpenBound) return;
    const host = document.getElementById('pollsList');
    if (!host) return;
    _shareOpenBound = true;
    host.addEventListener('click', function (ev) {
        const el = ev.target && ev.target.closest ? ev.target.closest('[data-action="share-open"]') : null;
        if (!el || !host.contains(el)) return;
        ev.preventDefault();
        ev.stopPropagation();
        openShareMenu(el.dataset.kind, el.dataset.shareId, el);
    }, true);
}

function getNotifications() {
    const base = state.notifLoaded ? (state.allNotifications || []) : buildNotifications();
    let all = base.slice();
    try { if (typeof window.getDmNotifItems === 'function') all = all.concat(window.getDmNotifItems()); } catch (e) { logError('getNotifications', e); }
    try { if (typeof window.getFriendNotifItems === 'function') all = all.concat(window.getFriendNotifItems()); } catch (e) { logError('getNotifications', e); }
    /* Ascunde notificările șterse: după ID (robust, indiferent de timestamp) ȘI
       tot ce e mai vechi decât ultima ștergere în bloc. */
    const clearedTs = getNotifClearedTs();
    const dismissed = getDismissedIds();
    all = all.filter(function (n) {
        if (!n || n.id == null) return false;
        if (dismissed.has(n.id)) return false;
        if (clearedTs && n.ts && n.ts <= clearedTs) return false;
        return true;
    });
    /* Deduplicare după id (mereu) — previne reapariția aceleiași notificări. */
    const seenIds = {};
    all = all.filter(function (n) { if (seenIds[n.id]) return false; seenIds[n.id] = 1; return true; });
    return all.sort((a, b) => new Date(b.ts) - new Date(a.ts));
}
function notifClearedKey() {
    const uid = state.currentUser && (state.currentUser.id || state.currentUser.email);
    return uid ? ('gbgsc_notif_cleared_ts_' + uid) : null;
}
function getNotifClearedTs() {
    const key = notifClearedKey(); if (!key) return 0;
    try { return Number(localStorage.getItem(key)) || 0; } catch (e) { return 0; }
}
function notifDismissedKey() {
    const uid = state.currentUser && (state.currentUser.id || state.currentUser.email);
    return uid ? ('gbgsc_notif_dismissed_ids_' + uid) : null;
}
function getDismissedIds() {
    const key = notifDismissedKey(); if (!key) return new Set();
    try { const raw = localStorage.getItem(key); return raw ? new Set(JSON.parse(raw)) : new Set(); } catch (e) { return new Set(); }
}
function saveDismissedIds(set) {
    const key = notifDismissedKey(); if (!key) return;
    try { let arr = Array.from(set); if (arr.length > 3000) arr = arr.slice(arr.length - 3000); localStorage.setItem(key, JSON.stringify(arr)); } catch (e) { logError('saveDismissedIds', e); }
}
function clearAllNotifications() {
    if (!state.currentUser) return;
    /* 1) Adună ID-urile notificărilor curente și marchează-le ca șterse (după ID). */
    let curIds = [];
    try { curIds = getNotifications().map(function (i) { return i.id; }); } catch (e) { logError('clearAllNotifications', e); }
    const dismissed = getDismissedIds();
    curIds.forEach(function (id) { dismissed.add(id); });
    saveDismissedIds(dismissed);
    /* 2) Prag de timp (ascunde și ce e mai vechi). */
    const nowTs = Date.now();
    const key = notifClearedKey();
    if (key) { try { localStorage.setItem(key, String(nowTs)); } catch (e) { logError('clearAllNotifications', e); } }
    /* 3) Marchează ca citite (fără badge). */
    try { const seen = getSeenIds() || new Set(); (state.allNotifications || []).forEach(function (i) { seen.add(i.id); }); saveSeenIds(seen); } catch (e) { logError('clearAllNotifications', e); }
    /* 4) PERSISTĂ SERVER-SIDE → permanent la refresh / re-login / alt dispozitiv. */
    if (typeof isDemoMode === 'function' && !isDemoMode() && sb && state.currentUser.id) {
        let arr = Array.from(dismissed); if (arr.length > 3000) arr = arr.slice(arr.length - 3000);
        try {
            sb.from('profiles')
                .update({ notif_cleared_at: new Date(nowTs).toISOString(), notif_dismissed: arr })
                .eq('id', state.currentUser.id)
                .then(function (r) { if (r && r.error) console.warn('notif clear persist:', r.error.message); }, function () {});
        } catch (e) { /* coloane lipsă → rulează turnorder_notif_clear.sql */ }
    }
    renderNotifPanel();
    renderNotifBadge();
}

/* Click pe o notificare -> du-te în clubul + secțiunea aferentă */
async function openNotif(clubId, tab, id) {
    const panel = document.getElementById('notifPanel'); if (panel) panel.classList.add('hidden');
    if (id) { const seen = getSeenIds() || new Set(); seen.add(id); saveSeenIds(seen); }
    /* ⚠ GARDĂ GENERALĂ: comutăm DOAR într-un club în care utilizatorul chiar e
       membru. O notificare poate purta un `club_id` greșit — moștenit din
       clubul expeditorului (defect reparat în `pushNotification`), rămas de la o
       notificare veche, sau dintr-un club din care utilizatorul a ieșit între
       timp. Fără garda asta, `switchClub` intra pe jumătate și lăsa un ecran gol
       cu numele implicit „Club", care părea un club fantomă.
       Fără club valid rămânem unde suntem: notificarea se marchează citită și
       panoul se închide — mai bine decât o navigare care duce nicăieri.
       EN: only switch into clubs the user actually belongs to; a stale or wrong
           club_id used to leave the app in a half-loaded, empty state. */
    const _potComuta = !!clubId && clubId !== currentClubId
        && Array.isArray(state.myClubs) && state.myClubs.some(c => c && c.id === clubId);
    if (_potComuta) {
        try { await switchClub(clubId); } catch (e) { logError('openNotif', e); }
    }
    try { const hub = document.getElementById('clubHub'); if (hub && !hub.classList.contains('hidden')) { hub.classList.add('hidden'); showMainApp(); } } catch (e) { logError('openNotif', e); }
    if (tab) { try { switchTab(tab); } catch (e) { logError('openNotif', e); } }
    renderNotifBadge();
}
async function openJoinReqNotif(clubId, notifId) {
    const panel = document.getElementById('notifPanel'); if (panel) panel.classList.add('hidden');
    if (notifId) { const seen = getSeenIds() || new Set(); seen.add(notifId); saveSeenIds(seen); }
    /* Aceeași gardă ca în `openNotif` — vezi nota de acolo. */
    if (clubId && clubId !== currentClubId
        && Array.isArray(state.myClubs) && state.myClubs.some(c => c && c.id === clubId)) {
        try { await switchClub(clubId); } catch (e) { logError('openJoinReqNotif', e); }
    }
    try { if (typeof openJoinRequests === 'function') openJoinRequests(); } catch (e) { logError('openJoinReqNotif', e); }
    renderNotifBadge();
}
function renderNotifBadge() {
    const bell = document.getElementById('notifBell');
    const badge = document.getElementById('notifBadge');
    if (!bell || !badge) return;
    /* v9 8.7-D8: aceeași cifră și pe iconița aplicației instalate (chat-core.js, `TO_CHAT.appBadge`). */
    if (!state.currentUser) { bell.classList.add('hidden'); if (window.TO_CHAT && window.TO_CHAT.appBadge) window.TO_CHAT.appBadge(0); return; }
    bell.classList.remove('hidden');
    const items = getNotifications();
    const seen = ensureSeenInit(items);
    /* ⚠ Pragul de cont intră în calcul ALĂTURI de lista locală de id-uri, ca marcajul
       „citit" să traverseze dispozitivele. EN: account-wide threshold counts too. */
    const readAt = getNotifReadAt();
    const unread = items.filter(n => !isNotifRead(n, seen, readAt)).length;
    if (window.TO_CHAT && window.TO_CHAT.appBadge) window.TO_CHAT.appBadge(unread);
    if (unread > 0) { badge.textContent = unread > 99 ? '99+' : unread; badge.classList.remove('hidden'); }
    else badge.classList.add('hidden');
    /* dacă panoul e deschis, reîmprospătează-l ca să apară imediat ce e nou */
    const panel = document.getElementById('notifPanel');
    if (panel && !panel.classList.contains('hidden')) renderNotifPanel();
}
/* ── SECURITATE (§9, Z9-A): panoul de notificări NU mai construiește `onclick` inline
   cu nume de utilizator interpolate în string-uri JS (vector de Stored XSS: un
   display_name de forma `');cod;//` se executa în sesiunea celui care deschidea
   clopoțelul). Acum: valorile pleacă DOAR ca atribute `data-*` (escapate cu escHtml,
   care escapează și apostroful) și click-ul e tratat prin DELEGARE de evenimente —
   numele nu mai ajunge niciodată într-un context executabil. Pattern identic cu
   `confirmModal` (§29). NU reintroduce `onclick` cu date de user aici.
   EN: notification panel uses data-* attributes + event delegation instead of inline
   onclick, so user-controlled names can never reach an executable JS context. ── */
function renderNotifPanel() {
    const panel = document.getElementById('notifPanel');
    if (!panel) return;
    const esc = window.escHtml;
    const items = getNotifications();
    const seen = ensureSeenInit(items);
    /* Citit o dată, folosit pentru toate rândurile — o citire din localStorage per
       randare, nu una per notificare. EN: read once, reused for every row. */
    const _notifReadAtNow = getNotifReadAt();
    const shown = items.slice(0, 50);
    const hint = window.t('notif.tapToOpen');
    const body = shown.length ? shown.map(n => {
        const unread = !isNotifRead(n, seen, _notifReadAtNow);
        const cid = n.clubId ? String(n.clubId) : '';
        const tab = n.tab || '';
        const kind = n.dmWith ? 'dm' : n.profileId ? 'friend' : n.joinClub ? 'join' : 'open';
        const attrs = [
            'data-nkind="' + esc(kind) + '"',
            'data-nid="' + esc(n.id) + '"',
            'data-ndm="' + esc(n.dmWith || '') + '"',
            'data-ndmname="' + esc(n.dmName || '') + '"',
            'data-npid="' + esc(n.profileId || '') + '"',
            'data-npname="' + esc(n.profileName || '') + '"',
            'data-njoin="' + esc(n.joinClub || '') + '"',
            'data-nclub="' + esc(cid) + '"',
            'data-ntab="' + esc(tab) + '"'
        ].join(' ');
        const openable = !!(tab || n.dmWith || n.profileId || n.joinClub);
        return `<div class="notif-item" ${attrs} style="padding:10px 12px; border-bottom:1px solid var(--border,#333); cursor:pointer; ${unread ? 'background:rgba(80,150,255,0.10);' : ''}">
            <i class="fas ${esc(n.icon)}" style="color:var(--text-secondary); margin-right:8px;"></i>${esc(n.text)}
            <div style="font-size:.72em; color:var(--text-secondary); margin-top:3px;">${esc(notifTimeAgo(n.ts))}${openable ? ' · ' + esc(hint) : ''}</div>
        </div>`;
    }).join('') : '<p style="padding:16px; color:var(--text-secondary);">' + esc(window.t('notif.empty')) + '</p>';
    panel.innerHTML = `
        <div style="padding:10px 12px; font-weight:700; border-bottom:1px solid var(--border,#333); display:flex; justify-content:space-between; align-items:center; gap:8px;">
            <span style="display:flex; align-items:center; gap:8px; min-width:0; flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;"><i class="fas fa-bell"></i> ${esc(window.t('notif.title'))}</span>
            <span style="display:flex; align-items:center; gap:14px; flex-shrink:0;">
                <i class="fas fa-clock-rotate-left" data-nact="toastlog" title="${esc(window.t('toastlog.title'))}" aria-label="${esc(window.t('toastlog.title'))}" style="cursor:pointer; color:var(--text-secondary); font-size:.95em;"></i>
                ${items.length ? `<i class="fas fa-check-double" data-nact="readall" title="${esc(window.t('notif.markAllRead'))}" aria-label="${esc(window.t('notif.markAllRead'))}" style="cursor:pointer; color:var(--accent-primary,#ff6b35); font-size:.95em;"></i>` : ''}
                ${items.length ? `<i class="fas fa-trash-can" data-nact="clearall" title="${esc(window.t('notif.clearAll'))}" aria-label="${esc(window.t('notif.clearAll'))}" style="cursor:pointer; color:var(--text-secondary); font-size:.95em;"></i>` : ''}
                <i class="fas fa-times" data-nact="close" style="cursor:pointer;"></i>
            </span>
        </div>
        <div style="max-height:60vh; overflow:auto;">${body}</div>`;
    bindNotifPanelDelegation(panel);
}

/* Delegare de evenimente pentru panoul de notificări (o singură dată per panou).
   EN: one-time event delegation for the notification panel. */
function bindNotifPanelDelegation(panel) {
    if (!panel || panel.dataset.notifBound === '1') return;
    panel.dataset.notifBound = '1';
    panel.addEventListener('click', function (ev) {
        try {
            const act = ev.target.closest('[data-nact]');
            if (act) {
                const a = act.getAttribute('data-nact');
                if (a === 'toastlog') { if (typeof openToastLog === 'function') openToastLog(); return; }
                if (a === 'readall') return markAllNotifRead();
                if (a === 'clearall') return clearAllNotifications();
                if (a === 'close') { panel.classList.add('hidden'); return; }
                return;
            }
            const row = ev.target.closest('.notif-item');
            if (!row || !panel.contains(row)) return;
            const d = row.dataset;
            const kind = d.nkind || 'open';
            if (kind === 'dm') return openDMFromNotif(d.ndm || '', d.ndmname || '', d.nid || '');
            if (kind === 'friend') return openFriendReqNotif(d.npid || '', d.npname || '', d.nid || '');
            if (kind === 'join') return openJoinReqNotif(d.njoin || '', d.nid || '');
            return openNotif(d.nclub || '', d.ntab || '', d.nid || '');
        } catch (e) { logError('notifPanelClick', e); }
    });
}
async function markAllNotifRead() {
    const items = getNotifications();
    const seen = getSeenIds() || new Set();
    items.forEach(i => seen.add(i.id));
    saveSeenIds(seen);
    /* Pragul local: instantaneu, ca interfața să reacționeze fără să aștepte rețeaua. */
    const nowTs = Date.now();
    setNotifReadAt(nowTs);
    renderNotifPanel();
    renderNotifBadge();
    /* PERSISTĂ SERVER-SIDE → supraviețuiește re-login-ului, ferestrei private și
       schimbării de dispozitiv. Același tipar ca `clearAllNotifications`.
       ⚠ MERGE peste valoarea REALĂ citită acum din DB, niciodată suprascriere: altfel
       am șterge `ownedGames` și restul cheilor din `profile_extra`. Dacă citirea eșuează,
       NU scriem — mai bine nepersistat decât date pierdute (lecția din `saveProfile`). */
    if (typeof isDemoMode === 'function' && isDemoMode()) return;
    if (!sb || !state.currentUser || !state.currentUser.id) return;
    try {
        const cur = await sb.from('profiles').select('profile_extra').eq('id', state.currentUser.id).maybeSingle();
        if (!cur || cur.error) { console.warn('notif read persist: nu am putut citi profile_extra -> NU scriu'); return; }
        const base = (cur.data && cur.data.profile_extra) || {};
        const merged = Object.assign({}, base, { notifReadAt: new Date(nowTs).toISOString() });
        const up = await sb.from('profiles').update({ profile_extra: merged }).eq('id', state.currentUser.id);
        if (up && up.error) { console.warn('notif read persist:', up.error.message); return; }
        /* Sincronizăm memoria cu ce e acum în DB (ca la `saveProfile`). */
        try {
            const _u = (state.currentUser.email && state.users && state.users[state.currentUser.email]) || null;
            if (_u) _u.profileExtra = merged;
            const _m = (typeof memberById === 'function') ? memberById(state.currentUser.id) : null;
            if (_m) _m.profileExtra = merged;
        } catch (e) { logError('markAllNotifRead', e); }
    } catch (e) { logError('markAllNotifRead', e); }
}
function toggleNotifPanel() {
    const panel = document.getElementById('notifPanel');
    if (!panel) return;
    if (panel.classList.contains('hidden')) {
        renderNotifPanel();
        panel.classList.remove('hidden');
        anchorPopup(panel, document.getElementById('notifBell'));
        /* Utilizatorul a deschis EXPLICIT clopoțelul → ocolim fereastra de prospețime.
           EN: user explicitly opened the bell, so bypass the freshness window. */
        try { loadAllNotifications({ force: true }).then(function () { renderNotifPanel(); renderNotifBadge(); }); } catch (e) { logError('toggleNotifPanel', e); }
    } else {
        panel.classList.add('hidden');
    }
}

function subscribeRealtimeChat() {    if (messagesChannel) { try { sb.removeChannel(messagesChannel); } catch (e) { logError('subscribeRealtimeChat', e); } messagesChannel = null; }
    /* v9 N1b: jeton per canal — deosebește canalul de acum de cele vechi, închise la
       schimbarea clubului (vezi `.subscribe` mai jos). */
    const _liveTok = {};
    messagesChannel = sb.channel('public:messages')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) => {
            const msg = payload.new;
            if (!msg) return;
            if (msg.club_id && msg.club_id !== currentClubId) return; // doar clubul activ
            if (state.chatMessages.some(m => String(m.id) === String(msg.id))) return; // anti-duplicat
            if (window.TO_CHAT && window.TO_CHAT.send && window.TO_CHAT.send.adopt('club', msg)) return;   /* v9 8.1-s: mesajul meu, încă „se trimite" */
            state.chatMessages.push({
                id: msg.id, sender: msg.sender_name, senderEmail: msg.sender_email, senderId: msg.sender_id || null,
                text: msg.text, timestamp: msg.timestamp,
                edited: !!msg.edited, replyTo: msg.reply_to || null, replySender: msg.reply_sender || null, replySnippet: msg.reply_snippet || null,
                /* v9 N1a (H1): pozele lipseau aici ⇒ un mesaj cu poze primit în timp real apărea
                   fără ele până la reîncărcare (confirmat pe Samsung). Aceeași mapare ca la
                   încărcarea de la pornire. EN: realtime rows now keep their photos. */
                photos: Array.isArray(msg.photos) ? msg.photos : [],
                mentions: Array.isArray(msg.mentions) ? msg.mentions : null,   /* v9 8.1-f */
                linkPreview: msg.link_preview || null,                         /* v9 8.6 */
                liveRef: msg.live_ref || null,                                 /* v9 8.16-4b */
                isAdmin: (msg.sender_id && typeof memberById === 'function' && memberById(msg.sender_id) && memberById(msg.sender_id).isAdmin)
                    || (state.users[msg.sender_email] && state.users[msg.sender_email].isAdmin) || false
            });
            state.chatMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            if (currentTab === 'chat') loadChat();
            if (typeof updateTabBadges === 'function') updateTabBadges();
            try { loadAllNotifications().then(function () { renderNotifBadge(); }); } catch (e) { if (typeof renderNotifBadge === 'function') renderNotifBadge(); }
            const fromOther = (msg.sender_id && state.currentUser)
                ? (msg.sender_id !== state.currentUser.id)
                : (msg.sender_email !== (state.currentUser && state.currentUser.email));
            /* v9 8.7-D3: comutatorul vechi doar pe dispozitiv (`messageNotifEnabled`) nu mai contează;
               bannerul urmează alegerea pe cont pentru club + blocările (TO_CHAT.notifGate), ca serverul.
               EN: banner follows the per-club account level + blocks, like the server. */
            if (fromOther && currentTab !== 'chat') {
                const tx = (msg.text || '');
                const clubName = (typeof clubDisplayName === 'function') ? clubDisplayName() : '';
                const showBanner = function (mention) {
                    if (typeof showPushBanner === 'function') {
                        /* Aceeași bară ca la push (cu dedup pe id-ul mesajului). */
                        showPushBanner({
                            title: (clubName ? clubName + ' • ' : '') + (msg.sender_name || '') + (mention ? ' ' + t('cnotif.mentioned') : ''),
                            body: tx,
                            data: { notifId: 'm' + msg.id, route: 'chat', clubId: msg.club_id, tab: 'chat' }
                        });
                    } else {
                        showNotification('💬 ' + msg.sender_name + ': ' + tx.slice(0, 50) + (tx.length > 50 ? '…' : ''), 'info');
                    }
                };
                if (window.TO_CHAT && typeof window.TO_CHAT.notifGate === 'function') window.TO_CHAT.notifGate(msg.club_id, msg, showBanner);
                else showBanner(false);
            }
            /* (sunetul de chat a fost eliminat — nu mai avea toggle în UI) */
        })
        .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'messages' }, (payload) => {
            const old = payload.old;
            if (old && old.id != null) {
                state.chatMessages = state.chatMessages.filter(m => String(m.id) !== String(old.id));
            }
            if (currentTab === 'chat') loadChat();
            if (typeof renderNotifBadge === 'function') renderNotifBadge();
        })
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'messages' }, (payload) => {
            const msg = payload.new;
            if (!msg || msg.id == null) return;
            if (msg.club_id && msg.club_id !== currentClubId) return;
            const idx = state.chatMessages.findIndex(m => String(m.id) === String(msg.id));
            if (idx === -1) return;
            const ex = state.chatMessages[idx];
            ex.text = msg.text; ex.edited = !!msg.edited;
            ex.replyTo = msg.reply_to || null; ex.replySender = msg.reply_sender || null; ex.replySnippet = msg.reply_snippet || null;
            if (Array.isArray(msg.photos)) ex.photos = msg.photos;   /* v9 N1a: ca la INSERT */
            ex.deletedAt = msg.deleted_at || null; ex.deletedByAdmin = !!msg.deleted_by_admin;   /* v9 B2a: urma apare la toți pe loc / trace shows live */
            ex.mentions = Array.isArray(msg.mentions) ? msg.mentions : null;   /* v9 8.1-f: editare / ștergere le curăță pe server */
            ex.linkPreview = msg.link_preview || null;                         /* v9 8.6: la fel cardul (linkul scos / mesaj șters) */
            ex.liveRef = msg.live_ref || null;                                 /* v9 8.16-4b: serverul o golește la ștergere */
            if (currentTab === 'chat') loadChat();
        })
        /* v9 N1b: starea legăturii merge la chat-core.js. Când legătura cade (aplicația
           în fundal) și se reface, mesajele trimise între timp NU sunt retrimise de
           server — chat-core.js le recuperează. EN: report channel status so missed
           messages can be fetched after a rejoin. */
        .subscribe(function (status) {
            try { if (window.TO_CHAT && window.TO_CHAT.club) window.TO_CHAT.club.liveStatus(status, _liveTok); }
            catch (e) { logError('subscribeRealtimeChat', e); }
        });
}

/* ---------- Realtime: jucători, catalog, evenimente, sesiuni ----------
   Când oricare din aceste tabele se schimbă (din orice browser), reîncărcăm
   datele din Supabase și reafișăm listele relevante — live, fără re-login.
   Folosim un mic „debounce" ca să nu reafișăm de zeci de ori la rafale. */
let dataChannel = null;
let realtimeRefreshTimer = null;

function scheduleRealtimeRefresh() {
    /* ⚠ Nu re-hidrata pe PROPRIILE scrieri.
       `updateMyProfile` face 4 UPDATE-uri pe `profiles` (name/avatar, user_tag,
       bio/preferred_games, profile_extra). Fiecare declanșa un eveniment realtime ->
       4 hidratări + 4 cascade de randări, suprapuse peste cea pe care updateMyProfile
       o face oricum la final. Zgomot pur, și sursa unei curse de randare.
       Cât timp salvăm profilul, ignorăm ecoul propriilor scrieri; `updateMyProfile`
       reîncarcă și randează el însuși, la final, o singură dată.

       ⚠ Dar NU aruncăm evenimentul: în fereastra aia poate sosi și o schimbare făcută de
       ALTCINEVA (un rol, un joc nou). Dacă o aruncam, dispărea definitiv — UI-ul rămânea
       învechit până la refresh. Deci o REȚINEM și o rulăm o singură dată, la final.
       Zero evenimente pierdute, zero zgomot. */
    if (window._selfWriteInFlight) { window._missedRefresh = true; return; }
    _runDataRefresh();
}

/* Refresh-ul propriu-zis, FĂRĂ garda de self-write. Evenimentele venite de la ALȚII
   (ex. o sesiune salvată de alt membru -> punctele mele se schimbă) trebuie procesate
   necondiționat; garda `_selfWriteInFlight` e doar pentru a suprima ecoul propriilor
   scrieri de profil. `applyMatchRealtime` cheamă direct aici. */
function _runDataRefresh() {
    realtimeRefreshTimer = setTimeout(async () => {
        realtimeRefreshTimer = null;
        if (!state.currentUser || isDemoMode()) return;
        try {
            /* P4-1b: evenimentele care ajung aici sunt pe tabele MICI (profiles, games,
               events, club_members, club_settings). NU reîncărcăm matches+messages
               (istoric nelimitat) — acelea se mențin incremental (applyMatchRealtime +
               messagesChannel). Reîncărcăm doar tabelele mărginite. */
            await hydrateFromSupabase({ skipHeavy: true });
            /* v9 8.4-b: bara „doar-citire / mod vizualizare (mut)" după o schimbare în timp real a
               membrilor (un admin dă / ia restricția): hidratarea (`loadModeration`) recalculează
               `isMuted`, dar bara se redesena doar la pornire / la schimbarea clubului — apărea
               abia după reîncărcare (Alex). EN: re-render the muted/read-only banner live. */
            if (typeof renderClubReadonlyBanner === 'function') renderClubReadonlyBanner();
            /* Reafișăm doar listele de tip „vizualizare", NU formularele în
               lucru (ex. formularul de sesiune), ca să nu pierzi ce scrii. */
            if (typeof updateTabBadges === 'function') updateTabBadges();
            if (typeof renderDashboard === 'function') renderDashboard();
            if (typeof renderPlayers === 'function') renderPlayers();
            if (typeof renderCatalog === 'function') renderCatalog();
            if (typeof renderHistory === 'function') renderHistory();
            if (typeof renderCalendar === 'function') renderCalendar();
            /* v8 — pop-up-ul DESCHIS al unui eveniment se reîmprospăta doar la
               propria apăsare (`_refreshOpenUis`), nu și la schimbările altora.
               RO: DEFECT cu consecințe abia de la lista de așteptare: cine e pe
                   listă și ține pop-up-ul deschis — exact ce faci dacă aștepți un
                   loc — rămânea pe „poziția 1" după ce serverul îl promovase și îi
                   trimisese notificarea. Interfața contrazicea notificarea.
                   Se redeschide DOAR dacă e deja deschis; nu apare nimic din senin.
               EN: the open event popup never reacted to other people's changes, so a
                   promoted waitlister kept seeing their old position. */
            try {
                const _openEv = document.getElementById('eventDetailModal');
                if (_openEv && typeof openEvent === 'function') {
                    const _eid = _openEv.dataset.eventId;
                    _openEv.remove();
                    openEvent(_eid);
                }
            } catch (e) { logError('_runDataRefresh.eventModal', e); }
            if (typeof renderStats === 'function') renderStats();
            if (typeof renderEloLeaderboard === 'function') renderEloLeaderboard();
            if (typeof renderAchievements === 'function') renderAchievements();
            if (window.TO_CHAT && window.TO_CHAT.readyModule && window.TO_CHAT.readyModule('TO_POLLS')) renderGameVote();   /* 8.16-3d: polls.js la cerere */
            /* ★ LIVE polls: hidratarea de mai sus aduce deja `state.polls` proaspăt
               din club_settings — dar lista de randare omitea poll-urile clasice,
               deci voturile/poll-urile noi ale altora apăreau doar la refresh. */
            if (window.TO_CHAT && window.TO_CHAT.readyModule && window.TO_CHAT.readyModule('TO_POLLS')) renderPolls();   /* 8.16-3d */
            if (typeof renderEloAdminControl === 'function') renderEloAdminControl();
            if (typeof renderAdminUsersList === 'function') renderAdminUsersList();
            try { if (typeof renderMyProfile === 'function') renderMyProfile(); } catch (e) { logError('scheduleRealtimeRefresh.profile', e); }
            if (typeof renderNotifBadge === 'function') renderNotifBadge();
            try { await loadAllNotifications(); renderNotifBadge(); } catch (e) { logError('scheduleRealtimeRefresh', e); }
            if (currentTab === 'chat' && typeof loadChat === 'function') loadChat();
            /* Campanii: la orice refresh global (ex. sesiune legată nouă) actualizează
               badge-ul + pagina de campanie dacă e deschisă (state.games e deja hidratat). */
            try { if (window.TO_CAMP && typeof TO_CAMP.rtRefresh === 'function') TO_CAMP.rtRefresh(); } catch (e) { logError('scheduleRealtimeRefresh.camp', e); }
        } catch (e) { console.warn('Realtime refresh:', e); }
    }, 350);
}

/* ★ FIX: cand cineva ma adauga/scoate dintr-un club, lista `state.myClubs` era
   reincarcata DOAR la login (`loadMyClubsAndSelect`). `scheduleRealtimeRefresh`
   rehidrateaza doar clubul CURENT, deci clubul nou nu aparea pana la refresh manual.
   Aici reincarcam lista de cluburi live si, daca nu aveam niciun club, intram
   automat in cel nou. */
let _myClubsReloadTimer = null;
function scheduleMyClubsReload() {
    if (_myClubsReloadTimer) clearTimeout(_myClubsReloadTimer);
    _myClubsReloadTimer = setTimeout(async function () {
        _myClubsReloadTimer = null;
        try {
            if (!sb || !state.currentUser || (typeof isDemoMode === 'function' && isDemoMode())) return;
            const before = new Set((state.myClubs || []).map(c => c.id));
            const prevClubId = currentClubId;
            await loadMyClubsAndSelect(state.currentUser);
            const now = state.myClubs || [];
            const added = now.filter(c => !before.has(c.id));

            /* ★ BUG (găsit după o restaurare de backup): `loadMyClubsAndSelect` REALEGE
               clubul curent din `my_last_club()` / localStorage — logica de LOGIN. Aici
               însă suntem la o simplă reîmprospătare declanșată de realtime, iar
               utilizatorul are deja un club deschis. Efectul: orice modificare în
               `club_members` (cineva intră/iese dintr-un club, o restaurare rescrie
               tabela) îl TELEPORTA tăcut pe utilizator în „clubul lui obișnuit", în
               mijlocul a ce făcea. Cazul concret: după restaurare se deschidea
               istoricul de backup al ALTUI club.
               Reașezările intenționate de mai jos (adăugat/scos din club) folosesc
               explicit `switchClub`, deci nu sunt afectate de restaurarea asta.
               EN: the realtime refresh reused the login-time club selection and silently
                   moved the user to a different club; keep the open club when still valid. */
            if (prevClubId && currentClubId !== prevClubId && now.some(c => c.id === prevClubId)) {
                currentClubId = prevClubId;
            }

            renderClubSwitcher();
            try { if (typeof renderClubHub === 'function') renderClubHub(); } catch (e) { logError('scheduleMyClubsReload', e); }
            try { if (typeof applyClubRole === 'function') applyClubRole(); } catch (e) { logError('scheduleMyClubsReload', e); }

            /* Am fost adaugat intr-un club NOU si nu eram in niciunul -> deschide-l acum. */
            if (added.length && !prevClubId && currentClubId) {
                const c = now.find(x => x.id === currentClubId) || added[0];
                showNotification(t('club.addedToClub').replace('{name}', c ? c.name : ''), 'success');
                const target = currentClubId;
                currentClubId = null;            /* switchClub iese devreme daca id-ul e acelasi */
                await switchClub(target);
                return;
            }
            if (added.length) {
                const c = added[0];
                showNotification(t('club.addedToClub').replace('{name}', c ? c.name : ''), 'success');
            }
            /* Am fost scos din clubul in care eram -> reasezare pe alt club / hub. */
            if (prevClubId && !now.some(c => c.id === prevClubId)) {
                showNotification(t('club.removedFromClub'), 'info');
                const target = currentClubId;
                currentClubId = null;
                if (target) { await switchClub(target); }
                else { try { if (typeof render === 'function') render(); } catch (e) { logError('scheduleMyClubsReload', e); } }
            }
        } catch (e) { logError('scheduleMyClubsReload', e); }
    }, 400);
}
window.scheduleMyClubsReload = scheduleMyClubsReload;

/* ─────────────────────────────────────────────────────────────────────────
   P4 (Z8-B/C): actualizare INCREMENTALĂ pe `matches` din realtime.
   Înainte: orice eveniment pe matches -> hydrateFromSupabase() -> 7 SELECT-uri
   fără limită (TOT istoricul de meciuri + mesaje + profiluri...), la fiecare
   membru online, la fiecare sesiune salvată de oricine. Cost O(membri × istoric).
   Acum: aplicăm DOAR rândul din payload în state.games (adaugă/înlocuiește/scoate)
   -> zero SELECT-uri suplimentare pentru istoric. Randările sunt debounced (coalesc
   rafalele). Dedup pe id => ecoul propriilor scrieri (saveSession a făcut deja push
   local) nu dublează nimic.
   ───────────────────────────────────────────────────────────────────────── */
/* Upsert local idempotent al unei sesiuni în state.games (dedup pe id). Folosit de
   saveSession la push -> dacă ecoul realtime a adăugat deja rândul (race rar), nu
   dublăm. */
function _upsertLocalGame(session) {
    if (!Array.isArray(state.games)) state.games = [];
    const idx = (session && session.id != null) ? state.games.findIndex(g => String(g.id) === String(session.id)) : -1;
    if (idx === -1) state.games.push(session); else state.games[idx] = session;
}

async function applyMatchRealtime(payload) {
    try {
        if (!state.currentUser || isDemoMode()) return;
        const ev = payload && (payload.eventType || payload.event);
        const row = payload && payload.new;
        const oldRow = payload && payload.old;

        /* Gardă de club: procesăm doar meciuri din clubul activ (canalul e global). */
        if (row && currentClubId && row.club_id && String(row.club_id) !== String(currentClubId)) return;

        if (!Array.isArray(state.games)) state.games = [];

        if (ev === 'DELETE') {
            const delId = oldRow && oldRow.id;
            if (delId == null) return;
            const before = state.games.length;
            state.games = state.games.filter(g => String(g.id) !== String(delId));
            if (state.games.length !== before) _runDataRefresh();
            return;
        }

        if (!row || row.id == null) return;
        const mapped = mapMatchRowToSession(row);
        const idx = state.games.findIndex(g => String(g.id) === String(mapped.id));

        if (ev === 'INSERT') {
            if (idx === -1) {                       /* nu dubla ecoul propriei scrieri */
                state.games.push(mapped);
                await resolveMissingNamesForGames([mapped]);
                _runDataRefresh();
            }
        } else if (ev === 'UPDATE') {
            if (idx === -1) state.games.push(mapped);
            else state.games[idx] = mapped;
            await resolveMissingNamesForGames([mapped]);
            _runDataRefresh();
        } else {
            /* eveniment necunoscut -> aplică defensiv ca upsert */
            if (idx === -1) state.games.push(mapped); else state.games[idx] = mapped;
            await resolveMissingNamesForGames([mapped]);
            _runDataRefresh();
        }
    } catch (e) { logError('applyMatchRealtime', e); }
}

function subscribeRealtimeData() {
    if (dataChannel) { try { sb.removeChannel(dataChannel); } catch (e) { logError('subscribeRealtimeData', e); } dataChannel = null; }
    const myId = (state.currentUser && state.currentUser.id) || '';
    dataChannel = sb.channel('public:appdata')
        /* ★ Apartenenta MEA la cluburi: orice schimbare -> reincarca lista de cluburi live. */
        .on('postgres_changes', { event: '*', schema: 'public', table: 'club_members', filter: myId ? ('profile_id=eq.' + myId) : undefined }, scheduleMyClubsReload)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, scheduleRealtimeRefresh)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'games' },    scheduleRealtimeRefresh)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'events' },   scheduleRealtimeRefresh)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'matches' },  applyMatchRealtime)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'club_members' }, scheduleRealtimeRefresh)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'club_settings' }, scheduleRealtimeRefresh)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'announcements' }, function () { try { loadAnnouncements().then(function () { if (currentTab === 'dashboard' && typeof renderAnnouncements === 'function') renderAnnouncements(); }); } catch (e) { logError('subscribeRealtimeData', e); } })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'announcement_replies' }, function () { try { loadAnnouncements().then(function () { if (currentTab === 'dashboard' && typeof renderAnnouncements === 'function') renderAnnouncements(); }); } catch (e) { logError('subscribeRealtimeData', e); } })
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: myId ? ('recipient_id=eq.' + myId) : undefined }, (payload) => {
            try {
                const n = payload && payload.new;
                if (!n || n.recipient_id !== myId) return;
                const nid = 'n' + n.id;
                /* Insert optimist în listă -> badge-ul apare instant, fără să aștepte reload-ul. */
                if (!Array.isArray(state.allNotifications)) state.allNotifications = [];
                if (!state.allNotifications.some(x => x.id === nid)) {
                    state.allNotifications.unshift({
                        id: nid, ts: n.created_at ? new Date(n.created_at).getTime() : Date.now(),
                        icon: n.icon || 'fa-bell', text: n.text || '', clubId: n.club_id || null, tab: n.tab || ''
                    });
                    state.notifLoaded = true;
                }
                /* Asigură că noua notificare e NECITITĂ (chiar și la primul notif din viața contului). */
                try {
                    let seen = getSeenIds();
                    if (seen === null) seen = new Set((getNotifications() || []).map(x => x.id));
                    seen.delete(nid);
                    saveSeenIds(seen);
                } catch (e) { logError('subscribeRealtimeData', e); }
                renderNotifBadge();
                /* Marcăm ȘI id-ul, ȘI textul, ca banner-ul de push care ajunge mai
                   târziu să găsească oricare dintre ele și să nu se mai afișeze. */
                if (n.text && typeof showNotification === 'function'
                    && !_pushAlreadyShown('n' + n.id) && !_notifTextAlreadyShown(n.text)) {
                    showNotification(n.text, 'info');
                }
                if (typeof loadAllNotifications === 'function') loadAllNotifications().then(function () { renderNotifBadge(); });
            } catch (e) { logError('subscribeRealtimeData', e); }
        })
        .subscribe();
}

/* ============================================================
   ===================== MOD COMPETITIV ELO ===================
   Opțional, dezactivat implicit, pornit doar de admin.
   Rating-urile NU se stochează — se calculează din istoricul de
   sesiuni (tabela `matches`), care e sursa de adevăr. Așa rămân
   mereu corecte și se recalculează singure dacă ștergi o sesiune.
   K=32, rating inițial 1000. Regula multiplayer: fiecare câștigător
   „bate" fiecare necâștigător (comparații pereche). Dacă o sesiune
   nu are câștigători marcați, îi derivăm din scorul maxim. */
const ELO_K = 32;
const ELO_START = 1000;

/* ★ SURSĂ UNICĂ DE ADEVĂR pentru algoritmul ELO (§23 — DRY, unificare iulie 2026):
   o SINGURĂ definiție a algoritmului, parametrizată pe setul de sesiuni, apelată
   din toate locurile (leaderboard club, profil jucător, „This club", „ELO per club",
   avgElo achievements). Execuția se repetă per set de date (alt club = alt istoric
   = alt ELO), dar DEFINIȚIA e una — imposibil să mai diveargă filtrele/regulile. */
function calcElo(sessions) {
    const ratings = {};
    const get = n => (ratings[n] != null ? ratings[n] : ELO_START);

    (sessions || []).slice()
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
        .forEach(s => {
            if (s.friendly || (s.meta && s.meta.friendly)) return; // sesiune amicală: nu afectează ELO
            if (s.type === 'coop' || (s.coop)) return; // co-op/RPG nu afectează ELO
            const players = Array.isArray(s.participants) ? s.participants.filter(Boolean) : [];
            if (players.length < 2) return;

            let winners = Array.isArray(s.winners) ? s.winners.filter(Boolean) : [];
            if (winners.length === 0 && s.scores && typeof s.scores === 'object') {
                let max = -Infinity;
                players.forEach(p => { const v = Number(s.scores[p]); if (!isNaN(v) && v > max) max = v; });
                if (max > -Infinity) winners = players.filter(p => Number(s.scores[p]) === max);
            }
            if (winners.length === 0 || winners.length === players.length) return; // nimic de departajat

            const losers = players.filter(p => !winners.includes(p));
            players.forEach(p => { if (ratings[p] == null) ratings[p] = ELO_START; });

            /* Calculăm toate modificările pe baza rating-urilor de DINAINTE de sesiune,
               apoi le aplicăm la final (corect pentru perechi multiple în aceeași sesiune). */
            const delta = {};
            players.forEach(p => delta[p] = 0);
            winners.forEach(w => {
                losers.forEach(l => {
                    const ew = 1 / (1 + Math.pow(10, (get(l) - get(w)) / 400));
                    delta[w] += ELO_K * (1 - ew);
                    delta[l] -= ELO_K * (1 - ew);
                });
            });
            players.forEach(p => { ratings[p] = get(p) + delta[p]; });
        });

    Object.keys(ratings).forEach(n => ratings[n] = Math.round(ratings[n]));
    return ratings;
}

/* Wrapper: ELO-ul clubului ACTIV (pe state.games). Toate apelurile existente
   (leaderboard, profil jucător, tab „This club") rămân neschimbate.
   ── P4-Faza 3: MEMOIZARE. `calcElo` e O(n×jucători²) și era apelat de mai multe ori
   per randare (leaderboard, profil, stats) -> recalcul integral repetat, lent la
   istoric mare. Cache-uim rezultatul și-l refolosim cât timp istoricul nu s-a
   schimbat. Cheia de invalidare (O(n), ieftină: length + sume de id/scoruri/winners/
   flags) surprinde add/edit/delete fără „bump-uri" manuale -> nu poate rămâne stale. */
let _eloCache = { key: null, val: null };
function _eloInvalidationKey(games) {
    let n = 0, acc = 0;
    for (let i = 0; i < games.length; i++) {
        const g = games[i];
        n++;
        const id = g.id;
        if (typeof id === 'number') acc += id;
        else if (typeof id === 'string') { for (let c = 0; c < id.length; c++) acc = (acc + id.charCodeAt(c)) % 2147483647; }
        if (g.scores) { for (const k in g.scores) { const v = +g.scores[k]; if (!isNaN(v)) acc += v; } }
        if (Array.isArray(g.winners)) acc += g.winners.length * 7;
        if (g.friendly || (g.meta && g.meta.friendly)) acc += 3;
        if (g.type === 'coop' || g.coop) acc += 5;
    }
    return n + '|' + acc;
}
function computeElo() {
    const g = state.games || [];
    const key = _eloInvalidationKey(g);
    if (_eloCache.key === key && _eloCache.val) return _eloCache.val;
    _eloCache = { key, val: calcElo(g) };
    return _eloCache.val;
}

function renderEloLeaderboard() {
    const container = document.getElementById('eloLeaderboard');
    if (!container) return;
    if (!state.eloEnabled) { container.innerHTML = ''; container.style.display = 'none'; return; }
    // Apare doar pe Overview
    if (typeof window.statsType !== 'undefined' && window.statsType && window.statsType !== 'overview') {
        container.innerHTML = ''; container.style.display = 'none'; return;
    }
    container.style.display = '';

    const ratings = computeElo();
    const rows = Object.keys(ratings)
        .map(id => {
            const m = (typeof memberById === 'function') ? memberById(id) : (state.membersById && state.membersById[id]);
            return {
                id: id,
                name: (typeof displayNameById === 'function' && displayNameById(id)) || (m && m.name) || id,
                elo: ratings[id],
                games: (m && m.gamesPlayed) || 0,
                avatar: (m && m.avatar) || null
            };
        })
        .sort((a, b) => b.elo - a.elo);

    const avFn = (typeof avatarHtml === 'function') ? avatarHtml : null;
    const SHOWN = 5;

    const body = rows.length === 0
        ? '<p class="text-center text-muted" style="padding:14px;">' + window.t('elo.empty') + '</p>'
        : rows.map((r, i) => {
            const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`;
            const hidden = i >= SHOWN ? ' stats-hidden' : '';
            const more = i >= SHOWN ? ' data-more="eloMore"' : '';
            const av = avFn ? `<span class="elo-av">${avFn(r.avatar, 30)}</span>` : '';
            return `
                <div class="elo-row${hidden}"${more}>
                    <span class="elo-rank">${medal}</span>
                    ${av}
                    <span class="elo-name elo-name-link" onclick="openLeaderboardProfile('${window.attrJs(r.id)}')">${window.escHtml(r.name)}</span>
                    <div class="elo-right">
                        <small class="elo-games">${r.games} ${t('elo.gamesWord')}</small>
                        <span class="badge badge-warning" style="font-size:0.95em;">${r.elo}</span>
                    </div>
                </div>`;
        }).join('');

    const moreBtn = (rows.length > SHOWN && typeof statsMoreBtn === 'function') ? statsMoreBtn('eloMore', rows.length, SHOWN) : '';

    container.innerHTML = `
        <div class="card" style="margin-bottom:20px;">
            <h2 class="card-title"><i class="fas fa-ranking-star"></i> ${t('elo.title')}</h2>
            <p style="color:var(--text-secondary); font-size:0.9em; margin-bottom:12px;">
                ${t('elo.activeNote')}
            </p>
            ${body}
            ${moreBtn}
        </div>`;
}

function renderEloAdminControl() {
    const el = document.getElementById('eloAdminControl');
    if (!el) return;
    if (!planAllows('elo')) { el.innerHTML = featureLockNote('elo'); return; }
    const on = !!state.eloEnabled;
    el.innerHTML = `
        <p style="color:var(--text-secondary); margin-bottom:10px;">
            ${t('elo.statePrefix')} <strong style="color:${on ? 'var(--success)' : 'var(--text-secondary)'};">${on ? t('elo.on') : t('elo.off')}</strong>.
            ${t('elo.descOn')}
        </p>
        <button class="btn ${on ? 'btn-danger' : 'btn-success'}" onclick="toggleElo()">
            <i class="fas fa-toggle-${on ? 'off' : 'on'}"></i> ${on ? t('elo.disable') : t('elo.enable')}
        </button>`;
}

async function toggleElo() {
    if (!isAdmin) { showNotification(t('sb.onlyAdminsCompetitive'), 'error'); return; }
    if (!requireFeature('elo')) return;
    const next = !state.eloEnabled;
    state.eloEnabled = next; /* optimistic UI; DB e sursa de adevăr (rollback la eroare) */
    renderEloAdminControl();
    renderEloLeaderboard();
    if (typeof renderPlayers === 'function') renderPlayers();

    if (!isDemoMode()) {
        const up = await sb.from('club_settings')
            .upsert({ club_id: currentClubId, key: 'elo_enabled', value: next, updated_at: new Date().toISOString() }, { onConflict: 'club_id,key' });
        if (up.error) {
            state.eloEnabled = !next; /* rollback: n-a ajuns în DB */
            renderEloAdminControl();
            renderEloLeaderboard();
            if (typeof renderPlayers === 'function') renderPlayers();
            showNotification(t('sb.settingSaveErrorSql').replace('{msg}', up.error.message), 'error');
        }
    }
    addAuditLog('audit.competitiveModeElo', state.currentUser.name, { key: next ? 'auditd.activated' : 'auditd.deactivated' });
    showNotification(next ? t('elo.enabledMsg') : t('elo.disabledMsg'), next ? 'success' : 'info');
}

/* ============================================================
   ============== ACHIEVEMENTS COLECTIVE (CLUB) ===============
   Se deblochează pentru TOT clubul, din datele comune (Supabase).
   Calculate din `state` (hidratat din Supabase) — fără stocare
   separată, deci mereu corecte și colective, ca la ELO. */
const CLUB_ACHIEVEMENTS = [
    // Sesiuni
    { id: 'first_session', cat: 'sessions', icon: '🎲', title: { ro: 'Start!', en: 'Start!' }, desc: { ro: 'Prima sesiune logată — aici începe totul.', en: 'First session logged — where it all begins.' }, target: 1, value: s => s.sessions },
    { id: 'sessions_10', cat: 'sessions', icon: '🌱', title: { ro: 'Clubul marelui început', en: 'The great-beginning club' }, desc: { ro: 'Zece sesiuni la activ — clubul a prins ritm.', en: 'Ten sessions in — the club has found its rhythm.' }, target: 10, value: s => s.sessions },
    { id: 'sessions_50', cat: 'sessions', icon: '🎮', title: { ro: 'Obișnuiții mesei', en: 'Table regulars' }, desc: { ro: 'Cincizeci de seri de joc — deja un obicei plăcut.', en: 'Fifty game nights — already a happy habit.' }, target: 50, value: s => s.sessions },
    { id: 'sessions_100', cat: 'sessions', icon: '🏅', title: { ro: 'Clubul consacrat', en: 'The established club' }, desc: { ro: 'O sută de sesiuni — suntem serioși la treabă.', en: 'A hundred sessions — we mean serious business.' }, target: 100, value: s => s.sessions },
    { id: 'sessions_1000', cat: 'sessions', icon: '👑', title: { ro: 'Clubul legendar', en: 'The legendary club' }, desc: { ro: 'O mie de sesiuni — o istorie scrisă seară de seară.', en: 'A thousand sessions — history written night by night.' }, target: 1000, value: s => s.sessions },
    // Diversitate
    { id: 'distinct_10', cat: 'diversity', icon: '🎯', title: { ro: 'Curioși', en: 'Curious' }, desc: { ro: 'Zece jocuri diferite — gusturile se diversifică.', en: 'Ten different games — tastes are branching out.' }, target: 10, value: s => s.distinctGames },
    { id: 'distinct_50', cat: 'diversity', icon: '🌈', title: { ro: 'Diversitate totală', en: 'Total diversity' }, desc: { ro: 'Cincizeci de jocuri diferite — nu ne plictisim niciodată.', en: 'Fifty different games — never a dull moment here.' }, target: 50, value: s => s.distinctGames },
    { id: 'distinct_200', cat: 'diversity', icon: '🏛️', title: { ro: 'Maeștri ai variației', en: 'Masters of variety' }, desc: { ro: 'Două sute de jocuri diferite — am încercat de toate.', en: 'Two hundred different games — we have tried it all.' }, target: 200, value: s => s.distinctGames },
    { id: 'catalog_25', cat: 'diversity', icon: '🗄️', title: { ro: 'Raft plin', en: 'Full shelf' }, desc: { ro: 'Douăzeci și cinci de jocuri în raft — colecția crește.', en: 'Twenty-five games on the shelf — the collection grows.' }, target: 25, value: s => s.catalog },
    { id: 'catalog_100', cat: 'diversity', icon: '🏰', title: { ro: 'Ludotecă', en: 'Game library' }, desc: { ro: 'O sută de jocuri în catalog — o adevărată ludotecă.', en: 'A hundred games catalogued — a true game library.' }, target: 100, value: s => s.catalog },
    // Comunitate
    { id: 'members_10', cat: 'community', icon: '👥', title: { ro: 'Club adevărat', en: 'A real club' }, desc: { ro: 'Zece membri la masă — clubul a devenit comunitate.', en: 'Ten members at the table — a real community now.' }, target: 10, value: s => s.members },
    { id: 'players_25', cat: 'community', icon: '🤝', title: { ro: 'Comunitate în expansiune', en: 'Growing community' }, desc: { ro: 'Douăzeci și cinci de jucători distincți — tot mai mulți.', en: 'Twenty-five distinct players — more join all the time.' }, target: 25, value: s => s.distinctPlayers },
    { id: 'perfect_month', cat: 'community', icon: '✅', title: { ro: 'Prezență perfectă', en: 'Perfect attendance' }, desc: { ro: 'Toți membrii activi au jucat luna aceasta — suntem compleți.', en: 'Every active member played this month — all present.' }, target: 1, value: s => s.perfectMonth },
    // Activitate
    { id: 'events_10', cat: 'activity', icon: '📅', title: { ro: 'Organizați', en: 'Organized' }, desc: { ro: 'Zece evenimente programate — planificăm cu cap.', en: 'Ten events scheduled — we plan ahead with care.' }, target: 10, value: s => s.events },
    { id: 'chat_100', cat: 'activity', icon: '💬', title: { ro: 'Gălăgioși', en: 'Chatty' }, desc: { ro: 'O sută de mesaje în chat — palavre fără sfârșit.', en: 'A hundred chat messages — the banter never stops.' }, target: 100, value: s => s.messages },
    // Performanță
    { id: 'coop_20', cat: 'performance', icon: '🛡️', title: { ro: 'Campionii cooperării', en: 'Co-op champions' }, desc: { ro: 'Douăzeci de victorii coop — împreună suntem mai puternici.', en: 'Twenty co-op wins — together we are stronger.' }, target: 20, value: s => s.coopWins },
    { id: 'coop_first', cat: 'performance', icon: '🤝', title: { ro: 'Prima victorie coop', en: 'First co-op win' }, desc: { ro: 'Prima partidă cooperativă câștigată împreună.', en: 'Your first cooperative game won together.' }, target: 1, value: s => s.coopWins },
    { id: 'coop_hard_10', cat: 'performance', icon: '🔥', title: { ro: 'Provocare acceptată', en: 'Challenge accepted' }, desc: { ro: 'Zece victorii coop pe dificultate mare (Greu, Eroic, Brutal).', en: 'Ten co-op wins on high difficulty (Hard, Heroic, Brutal).' }, target: 10, value: s => s.coopWinsHard },
    { id: 'coop_streak_5', cat: 'performance', icon: '⚡', title: { ro: 'Val de victorii', en: 'On a roll' }, desc: { ro: 'Cinci victorii coop la rând.', en: 'Five co-op wins in a row.' }, target: 5, value: s => s.coopStreak },
    { id: 'coop_variety_5', cat: 'diversity', icon: '🧭', title: { ro: 'Exploratori cooperativi', en: 'Co-op explorers' }, desc: { ro: 'Cinci jocuri cooperative diferite jucate.', en: 'Five different cooperative games played.' }, target: 5, value: s => s.distinctCoopGames },
    { id: 'elo_1500', cat: 'performance', icon: '🧠', title: { ro: 'Game masters', en: 'Game masters' }, desc: { ro: 'ELO mediu peste 1500 — suntem buni la ce facem.', en: 'Average ELO above 1500 — we are good at this.' }, target: 1500, value: s => s.avgElo },
];
const ACH_CATS = [
    { id: 'all', icon: '🏆', label: { ro: 'Toate', en: 'All' } },
    { id: 'sessions', icon: '🎲', label: { ro: 'Sesiuni', en: 'Sessions' } },
    { id: 'diversity', icon: '🌈', label: { ro: 'Diversitate', en: 'Diversity' } },
    { id: 'community', icon: '🤝', label: { ro: 'Comunitate', en: 'Community' } },
    { id: 'activity', icon: '📅', label: { ro: 'Activitate', en: 'Activity' } },
    { id: 'performance', icon: '🧠', label: { ro: 'Performanță', en: 'Performance' } },
];
function achTxt(v) { try { return (window.currentLang === 'en' && v && v.en) ? v.en : (v && v.ro ? v.ro : v); } catch (e) { return (v && v.ro) || v; } }

function computeClubStats() {
    const games = state.games || [];
    const distinct = new Set();
    const players = new Set();
    let coopWins = 0;
    const _coopList = []; /* {ts, outcome, difficulty, game} pentru metrici coop (Faza 3) */
    games.forEach(g => {
        if (g.gameName) distinct.add(g.gameName);
        (Array.isArray(g.participants) ? g.participants : []).forEach(p => { if (p) players.add(p); });
        if (g.scores && typeof g.scores === 'object') Object.keys(g.scores).forEach(p => players.add(p));
        const _cm = (g.meta && g.meta.coop) || g.coop || null;
        const co = (_cm && _cm.outcome) || '';
        if (co === 'win') coopWins++;
        if (_cm) _coopList.push({ ts: g.timestamp, outcome: co, difficulty: (_cm.difficulty || ''), game: (g.gameName || '') });
    });
    /* Metrici coop derivate (client-side, fără SQL). */
    const _HARD = { hard: 1, heroic: 1, brutal: 1, custom: 1 };
    let coopWinsHard = 0;
    const _coopGameNames = new Set();
    _coopList.forEach(c => {
        if (c.outcome === 'win' && _HARD[c.difficulty]) coopWinsHard++;
        if (c.game) _coopGameNames.add(c.game);
    });
    /* Serie curentă de victorii coop (de la cea mai recentă în urmă). */
    let coopStreak = 0;
    _coopList.slice().sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0))
        .some(c => { if (c.outcome === 'win') { coopStreak++; return false; } return true; });
    const _msrc = (state.membersById && Object.keys(state.membersById).length) ? state.membersById : (state.members || {});
    const activeMembers = Object.values(_msrc).filter(m => m && !m.hidden);
    const activeIds = activeMembers.map(m => m.dbId).filter(Boolean);
    // ELO mediu al membrilor activi
    let avgElo = 0;
    try {
        const ratings = (typeof _eloRatingsFromSessions === 'function') ? _eloRatingsFromSessions(games) : {};
        const start = (typeof ELO_START === 'number') ? ELO_START : 1200;
        if (activeIds.length) avgElo = Math.round(activeIds.reduce((s, id) => s + (ratings[id] != null ? ratings[id] : start), 0) / activeIds.length);
    } catch (e) { logError('computeClubStats', e); }
    // Prezență perfectă: toți membrii activi au jucat luna curentă
    let perfectMonth = 0;
    try {
        if (activeIds.length) {
            const now = new Date();
            const playedThisMonth = new Set();
            games.forEach(g => {
                const d = new Date(g.timestamp);
                if (d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) {
                    (Array.isArray(g.participants) ? g.participants : []).forEach(p => playedThisMonth.add(p));
                    if (g.scores) Object.keys(g.scores).forEach(p => playedThisMonth.add(p));
                }
            });
            perfectMonth = activeIds.every(id => playedThisMonth.has(id)) ? 1 : 0;
        }
    } catch (e) { logError('computeClubStats', e); }
    return {
        sessions: games.length,
        distinctGames: distinct.size,
        distinctPlayers: players.size,
        members: activeMembers.length,
        catalog: Object.keys(state.catalog || {}).length,
        events: (state.events || []).length,
        messages: (state.chatMessages || []).length,
        coopWins: coopWins,
        coopSessions: _coopList.length,
        coopWinsHard: coopWinsHard,
        coopStreak: coopStreak,
        distinctCoopGames: _coopGameNames.size,
        avgElo: avgElo,
        perfectMonth: perfectMonth
    };
}

function getClubAchievements() {
    const stats = computeClubStats();
    return CLUB_ACHIEVEMENTS.map(a => {
        const current = Math.max(0, Math.floor(a.value(stats) || 0));
        return { ...a, current, unlocked: current >= a.target, pct: Math.min(100, Math.round((current / a.target) * 100)) };
    });
}

/* F4 (3 oct. 2026): realizările se anunță DOAR când sunt obținute.
   RO: Înainte, „văzutele" stăteau doar în memorie, iar baza se lua la prima randare — adesea
       înainte ca datele clubului să fie încărcate complet. Când datele soseau, realizările
       existente păreau „noi" și apăreau toate ca notificări la login (și după mutarea pe
       app.turnorder.org, la fiecare dispozitiv). Acum:
       1) „văzutele" se păstrează pe dispozitiv, per utilizator și club (în demo: doar în memorie);
       2) în primele ACH_SILENT_MS după login sau după schimbarea clubului, orice realizare
          deblocată se adaugă la „văzute" FĂRĂ notificare (datele încă se încarcă);
       3) după aceea, se anunță doar realizările care nu fuseseră văzute niciodată.
   EN: unlocks are announced only when earned: persisted seen-set + a silent window while loading. */
const ACH_SILENT_MS = 20000;
let _achSilentUntil = 0;
const _achSeenMem = {};
function _achSeenKey() {
    const uid = (state.currentUser && (state.currentUser.id || (state.currentUser.isDemo ? 'demo' : ''))) || '';
    const demo = (typeof isDemoMode === 'function' && isDemoMode());
    const cid = (typeof currentClubId !== 'undefined' && currentClubId) ? currentClubId : (demo ? 'demo' : '');
    return (uid && cid) ? ('ach_seen:' + uid + ':' + cid) : '';
}
function _achSeenGet(key) {
    if (typeof isDemoMode === 'function' && isDemoMode()) return _achSeenMem[key] || null;
    try { const v = localStorage.getItem(key); return v ? new Set(JSON.parse(v)) : null; } catch (e) { return null; }
}
function _achSeenSave(key, set) {
    if (typeof isDemoMode === 'function' && isDemoMode()) { _achSeenMem[key] = set; return; }
    try { localStorage.setItem(key, JSON.stringify(Array.from(set))); } catch (e) { logError('_achSeenSave', e); }
}
/* Pornește fereastra în care deblocările se înregistrează fără notificare (login / schimbare de club). */
function achStartSilentWindow() { _achSilentUntil = Date.now() + ACH_SILENT_MS; }
window.achStartSilentWindow = achStartSilentWindow;
let _achFilter = 'all';
let _achExpanded = false;
function setAchFilter(c) { _achFilter = c; _achExpanded = (c !== 'all'); renderAchievements(); }
function toggleAchExpanded() { _achExpanded = !_achExpanded; renderAchievements(); }
window.setAchFilter = setAchFilter;
window.toggleAchExpanded = toggleAchExpanded;

function openAchievementDetail(id) {
    const a = getClubAchievements().find(x => x.id === id);
    if (!a) return;
    const cat = ACH_CATS.find(c => c.id === a.cat);
    const remaining = Math.max(0, a.target - a.current);
    const existing = document.getElementById('achDetailModal'); if (existing) existing.remove();
    const modal = document.createElement('div');
    modal.className = 'modal active'; modal.id = 'achDetailModal';
    modal.onclick = e => { if (e.target === modal) modal.remove(); };
    modal.innerHTML = `
        <div class="modal-content" style="max-width:440px;">
            <div class="modal-header">
                <h2><i class="fas fa-medal"></i> ${t('ach.detailTitle')}</h2>
                <button class="modal-close" onclick="this.closest('.modal').remove()">×</button>
            </div>
            <div class="ach-detail-hero${a.unlocked ? ' unlocked' : ''}">
                <div class="ach-detail-icon">${a.icon}</div>
                <div class="ach-detail-name">${achTxt(a.title)}</div>
                ${cat ? `<div class="ach-detail-cat">${cat.icon} ${achTxt(cat.label)}</div>` : ''}
                <div class="ach-detail-status ${a.unlocked ? 'on' : ''}">${a.unlocked ? '<i class="fas fa-check-circle"></i> ' + t('ach.unlockedStatus') : '<i class="fas fa-lock"></i> ' + t('ach.lockedStatus')}</div>
            </div>
            <p class="ach-detail-desc">${achTxt(a.desc)}</p>
            <div class="ach-detail-prog">
                <div class="ach-progress"><div style="width:${a.pct}%;"></div></div>
                <div class="ach-detail-prog-row">
                    <span><i class="fas fa-bullseye"></i> ${Math.min(a.current, a.target)} / ${a.target}</span>
                    <span>${a.pct}%</span>
                </div>
            </div>
            <div class="ach-detail-note${a.unlocked ? ' ok' : ''}">
                ${a.unlocked
            ? '<i class="fas fa-trophy"></i> ' + t('ach.doneNote')
            : '<i class="fas fa-flag-checkered"></i> ' + t('ach.remaining').replace('{n}', remaining)}
            </div>
        </div>`;
    document.body.appendChild(modal);
}
window.openAchievementDetail = openAchievementDetail;

function renderAchievements() {
    const container = document.getElementById('clubAchievements');
    if (!container) return;
    if (!planAllows('clubAchievements')) { container.innerHTML = featureLockNote('clubAchievements'); return; }
    const list = getClubAchievements();

    /* F4: anunțăm DOAR realizările obținute acum (vezi nota de la ACH_SILENT_MS). */
    try {
        const key = _achSeenKey();
        if (key) {
            const unlocked = list.filter(a => a.unlocked);
            const seen = _achSeenGet(key);
            const silent = (seen === null) || (Date.now() < _achSilentUntil);
            const next = new Set(seen ? Array.from(seen) : []);
            unlocked.forEach(a => {
                if (!next.has(a.id)) {
                    if (!silent) showNotification(`🎉 ${t('ach.unlockedOne')}: ${a.icon} ${achTxt(a.title)}!`, 'success');
                    next.add(a.id);
                }
            });
            _achSeenSave(key, next);
        }
    } catch (e) { logError('renderAchievements.seen', e); }

    const unlockedCount = list.filter(a => a.unlocked).length;
    const overallPct = list.length ? Math.round(unlockedCount / list.length * 100) : 0;

    const chips = ACH_CATS.map(c => {
        const inCat = c.id === 'all' ? list : list.filter(a => a.cat === c.id);
        const u = inCat.filter(a => a.unlocked).length;
        return `<button type="button" class="ach-chip${_achFilter === c.id ? ' active' : ''}" onclick="setAchFilter('${c.id}')">${c.icon} ${achTxt(c.label)} <span class="ach-chip-count">${u}/${inCat.length}</span></button>`;
    }).join('');

    let shown = _achFilter === 'all' ? list.slice() : list.filter(a => a.cat === _achFilter);
    shown.sort((a, b) => (b.unlocked - a.unlocked) || (b.pct - a.pct));
    const LIMIT = 6;
    const collapsed = (_achFilter === 'all') && !_achExpanded && shown.length > LIMIT;
    const visible = collapsed ? shown.slice(0, LIMIT) : shown;

    const cards = visible.map(a => `
        <div class="ach-card${a.unlocked ? ' unlocked' : ''}" onclick="openAchievementDetail('${a.id}')">
            ${a.unlocked ? '<i class="ach-check fas fa-check"></i>' : ''}
            <div class="ach-card-head">
                <div class="ach-icon">${a.icon}</div>
                <div class="ach-card-info">
                    <div class="ach-title">${achTxt(a.title)}</div>
                    <div class="ach-desc">${achTxt(a.desc)}</div>
                </div>
            </div>
            <div class="ach-foot">
                <div class="ach-progress"><div style="width:${a.pct}%;"></div></div>
                <small class="ach-count">${Math.min(a.current, a.target)} / ${a.target}</small>
            </div>
        </div>`).join('');

    let moreBtn = '';
    if (collapsed) moreBtn = `<button type="button" class="btn btn-sm btn-secondary players-more-btn" onclick="toggleAchExpanded()"><i class="fas fa-chevron-down"></i> ${t('players.showMore')} (${shown.length - LIMIT})</button>`;
    else if (_achFilter === 'all' && _achExpanded && shown.length > LIMIT) moreBtn = `<button type="button" class="btn btn-sm btn-secondary players-more-btn" onclick="toggleAchExpanded()"><i class="fas fa-chevron-up"></i> ${t('players.showLess')}</button>`;

    container.innerHTML = `
        <div class="ach-overview">
            <div class="ach-overview-bar"><div style="width:${overallPct}%;"></div></div>
            <div class="ach-overview-text"><i class="fas fa-medal"></i> ${unlockedCount} / ${list.length} ${t('ach.unlocked')}</div>
        </div>
        <div class="ach-chips">${chips}</div>
        <div class="ach-grid">${cards}</div>
        ${moreBtn}`;
}

/* 8.16-3c (31 oct. 2026): VOTUL DE GRUP „Ce jucam" (cheia, ponderile, pornirea, votul, candidatii,
   inchiderea, expirarea, numaratoarea, delegarea) a fost MUTAT identic in polls.js. `gvBannerHtml`
   ramane aici: il foloseste si randomizatorul. EN: game vote moved to polls.js. */

// Card-banner pentru un joc din catalog (poză pe fundal + titlu + rating + detalii vitale).
function gvBannerHtml(title, clickTitle) {
    const g = (state.catalog && state.catalog[title]) || {};
    const esc = s => window.escHtml(s);
    const img = g.imageUrl ? (typeof gameImgCss === 'function' ? gameImgCss(g.imageUrl, 'banner') : g.imageUrl) : '';
    const rating = (typeof gameRatingAvg === 'function' && gameRatingAvg(g) != null) ? gameRatingAvg(g).toFixed(1) : 'N/A';
    const weight = (typeof gameWeightAvg === 'function' && gameWeightAvg(g) != null) ? gameWeightAvg(g).toFixed(2) : 'N/A';
    const players = g.players || ((g.minPlayers || g.maxPlayers) ? `${g.minPlayers || '?'}-${g.maxPlayers || '?'}` : '?');
    const duration = g.duration ? (g.duration + ' min') : '?';
    const titleHtml = (clickTitle === false)
        ? `<span class="gv-banner-title">${esc(title)}</span>`
        : `<span class="gv-banner-title gv-title-link" title="${window.t('gv.viewDetails')}" data-action="gvact-title" data-game="${window.escHtml(title)}">${esc(title)}</span>`;
    const banner = `<div class="gv-banner${img ? '' : ' gv-banner-noimg'}"${img ? ` style="background-image:url('${img}');"` : ''}>
            <div class="gv-banner-ov">
                ${titleHtml}
                <span class="gv-banner-rating"><i class="fas fa-star"></i> ${rating}/10</span>
            </div>
        </div>`;
    const details = `<div class="gv-details">
            <span><i class="fas fa-users"></i> ${esc(players)}</span>
            <span><i class="fas fa-clock"></i> ${esc(duration)}</span>
            <span><i class="fas fa-chart-line"></i> ${weight}/5</span>
        </div>`;
    return banner + details;
}

/* 8.16-3c: renderGameVote ... confirmStartVote → polls.js. */

/* ============================================================
   ================== IMPORT DATE (BGG / BG Stats) ============
   BGG (CSV de colecție) -> doar catalog.
   BG Stats (JSON) -> catalog + sesiuni (intră în istoric, ELO,
   achievements). Se scrie în tabelele existente; ce există deja
   este ignorat automat (dedup după titlu / semnătură de sesiune). */
let _importPreview = null;

function csvParse(text) {
    const rows = []; let row = [], field = '', inQ = false;
    text = String(text).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    /* Detectează delimitatorul din primul rând: , ; sau tab */
    const firstLine = text.split('\n')[0] || '';
    const cc = (firstLine.match(/,/g) || []).length;
    const sc = (firstLine.match(/;/g) || []).length;
    const tc = (firstLine.match(/\t/g) || []).length;
    let delim = ',';
    if (sc > cc && sc >= tc) delim = ';';
    else if (tc > cc && tc > sc) delim = '\t';
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (inQ) {
            if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQ = false; }
            else field += c;
        } else {
            if (c === '"') inQ = true;
            else if (c === delim) { row.push(field); field = ''; }
            else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
            else field += c;
        }
    }
    if (field.length || row.length) { row.push(field); rows.push(row); }
    if (rows.length === 0) return [];
    const header = rows[0].map(h => String(h).trim());
    return rows.slice(1)
        .filter(r => r.some(x => x !== ''))
        .map(r => { const o = {}; header.forEach((h, idx) => o[h] = r[idx] != null ? r[idx] : ''); return o; });
}

function parseBggCsv(text) {
    const games = [];
    csvParse(text).forEach(r => {
        const title = (r.objectname || '').trim();
        if (!title) return;
        const w = parseFloat(r.avgweight) || 0;
        games.push({
            title,
            min_players: parseInt(r.minplayers) || null,
            max_players: parseInt(r.maxplayers) || null,
            duration: parseInt(r.playingtime) || parseInt(r.maxplaytime) || 60,
            complexity: w > 0 ? Math.round(Math.min(5, Math.max(1, w)) * 100) / 100 : 3,
            category: null, description: null, image_url: null
        });
    });
    return { games, sessions: [] };
}

function bgStatsDate(s) {
    if (!s) return new Date().toISOString();
    const d = new Date(String(s).replace(' ', 'T'));
    return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}

function parseBgStats(obj) {
    const gamesById = {}; (obj.games || []).forEach(g => gamesById[g.id] = g);
    const playersById = {}; (obj.players || []).forEach(p => playersById[p.id] = p);

    const games = (obj.games || []).map(g => ({
        title: (g.name || g.bggName || '').trim(),
        min_players: g.minPlayerCount || null,
        max_players: g.maxPlayerCount || null,
        duration: g.maxPlayTime || g.minPlayTime || 60,
        complexity: 3, category: null, description: null,
        image_url: g.urlImage || g.urlThumb || null
    })).filter(g => g.title);

    const sessions = [];
    (obj.plays || []).forEach(p => {
        if (p.ignored) return;
        const g = gamesById[p.gameRefId];
        const title = g ? (g.name || g.bggName || '').trim() : '';
        if (!title) return;
        const players = [], scores = {}, winners = [];
        (p.playerScores || []).forEach(s => {
            const pl = playersById[s.playerRefId];
            const name = pl ? (pl.name || '').trim() : '';
            if (!name) return;
            players.push(name);
            if (s.score != null && s.score !== '') { const num = Number(s.score); if (!isNaN(num)) scores[name] = num; }
            if (s.winner) winners.push(name);
        });
        if (players.length === 0) return;
        sessions.push({ game_title: title, date: bgStatsDate(p.playDate), players, scores, winners });
    });
    return { games, sessions };
}

/* ---------- ScorePal (CSV) ----------
   ScorePal a fost retras din Play Store (2024); backup-ul nativ e un fișier
   binar .realm, necitibil în browser. Acest parser acceptă CSV exportat/convertit
   din datele ScorePal și e tolerant la denumiri de coloane și la 2 formate:
   (A) „lung": un rând per jucător (Game, Date, Player, Score, Winner…);
   (B) „lat": un rând per partidă, cu coloane Players/Scores/Winners separate prin ; , sau |.
   Câștigătorul se deduce din scorul maxim dacă nu e marcat explicit. */
function _spNorm(h) { return String(h || '').toLowerCase().replace(/[\s_\-\.]/g, ''); }

function scorePalDate(s) {
    if (!s) return new Date().toISOString();
    const str = String(s).trim();
    let d = new Date(str.replace(' ', 'T'));
    if (isNaN(d.getTime())) {
        const m = str.match(/^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{2,4})$/);
        if (m) { let yy = +m[3]; if (yy < 100) yy += 2000; d = new Date(yy, (+m[2]) - 1, +m[1]); }
    }
    return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}

function parseScorePalCsv(text) {
    const rows = csvParse(text);
    if (!rows.length) return { games: [], sessions: [] };
    const headers = Object.keys(rows[0]);
    const find = (cands) => headers.find(h => cands.indexOf(_spNorm(h)) !== -1);
    const truthy = v => ['1', 'true', 'yes', 'y', 'x', 'win', 'won', 'winner', 'da', '1st', 'first'].indexOf(String(v || '').toLowerCase().trim()) !== -1;
    const num = v => { if (v == null || v === '') return null; const n = Number(String(v).replace(',', '.')); return isNaN(n) ? null : n; };

    const gameCol = find(['game', 'gamename', 'title', 'boardgame', 'gametitle']);
    const dateCol = find(['date', 'playdate', 'played', 'playedon', 'datetime', 'day', 'when']);
    const playerCol = find(['player', 'playername', 'name', 'user']);
    const scoreCol = find(['score', 'points', 'total', 'finalscore', 'result']);
    const winnerCol = find(['winner', 'won', 'iswinner', 'win', 'place', 'rank', 'position']);
    const playIdCol = find(['playid', 'play', 'session', 'sessionid', 'gameid', 'matchid', 'plays', 'id']);

    const sessions = [];
    const titles = new Set();

    if (playerCol && gameCol) {
        /* Format „lung": grupăm rândurile în partide */
        const groups = {};
        rows.forEach(r => {
            const title = (r[gameCol] || '').trim(); if (!title) return;
            const dateRaw = dateCol ? r[dateCol] : '';
            const key = (playIdCol && r[playIdCol]) ? ('id:' + r[playIdCol]) : (title + '|' + (dateRaw || ''));
            const g = groups[key] || (groups[key] = { title, date: dateRaw, players: [], scores: {}, winners: [] });
            if (dateRaw && !g.date) g.date = dateRaw;
            const pname = (r[playerCol] || '').trim(); if (!pname) return;
            if (g.players.indexOf(pname) === -1) g.players.push(pname);
            if (scoreCol) { const n = num(r[scoreCol]); if (n != null) g.scores[pname] = n; }
            if (winnerCol && truthy(r[winnerCol]) && g.winners.indexOf(pname) === -1) g.winners.push(pname);
        });
        Object.keys(groups).forEach(k => {
            const g = groups[k]; if (!g.players.length) return;
            if (!g.winners.length && Object.keys(g.scores).length) {
                const max = Math.max.apply(null, Object.values(g.scores));
                g.players.forEach(n => { if (g.scores[n] === max) g.winners.push(n); });
            }
            sessions.push({ game_title: g.title, date: scorePalDate(g.date), players: g.players, scores: g.scores, winners: g.winners });
            titles.add(g.title);
        });
    } else if (gameCol) {
        /* Format „lat": Players/Scores/Winners separate prin ; , | */
        const playersCol = find(['players', 'playerlist', 'participants']);
        const scoresCol = find(['scores', 'points', 'results']);
        const winnersCol = find(['winner', 'winners', 'won']);
        const split = v => String(v || '').split(/[;|]/).map(x => x.trim()).filter(Boolean);
        rows.forEach(r => {
            const title = (r[gameCol] || '').trim(); if (!title) return;
            const players = playersCol ? split(r[playersCol]) : [];
            const scoresArr = scoresCol ? split(r[scoresCol]) : [];
            const winnersArr = winnersCol ? split(r[winnersCol]) : [];
            if (!players.length) return;
            const scores = {};
            players.forEach((p, i) => { const n = num(scoresArr[i]); if (n != null) scores[p] = n; });
            let winners = winnersArr.filter(w => players.indexOf(w) !== -1);
            if (!winners.length && Object.keys(scores).length) {
                const max = Math.max.apply(null, Object.values(scores));
                winners = players.filter(p => scores[p] === max);
            }
            sessions.push({ game_title: title, date: scorePalDate(dateCol ? r[dateCol] : ''), players, scores, winners });
            titles.add(title);
        });
    }

    const games = Array.from(titles).map(t => ({
        title: t, min_players: null, max_players: null, duration: 60, complexity: 3,
        category: null, description: null, image_url: null
    }));
    return { games, sessions };
}

/* semnături pentru dedup */
function _titleKey(t) { return String(t || '').toLowerCase().trim(); }
/* RO: Normalizarea datei e ESENȚIALĂ, nu cosmetică. Cele două părți ale comparației
       scriu ACELAȘI moment în formate diferite:
         · sesiunile existente  -> `m.date` din PostgREST: "2026-06-30T07:15:00+00:00"
         · sesiunile importate  -> `bgStatsDate()`:        "2026-06-30T07:15:00.000Z"
       Ca șiruri de caractere nu sunt egale NICIODATĂ, deci deduplicarea nu se
       declanșa și orice reimport raporta „totul nou". Comparăm momentul, nu textul.
   EN: both sides describe the same instant in different string formats; compare the
       instant, not the text. */
function _sigDate(d) {
    if (!d) return '';
    const t = new Date(d);
    return isNaN(t.getTime()) ? String(d) : t.toISOString();
}

function _sessionSig(gameTitle, date, players) {
    return `${_titleKey(gameTitle)}|${_sigDate(date)}|${(players || []).slice().sort().join(',').toLowerCase()}`;
}

/* RO: Semnăturile sesiunilor DEJA existente, pentru deduplicare la import.
       ⚠ `g.participants` conține ID-uri de profil, iar sesiunile din fișierul importat
       au NUME. Fără maparea id -> nume, cele două seturi nu se intersectează
       NICIODATĂ și orice reimport pare „totul nou".
       Calculul exista în două locuri — `_importCounts` (previzualizarea) și
       `runImport` (importul propriu-zis) — iar maparea fusese adăugată doar în al
       doilea. Rezultatul: previzualizarea anunța „50 sesiuni noi", importul insera
       corect 0. Aceeași întrebare, două răspunsuri; aici e singurul răspuns.
   EN: one source for the existing-session signatures; participants are ids, imported
       players are names, so the id->name mapping must happen in both places. */
function _existingSessionSigs() {
    const nm = id => (typeof displayNameById === 'function' && displayNameById(id)) || id;
    return new Set((state.games || []).map(g => _sessionSig(
        g.gameName, g.timestamp,
        (Array.isArray(g.participants) ? g.participants.map(nm) : []))));
}

function handleImportFile(input, kind) {
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) return;

    /* RO: Gărzile erau abia în `runImport`, adică DUPĂ citirea fișierului, parsare,
           previzualizare și tot pasul de mapare a numelor. Un utilizator din demo
           sau fără drepturi trecea prin tot ritualul ca să afle la final că nu se
           salvează nimic. Acum ies din prima.
           `catalog.import` (rang 3) — aceeași capabilitate ca importul BGG: e tot un
           import în masă în catalogul clubului. Tab-ul Admin cere oricum rang 3
           (`member.manage`), deci butoanele nu sunt vizibile mai jos de atât;
           verificarea de aici acoperă apelul direct al funcției.
       EN: guards moved to the entry point; they used to run only after parsing,
           preview and the whole name-mapping step. */
    if (typeof isDemoMode === 'function' && isDemoMode()) {
        showNotification(t('sb.demoImportNoSave'), 'info');
        return;
    }
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    if (typeof can === 'function' && !can('catalog.import')) {
        showNotification(t('rbac.noPermission'), 'error');
        return;
    }
    const reader = new FileReader();
    reader.onload = function () {
        try {
            if (kind === 'bgg') _importPreview = parseBggCsv(String(reader.result));
            else if (kind === 'scorepal') _importPreview = parseScorePalCsv(String(reader.result));
            else _importPreview = parseBgStats(JSON.parse(String(reader.result)));
            showImportPreview(kind);
        } catch (e) {
            showNotification(t('sb.invalidFileColon') + e.message, 'error');
        }
    };
    reader.readAsText(file);
}

function _importCounts() {
    const p = _importPreview || { games: [], sessions: [] };
    const existingTitles = new Set(Object.keys(state.catalog || {}).map(_titleKey));
    let newG = 0, dupG = 0; const seenT = new Set();
    p.games.forEach(g => { const k = _titleKey(g.title); if (existingTitles.has(k) || seenT.has(k)) dupG++; else { seenT.add(k); newG++; } });

    const existingSigs = _existingSessionSigs();
    let newS = 0, dupS = 0; const seenS = new Set();
    p.sessions.forEach(s => { const k = _sessionSig(s.game_title, s.date, s.players); if (existingSigs.has(k) || seenS.has(k)) dupS++; else { seenS.add(k); newS++; } });
    return { newG, dupG, newS, dupS, p };
}

function showImportPreview(kind) {
    if (!_importPreview) return;
    const { newG, dupG, newS, dupS, p } = _importCounts();
    const modal = document.createElement('div');
    modal.className = 'modal active';
    modal.dataset.back = '1';   /* „înapoi" de pe telefon o închide (chat-core.js, v9 zona 2) */
    modal.innerHTML = `
        <div class="modal-content" style="max-width:560px;">
            <div class="modal-header">
                <h2><i class="fas fa-file-import"></i> ${window.t('import.previewTitle').replace('{src}', kind === 'bgg' ? 'BGG' : kind === 'scorepal' ? 'ScorePal' : 'BG Stats')}</h2>
                <button class="modal-close" data-catalog-act="close" aria-label="${window.t('common.close')}">×</button>
            </div>
            <div style="margin:8px 0 14px;">
                <label style="display:flex; align-items:center; gap:10px; padding:10px; ${p.games.length ? '' : 'opacity:.5;'}">
                    <input type="checkbox" id="impGames" ${newG ? 'checked' : (p.games.length ? 'checked' : 'disabled')}>
                    <span><strong>${window.t('import.catalogLabel')}:</strong> ${newG} ${window.t('import.newGames')} ${dupG ? `<span style="color:var(--text-secondary);">(${dupG} ${window.t('import.alreadyExist')})</span>` : ''}</span>
                </label>
                <label style="display:flex; align-items:center; gap:10px; padding:10px; ${p.sessions.length ? '' : 'opacity:.5;'}">
                    <input type="checkbox" id="impSessions" ${p.sessions.length ? 'checked' : 'disabled'}>
                    <span><strong>${window.t('import.sessionsLabel')}:</strong> ${newS} ${window.t('import.newShort')} ${dupS ? `<span style="color:var(--text-secondary);">(${dupS} ${window.t('import.alreadyImported')})</span>` : ''}</span>
                </label>
            </div>
            <p style="color:var(--text-secondary); font-size:.85em; margin-bottom:14px;">
                ${window.t('import.note')}
            </p>
            <div class="btn-group">
                <button class="btn btn-success" data-catalog-act="import-run"><i class="fas fa-check"></i> ${window.t('common.import')}</button>
                <button class="btn btn-secondary" data-catalog-act="close">${window.t('common.cancel')}</button>
            </div>
        </div>`;
    document.body.appendChild(modal);
}

let _pendingImportSessions = null;
let _pendingImportGamesCount = 0;
let _importMapNames = [];

async function runImport() {
    const p = _importPreview;
    if (!p) return;
    const doGames = !document.getElementById('impGames') || document.getElementById('impGames').checked;
    const doSessions = !document.getElementById('impSessions') || document.getElementById('impSessions').checked;

    const existingTitles = new Set(Object.keys(state.catalog || {}).map(_titleKey));
    const seenT = new Set(); const newGames = [];
    if (doGames) p.games.forEach(g => { const k = _titleKey(g.title); if (existingTitles.has(k) || seenT.has(k)) return; seenT.add(k); newGames.push(g); });

    /* Aceeași sursă ca previzualizarea (vezi `_existingSessionSigs`). */
    const existingSigs = _existingSessionSigs();
    const seenS = new Set(); const newSessions = [];
    if (doSessions) p.sessions.forEach(s => { const k = _sessionSig(s.game_title, s.date, s.players); if (existingSigs.has(k) || seenS.has(k)) return; seenS.add(k); newSessions.push(s); });

    if (newGames.length === 0 && newSessions.length === 0) { showNotification(t('sb.nothingNewImport'), 'info'); return; }
    if (isDemoMode()) { showNotification(t('sb.demoImportNoSave'), 'info'); return; }

    showNotification(t('sb.importingGS').replace('{g}', newGames.length).replace('{s}', newSessions.length), 'info');

    if (newGames.length) {
        /* RO: Scriere prin `import_games_bulk`, NU prin `sb.from('games').insert()`.
               Erau două drumuri pentru aceeași operație — importul din fișier insera
               direct, restaurarea și importul BGG mergeau prin RPC — cu praguri de
               rang diferite (2 vs 3) și cu deduplicare doar în client. RPC-ul
               deduplică server-side pe `lower(title)`, deci nu mai depinde de ce a
               apucat catalogul din memorie să încarce, și întoarce numărul REAL de
               rânduri inserate.
               Trimitem doar jocurile deja considerate NOI, deci ramura de update a
               RPC-ului nu se declanșează: comportamentul rămâne „ce există se sare",
               exact ce promite previzualizarea.
           EN: single write path through the same RPC used by BGG import and restore. */
        const rows = newGames.map(g => ({
            title: g.title, min_players: g.min_players, max_players: g.max_players,
            duration: g.duration, complexity: g.complexity,
            category: g.category, description: g.description,
            image_url: g.image_url || null, owner: state.currentUser.name
        }));
        for (let i = 0; i < rows.length; i += 100) {
            const ins = await sb.rpc('import_games_bulk', { p_club_id: currentClubId, p_games: rows.slice(i, i + 100) });
            if (ins.error) { showNotification(t('sb.importCatalogErrorColon') + ins.error.message, 'error'); break; }
        }
    }

    if (newSessions.length) {
        /* Pas de MAPARE manuală: nume din import -> membru existent / guest nou. */
        const names = new Set();
        newSessions.forEach(s => {
            (s.players || []).forEach(n => names.add(n));
            (s.winners || []).forEach(n => names.add(n));
            Object.keys(s.scores || {}).forEach(n => names.add(n));
        });
        _pendingImportSessions = newSessions;
        _pendingImportGamesCount = newGames.length;
        _importMapNames = Array.from(names);
        openImportMappingModal();
        return; // sesiunile se inserează după mapare
    }

    await _finalizeImport(newGames.length, 0);
}

function openImportMappingModal() {
    const esc = window.escHtml || (s => String(s == null ? '' : s));
    const members = (typeof membersList === 'function' ? membersList() : Object.values(state.membersById || {}))
        .filter(m => m && m.dbId)
        .sort((a, b) => String(a.displayName || a.name).localeCompare(String(b.displayName || b.name)));
    const memName = m => (m.displayName || m.name || '');

    const rows = _importMapNames.map((n, i) => {
        const same = members.filter(m => memName(m) === n);
        const preselect = same.length === 1 ? same[0].dbId : (same.length === 0 ? '__guest__' : '');
        const memOpts = members.map(m =>
            `<option value="${esc(m.dbId)}"${preselect === m.dbId ? ' selected' : ''}>${esc(memName(m))}${m.userTag ? (' @' + esc(m.userTag)) : ''}</option>`).join('');
        return `<tr>
            <td style="padding:6px 8px; font-weight:600; vertical-align:top;">${esc(n)}</td>
            <td style="padding:6px 8px;">
              <select id="impmap_${i}" class="form-control" data-catalog-field="impmap" data-i="${i}">
                <option value=""${preselect === '' ? ' selected' : ''}>${window.t('impmap.choose')}</option>
                ${memOpts}
                <option value="__guest__"${preselect === '__guest__' ? ' selected' : ''}>${window.t('impmap.guestNew')}</option>
              </select>
              <div id="impmapg_${i}" class="${preselect === '__guest__' ? '' : 'hidden'}" style="display:flex; gap:6px; margin-top:5px;">
                <input id="impmaptag_${i}" class="form-control" placeholder="${window.t('impmap.tagPh')}" style="flex:1;">
                <input id="impmapmail_${i}" class="form-control" placeholder="${window.t('impmap.emailPh')}" style="flex:1;">
              </div>
            </td>
          </tr>`;
    }).join('');

    const modal = document.createElement('div');
    modal.className = 'modal active';
    modal.dataset.back = '1';   /* „înapoi" de pe telefon o închide (chat-core.js, v9 zona 2) */
    modal.innerHTML = `<div class="modal-content" style="max-width:640px;">
        <h2><i class="fas fa-people-arrows"></i> ${window.t('impmap.title')}</h2>
        <p style="color:var(--text-secondary); font-size:.9em; margin-bottom:10px;">${window.t('impmap.intro')}</p>
        <div class="table-responsive" style="max-height:50vh; overflow:auto;">
          <table style="width:100%;"><thead><tr>
            <th style="text-align:left; padding:6px 8px;">${window.t('impmap.colImport')}</th>
            <th style="text-align:left; padding:6px 8px;">${window.t('impmap.colMapTo')}</th>
          </tr></thead><tbody>${rows}</tbody></table>
        </div>
        <div class="btn-group" style="margin-top:14px;">
          <button class="btn btn-success" data-catalog-act="impmap-apply"><i class="fas fa-check"></i> ${window.t('impmap.continue')}</button>
          <button class="btn btn-secondary" data-catalog-act="close">${window.t('common.cancel')}</button>
        </div>
      </div>`;
    document.body.appendChild(modal);
}

function onImportMapChange(i) {
    const sel = document.getElementById('impmap_' + i);
    const gf = document.getElementById('impmapg_' + i);
    if (sel && gf) gf.classList.toggle('hidden', sel.value !== '__guest__');
}

async function applyImportMapping() {
    for (let i = 0; i < _importMapNames.length; i++) {
        const sel = document.getElementById('impmap_' + i);
        if (!sel || !sel.value) { showNotification(window.t('impmap.needAll'), 'error'); return; }
    }
    showNotification(window.t('impmap.creating'), 'info');
    const nameToId = {};
    for (let i = 0; i < _importMapNames.length; i++) {
        const name = _importMapNames[i];
        const val = document.getElementById('impmap_' + i).value;
        if (val === '__guest__') {
            const tag = ((document.getElementById('impmaptag_' + i) || {}).value || '').trim();
            const email = ((document.getElementById('impmapmail_' + i) || {}).value || '').trim();
            const res = await sb.rpc('admin_add_guest', { p_club_id: currentClubId, p_name: name, p_email: email || null, p_user_tag: tag || null });
            if (res.error || !res.data) {
                const _iem = (res.error && res.error.message) || '';
                if (/TARGET_HAS_ACCOUNT/i.test(_iem)) { paOfferInviteInstead(name); return; }
                const msg = /MEMBER_LIMIT_REACHED/i.test(_iem)
                    ? window.t('players.clubFullAdd')
                    : /user_tag_taken/.test(_iem)
                        ? (window.t('impmap.tagTaken') + name)
                        : (_iem || t('players.createProfileError'));
                showNotification(msg, 'error');
                return;
            }
            nameToId[name] = res.data;
        } else {
            nameToId[name] = val; // id de membru existent
        }
    }
    const m = document.querySelector('.modal.active'); if (m) m.remove();
    await _finishSessionImport(nameToId);
}

/* Hash FNV-1a 32-bit (stabil, sincron) -> 8 hex. */
function _fnv1a32(str) {
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193); }
    return (h >>> 0).toString(16).padStart(8, '0');
}
/* UUID DETERMINIST din conținut (4 sub-hash-uri -> 32 hex). Folosit ca idem_key
   la import, ca re-rularea aceluiași fișier să NU dubleze sesiuni/puncte. */
function _deterministicUuid(str) {
    const p = _fnv1a32(str) + _fnv1a32(str + '|a') + _fnv1a32(str + '|b') + _fnv1a32(str + '|c');
    return p.slice(0, 8) + '-' + p.slice(8, 12) + '-' + p.slice(12, 16) + '-' + p.slice(16, 20) + '-' + p.slice(20, 32);
}

async function _finishSessionImport(nameToId) {
    const sessions = _pendingImportSessions || [];
    const rows = sessions.map(s => {
        const players = (s.players || []).map(n => nameToId[n]).filter(Boolean);
        const scores = {};
        Object.keys(s.scores || {}).forEach(n => { const id = nameToId[n]; if (id) scores[id] = s.scores[n]; });
        const winners = (s.winners || []).map(n => nameToId[n]).filter(Boolean);
        const idem_key = _deterministicUuid([s.game_title || '', s.date || '', players.slice().sort().join(','), JSON.stringify(scores)].join('|'));
        return { game_title: s.game_title, date: s.date, players, scores, winners, idem_key };
    });

    /* Persistare ATOMICĂ prin RPC SECURITY DEFINER (ca save_session_atomic):
       inserează sesiunile + aplică punctele per-club într-o tranzacție, sare
       rândurile cu idem_key existent (re-import IDEMPOTENT) și ocolește RLS
       (rolul se impune intern, rank>=2). recorded_by = auth.uid() (server-side). */
    let inserted = 0;
    if (rows.length) {
        const res = await sb.rpc('import_sessions_atomic', { p_club_id: currentClubId, p_rows: rows });
        if (res.error) {
            logError('importSessions.rpc', res.error);
            showNotification(t('sb.importSessionsErrorColon') + res.error.message, 'error');
        } else if (res.data) {
            inserted = Number(res.data.inserted) || 0;
        }
    }

    const nGames = _pendingImportGamesCount;
    _pendingImportSessions = null; _importMapNames = []; _pendingImportGamesCount = 0;
    await _finalizeImport(nGames, inserted);
}

async function _finalizeImport(nGames, nSessions) {
    addAuditLog('audit.importData', state.currentUser.name, { key: 'auditd.importGS', params: { g: nGames, s: nSessions } });
    await hydrateFromSupabase();
    /* XP/nivel sunt derivate server-side (player_public_stats) -> reîmprospătez
       cache-ul ca badge-ul de nivel să reflecte importul imediat. */
    try {
        if (typeof loadDiscoverPlayerStats === 'function') {
            await loadDiscoverPlayerStats(Object.keys(state.membersById || {}), { force: true });
        }
    } catch (e) { console.warn('refresh XP:', e && e.message); }
    render();
    document.querySelectorAll('.modal.active').forEach(m => m.remove());
    _importPreview = null;
    showNotification(t('sb.importDoneGS').replace('{g}', nGames).replace('{s}', nSessions), 'success');
}

/* ============================================================
   ===================== PROFILUL MEU =========================
   Fiecare user își editează datele proprii (nu și scorurile —
   acelea rămân la admini). Salvare „graceful": numele și avatarul
   se salvează mereu; câmpurile extra doar dacă există coloanele
   (scriptul profile_fields.sql). */
const PROFILE_AVATARS = ['🎲','🃏','♟️','🎯','🐉','🦉','🦊','🐼','🚀','⚔️','🏰','🌟','🧙','🤖','🐲','🍀'];
/* `PROFILE_COLORS` a fost ELIMINAT: era declarat aici în română și nefolosit
   nicăieri în proiect (verificat prin grep în toate fișierele). O listă moartă de
   texte netraduse e o capcană — primul care o leagă la o interfață livrează
   culori românești în engleză. */

function pfPickAvatar(a) {
    const inp = document.getElementById('pfAvatar'); if (inp) inp.value = a;
    const ph = document.getElementById('pfPhoto'); if (ph) ph.value = '';
    const mp = document.getElementById('pfAvatarModalPreview'); if (mp) mp.innerHTML = avatarHtml(a, 84);
    document.querySelectorAll('#pfAvatarPicker .pf-emoji-btn').forEach(b => {
        b.classList.toggle('is-on', b.getAttribute('data-av') === a);
    });
}

/* Extrage ID-ul (și un titlu din slug) dintr-un link BoardGameGeek sau dintr-un ID simplu.
   Ex: https://boardgamegeek.com/boardgame/169786/scythe -> { id:'169786', title:'Scythe' } */
function parseBggInput(input) {
    input = String(input || '').trim();
    if (!input) return null;
    if (/^\d+$/.test(input)) return { id: input, title: '' };
    const m = input.match(/boardgame(?:expansion|accessory)?\/(\d+)(?:\/([^\/?#]+))?/i);
    if (!m) return null;
    let title = '';
    if (m[2]) {
        title = decodeURIComponent(m[2]).replace(/-/g, ' ').trim()
            .split(' ').map(w => w ? w.charAt(0).toUpperCase() + w.slice(1) : w).join(' ');
    }
    return { id: m[1], title };
}

let _prefDraft = [];
let _prefImages = {};        // titlu (lowercase) -> URL copertă, pt. cardurile de favorite
let _favSearchTimer = null;  // debounce căutare BGG

/* Coperta pt. un titlu favorit: din coperțile salvate, apoi din catalogul clubului. */
function _favImgFor(title) {
    const k = String(title || '').toLowerCase();
    if (_prefImages && _prefImages[k]) return _prefImages[k];
    const cat = state.catalog || {};
    for (const name in cat) {
        if (name.toLowerCase() === k && cat[name] && cat[name].imageUrl) return cat[name].imageUrl;
    }
    return '';
}

/* Carduri read-only de favorite (titlu peste imagine) — reutilizabil oriunde se afișează
   jocurile preferate (discover, profil public etc.). imagesMap = profile_extra.favGameImages. */
/* Registru pentru pop-up-ul „Vezi toate": cheie STABILĂ (ex. 'fav-<id>' / 'owned-<id>'),
   deci re-randările suprascriu, nu acumulează. */
let _gameLists = {};

/* Deschide fișa jocului dintr-un profil. Jocul poate proveni dintr-un club comun,
   dar catalogul e PER-CLUB -> dacă nu e în clubul activ, spunem asta explicit
   (înainte, `showGameDetails` returna silențios și click-ul părea mort). */
function openGameFromProfile(title) {
    const name = String(title || '');
    if (!name) return;
    if (state.catalog && state.catalog[name]) { showGameDetails(name); return; }
    /* Potrivire case-insensitive (catalogurile pot diferi ca scriere). */
    const cat = state.catalog || {};
    const hit = Object.keys(cat).find(k => k.toLowerCase() === name.toLowerCase());
    if (hit) { showGameDetails(hit); return; }
    showNotification(t('pf.gameNotInClub').replace('{game}', name), 'info');
}
window.openGameFromProfile = openGameFromProfile;

function _gameCardHtml(title, imgs) {
    const k = String(title || '').toLowerCase();
    let img = imgs[k] || '';
    if (!img) {
        const cat = state.catalog || {};
        for (const n in cat) { if (n.toLowerCase() === k && cat[n] && cat[n].imageUrl) { img = cat[n].imageUrl; break; } }
    }
    const bg = img ? `background-image:url('${gameImgCss(img, 'thumb')}');` : '';
    return `<div class="pf-fav-card clickable${img ? '' : ' noimg'}" style="${bg}" title="${escHtml(title)}"
                 role="button" tabindex="0"
                 onclick="openGameFromProfile('${attrJs(title)}')"
                 onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();openGameFromProfile('${attrJs(title)}');}">
        <div class="pf-fav-card-ov"><span class="pf-fav-card-title">${escHtml(title)}</span></div>
    </div>`;
}

/* Carduri de joc (read-only) — folosite ȘI pentru „Jocuri favorite", ȘI pentru
   „Jocuri deținute". Clickabile (deschid fișa jocului). Afișăm primele `limit`
   (implicit 5); restul intră într-un pop-up „Vezi toate".
   NB: cardurile EDITABILE din profilul propriu (cu X) sunt altundeva
   (`renderPrefChips`) și rămân neschimbate. */
function favCardsHtml(titles, imagesMap, opts) {
    const o = opts || {};
    const list = (Array.isArray(titles) ? titles : []).filter(Boolean);
    if (!list.length) return '';
    const imgs = imagesMap || {};
    const limit = (typeof o.limit === 'number') ? o.limit : 5;
    const shown = (o.key && list.length > limit) ? list.slice(0, limit) : list;
    const cards = shown.map(tt => _gameCardHtml(tt, imgs)).join('');

    let more = '';
    if (o.key && list.length > limit) {
        _gameLists[o.key] = { titles: list.slice(), imgs: imgs, heading: o.heading || '' };
        more = `<button type="button" class="pf-fav-seeall" onclick="openGamesListModal('${attrJs(o.key)}')">
                    <i class="fas fa-layer-group"></i> ${t('pf.seeAll').replace('{n}', list.length)}
                </button>`;
    }
    return '<div class="pf-pref-chips">' + cards + '</div>' + more;
}
window.favCardsHtml = favCardsHtml;

/* Pop-up cu TOATE jocurile din listă (favorite sau deținute). Clickabile. */
function openGamesListModal(key) {
    const data = _gameLists[key];
    if (!data || !data.titles || !data.titles.length) return;
    const existing = document.getElementById('gamesListModal'); if (existing) existing.remove();

    const modal = document.createElement('div');
    modal.className = 'modal active';
    modal.id = 'gamesListModal';
    modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
    /* limit: 0 -> le arătăm pe TOATE, fără buton „Vezi toate" recursiv. */
    const cards = data.titles.map(tt => _gameCardHtml(tt, data.imgs || {})).join('');
    modal.innerHTML = `
        <div class="modal-content" style="max-width:620px;">
            <div class="modal-header">
                <h2><i class="fas fa-dice"></i> ${escHtml(data.heading || t('pf.ownedGames'))} (${data.titles.length})</h2>
                <button class="modal-close" onclick="closeModal('gamesListModal')">×</button>
            </div>
            <div class="pf-pref-chips">${cards}</div>
            <div class="btn-group" style="margin-top:16px;">
                <button class="btn btn-secondary" onclick="closeModal('gamesListModal')">
                    <i class="fas fa-times"></i> ${t('common.close')}
                </button>
            </div>
        </div>`;
    document.body.appendChild(modal);
}
window.openGamesListModal = openGamesListModal;

/* Cardurile cu jocurile favorite SALVATE — compacte, titlu peste imagine (cu X pentru editare). */
/* Un card de favorit: CLICKABIL (deschide jocul) + X de ștergere.
   ⚠ `stopPropagation` pe X: fără el, clicul pe „șterge" ar deschide și fișa jocului. */
function _myFavCardHtml(title) {
    const img = _favImgFor(title);
    const bg = img ? `background-image:url('${gameImgCss(img, 'thumb')}');` : '';
    return `<div class="pf-fav-card${img ? '' : ' noimg'}" style="${bg}; cursor:pointer;"
                 onclick="openGameFromProfile('${attrJs(title)}')"
                 title="${attrJs(title)}">
        <div class="pf-fav-card-ov"><span class="pf-fav-card-title">${escHtml(title)}</span></div>
        <button type="button" class="pf-fav-card-x"
                onclick="event.stopPropagation(); pfPrefRemove('${attrJs(title)}');"
                title="${t('common.remove')}" aria-label="${t('common.remove')}"><i class="fas fa-times"></i></button>
    </div>`;
}

const _MY_FAV_LIMIT = 5;

function renderPrefChips() {
    const box = document.getElementById('pfPrefChips');
    if (!box) return;

    /* ⚠ `#pfPrefChips` ARE DEJA clasa `pf-pref-chips` (container FLEX pe rând).
       Dacă punem ÎNCĂ un `.pf-pref-chips` înăuntru, containerul exterior tratează
       div-ul interior ȘI butonul „Vezi toate" ca două elemente flex -> le pune pe
       COLOANĂ, iar jocurile se stivuiesc vertical.
       Soluția (identică structural cu jocurile DEȚINUTE): scoatem clasa de flex de pe
       container și lăsăm rândul propriu-zis să fie div-ul dinăuntru, cu butonul dedesubt. */
    box.classList.remove('pf-pref-chips');

    if (!_prefDraft.length) { box.innerHTML = '<small style="color:var(--text-secondary);">' + window.t('profile.noFav') + '</small>'; return; }

    /* Aceeași regulă ca la jocurile deținute: primele 5, apoi „Vezi toate". */
    const shown = _prefDraft.slice(0, _MY_FAV_LIMIT);
    const cards = shown.map(_myFavCardHtml).join('');

    let more = '';
    if (_prefDraft.length > _MY_FAV_LIMIT) {
        more = `<button type="button" class="pf-fav-seeall" onclick="openMyFavsModal()">
                    <i class="fas fa-layer-group"></i> ${t('pf.seeAll').replace('{n}', _prefDraft.length)}
                </button>`;
    }
    box.innerHTML = '<div class="pf-pref-chips">' + cards + '</div>' + more;

    /* Pop-up-ul e deschis? Reîmprospătează-l (ex. tocmai ai șters un favorit de acolo). */
    const m = document.getElementById('myFavsModal');
    if (m) renderMyFavsModalBody();
}

/* Pop-up cu TOATE favoritele MELE.
   ⚠ Spre deosebire de `openGamesListModal` (read-only, pentru profilurile ALTORA), ăsta
   păstrează butonul de ȘTERGERE. Altfel, limitând editorul la 5, n-ai mai fi putut scoate
   favoritul al 6-lea — aș fi „reparat" afișarea stricând editarea. */
function renderMyFavsModalBody() {
    const body = document.getElementById('myFavsModalBody');
    if (!body) return;
    if (!_prefDraft.length) { closeModal('myFavsModal'); return; }
    body.innerHTML = _prefDraft.map(_myFavCardHtml).join('');
    const h = document.getElementById('myFavsModalCount');
    if (h) h.textContent = '(' + _prefDraft.length + ')';
}

function openMyFavsModal() {
    const existing = document.getElementById('myFavsModal'); if (existing) existing.remove();
    const modal = document.createElement('div');
    modal.className = 'modal active';
    modal.id = 'myFavsModal';
    modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
    modal.innerHTML = `
        <div class="modal-content" style="max-width:620px;">
            <div class="modal-header">
                <h2><i class="fas fa-star"></i> ${escHtml(t('profile.favGames'))} <span id="myFavsModalCount"></span></h2>
                <button class="modal-close" onclick="closeModal('myFavsModal')">×</button>
            </div>
            <div id="myFavsModalBody" class="pf-pref-chips"></div>
            <div class="btn-group" style="margin-top:16px;">
                <button class="btn btn-secondary" onclick="closeModal('myFavsModal')">
                    <i class="fas fa-times"></i> ${t('common.close')}
                </button>
            </div>
        </div>`;
    document.body.appendChild(modal);
    renderMyFavsModalBody();
}
window.openMyFavsModal = openMyFavsModal;
function pfPrefAdd(title, img) {
    title = (title || '').trim();
    if (!title) { renderPrefChips(); return; }
    if (!_prefDraft.some(t => t.toLowerCase() === title.toLowerCase())) _prefDraft.push(title);
    if (img) _prefImages[title.toLowerCase()] = img;
    renderPrefChips();
}
function pfPrefRemove(title) {
    _prefDraft = _prefDraft.filter(t => t !== title);
    try { delete _prefImages[String(title || '').toLowerCase()]; } catch (e) { logError('pfPrefRemove', e); }
    renderPrefChips();
}
function pfPrefAddBgg(link) {
    const p = parseBggInput(link);
    if (!p || !p.title) { showNotification(t('sb.bggLinkInvalid'), 'error'); return; }
    pfPrefAdd(p.title);
    const inp = document.getElementById('pfPrefBgg'); if (inp) inp.value = '';
}

/* ---- Căutare live favorite (catalog instant + BGG cu coperți), aceleași carduri ca în restul app-ului ---- */
function _favPickCard(name, img) {
    const bg = img ? `background-image:url('${gameImgCss(img, 'thumb')}');` : '';
    return `<div class="pf-fav-pick${img ? '' : ' noimg'}" style="${bg}" onclick="favPick('${attrJs(name)}','${attrJs(img || '')}')" title="${escHtml(name)}">
        <div class="pf-fav-card-ov"><span class="pf-fav-card-title">${escHtml(name)}</span></div>
    </div>`;
}
function favSearchLive() {
    const inp = document.getElementById('pfFavSearch');
    const box = document.getElementById('pfFavResults');
    if (!inp || !box) return;
    const q = inp.value.trim();
    clearTimeout(_favSearchTimer);
    if (q.length < 2) { box.innerHTML = ''; return; }
    const ql = q.toLowerCase();
    const local = Object.keys(state.catalog || {})
        .filter(n => n.toLowerCase().indexOf(ql) !== -1).slice(0, 12)
        .map(n => ({ name: n, img: (state.catalog[n] && state.catalog[n].imageUrl) || '' }));
    const canBgg = q.length >= 3 && sb && !(typeof isDemoMode === 'function' && isDemoMode());
    box.innerHTML = local.map(it => _favPickCard(it.name, it.img)).join('') +
        (canBgg ? `<div class="pf-fav-loading"><i class="fas fa-spinner fa-spin"></i> ${window.t('bgg.searching')}</div>`
            : (local.length ? '' : `<small style="opacity:.6;">${window.t('profile.favNoLocal')}</small>`));
    if (canBgg) _favSearchTimer = setTimeout(() => favRunSearch(q, local), 450);
}
async function favRunSearch(q, local) {
    const box = document.getElementById('pfFavResults');
    if (!box) return;
    try {
        const { data, error } = await sb.functions.invoke('bgg-proxy', { body: { type: 'search', q } });
        const cur = document.getElementById('pfFavSearch');
        if (cur && cur.value.trim() !== q) return; // utilizatorul a mai tastat între timp
        if (error) throw error;
        const items = (data && data.items) || [];
        const seen = {}; (local || []).forEach(l => seen[l.name.toLowerCase()] = 1);
        const bgg = items.filter(it => it && it.name && !seen[it.name.toLowerCase()]).slice(0, 12)
            .map(it => ({ name: it.name, img: it.thumbnail || '' }));
        const html = (local || []).map(it => _favPickCard(it.name, it.img)).join('') +
            bgg.map(it => _favPickCard(it.name, it.img)).join('');
        box.innerHTML = html || `<small style="opacity:.6;">${window.t('bgg.noResults')}</small>`;
    } catch (e) {
        const html = (local || []).map(it => _favPickCard(it.name, it.img)).join('') +
            `<small style="color:#e07a5f; grid-column:1/-1;">${window.t('bgg.searchUnavailable')}</small>`;
        box.innerHTML = html;
    }
}
function favPick(name, img) {
    pfPrefAdd(name, img);
    const inp = document.getElementById('pfFavSearch'); if (inp) { inp.value = ''; inp.focus(); }
    const box = document.getElementById('pfFavResults'); if (box) box.innerHTML = '';
}
window.favSearchLive = favSearchLive;
window.favPick = favPick;
window.pfPrefAddBgg = pfPrefAddBgg;

/* ============================================================
   EDITOR „Jocuri deținute" în profilul propriu (adaugă / șterge).
   Oglindă a editorului de favorite, dar pe `profile_extra.ownedGames`.
   Poți adăuga ORICE joc (chiar dacă niciun club nu-l are încă) — când un club
   îl adaugă în catalog cu același titlu, ești recunoscut automat ca deținător.
   Ce NU se poate edita aici sunt INFORMAȚIILE jocului (vin din catalogul clubului).
   ============================================================ */
let _ownDraft = [];
let _ownImages = {};
let _ownSearchTimer = null;

/* Inițializează editorul din SURSA AUTORITARĂ (DB), nu din memorie.
   Motiv: dacă profile_extra n-a fost hidratat încă,
   memoria e goală — iar înainte asta făcea editorul să pară GOL deși în DB aveai jocuri. */
async function ownEditorInit() {
    const me = (state.currentUser && state.currentUser.id) || null;
    if (!me) { _ownDraft = []; _ownImages = {}; renderOwnChips(); return; }

    /* Pictăm întâi ce avem local (instant), apoi corectăm din DB (fetch/paint separate). */
    _ownDraft = ownedTitlesOf(me);
    _ownImages = Object.assign({}, ownedImagesOf(me));
    renderOwnChips();

    const base = await loadMyOwnedFromDb();
    if (!base) return;                 /* citire eșuată -> păstrăm ce aveam, NU golim */
    _ownDraft = base.titles.slice();
    _ownImages = Object.assign({}, base.images);
    /* Resincronizăm și memoria, ca bifa „Îl am și eu" din catalog să fie corectă. */
    state.currentUser.ownedGames = base.titles.slice();
    const u = (state.currentUser.email && state.users && state.users[state.currentUser.email]) || null;
    if (u) u.profileExtra = base.extra;
    const m = (typeof memberById === 'function') ? memberById(me) : null;
    if (m) m.profileExtra = base.extra;
    renderOwnChips();
}
window.ownEditorInit = ownEditorInit;

function _ownImgFor(title) {
    const k = _ogKey(title);
    if (_ownImages[k]) return _ownImages[k];
    const cat = state.catalog || {};
    for (const n in cat) { if (n.toLowerCase() === k && cat[n] && cat[n].imageUrl) return cat[n].imageUrl; }
    return '';
}

/* Un card editabil: click pe CARD -> fișa jocului; click pe ✕ -> ștergere.
   ✕ face stopPropagation, altfel ștergerea ar deschide și fișa. */
function _ownChipHtml(title) {
    const img = _ownImgFor(title);
    const bg = img ? `background-image:url('${gameImgCss(img, 'thumb')}');` : '';
    return `<div class="pf-fav-card clickable${img ? '' : ' noimg'}" style="${bg}"
                 role="button" tabindex="0" title="${escHtml(title)}"
                 onclick="openGameFromProfile('${attrJs(title)}')"
                 onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();openGameFromProfile('${attrJs(title)}');}">
        <div class="pf-fav-card-ov"><span class="pf-fav-card-title">${escHtml(title)}</span></div>
        <button type="button" class="pf-fav-card-x" title="${t('common.remove')}" aria-label="${t('common.remove')}"
                onclick="event.stopPropagation(); pfOwnRemove('${attrJs(title)}');"><i class="fas fa-times"></i></button>
    </div>`;
}

function renderOwnChips() {
    const box = document.getElementById('pfOwnChips');
    if (!box) return;
    if (!_ownDraft.length) { box.innerHTML = '<small style="color:var(--text-secondary);">' + t('pf.noOwned') + '</small>'; return; }

    /* Aceeași regulă ca la profilurile publice: primele 5, restul în pop-up. */
    const LIMIT = 5;
    const shown = _ownDraft.slice(0, LIMIT);
    const more = _ownDraft.length - shown.length;

    box.innerHTML = '<div class="pf-pref-chips">' + shown.map(_ownChipHtml).join('') + '</div>'
        + (more > 0
            ? `<button type="button" class="pf-fav-seeall" onclick="openOwnedEditorAll()">
                   <i class="fas fa-layer-group"></i> ${t('pf.seeAll').replace('{n}', _ownDraft.length)}
               </button>`
            : '');

    /* Dacă pop-up-ul cu toate e deschis, îl reîmprospătăm și pe el. */
    const all = document.getElementById('ownedEditorModal');
    if (all) {
        const list = all.querySelector('#ownedEditorList');
        if (list) list.innerHTML = _ownDraft.map(_ownChipHtml).join('');
        const cnt = all.querySelector('#ownedEditorCount');
        if (cnt) cnt.textContent = String(_ownDraft.length);
        if (!_ownDraft.length) all.remove();
    }
}
window.renderOwnChips = renderOwnChips;

/* Pop-up cu TOATE jocurile deținute — clickabile ȘI cu ✕ (editabile). */
function openOwnedEditorAll() {
    const existing = document.getElementById('ownedEditorModal'); if (existing) existing.remove();
    const modal = document.createElement('div');
    modal.className = 'modal active';
    modal.id = 'ownedEditorModal';
    modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
    modal.innerHTML = `
        <div class="modal-content" style="max-width:620px;">
            <div class="modal-header">
                <h2><i class="fas fa-dice"></i> ${t('pf.ownedGames')} (<span id="ownedEditorCount">${_ownDraft.length}</span>)</h2>
                <button class="modal-close" onclick="closeModal('ownedEditorModal')">×</button>
            </div>
            <div id="ownedEditorList" class="pf-pref-chips">${_ownDraft.map(_ownChipHtml).join('')}</div>
            <div class="btn-group" style="margin-top:16px;">
                <button class="btn btn-secondary" onclick="closeModal('ownedEditorModal')">
                    <i class="fas fa-times"></i> ${t('common.close')}
                </button>
            </div>
        </div>`;
    document.body.appendChild(modal);
}
window.openOwnedEditorAll = openOwnedEditorAll;

async function pfOwnAdd(title, img) {
    title = (title || '').trim();
    if (!title) { renderOwnChips(); return; }
    if (_ownDraft.some(tt => _ogKey(tt) === _ogKey(title))) { renderOwnChips(); return; }
    /* Optimist doar VIZUAL; adevărul rămâne DB-ul (delta). */
    _ownDraft.push(title);
    if (img) _ownImages[_ogKey(title)] = img;
    renderOwnChips();
    const ok = await addOwnedGame(title, img);
    if (!ok) { await ownEditorInit(); return; }   /* eșec -> revenim la starea reală */
    _ownDraft = ownedTitlesOf(state.currentUser.id);
    renderOwnChips();
    _ownedRefreshOpenGameModal();
}
window.pfOwnAdd = pfOwnAdd;

async function pfOwnRemove(title) {
    _ownDraft = _ownDraft.filter(tt => _ogKey(tt) !== _ogKey(title));
    try { delete _ownImages[_ogKey(title)]; } catch (e) { logError('pfOwnRemove', e); }
    renderOwnChips();
    const ok = await removeOwnedGame(title);
    if (!ok) { await ownEditorInit(); return; }
    _ownDraft = ownedTitlesOf(state.currentUser.id);
    renderOwnChips();
    _ownedRefreshOpenGameModal();
}
window.pfOwnRemove = pfOwnRemove;

/* Dacă fișa unui joc e deschisă, reflectăm imediat noua stare a bifei „Îl am și eu". */
function _ownedRefreshOpenGameModal() {
    try {
        const sec = document.querySelector('.modal.active .game-owners-section');
        if (sec && typeof renderGameOwners === 'function') renderGameOwners(sec);
    } catch (e) { logError('_ownedRefreshOpenGameModal', e); }
}

function _ownPickCard(name, img) {
    const bg = img ? `background-image:url('${gameImgCss(img, 'thumb')}');` : '';
    return `<div class="pf-fav-pick${img ? '' : ' noimg'}" style="${bg}" onclick="ownPick('${attrJs(name)}','${attrJs(img || '')}')" title="${escHtml(name)}">
        <div class="pf-fav-card-ov"><span class="pf-fav-card-title">${escHtml(name)}</span></div>
    </div>`;
}

function ownSearchLive() {
    const inp = document.getElementById('pfOwnSearch');
    const box = document.getElementById('pfOwnResults');
    if (!inp || !box) return;
    const q = inp.value.trim();
    clearTimeout(_ownSearchTimer);
    if (q.length < 2) { box.innerHTML = ''; return; }
    const ql = q.toLowerCase();
    const local = Object.keys(state.catalog || {})
        .filter(n => n.toLowerCase().indexOf(ql) !== -1).slice(0, 12)
        .map(n => ({ name: n, img: (state.catalog[n] && state.catalog[n].imageUrl) || '' }));
    const canBgg = q.length >= 3 && sb && !(typeof isDemoMode === 'function' && isDemoMode());
    box.innerHTML = local.map(it => _ownPickCard(it.name, it.img)).join('') +
        (canBgg ? `<div class="pf-fav-loading"><i class="fas fa-spinner fa-spin"></i> ${window.t('bgg.searching')}</div>`
            : (local.length ? '' : `<small style="opacity:.6;">${window.t('profile.favNoLocal')}</small>`));
    if (canBgg) _ownSearchTimer = setTimeout(() => ownRunSearch(q, local), 450);
}
window.ownSearchLive = ownSearchLive;

async function ownRunSearch(q, local) {
    const box = document.getElementById('pfOwnResults');
    if (!box) return;
    try {
        const { data, error } = await sb.functions.invoke('bgg-proxy', { body: { type: 'search', q } });
        const cur = document.getElementById('pfOwnSearch');
        if (cur && cur.value.trim() !== q) return;   /* userul a mai tastat între timp */
        if (error) throw error;
        const items = (data && data.items) || [];
        const seen = {}; (local || []).forEach(l => seen[l.name.toLowerCase()] = 1);
        const bgg = items.filter(it => it && it.name && !seen[it.name.toLowerCase()]).slice(0, 12)
            .map(it => ({ name: it.name, img: it.thumbnail || '' }));
        const html = (local || []).map(it => _ownPickCard(it.name, it.img)).join('') +
            bgg.map(it => _ownPickCard(it.name, it.img)).join('');
        box.innerHTML = html || `<small style="opacity:.6;">${window.t('bgg.noResults')}</small>`;
    } catch (e) {
        box.innerHTML = (local || []).map(it => _ownPickCard(it.name, it.img)).join('') +
            `<small style="color:#e07a5f; grid-column:1/-1;">${window.t('bgg.searchUnavailable')}</small>`;
    }
}

function ownPick(name, img) {
    pfOwnAdd(name, img);
    const inp = document.getElementById('pfOwnSearch'); if (inp) { inp.value = ''; inp.focus(); }
    const box = document.getElementById('pfOwnResults'); if (box) box.innerHTML = '';
}
window.ownPick = ownPick;

/* ============================================================
   PROFIL — statistici cu switch „Acest club" / „Toate cluburile"
   ============================================================ */

/* Wrapper istoric (păstrat pentru apelanții existenți — „ELO per club" din
   loadProfileGlobalStats + avgElo din computeClubStats): deleagă la calcElo,
   SINGURA definiție a algoritmului ELO (vezi calcElo, lângă computeElo). */
function _eloRatingsFromSessions(sessions) {
    return calcElo(sessions);
}

/* Încarcă statisticile globale (toate cluburile userului) din `matches`.
   Agregă meciuri/victorii/puncte/win rate + ELO-ul deținut în fiecare club
   + XP-ul global (din profileXpInfo). Pune rezultatul în window._profileGlobalStats. */
async function loadProfileGlobalStats() {
    try {
        const myId = state.currentUser ? state.currentUser.id : null;
        const clubs = state.myClubs || [];
        const ids = clubs.map(c => c.id).filter(Boolean);
        if (!myId || !ids.length) { window._profileGlobalStats = { empty: true }; return; }

        const res = await sb.from('matches')
            .select('club_id, game_title, players, scores, winners, meta, date')
            .in('club_id', ids);
        if (res.error) throw res.error;
        const rows = res.data || [];

        const clubName = {}; clubs.forEach(c => clubName[c.id] = c.name);
        const byClub = {};
        let matches = 0, wins = 0, points = 0;
        const gameSet = {};

        rows.forEach(m => {
            const players = sbArray(m.players) || [];
            const winners = sbArray(m.winners) || [];
            const scores = sbObject(m.scores) || {};
            const meta = sbObject(m.meta) || {};
            /* date pentru ELO per club (toți jucătorii, nu doar userul) */
            (byClub[m.club_id] = byClub[m.club_id] || []).push({
                type: meta.type, coop: meta.coop, friendly: !!meta.friendly,
                participants: players,
                winners: winners, scores: scores, timestamp: m.date
            });
            /* agregare doar pentru sesiunile mele — amicalele NU contează la
               statisticile jucătorului (§12.d), aliniat cu gamesPlayedById/countWins
               din clubul activ. byClub rămâne COMPLET (calcElo filtrează singur). */
            if (meta.friendly) return;
            if (players.indexOf(myId) === -1) return;
            matches++;
            if (winners.indexOf(myId) !== -1) wins++;
            points += Number(scores[myId]) || 0;
            const gn = m.game_title || 'Joc'; gameSet[gn] = true;
        });

        const perClubElo = [];
        clubs.forEach(c => {
            const cid = c.id;
            let elo = null;
            if (byClub[cid]) {
                const ratings = _eloRatingsFromSessions(byClub[cid]);
                if (ratings[myId] != null) elo = ratings[myId];
            }
            perClubElo.push({ name: clubName[cid] || c.name || 'Club', elo: elo });
        });
        perClubElo.sort((a, b) => {
            if (a.elo == null && b.elo == null) return 0;
            if (a.elo == null) return 1;
            if (b.elo == null) return -1;
            return b.elo - a.elo;
        });

        const winRate = matches ? Math.round(wins / matches * 100) : 0;
        const distinctGames = Object.keys(gameSet).length;
        const xpInfo = (typeof window.profileXpInfo === 'function')
            ? window.profileXpInfo(matches, wins)
            : { xp: matches * 10 + wins * 25, key: 'exp.novice', nextThreshold: 500 };

        window._profileGlobalStats = {
            matches: matches, wins: wins, points: points, winRate: winRate,
            distinctGames: distinctGames, perClubElo: perClubElo, clubsCount: clubs.length,
            xp: xpInfo.xp, levelKey: xpInfo.key, nextThreshold: xpInfo.nextThreshold
        };
    } catch (e) {
        console.warn('loadProfileGlobalStats:', e && e.message);
        window._profileGlobalStats = { error: true };
    } finally {
        renderProfileStatsSection();
    }
}

/* Re-randează doar secțiunea de statistici (păstrează intacte câmpurile din editorul de profil). */
function renderProfileStatsSection() {
    const el = document.getElementById('profileStatsSection');
    if (el) el.innerHTML = profileStatsSectionHTML();
}

/* Comută între „Acest club" și „Toate cluburile" (apelat din switch). */
function setProfileStatsScope(scope) {
    window._profileStatsScope = (scope === 'global') ? 'global' : 'club';
    if (window._profileStatsScope === 'global') {
        window._profileGlobalStats = null;   // reîncărcare proaspătă la fiecare comutare
        loadProfileGlobalStats();            // async -> renderProfileStatsSection() la final
    }
    renderProfileStatsSection();
}

/* Construiește HTML-ul secțiunii de statistici în funcție de scope. */
function profileStatsSectionHTML() {
    if (!state.currentUser) return '';
    const esc = s => window.escHtml(s);
    const scope = (window._profileStatsScope === 'global') ? 'global' : 'club';

    const segBtn = (sc, label, icon) =>
        `<button type="button" onclick="setProfileStatsScope('${sc}')" class="pf-scope-btn${scope === sc ? ' active' : ''}" aria-pressed="${scope === sc}"><i class="fas ${icon}"></i> ${label}</button>`;
    const toggle = `<div class="pf-scope-toggle" role="group">
            ${segBtn('club', t('profile.statsScopeClub'), 'fa-user')}
            ${segBtn('global', t('profile.statsScopeGlobal'), 'fa-earth-americas')}
        </div>`;

    let body = '';
    if (scope === 'club') {
        const m = ((typeof memberById === "function" && memberById(state.currentUser.id)) || state.members[state.currentUser.name]) || {};
        const score = Number(state.currentUser.points != null ? state.currentUser.points : (m.score || 0));
        const games = m.gamesPlayed || 0;
        const wins = (typeof countWins === 'function') ? countWins(state.currentUser.id) : 0;
        const winRate = games > 0 ? Math.round((wins / games) * 100) : 0;
        let eloTxt = '';
        if (state.eloEnabled && typeof computeElo === 'function') {
            const r = computeElo()[state.currentUser.id];
            if (r != null) eloTxt = `<div class="stat-card"><div class="stat-label">ELO</div><div class="stat-value">${r}</div></div>`;
        }
        body = `<div class="stats-grid" style="margin-bottom:8px;">
                <div class="stat-card"><div class="stat-label">${t('profile.points')}</div><div class="stat-value">${score.toFixed(1)}</div></div>
                <div class="stat-card"><div class="stat-label">${t('profile.games')}</div><div class="stat-value">${games}</div></div>
                <div class="stat-card"><div class="stat-label">${t('profile.winRate')}</div><div class="stat-value">${winRate}%</div></div>
                ${eloTxt}
            </div>
            <p style="color:var(--text-secondary); font-size:.85em; margin-bottom:22px;"><i class="fas fa-lock"></i> ${t('profile.pointsNote')}</p>`;
    } else {
        const g = window._profileGlobalStats;
        if (!g) {
            body = `<p style="color:var(--text-secondary); font-size:.9em; margin:10px 0 22px;"><i class="fas fa-spinner fa-spin"></i> ${t('profile.statsCalculating')}</p>`;
        } else if (g.error) {
            body = `<p style="color:var(--danger); font-size:.9em; margin:10px 0 22px;"><i class="fas fa-triangle-exclamation"></i> ${t('profile.statsLoadError')}</p>`;
        } else if (g.empty || !g.matches) {
            body = `<p style="color:var(--text-secondary); font-size:.9em; margin:10px 0 22px;">${t('cross.noSessions')}</p>`;
        } else {
            const xpLine = (g.nextThreshold != null)
                ? `${g.xp} / ${g.nextThreshold} ${t('profile.xp')}`
                : `${g.xp} ${t('profile.xp')}`;
            const eloCards = (g.perClubElo && g.perClubElo.length)
                ? g.perClubElo.map(c => `<div class="stat-card"><div class="stat-label">${esc(c.name)}</div><div class="stat-value">${c.elo == null ? '<span style="opacity:.45;">—</span>' : c.elo}</div></div>`).join('')
                : `<p style="color:var(--text-secondary); font-size:.85em;">${t('profile.noEloClubs')}</p>`;
            body = `<div class="stats-grid" style="margin-bottom:8px;">
                    <div class="stat-card pf-exp-card"><div class="stat-label"><i class="fas fa-medal"></i> ${t('pf.experience')}</div><div class="stat-value pf-exp-value">${t(g.levelKey)}</div><div class="pf-exp-xp">${xpLine}</div></div>
                    <div class="stat-card"><div class="stat-label">${t('profile.statMatches')}</div><div class="stat-value">${g.matches}</div></div>
                    <div class="stat-card"><div class="stat-label">${t('profile.statDistinctGames')}</div><div class="stat-value">${g.distinctGames}</div></div>
                    <div class="stat-card"><div class="stat-label">${t('profile.winRate')}</div><div class="stat-value">${g.winRate}%</div></div>
                    <div class="stat-card" title="${t('stats.totalScoresTip')}"><div class="stat-label">${t('profile.totalScores')}</div><div class="stat-value">${Math.round(g.points)}</div></div>
                </div>
                <div style="margin:14px 0 6px; font-weight:600;"><i class="fas fa-ranking-star"></i> ${t('profile.eloPerClub')}</div>
                <div class="pf-elo-scroll" style="margin-bottom:8px;">${eloCards}</div>
                <p style="color:var(--text-secondary); font-size:.85em; margin:8px 0 22px;"><i class="fas fa-layer-group"></i> ${t('profile.statsGlobalNote')}</p>`;
        }
    }

    return `<div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:10px;">
            <h3 style="margin:0;"><i class="fas fa-chart-simple"></i> ${t('profile.statsTitle')}</h3>
            ${toggle}
        </div>
        ${body}`;
}

/* Secțiunea de abonament din profilul propriu (nivel + cluburi folosite). */
/* Comută auto-reînnoirea abonamentului (prin Edge Function toggle-auto-renew → Stripe). */
async function toggleAutoRenew(enable) {
    if (!state.userSub) { showNotification(t('profile.noSub'), 'error'); return; }
    try {
        showNotification(t('sub.updating'), 'info');
        const { data, error } = await sb.functions.invoke('toggle-auto-renew', { body: { cancel_at_period_end: !enable } });
        if (error || (data && data.error)) {
            const code = (data && data.error) || (error && error.message) || '';
            showNotification(t('sub.autoRenewError') + (code ? ' (' + code + ')' : ''), 'error');
            return;
        }
        await loadUserSubscription();
        try { if (typeof renderMyProfile === 'function') renderMyProfile(); } catch (e) { logError('toggleAutoRenew', e); }
        showNotification(enable ? t('sub.autoRenewOnMsg') : t('sub.autoRenewOffMsg'), 'success');
    } catch (e) {
        showNotification(t('sub.autoRenewError'), 'error');
    }
}

/* Containerul care chiar derulează: în tabul de profil e fereastra, în fereastra modală
   e `.modal-content` cu `overflow:auto`. Fără asta, restaurarea poziției ar cădea pe
   elementul greșit și n-ar face nimic. */
function _pfFindScroller(el) {
    try {
        let n = el && el.parentElement;
        while (n && n !== document.body) {
            const st = window.getComputedStyle(n);
            if ((st.overflowY === 'auto' || st.overflowY === 'scroll') && n.scrollHeight > n.clientHeight + 4) return n;
            n = n.parentElement;
        }
    } catch (e) { logError('_pfFindScroller', e); }
    return null;
}

function renderMyProfile() {
    const box = document.getElementById('myProfileContent');
    if (!box || !state.currentUser) return;

    /* RO: Delegare pe container (golden standard §5.1) — inlocuieste `onclick=`.
       ⚠ `#myProfileContent` NU e un singur element pe toata durata sesiunii: la
       `openMyProfileModal()` cel static din index.html e redenumit `_mpcHidden` si se
       creeaza altul, cu acelasi id, in modal. De aceea garda e o PROPRIETATE PE ELEMENT,
       nu un steag global: fiecare container isi leaga ascultatorul o singura data, iar
       cel din modal dispare odata cu modalul. Ascultatorul e pe `box`, deci supravietuieste
       oricarei rescrieri de `innerHTML` de mai jos.
       EN: click delegation bound per container element (the modal creates a second one
       with the same id), guarded by a per-element flag; survives innerHTML re-renders. */
    if (!box.__pfActionsBound) {
        box.__pfActionsBound = true;
        box.addEventListener('click', function (ev) {
            const el = ev.target && ev.target.closest ? ev.target.closest('[data-action]') : null;
            if (!el || !box.contains(el)) return;
            if (el.dataset.action === 'profile-save') updateMyProfile();
        });
        /* RO: „a atins omul ceva?" — necesar din cauza reperului intarziat de mai jos.
           Doua incarcari ASINCRONE (jocurile detinute si spatiul fizic) rescriu campuri
           dupa randare, deci reperul trebuie refixat cand raspund. Dar daca intre timp
           utilizatorul a apucat sa scrie, o refixare i-ar inghiti modificarea si ar face-o
           invizibila pentru totdeauna. Din clipa in care atinge ceva, reperul ingheata.
           EN: user-interaction flag; the late baseline must never swallow a real edit. */
        const _pfMarkTouched = function () { window._pfTouched = true; };
        box.addEventListener('input', _pfMarkTouched);
        box.addEventListener('change', _pfMarkTouched);
    }
    const esc = s => window.escHtml(s);
    const u = (state.currentUser.email && state.users[state.currentUser.email]) || {};
    const m = ((typeof memberById === "function" && memberById(state.currentUser.id)) || state.members[state.currentUser.name]) || {};
    const avatar = u.avatar || m.avatar || '';
    const bio = u.bio || '';
    const bgg = u.bggUsername || '';
    _prefDraft = (u.preferredGames || []).slice();
    _prefImages = (u.profileExtra && u.profileExtra.favGameImages) || (m.profileExtra && m.profileExtra.favGameImages) || {};

    /* Statisticile (Puncte/Jocuri/Win rate/ELO) au acum un switch Acest club / Toate cluburile.
       La fiecare deschidere a profilului pornim pe „Acest club"; valorile se calculează în
       profileStatsSectionHTML(). */
    window._profileStatsScope = 'club';
    window._friendsAccOpen = false;
    window._friendsExpanded = false;
    const catalogOptions = Object.keys(state.catalog || {}).sort();

    let publicHtml = (typeof window.profileBuildEditorHTML === 'function') ? window.profileBuildEditorHTML() : '';
    // scot h3-ul intern „Public profile" (îl înlocuiește antetul de acordeon)
    publicHtml = publicHtml.replace(/^\s*<h3[\s\S]*?<\/h3>/, '');
    const _pfVisSrc = (u.profileExtra && u.profileExtra.vis) || (m.profileExtra && m.profileExtra.vis) || {};
    const favGamesVis = (typeof _pfVisSrc.favGames === 'boolean') ? _pfVisSrc.favGames : true;

    /* ⚠ 9.2 — „PAGINA ÎȘI DĂ REFRESH". `renderMyProfile` rescrie TOT conținutul, deci
       fiecare acordeon deschis se închidea și pagina sărea în sus. Se cheamă după
       salvare, la evenimente realtime, la prieteni acceptați, la notificări push — adică
       exact în momentele în care omul tocmai lucra într-o secțiune deschisă. Datele nu se
       pierdeau, dar experiența era de reîncărcare bruscă.
       Reținem ce era deschis și unde era pagina, apoi punem la loc după rescriere.
       EN: renderMyProfile rewrites everything, collapsing open accordions and jumping to
       the top; remember and restore both. */
    const _pfOpenPanels = [];
    try {
        box.querySelectorAll('.admin-acc-body').forEach(function (p) {
            /* ⚠ Doar panourile cu `id`. Unul singur n-are (secțiunea de securitate a
               aplicației), iar restaurarea lui după poziție s-ar lega de elementul greșit:
               o parte din panouri sunt injectate ASINCRON, după randare, deci ordinea nu e
               stabilă în momentul restaurării. Un test a prins exact asta. */
            if (p.id && !p.classList.contains('hidden')) _pfOpenPanels.push(p.id);
        });
    } catch (e) { logError('renderMyProfile.keepOpen', e); }
    const _pfScroller = (typeof _pfFindScroller === 'function') ? _pfFindScroller(box) : null;
    const _pfScrollTop = _pfScroller ? _pfScroller.scrollTop : (window.scrollY || 0);

    box.innerHTML = `
        <div class="pf-identity">
            <div class="pf-identity-avatar">
                <div id="pfHeaderAvatar">${avatarHtml(avatar, 56)}</div>
                <button type="button" class="pf-avatar-edit" onclick="openAvatarEditor()" title="${t('profile.editPhoto')}" aria-label="${t('profile.editPhoto')}"><i class="fas fa-pen"></i></button>
            </div>
            <div class="pf-identity-info">
                <div class="pf-identity-top">
                    <span class="pf-name">${esc(state.currentUser.name)}</span>
                    ${state.currentUser.userTag ? `<span class="pf-tag">@${esc(state.currentUser.userTag)}</span>` : ''}
                </div>
                <small class="pf-meta">${esc(state.currentUser.email || '')}${m.joinDate ? ' &middot; ' + t('profile.memberSince') + ' ' + esc(m.joinDate) : ''}</small>
            </div>
        </div>


        <div id="profileStatsSection">${profileStatsSectionHTML()}</div>
        <div id="myProfileTournamentStats"></div>

        <div id="myFriendsSection">${typeof window.renderMyFriendsHTML === 'function' ? window.renderMyFriendsHTML() : ''}</div>

        <!-- Notificările push au fost MUTATE în panoul de Setări (iulie 2026):
             sunt o preferință de dispozitiv, ca limba, nu date de profil. -->

        <div class="admin-acc profile-acc" style="margin-top:6px;">
        <button type="button" class="admin-acc-head" onclick="toggleTilePanel('pfSec-data', this)"><span><i class="fas fa-user-pen"></i> ${t('profile.myData')}</span><i class="fas fa-chevron-down admin-acc-chev"></i></button>
        <div id="pfSec-data" class="admin-acc-body hidden">

        <div class="form-row">
        <div class="form-group">
            <label for="pfName">${t('profile.displayName')}</label>
            <input type="text" id="pfName" class="form-control" value="${esc(state.currentUser.name)}">
            <small style="color:var(--text-secondary);">${t('profile.nameNote')}</small>
        </div>

        <div class="form-group">
            <!-- ATENTIE: @eticheta are DOUA stari. Blocata = doar text, deci un for= fix ar
                 tinti un camp inexistent (browserul raporteaza "Incorrect use of label for").
                 Editabila = exista campul pfUserTag si eticheta TREBUIE legata de el.
                 De aceea NU atributul for e conditionat, ci ELEMENTUL INTREG: label cu for cand
                 exista campul, span cand nu exista. Prima incercare a pus doar for-ul conditionat,
                 iar in starea blocata ramanea un label fara destinatar - exact avertismentul
                 "No label associated with a form field".
                 EN: the tag is either locked (plain text, nothing to point at) or editable
                 (pfUserTag exists), so the whole ELEMENT is conditional - a labelled label when
                 the field exists, a span when it does not. Making only the for attribute
                 conditional left an orphan label in the locked state.
                 NB: fara accente si fara backticks - textul e INTR-UN template literal. -->
            ${state.currentUser.userTagLocked
                ? `<span class="form-section-label">${t('profile.userTag')}</span>`
                : `<label for="pfUserTag">${t('profile.userTag')}</label>`}
            ${state.currentUser.userTagLocked ? `
                <div style="display:flex; align-items:center; gap:8px; flex-wrap:wrap;">
                    <span style="font-weight:700; color:var(--accent-primary); font-size:1.1em;">@${esc(state.currentUser.userTag || '')}</span>
                    <span class="pf-tag-lock"><i class="fas fa-lock"></i> ${t('profile.userTagLocked')}</span>
                </div>
                <small style="color:var(--text-secondary);">${t('profile.userTagLockedNote')}</small>
            ` : `
                <div style="display:flex; align-items:center; gap:6px;">
                    <span style="font-weight:700; color:var(--accent-primary); font-size:1.1em;">@</span>
                    <input type="text" id="pfUserTag" class="form-control" value="${esc(state.currentUser.userTag || '')}" placeholder="alessandro" autocomplete="off" oninput="this.value=this.value.toLowerCase().replace(/[^a-z0-9._]/g,'')">
                </div>
                <small style="color:var(--accent-gold,#ffd700);"><i class="fas fa-triangle-exclamation"></i> ${t('profile.userTagOnceNote')}</small>
            `}
        </div>
        </div>

        <div class="form-group">
            <label for="pfBio">${t('profile.about')}</label>
            <textarea id="pfBio" class="form-control" rows="3" placeholder="${t('profile.aboutPh')}">${esc(bio)}</textarea>
        </div>

        <div class="form-group">
            <label for="pfBgg">${t('profile.bggUser')}</label>
            <input type="text" id="pfBgg" class="form-control" value="${esc(bgg)}" placeholder="${t('profile.optional')}">
        </div>

        <input type="hidden" id="pfAvatar" value="${!isPhotoAvatar(avatar) && avatar ? esc(avatar) : ''}">
        <input type="hidden" id="pfPhoto" value="${isPhotoAvatar(avatar) ? esc(avatar) : ''}">

        </div>
        <button type="button" class="admin-acc-head" onclick="toggleTilePanel('pfSec-public', this)"><span><i class="fas fa-id-badge"></i> ${t('pf.publicTitle')}</span><i class="fas fa-chevron-down admin-acc-chev"></i></button>
        <div id="pfSec-public" class="admin-acc-body hidden">
        ${publicHtml}
        <div class="form-group" style="margin-top:14px;">
            <label for="pfOwnSearch"><i class="fas fa-dice"></i> ${t('pf.ownedGames')}</label>
            <small style="display:block; color:var(--text-secondary); margin-bottom:6px;">${t('pf.ownedHint')}</small>
            <div id="pfOwnChips"></div>
            <div class="pf-fav-search-wrap">
                <i class="fas fa-search pf-fav-search-icon"></i>
                <input type="text" id="pfOwnSearch" class="form-control" placeholder="${t('profile.favSearchPh')}" oninput="ownSearchLive()" autocomplete="off">
            </div>
            <div id="pfOwnResults" class="pf-fav-results"></div>
        </div>
        <div class="form-group" style="margin-top:14px;">
            ${typeof _rowHead === 'function' ? _rowHead('<i class="fas fa-heart"></i> ' + t('profile.favGames'), 'favGames', favGamesVis) : `<label for="pfFavSearch">${t('profile.favGames')}</label>`}
            <div id="pfPrefChips" class="pf-pref-chips"></div>
            <div class="pf-fav-search-wrap">
                <i class="fas fa-search pf-fav-search-icon"></i>
                <input type="text" id="pfFavSearch" class="form-control" placeholder="${t('profile.favSearchPh')}" oninput="favSearchLive()" autocomplete="off">
            </div>
            <div id="pfFavResults" class="pf-fav-results"></div>
        </div>
        </div>

        <button type="button" class="admin-acc-head" onclick="toggleTilePanel('pfSec-security', this)"><span><i class="fas fa-shield-halved"></i> ${t('profile.security')}</span><i class="fas fa-chevron-down admin-acc-chev"></i></button>
        <div id="pfSec-security" class="admin-acc-body hidden">
        <div class="form-group">
            <label for="pfEmail"><i class="fas fa-envelope"></i> ${t('profile.email')}</label>
            <input type="email" id="pfEmail" class="form-control" value="${esc(state.currentUser.email || '')}">
            <small style="color:var(--text-secondary);">${t('profile.emailNote')}</small>
        </div>
        <div class="pf-sec-checks">
            <label class="pf-check"><input type="checkbox" id="pfHideEmail" ${u.hideEmail ? 'checked' : ''}> <span>${t('profile.hideEmail')}</span></label>
            <label class="pf-check"><input type="checkbox" id="pfDiscoverable" ${u.isDiscoverable ? 'checked' : ''}> <span>${t('profile.discoverable')}</span></label>
        </div>
        <div class="form-row">
            <div class="form-group">
                <label for="pfNewPassword"><i class="fas fa-key"></i> ${t('profile.newPassword')}</label>
                <input type="password" id="pfNewPassword" class="form-control" placeholder="${t('profile.newPasswordPh')}" oninput="pwChecksUpdate('pfNewPassword','pfNewPasswordConfirm','pfPwBox')">
            </div>
            <div class="form-group">
                <label for="pfNewPasswordConfirm"><i class="fas fa-key"></i> ${t('profile.confirmPassword')}</label>
                <input type="password" id="pfNewPasswordConfirm" class="form-control" placeholder="${t('profile.confirmPasswordPh')}" oninput="pwChecksUpdate('pfNewPassword','pfNewPasswordConfirm','pfPwBox')">
            </div>
        </div>
        <small style="color:var(--text-secondary); display:block; margin:-6px 0 8px;">${t('profile.setPwNote')}</small>
        <div id="pfPwBox" style="margin:-2px 0 12px; font-size:0.82em; display:none;">
            <div style="height:6px; border-radius:4px; background:rgba(255,255,255,0.1); overflow:hidden; margin-bottom:8px;">
                <div class="pw-strength-bar" style="height:100%; width:0%; background:#b23b3b; transition:all .25s;"></div>
            </div>
            <div style="display:grid; grid-template-columns:1fr 1fr; gap:2px 12px;">
                <span data-req="len"><i class="fas fa-circle"></i> <span data-i18n="pw.len">${t('pw.len')}</span></span>
                <span data-req="upper"><i class="fas fa-circle"></i> <span data-i18n="pw.upper">${t('pw.upper')}</span></span>
                <span data-req="lower"><i class="fas fa-circle"></i> <span data-i18n="pw.lower">${t('pw.lower')}</span></span>
                <span data-req="digit"><i class="fas fa-circle"></i> <span data-i18n="pw.digit">${t('pw.digit')}</span></span>
                <span data-req="special"><i class="fas fa-circle"></i> <span data-i18n="pw.special">${t('pw.special')}</span></span>
                <span data-req="match"><i class="fas fa-circle"></i> <span data-i18n="pw.match">${t('pw.match')}</span></span>
            </div>
        </div>
        <div class="pf-gdpr-row" style="display:flex; flex-wrap:wrap; gap:8px; margin-top:6px;">
            <button type="button" class="btn btn-secondary" onclick="exportMyData()"><i class="fas fa-file-export"></i> ${t('gdpr.exportBtn')}</button>
            <button type="button" class="btn btn-secondary" onclick="openGdprRequest()"><i class="fas fa-envelope-open-text"></i> ${t('gdpr.reqBtn')}</button>
        </div>
        <small style="color:var(--text-secondary); display:block; margin:6px 0 12px;">${t('gdpr.exportNote')}</small>
        <button type="button" class="admin-acc-head" onclick="toggleTilePanel('pfBlockedPanel', this)"><span><i class="fas fa-ban"></i> ${t('block.listTitle')}</span><i class="fas fa-chevron-down admin-acc-chev"></i></button>
        <div id="pfBlockedPanel" class="admin-acc-body hidden">
            <small style="color:var(--text-secondary); display:block; margin-bottom:8px;">${t('block.listNote')}</small>
            <div id="pfBlockedList"></div>
        </div>
        <div id="pfDeletionState"></div>
        </div>
        </div>

        <div class="btn-group" style="margin-top:14px;">
            <button type="button" id="pfSaveBtn" class="btn btn-success" data-action="profile-save"><i class="fas fa-save"></i> ${t('profile.save')}</button>
        </div>`;

    renderPrefChips();

    /* RO: reperul pentru „ai modificari nesalvate?" (§8.2, pasul 3a).
       Doua incarcari de mai jos sunt ASINCRONE si rescriu campuri DUPA randare:
       `ownEditorInit` (jocurile detinute, corectate din DB) si `profileLoadMySpace`
       (spatiul fizic). Un reper luat doar acum ar fi depasit in momentul in care
       raspund, iar formularul ar aparea „modificat" fara ca omul sa fi atins nimic —
       cursa confirmata prin test la pasul 2.
       Fixam deci de doua ori: acum (ca sa existe un reper si daca cererile esueaza)
       si din nou cand ambele s-au asezat. EN: baseline twice — now, and after the two
       async loaders settle. */
    window._pfTouched = false;
    const _pfLoads = [];

    /* „Jocuri deținute" — editor (adaugă/șterge) în profilul propriu. */
    try { _pfLoads.push(Promise.resolve(ownEditorInit())); renderOwnChips(); } catch (e) { logError('renderMyProfile.owned', e); }
    try { if (typeof refreshDeletionState === 'function') refreshDeletionState(); } catch (e) { logError('renderMyProfile', e); }
    /* Statistici de turneu în profilul propriu (logica în tournaments.js). */
    /* v9 8.3: tournaments.js se încarcă la cerere ⇒ „încarcă, apoi cheamă", în tăcere.
       8.5-E: DOAR când profilul chiar se vede (tabul Profil sau fereastra de profil). `render()` și
       fiecare actualizare în timp real redesenează profilul și când e ascuns ⇒ înainte, tournaments.js
       (93 kB) se încărca imediat după pornire și pleca o cerere de turnee la fiecare redesenare
       (măsurat pe 21 oct., pe pagina de start). Deschiderea Profilului cheamă renderMyProfile ⇒ apar
       atunci. EN: tournament stats only while the profile is actually on screen. */
    const _pfVisible = (typeof currentTab !== 'undefined' && currentTab === 'profile') || !!(box.closest && box.closest('.modal.active'));
    try { if (_pfVisible && state.currentUser && window.TO_CHAT && window.TO_CHAT.useModule) { const _me = state.currentUser.id; window.TO_CHAT.useModule('tournaments.js', 'TO_TOURN', () => window.injectTournamentStats('myProfileTournamentStats', _me), true); } } catch (e) { logError('renderMyProfile.tourn', e); }
    /* Spațiu fizic: încarcă rândul propriu din profile_spaces și completează inputurile. */
    try { if (typeof window.profileLoadMySpace === 'function') _pfLoads.push(Promise.resolve(window.profileLoadMySpace())); } catch (e) { logError('renderMyProfile', e); }
    try { if (typeof window.blockRenderBlockedList === 'function') window.blockRenderBlockedList(); } catch (e) { logError('renderMyProfile', e); }
    /* Reflectă starea reală a notificărilor push (după ce subscription-ul async se rezolvă). */
    try { if (typeof refreshPushUI === 'function') setTimeout(refreshPushUI, 0); } catch (e) { logError('renderMyProfile', e); }
    /* Încarcă prietenii și reîmprospătează caseta (altfel apărea goală). */
    try { if (typeof loadFriendData === 'function') loadFriendData().then(function () { var sec = document.getElementById('myFriendsSection'); if (sec && typeof window.renderMyFriendsHTML === 'function') sec.innerHTML = window.renderMyFriendsHTML(); }); } catch (e) { logError('renderMyProfile', e); }

    /* 9.2: redeschidem secțiunile care erau deschise și revenim unde era pagina.
       ⚠ Fără animație și fără derulare lină: e o restaurare, nu o navigare — orice
       tranziție s-ar vedea tot ca un salt. */
    try {
        _pfOpenPanels.forEach(function (id) {
            const p = document.getElementById(id);
            if (!p) return;
            p.classList.remove('hidden');
            const head = p.previousElementSibling;
            if (head && head.classList && head.classList.contains('admin-acc-head')) head.classList.add('is-open');
        });
        if (_pfScrollTop > 0) {
            const sc = _pfScroller || null;
            if (sc) sc.scrollTop = _pfScrollTop;
            else window.scrollTo(0, _pfScrollTop);
        }
    } catch (e) { logError('renderMyProfile.restoreOpen', e); }

    /* Reperul provizoriu — exista chiar daca cele doua cereri de mai sus esueaza. */
    try { profileFormBaseline(); } catch (e) { logError('renderMyProfile.baseline', e); }
    /* Reperul final, dupa ce jocurile detinute si spatiul fizic au venit din DB.
       ⚠ `allSettled`, nu `all`: o cerere esuata NU trebuie sa lase profilul fara reper.
       ⚠ Refixam DOAR daca omul n-a atins inca nimic — altfel i-am inghiti modificarea. */
    try {
        Promise.allSettled(_pfLoads).then(function () {
            if (window._pfTouched) return;
            try { profileFormBaseline(); } catch (e) { logError('renderMyProfile.baseline2', e); }
        });
    } catch (e) { logError('renderMyProfile.baseline2', e); }
}

/* Profil accesibil din hub, inclusiv pentru conturi noi FĂRĂ club (profilul era
   un tab din mainApp, deci inaccesibil înainte de a intra într-un club). */
function openMyProfileModal() {
    if (!state.currentUser) return;
    const menu = document.getElementById('clubMenu'); if (menu) menu.classList.add('hidden');
    /* Eliberăm id-ul „myProfileContent" din mainApp (ascuns) ca să nu existe id-uri
       duplicate (pfName etc.); îl restaurăm la închidere. */
    const main = document.getElementById('myProfileContent');
    if (main && !document.getElementById('myProfileModal')) { main.innerHTML = ''; main.id = '_mpcHidden'; }
    const modal = document.createElement('div');
    modal.className = 'modal active'; modal.id = 'myProfileModal';
    modal.onclick = (e) => { if (e.target === modal) closeMyProfileModal(); };
    /* ⚠ DEFECT REPARAT (exista dinainte de garda de mai jos): tasta `Escape` NU trecea
       pe aici. Mergea la ascultatorul global -> `closeAllModals()` -> `_disposeModal()`,
       care stergea direct elementul fiindca modalul nu avea `__dispose`. Consecinta:
       `_mpcHidden` nu-si mai primea inapoi id-ul `myProfileContent`, deci TABUL de profil
       ramanea gol pana la reincarcarea paginii (`renderMyProfile` iesea imediat, negasind
       containerul). `_disposeModal` cheama `__dispose` daca exista — mecanismul era deja
       prevazut in cod, doar ca modalul acesta nu-l folosea.
       Acelasi carlig face ca toate cele trei iesiri (✕, clic pe fundal, Escape) sa treaca
       prin aceeasi functie, deci o singura garda le acopera pe toate.
       EN: Escape bypassed the close function and left the tab container renamed; __dispose
       routes every exit through one place. */
    modal.__dispose = function () { closeMyProfileModal(); };
    modal.innerHTML = `<div class="modal-content" style="max-width:760px; max-height:88vh; overflow:auto;">
        <div class="modal-header"><h2><i class="fas fa-user"></i> ${t('nav.profile')}</h2>
        <button class="modal-close" onclick="closeMyProfileModal()">×</button></div>
        <div id="myProfileContent"></div></div>`;
    document.body.appendChild(modal);
    try { renderMyProfile(); } catch (e) { console.warn('openMyProfileModal:', e && e.message); }
}
function closeMyProfileModal() {
    /* Garda „modificari nesalvate" (§8.2, pasul 4). Refolosim EXACT garda din nav.js,
       expusa ca `_navGuardProfileExit`, ca sa nu existe o a doua implementare a aceleiasi
       reguli. Intoarce `true` daca a interceptat: atunci nu inchidem nimic, iar inchiderea
       se face din dialog, prin functia data ca argument.
       ⚠ Aici NU verificam tabul curent: modalul se poate deschide de pe orice tab si chiar
       din hub. EN: reuses the nav.js guard; no current-tab condition applies to the modal. */
    try {
        if (typeof window._navGuardProfileExit === 'function'
            && window._navGuardProfileExit(_closeMyProfileModalNow)) return;
    } catch (e) { logError('closeMyProfileModal.guard', e); }
    _closeMyProfileModalNow();
}

/* Inchiderea propriu-zisa, fara nicio intrebare. Separata ca garda de mai sus sa o poata
   chema dupa raspunsul utilizatorului. EN: the actual close, no questions asked. */
function _closeMyProfileModalNow() {
    const modal = document.getElementById('myProfileModal'); if (modal) modal.remove();
    const hidden = document.getElementById('_mpcHidden'); if (hidden) hidden.id = 'myProfileContent';
}
window.closeMyProfileModal = closeMyProfileModal;

async function openPlayerProfile(name) {
    const m = (state.membersById && state.membersById[name]) || state.members[name];
    if (!m) { showNotification(t('sb.playerNotFoundDot'), 'error'); return; }
    /* §7: nivelul de experiență se calculează din statisticile GLOBALE. Le garantăm
       ÎNAINTE de paint, altfel profileExperienceLevel cade pe fallback-ul „doar clubul
       curent" și subnumără meciurile (nivel prea mic). Fetch doar dacă lipsesc din cache. */
    try { if (m.dbId) await ensurePlayerStats([m.dbId]); } catch (e) { console.warn('openPlayerProfile stats:', e && e.message); }
    /* Starea de prietenie trebuie știută ÎNAINTE de prima desenare, altfel butonul
       apare o clipă ca „Adaugă prieten" și abia apoi se corectează. Dacă datele sunt
       deja încărcate, e o promisiune deja rezolvată — zero cost, zero cerere. */
    try { await ensureFriendData(); } catch (e) { logError('openPlayerProfile.friends', e); }
    /* RO: Prietenia e o relație între CONTURI. Un membru fără cont e o intrare în
       lista clubului: nu are cine să accepte, iar cererea ar rămâne agățată
       definitiv (vezi nota de la `_acctStatus`). Butonul ar sugera fals că
       persoana e pe platformă; acțiunea corectă acolo e linkul de activare.
       Sursa principală e acum `hasAccount()`, disponibilă TUTUROR membrilor.
       `paStatusOf()` rămâne ca rezervă pentru administratori, dacă RPC-ul nou nu
       răspunde. Dacă niciuna nu știe, arătăm butonul — triggerul din baza de date
       oprește oricum o cerere imposibilă.
   EN: primary source is the new member-wide RPC; the admin-only activation status
       remains a fallback. If neither knows, the DB trigger is the backstop. */
    try { await ensureAccountStatus(); } catch (e) { logError('openPlayerProfile.acct', e); }
    const _acct = (typeof hasAccount === 'function') ? hasAccount(m.dbId) : null;
    const _paSt = (typeof paStatusOf === 'function') ? paStatusOf(m.dbId) : null;
    const _paFaraCont = (_acct === false) || (_acct === null && !!(_paSt && _paSt.activated === false));
    const dispName = m.displayName || m.name || name;
    const esc = s => window.escHtml(s);
    const games = m.gamesPlayed || 0;
    const wins = (typeof countWins === 'function') ? countWins(m.dbId) : 0;
    const winRate = games > 0 ? Math.round((wins / games) * 100) : 0;
    let eloTxt = '';
    if (state.eloEnabled && typeof computeElo === 'function') {
        const r = computeElo()[m.dbId];
        if (r != null) eloTxt = `<div class="stat-card"><div class="stat-label">ELO</div><div class="stat-value">${r}</div></div>`;
    }
    const avatar = m.avatar || '';
    /* v9 8.14-b: punctul online pe avatar, ca în profilul public (presence.js). Lipsea:
       din Players se deschide ACEASTĂ fereastră, nu cea publică (raportat de Alex). */
    const _presOk = !!(m.dbId && window.TO_PRES && !(state.currentUser && m.dbId === state.currentUser.id)
        && !(typeof window.isBlockedPair === 'function' && window.isBlockedPair(m.dbId)));
    const _presId = _presOk ? window.escHtml(String(m.dbId)) : '';
    /* ⚠ Aceeasi regula ca la profilul public: cand relatia e blocata raman NUMELE,
         eticheta si STATISTICILE. Emailul, data inscrierii, biografia, jocurile
         preferate/detinute, BGG si spatiul fizic se ascund. Filtrasem doar celalalt
         modal, iar aici datele continuau sa apara. */
    const _blocat = !!(m.dbId && typeof window.isBlockedPair === 'function' && window.isBlockedPair(m.dbId));
    const modal = document.createElement('div');
    modal.className = 'modal active';
    if (modal.dataset) modal.dataset.dynamic = '1';
    modal.dataset.back = '1';   /* „înapoi" de pe telefon o închide (chat-core.js, v9 zona 2) */
    modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
    modal.innerHTML = `
        <div class="modal-content" style="max-width:520px;">
            <div class="modal-header">
                <h2><i class="fas fa-id-card"></i> ${window.t('profile.playerProfile')}</h2>
                <button class="modal-close" data-pl-act="close" aria-label="${t('common.close')}">×</button>
            </div>
            <div style="display:flex; align-items:center; gap:14px; margin-bottom:16px;">
                <div class="pres-av" style="line-height:1;"${_presOk ? ' data-pres-watch="' + _presId + '"' : ''}>${(typeof window.blockAvatarHtml === 'function') ? window.blockAvatarHtml(avatar, 52, m.dbId) : avatarHtml(avatar, 52)}${_presOk ? '<span class="presence-dot" aria-hidden="true" data-pres-id="' + _presId + '"></span>' : ''}</div>
                <div>
                    <div style="font-size:1.25em; font-weight:700;">${esc(dispName)}${typeof window.profileNameHistoryIcon === 'function' ? window.profileNameHistoryIcon(m.dbId || name, m.profileExtra) : ''} ${m.role === 'admin' ? '<span class="badge-admin">ADMIN</span>' : ''}</div>
                    ${m.userTag ? `<div style="color:var(--accent-primary); font-weight:600; font-size:0.9em;">@${esc(m.userTag)}</div>` : ''}
                    <small style="color:var(--text-secondary);">${(function () {
                        /* 9.6: aceeași sursă ca în profilul public — o singură regulă. */
                        const _em = _blocat ? null : ((typeof visibleEmailOf === 'function') ? visibleEmailOf(m.dbId || m.id) : (m.hideEmail ? null : m.email));
                        return _em ? esc(_em) : t('profile.emailHidden');
                    })()}${(m.joinDate && !_blocat) ? ' &middot; ' + t('profile.memberSince') + ' ' + esc(m.joinDate) : ''}</small>
                </div>
            </div>
            <div class="stats-grid" style="margin-bottom:14px;">
                <div class="stat-card"><div class="stat-label">${t('profile.points')}</div><div class="stat-value">${(m.score || 0).toFixed(1)}</div></div>
                <div class="stat-card"><div class="stat-label">${t('profile.games')}</div><div class="stat-value">${games}</div></div>
                <div class="stat-card"><div class="stat-label">${t('profile.winRate')}</div><div class="stat-value">${winRate}%</div></div>
                ${eloTxt}
            </div>
            ${(m.bio && !_blocat) ? `<p style="margin-bottom:12px;"><strong>${t('pf.about')}:</strong> ${esc(m.bio)}</p>` : ''}
            <div id="playerOwnedGames"></div>
            ${(m.preferredGames && m.preferredGames.length && !_blocat) ? `<div style="margin-bottom:12px;"><strong>${t('profile.favGames')}:</strong><div style="margin-top:6px;">${(typeof favCardsHtml === 'function') ? favCardsHtml(m.preferredGames, (m.profileExtra && m.profileExtra.favGameImages) || {}, { key: 'fav-' + (m.dbId || dispName), heading: t('profile.favGames') }) : m.preferredGames.map(g => `<span class="badge badge-primary" style="font-size:.8em;">${esc(g)}</span>`).join('')}</div></div>` : ''}
            ${(m.bggUsername && !_blocat) ? `<p style="margin-bottom:8px;"><strong>BoardGameGeek:</strong> ${esc(m.bggUsername)}</p>` : ''}
            ${typeof window.profileRenderPublic === 'function' ? window.profileRenderPublic(Object.assign({ name: dispName }, m)) : ''}
            <div id="playerTournamentStats"></div>
            <div id="playerCharEvolution"></div>
            <div id="playerProfileSpace"></div>
            ${(m.dbId && m.dbId !== (state.currentUser && state.currentUser.id)) ? `<div style="display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-top:14px; border-top:1px solid var(--border); padding-top:12px;">${(typeof window.userActionsHtml === 'function') ? window.userActionsHtml(m.dbId, { name: dispName, member: m }) : `${(!_paFaraCont && typeof _friendBtnHtml === 'function') ? _friendBtnHtml({ id: m.dbId }) : ''}${(typeof window.dmButtonHtml === 'function') ? window.dmButtonHtml(m.dbId, dispName, m) : ''}${(typeof window.reportIconBtn === 'function') ? window.reportIconBtn('user', m.dbId) : ''}`}</div>` : ''}
        </div>`;
    document.body.appendChild(modal);
    /* ⚠ Modalul se INREGISTREAZA ca redeschidere, ca blocarea sa aiba efect pe loc.
         Fara asta, datele personale dispareau abia la refresh: `blockRefreshUi`
         redeseneaza doar randul de actiuni, nu tot modalul. */
    try { if (typeof window.registerProfileModal === 'function') window.registerProfileModal(m.dbId, function () { openPlayerProfile(name); }); } catch (e) { logError('openPlayerProfile', e); }
    /* §3: „Jocuri deținute" — fetch/paint separate, scopat la elementul din modalul curent. */
    try { if (m.dbId && !_blocat && typeof injectOwnedGames === 'function') injectOwnedGames(modal.querySelector('#playerOwnedGames'), m.dbId); } catch (e) { logError('openPlayerProfile.owned', e); }
    try { if (m.dbId && !_blocat && typeof injectProfileSpace === 'function') injectProfileSpace('playerProfileSpace', m.dbId); } catch (e) { logError('openPlayerProfile', e); }
    try { if (m.dbId && window.TO_CAMP && typeof window.TO_CAMP.injectCharEvolution === 'function') window.TO_CAMP.injectCharEvolution('playerCharEvolution', m.dbId); } catch (e) { logError('openPlayerProfile.evo', e); }
    /* Statistici de turneu: logica în tournaments.js (modul autonom), hook vizibil. */
    try { if (m.dbId && window.TO_CHAT && window.TO_CHAT.useModule) window.TO_CHAT.useModule('tournaments.js', 'TO_TOURN', () => window.injectTournamentStats('playerTournamentStats', m.dbId), true); } catch (e) { logError('openPlayerProfile.tourn', e); }
}

/* ============================================================
   DETECTOR DE MODIFICARI NESALVATE — PROFIL   (§8.2, pasul 2)
   ------------------------------------------------------------
   RO: raspunde la o singura intrebare: „formularul de profil arata acum altfel decat
   cand a fost deschis?". Nu e conectat la nimic in acest pas — se cheama doar manual.

   ⚠ REGULA DUPA CARE E CONSTRUIT: instantaneul acopera EXACT ce scrie `updateMyProfile`,
   nici mai mult, nici mai putin. Mai mult => intreaba degeaba (si oamenii invata sa dea
   click orbeste pe dialog). Mai putin => pierde tacut tocmai modificarea neacoperita.
   De aceea oglindeste si normalizarile salvarii (user tag-ul e trecut prin `toLowerCase()`
   si i se taie `@`; `bgg` gol devine null), si garzile ei („rescriem coperti doar daca
   editorul e chiar randat"). Orice diferenta intre cele doua = fals pozitiv sau pierdere.

   ⚠ CE NU E IN DOM: jocurile favorite (`_prefDraft`) si cele detinute (`_ownDraft`), plus
   hartile lor de coperti, sunt variabile globale. Un detector care ar citi doar `input`-uri
   nu le-ar vedea deloc, deci ai pierde in tacere exact modificarile facute din chips-uri.

   EN: snapshot of exactly what updateMyProfile writes, including its normalisations and
   its "only if the editor is rendered" guards, plus the non-DOM chip drafts.
   ============================================================ */

/* Serializare STABILA: cheile obiectelor sunt sortate, ca doua obiecte identice ca
   CONTINUT sa dea acelasi sir chiar daca au fost construite in ordine diferita.
   `JSON.stringify` simplu pastreaza ordinea de inserare, deci ar raporta „modificat"
   pentru o simpla reordonare interna — un fals pozitiv greu de depistat.
   ⚠ Ordinea din TABLOURI se pastreaza: acolo chiar e semnificativa (ordinea chips-urilor
   se salveaza ca atare), deci o reordonare facuta de utilizator ESTE o modificare. */
function _pfStableStr(v) {
    if (v === null || v === undefined) return 'null';
    if (Array.isArray(v)) return '[' + v.map(_pfStableStr).join(',') + ']';
    if (typeof v === 'object') {
        return '{' + Object.keys(v).sort().map(function (k) {
            return JSON.stringify(k) + ':' + _pfStableStr(v[k]);
        }).join(',') + '}';
    }
    return JSON.stringify(v);
}

/* Normalizarea user tag-ului, IDENTICA cu cea din `updateMyProfile`. Daca cele doua
   ar diferi, un tag scris cu majuscule ar aparea „modificat" imediat dupa salvare. */
function _pfNormTag(s) {
    return String(s == null ? '' : s).trim().replace(/^@+/, '').toLowerCase();
}

/* Instantaneul formularului. Intoarce un SIR, sau `null` daca formularul nu e randat
   (caz in care nu avem ce compara si nu trebuie sa deranjam pe nimeni). */
function profileFormSnapshot() {
    try {
        const nameEl = document.getElementById('pfName');
        if (!nameEl) return null;                     /* profilul nu e randat */
        const val = function (id) { const e = document.getElementById(id); return e ? (e.value || '') : ''; };
        const chk = function (id) { const e = document.getElementById(id); return !!(e && e.checked); };

        const snap = {
            name: (val('pfName') || '').trim(),
            userTag: _pfNormTag(val('pfUserTag')),
            photo: val('pfPhoto'),
            avatar: val('pfAvatar'),
            bio: (val('pfBio') || '').trim(),
            bgg: (val('pfBgg') || '').trim(),
            hideEmail: chk('pfHideEmail'),
            discoverable: chk('pfDiscoverable'),
            /* Parolele NU intra ca valoare — nu tinem o parola intr-o variabila globala.
               Ne intereseaza doar DACA au fost completate, fiindca si asta e lucru nesalvat. */
            pwdTouched: !!(val('pfNewPassword') || val('pfNewPasswordConfirm'))
        };

        /* Jocuri FAVORITE — salvarea rescrie copertile doar daca `#pfPrefChips` e randat. */
        if (document.getElementById('pfPrefChips')) {
            const pref = (typeof _prefDraft !== 'undefined' && Array.isArray(_prefDraft)) ? _prefDraft.slice() : [];
            snap.pref = pref;
            const used = {};
            pref.forEach(function (tt) {
                const k = String(tt || '').toLowerCase();
                if (typeof _prefImages !== 'undefined' && _prefImages && _prefImages[k]) used[k] = _prefImages[k];
            });
            snap.prefImages = used;
        }

        /* Jocuri DETINUTE — aceeasi garda ca in salvare (`#pfOwnChips`). */
        if (document.getElementById('pfOwnChips')) {
            const own = (typeof _ownDraft !== 'undefined' && Array.isArray(_ownDraft)) ? _ownDraft.slice() : [];
            snap.own = own;
            const usedOwn = {};
            own.forEach(function (tt) {
                const k = _ogKey(tt);
                if (typeof _ownImages !== 'undefined' && _ownImages && _ownImages[k]) usedOwn[k] = _ownImages[k];
            });
            snap.ownImages = usedOwn;
        }

        /* Profilul public (varsta, gen, oras, disponibilitate, vizibilitati...) — exact
           obiectul pe care il scrie salvarea, nu o reconstructie de-a noastra. */
        if (typeof window.profileCollectEditor === 'function' && document.getElementById('pfCountry')) {
            snap.extra = window.profileCollectEditor();
        }

        /* Spatiul fizic (`profile_spaces`). ⚠ NU e in `profileCollectEditor` — se scrie
           separat, prin `profileSaveSpace()`, chemat de `updateMyProfile` (2d). Fara el,
           bifa „am spatiu", adresa, capacitatea si cele doua vizibilitati s-ar pierde in
           tacere: exact tipul de scapare pe care detectorul trebuie s-o previna.
           Oglindim garda din `profileSaveSpace`: fara `#pfHasSpace` in DOM nu se scrie
           nimic, deci nici nu comparam. EN: physical space is saved separately; mirror
           its own "editor must be rendered" guard. */
        if (typeof window.profileCollectSpace === 'function' && document.getElementById('pfHasSpace')) {
            snap.space = window.profileCollectSpace();
        }

        return _pfStableStr(snap);
    } catch (e) {
        logError('profileFormSnapshot', e);
        return null;      /* la orice indoiala, NU raportam modificari */
    }
}

/* Fixeaza punctul de referinta („asa arata formularul la deschidere").
   ⚠ CAND se cheama e o decizie separata, care se ia la conectare (pasii 3-4): editorul de
   jocuri detinute se initializeaza ASINCRON (`ownEditorInit`, chemat fara `await` din
   `renderMyProfile`) si isi rescrie continutul DUPA ce vine raspunsul din DB. Un reper
   luat prea devreme ar face formularul „modificat" fara ca utilizatorul sa fi atins ceva. */
function profileFormBaseline() {
    window._pfBaseline = profileFormSnapshot();
    return window._pfBaseline;
}

/* Formularul difera de reper? `false` la orice incertitudine (formular neradat, reper
   nefixat, eroare): un dialog aparut degeaba e mai daunator decat unul lipsa. */
function profileFormIsDirty() {
    const now = profileFormSnapshot();
    if (now === null) return false;
    if (typeof window._pfBaseline !== 'string') return false;
    return now !== window._pfBaseline;
}

window.profileFormSnapshot = profileFormSnapshot;
window.profileFormBaseline = profileFormBaseline;
window.profileFormIsDirty = profileFormIsDirty;

async function updateMyProfile() {    if (!state.currentUser) return;
    const name = (document.getElementById('pfName').value || '').trim();
    if (!name) { showNotification(t('sb.nameEmpty'), 'error'); return; }
    /* User Tag (handle unic): normalizat la litere mici, cifre, punct, underscore. */
    let userTag = (document.getElementById('pfUserTag') ? document.getElementById('pfUserTag').value : '').trim().replace(/^@+/, '').toLowerCase();
    if (document.getElementById('pfUserTag')) {
        if (!userTag) { showNotification(t('profile.userTagEmpty'), 'error'); return; }
        if (!/^[a-z0-9._]{3,30}$/.test(userTag)) { showNotification(t('profile.userTagInvalid'), 'error'); return; }
    }
    const photo = (document.getElementById('pfPhoto') && document.getElementById('pfPhoto').value) || '';
    const emoji = (document.getElementById('pfAvatar') && document.getElementById('pfAvatar').value) || '';
    const avatar = photo ? photo : (emoji || null);
    const bio = (document.getElementById('pfBio').value || '').trim();
    const bgg = (document.getElementById('pfBgg').value || '').trim() || null;
    const preferred = (typeof _prefDraft !== 'undefined' && Array.isArray(_prefDraft)) ? _prefDraft.slice() : [];
    const hideEmail = !!(document.getElementById('pfHideEmail') && document.getElementById('pfHideEmail').checked);
    const discoverable = !!(document.getElementById('pfDiscoverable') && document.getElementById('pfDiscoverable').checked);
    const profileExtra = (typeof window.profileCollectEditor === 'function') ? window.profileCollectEditor() : null;

    /* ⚠ DB-FIRST (lecția iulie 2026 — bug care a șters date reale).
       `profileCollectEditor()` construiește un obiect NOU, cu doar cheile din formular.
       Înainte, restul cheilor erau recuperate din MEMORIA LOCALĂ; dacă aceasta era
       goală/învechită, „Salvează" ȘTERGEA din DB chei pe care nici măcar nu le vedea
       (nameHistory, ownedGames, ownedGameImages, favGameImages...).
       Acum baza e ÎNTOTDEAUNA valoarea REALĂ din DB, iar cheile din formular se
       SUPRAPUN peste ea. Orice cheie necunoscută codului supraviețuiește automat.
       Dacă citirea din DB eșuează, NU scriem deloc profile_extra (mai bine nesalvat
       decât șters). */
    let _peBase = null;      /* profile_extra REAL din DB */
    let _peReadOk = false;
    if (profileExtra) {
        try {
            const _cur = await sb.from('profiles').select('profile_extra').eq('id', state.currentUser.id).maybeSingle();
            if (_cur && !_cur.error) {
                _peReadOk = true;
                _peBase = (_cur.data && _cur.data.profile_extra && typeof _cur.data.profile_extra === 'object' && !Array.isArray(_cur.data.profile_extra))
                    ? _cur.data.profile_extra : {};
            }
        } catch (e) { logError('updateMyProfile.peRead', e); }
    }

    /* Istoric nume: dacă numele afișat se schimbă, păstrăm numele vechi ca să se știe cine e persoana. */
    if (profileExtra && _peReadOk) {
        const oldName = state.currentUser.name;
        const prevExtra = _peBase || {};   /* sursa de adevăr = DB, nu memoria */
        let hist = Array.isArray(prevExtra.nameHistory) ? prevExtra.nameHistory.slice() : [];
        if (oldName && name && oldName !== name && hist.indexOf(oldName) === -1) hist.unshift(oldName);
        if (hist.length > 10) hist = hist.slice(0, 10);
        profileExtra.nameHistory = hist;

        /* Coperțile jocurilor FAVORITE — rescrise doar dacă editorul e chiar randat.
           Altfel păstrăm valoarea din DB (nu presupunem „lipsă în memorie" = „șterge"). */
        try {
            if (document.getElementById('pfPrefChips')) {
                const used = {};
                (_prefDraft || []).forEach(tt => { const k = String(tt || '').toLowerCase(); if (_prefImages && _prefImages[k]) used[k] = _prefImages[k]; });
                profileExtra.favGameImages = used;
            } else if (prevExtra.favGameImages) {
                profileExtra.favGameImages = prevExtra.favGameImages;
            }
        } catch (e) { logError('updateMyProfile', e); }

        /* Jocurile DEȚINUTE — idem: rescrise doar dacă editorul lor e randat. */
        try {
            const draft = (typeof _ownDraft !== 'undefined' && Array.isArray(_ownDraft)) ? _ownDraft : [];
            if (document.getElementById('pfOwnChips')) {
                profileExtra.ownedGames = draft.slice();
                const usedOwn = {};
                draft.forEach(tt => {
                    const k = _ogKey(tt);
                    if (typeof _ownImages !== 'undefined' && _ownImages && _ownImages[k]) usedOwn[k] = _ownImages[k];
                });
                profileExtra.ownedGameImages = usedOwn;
            } else {
                if (Array.isArray(prevExtra.ownedGames)) profileExtra.ownedGames = prevExtra.ownedGames;
                if (prevExtra.ownedGameImages) profileExtra.ownedGameImages = prevExtra.ownedGameImages;
            }
        } catch (e) { logError('updateMyProfile', e); }
    }

    /* Securitate cont: email + parolă nouă (opționale) */
    const newEmail = (document.getElementById('pfEmail') ? document.getElementById('pfEmail').value : '').trim();
    const emailChanged = newEmail && newEmail.toLowerCase() !== (state.currentUser.email || '').toLowerCase();
    const newPass = document.getElementById('pfNewPassword') ? document.getElementById('pfNewPassword').value : '';
    const newPassConfirm = document.getElementById('pfNewPasswordConfirm') ? document.getElementById('pfNewPasswordConfirm').value : '';
    if (newPass) {
        const _pwp = (typeof checkPasswordRules === 'function') ? checkPasswordRules(newPass) : { allMet: newPass.length >= 8 };
        if (!_pwp.allMet) { showNotification((typeof t === 'function' ? t('pw.weak') : 'Parola nu e suficient de puternică: minim 8 caractere, literă mare, literă mică, cifră și un caracter special.'), 'error'); return; }
        if (newPass !== newPassConfirm) { showNotification(t('sb.newPassMismatch'), 'error'); return; }
    }

    const oldName = state.currentUser.name;
    const nameChanged = name !== oldName;

    /* Conflict: numele nou aparține deja ALTUI cont (profil cu alt email).
       Permitem însă „adoptarea" unui nume de jucător din istoric fără cont
       (ex. nume importat din BG Stats) — exact ca să-ți legi sesiunile vechi. */
    /* Numele este LIBER (ne-unic) după refactorul name->id: identitatea reală e id-ul,
       iar handle-ul unic e user_tag. Nu mai blocăm numele duplicat. */

    if (isDemoMode()) { showNotification(t('sb.demoNoSaveChanges'), 'info'); return; }

    /* ★ Marcăm că urmează scrierile NOASTRE pe `profiles`. Cât timp steagul e sus,
       `scheduleRealtimeRefresh` ignoră ecoul lor (altfel: 4 hidratări suprapuse peste
       cea pe care o facem oricum la final -> randare peste stare pe jumătate încărcată,
       adică avatarul și favoritele care „dispăreau").
       ⚠ Steagul se coboară în `finally`-ul de la finalul funcției, ORICE s-ar întâmpla. */
    window._selfWriteInFlight = true;

    /* RO: Rotita pe buton (varianta A): pornita DUPA toate validarile timpurii, ca sa nu
       existe nicio cale de iesire pe care butonul sa ramana blocat. Toate iesirile de aici
       incolo trec prin `finally`-ul de mai jos, care e singurul punct de resetare.
       Nota: pe calea de succes `renderMyProfile()` recreeaza butonul, deci `_resetBtn`
       atinge un element detasat — inofensiv, butonul proaspat e deja in starea normala.
       EN: spinner started after all early validations; the existing `finally` is the single
       reset point, so no early return can leave the button stuck. */
    const _pfBtn = document.getElementById('pfSaveBtn');
    const _pfSaveEsc = window.escHtml || function (s) { return s == null ? '' : String(s); };
    if (_pfBtn) { _pfBtn.disabled = true; _pfBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> ' + _pfSaveEsc(t('common.saving')); }
    const _pfResetBtn = function () {
        if (!_pfBtn) return;
        _pfBtn.disabled = false;
        _pfBtn.innerHTML = '<i class="fas fa-save"></i> ' + _pfSaveEsc(t('profile.save'));
    };

    try {

    /* 1) coloane garantate (există mereu): name, avatar. display_name e oglinda lui name. */
    const base = await sb.from('profiles').update({ name, display_name: name, avatar }).eq('id', state.currentUser.id);
    if (base.error) {
        /* dacă încă nu s-a rulat scriptul SQL (lipsește display_name), salvează fără ea */
        if (/display_name|column/i.test(base.error.message || '')) {
            const b2 = await sb.from('profiles').update({ name, avatar }).eq('id', state.currentUser.id);
            if (b2.error) { showNotification(t('m1.saveErrorColon') + b2.error.message, 'error'); return; }
        } else {
            const msg = (base.error.code === '23505' || /duplicate|unique/i.test(base.error.message || ''))
                ? t('profile.usernameTaken')
                : (t('m1.saveErrorColon') + base.error.message);
            showNotification(msg, 'error'); return;
        }
    }

    /* 1b) user_tag (handle unic) — schimbare O SINGURĂ DATĂ, apoi permanent. */
    const tagChanged = userTag && userTag !== (state.currentUser.userTag || '');
    if (tagChanged) {
        if (state.currentUser.userTagLocked) { showNotification(t('profile.userTagLockedNote'), 'error'); return; }
        const warn = t('profile.userTagChangeWarn').replace('{tag}', '@' + userTag);
        if (!await confirmModal(warn, { danger: true })) return;
        const tg = await sb.from('profiles').update({ user_tag: userTag, user_tag_locked: true }).eq('id', state.currentUser.id);
        if (tg.error) {
            const em = tg.error.message || '';
            if (tg.error.code === '23505' || /duplicate|unique/i.test(em)) {
                showNotification(t('profile.userTagTaken'), 'error'); return;
            } else if (/user_tag_locked/i.test(em) && /does not exist|column/i.test(em)) {
                /* coloana user_tag_locked încă nu există (SQL nerulat) -> salvează doar tag-ul */
                const tg2 = await sb.from('profiles').update({ user_tag: userTag }).eq('id', state.currentUser.id);
                if (tg2.error) {
                    if (tg2.error.code === '23505' || /duplicate|unique/i.test(tg2.error.message || '')) { showNotification(t('profile.userTagTaken'), 'error'); return; }
                    if (/user_tag|column/i.test(tg2.error.message || '')) { showNotification(t('profile.userTagNeedsSql'), 'info'); }
                    else { showNotification(t('sb.userTagColon') + tg2.error.message, 'error'); return; }
                } else {
                    state.currentUser.userTag = userTag; state.currentUser.userTagLocked = true;
                }
            } else if (/locked|cannot be changed/i.test(em)) {
                showNotification(t('profile.userTagLockedNote'), 'error'); return;
            } else if (/user_tag|column/i.test(em)) {
                showNotification(t('profile.userTagNeedsSql'), 'info');
            } else {
                showNotification(t('sb.userTagColon') + em, 'error'); return;
            }
        } else {
            state.currentUser.userTag = userTag;
            state.currentUser.userTagLocked = true;
        }
    }

    /* 2) coloane opționale (necesită profile_fields.sql). Dacă lipsesc, nu blocăm salvarea de bază.
       ⚠ `preferred_games` se scrie DOAR dacă editorul de favorite e chiar randat.
       Altfel, un `_prefDraft` gol (stare locală neîncărcată) ar ȘTERGE favoritele din DB
       — aceeași clasă de bug care a șters jocurile deținute. */
    const _favEditorOn = !!document.getElementById('pfPrefChips');
    const _extPatch = { bio, bgg_username: bgg, hide_email: hideEmail, is_discoverable: discoverable };
    if (_favEditorOn) _extPatch.preferred_games = preferred;

    const ext = await sb.from('profiles').update(_extPatch).eq('id', state.currentUser.id);
    if (ext.error) {
        /* poate lipsește o coloană opțională -> reîncearcă fără hide_email/is_discoverable */
        const _extPatch2 = { bio, bgg_username: bgg };
        if (_favEditorOn) _extPatch2.preferred_games = preferred;
        const ext2 = await sb.from('profiles').update(_extPatch2).eq('id', state.currentUser.id);
        if (ext2.error) showNotification(t('sb.nameAvatarSavedSql'), 'info');
    }

    /* 2c) Profil extins -> coloana jsonb `profile_extra`.
       ⚠ MERGE peste valoarea REALĂ din DB (`_peBase`), nu suprascriere:
       cheile din formular se suprapun, iar orice cheie necunoscută codului
       SUPRAVIEȚUIEȘTE. Dacă n-am putut citi baza (_peReadOk = false), NU scriem —
       mai bine nesalvat decât șters (asta a fost cauza pierderii de date). */
    if (profileExtra) {
        if (!_peReadOk) {
            console.warn('profile_extra: baza nu a putut fi citită din DB -> NU scriu (protecție anti-ștergere).');
            showNotification(t('sb.profileExtraSaveSkipped'), 'error');
        } else {
            const _peMerged = Object.assign({}, _peBase || {}, profileExtra);
            const pe = await sb.from('profiles').update({ profile_extra: _peMerged }).eq('id', state.currentUser.id);
            if (pe.error) console.warn('profile_extra:', pe.error.message);
            else {
                /* Sincronizăm memoria cu ce e acum în DB. */
                const _u = (state.currentUser.email && state.users && state.users[state.currentUser.email]) || null;
                if (_u) _u.profileExtra = _peMerged;
                const _m = (typeof memberById === 'function') ? memberById(state.currentUser.id) : null;
                if (_m) _m.profileExtra = _peMerged;
                /* v9 8.14: setarea „Cine vede când sunt online" se aplică imediat. */
                try { if (window.TO_PRES) window.TO_PRES.refresh(_peMerged); } catch (e) { logError('updateMyProfile.presence', e); }
            }
        }
    }

    /* 2d) Spațiu fizic (PII sensibil) -> tabel separat profile_spaces, gated pe vizibilitate
       prin RPC (NU în profile_extra, care e larg lizibil). */
    try { if (typeof window.profileSaveSpace === 'function') await window.profileSaveSpace(); } catch (e) { console.warn('profileSaveSpace:', e && e.message); }

    /* Persistă ascunderea email-ului în club_settings -> DOAR sub-intrarea proprie,
       prin RPC (server-side, fără a putea atinge intrările altor membri). */
    try {
        await sb.rpc('cs_set_profile_pref', {
            p_club_id: currentClubId,
            p_value: Object.assign({}, { hideEmail: hideEmail }, profileExtra || {})
        });
    } catch (e) { console.warn('Salvare profile_prefs:', e); }

    /* 2b) Email + parolă (Supabase Auth).
       - Cont cu parolă: cere parola actuală (reauth).
       - Cont pur-OAuth care își setează PRIMA parolă: cod pe email (OTP).
       - Schimbarea emailului pe cont OAuth e protejată de linkurile de confirmare. */
    let doEmail = emailChanged;
    let doPass = !!newPass;
    let passNonce = null;
    if (doEmail || doPass) {
        const hasPw = await _hasPasswordIdentity();
        if (hasPw) {
            if (!await reauthModal({ message: t('reauth.changeAuth') })) {
                doEmail = false; doPass = false;
                showNotification(t('reauth.skipped'), 'info');
            }
        } else if (doPass) {
            passNonce = await _emailOtpReauth();
            if (passNonce === null) { doPass = false; showNotification(t('reauth.skipped'), 'info'); }
        }
    }
    if (doEmail || doPass) {
        /* Asigură o sesiune proaspătă înainte de operațiile auth. Pe sesiunile
           Google tokenul poate fi „rece"/expirat, iar updateUser ar da
           „Auth session missing". refreshSession reface tokenul valid. */
        try {
            const _s = await sb.auth.getSession();
            if (_s && _s.data && _s.data.session) await sb.auth.refreshSession();
        } catch (e) { logError('updateMyProfile', e); }
    }
    if (doEmail) {
        const er = await sb.auth.updateUser({ email: newEmail });
        if (er.error) { showNotification(t('sb.emailColon') + er.error.message, 'error'); }
        else {
            /* NU schimbăm emailul în aplicație până nu e confirmat pe AMBELE adrese
               (veche + nouă). Doar anunțăm; sincronizarea reală se face la următorul
               login, din authUser.email (sursa de adevăr după confirmare). */
            showNotification(t('sb.emailChangeLinks'), 'info');
            var _pfEmail = document.getElementById('pfEmail');
            if (_pfEmail) _pfEmail.value = state.currentUser.email || '';
        }
    }
    if (doPass) {
        const payload = passNonce
            ? { password: newPass, nonce: passNonce, data: { has_password: true } }
            : { password: newPass, data: { has_password: true } };
        const pr = await sb.auth.updateUser(payload);
        if (pr.error) showNotification(t('sb.passwordColon') + pr.error.message, 'error');
        else { showNotification(t('sb.passwordChanged'), 'success'); try { _gdprMail('security_alert'); } catch (e) { logError('updateMyProfile', e); } }
    }

    /* 3) Dacă s-a schimbat numele, mutăm tot istoricul pe numele nou. */
    let moved = null;
    if (nameChanged) {
        showNotification(t('sb.movingHistory'), 'info');
        moved = await renameInHistory(oldName, name);
    }

    state.currentUser.name = name;
    state.currentUser.displayName = name;
    state.currentUser.avatar = avatar;
    await hydrateFromSupabase();
    /* Reflectă imediat alegerile locale (chiar dacă o coloană opțională lipsește în DB). */
    state.currentUser.hideEmail = hideEmail;
    state.currentUser.isDiscoverable = discoverable;
    const _myMember = (typeof memberById === 'function' && memberById(state.currentUser.id)) || state.members[name];
    if (_myMember) { _myMember.hideEmail = hideEmail; _myMember.preferredGames = preferred; _myMember.bio = bio; if (profileExtra) _myMember.profileExtra = profileExtra; }
    const myEmail = state.currentUser && state.currentUser.email;
    if (myEmail && state.users[myEmail]) { state.users[myEmail].hideEmail = hideEmail; state.users[myEmail].isDiscoverable = discoverable; if (profileExtra) state.users[myEmail].profileExtra = profileExtra; }
    render();
    updateUserDisplay();
    renderMyProfile();
    /* Reflectă imediat noul nume în chat și în clopoțel (altfel apărea doar la refresh). */
    if (typeof loadChat === 'function') loadChat();
    try { if (typeof loadAllNotifications === 'function') loadAllNotifications().then(function () { if (typeof renderNotifBadge === 'function') renderNotifBadge(); }); } catch (e) { logError('updateMyProfile', e); }
    showNotification(t('sb.profileUpdated'), 'success');
    if (moved) {
        addAuditLog('audit.userRenamed', name, { key: 'auditd.renameDetails', params: { old: oldName, 'new': name, m: moved.changedMatches, e: moved.changedEvents } });
        showNotification(t('sb.historyMoved').replace('{name}', name).replace('{m}', moved.changedMatches).replace('{e}', moved.changedEvents), 'success');
    }

    } finally {
        /* ★ OBLIGATORIU în `finally`: funcția are 9 `return`-uri devreme (validări,
           erori de DB). Dacă steagul rămânea sus, aplicația devenea SURDĂ la realtime
           până la refresh — un bug mult mai rău decât cel pe care îl reparăm. */
        window._selfWriteInFlight = false;
        _pfResetBtn();   /* RO: singurul punct de resetare a butonului. EN: single reset point. */

        /* Recuperăm evenimentele venite de la ALȚII cât timp steagul era sus.
           O singură reîmprospătare, nu una per eveniment ratat. */
        if (window._missedRefresh) {
            window._missedRefresh = false;
            try { scheduleRealtimeRefresh(); } catch (e) { logError('updateMyProfile.missedRefresh', e); }
        }
    }
}

/* Mută tot ce e legat de un nume vechi pe numele nou: sesiuni (matches),
   RSVP-uri (events), mesaje de chat (după email) și votul de grup activ. */
async function renameInHistory(oldName, newName) {
    let changedMatches = 0, changedEvents = 0;
    const dedupe = arr => arr.filter((x, i) => arr.indexOf(x) === i);

    /* matches: players[], scores{}, winners[] */
    const mres = await sb.from('matches').select('*').eq('club_id', currentClubId);
    if (!mres.error && Array.isArray(mres.data)) {
        for (const m of mres.data) {
            let players = sbArray(m.players);
            let scores = sbObject(m.scores);
            let winners = sbArray(m.winners);
            let touched = false;
            if (players.includes(oldName)) { players = dedupe(players.map(p => p === oldName ? newName : p)); touched = true; }
            if (Object.prototype.hasOwnProperty.call(scores, oldName)) {
                if (scores[newName] === undefined) scores[newName] = scores[oldName];
                delete scores[oldName]; touched = true;
            }
            if (winners.includes(oldName)) { winners = dedupe(winners.map(w => w === oldName ? newName : w)); touched = true; }
            if (touched) {
                const up = await sb.from('matches').update({ players, scores, winners }).eq('id', m.id);
                if (!up.error) changedMatches++;
            }
        }
    }

    /* 8.16-3 (31 oct. 2026): ramurile pe NUME pentru RSVP (`events.participants`) si pentru votul
       „Ce jucam" au fost scoase: ambele sunt pe id (diagnosticul V9_816: 18 / 18 chei RSVP sunt id,
       votul: `gv_cast_vote` scrie id-ul contului), deci nu se mai potriveau niciodata; cea de RSVP citea la fiecare
       redenumire TOATE evenimentele clubului, degeaba. `changedEvents` ramane 0 (aceeasi forma
       intoarsa). EN: dead name-keyed branches removed; the data is id-keyed. */

    /* chat: redenumire precisă, după email (doar mesajele utilizatorului) */
    if (state.currentUser.email) {
        await sb.from('messages').update({ sender_name: newName }).eq('sender_email', state.currentUser.email);
    }

    return { changedMatches, changedEvents };
}

/* ---------- Autentificare (Supabase Auth) ---------- */
/* Semnalizator: câte profile "guest" a fuzionat ultimul claim (V8/guest-claim).
   Folosit în onAuthenticated pentru un re-load defensiv al listei de cluburi. */
let _lastClaimMergedCount = 0;
/* ★ T5: true doar când ensureProfile chiar a CREAT profilul în acest login —
   acoperă toate fluxurile (email cu sesiune instant, email cu confirmare,
   Google Sign-Up). Consumat o singură dată, la finalul onAuthenticated. */
let _freshProfileCreated = false;

async function ensureProfile(authUser, fallbackName) {
    _lastClaimMergedCount = 0;
    _freshProfileCreated = false;
    let prof = null;

    /* 1) Caută profilul după id (cazul normal: cont creat prin înregistrare).
       Reîncercăm de câteva ori: imediat după un login OAuth (Google) sesiunea
       poate să nu fie încă „caldă" pentru RLS, iar un singur SELECT ar întoarce
       null → am crea din greșeală un profil gol. Userii existenți îl găsesc de
       la prima încercare (fără întârziere); doar cazul „sesiune rece" mai așteaptă.

       ⚠ CRITIC: distingem „rândul nu există" (data null, FĂRĂ eroare) de
       „interogarea a EȘUAT" (eroare: coloană lipsă / RLS / rețea). Înainte, ambele
       erau tratate identic → la o migrare neaplicată aterizam într-un CONT GOL. */
    let selErr = null;
    for (let attempt = 0; attempt < 3; attempt++) {
        try {
            const res = await sb.from('profiles').select(window.PROFILE_COLS).eq('id', authUser.id).maybeSingle();
            if (res && res.data) { prof = res.data; selErr = null; break; }
            selErr = (res && res.error) || null;
        } catch (e) { selErr = e; }
        if (attempt < 2) await new Promise(function (r) { setTimeout(r, 300); });
    }

    /* Interogarea a eșuat definitiv => NU știm dacă ai profil. NU-l creăm, NU cădem
       pe un obiect gol (care s-ar putea salva peste rândul real). Oprim curat.
       Afișăm mesajul REAL al erorii — altfel nu se poate diagnostica. */
    if (!prof && selErr) {
        const emsg = (selErr && (selErr.message || selErr.msg)) || String(selErr);
        _authTrace('ensureProfile:SELECT-esuat', { err: emsg });
        console.error('ensureProfile: SELECT eșuat -', emsg, selErr);
        showNotification(t('sb.profileLoadFailed') + ' [' + emsg + ']', 'error');
        return null;
    }

    /* 2) Dacă nu există după id, caută un profil „Guest cu email" creat cândva de
       un admin (id aleatoriu, fără cont). NU mai facem id-swap (fragil cu FK-urile):
       reținem doar NUMELE guest-ului, ca să aliniem istoricul (meciurile sunt pe nume),
       iar corelarea completă (cluburi, referințe) o face RPC-ul `claim_guest_by_email`. */
    let guestName = null;
    if (!prof && authUser.email) {
        try {
            /* Coloana `email` e revocată la SELECT (V8) — nu mai putem filtra pe ea din client.
               RPC-ul definer derivă emailul din JWT și întoarce numele guest-ului cu același email. */
            const gn = await sb.rpc('guest_name_for_my_email');
            if (gn && !gn.error && gn.data) guestName = gn.data || null;
        } catch (e) { /* ignore */ }
    }

    /* 3) Creează profilul REAL (id = auth.uid), folosind numele guest-ului dacă există
       (pentru a alinia istoricul de meciuri, care e cheiat pe nume). */
    if (!prof) {
        const name = guestName || fallbackName || (authUser.user_metadata && authUser.user_metadata.name) || (authUser.email || '').split('@')[0];
        let ins = await sb.from('profiles')
            .insert({ id: authUser.id, email: authUser.email, name, display_name: name, role: 'member', points: 0 })
            .select(window.PROFILE_COLS).maybeSingle();
        if (ins.error && /display_name|column/i.test(ins.error.message || '')) {
            ins = await sb.from('profiles')
                .insert({ id: authUser.id, email: authUser.email, name, role: 'member', points: 0 })
                .select(window.PROFILE_COLS).maybeSingle();
        }
        if (ins.error) {
            /* Insert eșuat = profilul există deja (conflict de cheie) SAU sesiunea
               nu era încă caldă. NU întoarcem un fallback gol: re-citim profilul
               REAL, ca să nu „aterizăm" într-un cont gol (member, 0 puncte). */
            for (let attempt = 0; attempt < 3 && !prof; attempt++) {
                try {
                    const reSel = await sb.from('profiles').select(window.PROFILE_COLS).eq('id', authUser.id).maybeSingle();
                    if (reSel && reSel.data) { prof = reSel.data; break; }
                } catch (e) { /* reîncearcă */ }
                if (attempt < 2) await new Promise(function (r) { setTimeout(r, 300); });
            }
            if (!prof) {
                console.warn('ensureProfile:', ins.error.message);
                return { id: authUser.id, email: authUser.email, name, role: 'member', points: 0 };
            }
        } else {
            prof = ins.data;
            _freshProfileCreated = true;   /* ★ T5: cont nou, creat chiar acum */
        }
    }

    /* 4) Corelează (server-side, FK-safe) orice profil „Guest cu email" cu același email:
       mută apartenențele la cluburi și referințele pe noul cont, apoi șterge guest-ul.
       Best-effort: dacă RPC-ul nu e încă deployat, ignorăm fără să blocăm login-ul. */
    try {
        const cg = await sb.rpc('claim_guest_by_email');
        if (cg && !cg.error) {
            _lastClaimMergedCount = (cg.data | 0) || 0;   /* nr. profile guest fuzionate */
            /* re-citește profilul: numele/punctele pot fi schimbate de merge */
            const res2 = await sb.from('profiles').select(window.PROFILE_COLS).eq('id', authUser.id).maybeSingle();
            if (res2 && res2.data) prof = res2.data;
        }
    } catch (e) { /* RPC indisponibil → rulează turnorder_guest_claim.sql */ }

    /* user_tag-ul e generat pe SERVER (trigger handle_new_user pe auth.users),
       deci la momentul ăsta profilul îl are deja. Nu mai facem backfill din client
       (UPDATE-ul era oricum blocat de RLS). */
    return prof;
}

async function onAuthenticated(session, fallbackName) {
    const authUser = session.user;
    resetAppState(); /* slate curat: fără date din contul anterior */
    const prof = await ensureProfile(authUser, fallbackName);
    /* Profilul NU s-a putut încărca (eroare de interogare, nu „lipsă"). Nu intrăm în
       aplicație cu un cont gol — ar arăta ca un cont resetat (fără poză/user_tag/
       consimțământ) și un „Salvează" ar suprascrie rândul REAL din DB. */
    if (!prof || !prof.id) {
        _authTrace('onAuthenticated:FARA-profil -> login');
        resetAppState();
        if (typeof showLogin === 'function') showLogin();
        return;
    }
    /* Sincronizează emailul: authUser.email e sursa de adevăr. Coloana `email` din
       profiles nu mai e citibilă (V8 revoke), deci `prof.email` e null aici — folosim
       direct authUser.email pentru state și scriem best-effort în profiles (UPDATE-ul
       pe coloana email rămâne permis; doar SELECT e revocat) ca RPC-urile server-side
       (emails_for, claim) să aibă valoarea curentă. */
    /* ─── Batch 4a (perf): SCRIERE „trimite și uită" ────────────────────────────
       RO: era `await` — pornirea aplicației aștepta confirmarea unei SCRIERI de care
           nu depinde nimic mai jos. `prof.email` se setează local (nu se recitește
           de la server), iar valoarea din DB e folosită abia mai târziu, de RPC-uri
           server-side. Acum scrierea pleacă în fundal și pornirea continuă imediat.
       EN: this was an awaited WRITE nothing downstream depends on; now fire-and-forget. */
    try {
        if (authUser.email) {
            prof.email = authUser.email;
            sb.from('profiles').update({ email: authUser.email }).eq('id', prof.id)
                .then(function () { }, function (e) { logError('onAuthenticated:emailSync', e); });
        }
    } catch (e) { logError('onAuthenticated', e); }
    try { await checkAccountDeactivation(authUser); } catch (e) { logError('onAuthenticated', e); }
    state.currentUser = {
        id: prof.id, name: prof.name, email: authUser.email || prof.email || null,
        displayName: prof.display_name || prof.name,
        userTag: prof.user_tag || '',
        userTagLocked: !!prof.user_tag_locked,
        createdAt: prof.created_at || null,
        ownedGames: (prof.profile_extra && Array.isArray(prof.profile_extra.ownedGames)) ? prof.profile_extra.ownedGames.slice() : [],
        /* ⚠ 9.2: `profile_extra` INTREG, nu doar `ownedGames`. Acolo stau și preferințele
           de interfață (modul catalogului). Lipsa lui făcea ca preferința să pară că nu
           se salvează: era scrisă corect în baza de date, dar nu era citită nicăieri la
           pornire. ZERO cereri în plus — `prof` e deja încărcat. */
        profileExtra: (prof.profile_extra && typeof prof.profile_extra === 'object' && !Array.isArray(prof.profile_extra)) ? prof.profile_extra : null,
        role: prof.role || 'member', isAdmin: (prof.role === 'admin'),
        points: Number(prof.points) || 0, isDemo: false,
        /* Task 4: `last_club_id` NU e în PROFILE_COLS (și nici nu poate fi — vezi nota din
           loadMyClubsAndSelect), deci `prof.last_club_id` era MEREU undefined aici, iar
           expresia dădea invariabil null. Lăsăm null explicit: valoarea reală vine din
           RPC-ul `my_last_club()`, apelat în loadMyClubsAndSelect imediat după. */
        lastClubId: null
    };
    isAdmin = state.currentUser.isAdmin;
    /* GARDĂ DE CONSIMȚĂMÂNT (închide bypass-ul OAuth de pe Sign In): niciun user
       nu intră în #mainApp/hub fără consimțământ înregistrat în DB. Vezi
       _enforceConsentGate: persistă flag-ul de la register SAU cere-l blocant. */
    try {
        const _consentOk = await _enforceConsentGate(prof);
        if (!_consentOk) {
            try { await sb.auth.signOut(); } catch (e) { logError('onAuthenticated', e); }
            resetAppState();
            if (typeof showLogin === 'function') showLogin();
            return;
        }
    } catch (e) { logError('onAuthenticated', e); }
    /* Sincronizează ștergerile de notificări salvate server-side (permanent, cross-device). */
    try {
        if (prof.notif_cleared_at) {
            const k = notifClearedKey(); const sTs = new Date(prof.notif_cleared_at).getTime();
            if (k && sTs > getNotifClearedTs()) localStorage.setItem(k, String(sTs));
        }
        if (Array.isArray(prof.notif_dismissed) && prof.notif_dismissed.length) {
            const d = getDismissedIds(); prof.notif_dismissed.forEach(function (id) { d.add(id); }); saveDismissedIds(d);
        }
        /* Pragul „citit până la", salvat de „Marchează tot ca citit" în `profile_extra`.
           RO: fără linia asta, un browser nou / o fereastră privată porneau cu pragul 0
               și bulina reapărea cu notificări marcate demult ca citite pe alt dispozitiv.
               `setNotifReadAt` păstrează valoarea mai MARE, deci o marcare locală făcută
               între timp nu e retrogradată de o valoare veche din DB.
           EN: pull the account-wide "read up to" threshold; without it a fresh browser
               started at 0 and the badge resurfaced notifications read elsewhere.
               `setNotifReadAt` keeps the greater value, so a newer local mark wins. */
        try {
            const _pex = prof.profile_extra || null;
            const _ra = _pex && _pex.notifReadAt ? new Date(_pex.notifReadAt).getTime() : 0;
            if (_ra && typeof setNotifReadAt === 'function') setNotifReadAt(_ra);
        } catch (e) { logError('onAuthenticated', e); }
    } catch (e) { logError('onAuthenticated', e); }
    try { if (typeof setUserForLang === 'function') setUserForLang(state.currentUser.id); } catch (e) { logError('onAuthenticated', e); }
    /* F4: la login, datele clubului încă se încarcă => fără notificări de realizări „vechi". */
    try { achStartSilentWindow(); } catch (e) { logError('onAuthenticated', e); }
    await loadPlatformRole(authUser);
    try { sessionStorage.setItem('gbgsc_tab', '1'); } catch (e) { logError('onAuthenticated', e); }
    /* ─── Batch 4a (perf): cele două refresh-uri pleacă SIMULTAN ────────────────
       RO: erau două `await` unul după altul = două dus-întorsuri în serie, deși nu
           depind unul de celălalt (unul reîmprospătează ciclul de viață al cluburilor,
           celălalt starea de depășire de limită). Ambele trebuie însă TERMINATE
           înainte de `loadMyClubsAndSelect`, care le citește rezultatul — de aceea
           rămâne `await` pe grup, nu pe fiecare în parte.
       EN: two independent maintenance RPCs, previously serial; now issued in parallel
           but still awaited as a group because loadMyClubsAndSelect reads their result. */
    try {
        await Promise.all([
            sb.rpc('refresh_my_clubs_lifecycle').then(function () { }, function () { }),
            sb.rpc('refresh_my_over_limit').then(function () { }, function () { })
        ]);
    } catch (e) { /* RPC-uri indisponibile → vezi turnorder_club_lifecycle_claim.sql / _over_limit.sql */ }
    await loadMyClubsAndSelect(authUser);
    /* Hardening guest-claim: dacă tocmai am fuzionat un profil "guest" (cu apartenențe
       la cluburi), dar lista a venit goală/fără club selectat — posibil lag read-after-write
       imediat după merge — reîncercăm O DATĂ după o scurtă pauză, ca userul să NU vadă
       tranzitoriu "în afara oricărui club" chiar după fuziune. */
    if (_lastClaimMergedCount > 0 && (!(state.myClubs || []).length || !currentClubId)) {
        await new Promise(function (r) { setTimeout(r, 500); });
        await loadMyClubsAndSelect(authUser);
    }
    /* ─── Batch 4a (perf): abonamentul și starea trialului pleacă SIMULTAN ──────
       RO: două interogări independente (una scrie `state.userSub`, cealaltă
           `_trialStatus`) care se așteptau una pe alta degeaba. `applyClubRole()`
           de mai jos are nevoie de amândouă, deci le așteptăm ca grup.
       EN: two independent lookups, previously serial; awaited as a group. */
    try {
        await Promise.all([
            loadUserSubscription().catch(function (e) { logError('onAuthenticated:sub', e); }),
            loadTrialStatus(true).catch(function (e) { logError('onAuthenticated:trial', e); })
        ]);
    } catch (e) { logError('onAuthenticated', e); }
    applyClubRole();
    const hasClub = (state.myClubs || []).length > 0 && !!currentClubId;
    if (hasClub) {
        await hydrateFromSupabase();
        subscribeRealtimeChat();
        subscribeRealtimeData();
        subscribeRealtimePresence();
        subscribeRealtimeTimer();
        subscribeRealtimeLive();
        try {
            loadLiveSessions().then(function () {
                if (typeof renderLiveSessionsBar === 'function') renderLiveSessionsBar();
                /* 9.2: dacă tocmai am dat refresh într-o sesiune live vie, ne întoarce la ea. */
                try { if (typeof restoreLiveBinding === 'function') restoreLiveBinding(); } catch (e) { logError('init.restoreLive', e); }
            });
        } catch (e) { logError('init', e); }
    }
    /* ─── Batch 4b (perf): badge-urile NU mai întârzie afișarea aplicației ───────
       RO: erau două `await` chiar ÎNAINTE de `showMainApp()` — adică utilizatorul
           aștepta două dus-întorsuri la server ca să se populeze DOUĂ CIFRE pe niște
           iconițe (clopoțelul de notificări și badge-ul de cereri în așteptare).
           Aplicația e complet utilizabilă fără ele: se afișează imediat, iar cifrele
           apar o clipă mai târziu, exact ca într-o aplicație nativă.
           Ordinea internă e păstrată: `renderNotifBadge()` rulează tot DUPĂ
           `loadAllNotifications()`, doar că în fundal.
       EN: two awaited round trips gated the first paint of the app just to fill in
           two badge counters; now fire-and-forget, so the UI appears immediately. */
    Promise.resolve()
        .then(function () { return refreshPendingBadge(); })
        .catch(function (e) { logError('onAuthenticated:pendingBadge', e); });
    Promise.resolve()
        .then(function () { return loadAllNotifications(); })
        .then(function () { if (typeof renderNotifBadge === 'function') renderNotifBadge(); })
        .catch(function (e) { logError('onAuthenticated:notifs', e); });

    /* F1d (3 oct. 2026): cu o invitație sau o activare în așteptare, NU afișăm încă hub-ul:
       el ar apărea o clipă gol, apoi invitația ar intra în club. Ecranul de confirmare rămâne
       pe loc până intră clubul. Fără token, ordinea e cea de dinainte. */
    let _hadInvite = false;
    try { _hadInvite = !!paPeekEntryToken('invite'); } catch (e) { logError('onAuthenticated', e); }
    let _hadPa = false;
    try { _hadPa = !!paPeekEntryToken('pa'); } catch (e) { logError('onAuthenticated', e); }
    const _entryPending = _hadInvite || _hadPa;
    _authTrace('auth:intrare', { invitatie: _hadInvite, activare: _hadPa });
    const _renderInitialView = function () {
    let lastView = null;
    try { lastView = localStorage.getItem(lastViewKey(authUser.id)); } catch (e) { logError('onAuthenticated', e); }
    if (hasClub && lastView !== 'hub') {
        /* Rămâi în clubul în care erai (la refresh) */
        document.getElementById('clubHub').classList.add('hidden');
        showMainApp();
    } else {
        showClubHub();
    }
    };
    if (!_entryPending) _renderInitialView();
    /* ★ T5: onboarding la primul login — pe TOATE fluxurile (email instant, cod de
       confirmare, Google). Rulează după _enforceConsentGate (mai sus). Din F1d apare și
       după o invitație, dar abia DUPĂ ce clubul e pe ecran (vezi mai jos). */
    /* ★ Etapa 2: activarea ÎNAINTE de invitație. Activarea aduce cu ea
       apartenențele profilului vechi; dacă am rula invitația prima, am putea
       intra în club cu contul nou și apoi activarea s-ar lovi de propria noastră
       apartenență („already_member"). */
    await processPendingActivation();
    /* F1f (3 oct. 2026): activarea unui profil reușită reîncarcă pagina peste ~2 s (singura
       stare curată după fuziunea profilurilor). Până atunci NU mai pornim nimic: nici invitația,
       nici bun-venitul, nici ecranul. Le face, o singură dată, pornirea de după reîncărcare
       (tokenul invitației rămâne în sesiunea filei). Înainte, bun-venitul apărea o clipă și
       dispărea odată cu reîncărcarea. EN: after a successful activation, wait for the reload. */
    if (window.__paReloading) { _authTrace('auth:asteapta-reincarcarea'); return; }
    await processPendingInvite();
    /* F1d: dacă invitația n-a putut intra în club (invalidă, club plin), afișăm acum ecranul
       obișnuit. Excepție: activarea reușită, care oricum reîncarcă pagina. */
    if (_entryPending && !window.__paReloading) {
        const _app = document.getElementById('mainApp'), _hub = document.getElementById('clubHub');
        const _shown = (_app && !_app.classList.contains('hidden')) || (_hub && !_hub.classList.contains('hidden'));
        if (!_shown) _renderInitialView();
    }
    /* ★ T5-fix: profilul e creat de triggerul DB `handle_new_user` (nu de client),
       deci insert-ul din ensureProfile nu rulează aproape niciodată. „Cont proaspăt"
       = profil creat în ultimele 48h; marcajul `to_welcomed_<id>` (localStorage)
       garantează o singură afișare per dispozitiv. */
    let _isFresh = _freshProfileCreated;
    _freshProfileCreated = false;
    try {
        if (!_isFresh && state.currentUser && state.currentUser.createdAt) {
            const _ageMs = Date.now() - new Date(state.currentUser.createdAt).getTime();
            if (isFinite(_ageMs) && _ageMs >= 0 && _ageMs < 48 * 3600 * 1000) _isFresh = true;
        }
    } catch (e) { logError('onAuthenticated', e); }
    const _welcomedKey = 'to_welcomed_' + ((state.currentUser && state.currentUser.id) || '');
    const _welcomePendKey = 'to_welcome_pending_' + ((state.currentUser && state.currentUser.id) || '');
    /* F1d + F1e: bun-venitul apare și după o invitație, DUPĂ ce clubul e pe ecran. Rămâne „în
       așteptare" (localStorage) până când utilizatorul decide, așa că reapare și după o
       reîncărcare a paginii. Marcajul „văzut" îl pune doar decizia utilizatorului (welcomeDecide). */
    try { if (_isFresh && !localStorage.getItem(_welcomedKey)) localStorage.setItem(_welcomePendKey, '1'); } catch (e) { logError('onAuthenticated', e); }
    let _showWelcome = false;
    try { _showWelcome = localStorage.getItem(_welcomePendKey) === '1' && !localStorage.getItem(_welcomedKey); } catch (e) { logError('onAuthenticated', e); }
    if (_showWelcome) {
        _authTrace('welcome:arata');
        try { if (typeof showWelcomeModal === 'function') setTimeout(showWelcomeModal, 600); } catch (e) { logError('onAuthenticated', e); }
    }
    try { handleCheckoutReturn(); } catch (e) { logError('onAuthenticated', e); }
    try { syncPushOnLogin(); } catch (e) { logError('onAuthenticated', e); }
    try { syncLangOnLogin(); } catch (e) { logError('onAuthenticated', e); }
    try { maybeAutoPromptPush(); } catch (e) { logError('onAuthenticated', e); }
    try { consumePushRouteFromUrl(); } catch (e) { logError('onAuthenticated', e); }
}

/* ── Guard: rulează onAuthenticated O SINGURĂ DATĂ ──────────────────────────
   RO: punctele de intrare (login pe parolă, register, auto-login, restaurare
       sesiune la init, ȘI evenimentul SIGNED_IN de la returul Google) pot să
       încerce simultan să logheze userul. Acest wrapper coalescează apelurile:
       primul pornește onAuthenticated, ceilalți așteaptă același rezultat —
       așa evităm dubla execuție (care ar reseta starea de două ori).
   EN: coalesces concurrent auth entry points so onAuthenticated runs once. */
let _authPromise = null;
function runOnAuthenticated(session, fallbackName) {
    if (_authPromise) return _authPromise;
    if (state.currentUser && !state.currentUser.isDemo) return Promise.resolve();
    /* F1b: cât timp rulează prima pornire după login, celelalte file ale aceluiași cont o văd
       „ocupată" (localStorage) și o așteaptă, ca invitația, consimțământul și bun-venitul să
       ruleze o singură dată. */
    const _busyKey = 'to_auth_busy_' + ((session && session.user && session.user.id) || '');
    _authPromise = (async () => {
        try { localStorage.setItem(_busyKey, String(Date.now())); } catch (e) { logError('runOnAuthenticated', e); }
        try { await onAuthenticated(session, fallbackName); }
        finally {
            _authPromise = null;
            try { localStorage.removeItem(_busyKey); } catch (e) { logError('runOnAuthenticated', e); }
            try { sessionStorage.removeItem('to_auth_here'); } catch (e) { logError('runOnAuthenticated', e); }
        }
    })();
    return _authPromise;
}

/* ============================================================================
   F1b (3 oct. 2026): LOGAREA „DIN UMBRĂ" A ALTEI FILE
   RO: Când îți confirmi contul din linkul din email, linkul se deschide într-o FILĂ NOUĂ.
       Sesiunea ajunge automat și în fila în care ți-ai făcut contul, care se loga în
       paralel și rula a doua oară consimțământul, invitația și bun-venitul (defectul F1).
       Acum, o filă care primește o logare pe care N-A pornit-o ea așteaptă: întâi să
       redevină vizibilă, apoi ca fila care s-a logat să termine prima pornire. Abia apoi
       se loghează și ea, iar pașii unici (consimțământ, invitație) sunt deja făcuți.
   EN: a tab signed in by ANOTHER tab waits until it is visible and the other tab has
       finished its first-login steps, so they run once. */
function _markAuthHere() { try { sessionStorage.setItem('to_auth_here', '1'); } catch (e) { logError('_markAuthHere', e); } }
window._markAuthHere = _markAuthHere;
function _isShadowSignIn() {
    try { if (sessionStorage.getItem('to_auth_here') === '1') return false; } catch (e) { return false; }
    const h = window.location.hash || '', q = window.location.search || '';
    if (h.indexOf('access_token') !== -1 || q.indexOf('code=') !== -1) return false;   /* fila linkului: o tratează init() */
    return true;
}
async function _deferShadowSignIn(session) {
    _authTrace('signin:alta-fila', { ascunsa: document.visibilityState === 'hidden' });
    if (document.visibilityState === 'hidden') {
        await new Promise(function (res) {
            const on = function () { if (document.visibilityState !== 'hidden') { document.removeEventListener('visibilitychange', on); res(); } };
            document.addEventListener('visibilitychange', on);
        });
    }
    const key = 'to_auth_busy_' + ((session && session.user && session.user.id) || '');
    const t0 = Date.now();
    await new Promise(function (r) { setTimeout(r, 600); });
    while (Date.now() - t0 < 15000) {
        let b = 0;
        try { b = Number(localStorage.getItem(key) || 0); } catch (e) { b = 0; }
        if (!b || Date.now() - b > 20000) break;
        await new Promise(function (r) { setTimeout(r, 300); });
    }
    if (!(state.currentUser && !state.currentUser.isDemo)) runOnAuthenticated(session);
}

async function login() {
    /* ⚠ Batch 1b-fix v4: dacă deja așteptăm tokenul CAPTCHA (rețea lentă), a doua
       apăsare pe „Conectare" NU pornește un login paralel — doar reamintește că
       verificarea e în curs. Fără garda asta, un utilizator nerăbdător pe 3G ar
       declanșa două autentificări concurente peste aceeași sesiune.
       EN: guard against a second concurrent sign-in while we are still waiting for the
       CAPTCHA token on a slow network. */
    if (window._loginCaptchaWaiting) {
        showNotification((typeof t === 'function'
            ? t('login.captchaChecking')
            : 'Verificarea de securitate rulează... te conectăm imediat.'), 'info');
        return;
    }
    const identifier = document.getElementById('loginEmail').value.trim();
    const password = document.getElementById('loginPassword').value;
    if (!identifier || !password) { showNotification((typeof t === 'function' ? t('login.fillFields') : 'Completează datele de conectare și parola!'), 'error'); return; }
    /* Curăță un eventual flag OAuth rămas (ex. userul a anulat Google) ca să nu
       declanșeze reload la login-ul pe email. */
    try { sessionStorage.removeItem('to_oauth_pending'); } catch (e) { logError('login', e); }
    _markAuthHere();   /* F1b */
    const rm = document.getElementById('rememberMe');
    const remember = !(rm && !rm.checked);
    try { localStorage.setItem('gbgsc_remember', remember ? '1' : '0'); } catch (e) { logError('login', e); }
    showNotification((typeof t === 'function' ? t('login.connecting') : 'Se conectează...'), 'info');

    /* LOGIN HIBRID SECURIZAT (R3): trimitem identificatorul (email SAU @tag) + parola
       la Edge Function-ul `hybrid-login`. Rezolvarea tag→email și autentificarea se fac
       PE SERVER; emailul nu mai ajunge niciodată în browser pentru un tag, iar erorile
       sunt UNIFORME → fără user enumeration. Primim înapoi doar tokenii sesiunii. */
    let session = null;
    try {
        /* CAPTCHA (Turnstile): citim tokenul widget-ului și-l trimitem la server.
           Serverul verifică tokenul; dacă TURNSTILE_SECRET nu e setat, sare peste (rollout în etape).
           ⚡ PERF §54 (Batch 1b) — scriptul Turnstile NU mai vine din `index.html`, ci la
           cerere din `showLogin()`. Aici sunt DOUĂ plase de siguranță:
           (1) `ensureTurnstile()` acoperă drumurile care ajung la `login()` fără să fi
               trecut prin `showLogin()` (autocompletare de parolă, Enter foarte devreme);
               e memoizată, deci dacă a pornit deja din `showLogin()` nu reîncarcă nimic.
           (2) `turnstileToken()` AȘTEAPTĂ tokenul (max. 8 s) în loc să citească orbește.
               Fără ea, un utilizator rapid ar trimite un șir gol → `captcha_failed`,
               iar `TURNSTILE_SECRET` E setat pe `hybrid-login`, deci serverul chiar verifică.
               Repară și un defect preexistent: după o încercare eșuată, `turnstile.reset()`
               de mai jos golește tokenul, deci a doua apăsare imediată trimitea tot gol.
           Degradare grațioasă: dacă scriptul e blocat (adblocker), `turnstileToken` întoarce
           șir gol după 8 s, serverul răspunde `captcha_failed` și utilizatorul vede mesajul
           tradus de mai jos — exact ca înainte, fără cale de eroare nouă.
           EN: the Turnstile script is now lazy-loaded, so we (1) make sure it is requested
           even on paths that skip `showLogin()`, and (2) wait for the token instead of
           reading it blindly — which also fixes a pre-existing retry-after-reset bug. */
        if (typeof window.ensureTurnstile === 'function') {
            try { await window.ensureTurnstile(); } catch (e) { logError('login', e); }
        }
        /* ⚡ Batch 1b-fix v4 — feedback RAPID, dar FĂRĂ a-l pune pe om să apese din nou.
           RO: dacă după 2 secunde scutul tot lucrează, utilizatorul primește un mesaj
               informativ — dar așteptarea CONTINUĂ în fundal, iar în clipa în care
               tokenul sosește login-ul merge mai departe singur.
               ⚠ Mesajul e 'info', NU 'error': nimic n-a eșuat, doar durează.
           ⚠ RĂGAZUL E 60 s, nu 20 (defect real, 3G, aug. 2026): la 20 s apărea
               „Verificarea de securitate a eșuat", iar imediat DUPĂ aceea scutul afișa
               bifa verde — adică reușise, doar că eu renunțasem cu o clipă înainte.
               Un termen prea scurt transformă o rețea lentă într-o eroare inventată.
               Nimeni nu așteaptă 60 s degeaba: așteptarea se oprește instantaneu dacă
               widget-ul cere o bifă sau raportează o eroare reală.
           EN: 60 s, not 20 — on 3G the shield finished right AFTER we had declared
               failure, showing a green tick under an error message. The wait still bails
               out instantly on a tick request or a real error. */
        window._loginCaptchaWaiting = true;
        let captchaToken = '';
        try {
            captchaToken = (typeof window.turnstileToken === 'function')
                ? await window.turnstileToken(60000, function () {
                    try {
                        showNotification((typeof t === 'function'
                            ? t('login.captchaChecking')
                            : 'Verificarea de securitate rulează... te conectăm imediat.'), 'info');
                    } catch (e) { }
                }, 2000)
                : ((window.turnstile && typeof window.turnstile.getResponse === 'function')
                    ? (window.turnstile.getResponse() || '') : '');
        } finally {
            /* ⚠ `finally`: garda trebuie ridicată chiar dacă așteptarea aruncă,
               altfel butonul „Conectare" ar rămâne blocat până la reîncărcarea paginii. */
            window._loginCaptchaWaiting = false;
        }

        /* ⚠ Batch 1b-fix — NU mai trimitem o cerere condamnată.
           RO: `TURNSTILE_SECRET` e setat, deci serverul respinge sigur un token gol.
               Înainte plecam oricum la server și utilizatorul primea „Verificarea de
               securitate a eșuat" — mesaj care îl făcea să creadă că a greșit el ceva.
               Ajungem aici DOAR în două situații reale: widget-ul cere o bifă (și atunci
               mesajul apare instantaneu, fiindcă așteptarea se oprește din prima clipă),
               sau chiar a eșuat / a expirat răgazul.
           EN: the server is guaranteed to reject an empty token, so stop here with an
               accurate message instead of sending a doomed request. */
        if (!captchaToken) {
            const _why = (typeof window.turnstileMissingReason === 'function')
                ? window.turnstileMissingReason() : 'failed';
            const _key = (_why === 'tick') ? 'login.captchaPending' : 'login.captchaFailed';
            const _fallback = (_why === 'tick')
                ? 'Bifează caseta de verificare de mai jos, apoi apasă din nou Conectare.'
                : 'Verificarea de securitate a eșuat. Reîncearcă.';
            showNotification((typeof t === 'function' ? t(_key) : _fallback), 'error');
            return;
        }
        const resp = await fetch(SUPABASE_URL + '/functions/v1/hybrid-login', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'apikey': SUPABASE_ANON_KEY,
                'Authorization': 'Bearer ' + SUPABASE_ANON_KEY
            },
            body: JSON.stringify({ identifier, password, captchaToken })
        });
        const out = await resp.json().catch(() => null);
        /* Tokenul Turnstile e de unică folosință → îl resetăm pentru următoarea încercare.
           ⚠ Batch 1b-fix: prin `turnstileReset()`, care golește ȘI steagurile de stare
           (bifă cerută / eroare). Altfel un „cere bifă" rămas de la încercarea anterioară
           ar fi făcut ca următoarea apăsare să iasă instant cu mesajul greșit.
           EN: reset through `turnstileReset()` so the status flags are cleared too. */
        try {
            if (typeof window.turnstileReset === 'function') window.turnstileReset();
            else if (window.turnstile && typeof window.turnstile.reset === 'function') window.turnstile.reset();
        } catch (e) { logError('login', e); }
        if (resp.status === 400 && out && out.error === 'captcha_failed') {
            showNotification((typeof t === 'function' ? t('login.captchaFailed') : 'Verificarea de securitate a eșuat. Reîncearcă.'), 'error');
            return;
        }
        if (!resp.ok || !out || !out.access_token || !out.refresh_token) {
            showNotification((typeof t === 'function' ? t('login.failed') : 'Autentificare eșuată'), 'error');
            return;
        }
        const setRes = await sb.auth.setSession({ access_token: out.access_token, refresh_token: out.refresh_token });
        if (setRes.error || !setRes.data || !setRes.data.session) {
            showNotification((typeof t === 'function' ? t('login.failed') : 'Autentificare eșuată'), 'error');
            return;
        }
        session = setRes.data.session;
    } catch (e) {
        showNotification((typeof t === 'function' ? t('login.failed') : 'Autentificare eșuată') + ': ' + (e && e.message ? e.message : e), 'error');
        return;
    }

    try {
        if (remember) {
            /* Reținem identificatorul TASTAT pentru precompletare. Emailul îl
               acoperă browserul (autocomplete="email"), deci NU-l mai persistăm
               în localStorage (PII pe termen lung, semnalat la audit §24). Un
               @usertag nu e email → îl ținem doar în sessionStorage (dispare la
               închiderea browserului). */
            try { localStorage.removeItem('gbgsc_email'); } catch (_) {}
            try {
                if (identifier && identifier.charAt(0) === '@') sessionStorage.setItem('to_last_tag', identifier);
                else sessionStorage.removeItem('to_last_tag');
            } catch (_) {}
        } else {
            try { localStorage.removeItem('gbgsc_email'); } catch (_) {}
            try { sessionStorage.removeItem('to_last_tag'); } catch (_) {}
        }
        /* R1 (securitate): NU mai stocăm niciodată parola; ștergem reziduurile. */
        localStorage.removeItem('gbgsc_pass');
    } catch (e) { logError('login', e); }
    await runOnAuthenticated(session);
    showNotification(t('sb.welcomeName').replace('{name}', state.currentUser.name), 'success');
}

function checkPasswordRules(pw) {
    pw = pw || '';
    const r = {
        len: pw.length >= 8,
        upper: /[A-Z]/.test(pw),
        lower: /[a-z]/.test(pw),
        digit: /[0-9]/.test(pw),
        special: /[^A-Za-z0-9]/.test(pw)
    };
    r.metCount = ['len', 'upper', 'lower', 'digit', 'special'].filter(k => r[k]).length;
    r.allMet = r.metCount === 5;
    return r;
}

/* Checklist viu de criterii pentru ORICE câmp de parolă (reset, profil).
   boxId = containerul cu spans[data-req] și .pw-strength-bar; confId opțional. */
function pwChecksUpdate(pwId, confId, boxId) {
    var box = document.getElementById(boxId);
    var pwEl = document.getElementById(pwId);
    if (!box || !pwEl) return;
    var pw = pwEl.value || '';
    var conf = confId ? ((document.getElementById(confId) || {}).value || '') : '';
    box.style.display = pw ? 'block' : 'none';
    var r = checkPasswordRules(pw);
    var match = pw.length > 0 && pw === conf;
    var setReq = function (key, ok) {
        var el = box.querySelector('[data-req="' + key + '"]');
        if (!el) return;
        el.style.color = ok ? 'var(--success,#27ae60)' : 'var(--text-secondary,#888)';
        /* RO: era `far fa-circle`. Familia „regular" a Font Awesome apărea în tot proiectul
              DOAR aici și în geamănul de mai jos, dar obliga browserul să descarce
              `fa-regular-400.woff2` (~25 kB) la fiecare pornire. Markup-ul STATIC din
              index.html folosea deja `fas fa-circle` pentru aceeași stare „nebifat", deci
              schimbarea repară și o inconsecvență: bulina își schimba grosimea la prima tastare.
           EN: was `far fa-circle` — the only two uses of the FA "regular" family, costing a
              ~25 kB webfont on every startup. The static markup in index.html already used
              `fas fa-circle` for the same unchecked state, so this also fixes an inconsistency. */
        var ic = el.querySelector('i'); if (ic) ic.className = ok ? 'fas fa-check-circle' : 'fas fa-circle';
    };
    setReq('len', r.len); setReq('upper', r.upper); setReq('lower', r.lower);
    setReq('digit', r.digit); setReq('special', r.special); setReq('match', match);
    var bar = box.querySelector('.pw-strength-bar');
    if (bar) {
        var pct = Math.round((r.metCount / 5) * 100);
        bar.style.width = pct + '%';
        bar.style.background = r.metCount <= 2 ? '#b23b3b' : r.metCount <= 4 ? '#e6a700' : '#27ae60';
    }
}
function updatePwStrength() {
    const box = document.getElementById('pwStrengthBox');
    const pwEl = document.getElementById('regPassword');
    if (!box || !pwEl) return;
    const pw = pwEl.value || '';
    const conf = (document.getElementById('regPasswordConfirm') || {}).value || '';
    box.style.display = pw ? 'block' : 'none';
    const r = checkPasswordRules(pw);
    const match = pw.length > 0 && pw === conf;
    const setReq = (key, ok) => {
        const el = document.querySelector('#pwReqList [data-req="' + key + '"]');
        if (!el) return;
        el.style.color = ok ? 'var(--success,#27ae60)' : 'var(--text-secondary,#888)';
        /* RO: vezi motivul din `pwChecksUpdate` de mai sus — `far fa-circle` aducea
              `fa-regular-400.woff2` (~25 kB) pentru două bulinuțe.
           EN: see `pwChecksUpdate` above — this was the second and last use of the FA
              "regular" family, which cost a ~25 kB webfont download. */
        const ic = el.querySelector('i'); if (ic) ic.className = ok ? 'fas fa-check-circle' : 'fas fa-circle';
    };
    setReq('len', r.len); setReq('upper', r.upper); setReq('lower', r.lower);
    setReq('digit', r.digit); setReq('special', r.special); setReq('match', match);
    const bar = document.getElementById('pwStrengthBar');
    if (bar) {
        const pct = Math.round((r.metCount / 5) * 100);
        bar.style.width = pct + '%';
        bar.style.background = r.metCount <= 2 ? '#b23b3b' : r.metCount <= 4 ? '#e6a700' : '#27ae60';
    }
}

/* ── CONSIMȚĂMÂNT (vârstă ≥16 + Termeni/Confidențialitate) ──────────────────
   Ecranul de register e PRE-autentificare → NU se aplică can(cap)/FEATURE_MIN_PLAN.
   Autorizarea reală a scrierii e în RPC-ul `record_consent` (SECURITY DEFINER,
   identitate din auth.uid()). Aici e doar UX de client + persistarea DB-first. */

/* Validează cele două bife de pe #registerScreen. Pe eșec (dacă shakeOnFail):
   marchează roșu DOAR bifele netrecute + shake + arată mesajul de eroare. */
function _registerConsentOk(shakeOnFail) {
    var terms = document.getElementById('regAcceptTerms');
    var age = document.getElementById('regAge16');
    var err = document.getElementById('regConsentError');
    var termsOk = !!(terms && terms.checked);
    var ageOk = !!(age && age.checked);
    function markLabel(cb, ok) {
        var l = cb && cb.closest ? cb.closest('label.legal-consent') : null;
        if (!l) return;
        if (ok) { l.classList.remove('consent-error-on', 'shake'); return; }
        l.classList.remove('shake'); void l.offsetWidth; /* reflow → repornește animația la fiecare click */
        l.classList.add('consent-error-on', 'shake');
    }
    if (termsOk && ageOk) {
        markLabel(terms, true); markLabel(age, true);
        if (err) err.classList.add('hidden');
        return true;
    }
    if (shakeOnFail) {
        markLabel(age, ageOk); markLabel(terms, termsOk);
        if (err) err.classList.remove('hidden');
    }
    return false;
}

/* Persistă consimțământul DOAR prin RPC (DB-first). Întoarce true la succes. */
async function _recordConsentDbFirst() {
    try {
        const r = await sb.rpc('record_consent');
        if (r && r.error) { logError('record_consent', r.error); return false; }
        return true;
    } catch (e) { logError('record_consent', e); return false; }
}

/* Gardă post-login: asigură consimțământul ÎNAINTE de a intra în aplicație.
   (1) dacă tocmai a bifat pe register (email/Google) → persistă și continuă;
   (2) dacă profilul are deja ambele timestamps → continuă;
   (3) altfel (bypass OAuth de pe Sign In / cont vechi) → modal BLOCANT.
   Întoarce false DOAR dacă userul refuză modalul → apelantul îl deloghează. */
async function _enforceConsentGate(prof) {
    var pending = false;
    try { pending = sessionStorage.getItem('to_consent_pending') === '1'; } catch (e) { /* ignore */ }
    if (pending) {
        try { sessionStorage.removeItem('to_consent_pending'); } catch (e) { /* ignore */ }
        if (await _recordConsentDbFirst()) {
            prof.consent_terms_at = prof.consent_terms_at || new Date().toISOString();
            prof.consent_age16_at = prof.consent_age16_at || new Date().toISOString();
            return true;
        }
        /* scrierea a eșuat → cădem în modalul blocant de mai jos */
    }
    if (prof.consent_terms_at && prof.consent_age16_at) return true;

    /* ★ T5-fix: dovada bifelor de la Sign-Up (fluxul cu confirmare pe email) —
       supraviețuiește în localStorage. O consumăm o singură dată, tăcut. */
    try {
        var _em = ((state.currentUser && state.currentUser.email) || '').trim().toLowerCase();
        if (_em) {
            var _ck = 'to_consent_signup_' + _em;
            var _cts = Number(localStorage.getItem(_ck) || 0);
            if (_cts && (Date.now() - _cts) < 7 * 24 * 3600 * 1000) {
                localStorage.removeItem(_ck);
                if (await _recordConsentDbFirst()) {
                    prof.consent_terms_at = prof.consent_terms_at || new Date().toISOString();
                    prof.consent_age16_at = prof.consent_age16_at || new Date().toISOString();
                    return true;
                }
            } else if (_cts) { localStorage.removeItem(_ck); }
        }
    } catch (e) { logError('_enforceConsentGate', e); }

    /* F1b: altă filă (ex. cea a linkului de confirmare) poate să-l fi înregistrat chiar acum.
       Îl recitim din baza de date înainte să-l cerem din nou. */
    try {
        const _uid = state.currentUser && state.currentUser.id;
        if (_uid && sb) {
            const _r = await sb.from('profiles').select('consent_terms_at, consent_age16_at').eq('id', _uid).maybeSingle();
            if (_r && _r.data && _r.data.consent_terms_at && _r.data.consent_age16_at) {
                prof.consent_terms_at = _r.data.consent_terms_at;
                prof.consent_age16_at = _r.data.consent_age16_at;
                return true;
            }
        }
    } catch (e) { logError('_enforceConsentGate', e); }
    const accepted = await showConsentGateModal();
    if (!accepted) return false;
    if (!(await _recordConsentDbFirst())) { showNotification(t('sb.consentSaveError'), 'error'); return false; }
    prof.consent_terms_at = new Date().toISOString();
    prof.consent_age16_at = new Date().toISOString();
    return true;
}

/* Modal blocant de consimțământ (§17 unificat). Promise<boolean>. */
function showConsentGateModal() {
    return new Promise(function (resolve) {
        /* ⛔ OBLIGATORIU: acest modal e BLOCANT și e afișat în timpul pornirii,
           din `onAuthenticated` → `_enforceConsentGate`, ÎNAINTE de orice ecran.
           `.modal` are z-index 1000, `#loader` are 9999 (style.css) — dacă
           loaderul ar rămâne deasupra, utilizatorul n-ar putea bifa nimic, iar
           `onAuthenticated` ar aștepta la infinit: blocaj total, fără ieșire.
           EN: this blocking startup modal renders BELOW the loader, so the loader
           must be dismissed before it is shown. */
        try { if (typeof hideLoader === 'function') hideLoader(); } catch (e) { logError('showConsentGateModal', e); }
        var m = document.getElementById('consentGateModal');
        if (!m) { resolve(false); return; }
        var age = document.getElementById('cgAge16');
        var terms = document.getElementById('cgTerms');
        var err = document.getElementById('cgError');
        var accept = document.getElementById('cgAccept');
        var cancel = document.getElementById('cgCancel');
        if (age) age.checked = false;
        if (terms) terms.checked = false;
        if (err) err.classList.add('hidden');
        [age, terms].forEach(function (cb) {
            var l = cb && cb.closest ? cb.closest('label.legal-consent') : null;
            if (l) l.classList.remove('consent-error-on', 'shake');
        });
        function cleanup(val) {
            document.removeEventListener('keydown', onKey, true);
            m.classList.remove('active');
            if (accept) accept.onclick = null;
            if (cancel) cancel.onclick = null;
            resolve(val);
        }
        /* Escape = „Renunț" (blocant): interceptăm pe faza de CAPTURE, ÎNAINTEA
           handlerului global closeAllModals din app-core → modalul nu poate fi
           „ascuns pe lângă" lăsând onAuthenticated în așteptare. */
        function onKey(e) {
            if (e.key === 'Escape') {
                e.preventDefault(); e.stopPropagation();
                if (e.stopImmediatePropagation) e.stopImmediatePropagation();
                cleanup(false);
            }
        }
        document.addEventListener('keydown', onKey, true);
        if (accept) accept.onclick = function () {
            var ageOk = !!(age && age.checked);
            var termsOk = !!(terms && terms.checked);
            if (ageOk && termsOk) { cleanup(true); return; }
            [[age, ageOk], [terms, termsOk]].forEach(function (p) {
                var l = p[0] && p[0].closest ? p[0].closest('label.legal-consent') : null;
                if (!l) return;
                if (p[1]) { l.classList.remove('consent-error-on', 'shake'); return; }
                l.classList.remove('shake'); void l.offsetWidth;
                l.classList.add('consent-error-on', 'shake');
            });
            if (err) err.classList.remove('hidden');
        };
        if (cancel) cancel.onclick = function () { cleanup(false); };
        m.classList.add('active');
        try { if (typeof window.applyTranslations === 'function') window.applyTranslations(m); } catch (e) { /* ignore */ }
    });
}

async function register() {
    const name = document.getElementById('regName').value.trim();
    const email = document.getElementById('regEmail').value.trim();
    const password = document.getElementById('regPassword').value;
    const confirmPassword = document.getElementById('regPasswordConfirm').value;
    const acceptTerms = document.getElementById('regAcceptTerms').checked;

    if (!name || !email || !password || !confirmPassword) { showNotification(t('sb.fillAllFields'), 'error'); return; }
    const pw = checkPasswordRules(password);
    if (!pw.allMet) { showNotification(t('sb.weakPassword'), 'error'); return; }
    if (password !== confirmPassword) { showNotification(t('sb.passMismatch'), 'error'); return; }
    /* Gardă de consimțământ UNIFICATĂ (vârstă ≥16 + Termeni/Confidențialitate).
       Aceeași funcție e folosită și de gate-ul Google de pe register, ca fluxul
       clasic pe email să nu ocolească bifa nouă. Pe eșec → shake + mesaj roșu. */
    if (!_registerConsentOk(true)) { showNotification(t('register.consentError'), 'error'); return; }

    showNotification(t('sb.creatingAccount'), 'info');
    _markAuthHere();   /* F1b */
    const { data, error } = await sb.auth.signUp({ email, password, options: { data: { name }, emailRedirectTo: paAuthRedirectUrl() } });
    if (error) { showNotification(t('sb.registerErrorColon') + error.message, 'error'); return; }

    /* Profilul NU se mai creează manual aici. Se ocupă `ensureProfile`
       (apelat din `onAuthenticated`), care fie creează un profil nou, fie
       „adoptă" unul existent cu același email — așa evităm dublurile. */

    if (data.session) {
        /* Consimțământul a fost bifat și validat mai sus → îl marcăm pentru
           persistare în DB. Garda din onAuthenticated apelează `record_consent`
           (DB-first) și, dacă din orice motiv nu s-a scris, afișează modalul blocant. */
        try { sessionStorage.setItem('to_consent_pending', '1'); } catch (e) { logError('register', e); }
        await runOnAuthenticated(data.session, name);
        showNotification(t('sb.accountCreatedWelcome').replace('{name}', name), 'success');
        /* ★ T5: modalul de bun venit se declanșează acum central, în onAuthenticated
           (flag _freshProfileCreated) — un singur drum pentru toate fluxurile. */
    } else {
        /* ★ T5-fix: bifele de consimțământ au fost validate MAI SUS, dar userul
           pleacă acum spre emailul de confirmare — sessionStorage nu supraviețuiește
           tab-ului nou. Persistăm dovada în localStorage (cheiată pe email, 7 zile);
           garda o consumă la primul login și scrie consimțământul prin ACELAȘI
           RPC record_consent (§28 neatins). Pe alt dispozitiv → modal blocant (fallback). */
        try { localStorage.setItem('to_consent_signup_' + String(email || '').trim().toLowerCase(), String(Date.now())); } catch (e) { logError('register', e); }
        showNotification(t('sb.accountCreatedVerify'), 'success');
        if (typeof showConfirmEmail === 'function') showConfirmEmail(email);
        else showLogin();
    }
}

/* F1b (3 oct. 2026): confirmarea contului cu CODUL din email, în ACEEAȘI filă.
   RO: Linkul din email se deschide mereu într-o filă nouă (sau, pe telefon, în alt browser decât
       aplicația instalată). Cu codul, totul rămâne în fila în care ți-ai făcut contul: invitația,
       consimțământul și clubul. Linkul rămâne în email ca rezervă.
   EN: confirm sign-up with the e-mailed code in the same tab; the link stays as a fallback. */
/* F1c: cele 6 căsuțe ale codului (ca la 2FA). Idempotent: se leagă o singură dată.
   - o cifră pe căsuță, avans automat; Backspace pe o căsuță goală întoarce la cea anterioară;
   - lipirea sau completarea automată (iPhone/Android) a întregului cod îl împarte pe căsuțe;
   - butonul „Confirmă" se activează la 6 cifre, iar codul complet se trimite singur. */
/* F1d: lungimea codului e o setare a serverului (Supabase: Email OTP Length, 6–10). Ecranul
   pornește cu OTP_LEN căsuțe; dacă lipești sau completezi automat un cod mai lung, căsuțele se
   înmulțesc singure. OTP_LEN trebuie să fie egal cu setarea din Supabase (recomandat: 6). */
const OTP_LEN = 6;
function _otpBoxes() { return Array.prototype.slice.call(document.querySelectorAll('#confirmOtpBoxes .otp-box')); }
function _otpEnsureLen(n) {
    n = Math.max(OTP_LEN, Math.min(10, n));
    const g = document.getElementById('confirmOtpBoxes'); if (!g) return;
    let boxes = _otpBoxes();
    if (boxes.length >= n) return;
    const dash = g.querySelector('.otp-dash'); if (dash) dash.remove();
    while (boxes.length < n) {
        const b = document.createElement('input');
        b.className = 'otp-box'; b.type = 'text'; b.setAttribute('inputmode', 'numeric'); b.setAttribute('autocomplete', 'off'); b.maxLength = 10;
        g.appendChild(b); boxes = _otpBoxes();
    }
    g.classList.add('otp-long');
    setupOtpBoxes();
}
function _otpCode() { return _otpBoxes().map(function (b) { return (b.value || '').replace(/\D/g, '').slice(0, 1); }).join(''); }
function _otpShowError(on) {
    const e = document.getElementById('confirmOtpError'); if (e) e.classList.toggle('hidden', !on);
    const g = document.getElementById('confirmOtpBoxes');
    if (g) { g.classList.remove('otp-shake'); if (on) { void g.offsetWidth; g.classList.add('otp-shake'); } g.classList.toggle('otp-has-error', !!on); }
}
function _otpSync() {
    const btn = document.getElementById('confirmOtpBtn');
    const full = _otpCode().length === _otpBoxes().length;
    if (btn && !btn.classList.contains('is-busy')) btn.disabled = !full;
    return full;
}
function _otpFill(from, digits) {
    const boxes = _otpBoxes();
    let i = from;
    for (const d of digits) { if (i >= boxes.length) break; boxes[i].value = d; i++; }
    const next = boxes[Math.min(i, boxes.length - 1)]; if (next) next.focus();
    if (_otpSync()) verifySignupCode();
}
function resetOtpBoxes() {
    _otpBoxes().slice(OTP_LEN).forEach(function (b) { b.remove(); });
    const _g = document.getElementById('confirmOtpBoxes'); if (_g) _g.classList.remove('otp-long');
    const boxes = _otpBoxes();
    boxes.forEach(function (b) { b.value = ''; });
    _otpShowError(false); _otpSync();
    try { if (boxes[0]) boxes[0].focus(); } catch (e) { logError('resetOtpBoxes', e); }
}
window.resetOtpBoxes = resetOtpBoxes;
function setupOtpBoxes() {
    const boxes = _otpBoxes();
    boxes.forEach(function (b, i) {
        try { b.setAttribute('aria-label', t('confirm.digitLabel').replace('{n}', String(i + 1))); } catch (e) { logError('setupOtpBoxes', e); }
        if (b.__otpBound) return;
        b.__otpBound = true;
        b.addEventListener('input', function () {
            const digits = (b.value || '').replace(/\D/g, '');
            _otpShowError(false);
            if (digits.length > 1) { b.value = ''; if (i === 0 && digits.length > boxes.length) _otpEnsureLen(digits.length); _otpFill(i, digits.split('')); return; }
            b.value = digits;
            const _all = _otpBoxes(); if (digits && i < _all.length - 1) _all[i + 1].focus();
            if (_otpSync()) verifySignupCode();
        });
        b.addEventListener('keydown', function (ev) {
            const _all = _otpBoxes(); const _i = _all.indexOf(b);
            if (ev.key === 'Backspace' && !b.value && _i > 0) { _all[_i - 1].value = ''; _all[_i - 1].focus(); _otpSync(); ev.preventDefault(); }
            else if (ev.key === 'ArrowLeft' && _i > 0) { _all[_i - 1].focus(); ev.preventDefault(); }
            else if (ev.key === 'ArrowRight' && _i < _all.length - 1) { _all[_i + 1].focus(); ev.preventDefault(); }
            else if (ev.key === 'Enter') { if (_otpSync()) verifySignupCode(); }
        });
        b.addEventListener('paste', function (ev) {
            const txt = ((ev.clipboardData || window.clipboardData) && (ev.clipboardData || window.clipboardData).getData('text')) || '';
            const digits = txt.replace(/\D/g, '');
            if (digits) { ev.preventDefault(); _otpShowError(false); if (digits.length > _otpBoxes().length) _otpEnsureLen(digits.length); _otpFill(0, digits.slice(0, 10).split('')); }
        });
        b.addEventListener('focus', function () { try { b.select(); } catch (e) { /* noop */ } });
    });
}
window.setupOtpBoxes = setupOtpBoxes;

async function verifySignupCode() {
    const email = (typeof window !== 'undefined' && window._pendingConfirmEmail) || '';
    const code = _otpCode();
    if (!email) { showNotification(t('sb.unknownAddress'), 'error'); return; }
    if (code.length < 6 || code.length !== _otpBoxes().length) { _otpShowError(true); return; }
    const btn = document.getElementById('confirmOtpBtn');
    if (btn && btn.classList.contains('is-busy')) return;
    const label = btn && btn.querySelector('span');
    if (btn) { btn.classList.add('is-busy'); btn.disabled = true; }
    if (label) label.textContent = t('confirm.codeChecking');
    const done = function () { if (btn) btn.classList.remove('is-busy'); if (label) label.textContent = t('confirm.codeBtn'); _otpSync(); };
    try {
        _markAuthHere();
        let r = await sb.auth.verifyOtp({ email: email, token: code, type: 'email' });
        if (r && r.error) r = await sb.auth.verifyOtp({ email: email, token: code, type: 'signup' });
        const session = r && r.data && r.data.session;
        if (!session) {
            _authTrace('otp:respins', { cifre: code.length, mesaj: (r && r.error && r.error.message) || '' });
            done();
            _otpBoxes().forEach(function (b) { b.value = ''; });
            _otpShowError(true); _otpSync();
            const first = _otpBoxes()[0]; if (first) first.focus();
            return;
        }
        await runOnAuthenticated(session);
        done();
    } catch (e) {
        logError('verifySignupCode', e);
        done(); _otpShowError(true);
    }
}
window.verifySignupCode = verifySignupCode;

/* Retrimite emailul de confirmare pentru adresa tocmai înregistrată.
   Sigur din punct de vedere al securității: e apelat doar de pe ecranul afișat
   imediat după înregistrare (userul știe că adresa e a lui), nu la login. */
async function resendConfirmation() {
    var email = (typeof window !== 'undefined' && window._pendingConfirmEmail) || '';
    if (!email) { showNotification(t('sb.unknownAddress'), 'error'); return; }
    if (!sb) { showNotification(t('sb.connUnavailable'), 'error'); return; }
    var btn = document.getElementById('resendConfirmBtn');
    if (btn && btn.disabled) return; /* deja în pauză */
    if (btn) btn.disabled = true;
    try {
        const { error } = await sb.auth.resend({
            type: 'signup',
            email: email,
            options: { emailRedirectTo: paAuthRedirectUrl() }
        });
        if (error) {
            /* F1d: serverul cere o pauză între emailuri („...after N seconds"). O arătăm ca
               numărătoare pe buton, nu ca eroare de securitate. */
            const _m = /(\d+)\s*second/i.exec(error.message || '');
            if (_m) { if (btn) startResendCountdown(btn, Number(_m[1])); showNotification(t('confirm.resendWait').replace('{n}', _m[1]), 'info'); return; }
            showNotification(t('sb.resendErrorColon') + error.message, 'error');
            if (btn) btn.disabled = false;
            return;
        }
        showNotification(t('sb.confirmResent'), 'success');
        if (typeof resetOtpBoxes === 'function') resetOtpBoxes();
        if (btn) startResendCountdown(btn, RESEND_WAIT_S);
    } catch (e) {
        showNotification(t('sb.resendError'), 'error');
        if (btn) btn.disabled = false;
    }
}

/* F1d: pauza minimă dintre două emailuri de confirmare (Supabase: Email „max frequency", implicit 60 s). */
const RESEND_WAIT_S = 60;
window.RESEND_WAIT_S = RESEND_WAIT_S;
/* Countdown vizibil pe butonul de retrimitere (anti-spam, rate limit Supabase). */
function startResendCountdown(btn, seconds) {
    /* F1d: o singură numărătoare pe buton (pornirea unei noi o oprește pe cea veche). */
    if (btn.__cdTimer) { clearInterval(btn.__cdTimer); btn.__cdTimer = null; }
    var label = btn.querySelector('span');
    var remaining = seconds;
    btn.disabled = true;
    function tick() {
        if (label) {
            var word = (typeof t === 'function') ? t('confirm.resendIn') : 'Resend in';
            label.textContent = word + ' ' + remaining + 's';
        }
        if (remaining <= 0) {
            clearInterval(timer);
            btn.disabled = false;
            if (label) label.textContent = (typeof t === 'function') ? t('confirm.resend') : 'Resend email';
            return;
        }
        remaining--;
    }
    tick();
    var timer = setInterval(tick, 1000);
    btn.__cdTimer = timer;
}
window.startResendCountdown = startResendCountdown;

/* ── Autentificare cu Google (OAuth) ────────────────────────────────────────
   RO: pornește fluxul OAuth Google. La revenire pe site, `init()` prinde
       sesiunea prin getSession()/detectSessionInUrl, iar `ensureProfile`
       (din onAuthenticated) creează profilul cu nivel Membru dacă e prima
       logare. NU trece prin hybrid-login/CAPTCHA (Google are protecția proprie).
   EN: starts the Google OAuth flow. On return, init() picks up the session and
       ensureProfile bootstraps a Member-level profile on first sign-in. */
async function loginWithGoogle() {
    if (!sb) { showNotification(t('sb.connUnavailable'), 'error'); return; }
    /* Gate de consimțământ DOAR pe formularul de Sign-Up (când #registerScreen e vizibil).
       Pe Sign In ecranul e ascuns → condiția e falsă → fluxul rămâne 100% neschimbat.
       Dacă bifele nu sunt trecute → oprim redirectul OAuth și declanșăm shake + eroare.
       Dacă sunt trecute → marcăm consimțământul pentru persistare post-login (record_consent). */
    try {
        var _regScr = document.getElementById('registerScreen');
        var _onRegister = _regScr && !_regScr.classList.contains('hidden');
        if (_onRegister) {
            if (!_registerConsentOk(true)) return;
            try { sessionStorage.setItem('to_consent_pending', '1'); } catch (e) { logError('loginWithGoogle', e); }
        }
    } catch (e) { logError('loginWithGoogle', e); }
    try {
        /* Respectă bifa „Ține-mă minte": o salvăm ÎNAINTE de redirect, ca la
           login-ul clasic. La revenire, init() aplică aceeași logică (dacă e
           debifat și e filă nouă, sesiunea nu se reia automat). */
        const rm = document.getElementById('rememberMe');
        const remember = !(rm && !rm.checked);
        try { localStorage.setItem('gbgsc_remember', remember ? '1' : '0'); } catch (e) { logError('loginWithGoogle', e); }
        /* Marcăm că urmează un retur OAuth, ca să reîncărcăm curat la întoarcere
           (sesiune caldă, date complete). Vezi handlerul SIGNED_IN din init(). */
        try { sessionStorage.setItem('to_oauth_pending', '1'); } catch (e) { logError('loginWithGoogle', e); }
        _markAuthHere();   /* F1b */
        /* ⚠ ȘI `gbgsc_tab`, ÎNAINTE de redirect. Altfel, la retur, `init()` vedea
           `!remembered && !sameTab` și făcea `signOut()` — te deloga fix după ce
           tocmai te autentificaseși cu Google. `sessionStorage` supraviețuiește
           redirect-ului în același tab, deci e locul potrivit. */
        try { sessionStorage.setItem('gbgsc_tab', '1'); } catch (e) { logError('loginWithGoogle', e); }
        _authTrace('google:redirect', { remember: remember });
        showNotification(t('sb.redirectGoogle'), 'info');
        const { error } = await sb.auth.signInWithOAuth({
            provider: 'google',
            options: { redirectTo: paAuthRedirectUrl() }
        });
        if (error) { showNotification(t('sb.googleErrorColon') + error.message, 'error'); }
    } catch (e) {
        showNotification(t('sb.googleConnError'), 'error');
    }
}

/* ============================================================
   DIAGNOSTIC LOGIN — jurnal SILENȚIOS care supraviețuiește reload-ului.
   Cauza rădăcină a fost găsită (vezi `gbgsc_tab` în loginWithGoogle): la returul
   OAuth, `init()` executa `signOut()` pentru că „ține-mă minte" era debifat și
   `gbgsc_tab` nu era încă setat — deci te deconecta FIX după autentificare.
   Bug-ul era intermitent (cursă între `init()` și evenimentul SIGNED_IN), deci
   păstrăm jurnalul ca plasă de siguranță: NU scrie în consolă, dar dacă loginul
   mai dă rateuri, rulezi `authTrace()` și avem imediat traseul complet.
   ============================================================ */
function _authTrace(step, data) {
    try {
        const arr = JSON.parse(sessionStorage.getItem('to_auth_trace') || '[]');
        arr.push({ t: new Date().toISOString().substr(11, 12), step: step, data: data || null });
        if (arr.length > 60) arr.splice(0, arr.length - 60);
        sessionStorage.setItem('to_auth_trace', JSON.stringify(arr));
    } catch (e) { /* ignoră */ }
    /* Silențios by default. `authTraceVerbose(true)` dacă vrei log live în consolă. */
    if (window.__authTraceVerbose) { try { console.log('[auth]', step, data || ''); } catch (e) { /* ignoră */ } }
}
window._authTrace = _authTrace;

/* Rulează `authTrace()` în consolă dacă loginul mai dă rateuri. */
window.authTrace = function () {
    let arr = [];
    try { arr = JSON.parse(sessionStorage.getItem('to_auth_trace') || '[]'); } catch (e) { /* ignoră */ }
    console.table(arr);
    return arr;
};
window.authTraceClear = function () {
    try { sessionStorage.removeItem('to_auth_trace'); } catch (e) { /* ignoră */ }
};
window.authTraceVerbose = function (on) { window.__authTraceVerbose = !!on; };

/* Reîncarcă pagina DOAR după ce sesiunea OAuth e confirmată ca persistată.
   Fără asta, `location.replace()` putea porni înaintea scrierii în localStorage,
   iar la reload `init()` nu găsea nicio sesiune -> ecran de login (de unde
   „loginul cu Google pică o dată/de două ori"). */
async function _oauthReloadWhenPersisted() {
    if (window.__oauthReloading) { _authTrace('oauth:reload:deja-in-curs'); return; }
    window.__oauthReloading = true;
    let ok = false;
    for (let i = 0; i < 20; i++) {                 /* ~3s, în pași de 150ms */
        try {
            const r = await sb.auth.getSession();
            if (r && r.data && r.data.session) { ok = true; _authTrace('oauth:sesiune-persistata', { dupaMs: i * 150 }); break; }
        } catch (e) { /* reîncearcă */ }
        await new Promise(function (res) { setTimeout(res, 150); });
    }
    if (!ok) _authTrace('oauth:sesiune-NEPERSISTATA-dupa-3s');
    try { sessionStorage.setItem('gbgsc_tab', '1'); } catch (e) { logError('oauth', e); }
    _authTrace('oauth:reload', { spre: window.location.origin });
    window.location.replace(window.location.origin);
}
window._oauthReloadWhenPersisted = _oauthReloadWhenPersisted;

function resetAppState() {
    /* Curățenie completă la logout/login, ca să nu rămână date din contul anterior. */
    /* Bannerul de club (read-only / mute): îl scoatem din DOM. Altfel rămânea vizibil
       pe ecranul de login, raportând starea contului ANTERIOR. */
    try { if (typeof clearClubReadonlyBanner === 'function') clearClubReadonlyBanner(); } catch (e) { logError('resetAppState', e); }
    try { isMuted = false; } catch (e) { logError('resetAppState', e); }
    try { if (messagesChannel) sb.removeChannel(messagesChannel); } catch (e) { logError('resetAppState', e); } messagesChannel = null;
    try { if (presenceChannel) sb.removeChannel(presenceChannel); } catch (e) { logError('resetAppState', e); } presenceChannel = null;
    try { if (dataChannel) sb.removeChannel(dataChannel); } catch (e) { logError('resetAppState', e); } dataChannel = null;
    try { if (typeof timerChannel !== 'undefined' && timerChannel) sb.removeChannel(timerChannel); } catch (e) { logError('resetAppState', e); } try { timerChannel = null; } catch (e) { logError('resetAppState', e); }
    try { if (liveChannel) sb.removeChannel(liveChannel); } catch (e) { logError('resetAppState', e); } liveChannel = null;
    try { stopLiveHeartbeat(); } catch (e) { logError('resetAppState', e); }
    state.liveSessions = [];
    if (typeof realtimeRefreshTimer !== 'undefined' && realtimeRefreshTimer) { clearTimeout(realtimeRefreshTimer); realtimeRefreshTimer = null; }
    try { if (window.TO_CAMP && typeof TO_CAMP._rtCleanup === 'function') TO_CAMP._rtCleanup(); } catch (e) { logError('resetAppState', e); }
    try { resetLiveTimer(); } catch (e) { logError('resetAppState', e); }

    state.currentUser = null; isAdmin = false;
    try { if (typeof clearUserForLang === 'function') clearUserForLang(); } catch (e) { logError('resetAppState', e); }
    state.users = {}; state.members = {}; state.membersById = {}; state.profileNamesById = {}; state.catalog = {};
    state.games = []; state.events = []; state.chatMessages = [];
    state.polls = [];
    /* ⚠ state.audit NU se golește aici: jurnalul de audit e persistent per browser
       (localStorage) și filtrat pe club la randare. Golirea lui la login făcea ca
       primul saveState() de după să SUPRASCRIE definitiv istoricul cu un array gol. */
    state.notifications = []; state.seasons = [];
    state.auditDb = null; state._auditDbLoadedFor = null; /* cache-ul DB al jurnalului se reîncarcă la nevoie */
    state.scoresheets = {}; state.gameRoles = {}; state.campaigns = []; state.scoringTemplates = null;
    state.regulation = { version: '1.0', lastModified: new Date().toISOString(), articles: [] };
    state.eloEnabled = false; state.gameVote = null;
    state.myClubs = []; currentClubId = null;
    state.seasonLabel = '';
    state.allNotifications = []; state.notifLoaded = false;
    /* H20 (v9): mesajele private ale contului anterior ramaneau in memorie (social.js).
       Garda „DM-uri incarcate pentru X" se reseteaza odata cu golirea, ca `notifLoaded`
       mai sus: altfel acelasi om, reintrat dupa o sesiune expirata, ramanea fara mesaje.
       EN: clear DM memory; reset the loaded-for guard together with the data. */
    try { if (typeof window.dmResetState === 'function') window.dmResetState(); } catch (e) { logError('resetAppState', e); }
    _crossClubLoadedFor = null;
    /* H29 (v9): doua cache-uri DOAR din memorie pastrau continut al contului anterior:
       adresele semnate ale pozelor (inclusiv din DM; „chei" valabile pana la 60 min, §66.7)
       si ultimele texte de notificari (pot contine text din DM). Pe toata sesiunea raman
       neatinse (de aici viteza pozelor); se golesc DOAR la schimbarea contului.
       EN: per-session caches, emptied on account change only. */
    _spCache = Object.create(null);
    window._recentNotifText = {};

    /* Golește DOM-ul sensibil (ca să nu mai pâlpâie date vechi înainte de re-randare) */
    ['clubHubList', 'myProfileContent', 'historyList', 'clubAchievements', 'campaignsList',
     'regulationContent', 'statsContent', 'sessionPlayers', 'chatMessages', 'membersList',
     'leaderboard', 'eloLeaderboard', 'catalogGrid', 'notifList'].forEach(function (id) {
        var el = document.getElementById(id); if (el) el.innerHTML = '';
    });
    ['mainApp', 'clubHub', 'clubSwitcher', 'notifBell', 'currentUserDisplay'].forEach(function (id) {
        var el = document.getElementById(id); if (el) el.classList.add('hidden');
    });
    try { if (typeof _detachMainApp === 'function') _detachMainApp(); } catch (e) { logError('resetAppState', e); }
}

async function logout() {
    if (typeof window.t === 'function' && !await confirmModal(window.t('logout.confirm'))) return;
    try { if (typeof notifPollStop === 'function') notifPollStop(); } catch (e) { logError('logout', e); }
    /* Jurnalul mesajelor e strict personal si strict pe sesiune: nu ramane pentru
       urmatorul om care se conecteaza pe acelasi dispozitiv. */
    try { if (typeof toastLogClear === 'function') toastLogClear(true); } catch (e) { logError('logout', e); }
    /* Garda de idempotență a datelor cross-club se resetează la schimbarea de
       utilizator, altfel următorul cont care intră fără club n-ar mai încărca. */
    try { if (typeof window.resetCrossClubGuard === 'function') window.resetCrossClubGuard(); } catch (e) { logError('logout', e); }
    try { if (typeof unsubscribeRealtimeSocial === 'function') unsubscribeRealtimeSocial(); } catch (e) { logError('logout', e); }
    try { await _logoutPushCleanup(); } catch (e) { logError('logout', e); }
    try { if (!isDemoMode()) await sb.auth.signOut(); } catch (e) { logError('logout', e); }
    /* Oprește auto-login-ul și șterge parola salvată (păstrăm email-ul pentru precompletare). */
    try {
        localStorage.setItem('gbgsc_remember', '0');
        localStorage.removeItem('gbgsc_pass');
        sessionStorage.removeItem('gbgsc_tab');
        /* v9 8.5-D2: harta coperților mici (covers.js) — date publice BGG, dar din ea se vede
           ce jocuri are clubul ⇒ nu rămâne pe dispozitiv după delogare. */
        if (window.TO_COVER) window.TO_COVER.clear();
    } catch (e) { logError('logout', e); }
    resetAppState();
    showWelcomeScreen();
    showNotification(t('sb.loggedOut'), 'success');
    /* Plasă de siguranță: dacă ceva de mai sus a eșuat, jurnalul tot nu are voie
       să supraviețuiască deconectării. */
    try { if (typeof toastLogClear === 'function') toastLogClear(true); } catch (e) { logError('logout', e); }
}

function showDemo() {
    /* Demo MEREU cu date fictive: golim starea reală (dacă fusese hidratată din
       Supabase), ca garda din initDemoData să încarce datele demo. */
    state.users = {}; state.members = {}; state.membersById = {}; state.profileNamesById = {}; state.catalog = {}; state.games = [];
    state.events = []; state.chatMessages = [];
    state.polls = []; state.notifications = [];
    /* ⚠ state.audit NU se golește: jurnalul persistă; intrările reale au clubId
       real și nu apar în demo (renderAuditLog filtrează pe clubul curent). */
    state.eloEnabled = false; state.gameVote = null;
    initDemoData();
    seedDemoSessions();
    state.currentUser = { name: "Demo User", email: "demo@gbgsc.ro", role: "admin", isAdmin: true, isDemo: true };
    isAdmin = true;
    showMainApp();
    /* 7.4: bara care spune că ești în demo și oferă calea spre un cont real. */
    try { var _db = document.getElementById('demoBar'); if (_db) _db.classList.remove('hidden'); } catch (e) { logError('showDemo', e); }
    showNotification(t('sb.demoModeData'), 'info');
}

/* 7.4 (3 oct. 2026): butonul din bara de demo. Iese din demo (starea fictivă se golește, fără
   nicio scriere) și deschide direct formularul de înregistrare.
   EN: leave the demo (fictional state cleared, nothing written) and open sign-up. */
function demoCreateAccount() {
    try { resetAppState(); } catch (e) { logError('demoCreateAccount', e); }
    showWelcomeScreen();
    showRegister();
}
window.demoCreateAccount = demoCreateAccount;

/* 7.4: linkul direct spre demo, https://app.turnorder.org/?demo=1 (folosit de site).
   Scoate parametrul din adresă (un refresh nu repornește demo-ul) și spune dacă era cerut.
   Folosit DOAR pentru vizitatorii fără sesiune: un utilizator logat nu e scos din cont.
   EN: consumes ?demo=1 from the URL; only honoured for visitors without a session. */
function _consumeDemoParam() {
    try {
        var u = new URL(window.location.href);
        if (!u.searchParams.has('demo')) return false;
        var wanted = u.searchParams.get('demo') === '1';
        u.searchParams.delete('demo');
        history.replaceState(null, '', u.pathname + (u.search ? u.search : '') + u.hash);
        return wanted;
    } catch (e) { logError('_consumeDemoParam', e); return false; }
}

/* Câteva sesiuni fictive ca demo-ul să aibă istoric (stats / ELO / realizări). */
function seedDemoSessions() {
    const mk = (gameName, daysAgo, scores, winners) => ({
        id: 'demo-s-' + Math.random().toString(36).slice(2),
        timestamp: new Date(Date.now() - daysAgo * 86400000).toISOString(),
        gameName, duration: 60, location: 'Acasă', notes: null,
        participants: Object.keys(scores), scores, winners,
        guests: [], type: 'regular', isTournament: false, recordedBy: 'Administrator'
    });
    state.games = [
        mk('Catan', 2, { Alice: 10, Bob: 8, Charlie: 7 }, ['Alice']),
        mk('Azul', 5, { Bob: 75, Diana: 60, Alice: 55 }, ['Bob']),
        mk('Ticket to Ride', 9, { Charlie: 120, Alice: 110, Diana: 95 }, ['Charlie']),
        mk('Catan', 14, { Diana: 11, Bob: 9, Charlie: 8 }, ['Diana']),
        mk('Azul', 20, { Alice: 88, Charlie: 70 }, ['Alice'])
    ];
}

/* v9 8.13-5b (30 oct. 2026): POZELE din chatul clubului (CHAT_MAX_PHOTOS, alegerea și comprimarea,
   previzualizarea, ștergerea, legarea zonei de scriere) au fost MUTATE identic în chat-club.js.
   EN: chat photos moved to chat-club.js. */


/* RO: Delegare pentru butonul „Salvează Sesiune" (golden standard §5.1) — inlocuieste
   `onclick=`. Containerul `#tab-session` e STATIC in index.html (doar continutul lui se
   redeseneaza), deci legarea se face O SINGURA DATA la pornire, dupa modelul de mai sus.
   ⚠ Nu legam pe `.sess-actions`: acel div e in interiorul zonei redesenate.
   EN: one-time click delegation on the static #tab-session container; the actions row
   itself lives inside re-rendered markup, so we bind on the tab, not on the row. */
function _bindSessionActionsDelegation() {
    const host = document.getElementById('tab-session');
    if (!host || host.__ssActionsBound) return;
    host.__ssActionsBound = true;
    host.addEventListener('click', function (ev) {
        const el = ev.target && ev.target.closest ? ev.target.closest('[data-action]') : null;
        if (!el || !host.contains(el)) return;
        if (el.dataset.action === 'session-save') saveSession();
        /* RO: „Reseteaza" e SINCRON (`clearSessionForm` nu are niciun `await`), deci NU
           primeste indicator de incarcare — o rotita pe o actiune instantanee ar sugera
           o operatie care nu exista.
           ⚠ DE STIUT: butonul arunca lucrul nesalvat FARA sa intrebe, desi `nav.js`
           (`_navNewSession`) face deja opusul inainte sa cheme aceeasi functie, folosind
           `sessionFormHasWork()` + `confirmModal`. Confirmarea a fost AMANATA deliberat
           pentru punctul 8.2, ca sa nu ajungem cu doua detectii diferite de „modificari
           nesalvate" in acelasi tab.
           EN: reset is synchronous, so no spinner. It discards unsaved work without asking;
           the confirmation is deferred to the unsaved-changes work item on purpose. */
        else if (el.dataset.action === 'session-reset') clearSessionForm();
    });
}
try { if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', _bindSessionActionsDelegation); else _bindSessionActionsDelegation(); } catch (e) { /* noop */ }

/* v9 8.13-5b (30 oct. 2026): `sendMessage` (trimiterea din chatul clubului) a fost MUTATĂ identic
   în chat-club.js. EN: moved to chat-club.js. */

/* ---------- Sesiuni / Meciuri ---------- */
/* ============================================================================
   SISTEM DE UNIFORMIZARE A SCORURILOR — STRAT DE CONFIG (Batch A)
   EN: Score uniformization — config layer.

   Transformă scorurile brute în „Puncte de Club" pe bază de poziție (rank-based),
   cu 3 șabloane: A Competitiv, B Coop (Win/Loss), C Campanie (participare+bonus).
   Config-ul clubului se ține în club_settings cheia 'scoring_templates' (jsonb).
   Coloana matches.club_points stochează punctele acordate la sesiunile uniformizate.
   Scorurile brute rămân în matches.scores (NEATINSE) -> zero regresie. */

/* Valori implicite (folosite când clubul n-a configurat încă nimic). */
const DEFAULT_SCORING_TEMPLATES = {
    enabled: false,        // clubul folosește uniformizarea? (master switch admin)
    defaultMode: 'raw',    // mod implicit per sesiune: 'raw' | 'uniform'
    /* Șablonul A — Competitiv (rank-based). perRank[0] = Locul 1, perRank[1] = Locul 2 ...
       Ultima valoare din array se aplică TUTUROR locurilor inferioare (ex. „Locul 4+"). */
    competitive: { perRank: [2, 1, 0.5, 0] },
    /* Șablonul B — Coop / Semi-coop (Win/Loss). Integrat în blocul coop existent. */
    coop: { win: 2, loss: 0 },
    /* Șablonul C — Campanie / RPG. Participare fixă + bonusuri manuale per jucător. */
    campaign: { participation: 1 }
};

/* Întoarce config-ul efectiv al clubului (merge peste valorile implicite, defensiv).
   NU mută niciodată state-ul; doar citește. */
/* RO: Uniformizarea scorurilor e activă în clubul curent?
       Într-un club FĂRĂ uniformizare, „Puncte de club" și „Scoruri" descriu practic
       aceeași realitate, iar comutatorul Puncte/Scoruri sugerează o alegere care nu
       există. Acolo afișăm doar scorurile. Definit o singură dată, lângă sursa lui,
       ca să nu apară trei copii ale aceleiași condiții în trei fișiere.
   EN: is club-level score uniformization enabled? Without it the Points/Scores toggle
       offers a choice that isn't real. */
function clubUniformScoringOn() {
    try {
        const tpl = getScoringTemplates();
        return !!(tpl && tpl.enabled);
    } catch (e) { return false; }
}
window.clubUniformScoringOn = clubUniformScoringOn;

function getScoringTemplates() {
    const t0 = DEFAULT_SCORING_TEMPLATES;
    const s = (state && state.scoringTemplates && typeof state.scoringTemplates === 'object' && !Array.isArray(state.scoringTemplates)) ? state.scoringTemplates : {};
    const arr = (a, def) => (Array.isArray(a) && a.length) ? a.map(Number).filter(n => !isNaN(n)) : def.slice();
    return {
        enabled: (s.enabled === true || s.enabled === 'true'),
        defaultMode: (s.defaultMode === 'uniform') ? 'uniform' : 'raw',
        competitive: { perRank: arr(s.competitive && s.competitive.perRank, t0.competitive.perRank) },
        coop: {
            win: Number((s.coop && s.coop.win != null) ? s.coop.win : t0.coop.win) || 0,
            loss: Number((s.coop && s.coop.loss != null) ? s.coop.loss : t0.coop.loss) || 0
        },
        campaign: { participation: Number((s.campaign && s.campaign.participation != null) ? s.campaign.participation : t0.campaign.participation) || 0 }
    };
}

/* Modul implicit al unei sesiuni noi: 'uniform' doar dacă clubul a activat + a ales uniform. */
function clubDefaultScoreMode() {
    const tpl = getScoringTemplates();
    return (tpl.enabled && tpl.defaultMode === 'uniform') ? 'uniform' : 'raw';
}

/* Persistă config-ul șabloanelor în club_settings (folosit din Admin Panel — Batch B).
   Gated pe capabilitatea settings.edit (Admin secundar+). Single sink pt. salvare. */
async function saveScoringTemplatesToDB(tpl) {
    if (typeof can === 'function' && !can('settings.edit')) { showNotification(t('rbac.noPermission'), 'error'); return false; }
    state.scoringTemplates = tpl;
    if (isDemoMode()) return true;
    try {
        const up = await sb.from('club_settings').upsert(
            { club_id: currentClubId, key: 'scoring_templates', value: tpl, updated_at: new Date().toISOString() },
            { onConflict: 'club_id,key' }
        );
        if (up.error) { showNotification(t('m1.saveErrorColon') + up.error.message, 'error'); return false; }
        return true;
    } catch (e) { console.warn('scoring_templates:', e && e.message); return false; }
}

/* ---------- Admin Panel: configurare șabloane (Batch B) ----------
   Draft de lucru: editările se țin local până la „Salvează" (ca să poți adăuga/
   șterge locuri fără a salva la fiecare tastă). */
let _scTplDraft = null;

function _scEnsureDraft() {
    if (_scTplDraft) return _scTplDraft;
    const cur = getScoringTemplates();
    _scTplDraft = {
        enabled: cur.enabled,
        defaultMode: cur.defaultMode,
        competitive: { perRank: cur.competitive.perRank.slice() },
        coop: { win: cur.coop.win, loss: cur.coop.loss },
        campaign: { participation: cur.campaign.participation }
    };
    return _scTplDraft;
}

/* Citește valorile curente din inputuri în draft (înainte de re-randare/salvare). */
function _scReadInputs() {
    const d = _scEnsureDraft();
    const en = document.getElementById('scTplEnabled');
    if (en) d.enabled = !!en.checked;
    const dm = document.getElementById('scTplDefaultMode');
    if (dm) d.defaultMode = (dm.value === 'uniform') ? 'uniform' : 'raw';
    d.competitive.perRank = d.competitive.perRank.map((_, i) => {
        const el = document.getElementById('scRank-' + i);
        return el ? (Number(el.value) || 0) : 0;
    });
    const w = document.getElementById('scCoopWin'); if (w) d.coop.win = Number(w.value) || 0;
    const l = document.getElementById('scCoopLoss'); if (l) d.coop.loss = Number(l.value) || 0;
    const p = document.getElementById('scCampPart'); if (p) d.campaign.participation = Number(p.value) || 0;
    return d;
}

function scAddRank() { const d = _scReadInputs(); d.competitive.perRank.push(0); renderScoringTemplatesAdmin(); }
function scRemoveRank() { const d = _scReadInputs(); if (d.competitive.perRank.length > 1) d.competitive.perRank.pop(); renderScoringTemplatesAdmin(); }

function renderScoringTemplatesAdmin() {
    const el = document.getElementById('scoringTplControl');
    if (!el) return;
    if (typeof can === 'function' && !can('settings.edit')) { el.innerHTML = ''; return; }
    const d = _scEnsureDraft();
    const enabled = d.enabled;

    const ranksRows = d.competitive.perRank.map((v, i) => {
        const last = i === d.competitive.perRank.length - 1;
        const label = (i + 1) + (last && d.competitive.perRank.length > 1 ? window.t('scoreTpl.placeAndBelow') : '');
        return `<div style="display:flex; align-items:center; gap:10px; margin-top:6px;">
            <span style="flex:1; font-size:0.88em;">${window.t('scoreTpl.place')} <strong>${label}</strong></span>
            <input type="number" step="0.5" id="scRank-${i}" value="${v}" class="form-control" style="width:96px;">
            <span style="font-size:0.85em; opacity:0.7;">${window.t('scoreTpl.pts')}</span>
        </div>`;
    }).join('');

    el.innerHTML = `
        <p style="color:var(--text-secondary); font-size:0.9em; margin-bottom:12px;">${window.t('scoreTpl.intro')}</p>

        <div class="form-group">
            <label><input type="checkbox" id="scTplEnabled" ${enabled ? 'checked' : ''} onchange="_scReadInputs(); renderScoringTemplatesAdmin();"> ${window.t('scoreTpl.useUniform')}</label>
        </div>

        <div id="scTplBody" style="${enabled ? '' : 'opacity:0.5; pointer-events:none;'}">
            <div class="form-group">
                <label for="scTplDefaultMode"><i class="fas fa-sliders"></i> ${window.t('scoreTpl.defaultMode')}</label>
                <select id="scTplDefaultMode" class="form-control">
                    <option value="raw" ${d.defaultMode !== 'uniform' ? 'selected' : ''}>${window.t('scoreTpl.modeRaw')}</option>
                    <option value="uniform" ${d.defaultMode === 'uniform' ? 'selected' : ''}>${window.t('scoreTpl.modeUniform')}</option>
                </select>
            </div>

            <div class="form-group" style="border:1px solid var(--border,#333); border-radius:10px; padding:12px;">
                <span class="form-section-label" style="margin-bottom:4px;"><i class="fas fa-trophy"></i> ${window.t('scoreTpl.tplA')}</span>
                <small style="color:var(--text-secondary); display:block; margin-bottom:8px;">${window.t('scoreTpl.tplADesc')}</small>
                ${ranksRows}
                <div style="display:flex; gap:8px; margin-top:10px;">
                    <button type="button" class="btn btn-sm btn-secondary" onclick="scAddRank()"><i class="fas fa-plus"></i> ${window.t('scoreTpl.addPlace')}</button>
                    <button type="button" class="btn btn-sm btn-secondary" onclick="scRemoveRank()"><i class="fas fa-minus"></i> ${window.t('scoreTpl.removePlace')}</button>
                </div>
            </div>

            <div class="form-group" style="border:1px solid var(--border,#333); border-radius:10px; padding:12px;">
                <label for="scCoopWin" style="margin-bottom:4px;"><i class="fas fa-handshake"></i> ${window.t('scoreTpl.tplB')}</label>
                <small style="color:var(--text-secondary); display:block; margin-bottom:8px;">${window.t('scoreTpl.tplBDesc')}</small>
                <div style="display:flex; align-items:center; gap:10px; margin-top:6px;">
                    <span style="flex:1; font-size:0.88em;">${window.t('scoreTpl.win')}</span>
                    <input type="number" step="0.5" id="scCoopWin" value="${d.coop.win}" class="form-control" style="width:96px;">
                    <span style="font-size:0.85em; opacity:0.7;">${window.t('scoreTpl.pts')}</span>
                </div>
                <div style="display:flex; align-items:center; gap:10px; margin-top:6px;">
                    <span style="flex:1; font-size:0.88em;">${window.t('scoreTpl.loss')}</span>
                    <input type="number" step="0.5" id="scCoopLoss" value="${d.coop.loss}" class="form-control" style="width:96px;">
                    <span style="font-size:0.85em; opacity:0.7;">${window.t('scoreTpl.pts')}</span>
                </div>
            </div>

            <div class="form-group" style="border:1px solid var(--border,#333); border-radius:10px; padding:12px;">
                <label for="scCampPart" style="margin-bottom:4px;"><i class="fas fa-dragon"></i> ${window.t('scoreTpl.tplC')}</label>
                <small style="color:var(--text-secondary); display:block; margin-bottom:8px;">${window.t('scoreTpl.tplCDesc')}</small>
                <div style="display:flex; align-items:center; gap:10px; margin-top:6px;">
                    <span style="flex:1; font-size:0.88em;">${window.t('scoreTpl.participation')}</span>
                    <input type="number" step="0.5" id="scCampPart" value="${d.campaign.participation}" class="form-control" style="width:96px;">
                    <span style="font-size:0.85em; opacity:0.7;">${window.t('scoreTpl.pts')}</span>
                </div>
            </div>
        </div>

        <button class="btn btn-primary" onclick="saveScoringTemplatesFromAdmin()"><i class="fas fa-save"></i> ${window.t('common.save')}</button>`;
}

async function saveScoringTemplatesFromAdmin() {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    const d = _scReadInputs();
    const tpl = {
        enabled: !!d.enabled,
        defaultMode: (d.defaultMode === 'uniform') ? 'uniform' : 'raw',
        competitive: { perRank: d.competitive.perRank.map(Number) },
        coop: { win: Number(d.coop.win) || 0, loss: Number(d.coop.loss) || 0 },
        campaign: { participation: Number(d.campaign.participation) || 0 }
    };
    const ok = await saveScoringTemplatesToDB(tpl);
    if (!ok) return;
    _scTplDraft = null; /* re-sincronizează din state la următoarea randare */
    addAuditLog('audit.scoringConfig', state.currentUser.name, { key: tpl.enabled ? 'auditd.activated' : 'auditd.deactivated' });
    showNotification(t('scoreTpl.saved'), 'success');
    renderScoringTemplatesAdmin();
}

/* ---------- MOTORUL DE CONVERSIE (raw -> Puncte de Club) ----------
   Funcție PURĂ: din scorurile brute + șablon + (opțional) departajări manuale,
   întoarce { points: {pid: clubPts}, winners: [pid] }.
   `kind`: 'competitive' (Șablonul A) | 'coop' (Șablonul B) | 'campaign' (Șablonul C).
   tieBreaks: { pid: true } — jucătorul are avantaj la departajare (Locul superior). */
function computeClubPoints(participants, scores, kind, tpl, opts) {
    opts = opts || {};
    tpl = tpl || getScoringTemplates();
    const ids = (participants || []).filter(Boolean);
    const pts = {};
    const num = v => { const n = Number(v); return isNaN(n) ? 0 : n; };

    /* Șablonul B — Cooperativ (Victorie/Înfrângere): toți primesc același rezultat. */
    if (kind === 'coop') {
        const win = !!opts.coopWin;
        ids.forEach(p => { pts[p] = win ? num(tpl.coop.win) : num(tpl.coop.loss); });
        return { points: pts, winners: win ? ids.slice() : [] };
    }

    /* Șablonul C — Campanie / RPG: participare fixă + bonus individual manual. */
    if (kind === 'campaign') {
        const bon = opts.bonuses || {};
        ids.forEach(p => { pts[p] = num(tpl.campaign.participation) + num(bon[p]); });
        return { points: pts, winners: [] };
    }

    /* Șablonul A — Competitiv (după loc). */
    const perRank = (tpl.competitive && tpl.competitive.perRank && tpl.competitive.perRank.length)
        ? tpl.competitive.perRank.map(num) : [0];
    const ptsForPlace = place => perRank[Math.min(Math.max(place, 1) - 1, perRank.length - 1)]; // place 1-based; ultimul se aplică tuturor locurilor inferioare
    const tb = opts.tieBreaks || {};

    /* Direcția scorului (Z1-A, model BG Stats). Implicit 'high' = comportamentul
       istoric (scor mare câștigă) ⇒ ZERO regresie pentru orice sesiune fără scoreDir.
       'low' = scor mic câștigă (golf, jocuri de penalizări, unele jocuri de cărți).
       EN: score direction; 'high' (default) keeps legacy behavior, 'low' inverts. */
    const lowWins = String(opts.scoreDir || 'high').toLowerCase() === 'low';

    /* Sortare după scor + departajare. La 'high' scorul mare urcă; la 'low' cel mic
       urcă. La egalitate, cel cu avantaj de departajare urcă (identic în ambele). */
    const sorted = ids.slice().sort((a, b) => {
        const sa = num(scores && scores[a]), sb = num(scores && scores[b]);
        if (sb !== sa) return lowWins ? (sa - sb) : (sb - sa);
        return (tb[b] ? 1 : 0) - (tb[a] ? 1 : 0);
    });

    /* Atribuire locuri pe grupuri de scor egal. Dacă într-un grup există o
       departajare manuală -> locuri distincte (ordinea din sortare). Altfel ->
       egalitate reală -> împart media punctelor locurilor ocupate. */
    const placeOf = {};
    let i = 0, place = 1;
    /* Atribuie un set de jucători locurilor consecutive, împărțind media punctelor. */
    const assignSplit = (members, startPlace) => {
        if (!members.length) return;
        let sum = 0;
        for (let q = startPlace; q < startPlace + members.length; q++) sum += ptsForPlace(q);
        const avg = sum / members.length;
        members.forEach(p => { pts[p] = avg; placeOf[p] = startPlace; });
    };
    while (i < sorted.length) {
        const si = num(scores && scores[sorted[i]]);
        let k = i;
        while (k + 1 < sorted.length && num(scores && scores[sorted[k + 1]]) === si) k++;
        const group = sorted.slice(i, k + 1);
        /* Cei cu departajare câștigată iau locurile de sus (în ordine); restul, egali,
           împart media locurilor rămase. Fără nicio departajare => tot grupul împarte. */
        const flagged = group.filter(p => tb[p]);
        const rest = group.filter(p => !tb[p]);
        assignSplit(flagged, place);
        assignSplit(rest, place + flagged.length);
        place += group.length;
        i = k + 1;
    }
    /* Regula scorului nul, dependentă de direcție (Z3-A):
       • HIGH (§18, istoric): scor brut <= 0 => 0 Puncte de Club; nu ești câștigător
         doar fiindcă ai „ocupat" un loc cu 0 (ex. toți 0). Comportament NESCHIMBAT.
       • LOW: scorul mic e BUN, deci 0 (sau negativ) e valid și poate câștiga. Nu
         excludem pe baza valorii; toți participanții primesc locul lor, iar
         câștigătorii sunt cei de pe locul 1 (scorul minim). */
    let winners;
    if (lowWins) {
        winners = ids.filter(p => placeOf[p] === 1);
    } else {
        ids.forEach(p => { if (num(scores && scores[p]) <= 0) pts[p] = 0; });
        winners = ids.filter(p => placeOf[p] === 1 && num(scores && scores[p]) > 0);
    }
    return { points: pts, winners };
}

/* Detectează grupurile de jucători cu scor brut egal (pt. UI-ul de tie-breaker — Batch C2).
   Întoarce [[pid,pid],...] doar pentru grupurile de ≥2 cu scor > 0. */
function detectScoreTies(participants, scores) {
    const num = v => { const n = Number(v); return isNaN(n) ? 0 : n; };
    const byScore = {};
    (participants || []).filter(Boolean).forEach(p => {
        const s = num(scores && scores[p]);
        if (s <= 0) return;
        (byScore[s] = byScore[s] || []).push(p);
    });
    return Object.keys(byScore).map(s => byScore[s]).filter(g => g.length >= 2);
}

function _buildSessionMeta(session) {
    const meta = { type: session.type || 'standard' };
    if (session.duration && Number(session.duration) > 0) meta.duration = Number(session.duration);
    if (session.isTournament) {
        meta.tournamentName = session.tournamentName || '';
        meta.prize = session.prize || '';
        meta.format = session.format || '';
        meta.rounds = session.rounds || null;
        meta.fee = session.fee || '';
    }
    if (session.scoresheet) meta.scoresheet = session.scoresheet;
    if (session.roles && Object.keys(session.roles).length) meta.roles = session.roles;
    if (session.playerTimes && Object.keys(session.playerTimes).length) meta.playerTimes = session.playerTimes;
    if (session.type === 'coop' && session.coop) meta.coop = session.coop;
    if (session.friendly) meta.friendly = true;
    if (session.scoreMode === 'uniform') { meta.scoreMode = 'uniform'; if (session.scoreKind) meta.scoreKind = session.scoreKind; }
    /* Direcția scorului: persistăm DOAR când e 'low' (non-implicit) ca să nu umflăm
       istoricul; absența => 'high' (identic cu comportamentul dinainte). */
    if (session.scoreDir === 'low') meta.scoreDir = 'low';
    if (session.tieBreaks && Object.keys(session.tieBreaks).length) meta.tieBreaks = session.tieBreaks;
    if (session.photos && session.photos.length) meta.photos = session.photos;
    if (session.location) meta.location = session.location;
    if (session.notes) meta.notes = session.notes;
    if (session.guests && session.guests.length) meta.guests = session.guests;
    /* Legare la campanie (Faza 2): meciul aparține unei campanii -> meta.campaignId.
       save_session_atomic pasează meta intactă (doar remapare guest), deci ajunge în DB. */
    if (session.campaignId) {
        meta.campaignId = session.campaignId;
        if (session.scenarioName) meta.scenarioName = session.scenarioName;
    }
    return meta;
}

/* ============================================================================
   FAZA 2 (V4/V9) — bucket 'session-photos' PRIVAT + URL semnate.
   Datele stocate (matches.meta.photos / announcements.images) țin PATH-ul
   relativ la bucket ({club_id}/...), NU URL public. Randarea folosește
   <img data-sp="{path}"> iar un MutationObserver global le semnează automat.
   Compat înapoi: _spPath() acceptă și URL-urile publice vechi.
   ============================================================================ */
/* Placeholder transparent 1px cât timp poza nu e semnată. */
var _SP_PH = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
window._SP_PH = _SP_PH;

/* Extrage path-ul relativ la bucket dintr-o valoare stocată.
   Acceptă: path nou ("{club}/{match}/f.jpg") SAU URL public/semnat vechi. */
function _spPath(stored) {
    var v = ('' + (stored || '')).trim();
    if (!v) return '';
    /* ⚠ Ambele bucket-uri: o adresă deja semnată din `chat-photos` ar fi rămas cu
       prefixul în ea, iar o a doua semnare ar fi eșuat (cale inexistentă). */
    ['/session-photos/', '/chat-photos/'].forEach(function (m) {
        var i = v.indexOf(m);
        if (i !== -1) v = v.slice(i + m.length);   // URL (public sau semnat) -> path
    });
    var q = v.indexOf('?'); if (q !== -1) v = v.slice(0, q);   // taie ?token=...
    return v.replace(/^\/+/, '');
}
window._spPath = _spPath;

/* Semnează un singur path (folosit la click în lightbox). Fallback = valoarea dată. */
/* ⚠ Bucket-ul se DEDUCE din cale, nu mai e scris fix. Pozele de chat (§8.9) stau
   în `chat-photos`, sub `club/…` sau `dm/…`; cele de sesiune și de anunț stau în
   `session-photos`, sub `<club_id>/…`. Înainte, semnarea cerea MEREU
   `session-photos`, deci pozele de chat nu primeau adresă semnată și rămâneau pe
   substituent — vizibile doar în lightbox, unde calea se transmite direct.
   EN: the bucket is derived from the path prefix; it used to be hardcoded to
       session-photos, so chat photos never got a signed URL. */
function _spBucket(p) {
    var s = String(p || '');
    return (s.indexOf('club/') === 0 || s.indexOf('dm/') === 0) ? 'chat-photos' : 'session-photos';
}
window._spBucket = _spBucket;

/* ============================================================
   CACHE PENTRU ADRESELE SEMNATE   (§8.6)
   ------------------------------------------------------------
   RO: fiecare semnare producea un TOKEN NOU, deci o adresa noua. Pentru browser,
   `poza.jpg?token=A` si `poza.jpg?token=B` sunt doua fisiere diferite — asa ca la
   fiecare re-randare poza se descarca DIN NOU, integral, desi fisierele se incarca
   cu `cacheControl: '3600'`, adica sunt declarate cacheabile o ora. Plateam pentru
   cache si il anulam singuri.
   Retinand adresa, re-randarea refoloseste ACEEASI adresa ⇒ cache-ul browserului
   functioneaza ⇒ zero trafic si afisare instantanee.

   ⚠ TERMENUL E DELIBERAT MAI SCURT DECAT SEMNATURA: semnatura tine 3600 s, noi
   retinem 50 de minute. Marja de 10 minute exista fiindca o adresa expirata da o
   poza STRICATA, iar defectul ar aparea abia dupa o ora de folosire — exact genul
   care scapa la testare. Mai bine o semnare in plus decat o poza rupta.

   ⚠ NU se pastreaza intre reincarcari (nu e in localStorage): la un refresh, oricum
   se re-randeaza tot, iar o adresa salvata pe disc ar putea supravietui expirarii.
   EN: signed URLs rotate their token, so every re-render defeated the browser cache.
   Cached in memory for 50 min — deliberately shorter than the 60 min signature. */
var _spCache = Object.create(null);
var _SP_CACHE_MS = 50 * 60 * 1000;

function _spCacheGet(cale) {
    var v = _spCache[cale];
    if (!v) return null;
    if (Date.now() > v.expira) { delete _spCache[cale]; return null; }
    return v.url;
}

function _spCachePut(cale, url) {
    if (!cale || !url) return;
    _spCache[cale] = { url: url, expira: Date.now() + _SP_CACHE_MS };
}
window._spCacheGet = _spCacheGet;
window._spCachePut = _spCachePut;

async function signOnePhoto(stored) {
    try {
        if (!sb) return stored;
        var p = _spPath(stored); if (!p) return stored;
        var dinCache = _spCacheGet(p);
        if (dinCache) return dinCache;
        var res = await sb.storage.from(_spBucket(p)).createSignedUrl(p, 3600);
        var url = (res && res.data && res.data.signedUrl) ? res.data.signedUrl : null;
        if (url) { _spCachePut(p, url); return url; }
        return stored;
    } catch (e) { logError('signOnePhoto', e); return stored; }
}
window.signOnePhoto = signOnePhoto;

/* Observer global: orice <img data-sp="..."> nou în DOM primește automat un src semnat.
   Astfel toate punctele de randare (card sesiune, feed anunțuri, panou moderare,
   prezente și viitoare) sunt acoperite fără hook-uri per-loc. */
(function _initSignedPhotoObserver() {
    if (typeof document === 'undefined' || window._spObserverInit) return;
    window._spObserverInit = true;
    var pending = [];
    var scheduled = false;
    /* v9 8.5-B: calea miniaturii unei poze (același dosar: `…/0-abc.jpg` → `…/0-abc.thumb.jpg`,
       chat-thumb.js), doar pentru imaginile marcate `data-spt`. 8.5-C: și pozele din sesiuni /
       anunțuri (session-photos), nu doar chatul. EN: thumbnail path for marked photos. */
    function _spThumbOf(el, p) {
        if (!el || !el.hasAttribute || !el.hasAttribute('data-spt') || /[.]thumb[.]jpg$/.test(p)) return null;
        var t = String(p).replace(/[.][A-Za-z0-9]+$/, '.thumb.jpg');
        return t !== p ? t : null;
    }
    function flush() {
        scheduled = false;
        if (!sb || !pending.length) return;
        var batch = pending.splice(0, pending.length);
        /* ⚠ Grupare pe BUCKET, nu doar pe cale: `createSignedUrls` semnează dintr-un
           singur bucket per apel. Înainte se cerea mereu `session-photos`, deci
           pozele de chat (`chat-photos`) nu primeau niciodată adresă semnată și
           rămâneau pe substituent. Un apel per bucket, în loc de unul singur. */
        var byBucket = {}, thumbOrig = {};
        batch.forEach(function (el) {
            if (!el || !el.isConnected) return;
            var p = _spPath(el.getAttribute('data-sp'));
            if (!p) { if (el.removeAttribute) el.removeAttribute('data-sp'); return; }
            /* ⚠ Avem deja o adresa valabila pentru calea asta? Atunci o folosim direct,
               FARA sa mai cerem una. Aceeasi adresa inseamna ca browserul o gaseste in
               cache-ul lui si nu mai descarca nimic — motivul pentru care pozele se
               reincarcau la fiecare intrare in tab.
               EN: reuse the cached signed URL so the browser cache can actually work. */
            /* v9 8.5-B: `data-spt` (colajele / citatele din chat) ⇒ miniatura `…thumb.jpg`, cu
               originalul ca rezervă în ACEEAȘI cerere (pozele vechi n-au miniatură). EN: thumbnail first. */
            var t = _spThumbOf(el, p), key = t || p;
            var gata = _spCacheGet(key);
            if (gata) { el.src = gata; el.removeAttribute('data-sp'); return; }
            var b = _spBucket(p);
            var m = (byBucket[b] = byBucket[b] || {});
            (m[key] = m[key] || []).push(el);
            if (t) (thumbOrig[b] = thumbOrig[b] || {})[t] = p;
        });
        Object.keys(byBucket).forEach(function (bucket) {
            var byPath = byBucket[bucket];
            var paths = Object.keys(byPath);
            if (!paths.length) return;
            var fbm = thumbOrig[bucket] || {}, all = paths.slice();
            Object.keys(fbm).forEach(function (t) { if (all.indexOf(fbm[t]) === -1) all.push(fbm[t]); });
            function apply(list) {
                var got = {};
                (list || []).forEach(function (r) { if (r && r.path && r.signedUrl) { got[r.path] = r.signedUrl; _spCachePut(r.path, r.signedUrl); } });
                paths.forEach(function (k) {
                    var url = got[k] || (fbm[k] ? got[fbm[k]] : null);
                    if (!url) return;
                    /* 8.5-B: poză fără miniatură ⇒ ținem minte originalul și pentru cheia miniaturii
                       (fără o nouă cerere la fiecare desenare) */
                    if (fbm[k] && !got[k]) _spCachePut(k, url);
                    byPath[k].forEach(function (el) { el.src = url; el.removeAttribute('data-sp'); });
                });
            }
            sb.storage.from(bucket).createSignedUrls(all, 3600).then(function (res) {
                if (res && res.data && !res.error) { apply(res.data); return; }
                /* 8.5-B: cererea întreagă refuzată (ex. o miniatură lipsă) ⇒ doar originalele, ca înainte */
                var orig = all.filter(function (x) { return !fbm[x]; });
                if (orig.length !== all.length) return sb.storage.from(bucket).createSignedUrls(orig, 3600).then(function (r2) { apply(r2 && r2.data); });
                apply(res && res.data);
            }).catch(function (e) { logError('spObserver', e); });
        });
    }
    function schedule() { if (!scheduled) { scheduled = true; setTimeout(flush, 30); } }
    function collect(root) {
        if (!root || root.nodeType !== 1) return;
        try {
            if (root.matches && root.matches('img[data-sp]')) pending.push(root);
            if (root.querySelectorAll) Array.prototype.push.apply(pending, root.querySelectorAll('img[data-sp]'));
        } catch (e) { /* noop */ }
    }
    try {
        var obs = new MutationObserver(function (muts) {
            for (var i = 0; i < muts.length; i++) {
                var added = muts[i].addedNodes;
                for (var j = 0; j < added.length; j++) collect(added[j]);
            }
            if (pending.length) schedule();
        });
        obs.observe(document.documentElement || document.body, { childList: true, subtree: true });
        collect(document.body); if (pending.length) schedule();
    } catch (e) { logError('spObserverInit', e); }
})();

/* Anunță utilizatorul câte fotografii au fost respinse la încărcare.
   ⚠ MOTIVUL EXISTENȚEI: ambele funcții de upload săreau peste fișierul eșuat cu
     un simplu `console.warn` + `continue`. Cât timp bucket-ul `session-photos`
     nu avea `file_size_limit`/`allowed_mime_types`, nimic nu era respins și
     tăcerea nu se vedea. De la momentul în care Storage validează pe server, un
     fișier prea mare sau de tip nepermis ar DISPĂREA din sesiune fără ca nimeni
     să afle — o limită fără feedback transformă o problemă de securitate într-una
     de pierdere de date.
   ⚠ Nu blochează salvarea: restul sesiunii/anunțului se salvează, ca până acum.
   EN: surface rejected uploads; server-side bucket validation makes the previously
       invisible silent skip user-visible data loss. */
function _warnPhotosRejected(n) {
    if (!n) return;
    try {
        if (typeof showNotification === 'function' && typeof window.t === 'function') {
            showNotification(window.t('photo.uploadRejected').replace('{n}', n), 'error');
        }
    } catch (e) { logError('_warnPhotosRejected', e); }
}
window._warnPhotosRejected = _warnPhotosRejected;

/* Încarcă fotografiile sesiunii în bucket-ul session-photos și întoarce PATH-urile (bucket privat). */
async function uploadSessionPhotos(matchId, files) {
    const urls = [];
    let rejected = 0;
    for (const f of files) {
        try {
            const ext = ((f.name && f.name.split('.').pop()) || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
            const path = (currentClubId || 'club') + '/' + matchId + '/' + Date.now() + '-' + Math.random().toString(36).slice(2, 8) + '.' + ext;
            const up = await sb.storage.from('session-photos').upload(path, f, { cacheControl: '3600', upsert: false, contentType: f.type || 'image/jpeg' });
            if (up.error) { console.warn('photo upload:', up.error.message); rejected++; continue; }
            urls.push(path);   /* bucket privat: stocăm PATH-ul, semnarea se face la randare */
            /* v9 8.5-C: miniatura (320 px) lângă poză, ÎNAINTE ca sesiunea să fie salvată / văzută
               de ceilalți (altfel ar primi originalul ~50 min); eșecul nu oprește nimic */
            if (window.TO_CHAT && TO_CHAT.thumbUp) await TO_CHAT.thumbUp('session-photos', f, path);
        } catch (e) { console.warn('photo:', e && e.message); rejected++; }
    }
    _warnPhotosRejected(rejected);
    return urls;
}
/* Faza 3a: garantează un profil REAL în DB pentru un invitat inline și întoarce id-ul.
   Reutilizează pattern-ul din addPlayer (id random + insert profiles + legare la club).
   Adaugă jucătorul și în state local (ambele hărți) ca să fie coerent imediat. */
/* Înregistrează guest-ul în state-ul local (ambele hărți + numele). */
function _registerGuestInState(id, name, email) {
    if (!id) return;
    if (!state.membersById) state.membersById = {};
    if (!state.profileNamesById) state.profileNamesById = {};
    if (!state.profileNamesById[id]) state.profileNamesById[id] = name;
    if (!state.membersById[id]) {
        const mo = {
            dbId: id, name, displayName: name, role: 'member', userTag: '',
            score: 0, gamesPlayed: 0, hidden: false, email: email || null,
            joinDate: new Date().toISOString().split('T')[0],
            preferredGames: [], skillLevel: 3, isGuest: true
        };
        state.membersById[id] = mo;
        if (!state.members[name]) state.members[name] = mo;
    }
}

/* Ajustează punctele (profiles.points) prin RPC SECURITY DEFINER, ocolind RLS.
   deltas: { id: delta }. Sursă unică pentru TOATE căile care ating punctele. */
async function _adjustPointsDB(deltas) {
    const clean = {};
    Object.keys(deltas || {}).forEach(id => { const d = Number(deltas[id]) || 0; if (id && d) clean[id] = d; });
    if (!Object.keys(clean).length || isDemoMode()) return null;
    const res = await sb.rpc('adjust_club_points', { p_club_id: currentClubId, p_deltas: clean });
    if (res.error) logError('adjust_club_points', res.error);
    return res.error || null;  /* null = succes/no-op; eroare = apelantul poate reverti */
}

async function _ensureGuestProfile(guest) {
    if (guest && guest.dbId) return guest.dbId;
    const name = (guest && guest.name) ? String(guest.name).trim() : '';
    if (!name) return null;
    const email = (guest && guest.email) ? String(guest.email).trim() : null;

    if (isDemoMode()) {
        const gid = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : ('guest-' + Date.now());
        _registerGuestInState(gid, name, email);
        return gid;
    }

    /* Creare/garantare guest SERVER-SIDE (RPC SECURITY DEFINER):
       dedup pe email (claim) + profil garantat în DB + legare la club, ocolind RLS.
       Asta elimină profilurile-fantomă și id-urile diferite pt. același email. */
    const res = await sb.rpc('admin_add_guest', { p_club_id: currentClubId, p_name: name, p_email: email });
    if (res.error || !res.data) {
        const _sem = (res.error && res.error.message) || '';
        /* ★ Adresa e a cuiva cu cont, care nu e membru: NU creăm un profil-fantomă
           cu numele lui. Spunem clar ce s-a întâmplat și oprim aici. */
        if (/TARGET_HAS_ACCOUNT/i.test(_sem)) { paOfferInviteInstead(name); return null; }
        console.warn('admin_add_guest:', _sem);
        const gid = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : ('guest-' + Date.now());
        _registerGuestInState(gid, name, email);
        return gid;
    }
    const finalId = res.data;
    _registerGuestInState(finalId, name, email);
    return finalId;
}

/* Mapează cheile temporare de guest (gtmp-...) pe id-urile reale întoarse de
   save_session_atomic (sau locale, în demo) în TOATE câmpurile sesiunii —
   oglindă a _remap_ids din DB, ca starea locală să fie identică cu rândul scris. */
function _applyGuestMapToSession(session, map) {
    if (!session || !map || !Object.keys(map).length) return;
    const arr = a => Array.isArray(a) ? a.map(x => map[x] || x) : a;
    const obj = o => {
        if (!o || typeof o !== 'object') return o;
        const out = {};
        Object.keys(o).forEach(k => { out[map[k] || k] = o[k]; });
        return out;
    };
    session.participants = arr(session.participants);
    session.winners = arr(session.winners);
    session.scores = obj(session.scores);
    if (session.clubPoints) session.clubPoints = obj(session.clubPoints);
    if (session.roles) session.roles = obj(session.roles);
    if (session.playerTimes) session.playerTimes = obj(session.playerTimes);
    if (session.scoresheet && session.scoresheet.breakdown) session.scoresheet.breakdown = obj(session.scoresheet.breakdown);
    (session.guests || []).forEach(g => { if (g && g.tmp_key && map[g.tmp_key]) g.dbId = map[g.tmp_key]; });
}

/* Aplică efectele LOCALE ale unei sesiuni salvate cu SUCCES: puncte de clasament
   + jocuri jucate per membru (sărind amicalele, §12.d) + statistici de joc în
   catalog + sincronizarea punctelor utilizatorului curent. Se cheamă DOAR după
   ce persistarea a reușit (sau în demo) — nicio mutație optimistă înainte de scriere. */
function _applySessionLocalEffects(session, rankVal, isFriendly, gameName, timestamp, duration) {
    Object.keys(session.scores || {}).forEach(pid => {
        let m = (typeof memberById === 'function') ? memberById(pid) : (state.membersById && state.membersById[pid]);
        if (!m) {
            const knownName = (typeof displayNameById === 'function' && displayNameById(pid)) || pid;
            m = {
                dbId: pid, name: knownName, displayName: knownName, role: 'member',
                score: 0, gamesPlayed: 0, hidden: false, email: null,
                joinDate: new Date().toISOString().split('T')[0],
                preferredGames: [], skillLevel: 3, isGuest: true
            };
            if (!state.membersById) state.membersById = {};
            state.membersById[pid] = m;
        }
        if (!isFriendly) {
            m.score = (m.score || 0) + rankVal(pid);
            m.gamesPlayed = (m.gamesPlayed || 0) + 1;
        }
    });
    if (gameName && state.catalog[gameName]) {
        const g = state.catalog[gameName];
        g.timesPlayed = (g.timesPlayed || 0) + 1;
        g.lastPlayed = timestamp;
        if (duration > 0) {
            if (!Array.isArray(g.actualDurations)) g.actualDurations = [];
            g.actualDurations.push(duration);
            g.avgDuration = Math.round(g.actualDurations.reduce(function (a, b) { return a + b; }, 0) / g.actualDurations.length);
        }
    }
    /* Optimist↔hidratat: ține currentUser.points aliniat cu membrul (per-club). */
    if (state.currentUser && state.currentUser.id) {
        const _cm = (typeof memberById === 'function') ? memberById(state.currentUser.id) : (state.membersById && state.membersById[state.currentUser.id]);
        if (_cm) state.currentUser.points = _cm.score;
    }
}

async function saveSession() {
    if (!requireWritableClub()) return;
    if (typeof can === 'function' && !can('session.create')) { showNotification(t('rbac.noPermission'), 'error'); return; }
    /* ⚠ Fără garda asta, cine apasă „Salvează" imediat după ce a ales 10 poze ar
       trimite sesiunea cu doar câteva dintre ele — restul încă se comprimă, iar
       pierderea ar fi TĂCUTĂ. Vezi `onSessionPhotosPicked` (app-modules-1.js).
       EN: prevents saving while photos are still being compressed, which would
           silently drop the ones not yet processed. */
    if (window._photoPrepBusy) { showNotification(t('photo.stillPreparing'), 'info'); return; }
    const date = document.getElementById('sessionDate').value;
    const startTime = document.getElementById('sessionStartTime').value;
    const duration = parseInt(document.getElementById('sessionDuration').value) || (planAllows('timer') ? Math.round(sessionElapsedMs() / 60000) : 0) || 0;
    const gameName = document.getElementById('sessionGame').value;
    const location = document.getElementById('sessionLocation').value.trim();
    const notes = document.getElementById('sessionNotes').value.trim();
    const sessionType = (typeof currentSessionType !== 'undefined' && currentSessionType) ? currentSessionType : 'standard';
    const isTournament = sessionType === 'tournament';
    const isFriendly = !!(document.getElementById('sessionFriendly') && document.getElementById('sessionFriendly').checked);
    /* Mod de scorare al sesiunii (hibrid). Uniformizat DOAR dacă clubul a activat
       sistemul ȘI există comutatorul setat pe 'uniform'. Friendly => mereu brut
       (nu afectează clasamentul oricum). */
    let scoreMode = 'raw';
    try {
        const _tpl = getScoringTemplates();
        const _sel = document.getElementById('sessionScoreMode');
        if (_tpl.enabled && !isFriendly && _sel && _sel.value === 'uniform') scoreMode = 'uniform';
    } catch (e) { logError('saveSession', e); }

    /* Direcția scorului efectivă a sesiunii (Faza 3): override din foaie dacă e
       setat, altfel moștenită de la joc, altfel 'high'. Determină câștigătorul
       (raw + uniform) și se persistă în meta pentru afișare corectă ulterioară. */
    let _scoreDir = 'high';
    try {
        if (typeof sessionEffectiveScoreDir === 'function') _scoreDir = sessionEffectiveScoreDir();
        else {
            const _g = (state.catalog || {})[gameName];
            _scoreDir = (_g && _g.scoreDir === 'low') ? 'low' : 'high';
        }
    } catch (e) { logError('saveSession.scoreDir', e); }

    if (!date || !gameName) { showNotification(t('sb.fillDateGame'), 'error'); return; }

    const timestamp = new Date(`${date}T${startTime || '00:00'}`).toISOString();
    const session = {
        id: 'tmp-' + Date.now(), timestamp, gameName,
        location: location || null, duration, notes: notes || null,
        scores: {}, participants: [], guests: [],
        type: sessionType, isTournament, friendly: isFriendly,
        recordedBy: state.currentUser.id, recordedAt: new Date().toISOString()
    };
    if (isTournament) {
        session.tournamentName = (document.getElementById('tournamentName') || {}).value || '';
        session.prize = (document.getElementById('tournamentPrize') || {}).value || '';
        session.format = (document.getElementById('tournamentFormat') || {}).value || '';
        session.rounds = parseInt((document.getElementById('tournamentRounds') || {}).value) || null;
        session.fee = (document.getElementById('tournamentFee') || {}).value || '';
    }
    /* Legare la campanie (Faza 2): DOAR sesiuni standard (nu turneu/coop). Flag setat
       de „+ Sesiune de campanie" / selectorul „Atașează la campanie" din campaigns_ext.js. */
    try {
        if (window._sessCampaignId && !isTournament && sessionType !== 'coop') {
            session.campaignId = window._sessCampaignId;
            const _scn = (document.getElementById('sessionScenarioName') || {}).value;
            if (_scn && _scn.trim()) session.scenarioName = _scn.trim();
        }
    } catch (e) { logError('saveSession.campaign', e); }

    /* Scoruri jucători înregistrați */
    const _ssFields = scoresheetFor(gameName);
    const _roles = rolesFor(gameName);
    const _timer = planAllows('timer');
    if (liveTimer.sessionRunning) { bankActivePlayer(); } // bancarizează timpul curent
    if (_ssFields) session.scoresheet = { fields: _ssFields.slice(), breakdown: {} };
    if (_roles) session.roles = {};
    sessionPlayers.forEach(_pid => {
        const member = (typeof memberById === 'function') ? memberById(_pid) : (state.membersById && state.membersById[_pid]);
        if (!member) return;
        const pid = member.dbId || _pid; // identitate = id (selectat explicit)
        const safeName = String(pid).replace(/[^a-zA-Z0-9]/g, '-');
        const card = document.getElementById(`player-${safeName}`);
        if (!card || card.classList.contains('absent')) return;
        const scoreInput = document.getElementById(`score-${safeName}`);
        const score = parseFloat(scoreInput && scoreInput.value) || 0;
        session.participants.push(pid);
        session.scores[pid] = score;
        if (_ssFields) {
            const detail = {};
            _ssFields.forEach((label, i) => {
                const el = document.getElementById('ss-' + safeName + '-' + i);
                detail[label] = parseFloat(el && el.value) || 0;
            });
            session.scoresheet.breakdown[pid] = detail;
        }
        if (_roles) {
            const rsel = document.getElementById('role-' + safeName);
            if (rsel && rsel.value) session.roles[pid] = rsel.value;
        }
        if (_timer) {
            const secs = Math.round(playerElapsedMs(pid) / 1000);
            if (secs > 0) { session.playerTimes = session.playerTimes || {}; session.playerTimes[pid] = secs; }
        }
    });
    if (liveTimer.activePlayer) { liveTimer.playerStartTs = Date.now(); } // re-pornește cronometrarea curentă (dacă utilizatorul continuă)
    /* Scoruri invitați: gueștii NOI primesc o cheie temporară (gtmp-...) și se
       creează ATOMIC în save_session_atomic (rezolvă riscul A — fără orfani la
       eșec); cei existenți (au dbId) trec direct. Substituția temporar->real se
       face DOAR după succesul persistării, prin _applyGuestMapToSession. */
    /* ⚠ 9.1: emailul invitatului nu supraviețuia unui refresh (foaia live nu-l poartă,
       §48), iar serverul respingea salvarea cu GUEST_EMAIL_REQUIRED — fără nicio cale de
       a-l reintroduce. Îl cerem ACUM, înainte de a construi sarcina, cu exact aceleași
       verificări ca la adăugare. Renunțarea oprește salvarea fără efecte secundare:
       până aici nu s-a scris nimic, nici local, nici pe server.
       EN: ask for missing guest emails before building the payload instead of failing
       server-side with no way to re-enter them. */
    /* ⚠ 9.1 — DUBLARE ÎN ISTORIC. Aceeași sesiune live deschisă pe două dispozitive cu drept
       de salvare: salvezi pe primul, iar pe al doilea formularul rămâne plin și „Salvează"
       creează o A DOUA partidă (plus un invitat duplicat). `idem_key` nu ajută — se generează
       la fiecare apăsare, deci apără doar de dublu-clic pe ACELAȘI dispozitiv.
       Două gărzi: (a) am aflat deja, prin realtime, că a fost salvată; (b) încă suntem legați,
       dar altcineva a salvat-o între timp — întrebăm serverul înainte de a scrie ceva.
       Nu golim formularul: datele rămân pe ecran, decizia e a utilizatorului.
       EN: same live session open on two devices -> second Save created a duplicate match.
       Guard (a) uses the realtime verdict, guard (b) asks the server before writing. */
    if (window._liveSavedElsewhereId) {
        showNotification(t('live.alreadySavedBlock'), 'error');
        return;
    }
    if (window._liveSessionId && !isDemoMode() && sb) {
        try {
            const _chk = await sb.from('live_sessions')
                .select('status, match_id').eq('id', window._liveSessionId).maybeSingle();
            if (!_chk.error && _chk.data && _chk.data.status === 'saved') {
                window._liveSavedElsewhereId = window._liveSessionId;
                window._liveSavedElsewhereMatch = _chk.data.match_id || null;
                window._liveSessionId = null;
                window._liveReadOnly = false;
                try { if (typeof applyLiveReadOnly === 'function') applyLiveReadOnly(); } catch (e) { logError('saveSession.readonly', e); }
                try { if (typeof renderLiveSessionsBar === 'function') renderLiveSessionsBar(); } catch (e) { logError('saveSession.bar', e); }
                showNotification(t('live.alreadySavedBlock'), 'error');
                return;
            }
        } catch (e) { logError('saveSession.liveCheck', e); }
    }

    if (typeof ensureGuestEmails === 'function') {
        let _gemOk = false;
        try { _gemOk = await ensureGuestEmails(); } catch (e) { logError('saveSession.guestEmails', e); _gemOk = false; }
        if (!_gemOk) return;
    }

    const _newGuests = [];
    /* ★ Cheia unui invitat in interfata (`guest.key` sau `dbId`) NU e cea sub care
       ajunge in `participants` (invitatii noi primesc abia aici un `gtmp-...`).
       Retinem corespondenta, ca departajarile alese de utilizator sa fie regasite. */
    const _guestKeyToGid = {};
    for (const guest of (typeof sessionGuests !== 'undefined' ? sessionGuests : [])) {
        const safeName = guest.name.replace(/\s+/g, '-');
        const scoreInput = document.getElementById(`score-guest-${safeName}`);
        const score = parseFloat(scoreInput && scoreInput.value) || 0;
        let gid;
        if (guest.dbId) {
            gid = guest.dbId;
        } else {
            gid = 'gtmp-' + ((window.crypto && crypto.randomUUID) ? crypto.randomUUID() : (Date.now() + '-' + Math.random().toString(36).slice(2)));
            guest.tmp_key = gid;
            _newGuests.push({ tmp_key: gid, name: guest.name, email: guest.email || null });
        }
        if (guest.key) _guestKeyToGid[guest.key] = gid;
        if (guest.dbId) _guestKeyToGid[guest.dbId] = gid;
        session.participants.push(gid);
        session.guests.push(guest);
        session.scores[gid] = score;
        if (_ssFields) {
            const detail = {};
            /* ⚠ Randarea foloseste `guest.key` pentru id-ul campurilor (`ssg-<key>-<i>`),
               nu numele. Cautarea dupa nume nu gasea nimic, deci detalierea pe
               categorii a invitatilor se salva mereu ca zerouri. */
            const gtid = String(guest.key || guest.name).replace(/[^a-zA-Z0-9]/g, '-');
            _ssFields.forEach((label, i) => {
                const el = document.getElementById('ssg-' + gtid + '-' + i);
                detail[label] = parseFloat(el && el.value) || 0;
            });
            session.scoresheet.breakdown[gid] = detail;
        }
        if (_roles) {
            const rsel = document.getElementById('role-guest-' + safeName);
            if (rsel && rsel.value) session.roles[gid] = rsel.value;
        }
        if (_timer) {
            const secs = Math.round(playerElapsedMs(guest.key) / 1000);
            if (secs > 0) { session.playerTimes = session.playerTimes || {}; session.playerTimes[gid] = secs; }
        }
    }

    if (session.participants.length === 0) { showNotification(t('sb.addParticipant'), 'error'); return; }

    /* RO: Rotita pe buton (varianta A): pornita DUPA toate validarile sincrone (club scris,
       permisiune, poze in comprimare, data+joc, cel putin un participant) si INAINTE de
       orice scriere. Tot ce urmeaza e inchis in `try/finally`, deci butonul revine la normal
       pe ORICE iesire, inclusiv pe `return`-ul de eroare al RPC-ului.
       ⚠ Butonul contine un `<span data-i18n>`; la resetare il RECONSTRUIM cu atributul intact,
       altfel o schimbare ulterioara de limba nu i-ar mai actualiza textul (i18n seteaza
       `textContent` pe `[data-i18n]`).
       EN: spinner after all sync validations, before any write; the try/finally below is the
       single reset point and restores the `data-i18n` span so language switching keeps working. */
    const _ssBtn = document.getElementById('sessionSaveBtn');
    const _ssSaveEsc = window.escHtml || function (s) { return s == null ? '' : String(s); };
    if (_ssBtn) { _ssBtn.disabled = true; _ssBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> ' + _ssSaveEsc(t('common.saving')); }
    const _ssResetBtn = function () {
        if (!_ssBtn) return;
        _ssBtn.disabled = false;
        _ssBtn.innerHTML = '<i class="fas fa-save"></i> <span data-i18n="session.save">' + _ssSaveEsc(t('session.save')) + '</span>';
    };

    try {

    if (isFriendly) {
        /* Sesiune amicală: scorurile se SALVEAZĂ ca atare, dar nu produc câștigători
           și nu afectează stats/scorurile jucătorilor (vezi mai jos + countWins + ELO). */
        session.winners = [];
    } else if (sessionType === 'coop') {
        /* Cooperativ: rezultat de echipă, fără clasament individual.
           Faza 1: câmpuri extinse (difficulty/detail) — backward-compatible. */
        const outcome = (document.getElementById('coopOutcome') || {}).value || 'win';
        const teamScore = (document.getElementById('coopScore') || {}).value;
        const teamScoreNum = (teamScore === '' || teamScore == null) ? null : Number(teamScore);
        const scenario = ((document.getElementById('coopScenario') || {}).value || '').trim();
        const difficulty = (document.getElementById('coopDifficulty') || {}).value || '';
        const difficultyLabel = ((document.getElementById('coopDifficultyLabel') || {}).value || '').trim();
        const detail = ((document.getElementById('coopDetail') || {}).value || '').trim();
        const mvpSel = (document.getElementById('coopMvp') || {}).value || '';
        /* MVP = evidențiere simbolică (metadată), fără impact pe puncte. Poate fi un
           participant înregistrat (validat) SAU un nume liber (ex. invitat) via „custom". */
        let mvp = null, mvpCustom = null;
        if (mvpSel === '__custom__') {
            mvpCustom = ((document.getElementById('coopMvpCustom') || {}).value || '').trim() || null;
        } else if (mvpSel && session.participants.indexOf(mvpSel) !== -1) {
            mvp = mvpSel;
        }
        session.coop = {
            outcome,
            teamScore: teamScoreNum,
            scenario: scenario || null,
            difficulty: difficulty || null,
            difficultyLabel: (difficulty === 'custom') ? (difficultyLabel || null) : null,
            detail: detail || null,
            mvp: mvp,
            mvpCustom: mvpCustom
        };
        /* Pe cluburile RAW (uniformizare inactivă/neselectată), scorul de echipă se
           adaugă egal tuturor jucătorilor — DOAR la victorie. Astfel co-op-ul contează
           în logica proprie a modului raw, fără să atingem uniformizarea. La înfrângere
           scorul rămâne doar metadată în istoric (session.coop.teamScore), fără puncte.
           Pe uniform, ramura de mai jos (Șablonul B) guvernează punctele. */
        const _teamToPoints = (scoreMode === 'raw' && outcome === 'win'
            && teamScoreNum != null && !isNaN(teamScoreNum) && teamScoreNum > 0);
        session.scores = {};
        session.participants.forEach(p => { session.scores[p] = _teamToPoints ? teamScoreNum : 0; });
        session.winners = outcome === 'win' ? session.participants.slice() : [];
    } else {
        /* Câștigători după direcția scorului (Z1-A). HIGH: scor maxim > 0 (istoric,
           neschimbat). LOW: scor minim câștigă (0/negativ valid). */
        const vals = Object.values(session.scores);
        if (_scoreDir === 'low') {
            const minScore = vals.length ? Math.min(...vals) : 0;
            session.winners = vals.length
                ? Object.entries(session.scores).filter(([, s]) => s === minScore).map(([n]) => n)
                : [];
        } else {
            const maxScore = vals.length ? Math.max(...vals) : 0;
            session.winners = maxScore > 0
                ? Object.entries(session.scores).filter(([, s]) => s === maxScore).map(([n]) => n)
                : [];
        }
    }

    /* Conversie în Puncte de Club (mod uniformizat). Scorurile brute RĂMÂN în
       session.scores; punctele de club merg separat în session.clubPoints. */
    session.scoreMode = scoreMode;
    session.scoreDir = _scoreDir;
    session.clubPoints = null;
    if (scoreMode === 'uniform') {
        const _tbRaw = (typeof sessionTieBreaks !== 'undefined' && sessionTieBreaks) ? sessionTieBreaks : {};
        /* Traducem cheile de interfata in identificatorii finali (vezi `_guestKeyToGid`).
           Pentru membrii inregistrati traducerea e identitate. */
        const _tb = {};
        Object.keys(_tbRaw).forEach(function (k) { _tb[_guestKeyToGid[k] || k] = _tbRaw[k]; });
        const _kindSel = (document.getElementById('sessionScoreKind') || {}).value;
        const _kind = (sessionType === 'coop') ? 'coop' : (_kindSel === 'campaign' ? 'campaign' : 'competitive');
        session.scoreKind = _kind;
        if (_kind === 'competitive' && Object.keys(_tb).length) session.tieBreaks = _tb;
        const conv = computeClubPoints(session.participants, session.scores, _kind, getScoringTemplates(), {
            coopWin: (session.coop && session.coop.outcome === 'win'),
            tieBreaks: _tb,
            scoreDir: _scoreDir,
            bonuses: session.scores /* la campanie: scorurile introduse = bonusuri individuale */
        });
        session.clubPoints = conv.points;
        session.winners = conv.winners; /* câștigătorii din clasarea uniformizată (respectă tie-break); campania -> [] */
    }

    /* Valoarea care intră în clasament per jucător: club points la uniform, brut altfel. */
    const _rankVal = pid => (scoreMode === 'uniform' && session.clubPoints) ? (Number(session.clubPoints[pid]) || 0) : (Number(session.scores[pid]) || 0);

    /* ---- Persistă ATOMIC; mută starea locală DOAR după succes ----
       Fără mutație optimistă înainte de scriere, fără fallback de coloane care
       degrada datele (riscul B), fără rollback manual: la eșec nu s-a mutat nimic. */
    if (isDemoMode()) {
        /* Demo: gueștii noi primesc id-uri locale (fără DB), apoi efecte locale. */
        const _demoMap = {};
        _newGuests.forEach(ng => {
            const did = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : ('guest-' + Date.now() + Math.random().toString(36).slice(2));
            _demoMap[ng.tmp_key] = did;
            _registerGuestInState(did, ng.name, ng.email);
        });
        _applyGuestMapToSession(session, _demoMap);
        _applySessionLocalEffects(session, _rankVal, isFriendly, gameName, timestamp, duration);
        _upsertLocalGame(session);
    } else {
        showNotification(t('sb.savingSession'), 'info');
        const _idemKey = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : ('idem-' + Date.now() + '-' + Math.random().toString(36).slice(2));

        /* (D6) Poze: încărcate ÎNAINTE de RPC, cheiate pe idem_key -> URL-urile intră
           în meta la insert (o singură scriere atomică, fără update post-insert care
           oricum ar fi blocat de RLS pentru moderatori). Eșecul de upload e NON-critic. */
        if (planAllows('photos')) {
            /* 9.3: într-o sesiune live pozele sunt DEJA în Storage (urcate la alegere, ca să
               supraviețuiască unui refresh și să le vadă toți). Ele vin gata, sub formă de
               căi. Aici mai urcăm doar ce a rămas local: sesiunile obișnuite, fără masă
               live, unde comportamentul e neschimbat.
               EN: live-session photos are already uploaded; only leftovers go up here. */
            const _already = (typeof sessionPhotoPaths !== 'undefined' && Array.isArray(sessionPhotoPaths))
                ? sessionPhotoPaths.slice() : [];
            let _fresh = [];
            if (typeof sessionPhotoFiles !== 'undefined' && sessionPhotoFiles && sessionPhotoFiles.length) {
                try {
                    showNotification(window.t('session.uploadingPhotos'), 'info');
                    _fresh = await uploadSessionPhotos(_idemKey, sessionPhotoFiles) || [];
                } catch (e) { logError('saveSession.photos', e); }
            }
            const _all = _already.concat(_fresh);
            if (_all.length) session.photos = _all;
        }

        const _payload = {
            game_title: gameName, date: timestamp,
            players: session.participants, scores: session.scores,
            winners: session.winners, club_points: session.clubPoints,
            meta: _buildSessionMeta(session), guests: _newGuests, idem_key: _idemKey
        };
        const _res = await sb.rpc('save_session_atomic', { p_club_id: currentClubId, p_payload: _payload });
        if (_res.error || !_res.data) {
            /* Nimic nu a fost mutat local -> nu există ce reversa. Eroare clară; DB-ul
               a făcut rollback complet (fără puncte fantomă, sesiune sau guest orfan). */
            logError('saveSession.rpc', _res.error);
            const _em = _res.error ? String(_res.error.message || '') : 'unknown';
            let _friendly = _em;
            if (/GUEST_EMAIL_REQUIRED/.test(_em)) _friendly = t('m1.guestEmailRequired');
            else if (/GUEST_EMAIL_DUP/.test(_em)) _friendly = t('m1.guestEmailDupSession');
            else if (/EMPTY_SESSION/.test(_em)) _friendly = t('sb.emptySession');
            showNotification(_friendly === _em ? (t('sb.saveSessionErrorColon') + _em) : _friendly, 'error');
            return;
        }
        /* Succes: reconciliază id-urile reale de guest + id-ul sesiunii, apoi efecte locale. */
        const _out = _res.data;
        const _gmap = _out.guest_map || {};
        _newGuests.forEach(ng => { const rid = _gmap[ng.tmp_key]; if (rid) _registerGuestInState(rid, ng.name, ng.email); });
        _applyGuestMapToSession(session, _gmap);
        session.id = _out.match_id;
        _applySessionLocalEffects(session, _rankVal, isFriendly, gameName, timestamp, duration);
        _upsertLocalGame(session);
    }

    addAuditLog('audit.sessionSaved', state.currentUser.name, { key: 'auditd.sessionParticipants', params: { game: gameName, n: session.participants.length } });

    /* §2 batch D: dacă sesiunea salvată era LIVE, o marcăm `saved` și îi legăm meciul.
       `ls_close` e IDEMPOTENT server-side: dacă e deja închisă, întoarce match_id-ul
       existent în loc să creeze altul. Împreună cu `matches.idem_key`, o dublă apăsare
       pe „Salvează" nu poate produce două meciuri.
       Notă: rulează DUPĂ salvare — dacă salvarea eșuează, funcția iese mai devreme și
       sesiunea live rămâne deschisă (corect: n-ai pierdut nimic). */
    try {
        const _lsId = window._liveSessionId;
        if (_lsId && typeof lsClose === 'function') {
            await lsClose(_lsId, 'saved', (session && session.id) ? session.id : null);
            window._liveSessionId = null;
            if (typeof _liveLastForget === 'function') _liveLastForget();   /* 9.2: masa s-a închis */
            if (typeof renderLiveSessionsBar === 'function') renderLiveSessionsBar();
        }
    } catch (e) { logError('saveSession.liveClose', e); }

    clearSessionForm();
    render();
    /* Self-save: ecoul realtime pe matches e ignorat (dedup), deci punctele reale
       (aplicate server-side, mai ales la uniformizat) nu s-ar re-sincroniza singure.
       Forțăm o re-sincronizare ieftină (skipHeavy: doar club_members/puncte + randări,
       fără istoric) ca punctele din profil/clasament să reflecte instant valoarea din DB. */
    try { _runDataRefresh(); } catch (e) { logError('saveSession.sync', e); }
    showNotification(t('sb.sessionSaved'), 'success');
    switchTab('dashboard');

    } finally {
        /* RO: singurul punct de resetare a butonului — acopera si iesirea de eroare a RPC-ului.
           EN: single reset point, covers the RPC error return too. */
        _ssResetBtn();
    }
}

async function deleteSession(sessionId) {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    if (typeof can !== 'function' || !can('match.delete')) { showNotification(t('rbac.noPermission'), 'error'); return; }
    if (!await confirmModal(t('m3.confirmDeleteSession'), { danger: true })) return;
    const idx = state.games.findIndex(g => String(g.id) === String(sessionId));
    if (idx === -1) return;
    const session = state.games[idx];

    const _delVal = pid => (session.scoreMode === 'uniform' && session.clubPoints) ? (Number(session.clubPoints[pid]) || 0) : (Number(session.scores && session.scores[pid]) || 0);

    /* ---- Persistă ATOMIC; mută starea locală DOAR după succes (riscul F) ----
       Fără mutație optimistă înainte de ștergere, fără _adjustPointsDB necondiționat:
       la eșec nu se mută nimic, deci zero desincronizare local↔DB. */
    if (!isDemoMode()) {
        const _isNumeric = session.id != null && /^[0-9]+$/.test(String(session.id));
        if (_isNumeric) {
            const _res = await sb.rpc('delete_session_atomic', { p_club_id: currentClubId, p_match_id: session.id });
            if (_res.error) {
                logError('deleteSession.rpc', _res.error);
                showNotification(t('m2.deleteErrorColon') + _res.error.message, 'error');
                return;  /* nimic mutat local -> sesiunea rămâne și local și în DB, punctele neatinse */
            }
            /* _res.data.deleted poate fi false (rând deja șters între timp) -> idempotent:
               tot curățăm starea locală ca să convergem. Punctele NU se reversază a doua oară
               (RPC-ul a sărit reversarea când rândul lipsea). */
        }
    }

    /* Succes (sau demo, sau sesiune locală netrimisă): aplică efectele locale. */
    const _i2 = state.games.indexOf(session);
    if (_i2 !== -1) state.games.splice(_i2, 1);
    if (session.scores && !session.friendly) {
        Object.keys(session.scores).forEach(pid => {
            const m = (typeof memberById === 'function') ? memberById(pid) : (state.membersById && state.membersById[pid]);
            if (m) {
                m.score = (m.score || 0) - _delVal(pid);
                m.gamesPlayed = Math.max(0, (m.gamesPlayed || 0) - 1);
            }
        });
    }
    if (session.gameName && state.catalog[session.gameName]) {
        state.catalog[session.gameName].timesPlayed = Math.max(0, (state.catalog[session.gameName].timesPlayed || 0) - 1);
    }
    /* Optimist↔hidratat: ține currentUser.points aliniat (per-club). */
    if (state.currentUser && state.currentUser.id) {
        const _cm = (typeof memberById === 'function') ? memberById(state.currentUser.id) : (state.membersById && state.membersById[state.currentUser.id]);
        if (_cm) state.currentUser.points = _cm.score;
    }

    addAuditLog('audit.sessionDeleted', state.currentUser.name, session.gameName);
    renderHistory(); renderDashboard(); renderPlayers(); renderCatalog();
    showNotification(t('m3.sessionDeleted'), 'success');
}

/* ---------- Căutare live BoardGameGeek (prin proxy Edge Function) ---------- */
let _bggTimer = null;

/* live: se cheamă pe măsură ce scrii (debounced) */
function bggSearchLive() {
    const inp = document.getElementById('bggSearchInput');
    const box = document.getElementById('bggResults');
    if (!inp || !box) return;
    const q = inp.value.trim();
    clearTimeout(_bggTimer);
    if (q.length < 3) {
        box.innerHTML = q.length ? `<p style="opacity:.6; font-size:.82em;">${window.t('bgg.typeMore')}</p>` : '';
        return;
    }
    box.innerHTML = `<p style="opacity:.7; font-size:.85em;">${window.t('bgg.searching')}</p>`;
    _bggTimer = setTimeout(() => bggRunSearch(q), 450);
}

/* manual: buton / Enter */
function bggSearch() {
    const inp = document.getElementById('bggSearchInput');
    if (!inp) return;
    const q = inp.value.trim();
    if (q.length < 1) return;
    clearTimeout(_bggTimer);
    bggRunSearch(q);
}

/* nucleul: interoghează proxy-ul și randează rezultatele ranked, cu miniaturi */
async function bggRunSearch(q) {
    const box = document.getElementById('bggResults');
    if (!box) return;
    try {
        const { data, error } = await sb.functions.invoke('bgg-proxy', { body: { type: 'search', q } });
        if (error) throw error;
        /* dacă utilizatorul a mai tastat între timp, ignorăm răspunsul vechi */
        const cur = document.getElementById('bggSearchInput');
        if (cur && cur.value.trim() !== q) return;
        const items = (data && data.items) || [];
        if (!items.length) { box.innerHTML = `<p style="opacity:.7; font-size:.85em;">${window.t('bgg.noResults')}</p>`; return; }
        box.innerHTML =
            `<style>.bgg-result-item:hover{background:rgba(255,107,53,0.14)!important;}</style>` +
            items.slice(0, 12).map(it => {
                const thumb = it.thumbnail
                    ? `<img src="${clubEsc(it.thumbnail)}" loading="lazy" style="width:36px;height:36px;object-fit:cover;border-radius:6px;flex:none;background:rgba(0,0,0,.2);">`
                    : `<span style="width:36px;height:36px;border-radius:6px;flex:none;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.2);">🎲</span>`;
                return `<a href="javascript:void(0)" data-catalog-act="bgg-pick" data-bgg-id="${clubEsc(String(it.id))}" class="bgg-result-item" style="display:flex; gap:10px; align-items:center; padding:7px 6px; border-bottom:1px solid var(--border,#333); text-decoration:none; color:var(--text-primary,#eee); border-radius:6px;">
                    ${thumb}
                    <span style="flex:1 1 auto; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${clubEsc(it.name)} ${it.year ? `<span style="opacity:.55; font-size:.85em;">(${it.year})</span>` : ''}</span>
                </a>`;
            }).join('');
    } catch (e) {
        box.innerHTML = `<p style="color:#e07a5f; font-size:.82em;">${window.t('bgg.searchUnavailable')} <br><span style="opacity:.7;">${clubEsc((e && e.message) || '')}</span></p>`;
    }
}
window.bggSearchLive = bggSearchLive;

async function bggPick(id) {
    const box = document.getElementById('bggResults');
    if (box) box.innerHTML = `<p style="opacity:.7; font-size:.85em;">${window.t('bgg.fetchingDetails')}</p>`;
    try {
        const { data, error } = await sb.functions.invoke('bgg-proxy', { body: { type: 'thing', id } });
        if (error) throw error;
        const g = data && data.item;
        if (!g) { if (box) box.innerHTML = '<p style="opacity:.7; font-size:.85em;">' + window.t('bgg.noDetails') + '</p>'; return; }
        const set = (eid, val) => { const el = document.getElementById(eid); if (el && val != null && val !== '') el.value = val; };
        set('newGameName', g.name);
        if (g.minPlayers && g.maxPlayers) set('newGamePlayers', g.minPlayers === g.maxPlayers ? String(g.minPlayers) : (g.minPlayers + '-' + g.maxPlayers));
        if (g.playingTime) set('newGameDuration', g.playingTime);
        if (g.weight) { const sel = document.getElementById('newGameDifficulty'); if (sel) sel.value = String(Math.min(5, Math.max(1, Math.round(g.weight)))); }
        if (g.rating) set('newGameRating', Math.round(g.rating * 10) / 10);
        set('newGameBggId', String(g.id));
        if (g.categories && g.categories.length) set('newGameCategories', g.categories.join(', '));
        /* descriere (originalul în engleză din BGG) */
        if (g.description) set('newGameDescription', g.description);
        /* bifează automat categoriile potrivite (inclusiv aproximativ), din categorii+mecanici+subdomenii BGG */
        try {
            const pools = [].concat(g.categories || [], g.mechanics || [], g.subdomains || []).map(x => String(x).toLowerCase());
            const hay = ' ' + pools.join(' | ') + ' ';
            const CATMAP = {
                'eurogame': ['eurogame'],
                'ameritrash': ['ameritrash', 'thematic', 'wargame', 'miniatures'],
                'party': ['party'],
                'cooperativ': ['cooperative', 'co-operative'],
                'strategie': ['strategy'],
                'filler': ['filler'],
                'legacy': ['legacy'],
                'deck building': ['deck'],
                'rpg': ['role playing', 'rpg'],
                'worker placement': ['worker placement'],
                'engine building': ['engine building']
            };
            document.querySelectorAll('.game-category').forEach(cb => {
                const kws = CATMAP[String(cb.value || '').toLowerCase()];
                if (kws && kws.some(k => hay.includes(k))) cb.checked = true;
            });
        } catch (_) { logError('bggPick', _); }
        const img = document.getElementById('newGameImageUrl'); if (img) img.value = g.image || g.thumbnail || '';
        if (g.minAge || g.minage) set('newGameAge', g.minAge || g.minage);
        set('newGameDesigner', Array.isArray(g.designers) ? g.designers.join(', ') : g.designer);
        set('newGameArtist', Array.isArray(g.artists) ? g.artists.join(', ') : g.artist);
        set('newGamePublisher', Array.isArray(g.publishers) ? g.publishers.join(', ') : g.publisher);
        if (g.thumbnail || g.image || g.minAge || g.designer || g.artist || g.publisher) { const more = document.getElementById('addGameMore'); if (more) more.classList.remove('hidden'); }
        if (box) {
            const pic = g.thumbnail || g.image;
            box.innerHTML = `<div style="display:flex; gap:10px; align-items:center; padding:8px 0;">
                ${pic ? `<img src="${clubEsc(pic)}" style="width:54px;height:54px;object-fit:cover;border-radius:8px; flex:none;">` : ''}
                <div><div style="font-weight:600;">${clubEsc(g.name)} ${g.year ? '(' + g.year + ')' : ''}</div>
                <div style="font-size:.8em; opacity:.7;">${window.t('bgg.filledFromBgg')}</div></div></div>`;
        }
        /* ediții / versiuni (opțional) — setează eticheta + coperta corectă */
        try {
            const vr = await sb.functions.invoke('bgg-proxy', { body: { type: 'versions', id } });
            renderBggVersions((vr.data && vr.data.versions) || []);
        } catch (_) { logError('bggPick', _); }
    } catch (e) {
        if (box) box.innerHTML = `<p style="color:#e07a5f; font-size:.82em;">Nu am putut aduce detaliile. ${clubEsc((e && e.message) || '')}</p>`;
    }
}

/* selector ediții din BGG, cu căutare; alegerea setează newGameEdition + coperta */
function renderBggVersions(versions) {
    const box = document.getElementById('bggResults');
    window._bggVersions = versions || [];
    const old = document.getElementById('bggVersionWrap');
    if (old) old.remove();
    if (!box || !versions || !versions.length) return;
    const html = '<div id="bggVersionWrap" style="margin-top:8px;">' +
        '<label for="bggVersionFilter" style="font-size:.8em; opacity:.85; display:block; margin-bottom:4px;">' + window.t('game.editionLabel') + ' (' + versions.length + ')</label>' +
        '<input type="text" id="bggVersionFilter" class="form-control" autocomplete="off" placeholder="' + window.t('game.editionSearch') + '" data-catalog-field="bgg-ver" style="margin-bottom:6px;">' +
        '<div id="bggVersionList" style="max-height:190px; overflow:auto; border:1px solid var(--border,#333); border-radius:8px;"></div>' +
        '</div>';
    box.insertAdjacentHTML('beforeend', html);
    filterBggVersions();
}
function filterBggVersions() {
    const list = document.getElementById('bggVersionList');
    if (!list) return;
    const q = (((document.getElementById('bggVersionFilter') || {}).value) || '').trim().toLowerCase();
    const versions = window._bggVersions || [];
    const parts = q ? q.split(/\s+/) : [];
    const filtered = versions
        .map(function (v, i) { return { v: v, i: i }; })
        .filter(function (o) {
            if (!parts.length) return true;
            const hay = ((o.v.name || '') + ' ' + (o.v.language || '') + ' ' + (o.v.year || '')).toLowerCase();
            return parts.every(function (p) { return hay.indexOf(p) !== -1; });
        })
        .slice(0, 60);
    if (!filtered.length) { list.innerHTML = '<div style="padding:8px; opacity:.6; font-size:.85em;">' + window.t('bgg.noResults') + '</div>'; return; }
    list.innerHTML = '<style>.bggver-item:hover{background:rgba(255,107,53,0.12);}</style>' + filtered.map(function (o) {
        const v = o.v;
        const thumb = v.thumbnail
            ? '<img src="' + clubEsc(v.thumbnail) + '" loading="lazy" style="width:30px;height:30px;object-fit:cover;border-radius:5px;flex:none;background:rgba(0,0,0,.2);">'
            : '<span style="width:30px;height:30px;border-radius:5px;flex:none;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.2);font-size:.8em;">🎲</span>';
        const meta = [v.year, v.language].filter(Boolean).join(' · ');
        return '<a href="javascript:void(0)" data-catalog-act="bgg-ver" data-i="' + o.i + '" class="bggver-item" style="display:flex; gap:8px; align-items:center; padding:6px 8px; text-decoration:none; color:var(--text-primary,#eee); border-bottom:1px solid var(--border,#333);">' +
            thumb + '<span style="flex:1 1 auto; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">' +
            clubEsc(v.name || ('#' + v.id)) + (meta ? ' <span style="opacity:.55; font-size:.85em;">(' + clubEsc(meta) + ')</span>' : '') +
            '</span></a>';
    }).join('');
}
function bggPickVersion(idx) {
    const versions = window._bggVersions || [];
    const v = versions[parseInt(idx, 10)];
    if (!v) return;
    const ed = document.getElementById('newGameEdition');
    const img = document.getElementById('newGameImageUrl');
    if (ed) ed.value = v.name || '';
    if (img && (v.image || v.thumbnail)) img.value = v.image || v.thumbnail;
    const filt = document.getElementById('bggVersionFilter');
    if (filt) filt.value = v.name || '';
    if (typeof showNotification === 'function') showNotification(window.t('game.editionSet') + ': ' + (v.name || ''), 'success');
}
window.renderBggVersions = renderBggVersions;
window.filterBggVersions = filterBggVersions;
window.bggPickVersion = bggPickVersion;

/* ---------- Catalog jocuri (tabela games) ---------- */
/* Persistă ratingul MEU printr-un RPC atomic.
   ⚠ Înainte scria ÎNTREAGA hartă `rating_votes` din memorie -> doi oameni care
   dădeau rating simultan se suprascriau reciproc (voturi pierdute). Acum RPC-ul
   aplică un DELTA doar pe cheia mea, server-side.
   Bonus: RPC-ul permite ORICE membru (rang>=1) să voteze, fără să-i dea drept de
   UPDATE pe `games` (care i-ar permite să editeze titlu/imagine/descriere). */
async function persistGameRating(gameName) {
    try {
        if (typeof isDemoMode === 'function' && isDemoMode()) return;
        const g = state.catalog[gameName];
        if (!g || !g.dbId || typeof sb === 'undefined' || !sb) return;
        const me = state.currentUser && state.currentUser.id;
        if (!me) return;
        const mine = (g.ratingVotes && g.ratingVotes[me]) || null;
        if (!mine || typeof mine.r !== 'number') return;

        const res = await sb.rpc('game_rate', {
            p_club_id: currentClubId,
            p_game_id: g.dbId,
            p_r: mine.r,
            p_w: (typeof mine.w === 'number' ? mine.w : null)
        });
        if (res.error) { showNotification(res.error.message, 'error'); return; }

        /* DB-first: harta autoritară vine din server (conține și voturile altora,
           inclusiv pe cele apărute între timp). */
        if (res.data && typeof res.data === 'object') {
            g.ratingVotes = res.data;
            const ra = gameRatingAvg(g); if (ra != null) g.rating = Math.round(ra * 10) / 10;
            const wa = gameWeightAvg(g); if (wa != null) g.difficulty = Math.round(wa * 100) / 100;
            if (typeof renderCatalog === 'function') renderCatalog();
        }
    } catch (e) { console.warn('persistGameRating:', e && e.message); }
}
window.persistGameRating = persistGameRating;

async function saveGameEdits(originalName) {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    const game = state.catalog[originalName];
    if (!game) return;
    /* NU adăuga `|| isAdmin` aici: `isAdmin` e un flag GLOBAL (profiles.role), nu rolul
       din CLUB. Un „admin global" care e simplu membru în club trecea de gardă și primea
       o eroare RLS brută. Capabilitățile (`can`) sunt club-scoped => sursa de adevăr. */
    if (typeof can === 'function' && !can('catalog.manage')) { showNotification(t('rbac.noPermission'), 'error'); return; }

    const players = (document.getElementById('newGamePlayers').value || '').trim();
    const duration = parseInt(document.getElementById('newGameDuration').value) || game.duration || 60;
    const difficulty = parseInt(document.getElementById('newGameDifficulty').value) || game.baseDifficulty || 3;
    const ratingRaw = parseFloat(document.getElementById('newGameRating').value);
    const rating = isNaN(ratingRaw) ? null : Math.min(10, Math.max(1, ratingRaw));
    const description = (document.getElementById('newGameDescription').value || '').trim();
    const bggRaw = (document.getElementById('newGameBggId') ? document.getElementById('newGameBggId').value : '').trim();
    const bggParsed = bggRaw ? parseBggInput(bggRaw) : null;
    const bggId = bggParsed ? bggParsed.id : (bggRaw || null);
    const imageUrl = (document.getElementById('newGameImageUrl') ? document.getElementById('newGameImageUrl').value : '').trim() || null;
    const minAge = parseInt((document.getElementById('newGameAge') || {}).value) || null;
    const designer = ((document.getElementById('newGameDesigner') || {}).value || '').trim() || null;
    const artist = ((document.getElementById('newGameArtist') || {}).value || '').trim() || null;
    const publisher = ((document.getElementById('newGamePublisher') || {}).value || '').trim() || null;
    const edition = ((document.getElementById('newGameEdition') || {}).value || '').trim() || null;
    const _sdEl2 = document.getElementById('newGameScoreDir');
    const scoreDir = (_sdEl2 && _sdEl2.value === 'low') ? 'low' : 'high';
    const cbs = document.querySelectorAll('.game-category:checked');
    let categories = Array.from(cbs).map(cb => cb.value);
    const custom = ((document.getElementById('newGameCategories').value) || '').split(',').map(c => c.trim()).filter(Boolean);
    categories = [...categories, ...custom];
    if (!categories.length) categories = ['Altele'];

    let min = null, max = null;
    const range = players.match(/(\d+)\s*-\s*(\d+)/);
    if (range) { min = parseInt(range[1]); max = parseInt(range[2]); }
    else { const one = players.match(/(\d+)/); if (one) { min = parseInt(one[1]); max = parseInt(one[1]); } }

    /* persistă în DB ÎNTÂI; aplică local DOAR la succes (fără desync) */
    if (!isDemoMode() && game.dbId) {
        const payloadFull = {
            min_players: min, max_players: max, duration, complexity: difficulty,
            category: categories.join(', '), description: description || null,
            image_url: imageUrl, bgg_id: bggId, base_rating: rating,
            min_age: minAge, designer: designer, artist: artist, publisher: publisher, edition: edition, score_dir: scoreDir
        };
        let upd = await sb.from('games').update(payloadFull).eq('id', game.dbId);
        if (upd.error) {
            /* poate lipsesc coloanele noi -> reîncearcă doar cu cele de bază */
            upd = await sb.from('games').update({
                min_players: min, max_players: max, duration, complexity: difficulty,
                category: categories.join(', '), description: description || null, image_url: imageUrl
            }).eq('id', game.dbId);
        }
        if (upd.error) {
            logError('saveGameEdits', upd.error);
            showNotification(t('sb.saveChangesErrorColon') + upd.error.message, 'error');
            return;
        }
    }

    /* actualizare locală DOAR după succes (sau în demo) */
    game.players = players || game.players;
    if (min) game.minPlayers = min; if (max) game.maxPlayers = max;
    game.duration = duration; game.baseDifficulty = difficulty;
    if (rating != null) game.baseRating = rating;
    game.categories = categories; game.description = description;
    game.imageUrl = imageUrl; game.minAge = minAge; game.designer = designer;
    game.artist = artist; game.publisher = publisher; game.bggId = bggId; game.edition = edition;
    game.scoreDir = scoreDir;
    const wa = gameWeightAvg(game); if (wa != null) game.difficulty = Math.round(wa * 100) / 100;
    const ra = gameRatingAvg(game); if (ra != null) game.rating = Math.round(ra * 10) / 10;

    renderCatalog();
    const modal = document.querySelector('.modal.active'); if (modal) modal.remove();
    if (typeof addAuditLog === 'function') addAuditLog('audit.gameEditedCatalog', state.currentUser.name, originalName);
    showNotification(t('sb.gameUpdated').replace('{name}', originalName), 'success');
}
window.saveGameEdits = saveGameEdits;

async function addNewGame() {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    /* NU adăuga `|| isAdmin` aici: `isAdmin` e un flag GLOBAL (profiles.role), nu rolul
       din CLUB. Un „admin global" care e simplu membru în club trecea de gardă și primea
       o eroare RLS brută. Capabilitățile (`can`) sunt club-scoped => sursa de adevăr. */
    if (typeof can === 'function' && !can('catalog.manage')) { showNotification(t('rbac.noPermission'), 'error'); return; }
    let name = document.getElementById('newGameName').value.trim();
    const players = document.getElementById('newGamePlayers').value.trim();
    const duration = parseInt(document.getElementById('newGameDuration').value) || 60;
    const difficulty = parseInt(document.getElementById('newGameDifficulty').value) || 3;
    const rating = parseFloat((document.getElementById('newGameRating') || {}).value) || 7.0;
    const description = document.getElementById('newGameDescription').value.trim();
    const bggRaw = (document.getElementById('newGameBggId') ? document.getElementById('newGameBggId').value : '').trim();
    const bggParsed = bggRaw ? parseBggInput(bggRaw) : null;
    const bggId = bggParsed ? bggParsed.id : null;
    const imageUrl = (document.getElementById('newGameImageUrl') ? document.getElementById('newGameImageUrl').value : '').trim() || null;
    const minAge = parseInt((document.getElementById('newGameAge') || {}).value) || null;
    const designer = ((document.getElementById('newGameDesigner') || {}).value || '').trim() || null;
    const artist = ((document.getElementById('newGameArtist') || {}).value || '').trim() || null;
    const publisher = ((document.getElementById('newGamePublisher') || {}).value || '').trim() || null;
    const edition = ((document.getElementById('newGameEdition') || {}).value || '').trim() || null;
    const _sdEl = document.getElementById('newGameScoreDir');
    const scoreDir = (_sdEl && _sdEl.value === 'low') ? 'low' : 'high';
    if (!name && bggParsed && bggParsed.title) name = bggParsed.title; /* completează numele din link */
    const categoryCheckboxes = document.querySelectorAll('.game-category:checked');
    let categories = Array.from(categoryCheckboxes).map(cb => cb.value);
    const custom = (document.getElementById('newGameCategories').value || '').split(',').map(c => c.trim()).filter(Boolean);
    categories = [...categories, ...custom];

    if (!name || !players) { showNotification(t('m1.fillNamePlayers'), 'error'); return; }
    if (state.catalog[name]) { showNotification(t('m1.gameInCatalog'), 'error'); return; }

    let min = null, max = null;
    const range = players.match(/(\d+)\s*-\s*(\d+)/);
    if (range) { min = parseInt(range[1]); max = parseInt(range[2]); }
    else { const one = players.match(/(\d+)/); if (one) { min = parseInt(one[1]); max = parseInt(one[1]); } }

    const entry = {
        dbId: null, categories: categories.length ? categories : ['Altele'],
        players, minPlayers: min, maxPlayers: max, duration, difficulty,
        rating: 0, baseRating: rating, baseDifficulty: difficulty, ratingVotes: {},
        description: description || '', imageUrl: imageUrl,
        minAge: minAge, designer: designer, artist: artist, publisher: publisher, edition: edition,
        scoreDir: scoreDir,
        owner: state.currentUser.name, bggId: bggId,
        timesPlayed: 0, actualDurations: [], avgDuration: null, lastPlayed: null,
        addedBy: state.currentUser.name, addedAt: new Date().toISOString()
    };
    /* Persistă ÎNTÂI; adaugă în catalogul local DOAR după succes (fără desync). */
    if (!isDemoMode()) {
        let ins = await sb.from('games').insert({ club_id: currentClubId,
            title: name, min_players: min, max_players: max, duration,
            complexity: difficulty, category: entry.categories.join(', '),
            description: description || null, owner: state.currentUser.name, bgg_id: bggId,
            image_url: imageUrl, base_rating: rating, rating_votes: {},
            min_age: minAge, designer: designer, artist: artist, publisher: publisher, edition: edition, score_dir: scoreDir
        }).select().maybeSingle();
        if (ins.error) {
            /* poate lipsesc coloanele noi (base_rating/rating_votes/bgg_id) -> reîncearcă fără ele */
            ins = await sb.from('games').insert({ club_id: currentClubId,
                title: name, min_players: min, max_players: max, duration,
                complexity: difficulty, category: entry.categories.join(', '),
                description: description || null, owner: state.currentUser.name,
                image_url: imageUrl
            }).select().maybeSingle();
        }
        if (ins.error) {
            /* Eșec (ex. RLS: doar Moderator+ poate adăuga jocuri). NU adăugăm local
               și NU arătăm succes -> fără desincronizare catalog↔DB. */
            logError('addNewGame', ins.error);
            showNotification(t('sb.addGameErrorColon') + ins.error.message, 'error');
            return;
        }
        if (ins.data) entry.dbId = ins.data.id;
    }

    state.catalog[name] = entry;   /* local DOAR după succes (sau în demo) */
    renderCatalog();
    const modal = document.querySelector('.modal.active'); if (modal) modal.remove();
    addAuditLog('audit.gameAddedCatalog', state.currentUser.name, name);
    showNotification(t('m1.gameAddedCatalog').replace('{name}', name), 'success');
    /* dacă s-a deschis din tab-ul de sesiune, selectează automat jocul nou (rapiditate) */
    if (window._addGameReturnToSession) {
        window._addGameReturnToSession = false;
        if (typeof pickSessionGame === 'function') pickSessionGame(name);
    }
}

async function deleteGameFromCatalog(gameName) {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    /* `catalog.delete` (rang 3), nu `isAdmin`: acelaşi public, dar dintr-o sursă
       neambiguă (rolul din clubul ACTIV) şi identică cu politica RLS
       `games_delete_min_admin`. */
    if (typeof can === 'function' ? !can('catalog.delete') : !isAdmin) { showNotification(t('m1.onlyAdminDeleteGames'), 'error'); return; }
    if (!await confirmModal(t('m1.confirmDeleteGame').replace('{game}', gameName), { danger: true })) return;
    const game = state.catalog[gameName];
    if (!game) return;

    /* șterge din DB ÎNTÂI; curăță local DOAR la succes (fără desync) */
    if (!isDemoMode() && game.dbId) {
        const del = await sb.from('games').delete().eq('id', game.dbId);
        if (del.error) {
            logError('deleteGameFromCatalog', del.error);
            showNotification(t('m2.deleteErrorColon') + del.error.message, 'error');
            return;
        }
    }

    delete state.catalog[gameName];
    Object.values(state.members).forEach(m => {
        if (m.preferredGames) m.preferredGames = m.preferredGames.filter(g => g !== gameName);
    });

    saveState();
    renderCatalog();
    addAuditLog('audit.gameDeletedCatalog', state.currentUser.name, gameName);
    showNotification(t('m1.gameDeleted').replace('{name}', gameName), 'success');
}

/* ---------- Jucători (tabela profiles) ---------- */
async function addPlayer() {
    if (!requireWritableClub()) return;
    if (!requireMemberSlot()) return;
    const name = document.getElementById('newPlayerName').value.trim();
    const email = document.getElementById('newPlayerEmail').value.trim();
    let userTag = (document.getElementById('newPlayerTag') ? document.getElementById('newPlayerTag').value : '').trim().replace(/^@+/, '').toLowerCase();
    if (!name) { showNotification(t('m1.enterPlayerName'), 'error'); return; }
    /* Email OBLIGATORIU: e ancora de identitate (claim + dedup). */
    if (!email) { showNotification(t('players.emailRequired'), 'error'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { showNotification(t('players.emailInvalid'), 'error'); return; }
    if (userTag && !/^[a-z0-9._]{3,30}$/.test(userTag)) { showNotification(t('players.tagInvalid'), 'error'); return; }

    /* Demo: adăugare doar locală (fără DB). */
    if (isDemoMode()) {
        const newId = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : ('local-' + Date.now());
        const mo = {
            dbId: newId, name, displayName: name, role: 'member', userTag: userTag || '',
            score: 0, gamesPlayed: 0, hidden: false, email: email || null,
            joinDate: new Date().toISOString().split('T')[0], preferredGames: [], skillLevel: 3
        };
        state.members[name] = mo;
        if (!state.membersById) state.membersById = {};
        state.membersById[newId] = mo;
        if (state.profileNamesById) state.profileNamesById[newId] = name;
        document.getElementById('newPlayerName').value = '';
        document.getElementById('newPlayerEmail').value = '';
        if (document.getElementById('newPlayerTag')) document.getElementById('newPlayerTag').value = '';
        renderPlayers();
        showNotification(t('m1.playerAdded').replace('{name}', name), 'success');
        return;
    }

    /* Real: profil garantat în DB prin RPC (dedup pe email -> leagă cont existent,
       creează profil + legătură club_members, ocolește RLS). */
    showNotification(t('players.adding'), 'info');
    /* ⚠ Retinem cine e membru ACUM, ca sa putem deosebi „am adaugat pe cineva nou" de
       „era deja acolo, nu s-a intamplat nimic". Functia din baza de date intoarce acelasi
       lucru in ambele cazuri — id-ul profilului — fiindca dedubleaza pe email
       (`profiles_email_unique` pe `lower(email)`) si apoi face
       `insert into club_members ... on conflict (club_id, profile_id) do nothing`.
       Fara verificarea asta, adminul primea „jucator adaugat" desi nu se schimbase nimic,
       iar numele pe care tocmai il scrisese era ignorat in tacere.
       ⚠ `state.membersById` contine EXACT membrii activi ai clubului curent (hidratarea
       filtreaza `.eq('status','active')`), iar iesirile din club STERG randul din
       `club_members` — deci nu exista membri „ascunsi" care sa strice comparatia.
       EN: the RPC returns a profile id whether it added someone or found an existing
       member; snapshot the current members so we can tell the two apart. */
    const _apMembriInainte = Object.assign({}, (state && state.membersById) || {});
    const res = await sb.rpc('admin_add_guest', { p_club_id: currentClubId, p_name: name, p_email: email, p_user_tag: userTag || null });
    if (res.error || !res.data) {
        const em = (res.error && res.error.message) || '';
        /* ★ Adresa aparține unui om care are deja cont: nu-l băgăm în club fără
           acordul lui. Îi oferim adminului calea corectă — linkul de invitație. */
        if (/TARGET_HAS_ACCOUNT/i.test(em)) { paOfferInviteInstead(name); return; }
        const msg = /MEMBER_LIMIT_REACHED/i.test(em) ? t('players.clubFullAdd')
            : /user_tag_taken|duplicate|unique|23505/i.test(em) ? t('players.tagTaken')
            : /forbidden/.test(em) ? t('rbac.noPermission')
                : (t('players.addErrorColon') + (em || t('common.unknownF')));
        showNotification(msg, 'error');
        return;
    }

    /* Era deja membru? Atunci nu s-a adaugat nimeni: spunem asta si NU golim campurile,
       ca adminul sa vada ce a introdus si sa poata corecta adresa daca a gresit-o. */
    const _apEraDejaMembru = !!_apMembriInainte[res.data];
    if (_apEraDejaMembru) {
        const _numeExistent = (state.membersById && state.membersById[res.data]
            && (state.membersById[res.data].displayName || state.membersById[res.data].name)) || name;
        showNotification(t('players.alreadyMember').replace('{name}', _numeExistent), 'info');
        return;
    }

    document.getElementById('newPlayerName').value = '';
    document.getElementById('newPlayerEmail').value = '';
    if (document.getElementById('newPlayerTag')) document.getElementById('newPlayerTag').value = '';
    await hydrateFromSupabase();
    /* ⚠ Reimprospatare FORTATA a celor doua harti de stare, INAINTE de randare.
       Ambele au garda `if (!force && harta.clubId === currentClubId) return;`. Clubul NU
       s-a schimbat, deci fara `true` reincarcarea se sare, iar hartile nu contin profilul
       nou. Consecinta vazuta de utilizator: butoanele de invitatie / link de activare NU
       apar decat dupa un refresh de pagina — la refresh hartile pornesc goale si se umplu.
       `paRefreshActivationStatus` decide butoanele din lista de jucatori; `refreshAccountStatus`
       decide daca in fereastra de profil apare butonul „adauga prieten" pentru cineva fara cont.
       ⚠ `await`, nu apel liber: randarea trebuie sa vada datele NOI, altfel nu rezolvam nimic.
       Costul: doua cereri in plus, pe o actiune manuala si rara. `paRefreshActivationStatus`
       are deja garda de rol, deci pentru neadmini nici nu pleaca.
       Tiparul e cel folosit deja de `paIssueLinkFor` / `paRevokeLinkFor`.
       EN: both status maps are keyed by club and skip reloading when the club is unchanged;
       after adding a member they must be forced, before rendering. */
    try { if (typeof paRefreshActivationStatus === 'function') await paRefreshActivationStatus(true); } catch (e) { logError('addPlayer.paStatus', e); }
    try { if (typeof refreshAccountStatus === 'function') await refreshAccountStatus(true); } catch (e) { logError('addPlayer.acctStatus', e); }
    renderPlayers();
    addAuditLog('audit.playerAdded', state.currentUser.name, name);
    showNotification(t('m1.playerAdded').replace('{name}', name), 'success');
}

async function editPlayer(playerName) {
    if (!isAdmin) { showNotification(t('m1.onlyAdminEditPlayers'), 'error'); return; }
    if (!((state.membersById && state.membersById[playerName]) || state.members[playerName])) return;
    openEditScoreModal(playerName);
}

function openEditScoreModal(playerName) {
    const player = (state.membersById && state.membersById[playerName]) || state.members[playerName];
    if (!player) return;
    const dispName = player.name || playerName;
    const existing = document.getElementById('editScoreModal'); if (existing) existing.remove();
    const av = (typeof avatarHtml === 'function') ? avatarHtml(player.avatar, 48) : '';
    const cur = Number(player.score) || 0;
    const modal = document.createElement('div');
    modal.className = 'modal active'; modal.id = 'editScoreModal';
    modal.dataset.back = '1';   /* „înapoi" de pe telefon o închide (chat-core.js, v9 zona 2) */
    modal.onclick = e => { if (e.target === modal) modal.remove(); };
    modal.innerHTML = `
        <div class="modal-content" style="max-width:400px;">
            <div class="modal-header"><h2><i class="fas fa-pen"></i> ${t('players.editScoreTitle')}</h2><button class="modal-close" data-pl-act="close" aria-label="${t('common.close')}">×</button></div>
            <div class="es-head">
                <div class="es-avatar">${av}</div>
                <div><div class="es-name">${window.escHtml(dispName)}</div><div class="es-cur">${t('players.currentScore')}: <b>${cur.toFixed(2)}</b></div></div>
            </div>
            <label for="esScoreInput" class="es-label">${t('players.newScore')}</label>
            <div class="es-input-row">
                <button type="button" class="es-step" data-pl-act="es-adj" data-d="-1">−1</button>
                <button type="button" class="es-step" data-pl-act="es-adj" data-d="-0.5">−0.5</button>
                <input type="number" step="0.5" id="esScoreInput" class="form-control" value="${cur}">
                <button type="button" class="es-step" data-pl-act="es-adj" data-d="0.5">+0.5</button>
                <button type="button" class="es-step" data-pl-act="es-adj" data-d="1">+1</button>
            </div>
            <div style="display:flex; gap:8px; margin-top:16px;">
                <button class="btn" style="flex:1;" data-pl-act="es-save" data-id="${window.escHtml(playerName)}"><i class="fas fa-check"></i> ${t('common.save')}</button>
                <button class="btn btn-secondary" style="flex:1;" data-pl-act="close">${t('common.cancel')}</button>
            </div>
        </div>`;
    document.body.appendChild(modal);
    setTimeout(() => { const i = document.getElementById('esScoreInput'); if (i) { i.focus(); i.select(); } }, 30);
}
function esAdjust(delta) {
    const i = document.getElementById('esScoreInput'); if (!i) return;
    const v = (parseFloat(i.value) || 0) + delta;
    i.value = (Math.round(v * 100) / 100);
}
async function saveEditScore(playerName) {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    if (typeof can !== 'function' || !can('match.edit')) { showNotification(t('rbac.noPermission'), 'error'); return; }
    const player = (state.membersById && state.membersById[playerName]) || state.members[playerName];
    if (!player) return;
    const i = document.getElementById('esScoreInput'); if (!i) return;
    const score = parseFloat(i.value);
    if (isNaN(score)) { showNotification(t('m1.invalidScore'), 'error'); return; }
    const _oldScore = Number(player.score) || 0;
    /* Persistă ÎNTÂI; mută local DOAR după succes (fără desincronizare la eroare). */
    if (!isDemoMode() && player.dbId) {
        const _err = await _adjustPointsDB({ [player.dbId]: score - _oldScore });
        if (_err) { showNotification(t('sb.errorColon') + (_err.message || ''), 'error'); return; }
    }
    player.score = score;
    if (state.currentUser && state.currentUser.id === player.dbId) state.currentUser.points = score;
    const modal = document.getElementById('editScoreModal'); if (modal) modal.remove();
    addAuditLog('audit.playerEdited', state.currentUser.name, { key: 'auditd.playerScoreArrow', params: { name: player.name || playerName, score: score } });
    renderPlayers(); renderDashboard();
    showNotification(t('players.scoreUpdated'), 'success');
}
window.openEditScoreModal = openEditScoreModal;
window.esAdjust = esAdjust;
window.saveEditScore = saveEditScore;

async function deletePlayer(playerName) {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    const player = (state.membersById && state.membersById[playerName]) || state.members[playerName];
    if (!player) return;
    const pname = player.name || playerName;
    if (!requireWritableClub()) return;
    if (!await confirmModal(t('sb.confirmRemovePlayerClub').replace('{name}', pname), { danger: true })) return;
    if (!await requireReauth('player.delete')) return;
    /* Curățare omonim-safe: ștergem pe ID din membersById, iar din harta pe nume
       doar dacă pointează exact către ACEST membru (nu către un omonim). */
    if (player.dbId && state.membersById) delete state.membersById[player.dbId];
    if (state.members[pname] === player) delete state.members[pname];
    if (player.email && state.users[player.email] && state.users[player.email].id === player.dbId) delete state.users[player.email];
    if (player.dbId && state.users['id:' + player.dbId]) delete state.users['id:' + player.dbId];

    if (!isDemoMode() && player.dbId) {
        const del = await sb.from('club_members').delete()
            .eq('club_id', currentClubId).eq('profile_id', player.dbId);
        if (del.error) showNotification(t('sb.removeFromClubErrorColon') + del.error.message, 'error');
    }
    addAuditLog('audit.playerRemovedFromClub', state.currentUser.name, pname);
    renderPlayers(); renderDashboard();
    showNotification(`${pname} a fost scos din club.`, 'success');
}

/* ---------- Administrare (tabela profiles: role / points) ---------- */
/* ============================================================
   RBAC — Badge de rol + Ecran de gestiune comunitate (Nivel 1 & 2)
   ============================================================ */

/* Etichetă vizuală colorată subtil pentru rolul unui membru (liste & chat). */
function roleBadgeHtml(role) {
    const map = {
        principal_admin: { bg: 'rgba(212,160,23,0.18)', fg: '#d4a017', icon: 'fa-crown' },
        secondary_admin: { bg: 'rgba(52,120,246,0.18)', fg: '#5b9cff', icon: 'fa-user-shield' },
        moderator:       { bg: 'rgba(39,174,96,0.18)',  fg: '#2ecc71', icon: 'fa-gavel' },
        member:          { bg: 'rgba(255,255,255,0.10)', fg: 'var(--text-secondary,#aaa)', icon: 'fa-user' }
    };
    const s = map[role] || map.member;
    return `<span style="display:inline-flex; align-items:center; gap:5px; font-size:0.72em; font-weight:600; background:${s.bg}; color:${s.fg}; border-radius:8px; padding:2px 8px; white-space:nowrap;"><i class="fas ${s.icon}"></i> ${clubRoleLabel(role)}</span>`;
}
/* Badge după nume (folosit în chat); întoarce '' pentru membru simplu, ca să nu aglomereze. */
/* Ecran de gestiune comunitate — override peste versiunea din app-modules-3.
   Afișează rolul fiecărui membru (badge) + dropdown de promovare/retrogradare,
   respectând restricțiile ierarhiei. Tab-ul Admin e deja vizibil doar pentru
   Nivel 1 & 2, dar gardăm și fiecare rând defensiv. */
var adminUsersSearch = '';
var adminUsersPage = 1;

function renderAdminUsersList() {
    const container = document.getElementById('adminUsersList');
    if (!container) return;
    container.innerHTML = `
        <div style="font-weight:700; margin:4px 0 8px;"><i class="fas fa-users-gear"></i> ${t('rbac.community')}</div>
        <input type="text" id="adminUsersSearch" class="form-control" style="margin-bottom:10px;" placeholder="${t('adm.searchMembers')}" value="${window.escHtml(adminUsersSearch || '')}" oninput="adminUsersSearchChanged(this.value)">
        <div id="adminUsersTableWrap"></div>`;
    renderAdminUsersTable();
}

function adminUsersSearchChanged(val) {
    adminUsersSearch = val || '';
    adminUsersPage = 1;
    renderAdminUsersTable();
}

function adminUsersGoPage(p) {
    adminUsersPage = p;
    renderAdminUsersTable();
}

function renderAdminUsersTable() {
    const wrap = document.getElementById('adminUsersTableWrap');
    if (!wrap) return;
    const myRole = currentClubMyRole();
    const canManage = can('member.manage');
    const assignable = assignableRolesFor(myRole);
    const meEmail = state.currentUser ? state.currentUser.email : null;

    let users = Object.entries(state.users).map(([email, user]) => Object.assign({ email }, user));
    /* Ordine STABILĂ: după data intrării în club, apoi după id ca departajare.
       `Object.entries` respectă ordinea de inserare, care e ordinea în care baza a
       returnat rândurile — și aceea se schimbă când un rând e actualizat. De asta
       lista sărea la fiecare schimbare de rol. */
    users.sort((a, b) => {
        /* Fără dată de intrare (rânduri vechi sau profiluri fără apartenență activă)
           merg la COADĂ, nu în frunte: un `''` s-ar sorta înaintea oricărei date și
           i-ar arunca sus pe cei despre care știm cel mai puțin. */
        const ja = a.joinedAt || '\uffff', jb = b.joinedAt || '\uffff';
        if (ja !== jb) return ja < jb ? -1 : 1;
        return String(a.id || a.email || '').localeCompare(String(b.id || b.email || ''));
    });
    const q = (adminUsersSearch || '').trim().toLowerCase();
    if (q) {
        users = users.filter(u =>
            (u.name && u.name.toLowerCase().includes(q)) ||
            (u.email && u.email.toLowerCase().includes(q))
        );
    }

    if (users.length === 0) {
        wrap.innerHTML = `<div style="text-align:center; padding:20px; color:var(--text-secondary);"><i class="fas fa-user-slash"></i> ${t('players.noneFound')}</div>`;
        return;
    }

    const PER_PAGE = 10;
    const totalPages = Math.max(1, Math.ceil(users.length / PER_PAGE));
    let page = Math.min(Math.max(1, adminUsersPage || 1), totalPages);
    adminUsersPage = page;
    const startIdx = (page - 1) * PER_PAGE;
    const pageUsers = users.slice(startIdx, startIdx + PER_PAGE);

    const rows = pageUsers.map(user => {
        const pid = user.id || null;
        const role = (pid && state.memberRoleById && state.memberRoleById[pid])
            || (state.memberRoleByName && state.memberRoleByName[user.name])
            || (user.isAdmin ? 'secondary_admin' : 'member');
        const isSelf = meEmail && user.email === meEmail;
        const manageable = canManage && !isSelf && pid && canManageMember(role);

        let roleCell;
        if (manageable) {
            const opts = assignable.map(rr => `<option value="${rr}" ${rr === role ? 'selected' : ''}>${clubRoleLabel(rr)}</option>`).join('');
            roleCell = `${roleBadgeHtml(role)} <select class="form-control" style="display:inline-block; width:auto; min-width:150px; margin-left:6px; padding:4px 8px;" onchange="setMemberRole('${pid}', this.value, '${role}')" title="${t('rbac.changeRole')}">${opts}</select>`;
        } else {
            const tag = isSelf
                ? ` <span style="font-size:0.72em; opacity:0.6;">${t('rbac.you')}</span>`
                : (role === 'principal_admin' ? ` <i class="fas fa-lock" style="font-size:0.7em; opacity:0.5;" title="${t('rbac.cantManagePrincipal')}"></i>` : '');
            roleCell = `${roleBadgeHtml(role)}${tag}`;
        }
        /* ⚠ Butonul „Șterge" a fost ELIMINAT (iulie 2026, finding c).
           Profilul aparține UTILIZATORULUI, nu clubului. Un admin de club poate
           schimba rolul, poate scoate pe cineva din club sau îi poate da ban —
           toate există și funcționează. Ștergerea rândului din `profiles` ar fi
           declanșat 14 cascade care distrug date ale ALTOR utilizatori (banuri,
           mesaje primite, rezultate de turneu).
           În plus, butonul nu ștergea nimic: politica RLS `profiles_delete`
           permitea doar `auth.uid() = id`, deci afecta 0 rânduri, dar raporta
           „Profil șters" — adică MINȚEA administratorul. Politica a fost ștearsă.
           NOTĂ: prima oară am scos butonul din `renderAdminUsersList` din
           app-modules-3.js — versiunea LEGACY, suprascrisă de asta (§5 Override
           Map). Butonul real e cel de aici.
           EN: removed. Profiles belong to users, not clubs; use role change,
           remove-from-club or ban. It never actually deleted anything anyway. */

        return `<tr>
            <td>${clubEsc(user.name)}</td>
            <td>${user.email ? clubEsc(user.email) : '<span style="opacity:.5;">—</span>'}</td>
            <td>${roleCell}</td>
        </tr>`;
    }).join('');

    let pager = '';
    if (totalPages > 1) {
        let btns = '';
        for (let i = 1; i <= totalPages; i++) {
            btns += `<button class="btn btn-sm admin-page-btn${i === page ? '' : ' btn-secondary'}" onclick="adminUsersGoPage(${i})">${i}</button>`;
        }
        pager = `<div class="admin-pager">${btns}</div>`;
    }

    wrap.innerHTML = `
        <table class="table-responsive" style="margin-top:6px;">
            <thead><tr><th>${t('adm.name')}</th><th>${t('adm.email')}</th><th>${t('rbac.role')}</th></tr></thead>
            <tbody>${rows}</tbody>
        </table>${pager}`;
}

/* Schimbă rolul unui membru în clubul activ, cu verificări de ierarhie
   (defense-in-depth; bariera reală e RLS-ul din DB). */
async function setMemberRole(profileId, newRole, currentRole) {
    if (typeof requireWritableClub === 'function' && !requireWritableClub()) return;
    if (!profileId) return;
    if (!canManageMember(currentRole)) { showNotification(t('rbac.cantManageHigher'), 'error'); renderAdminUsersList(); return; }
    if (assignableRolesFor(currentClubMyRole()).indexOf(newRole) === -1) { showNotification(t('rbac.noPermission'), 'error'); renderAdminUsersList(); return; }
    if (state.currentUser && profileId === state.currentUser.id) { showNotification(t('rbac.noPermission'), 'error'); renderAdminUsersList(); return; }
    if (newRole === currentRole) { renderAdminUsersList(); return; }

    if (!isDemoMode()) {
        const up = await sb.from('club_members').update({ role: newRole }).eq('club_id', currentClubId).eq('profile_id', profileId);
        if (up.error) { showNotification(t('sb.errorColon') + up.error.message, 'error'); renderAdminUsersList(); return; }
    }
    /* reflectă local */
    if (!state.memberRoleById) state.memberRoleById = {};
    state.memberRoleById[profileId] = newRole;
    Object.keys(state.users).forEach(email => {
        const u = state.users[email];
        if (u && u.id === profileId) {
            u.isAdmin = (newRole === 'principal_admin' || newRole === 'secondary_admin');
            u.role = u.isAdmin ? 'admin' : 'member';
            if (state.membersById && state.membersById[profileId]) state.membersById[profileId].role = u.role;
            if (u.name && state.members[u.name] && state.members[u.name].dbId === profileId) state.members[u.name].role = u.role;
            if (u.name) { if (!state.memberRoleByName) state.memberRoleByName = {}; state.memberRoleByName[u.name] = newRole; }
        }
    });
    renderAdminUsersList();
    /* Audit: NUMELE persoanei + rolul TRADUS. Înainte se scria `profileId + ' → ' + newRole`,
       adică un UUID brut și un rol în engleză — ilizibil pentru un admin care citește jurnalul. */
    if (typeof addAuditLog === 'function') {
        try {
            const _m = (typeof memberById === 'function') ? memberById(profileId) : null;
            const _nm = (_m && (_m.displayName || _m.name)) || t('live.someone');
            const _rl = (typeof clubRoleLabel === 'function') ? clubRoleLabel(newRole) : newRole;
            addAuditLog('audit.clubRoleChanged', state.currentUser.name,
                { key: 'auditd.roleArrow', params: { name: _nm, role: _rl } });
        } catch (e) { logError('setMemberRole', e); }
    }
    /* Notifică membrul vizat despre noul rol (clopoțel + push). */
    try {
        if (typeof pushNotification === 'function') {
            const roleLbl = { principal_admin: t('role.principal'), secondary_admin: t('role.secondary'), moderator: t('role.moderator'), member: t('role.member') }[newRole] || newRole;
            pushNotification([profileId], { clubId: currentClubId, type: 'role', icon: 'fa-user-shield', tab: 'players', text: t('notif.roleChanged').replace('{role}', roleLbl) });
        }
    } catch (e) { logError('setMemberRole', e); }
    showNotification(t('rbac.roleUpdated'), 'success');
}

async function toggleAdminStatus(ref, makeAdmin) {
    if (!isAdmin) return;
    const key = (state.users[ref] ? ref : (state.users['id:' + ref] ? ('id:' + ref) : (function () {
        for (const k in state.users) { const u = state.users[k]; if (u && (u.id === ref || u.email === ref)) return k; }
        return null;
    })()));
    const user = key ? state.users[key] : null;
    if (!user) return;
    if (!isDemoMode() && user.id) {
        const newRole = makeAdmin ? 'secondary_admin' : 'member';
        const up = await sb.from('club_members').update({ role: newRole })
            .eq('club_id', currentClubId).eq('profile_id', user.id);
        if (up.error) { showNotification(t('sb.errorColon') + up.error.message, 'error'); return; }
    }
    user.isAdmin = makeAdmin;
    user.role = makeAdmin ? 'admin' : 'member';
    if (state.members[user.name]) state.members[user.name].role = user.role;
    renderAdminUsersList();
    addAuditLog('audit.roleChangedInClub', state.currentUser.name, { key: makeAdmin ? 'auditd.roleArrowSecondary' : 'auditd.roleArrowMember', params: { name: (user.name || user.email || String(ref)) } });
    try {
        if (typeof pushNotification === 'function' && user.id) {
            const nr = makeAdmin ? 'secondary_admin' : 'member';
            const roleLbl = makeAdmin ? t('role.secondary') : t('role.member');
            pushNotification([user.id], { clubId: currentClubId, type: 'role', icon: 'fa-user-shield', tab: 'players', text: t('notif.roleChanged').replace('{role}', roleLbl) });
        }
    } catch (e) { logError('toggleAdminStatus', e); }
    showNotification(makeAdmin ? t('sb.userMadeAdminClub') : t('sb.userDemotedClub'), 'success');
}

async function deleteUser(ref) {
    /* ⚠ RETRASĂ (iulie 2026). Profilul aparține UTILIZATORULUI, nu clubului.
       Un admin de club poate scoate pe cineva din club sau îi poate da ban —
       ambele există și funcționează. Ștergerea rândului din `profiles` ar
       declanșa 14 cascade care distrug date ale ALTOR utilizatori: banuri,
       mesaje primite, rezultate de turneu.
       Funcția oricum nu ștergea nimic: politica RLS permitea `auth.uid() = id`,
       deci ștergea 0 rânduri — dar raporta „Profil șters", adică MINȚEA
       administratorul. O lăsăm pe loc (ar putea fi apelată din cod vechi), dar
       refuză explicit în loc să pretindă succes. Butonul din interfață a fost
       eliminat separat.
       EN: retired. Profiles belong to users, not clubs; use remove-from-club or
       ban. This never deleted anything (RLS filtered it) yet reported success. */
    try {
        showNotification(
            (typeof t === 'function' ? t('admin.noProfileDelete')
                : 'Profilul aparține utilizatorului. Folosește „Scoate din club" sau „Ban".'),
            'warning');
    } catch (e) { logError('deleteUser', e); }
    return;

    /* eslint-disable no-unreachable */
    if (!isAdmin) return;
    /* `ref` poate fi id (nou), email (vechi) sau cheia 'id:<uuid>'. Rezolvăm tolerant. */
    const key = (state.users[ref] ? ref : (state.users['id:' + ref] ? ('id:' + ref) : (function () {
        for (const k in state.users) { const u = state.users[k]; if (u && (u.id === ref || u.email === ref)) return k; }
        return null;
    })()));
    const user = key ? state.users[key] : null;
    if (!user) return;
    if (user.id && state.currentUser && user.id === state.currentUser.id) { showNotification(t('m3.cantDeleteSelf'), 'error'); return; }
    const label = user.name || user.email || String(ref);
    if (!await confirmModal(t('sb.confirmDeleteProfile').replace('{email}', label), { danger: true })) return;
    delete state.users[key];
    if (user.name) delete state.members[user.name];

    if (!isDemoMode() && user.id) {
        const del = await sb.from('profiles').delete().eq('id', user.id);
        if (del.error) showNotification(t('sb.errorColon') + del.error.message, 'error');
    }
    renderAdminUsersList();
    addAuditLog('audit.userProfileDeleted', state.currentUser.name, label);
    showNotification(t('sb.profileDeleted'), 'success');
}

async function updatePlayerScore(playerName) {
    if (!isAdmin) return;
    const input = document.getElementById(`edit-score-${playerName.replace(/[^a-zA-Z0-9]/g, '-')}`);
    const newScore = parseFloat(input.value);
    if (isNaN(newScore) || newScore < 0) { showNotification(t('m1.invalidScore'), 'error'); return; }
    if (!state.members[playerName]) return;
    const _oldScore = Number(state.members[playerName].score) || 0;
    /* Persistă ÎNTÂI; mută local DOAR după succes (fără desincronizare la eroare). */
    if (!isDemoMode() && state.members[playerName].dbId) {
        const _err = await _adjustPointsDB({ [state.members[playerName].dbId]: newScore - _oldScore });
        if (_err) { showNotification(t('sb.errorColon') + (_err.message || ''), 'error'); return; }
    }
    state.members[playerName].score = newScore;
    if (state.currentUser && state.currentUser.id === state.members[playerName].dbId) state.currentUser.points = newScore;
    renderAdminScoreEditor(); renderDashboard();
    addAuditLog('audit.scoreManual', state.currentUser.name, `${playerName}: ${newScore}`);
    showNotification(t('m3.scoreUpdatedFor').replace('{name}', playerName), 'success');
}

/* ---------- Calendar / Evenimente (tabela events) ---------- */
/* v8 — citește limita de locuri din formularul deschis.
   RO: întoarce `undefined` dacă ȘI NUMAI DACĂ câmpul nu e în DOM. Distincția e
       esențială: `null` înseamnă „utilizatorul a golit câmpul, șterge limita",
       iar `undefined` înseamnă „câmpul nici n-a fost afișat, NU atinge coloana".
       Fără ea, o cale care nu randează câmpul ar șterge tăcut limita altcuiva.
   EN: `undefined` = field absent, leave the column alone; `null` = user cleared
       it on purpose. Conflating the two would silently wipe existing limits. */
function _readEventCapacity() {
    const el = document.getElementById('eventCapacity');
    if (!el) return undefined;
    const raw = String(el.value == null ? '' : el.value).trim();
    if (raw === '') return null;
    const n = parseInt(raw, 10);
    return (!isNaN(n) && n > 0) ? n : null;
}
window._readEventCapacity = _readEventCapacity;

async function saveEvent() {
    if (!requireWritableClub()) return;
    /* Vezi nota de la catalog.manage: fără portița `isAdmin` (flag global, nu de club). */
    if (typeof can === 'function' && !can('event.manage')) { showNotification(t('rbac.noPermission'), 'error'); return; }
    const title = document.getElementById('eventTitle').value.trim();
    const date = document.getElementById('eventDate').value;
    const time = document.getElementById('eventTime').value;
    const location = document.getElementById('eventLocation').value.trim();
    const type = document.getElementById('eventType').value;
    const description = document.getElementById('eventDescription').value.trim();
    if (!title || !date || !time) { showNotification(t('m2.fillTitleDateTime'), 'error'); return; }

    const status = type === 'tournament' ? 'tournament' : 'confirmed';
    /* v8 — limita de locuri. `undefined` (câmp neafișat) se tratează ca „fără limită"
       la CREARE, pentru că un eveniment nou nu are ce limită să păstreze. */
    const _cap = _readEventCapacity();
    const capacity = (_cap === undefined) ? null : _cap;
    /* Config remindere din formular (component reutilizabil, §5). */
    const reminders = (typeof readReminderConfig === 'function') ? readReminderConfig() : { enabled: false, rules: [] };

    /* Recurență: săptămânal / la 2 săptămâni / lunar. Generăm direct N evenimente. */
    const recurrence = (document.getElementById('eventRecurrence') || {}).value || 'none';
    let count = parseInt((document.getElementById('eventRecurrenceCount') || {}).value, 10) || 1;
    if (recurrence === 'none') count = 1;
    count = Math.max(1, Math.min(52, count));

    const baseDate = new Date(`${date}T${time}`);
    const isoDates = [];
    for (let i = 0; i < count; i++) {
        const d = new Date(baseDate.getTime());
        /* RO: `setMonth` fără limitare rupea seriile pornite pe 29–31: o serie
               lunară din 31 ianuarie genera 3 martie în loc de 28 februarie, apoi
               31 martie — adică o dată inexistentă, mutată tăcut. `addMonths`
               limitează ziua la ultima zi a lunii țintă.
           EN: monthly series starting on the 29th–31st overflowed into the next month. */
        if (recurrence === 'weekly') d.setDate(baseDate.getDate() + 7 * i);
        else if (recurrence === 'biweekly') d.setDate(baseDate.getDate() + 14 * i);
        else if (recurrence === 'monthly') { const _m = addMonths(baseDate, i); d.setFullYear(_m.getFullYear(), _m.getMonth(), _m.getDate()); }
        isoDates.push(d.toISOString());
    }

    let created = 0, _first = null;   /* 8.16-4f: primul creat (pentru chat) */
    for (const iso of isoDates) {
        const event = {
            id: 'tmp-' + Date.now() + '-' + created, title, date: iso, location: location || null,
            type, description: description || null, organizer: state.currentUser.id,
            participants: {}, status, maxParticipants: capacity, gameId: null,
            reminders: reminders,
            createdAt: new Date().toISOString()
        };

        if (!isDemoMode()) {
            /* FIX: `description` și `type` NU erau trimise deloc -> descrierea se pierdea,
               iar „meeting"/„other" se colapsau în „session". Fallback grațios dacă
               migrarea SQL nu e încă aplicată (coloanele lipsesc). */
            /* v8 — ÎNCERCAREA 0: cu limita de locuri. E o TREAPTĂ NOUĂ pusă în CAPUL
               lanțului, nu o modificare a celor existente: dacă aș fi adăugat
               `max_participants` în încercarea de mai jos, un eșec ar fi aruncat
               odată cu ea ȘI `description` ȘI `type` (§37 — citire/scriere tolerantă).
               Când nu există limită, treapta se sare complet ⇒ zero schimbare
               pentru evenimentele obișnuite.
               EN: new FIRST rung, so a failure here doesn't also drop description/type.
                   Skipped entirely when there is no limit. */
            let ins = { error: { skipped: true } };
            if (capacity != null) {
                ins = await sb.from('events').insert({ club_id: currentClubId,
                    title, date: iso, location: location || null, participants: {}, status,
                    organizer: state.currentUser.id, reminders: reminders,
                    description: description || null, type, max_participants: capacity
                }).select().maybeSingle();
            }
            if (ins.error) {
                ins = await sb.from('events').insert({ club_id: currentClubId,
                    title, date: iso, location: location || null, participants: {}, status,
                    organizer: state.currentUser.id, reminders: reminders,
                    description: description || null, type
                }).select().maybeSingle();
            }
            if (ins.error) {
                /* coloanele description/type pot lipsi (migrare neaplicată) -> reîncearcă fără ele */
                ins = await sb.from('events').insert({ club_id: currentClubId,
                    title, date: iso, location: location || null, participants: {}, status,
                    organizer: state.currentUser.id, reminders: reminders
                }).select().maybeSingle();
            }
            if (ins.error) {
                /* poate lipsește coloana organizer/reminders -> reîncearcă fără ele */
                ins = await sb.from('events').insert({ club_id: currentClubId,
                    title, date: iso, location: location || null, participants: {}, status
                }).select().maybeSingle();
            }
            if (ins.error) {
                /* Eșec (ex. RLS: doar Moderator+ creează evenimente). NU adăugăm local. */
                logError('saveEvent', ins.error);
                showNotification(t('sb.createEventErrorColon') + ins.error.message, 'error');
                continue;
            }
            if (ins.data) {
                event.id = ins.data.id;
                /* Adevărul e ce a acceptat baza, nu ce am cerut noi: dacă inserarea
                   cu limită a eșuat și am căzut pe treapta fără ea, starea locală
                   trebuie să arate asta, nu o limită inexistentă. */
                event.maxParticipants = (ins.data.max_participants != null) ? ins.data.max_participants : null;
                /* v9 8.16-4g: listele de locuri de la început (cum le-a scris serverul; un eveniment nou = goale).
                   Înainte lipseau până la prima reîncărcare ⇒ rândul „0 / 3 locuri" apărea cu întârziere. */
                if (event.maxParticipants != null) {
                    event.rsvpConfirmed = Array.isArray(ins.data.rsvp_confirmed) ? ins.data.rsvp_confirmed.map(String) : [];
                    event.rsvpWaitlist = Array.isArray(ins.data.rsvp_waitlist) ? ins.data.rsvp_waitlist.map(String) : [];
                }
                /* Interfața nu promite ce baza a refuzat: dacă am cerut o limită și
                   evenimentul s-a creat fără ea, utilizatorul află pe loc. */
                if (capacity != null && event.maxParticipants == null) {
                    showNotification(t('ev.seatLimitHint'), 'warning');
                }
            }
        }
        if (isDemoMode() && capacity != null) { event.rsvpConfirmed = []; event.rsvpWaitlist = []; }   /* 8.16-4g: ca mai sus */
        state.events.push(event);   /* local DOAR după succes (sau în demo) */
        if (!_first) _first = event;
        created++;
    }

    renderCalendar();
    if (typeof renderNotifBadge === 'function') renderNotifBadge();
    if (created === 0) return;   /* nimic creat -> eroarea a fost deja afișată */
    /* v9 8.16-4f: EXACT fereastra de creare (din chat pot fi deschise și altele); deschisă din chat
       (`__onCreated`, addEventModal) ⇒ primul eveniment creat se atașează în conversație. */
    const modal = document.getElementById('addEventModal') || document.querySelector('.modal.active');
    const _onCreated = modal && modal.__onCreated;
    if (modal) modal.remove();
    addAuditLog('audit.eventCreated', state.currentUser.name, `${title}${created > 1 ? ` (×${created})` : ''}`);
    showNotification(created > 1 ? `${created} evenimente create!` : 'Eveniment creat!', 'success');
    if (typeof _onCreated === 'function' && _first) { try { _onCreated(_first); } catch (e) { logError('saveEvent.onCreated', e); } }
}

async function setRSVP(eventId, status) {
    const idx = state.events.findIndex(e => String(e.id) === String(eventId));
    if (idx === -1) { showNotification(t('sb.eventNotFoundExcl'), 'error'); return; }
    const event = state.events[idx];
    /* v9 8.16-4g: început ⇒ răspunsurile închise (și pe server). */
    { const _t0 = Date.parse(event.date); if (isFinite(_t0) && _t0 < Date.now()) { showNotification(window.t('ev.rsvpClosed'), 'info'); return; } }
    /* normalizează participants la hartă nume->status (compatibil cu listele vechi) */
    if (!event.participants || typeof event.participants !== 'object' || Array.isArray(event.participants)) {
        event.participants = rsvpMap(event.participants);
    }
    const me = state.currentUser.id;
    const cur = event.participants[me] || null;
    const desired = (cur === status) ? null : status; /* re-apăsare pe același răspuns => retragere */
    const labels = { yes: window.t('rsvp.yes'), maybe: window.t('rsvp.maybe'), no: window.t('rsvp.no') };

    /* ★ RSVP OPTIMIST: aplicăm local + randăm INSTANT (calendar + modalele deschise);
       RPC-ul atomic `event_rsvp` rulează în fundal; harta autoritară din server
       înlocuiește apoi copia locală (§23). La eroare: revert + notificare. */
    const _snapshot = JSON.parse(JSON.stringify(event.participants));
    const _snapConf = Array.isArray(event.rsvpConfirmed) ? event.rsvpConfirmed.slice() : null;
    const _snapWait = Array.isArray(event.rsvpWaitlist) ? event.rsvpWaitlist.slice() : null;
    if (desired === null) delete event.participants[me]; else event.participants[me] = desired;

    /* v8 — listele ordonate se întrețin local ȘI ele, altfel indicatorul „3 / 8 locuri"
       ar rămâne în urmă cu o apăsare, exact pe fluxul pe care îl vrem instant.
       ⚠ DOAR în cazurile SIGURE. Când e în joc ultimul loc sau evenimentul e plin,
       clientul NU poate ști dacă intri sau ajungi pe listă — atunci nu atingem nimic
       și lăsăm serverul să decidă. `_ambiguu` marchează exact acele apăsări.
       EN: keep the ordered lists in sync locally, but only where the outcome is
           certain; the last seat and a full event are decided by the server. */
    const _cap = evCapacityOf(event);
    const _hasLists = Array.isArray(event.rsvpConfirmed) && Array.isArray(event.rsvpWaitlist);
    let _ambiguu = false;
    if (_hasLists) {
        const drop = arr => { const k = arr.indexOf(String(me)); if (k !== -1) arr.splice(k, 1); };
        if (desired !== 'yes') {
            drop(event.rsvpConfirmed); drop(event.rsvpWaitlist);
            /* Locul eliberat se umple imediat, ca pe server: primul de pe listă urcă. */
            if (_cap != null && event.rsvpWaitlist.length && event.rsvpConfirmed.length < _cap) {
                event.rsvpConfirmed.push(event.rsvpWaitlist.shift());
            }
        } else if (event.rsvpConfirmed.indexOf(String(me)) === -1) {
            if (_cap == null) {
                drop(event.rsvpWaitlist); event.rsvpConfirmed.push(String(me));
            } else if (_cap - event.rsvpConfirmed.length >= 2) {
                drop(event.rsvpWaitlist); event.rsvpConfirmed.push(String(me));
            } else {
                _ambiguu = true;   /* ultimul loc sau plin -> hotărăște serverul */
            }
        }
    }

    const _refreshOpenUis = function () {
        renderCalendar();
        /* ⚡ RĂSPUNS INSTANT ȘI PE DASHBOARD (aug. 2026).
           RO: DEFECT RAPORTAT: butoanele „Vin / Poate / Nu vin" din cardul „Next event"
               de pe Dashboard păreau că se încarcă greu, deși în pagina de Calendar
               reacționau imediat. Cauza NU era serverul: actualizarea optimistă exista
               deja, dar reîmprospăta DOAR calendarul și ferestrele deschise. Cardul din
               Dashboard rămânea pe starea veche până la următoarea randare venită din
               altă parte (realtime sau un cronometru) — de unde impresia de lentoare.
               Aceeași hartă locală, aceeași scriere în fundal: doar afișăm rezultatul
               și acolo unde utilizatorul chiar se uită.
           EN: the "Next event" RSVP buttons on the Dashboard felt slow while the Calendar
               reacted instantly. The optimistic update was already there — it just never
               re-rendered the Dashboard card, which stayed stale until some unrelated
               render came along. Same local state, same background write. */
        try { if (typeof renderDashboardEvent === 'function') renderDashboardEvent(); } catch (e) { logError('setRSVP.dash', e); }
        /* dacă fereastra cu evenimentele zilei e deschisă, o reîmprospătăm */
        const openDay = document.querySelector('.modal.active[data-day]');
        if (openDay) { const d = openDay.dataset.day; openDay.remove(); showDayEvents(d); }
        /* idem pentru pop-up-ul de detalii eveniment (§6): reflectă imediat noul răspuns. */
        const openEv = document.getElementById('eventDetailModal');
        if (openEv && typeof openEvent === 'function') { const eid = openEv.dataset.eventId; openEv.remove(); openEvent(eid); }
    };

    /* În cazul ambiguu tăcem până răspunde serverul: un „RSVP salvat: Vin" urmat de
       „ești pe lista de așteptare" e exact promisiunea falsă interzisă de §7.5. */
    if (!_ambiguu) {
        showNotification(
            event.participants[me] ? (window.t('rsvp.saved') + ': ' + labels[status] + ' — „' + event.title + '"') : (window.t('rsvp.withdrawn') + ' „' + event.title + '"'),
            event.participants[me] ? 'success' : 'info'
        );
    }
    _refreshOpenUis();

    if (!isDemoMode() && !String(event.id).startsWith('tmp-')) {
        window._rsvpSeq = window._rsvpSeq || {};
        const _mySeq = (window._rsvpSeq[event.id] = (window._rsvpSeq[event.id] || 0) + 1);
        /* v9 8.16-4g (2 nov. 2026): FĂRĂ CLIPIRI. Cât timp răspunsul e „în zbor", o reîncărcare a datelor
           pornită de altceva (realtime: alt răspuns, alt eveniment) aducea de pe server starea de DINAINTE
           și o punea peste răspunsul optimist ⇒ butonul sărea înapoi o clipă (raportat de Alex). Acum
           răspunsul în zbor e ținut aici, iar hidratarea îl reaplică (hydrateFromSupabase) până răspunde
           serverul; atunci intrarea dispare (plasă: 15 s). EN: keep the in-flight answer so a concurrent
           rehydrate can't flash the old state back. */
        window._rsvpPending = window._rsvpPending || {};
        window._rsvpPending[event.id] = { desired: desired, at: Date.now(), seq: _mySeq,
            conf: (!_ambiguu && Array.isArray(event.rsvpConfirmed)) ? event.rsvpConfirmed.slice() : null,
            wait: (!_ambiguu && Array.isArray(event.rsvpWaitlist)) ? event.rsvpWaitlist.slice() : null };
        const _endPending = function () { const _p = window._rsvpPending[event.id]; if (_p && _p.seq === _mySeq) delete window._rsvpPending[event.id]; };
        /* obiectul de ACUM (o hidratare îl poate fi înlocuit între timp) */
        const _curEv = function () { return (state.events || []).find(e => String(e.id) === String(event.id)) || event; };
        /* v8 — cerem forma BOGATĂ (capacitate + liste ordonate + starea proprie).
           ⚠ TOLERANȚĂ JS-nou / SQL-vechi (§38.f): dacă `event_rsvp_v2` nu există în
           baza de date, cădem pe `event_rsvp`, care întoarce harta simplă, și totul
           se comportă ca înainte de v8. Reapelarea e sigură: RPC-ul e idempotent
           (`desired` e calculat pe client, nu comutat pe server).
           EN: fall back to the old RPC when v2 is absent; the call is idempotent. */
        let res = await sb.rpc('event_rsvp_v2', { p_club_id: currentClubId, p_event_id: event.id, p_status: desired });
        /* 8.16-4g: „event_past" (a început între timp) e un refuz, nu o funcție lipsă ⇒ fără a doua cerere */
        const _isPast = r => !!(r && r.error && /event_past/.test(String(r.error.message || '')));
        if (res.error && !_isPast(res)) res = await sb.rpc('event_rsvp', { p_club_id: currentClubId, p_event_id: event.id, p_status: desired });
        _endPending();
        if (res.error) {
            const _ce = _curEv();
            if (_ce === event) {
                event.participants = _snapshot;
                if (_snapConf) event.rsvpConfirmed = _snapConf;
                if (_snapWait) event.rsvpWaitlist = _snapWait;
            } else {
                /* obiect proaspăt de pe server ⇒ refacem DOAR răspunsul meu (al celorlalți e deja cel bun) */
                if (_snapshot[me]) _ce.participants[me] = _snapshot[me]; else delete _ce.participants[me];
            }
            _refreshOpenUis();
            showNotification(_isPast(res) ? window.t('ev.rsvpClosed') : (t('sb.updateErrorColon') + res.error.message), _isPast(res) ? 'info' : 'error');
            return;
        }
        if (window._rsvpSeq[event.id] === _mySeq) {
            const _cur = _curEv();
            const _raw = res.data;
            const _rich = (_raw && typeof _raw === 'object' && !Array.isArray(_raw) && _raw.participants) ? _raw : null;
            const _server = _rich ? rsvpMap(_rich.participants)
                : ((_raw && typeof _raw === 'object' && !Array.isArray(_raw)) ? _raw : rsvpMap(_raw));
            const _norm = m => JSON.stringify(Object.keys(m || {}).sort().map(k => [k, m[k]]));
            const _normA = a => JSON.stringify(Array.isArray(a) ? a.map(String) : null);
            let _changed = _norm(_server) !== _norm(_cur.participants);
            _cur.participants = _server;
            if (_rich) {
                if (Array.isArray(_rich.confirmed)) {
                    if (_normA(_rich.confirmed) !== _normA(_cur.rsvpConfirmed)) _changed = true;
                    _cur.rsvpConfirmed = _rich.confirmed.map(String);
                }
                if (Array.isArray(_rich.waitlist)) {
                    if (_normA(_rich.waitlist) !== _normA(_cur.rsvpWaitlist)) _changed = true;
                    _cur.rsvpWaitlist = _rich.waitlist.map(String);
                }
                if (_rich.capacity !== undefined) _cur.maxParticipants = _rich.capacity;
            }
            if (_changed) _refreshOpenUis();   /* re-randăm doar dacă serverul diferă de optimist */

            /* Verdictul serverului, spus O SINGURĂ DATĂ și fără să pară eroare (§7.5).
               RO: ⚠ măsurat 30 aug. 2026: p50 ≈ 104 ms, p90 ≈ 120 ms (preflight + fetch,
                   desktop, fără throttling). Sub pragul de 200 ms, deci NU adăugăm nicio
                   stare de așteptare vizibilă — ar clipi și ar dispărea înainte de a fi
                   percepută. A rămas doar problema de CORECTITUDINE de mai jos.
                   Serverul poate să ne pună pe listă în DOUĂ situații:
                     (a) clientul știa că nu e sigur — ultimul loc sau plin (`_ambiguu`);
                     (b) clientul credea că sunt locuri, dar datele locale erau învechite
                         (realtime întârziat) și evenimentul se umpluse între timp.
                   Cazul (b) era tratat TĂCUT: vedeai „ești înăuntru", apoi listele se
                   corectau fără niciun cuvânt — exact retrogradarea nemarcată pe care
                   §7.5 o interzice. Acum verdictul se anunță după STAREA REALĂ întoarsă
                   de server, nu după ce bănuia clientul înainte de apel. Un singur punct
                   de decizie => imposibil să apară două mesaje.
               EN: measured p90 ≈ 120 ms, so no waiting indicator is warranted. What
                   remained was correctness: a stale client could be silently demoted to
                   the waitlist. The verdict now follows the server's actual state. */
            const _meState = (_rich && _rich.me) ? _rich.me.state : null;
            const _pusPeLista = (_meState === 'waitlist' && desired === 'yes');
            if (_pusPeLista) {
                const _poz = (_rich.me.position != null) ? _rich.me.position : null;
                showNotification(
                    _poz != null
                        ? (window.t('ev.waitlistJoined') + ' ' + window.t('ev.waitlistMine').replace('{n}', _poz))
                        : window.t('ev.waitlistJoined'),
                    'info');
            } else if (_ambiguu) {
                /* Nu era sigur, dar a ieșit bine: confirmăm acum, fiindcă la apăsare
                   am tăcut intenționat ca să nu promitem ceva ce serverul putea refuza. */
                showNotification(window.t('rsvp.saved') + ': ' + labels[status] + ' — „' + event.title + '"', 'success');
            }
        }
    }
}

/* ---------- Persistență locală: DOAR pentru funcții auxiliare ----------
   (poll-uri, regulament, setări, liste de jocuri etc.). Datele de bază
   (conturi, jucători, jocuri, evenimente, sesiuni, mesaje) trăiesc în
   Supabase. Nicio parolă nu este stocată vreodată. */
function loadState() {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (!saved) return;
        const loaded = JSON.parse(saved);
        /* Citim din blob DOAR câmpurile fără sursă autoritară în DB + cache-ul
           audit. NU mai preîncărcăm regulation/polls/seasons/notifications: ele
           vin din club_settings/DB la hidratare, iar o preîncărcare din blob ar
           putea arăta pe scurt datele ALTUI club până la hidratare. Curățăm și
           reziduurile vechi din blob (migrare tăcută). */
        const auxKeys = ['settings', 'audit'];
        const stale = ['polls', 'regulation', 'seasons', 'currentSeason', 'notifications', 'locations', 'categories'];
        let hadStale = false;
        stale.forEach(k => { if (k in loaded) { delete loaded[k]; hadStale = true; } });
        auxKeys.forEach(k => {
            if (!(k in loaded)) return;
            if (state[k] && typeof state[k] === 'object' && !Array.isArray(state[k]) &&
                loaded[k] && typeof loaded[k] === 'object' && !Array.isArray(loaded[k])) {
                state[k] = { ...state[k], ...loaded[k] };
            } else {
                state[k] = loaded[k];
            }
        });
        /* Dacă blob-ul conținea chei vechi migrate în DB, îl rescriem curat o dată. */
        if (hadStale) { try { saveState(); } catch (e) { logError('loadState cleanup', e); } }
    } catch (e) { console.error('loadState (aux):', e); }
}

function saveState() {
    try {
        /* Plasă de siguranță anti-pierdere jurnal: dacă în memorie auditul e GOL
           dar în storage există intrări, le restaurăm ÎNAINTE de scriere — altfel
           orice flux care a golit temporar starea ar suprascrie definitiv istoricul.
           Parse-ul rulează doar în cazul rar (audit gol), nu la fiecare salvare. */
        try {
            if (!Array.isArray(state.audit) || state.audit.length === 0) {
                const prev = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
                if (prev && Array.isArray(prev.audit) && prev.audit.length) state.audit = prev.audit;
            }
        } catch (e) { logError('saveState audit-guard', e); }
        /* Blob local = DOAR câmpuri fără sursă autoritară în DB + cache-ul audit.
           regulation/polls/seasons/currentSeason se hidratează din club_settings
           (DB = sursa de adevăr, cross-device); notifications se încarcă din DB
           (loadAllNotifications). Nu le mai dublăm aici — o singură sursă de adevăr
           per câmp (golden standard), fără riscul „blob local vs DB divergente". */
        const aux = {
            settings: state.settings,
            audit: state.audit,
            lastSaved: new Date().toISOString()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(aux));
        /* ⚠ ELIMINAT (iul. 2026 — bug de pierdere de date, multi-user):
           aici se făcea `upsert` cu ÎNTREGUL `state.polls` din MEMORIE. `saveState()`
           e apelat din ~28 de locuri (ex. adaugi un joc în catalog), deci orice
           acțiune a unui Moderator+ SUPRASCRIA poll-urile cu copia lui, învechită,
           ștergând toate voturile date de alții între timp — anulând complet
           atomicitatea RPC-ului `poll_vote`.
           Poll-urile se persistă acum DOAR prin operațiuni ȚINTITE, server-side:
           poll_vote / poll_retract / poll_create / poll_close / poll_delete. */
        if (typeof updateLastSaveIndicator === 'function') updateLastSaveIndicator();
        return true;
    } catch (e) { console.error('saveState:', e); return false; }
}


/* ============================================================
   AUDIT LOG — persistent SERVER-SIDE (tabela `audit_log` + RPC
   `audit_log_add`, vezi audit_log_setup.sql). Override (§5) peste
   versiunile locale din app-modules-3: DB = sursa de adevăr pentru
   admini (rang>=3, RLS); localStorage rămâne cache instant + fallback
   offline/demo. Scrierea e ASINCRONĂ (nu blochează acțiunea userului);
   actorul e impus server-side din JWT (anti-spoofing, §23).
   ============================================================ */
function addAuditLog(action, user, details = '') {
    if (!Array.isArray(state.audit)) state.audit = [];
    /* Detalii structurate {key, params} -> serializate ca JSON; traducerea se
       face LA RANDARE (jurnalul rămâne comutabil RO<->EN oricând). Stringurile
       simple (nume, valori) rămân neschimbate. */
    if (details && typeof details === 'object' && details.key) {
        try { details = JSON.stringify({ k: details.key, p: details.params || {} }); }
        catch (e) { details = String(details.key); }
    }
    const logEntry = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        clubId: (typeof currentClubId !== 'undefined' ? currentClubId : null),
        action, user, details,
        ip: (typeof getUserIP === 'function' ? getUserIP() : ''),
        userAgent: navigator.userAgent
    };
    state.audit.push(logEntry);
    if (state.audit.length > 5000) state.audit = state.audit.slice(-5000);
    saveState();

    /* Persistă în DB (append-only). Fire-and-forget cu logare de eroare. */
    try {
        if (sb && typeof currentClubId !== 'undefined' && currentClubId &&
            !(typeof isDemoMode === 'function' && isDemoMode()) &&
            state.currentUser && state.currentUser.id) {
            sb.rpc('audit_log_add', {
                p_club_id: currentClubId,
                p_action: String(action || '').slice(0, 200),
                p_details: String(details || '').slice(0, 2000)
            }).then(function (r) {
                if (r && r.error) { logError('audit_log_add', r.error); return; }
                /* Reflectă imediat în cache-ul DB (dacă e încărcat), fără refetch. */
                if (state._auditDbLoadedFor === currentClubId && Array.isArray(state.auditDb)) {
                    state.auditDb.unshift({
                        id: 'local-' + logEntry.id, actor_id: state.currentUser.id,
                        actor_name: state.currentUser.name || '', action: logEntry.action,
                        details: logEntry.details, created_at: logEntry.timestamp
                    });
                    if (typeof renderAuditLog === 'function') renderAuditLog();
                }
            }, function (e) { logError('audit_log_add', e); });
        }
    } catch (e) { logError('addAuditLog rpc', e); }
}

async function loadAuditFromDb(force) {
    try {
        if (!sb || typeof currentClubId === 'undefined' || !currentClubId) return;
        if (typeof isDemoMode === 'function' && isDemoMode()) return;
        if (typeof roleRank === 'function' && typeof currentClubMyRole === 'function' &&
            roleRank(currentClubMyRole()) < 3) return; /* RLS oricum refuză sub rang 3 */
        if (state._auditDbLoadedFor === currentClubId && !force) return;
        state._auditDbLoadedFor = currentClubId; /* anti-dublu-fetch în paralel */
        const res = await sb.from('audit_log')
            .select('id, actor_id, actor_name, action, details, created_at')
            .eq('club_id', currentClubId)
            .order('created_at', { ascending: false })
            .limit(1000);
        if (res.error) { state._auditDbLoadedFor = null; throw res.error; }
        state.auditDb = res.data || [];
        if (typeof renderAuditLog === 'function') renderAuditLog();
    } catch (e) { logError('loadAuditFromDb', e); }
}
window.loadAuditFromDb = loadAuditFromDb;

/* Detalii de audit: dacă sunt structurate ({"k":cheie,"p":{...}}), traducem
   cheia în limba curentă și interpolăm parametrii; altfel afișăm textul brut
   (compatibil cu intrările vechi, salvate deja traduse). */
function _auditDetailsText(d) {
    if (!d) return '';
    if (typeof d === 'string' && d.charAt(0) === '{') {
        try {
            const o = JSON.parse(d);
            if (o && o.k) {
                let s = t(o.k);
                Object.keys(o.p || {}).forEach(kk => { s = s.split('{' + kk + '}').join(o.p[kk]); });
                return s;
            }
        } catch (e) { /* nu era JSON — cade pe text brut */ }
    }
    return d;
}

function renderAuditLog() {
    const container = document.getElementById('auditLog');
    if (!container) return;
    const esc = s => window.escHtml(s);
    const q = (document.getElementById('auditSearch') ? document.getElementById('auditSearch').value : '').trim().toLowerCase();

    const dbMode = Array.isArray(state.auditDb) && state._auditDbLoadedFor === currentClubId;
    if (!dbMode) loadAuditFromDb(); /* async — re-randează singur la sosire */

    let logs;
    if (dbMode) {
        const nmA = r => (typeof displayNameById === 'function' && r.actor_id && displayNameById(r.actor_id)) || r.actor_name || '—';
        logs = state.auditDb.map(r => ({ timestamp: r.created_at, user: nmA(r), action: r.action, details: r.details }));
    } else {
        logs = (state.audit || []).slice().reverse();
        if (typeof currentClubId !== 'undefined' && currentClubId) {
            logs = logs.filter(l => l.clubId === currentClubId);
        }
    }
    if (q) {
        logs = logs.filter(l => [t(l.action), l.user, _auditDetailsText(l.details)].filter(Boolean).join(' ').toLowerCase().includes(q));
    }

    const srcNote = '<p style="color:var(--text-secondary); font-size:.8em; margin-top:10px;"><i class="fas fa-' +
        (dbMode ? 'cloud-arrow-up' : 'laptop') + '"></i> ' + t(dbMode ? 'adm.auditSynced' : 'adm.auditLocalNote') + '</p>';

    if (logs.length === 0) {
        container.innerHTML = '<p class="text-center text-muted">' + t('adm.noAudit') + '</p>' + srcNote;
        const pag0 = document.getElementById('auditPagination'); if (pag0) pag0.innerHTML = '';
        return;
    }

    const perPage = 20;
    const totalPages = Math.ceil(logs.length / perPage);
    let page = Math.min(Math.max(1, auditPage || 1), totalPages || 1);
    auditPage = page;
    const slice = logs.slice((page - 1) * perPage, page * perPage);

    container.innerHTML = slice.map(log => `
        <div class="audit-entry">
            <div class="audit-time">${new Date(log.timestamp).toLocaleString('en-GB')}</div>
            <div class="audit-action">
                <strong>${esc(log.user)}:</strong> ${esc(t(log.action))}
                ${log.details ? `<br><small style="color: var(--text-secondary);">${esc(_auditDetailsText(log.details))}</small>` : ''}
            </div>
        </div>
    `).join('') + srcNote;

    const pag = document.getElementById('auditPagination');
    if (pag) {
        if (totalPages <= 1) { pag.innerHTML = ''; }
        else {
            let h = `<div style="display:flex; justify-content:center; align-items:center; gap:10px; flex-wrap:wrap;">
                <span style="color:var(--text-secondary);">${logs.length} ${t('admin.entries')} • ${t('common.page')} ${page}/${totalPages}</span>`;
            if (page > 1) h += `<button class="btn btn-sm" onclick="goToAuditPage(${page - 1})"><i class="fas fa-chevron-left"></i> ${t('common.prev')}</button>`;
            if (page < totalPages) h += `<button class="btn btn-sm" onclick="goToAuditPage(${page + 1})">${t('common.next')} <i class="fas fa-chevron-right"></i></button>`;
            h += `</div>`;
            pag.innerHTML = h;
        }
    }
}

/* ---------- Inițializare (verifică sesiunea Supabase) ---------- */
async function init() {
    /* ⚡ RO: era `hideLoader()` — loaderul pleca pe un cronometru fix de 1000 ms,
       fără nicio legătură cu starea aplicației, lăsând ~2,5 s de pagină goală
       (doar subsolul legal) până când `onAuthenticated` termina gărzile
       secvențiale. Acum loaderul rămâne până când se afișează un ECRAN REAL
       (`showWelcomeScreen` / `showLogin` / `showRecoveryForm` / `showMainApp` /
       `showClubHub`, toate în app-core.js) sau până când intervine plasa de
       siguranță pornită aici. Detalii complete: nota de la `hideLoader`.
       EN: replaced the fixed 1s hide with a readiness-driven one + failsafe. */
    startLoaderWatchdog();
    loadState();
    state.currentUser = null; isAdmin = false; /* sesiunea Supabase decide cine e logat */
    setupEventListeners();
    initTooltips();

    if (!sb) {
        showWelcomeScreen();
        showNotification(t('sb.supabaseLoadError'), 'error');
        return;
    }

    /* Reacționează la schimbări de autentificare (refresh token, logout, resetare parolă) */
    sb.auth.onAuthStateChange((event, session) => {
        _authTrace('onAuthStateChange', { event: event, areSesiune: !!session });
        if (event === 'PASSWORD_RECOVERY') {
            showRecoveryForm();
            return;
        }
        /* Retur de la un provider OAuth (ex. Google): sesiunea sosește asincron,
           după ce getSession() din init() a rulat deja. Prindem SIGNED_IN și
           logăm userul (guard-ul împiedică dubla execuție cu loginul clasic). */
        if (event === 'SIGNED_IN' && session) {
            var h = window.location.hash || '';
            if (h.indexOf('type=recovery') !== -1) return; /* lasă fluxul de resetare parolă */
            /* Retur de la Google (OAuth): sesiunea tocmai s-a stabilit, dar e „rece"
               pentru RLS → dacă încărcăm datele acum, cluburile/discover/membership
               pot ieși goale. Reîncărcăm curat spre origine: init() preia sesiunea
               persistată (caldă) și încarcă TOTUL corect, identic cu login-ul pe email. */
            var _pendingOAuth = false;
            try { _pendingOAuth = sessionStorage.getItem('to_oauth_pending') === '1'; } catch (e) { logError('init', e); }
            if (_pendingOAuth) {
                try { sessionStorage.removeItem('to_oauth_pending'); } catch (e) { logError('init', e); }
                /* ⚠ CURSĂ (cauza login-ului Google care „pică" o dată sau de două ori):
                   `location.replace()` se executa IMEDIAT, fără să aștepte ca sesiunea
                   să fie chiar PERSISTATĂ în localStorage. Dacă reload-ul o lua înaintea
                   scrierii, `init()` nu găsea nicio sesiune -> ecran de login. La a doua
                   încercare sesiunea era deja acolo -> mergea.
                   Acum așteptăm confirmarea persistării ÎNAINTE de reload. */
                _oauthReloadWhenPersisted();
                return;
            }
            /* F1b: logare pornită de ALTĂ filă (ex. linkul de confirmare din email) => așteptăm. */
            if (!(state.currentUser && !state.currentUser.isDemo) && !_authPromise && _isShadowSignIn()) {
                _deferShadowSignIn(session);
                return;
            }
            runOnAuthenticated(session);
            return;
        }
        if (event === 'SIGNED_OUT' && state.currentUser && !state.currentUser.isDemo) {
            state.currentUser = null;
            isAdmin = false;
            showWelcomeScreen();
        }
    });

    /* Dacă utilizatorul a ajuns aici dintr-un link de resetare parolă,
       arată formularul de parolă nouă în loc să-l logăm direct. */
    var hash = window.location.hash || '';
    if (hash.indexOf('type=recovery') !== -1) {
        showRecoveryForm();
        return;
    }

    try {
        try { localStorage.removeItem('gbgsc_pass'); } catch (_) { logError('init', _); } /* R1: migrare — șterge parola în clar a userilor existenți */

        /* ⚠ A DOUA CURSĂ a login-ului cu Google:
           la retur, `detectSessionInUrl` parsează token-ul din hash ASINCRON. `init()`
           putea rula `getSession()` ÎNAINTE de asta -> nicio sesiune -> ecran de login,
           deși userul tocmai se autentificase. (Evenimentul SIGNED_IN venea mai târziu.)
           Dacă detectăm un retur OAuth, AȘTEPTĂM sesiunea în loc să-l aruncăm la login. */
        let _sess = null;
        const _hash = window.location.hash || '';
        const _srch = window.location.search || '';
        const _isOAuthReturn = (_hash.indexOf('access_token') !== -1)
            || (_srch.indexOf('code=') !== -1)
            || (function () { try { return sessionStorage.getItem('to_oauth_pending') === '1'; } catch (e) { return false; } })();

        _authTrace('init:start', {
            oauthReturn: _isOAuthReturn,
            hasHashToken: _hash.indexOf('access_token') !== -1,
            hasCode: _srch.indexOf('code=') !== -1,
            pending: (function () { try { return sessionStorage.getItem('to_oauth_pending'); } catch (e) { return '?'; } })(),
            gbgsc_tab: (function () { try { return sessionStorage.getItem('gbgsc_tab'); } catch (e) { return '?'; } })(),
            gbgsc_remember: (function () { try { return localStorage.getItem('gbgsc_remember'); } catch (e) { return '?'; } })()
        });

        if (_isOAuthReturn) {
            for (let i = 0; i < 20 && !_sess; i++) {     /* ~3s */
                try {
                    const r = await sb.auth.getSession();
                    if (r && r.data && r.data.session) { _sess = r.data.session; _authTrace('init:sesiune-gasita', { dupaMs: i * 150 }); break; }
                } catch (e) { /* reîncearcă */ }
                await new Promise(function (res) { setTimeout(res, 150); });
            }
            if (!_sess) _authTrace('init:FARA-sesiune-dupa-3s');
        }
        const { data } = _sess ? { data: { session: _sess } } : await sb.auth.getSession();
        _authTrace('init:getSession', { areSesiune: !!(data && data.session) });
        let remembered = localStorage.getItem('gbgsc_remember') !== '0';
        /* Revenire de la Stripe: păstrează sesiunea chiar dacă „ține-mă minte" e oprit. */
        try { if (localStorage.getItem('gbgsc_checkout_return') === '1') { remembered = true; localStorage.removeItem('gbgsc_checkout_return'); } } catch (e) { logError('init', e); }
        if (data && data.session) {
            /* „Ține-mă minte": dacă a fost debifat, nu reconecta automat la o
               sesiune NOUĂ de browser (dar refresh-ul în aceeași filă păstrează login-ul). */
            const sameTab = sessionStorage.getItem('gbgsc_tab') === '1';
            /* ⚠ NU deconectăm la un retur OAuth: userul TOCMAI s-a autentificat explicit
               în acest tab. Înainte, dacă „ține-mă minte" era debifat, `signOut()` se
               declanșa fix după Google -> te întorcea la login. */
            if (!remembered && !sameTab && !_isOAuthReturn) {
                _authTrace('init:signOut(remember-off)');
                try { await sb.auth.signOut(); } catch (e) { logError('init', e); }
                prefillLogin(); showWelcomeScreen(); return;
            }
            _authTrace('init:runOnAuthenticated');
            _consumeDemoParam();   /* 7.4: logat => parametrul demo e ignorat (doar scos din adresă) */
            try { await runOnAuthenticated(data.session); return; }
            catch (e) { console.error('Restaurare sesiune:', e); }
        }
        /* Fără sesiune validă: dacă „ține-mă minte" e activ și avem credențiale salvate,
           ne autentificăm automat (ca pe alte site-uri). */
        if (remembered && await tryAutoLogin()) { _consumeDemoParam(); return; }
        prefillLogin();
        /* 7.4: vizitator fără sesiune, venit de pe linkul de demo al site-ului => direct în demo. */
        if (_consumeDemoParam()) { showDemo(); return; }
        showWelcomeScreen();
    } catch (e) {
        console.error('Eroare la verificarea sesiunii:', e);
        prefillLogin();
        showWelcomeScreen();
    }
}

/* Precompletează email-ul (și parola, dacă „ține-mă minte") pe ecranul de login. */
function prefillLogin() {
    try {
        /* Curățăm reziduul PII vechi (emailuri persistate în versiuni anterioare). */
        try { localStorage.removeItem('gbgsc_email'); } catch (_) {}
        let em = null;
        try { em = sessionStorage.getItem('to_last_tag'); } catch (_) {}
        const remembered = localStorage.getItem('gbgsc_remember') !== '0';
        const e = document.getElementById('loginEmail'); if (e && em) e.value = em;
        /* R1 (securitate): nu mai precompletăm parola și ștergem orice parolă stocată anterior. */
        try { localStorage.removeItem('gbgsc_pass'); } catch (_) { logError('prefillLogin', _); }
        const rm = document.getElementById('rememberMe'); if (rm) rm.checked = remembered;
    } catch (e) { logError('prefillLogin', e); }
}

/* R1 (securitate): auto-login FĂRĂ parolă stocată. Ne bazăm exclusiv pe sesiunea
   persistată de Supabase (refresh token, autoRefreshToken=true). Dacă refresh token-ul
   a expirat sau lipsește, userul reintroduce parola o singură dată — corect și securizat. */
async function tryAutoLogin() {
    try {
        try { localStorage.removeItem('gbgsc_pass'); } catch (_) { logError('tryAutoLogin', _); } /* curăță parola în clar salvată anterior */
        const r = await sb.auth.refreshSession();
        if (!r.error && r.data && r.data.session) { await runOnAuthenticated(r.data.session); return true; }
    } catch (e) { console.warn('Auto-login eșuat:', e); }
    return false;
}

/* ============================================================================
   ===================== WEB PUSH — CLIENT (stil WhatsApp) ====================
   RO: abonare la notificări push (PushSubscription) + salvare token în Supabase,
       buton on/off în Profil, banner in-app când aplicația e în prim-plan,
       deep linking la click. Reutilizează openNotif/openDMFromNotif existente.
   EN: push subscription + token save, profile on/off toggle, in-app foreground
       banner, deep linking on click. Reuses existing openNotif/openDMFromNotif.
   ⚠ Înlocuiește VAPID_PUBLIC_KEY cu cheia ta publică (base64url). E PUBLICĂ,
     deci sigură în client. Cheia privată stă DOAR în secretele Edge Function (§9).
   ============================================================================ */
const VAPID_PUBLIC_KEY = 'BCJY772NbnQ-mW_jQYxrVwQ9LlKY-xpxxF-3IHe9_whvpjzu_pCmWIMe0WiUbuAERQNaXmeLh1rlVPXimbVbtVs';

function _vapidPlaceholder() { return !VAPID_PUBLIC_KEY || VAPID_PUBLIC_KEY.indexOf('REPLACE_') === 0; }

function pushSupported() {
    return ('serviceWorker' in navigator) && ('PushManager' in window) && ('Notification' in window);
}

/* iOS: Web Push merge DOAR în PWA instalată (Adaugă la ecranul principal), iOS 16.4+. */
function _isIos() { return /iphone|ipad|ipod/i.test(navigator.userAgent || ''); }
function _isStandalone() {
    try { return window.navigator.standalone === true || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches); }
    catch (e) { return false; }
}

function _urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
}

/* Starea curentă a abonării pe ACEST dispozitiv (pentru UI). */
async function pushIsSubscribedHere() {
    try {
        if (!pushSupported()) return false;
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        return !!sub;
    } catch (e) { return false; }
}

/* Intenția userului (durabilă, per-dispozitiv). Sursa de adevăr pentru buton:
   rămâne exact cum a setat-o userul, până o schimbă el. */
/* Intenția userului (durabilă, per-dispozitiv). DEFAULT = pornit pentru toți
   (rămâne pornit până când userul apasă explicit „Dezactivează" -> '0'). */
function pushIntentOn() { try { return localStorage.getItem('to_push_enabled') !== '0'; } catch (e) { return true; } }
function setPushIntent(on) { try { localStorage.setItem('to_push_enabled', on ? '1' : '0'); } catch (e) { logError('setPushIntent', e); } }

/* Asigură un abonament valid + token salvat (NU cere permisiune — o presupune acordată).
   Folosit la (re)abonare, la auto-vindecare și la login. */
async function _ensureSubscribed() {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: _urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
        });
    }
    const j = sub.toJSON();
    const keys = j.keys || {};
    const r = await sb.rpc('save_push_token', {
        p_endpoint: j.endpoint, p_p256dh: keys.p256dh || '', p_auth: keys.auth || '',
        p_ua: (navigator.userAgent || '').slice(0, 250)
    });
    return !(r && r.error);
}

/* HTML-ul secțiunii de notificări din Profil (acordeon). Disponibil tuturor
   nivelurilor (preferință personală, nu capabilitate de moderare — fără CAP_MIN_RANK). */
function renderPushSettingsHTML() {
    if (!state.currentUser) return '';
    const supported = pushSupported();
    const iosBlocked = _isIos() && !_isStandalone();
    let statusKey = 'push.off', btnKey = 'push.enable', disabled = '', btnStyle = 'width:100%;';
    /* ⚠ `btnStyle` există ca variabilă separată fiindcă stările blocate adăugau
       ele însele un atribut `style`. Un al doilea `style=` pe același buton ar fi
       fost IGNORAT de browser (câștigă primul), deci lățimea sau opacitatea s-ar
       fi pierdut, în funcție de ordine. Aici se compun într-un singur atribut.
       `refreshPushUI()` atinge doar `innerHTML` și clasele, nu `style`, deci
       lățimea supraviețuiește reîmprospătărilor.
       EN: single merged style attribute — a duplicate `style=` would be dropped. */
    if (!supported || iosBlocked) {
        statusKey = iosBlocked ? 'push.iosHint' : 'push.unsupported';
        disabled = 'disabled';
        btnStyle = 'width:100%; opacity:.5; cursor:not-allowed;';
    } else if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
        statusKey = 'push.denied';
        disabled = 'disabled';
        btnStyle = 'width:100%; opacity:.5; cursor:not-allowed;';
    } else if (pushIntentOn()) {
        /* Z8-E: intenția e pornită, dar push-ul e ACTIV cu adevărat doar dacă permisiunea
           e „granted". Dacă e încă „default" (userul n-a fost întrebat), arătăm „în
           așteptare" + buton de activare — nu „activ" (altfel userul crede că primește
           notificări când de fapt nu). Abonamentul real se confirmă în refreshPushUI. */
        const _granted = (typeof Notification !== 'undefined' && Notification.permission === 'granted');
        if (_granted) { statusKey = 'push.on'; btnKey = 'push.disable'; }
        else { statusKey = 'push.pending'; btnKey = 'push.enable'; }
    }
    const onNow = (statusKey === 'push.on');
    return `
    <div class="admin-acc profile-acc" style="margin-top:6px;">
        <button type="button" class="admin-acc-head" onclick="toggleTilePanel('pfSec-push', this); refreshPushUI();">
            <span><i class="fas fa-bell"></i> ${t('push.title')}</span>
            <i class="fas fa-chevron-down admin-acc-chev"></i>
        </button>
        <div id="pfSec-push" class="admin-acc-body hidden">
            <p style="color:var(--text-secondary); font-size:.9em; margin:0 0 10px;">${t('push.desc')}</p>
            <p id="pushStatusLine" style="font-size:.9em; margin:0 0 12px; color:var(--text-secondary);"><i class="fas fa-circle-info"></i> ${t(statusKey)}</p>
            <button type="button" id="pushToggleBtn" class="btn ${onNow ? 'btn-secondary' : 'btn-primary'}" ${disabled} style="${btnStyle}" data-push-on="${onNow ? '1' : '0'}" onclick="togglePush()">
                <i class="fas fa-bell${onNow ? '-slash' : ''}"></i> ${t(btnKey)}
            </button>
        </div>
    </div>`;
}

/* Reîmprospătează textul + butonul în funcție de starea reală + auto-vindecare. */
async function refreshPushUI() {
    const line = document.getElementById('pushStatusLine');
    const btn = document.getElementById('pushToggleBtn');
    if (!line || !btn) return;
    if (!pushSupported() || (_isIos() && !_isStandalone()) ||
        (typeof Notification !== 'undefined' && Notification.permission === 'denied')) {
        return; // stările blocate sunt deja randate de renderPushSettingsHTML
    }
    let want = pushIntentOn();
    /* Auto-vindecare în fundal: dacă vrea notificări + permisiune acordată dar abonamentul
       lipsește (reset browser/SW) -> reabonează silențios. */
    let reallyActive = false;
    if (want && typeof Notification !== 'undefined' && Notification.permission === 'granted' && !_vapidPlaceholder()) {
        try {
            if (!(await pushIsSubscribedHere())) await _ensureSubscribed();
            reallyActive = await pushIsSubscribedHere();
        } catch (e) { logError('refreshPushUI', e); }
    }
    /* Z8-E: afișăm starea REALĂ. Intenție ON dar neabonat efectiv (permisiune neacordată
       sau abonament indisponibil) => „în așteptare", nu „activ". */
    let showKey, btnKey2;
    if (want && reallyActive) { showKey = 'push.on'; btnKey2 = 'push.disable'; }
    else if (want) { showKey = 'push.pending'; btnKey2 = 'push.enable'; }
    else { showKey = 'push.off'; btnKey2 = 'push.enable'; }
    const _isOnState = (showKey === 'push.on');
    line.innerHTML = '<i class="fas fa-circle-info"></i> ' + t(showKey);
    btn.innerHTML = '<i class="fas fa-bell' + (_isOnState ? '-slash' : '') + '"></i> ' + t(btnKey2);
    btn.classList.toggle('btn-secondary', _isOnState);
    btn.classList.toggle('btn-primary', !_isOnState);
    /* ⚠ 9.1g: starea AFIȘATĂ, scrisă pe buton. `togglePush` o citește de aici, ca să facă
       exact ce scrie pe el. Vezi explicația de la `togglePush`.
       EN: the displayed state is stored on the button; togglePush reads it. */
    btn.dataset.pushOn = _isOnState ? '1' : '0';
}

/* Comutator on/off legat de buton.
   ⚠ 9.1g — BUTONUL MINȚEA. RO: decizia se lua din `pushIntentOn()`, adică din INTENȚIE,
   nu din starea afișată. La prima folosire intenția e `true` (implicit pentru toți), dar
   permisiunea e încă `default` și nu există abonament — deci butonul scria „Activează",
   iar apăsarea lui chema `unsubscribePush()`: „Notificările au fost dezactivate", pentru
   ceva ce nu fusese niciodată activat. Abia a doua apăsare cerea permisiunea.
   Reparația: butonul face EXACT ce scrie pe el. `refreshPushUI` calculează deja corect
   starea reală (`want && reallyActive`) și o scrie în `data-push-on`; o citim de acolo.
   ⚠ De ce din DOM și nu recalculăm aici: o recalculare ar putea da alt rezultat decât ce
   a văzut utilizatorul când a apăsat (abonamentul se poate vindeca între timp, în fundal),
   și am ajunge din nou la un buton care face altceva decât spune. Sursa de adevăr pentru
   ACȚIUNE e ce scria pe buton în momentul apăsării.
   EN: the button decided from INTENT, not from the displayed state, so the first press
   "disabled" something never enabled. Now it does exactly what its label says. */
async function togglePush() {
    const btn = document.getElementById('pushToggleBtn');
    let on;
    if (btn && btn.dataset && (btn.dataset.pushOn === '1' || btn.dataset.pushOn === '0')) {
        on = (btn.dataset.pushOn === '1');
    } else {
        /* Buton absent (apel din altă parte) sau încă nerandat: calculăm starea reală,
           cu aceeași regulă ca `refreshPushUI` — NICIODATĂ doar din intenție. */
        on = await pushIsOnNow();
    }
    if (on) await unsubscribePush();   // chiar e pornit -> oprește
    else await subscribePush();        // oprit sau în așteptare -> pornește (cere permisiunea)
    try { await refreshPushUI(); } catch (e) { logError('togglePush', e); }
}

/* Starea REALĂ, fără efecte secundare: intenție + permisiune + abonament existent.
   Aceeași regulă pe care o folosește `refreshPushUI` pentru afișare. */
async function pushIsOnNow() {
    try {
        if (!pushIntentOn()) return false;
        if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;
        if (_vapidPlaceholder()) return false;
        return await pushIsSubscribedHere();
    } catch (e) { logError('pushIsOnNow', e); return false; }
}
window.pushIsOnNow = pushIsOnNow;

/* ============================================================
   LIMBA PREFERATĂ (profiles.lang) — pentru remindere push în limba
   setată de user. Sincronizată la schimbarea limbii (setLang) și la login.
   RPC `set_my_lang(p_lang)` scrie DOAR propriul profil (auth.uid()). §23.
   ============================================================ */
let _lastPersistedLang = null;
async function persistUserLang(lang) {
    try {
        lang = (lang === 'ro') ? 'ro' : 'en';
        if (!sb || !state.currentUser || !state.currentUser.id) return;
        if (typeof isDemoMode === 'function' && isDemoMode()) return;
        if (_lastPersistedLang === lang) return;         // nu spamăm DB la fiecare render
        _lastPersistedLang = lang;
        const r = await sb.rpc('set_my_lang', { p_lang: lang });
        if (r && r.error) { _lastPersistedLang = null; logError('persistUserLang', r.error); }
    } catch (e) { _lastPersistedLang = null; logError('persistUserLang', e); }
}
window.persistUserLang = persistUserLang;

/* La fiecare pornire cu sesiune (H30, decizia B, 6 oct. 2026) — CONTUL e sursa de adevăr:
   RO: 1) Limba se citește din cont (`my_lang()`) și se aplică; interfața se redesenează DOAR
          dacă diferă de cea afișată. Comutarea din buton (`setLang` -> `persistUserLang`)
          scrie în cont, deci celelalte dispozitive o preiau la următoarea pornire.
       2) Doar dacă citirea eșuează (fără internet, funcția lipsă): comportamentul vechi —
          alegerea salvată pe dispozitiv, altfel limba afișată, merge în cont.
       ⚠ DE CE NU MAI CÂȘTIGĂ DISPOZITIVUL (regula 7.3 din 3 oct.): memoria dispozitivului
         conținea „alegeri" pe care omul nu le făcuse (codul de dinainte de 3 oct. salva automat
         limba implicită la fiecare logare; cheia generală `lang` e scrisă de ORICE apăsare, chiar
         nelogat), iar ultimul dispozitiv pornit își scria limba în cont. Dovedit pe calea reală:
         telefonul a trecut contul din „ro" în „en", deși desktopul afișa română.
       ⚠ H30a: citirea directă `profiles.select('lang')` primea 403 / 42501 la FIECARE logare
         (`authenticated` n-are SELECT pe coloană); `my_lang()` (V9_H30_MY_LANG.sql, KB 53.d)
         întoarce DOAR limba proprie, din auth.uid().
       Costul: o citire la pornire ÎN LOCUL scrierii de la fiecare pornire din varianta veche.
   EN: the ACCOUNT is the source of truth (read via my_lang, re-render only on change); a toggle
       writes the account; the device value is only a fallback when the read fails. The old
       device-wins rule let stale, never-chosen device values overwrite the account. */
async function syncLangOnLogin() {
    try {
        if (!sb || !state.currentUser || !state.currentUser.id) return;
        if (typeof isDemoMode === 'function' && isDemoMode()) return;
        const uid = state.currentUser.id;
        let accountLang = null;
        try {
            const r = await sb.rpc('my_lang');
            if (r && !r.error && typeof r.data === 'string') accountLang = r.data;
        } catch (e) { logError('syncLangOnLogin', e); }
        if (accountLang === 'ro' || accountLang === 'en') {
            _lastPersistedLang = accountLang;            // e deja în cont: fără scriere inutilă
            if (accountLang !== window.currentLang && typeof setLang === 'function') setLang(accountLang);   // aplică + salvează pe dispozitiv
            return;
        }
        let devicePref = null;
        try { devicePref = localStorage.getItem('lang:' + uid) || localStorage.getItem('lang'); } catch (e) {}
        if (devicePref === 'ro' || devicePref === 'en') { await persistUserLang(devicePref); return; }
        const cur = (document.documentElement.lang === 'ro') ? 'ro' : 'en';
        await persistUserLang(cur);
    } catch (e) { logError('syncLangOnLogin', e); }
}
window.syncLangOnLogin = syncLangOnLogin;

/* Cere permisiunea, generează PushSubscription și salvează token-ul în Supabase. */
async function subscribePush() {
    try {
        if (isDemoMode && isDemoMode()) { showNotification(t('dm.demo'), 'info'); return; }
        if (!pushSupported()) { showNotification(t('push.unsupported'), 'error'); return; }
        if (_isIos() && !_isStandalone()) { showNotification(t('push.iosHint'), 'info'); return; }
        if (_vapidPlaceholder()) { console.warn('VAPID_PUBLIC_KEY neconfigurat'); showNotification(t('push.error'), 'error'); return; }

        const perm = await Notification.requestPermission();
        if (perm !== 'granted') { showNotification(t('push.denied'), 'error'); try { renderMyProfile(); } catch (e) { logError('subscribePush', e); } return; }

        const ok = await _ensureSubscribed();
        if (!ok) { showNotification(t('push.error'), 'error'); return; }
        setPushIntent(true);
        showNotification(t('push.granted'), 'success');
    } catch (e) {
        console.warn('subscribePush:', e && e.message);
        showNotification(t('push.error'), 'error');
    }
}

/* Dezabonare pe acest dispozitiv + ștergere token din DB. Marchează intenția OFF. */
/* Componenta 5 — la LOGOUT oprim livrarea push către ACEST dispozitiv pentru contul
   care pleacă: ștergem token-ul pe server (necesită sesiune validă, deci se apelează
   ÎNAINTE de signOut) și dezabonăm abonamentul din browser. NU atingem intenția
   userului (to_push_enabled) și nu arătăm toast — la următorul login push-ul se
   reactivează automat (_ensureSubscribed re-salvează token-ul). */
async function _logoutPushCleanup() {
    try {
        if (typeof pushSupported === 'function' && !pushSupported()) return;
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
            const endpoint = sub.endpoint;
            try { await sb.rpc('delete_push_token', { p_endpoint: endpoint }); } catch (e) { logError('_logoutPushCleanup', e); }
            try { await sub.unsubscribe(); } catch (e) { logError('_logoutPushCleanup', e); }
        }
    } catch (e) { console.warn('logout push cleanup:', e && e.message); }
}

async function unsubscribePush() {
    try {
        setPushIntent(false);
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
            const endpoint = sub.endpoint;
            try { await sub.unsubscribe(); } catch (e) { logError('unsubscribePush', e); }
            try { await sb.rpc('delete_push_token', { p_endpoint: endpoint }); } catch (e) { logError('unsubscribePush', e); }
        }
        showNotification(t('push.disabled'), 'info');
    } catch (e) { console.warn('unsubscribePush:', e && e.message); }
}

/* La login: dacă userul vrea notificări (intenție) și permisiunea e acordată,
   (re)asigură abonamentul + token-ul — silențios, fără a cere din nou permisiunea.
   Așa rămâne activ cross-login, chiar dacă abonamentul s-a pierdut între timp. */
async function syncPushOnLogin() { try { await ensurePushSubscription(); } catch (e) { logError('syncPushOnLogin', e); } }

/* Asigură abonamentul push (subscribe + salvare token). Dacă subscribe eșuează fără un
   „gest" (cazul Android Chrome la prompt automat), reîncearcă AUTOMAT la prima atingere. */
async function ensurePushSubscription() {
    try {
        if (!pushSupported() || _vapidPlaceholder()) return;
        if (_isIos() && !_isStandalone()) return;
        if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
        if (!pushIntentOn()) return;                 // userul a oprit explicit
        try { await _ensureSubscribed(); } catch (e) { console.warn('ensurePushSubscription:', e && e.message); }
        try { if (!(await pushIsSubscribedHere())) _armSubscribeOnGesture(); } catch (e) { logError('ensurePushSubscription', e); }
    } catch (e) { logError('ensurePushSubscription', e); }
}

/* Plasă de siguranță: la primul click/tap, dacă avem permisiune + intenție dar NU abonament,
   abonează (un gest garantează că subscribe nu mai e blocat de browser). O singură dată. */
function _armSubscribeOnGesture() {
    if (window._pushGestureArmed) return;
    window._pushGestureArmed = true;
    const h = async function () {
        document.removeEventListener('click', h, true);
        document.removeEventListener('touchend', h, true);
        window._pushGestureArmed = false;
        try {
            if (pushIntentOn() && typeof Notification !== 'undefined' && Notification.permission === 'granted'
                && !_vapidPlaceholder() && !(await pushIsSubscribedHere())) {
                await _ensureSubscribed();
                try { await refreshPushUI(); } catch (e) { logError('_armSubscribeOnGesture', e); }
            }
        } catch (e) { logError('_armSubscribeOnGesture', e); }
    };
    document.addEventListener('click', h, true);
    document.addEventListener('touchend', h, true);
}

/* Prompt automat de sistem („Permite notificări"), O SINGURĂ DATĂ per dispozitiv.
   Default ON pentru toți: dacă permisiunea n-a fost încă cerută, o cerem singuri.
   ⚠ Pe iOS/Safari și Firefox sistemul cere un gest (click) -> acolo rămâne butonul. */
async function maybeAutoPromptPush() {
    try {
        if (!pushSupported() || _vapidPlaceholder()) return;
        if (_isIos() && !_isStandalone()) return;          // iOS: doar din PWA instalată + gest
        if (typeof Notification === 'undefined') return;
        if (Notification.permission === 'denied') return;
        if (Notification.permission === 'default') {
            if (localStorage.getItem('to_push_prompted') === '1') { _armSubscribeOnGesture(); return; } // întrebat deja o dată
            localStorage.setItem('to_push_prompted', '1');
            const perm = await Notification.requestPermission();
            if (perm !== 'granted') {
                try { if (typeof renderMyProfile === 'function' && currentTab === 'profile') renderMyProfile(); } catch (e) { logError('maybeAutoPromptPush', e); }
                return;
            }
            setPushIntent(true);
        }
        /* Permisiune acordată -> asigură abonamentul (cu reîncercare la gest dacă e nevoie). */
        await ensurePushSubscription();
        try { if (typeof renderMyProfile === 'function' && currentTab === 'profile') renderMyProfile(); } catch (e) { logError('maybeAutoPromptPush', e); }
    } catch (e) { console.warn('maybeAutoPromptPush:', e && e.message); }
}
window.maybeAutoPromptPush = maybeAutoPromptPush;

/* ===== Banner in-app stil WhatsApp (foreground) ===== */
/* Dedup: aceeași notificare poate sosi pe 2 căi (toast realtime + push foreground).
   Arătăm o singură dată per id, într-o fereastră scurtă. */
window._recentPushShown = window._recentPushShown || {};
/* ============================================================================
   O SINGURĂ notificare pop-up per eveniment
   ----------------------------------------------------------------------------
   Există DOUĂ căi care afișează același eveniment:
     1. `showNotification(n.text)` — din abonamentul realtime pe `notifications`;
        merge întotdeauna, chiar dacă push-ul e oprit.
     2. `showPushBanner(payload)` — cardul mare, când push-ul ajunge cât timp
        aplicația e în prim-plan; e mai bogat (titlu + corp + rutare la tap).
   Amândouă chemau `_pushAlreadyShown`, deci dublarea NU era o scăpare de
   concepție, ci o dedublare care rata din trei motive:
     · FORMAT — realtime marchează `'n' + id`, push-ul trimite `notifId` brut;
     · TIMP   — fereastra era de 8s, dar un push poate ajunge și după 20-30s;
     · ID LIPSĂ — dacă payload-ul n-are `notifId`, verificarea era sărită complet.
   Reparăm toate trei: normalizăm id-ul, lărgim fereastra și adăugăm o a doua
   plasă, pe TEXT, pentru cazul fără id.
   Cine ajunge primul câștigă — nu contează care, userul vede una singură.
   EN: both toast paths deduped on a normalised id + wider window + text fallback.
   ============================================================================ */

const _PUSH_DEDUP_MS = 60000;   /* id: fereastră generoasă, push-ul poate întârzia */
const _TEXT_DEDUP_MS = 30000;   /* text: mai scurtă, ca două mesaje identice reale
                                   trimise la distanță să nu fie înghițite */
window._recentNotifText = window._recentNotifText || {};

/* `'n123'` și `123` sunt același eveniment. */
function _normPushId(id) {
    const s = String(id == null ? '' : id);
    return s.replace(/^n(?=\d)/, '');
}

/* Plasă de rezervă când payload-ul de push n-are `notifId`. */
function _notifTextAlreadyShown(text) {
    const key = String(text == null ? '' : text).trim().slice(0, 160);
    if (!key) return false;
    const now = Date.now();
    Object.keys(window._recentNotifText).forEach(k => {
        if (now - window._recentNotifText[k] > _TEXT_DEDUP_MS) delete window._recentNotifText[k];
    });
    if (window._recentNotifText[key]) return true;
    window._recentNotifText[key] = now;
    return false;
}
window._notifTextAlreadyShown = _notifTextAlreadyShown;

function _pushAlreadyShown(id) {
    if (!id) return false;
    id = _normPushId(id);
    const now = Date.now();
    Object.keys(window._recentPushShown).forEach(k => { if (now - window._recentPushShown[k] > _PUSH_DEDUP_MS) delete window._recentPushShown[k]; });
    if (window._recentPushShown[id]) return true;
    window._recentPushShown[id] = now;
    return false;
}
window._pushAlreadyShown = _pushAlreadyShown;

function showPushBanner(payload) {
    try {
        payload = payload || {};
        const data = payload.data || {};
        if (data.notifId && _pushAlreadyShown(data.notifId)) return; // deja arătat prin toast-ul realtime
        /* Fără `notifId` (sau cu unul care nu se potrivește) verificăm textul.
           Pentru mesaje private, cheia comună cu toast-ul din social.js e CORPUL
           mesajului: acolo `payload.body` e chiar textul trimis. */
        if (_notifTextAlreadyShown(payload.body || payload.title || '')) return;
        /* Nu banner-iza conversația DM pe care o ai deja deschisă (o vezi live). */
        try {
            if (data.route === 'dm' && data.dmWith && typeof window._dmOpenWith !== 'undefined'
                && String(window._dmOpenWith || '') === String(data.dmWith)) return;
        } catch (e) { logError('showPushBanner', e); }
        /* Nu banner-iza chat-ul pe care-l ai deja deschis (ca WhatsApp). */
        try {
            if (data.route === 'chat' && typeof currentTab !== 'undefined' && currentTab === 'chat'
                && String(data.clubId || '') === String((typeof currentClubId !== 'undefined' ? currentClubId : '') || '')) return;
        } catch (e) { logError('showPushBanner', e); }
        const esc = s => window.escHtml(s);
        let host = document.getElementById('pushBannerHost');
        if (!host) {
            host = document.createElement('div');
            host.id = 'pushBannerHost';
            host.style.cssText = 'position:fixed; top:0; left:0; right:0; z-index:99999; display:flex; flex-direction:column; gap:8px; padding:8px 10px; pointer-events:none;';
            document.body.appendChild(host);
        }
        const card = document.createElement('div');
        card.style.cssText = [
            'pointer-events:auto', 'cursor:pointer',
            'max-width:520px', 'margin:0 auto', 'width:100%',
            'background:var(--bg-card,#1e1e2e)', 'border:1px solid var(--border,#333)',
            'border-left:4px solid var(--accent-primary,#ff6b35)',
            'border-radius:14px', 'box-shadow:0 8px 30px rgba(0,0,0,.45)',
            'padding:12px 14px', 'display:flex', 'gap:12px', 'align-items:flex-start',
            'transform:translateY(-130%)', 'transition:transform .28s cubic-bezier(.2,.8,.2,1)',
            'font-family:inherit'
        ].join(';');
        const icon = esc(payload.icon || '');
        const iconHtml = (icon && /^https?:|^data:/.test(icon))
            ? `<img src="${icon}" alt="" style="width:38px;height:38px;border-radius:9px;object-fit:cover;flex-shrink:0;">`
            : `<div style="width:38px;height:38px;border-radius:9px;flex-shrink:0;display:flex;align-items:center;justify-content:center;background:var(--gradient-primary,linear-gradient(135deg,#ff6b35,#ff8c42));color:#fff;"><i class="fas fa-bell"></i></div>`;
        card.innerHTML = `
            ${iconHtml}
            <div style="min-width:0; flex:1;">
                <div style="font-weight:700; color:var(--text-primary,#fff); font-size:.95em; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${esc(payload.title || 'TurnOrder')}</div>
                <div style="color:var(--text-secondary,#b8b8c8); font-size:.88em; margin-top:2px; overflow:hidden; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical;">${esc(payload.body || '')}</div>
            </div>
            <i class="fas fa-times" style="color:var(--text-secondary,#888); flex-shrink:0; padding:2px 4px;" onclick="event.stopPropagation(); this.closest('div[role=alert]')&&this.closest('div[role=alert]').remove();"></i>`;
        card.setAttribute('role', 'alert');
        card.onclick = function () { try { _pushRoute(data); } catch (e) { logError('showPushBanner', e); } card.remove(); };
        host.appendChild(card);
        requestAnimationFrame(() => { card.style.transform = 'translateY(0)'; });
        /* vibrație discretă dacă dispozitivul o permite (foreground) */
        try { if (navigator.vibrate) navigator.vibrate([60, 30, 60]); } catch (e) { logError('showPushBanner', e); }
        setTimeout(() => {
            card.style.transform = 'translateY(-130%)';
            setTimeout(() => { try { card.remove(); } catch (e) { logError('showPushBanner', e); } }, 320);
        }, 4000);
    } catch (e) { console.warn('showPushBanner:', e && e.message); }
}

/* Rutează către locul acțiunii. Reutilizează deep linking-ul existent. */
function _pushRoute(data) {
    data = data || {};
    /* Fără sesiune activă NU rutăm și NU expunem conținut (ex. un DM). Reținem
       ținta și o reluăm după login (vezi showMainApp). */
    if (!state.currentUser) {
        try { window._pendingPushRoute = data; } catch (e) { logError('_pushRoute', e); }
        return;
    }
    /* v9 8.7-D7: întâi rutarea obișnuită (la club așteptăm și schimbarea clubului), apoi saltul la
       mesajul exact (club-notif.js, la cerere). EN: route first, then jump to the exact message. */
    if ((data.jump || data.notifId) && (data.dmWith || data.route === 'chat') && !data._jumpDone) {
        const routed = _pushRoute(Object.assign({}, data, { _jumpDone: true }));
        Promise.resolve(routed).then(function () {
            if (window.TO_CHAT && typeof window.TO_CHAT.useModule === 'function') window.TO_CHAT.useModule('club-notif.js', 'TO_CNOTIF', function (M) { M.jump(data); }, true);
        }).catch(function (e) { logError('_pushRoute', e); });
        return routed;
    }
    try {
        if (data.dmWith) {
            if (typeof openDMFromNotif === 'function') return openDMFromNotif(data.dmWith, data.dmName || '', null);
            if (typeof openDM === 'function') return openDM(data.dmWith, data.dmName || '');
        }
        if (data.profileId) {
            if (typeof openPublicProfileById === 'function') return openPublicProfileById(data.profileId);
            if (typeof openFriendReqNotif === 'function') return openFriendReqNotif(data.profileId, '', null);
        }
        if (typeof openNotif === 'function') return openNotif(data.clubId || '', data.tab || '', null);
        if (data.tab && typeof switchTab === 'function') switchTab(data.tab);
    } catch (e) { console.warn('_pushRoute:', e && e.message); }
}

/* Mesaje de la Service Worker: foreground banner + rutare la click pe notificarea de sistem. */
if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', function (event) {
        const d = event.data || {};
        if (d.kind === 'push-foreground') { showPushBanner(d.payload || {}); }
        else if (d.kind === 'push-route') { _pushRoute(d.data || {}); }
        else if (d.kind === 'push-ping' && event.ports && event.ports[0]) {
            /* 8.7-B4: service worker-ul întreabă dacă pagina e în fața utilizatorului (`pageInFront`
               din sw.js). Pe Samsung (PWA) aplicația deschisă nu era raportată „focused" ⇒ mesaj în
               aplicație + notificare de sistem, dublate (cazul 7, 22 oct.). Ecran tactil (telefon /
               tabletă): vizibilă = în față. Desktop: trebuie și focus (fereastra poate fi vizibilă în
               spatele altei aplicații); laptopul cu touchscreen are cursor ⇒ e tratat ca desktop.
               EN: answers the SW ping — touch-primary + visible counts as in front; desktop needs focus. */
            let fg = false;
            try {
                fg = document.visibilityState === 'visible' && (document.hasFocus()
                    || !!(window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches));
            } catch (e) { logError('push-ping', e); }
            try { event.ports[0].postMessage({ fg: fg }); } catch (e) { logError('push-ping', e); }
        }
    });
}

/* Cold start prin click pe notificare (fereastră nouă): consumă ?route=... din URL. */
function consumePushRouteFromUrl() {
    try {
        const p = new URLSearchParams(window.location.search || '');
        if (!p.has('route') && !p.has('tab') && !p.has('dmWith')) return;
        const data = {
            route: p.get('route') || '', clubId: p.get('clubId') || '',
            tab: p.get('tab') || '', dmWith: p.get('dmWith') || '', dmName: p.get('dmName') || '',
            jump: p.get('jump') || ''   /* v9 8.7-D7 */
        };
        /* curăță query-ul ca să nu re-ruteze la refresh */
        try { history.replaceState(null, '', window.location.pathname + window.location.hash); } catch (e) { logError('consumePushRouteFromUrl', e); }
        setTimeout(() => { try { _pushRoute(data); } catch (e) { logError('consumePushRouteFromUrl', e); } }, 600);
    } catch (e) { logError('consumePushRouteFromUrl', e); }
}
window.subscribePush = subscribePush;
window.unsubscribePush = unsubscribePush;
window.togglePush = togglePush;
window.refreshPushUI = refreshPushUI;
window.renderPushSettingsHTML = renderPushSettingsHTML;
window.showPushBanner = showPushBanner;

/* ================= INITIALIZATION ================= */
// Start the application when the DOM is loaded
document.addEventListener('DOMContentLoaded', init);

// Handle hash changes for tab navigation
window.addEventListener('hashchange', () => {
    const hash = window.location.hash.substring(1);
    if (hash && document.querySelector(`[data-tab="${hash}"]`)) {
        switchTab(hash);
    }
});


/* ============================================================================
   ★ OVERRIDE (§5): settingsSubHTML — card de abonament cu stare de FREE TRIAL.
   RO: daca userul e in trial -> card dedicat (countdown + CTA plata). Daca nu are
       abonament dar e ELIGIBIL -> oferta de trial. Altfel -> varianta din app-core.
   EN: subscription card with free-trial state (countdown + paid CTA / trial offer).
   ============================================================================ */
const _origSettingsSubHTML = window.settingsSubHTML;
window.settingsSubHTML = function () {
    const T = (typeof t === 'function') ? t : function (k) { return k; };
    const _plan = (typeof userActivePlan === 'function') ? userActivePlan() : null;

    /* Fara abonament, dar eligibil de trial -> oferta. */
    if (!_plan && typeof trialEligible === 'function' && trialEligible()) {
        return '<div class="sub-card sub-card-empty">'
            + '<span class="sub-empty-row"><i class="fas fa-gift"></i> ' + T('trial.offerTitle') + '</span>'
            + '<button class="btn btn-sm sub-cta" onclick="openPlansModal()"><i class="fas fa-gift"></i> ' + T('trial.cta') + '</button>'
            + '</div>';
    }
    if (!(typeof isTrialing === 'function' && isTrialing())) return _origSettingsSubHTML();

    const u = state.userSub || {};
    const plan = String(u.plan || '').toLowerCase();
    const info = (typeof PLAN_INFO !== 'undefined' && PLAN_INFO[plan]) || {};
    const d = trialDaysLeft();
    const end = u.current_period_end ? new Date(u.current_period_end) : null;
    const dateStr = end
        ? ((end.getDate() < 10 ? '0' : '') + end.getDate() + '/' + (end.getMonth() + 1 < 10 ? '0' : '') + (end.getMonth() + 1) + '/' + end.getFullYear())
        : '';
    const maxClubs = (typeof planClubLimit === 'function') ? planClubLimit(plan) : Infinity;
    const owned = (typeof countMyOwnedClubs === 'function') ? countMyOwnedClubs() : 0;
    const clubsTxt = (maxClubs === Infinity) ? (owned + ' \u00b7 \u221e') : (owned + '/' + maxClubs);
    const membersTxt = (info.members === Infinity || info.members == null) ? '\u221e' : info.members;

    return '<div class="sub-card">'
        + '<div class="sub-title"><i class="fas fa-gift"></i> ' + T('trial.badge') + ' \u2014 ' + (info.label || plan) + '</div>'
        + '<div class="sub-rows">'
        +   '<div class="sub-line"><span class="sub-k"><i class="fas fa-hourglass-half"></i> ' + T('trial.remaining') + '</span>'
        +   '<span class="sub-v" style="color:var(--accent-gold,#f9a826);">' + d + ' ' + T('sub.daysLeft') + '</span></div>'
        +   '<div class="sub-line"><span class="sub-k"><i class="fas fa-layer-group"></i> ' + T('profile.clubsUsed') + '</span><span class="sub-v">' + clubsTxt + '</span></div>'
        +   '<div class="sub-line"><span class="sub-k"><i class="fas fa-users"></i> ' + T('profile.membersPerClub') + '</span><span class="sub-v">' + membersTxt + '</span></div>'
        +   '<div class="sub-note">' + T('trial.endsOn') + ' ' + dateStr + ' \u00b7 ' + T('trial.noCardNote') + '</div>'
        + '</div>'
        + '<div class="sub-actions">'
        +   '<button class="btn btn-sm" onclick="openPlansModal()"><i class="fas fa-credit-card"></i> ' + T('trial.activatePaid') + '</button>'
        + '</div>'
        + '</div>';
};
